import {t, useLanguage} from '../i18n';
import {BusinessRegionContext,regionMoney,useRegionMoney,assertSameCurrency} from '../utils/businessRegionContext';
import {wallToInstant,wallDate} from '../utils/businessTime';
import "./finance/FinanceMobile.css";
import "./finance/ReportPolish.css";
import {eventFinanceSummary,closedEventWorkerPayout} from '../utils/eventFinanceSummaryV1';
import BusinessMembershipRenewal from "./memberships/BusinessMembershipRenewal";
import ReportMembershipEnrollment from "./memberships/ReportMembershipEnrollment";
// src/components/BusinessAnalytics.jsx
// LOOP PHASE 3.0 — Smart Replenishment Planner / compras sugeridas accionables
// LOOP PHASE 2.9 — Inventory service connection map / materiales por servicio
// LOOP PHASE 2.8 — Inventory Automation Bridge / Auto descuento de materiales
// LOOP PHASE 2.7 — Smart Replenishment / Lista de compra sugerida
// LOOP PHASE 2.6 — Daily Cash Closing / Caja del día
// LOOP PHASE 2.5B — Service materials margin reporting
// LOOP PHASE 2.5A — ANALYTICS LAYOUT SIDEBAR / FILTER POLISH
// LOOP PHASE 2.5A HOTFIX — sidebar safe counters
// LOOP PHASE 2.4 — FINANCE EXECUTIVE DASHBOARD
// LOOP PHASE 2.3 — Smart Tasks / light automations
// LOOP PHASE 2.2F — CRM Executive Dashboard + Weekly Priorities
// LOOP PHASE 2.2E — CRM Revenue Attribution + Reactivation ROI
// LOOP PHASE 2.2D — CRM Follow-up Pipeline + Reactivation Tracking
// Built on Phase 2.2C: action log, WhatsApp campaign modal and retention action center.
// LOOP PHASE 3A.2 — Clean reports header: business identity lives in Admin sidebar.
// LOOP PHASE 3B.1 — Premium clean executive summary: less visual noise, more Loop minimalism.
// LOOP PHASE 3C — Finance clean operativo: simple close view for owners and teams.
// LOOP PHASE 3C.1 — Finance micro-polish: compact KPI cards + premium column action.
// LOOP PHASE 3C.2 — Premium module switcher + no skeleton flash.
// LOOP PHASE 3C.3 — Minimal module switcher: compact premium menu.
// LOOP PHASE 3C.4 — Finance split layout: compact summary + cash method breakdown.
// LOOP PHASE 3C.5 — Finance method table: service label + centered money columns.
// LOOP PHASE 3C.6 — Finanzas + Caja unified: Caja becomes a shortcut to Finanzas.
import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db as defaultDb, auth as defaultAuth } from "../firebase";
import fsClient from "../data/firestoreClient";
import { isIosCapacitor } from "../utils/nativeHttp";
import { normalizeLoopBusinessSettings, normalizeWorkerPermissions } from "../adminCalendar/workerPermissions";
import { bookingLifecycleFromRecord, normalizePaymentStatus as normalizeCanonicalPaymentStatus, shouldAutoDeductInventoryForBooking, normalizeInventoryDeductionStatus } from "../utils/bookingLifecycle";
import ExpenseLedger from "./finance/ExpenseLedger";
import { FinanceAgingPanel, FinanceCloseoutPanel, FinanceCutoverStatus, FinanceForecastPanel, FinanceProfitabilityPanel, FinanceWorkspaceNav, overlayFinanceSummary, useFinanceRemoteCutover } from "./finance/useFinanceRemoteCutover";
import MembershipCollectionsPanel from "./finance/MembershipCollectionsPanel";
import LoopCollectionsPanel from "./finance/LoopCollectionsPanel";

const BRAND = "#00675f";
const BRAND_2 = "#14b8a6";


function safeDate(value) {
  try {
    if (!value) return null;
    if (typeof value.toDate === "function") return value.toDate();
    if (value.seconds) return new Date(Number(value.seconds) * 1000);
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (_) {
    return null;
  }
}

function getBookingDate(b) {
  return safeDate(b?.scheduledAt) || safeDate(b?.date) || safeDate(b?.createdAt) || null;
}

function getCreatedDate(b) {
  return safeDate(b?.createdAt) || safeDate(b?.requestedAt) || safeDate(b?.submittedAt) || getBookingDate(b);
}

function numberFrom(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function getServiceId(b) {
  return String(b?.serviceId || b?.serviceID || b?.serviceKey || b?.serviceRef || "").trim();
}

function getServiceName(b, serviceMap) {
  const sid = getServiceId(b);
  const svc = sid ? serviceMap.get(sid) : null;
  return b?.serviceName || b?.service || b?.packageName || b?.serviceTitle || b?.title || svc?.name || svc?.title || sid || "Servicio";
}

function getBookingServiceCollectedAmount(b) {
  // LOOP PHASE 1 HOTFIX — Analytics must use the real service amount collected.
  // Tip is intentionally separate: commission is based on service collected,
  // while business net is service collected + tip - worker payout.
  const explicit =
    b?.serviceCollectedAmount ??
    b?.payment?.serviceCollectedAmount ??
    b?.amountCharged ??
    b?.payment?.amountCharged ??
    b?.paidAmount ??
    b?.payment?.paidAmount ??
    b?.paymentAmount ??
    b?.payment?.paymentAmount ??
    b?.amountPaid ??
    b?.payment?.amountPaid ??
    null;

  const n = numberFrom(explicit);
  return n > 0 ? n : 0;
}

function getBookingRevenue(b, serviceMap) {
  // LOOP PHASE 2.5C — canonical expected revenue.
  // If a service has no configured price but the booking was charged manually,
  // reports use the booking-level amount instead of showing blanks everywhere.
  const lifecycle = bookingLifecycleFromRecord(b || {});
  const productSalesTotal = getBookingProductSalesTotal(b);
  if (lifecycle.expectedServiceAmount > 0 || lifecycle.serviceCollectedAmount > 0) {
    return Math.max(lifecycle.expectedServiceAmount, lifecycle.serviceCollectedAmount) + productSalesTotal;
  }

  const sid = getServiceId(b);
  const svc = sid ? serviceMap.get(sid) : null;
  return numberFrom(svc?.price ?? svc?.amount ?? svc?.cost ?? svc?.servicePrice ?? svc?.basePrice) + productSalesTotal;
}


const PAYMENT_METHOD_LABELS = {
  cash: "Efectivo",
  yappy: "Yappy",
  card: "Tarjeta",
  transfer: "Transferencia",
  other: "Otro",
  membership: "Membresía",
  loyalty_reward: "Fidelidad",
  courtesy: "Cortesía",
  unknown: "Sin método",
};

function normalizePaymentStatus(value, bookingLike = {}) {
  const lifecycle = bookingLike && typeof bookingLike === "object" ? bookingLifecycleFromRecord(bookingLike) : null;
  return normalizeCanonicalPaymentStatus(value, {
    collected: lifecycle?.serviceCollectedAmount ?? bookingLike?.serviceCollectedAmount ?? bookingLike?.amountCharged ?? 0,
    expected: lifecycle?.expectedServiceAmount ?? bookingLike?.expectedServiceAmount ?? bookingLike?.totalDueAmount ?? 0,
  });
}

function normalizePaymentMethod(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (["cash", "efectivo"].includes(raw)) return "cash";
  if (["yappy", "japi", "yappi"].includes(raw)) return "yappy";
  if (["card", "tarjeta", "credit_card", "debit_card"].includes(raw)) return "card";
  if (["transfer", "transferencia", "bank_transfer"].includes(raw)) return "transfer";
  if (["membership", "membresia", "membresía"].includes(raw)) return "membership";
  if (["loyalty_reward", "loyalty", "fidelidad", "reward", "recompensa"].includes(raw)) return "loyalty_reward";
  if (["courtesy", "cortesia", "cortesía", "free", "gratis"].includes(raw)) return "courtesy";
  if (["other", "otro"].includes(raw)) return "other";
  return "unknown";
}

function isCoveredPaymentMethod(value) {
  return ["membership", "loyalty_reward", "courtesy"].includes(normalizePaymentMethod(value));
}

function isCashLikePaymentMethod(value) {
  return ["cash", "yappy", "card", "transfer", "other"].includes(normalizePaymentMethod(value));
}

function normalizeCashMethod(value, fallback = "unknown") {
  const method = normalizePaymentMethod(value);
  return isCashLikePaymentMethod(method) ? method : fallback;
}

function getBookingTipPaymentMethod(b = {}, paymentMethod = "unknown") {
  const explicit = b?.tipPaymentMethod || b?.payment?.tipPaymentMethod || b?.workerTipPaymentMethod || b?.payment?.workerTipPaymentMethod || b?.tipMethod || b?.payment?.tipMethod || "";
  if (explicit) return normalizeCashMethod(explicit, "unknown");
  const main = normalizePaymentMethod(paymentMethod || b?.paymentMethod || b?.payment?.method);
  return isCashLikePaymentMethod(main) ? main : "unknown";
}

function coveredMethodLabel(value) {
  const method = normalizePaymentMethod(value);
  if (method === "membership") return "Membresía";
  if (method === "loyalty_reward") return "Fidelidad";
  if (method === "courtesy") return "Cortesía";
  return "Cubierto";
}

function getBookingCollectedAmount(b, expectedRevenue = 0) {
  // LOOP PHASE 2.5C — canonical collected amount.
  const lifecycle = bookingLifecycleFromRecord({ ...(b || {}), expectedServiceAmount: expectedRevenue || b?.expectedServiceAmount });
  return lifecycle.totalCollectedAmount || 0;
}


function bookingFinanceHasExplicitValue(value) {
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function bookingHasPaymentTrace(b = {}) {
  const values = [
    b?.paymentStatus,
    b?.payment?.status,
    b?.finance?.paymentStatus,
    b?.paymentMethod,
    b?.payment?.method,
    b?.finance?.paymentMethod,
    b?.serviceCollectedAmount,
    b?.payment?.serviceCollectedAmount,
    b?.amountCharged,
    b?.payment?.amountCharged,
    b?.paidAmount,
    b?.payment?.paidAmount,
    b?.paymentAmount,
    b?.payment?.paymentAmount,
    b?.amountPaid,
    b?.payment?.amountPaid,
    b?.totalCollectedAmount,
    b?.grossCollectedAmount,
    b?.payment?.totalCollectedAmount,
    b?.payment?.grossCollectedAmount,
    b?.coveredAmount,
    b?.payment?.coveredAmount,
    b?.benefitCoveredAmount,
    b?.membershipCoveredAmount,
    b?.payment?.membershipCoveredAmount,
    b?.refundAmount,
    b?.refundedAmount,
    b?.payment?.refundAmount,
    b?.payment?.refundedAmount,
  ];
  return values.some(bookingFinanceHasExplicitValue);
}

function getBookingExplicitGrossCollectedAmount(b = {}) {
  return numberFrom(
    b?.totalCollectedAmount ??
    b?.grossCollectedAmount ??
    b?.payment?.totalCollectedAmount ??
    b?.payment?.grossCollectedAmount ??
    0
  );
}

function getBookingRefundAmount(b = {}) {
  return numberFrom(
    b?.refundAmount ??
    b?.refundedAmount ??
    b?.payment?.refundAmount ??
    b?.payment?.refundedAmount ??
    0
  );
}


function getBookingDiscountAmount(b) {
  return numberFrom(b?.discountAmount ?? b?.discount ?? b?.payment?.discountAmount ?? 0);
}

function getBookingTipAmount(b) {
  return numberFrom(b?.tipAmount ?? b?.tip ?? b?.payment?.tipAmount ?? 0);
}

function getBookingProductSalesTotal(b) {
  const direct = numberFrom(b?.productSalesTotalAmount ?? b?.retailSalesTotalAmount ?? b?.payment?.productSalesTotalAmount ?? 0);
  if (direct > 0) return direct;
  const rows = Array.isArray(b?.productSales) ? b.productSales : Array.isArray(b?.retailSales) ? b.retailSales : [];
  return rows.reduce((sum, row) => sum + numberFrom(row?.lineTotal ?? (numberFrom(row?.quantity ?? row?.qty) * numberFrom(row?.unitPrice ?? row?.price))), 0);
}

function getBookingProductSalesCost(b) {
  const direct = numberFrom(b?.productSalesCostAmount ?? b?.retailCostAmount ?? 0);
  if (direct > 0) return direct;
  const rows = Array.isArray(b?.productSales) ? b.productSales : Array.isArray(b?.retailSales) ? b.retailSales : [];
  return rows.reduce((sum, row) => sum + numberFrom(row?.costTotal ?? (numberFrom(row?.quantity ?? row?.qty) * numberFrom(row?.unitCost ?? row?.cost))), 0);
}

function getBookingProductSalesPayout(b) {
  const direct = numberFrom(b?.productSalesPayoutAmount ?? b?.retailPayoutAmount ?? b?.payment?.productSalesPayoutAmount ?? 0);
  if (direct > 0) return direct;
  const rows = Array.isArray(b?.productSales) ? b.productSales : Array.isArray(b?.retailSales) ? b.retailSales : [];
  return rows.reduce((sum, row) => sum + numberFrom(row?.workerPayoutAmount ?? row?.payoutAmount), 0);
}

function getBookingInventoryCost(b) {
  // Costos de materiales usados por el servicio.
  // Importante: NO incluye costo de productos vendidos; esos viven en getBookingProductSalesCost()
  // para evitar doble conteo en neto negocio.
  const direct = numberFrom(b?.inventoryCostAmount ?? b?.materialCostAmount ?? b?.inventoryCost ?? 0);
  const rows = Array.isArray(b?.inventoryConsumed) ? b.inventoryConsumed : [];
  return direct > 0 ? direct : rows.reduce((sum, row) => sum + numberFrom(row?.totalCost ?? (numberFrom(row?.quantityUsed) * numberFrom(row?.unitCost))), 0);
}

function getBookingServiceCommissionAmount(b, expectedRevenue = 0) {
  const direct = numberFrom(b?.workerCommissionAmount ?? b?.commissionAmount ?? b?.payment?.workerCommissionAmount ?? b?.payment?.commissionAmount ?? 0);
  if (direct > 0) return direct;
  const productSalesTotal = getBookingProductSalesTotal(b);
  const serviceRevenue = Math.max(0, expectedRevenue - productSalesTotal);
  const type = String(b?.workerCommissionType || b?.commissionType || b?.payment?.workerCommissionType || "none").trim().toLowerCase();
  const value = numberFrom(b?.workerCommissionValue ?? b?.commissionValue ?? b?.payment?.workerCommissionValue ?? 0);
  if (type === "percentage" && value > 0) return Math.max(0, serviceRevenue * (value / 100));
  if (type === "fixed" && value > 0) return Math.max(0, value);
  return 0;
}

function getBookingWorkerTipAmount(b) {
  return numberFrom(b?.workerTipAmount ?? b?.payment?.workerTipAmount ?? b?.tipAmount ?? b?.tip ?? b?.payment?.tipAmount ?? 0);
}

function getInventoryUsageRows(service) {
  return (Array.isArray(service?.inventoryUsage) ? service.inventoryUsage : Array.isArray(service?.inventoryConsumption) ? service.inventoryConsumption : [])
    .map((row) => ({ itemId: String(row?.itemId || row?.inventoryItemId || "").trim(), quantityUsed: numberFrom(row?.quantityUsed ?? row?.quantity ?? row?.qty) }))
    .filter((row) => row.itemId && row.quantityUsed > 0);
}

function inventoryItemName(item = {}) {
  return String(item.name || item.title || item.label || item.id || "Producto").trim();
}

function serviceMaterialCostFromInventory(service = {}, inventoryItems = []) {
  const itemMap = new Map((Array.isArray(inventoryItems) ? inventoryItems : []).map((item) => [String(item.id), item]));
  return getInventoryUsageRows(service).reduce((sum, row) => {
    const item = itemMap.get(String(row.itemId));
    const unitCost = numberFrom(item?.unitCost ?? item?.cost ?? 0);
    return sum + unitCost * numberFrom(row.quantityUsed);
  }, 0);
}

function serviceMaterialDetailRows(service = {}, inventoryItems = []) {
  const itemMap = new Map((Array.isArray(inventoryItems) ? inventoryItems : []).map((item) => [String(item.id), item]));
  return getInventoryUsageRows(service).map((row) => {
    const item = itemMap.get(String(row.itemId)) || {};
    const unitCost = numberFrom(item.unitCost ?? item.cost ?? 0);
    const quantityUsed = numberFrom(row.quantityUsed);
    return {
      ...row,
      itemName: inventoryItemName(item),
      unit: item.unit || "un.",
      unitCost,
      lineCost: unitCost * quantityUsed,
      stock: numberFrom(item.currentStock ?? item.stock ?? item.quantity ?? 0),
    };
  });
}

function serviceMaterialMargin(service = {}, inventoryItems = []) {
  const price = numberFrom(service.price ?? service.basePrice ?? service.servicePrice ?? 0);
  const materialCost = serviceMaterialCostFromInventory(service, inventoryItems);
  const margin = Math.max(0, price - materialCost);
  return {
    price,
    materialCost,
    margin,
    marginPct: price > 0 ? Math.round((margin / price) * 100) : null,
    rows: getInventoryUsageRows(service),
  };
}

function serviceInventoryDisplayName(service = {}) {
  return String(service.name || service.title || service.label || service.serviceName || "Servicio").trim() || "Servicio";
}

function serviceInventoryConnectionSummary(service = {}, inventoryItems = [], MONEY = regionMoney()) {
  const usageRows = getInventoryUsageRows(service);
  const itemMap = new Map((Array.isArray(inventoryItems) ? inventoryItems : []).map((item) => [String(item.id), normalizeInventoryItem(item)]));
  const rows = usageRows.map((usage) => {
    const item = itemMap.get(String(usage.itemId));
    const quantityUsed = numberFrom(usage.quantityUsed);
    const unitCost = numberFrom(item?.unitCost ?? item?.cost ?? 0);
    const stock = numberFrom(item?.currentStock ?? item?.stock ?? item?.quantity ?? 0);
    const minStock = numberFrom(item?.minStock ?? item?.minimumStock ?? item?.reorderPoint ?? 0);
    const missing = !item;
    const insufficient = Boolean(item) && stock < quantityUsed;
    const stockAfter = missing ? 0 : Math.max(0, stock - quantityUsed);
    const nearMinimum = Boolean(item) && minStock > 0 && stockAfter <= minStock && !insufficient;
    return {
      ...usage,
      item,
      itemName: item ? inventoryItemName(item) : String(usage.itemId || "Producto no encontrado"),
      unit: item?.unit || "un.",
      quantityUsed,
      unitCost,
      lineCost: quantityUsed * unitCost,
      stock,
      minStock,
      stockAfter,
      missing,
      insufficient,
      nearMinimum,
    };
  });

  const price = numberFrom(service.price ?? service.basePrice ?? service.servicePrice ?? 0);
  const materialCost = rows.reduce((sum, row) => sum + numberFrom(row.lineCost), 0);
  const margin = Math.max(0, price - materialCost);
  const marginPct = price > 0 ? Math.round((margin / price) * 100) : null;
  const missingCount = rows.filter((row) => row.missing).length;
  const insufficientCount = rows.filter((row) => row.insufficient).length;
  const nearMinimumCount = rows.filter((row) => row.nearMinimum).length;

  let tone = "muted";
  let label = "Sin materiales";
  let hint = "Conecta productos para calcular costo y margen.";

  if (rows.length) {
    tone = "ok";
    label = "Conectado";
    hint = `${rows.length} material${rows.length === 1 ? "" : "es"} · costo ${MONEY.format(materialCost)}`;
  }
  if (rows.length && marginPct !== null && marginPct < 45) {
    tone = "warning";
    label = "Margen bajo";
    hint = `Margen estimado ${marginPct}%. Conviene revisar costo o precio.`;
  }
  if (nearMinimumCount > 0) {
    tone = "warning";
    label = "Stock sensible";
    hint = `${nearMinimumCount} material${nearMinimumCount === 1 ? "" : "es"} quedaría cerca del mínimo.`;
  }
  if (missingCount > 0 || insufficientCount > 0) {
    tone = "critical";
    label = missingCount > 0 ? "Material faltante" : "Stock insuficiente";
    hint = missingCount > 0
      ? `${missingCount} material${missingCount === 1 ? "" : "es"} ya no existe en inventario.`
      : `${insufficientCount} material${insufficientCount === 1 ? "" : "es"} sin stock suficiente.`;
  }

  return {
    service,
    id: String(service.id || service.serviceId || service.name || ""),
    name: serviceInventoryDisplayName(service),
    rows,
    connected: rows.length > 0,
    materialCost,
    price,
    margin,
    marginPct,
    missingCount,
    insufficientCount,
    nearMinimumCount,
    tone,
    label,
    hint,
  };
}

function serviceInventorySearchText(item = {}) {
  return [
    item.name,
    item.label,
    item.hint,
    item.service?.category,
    item.service?.type,
    ...(Array.isArray(item.rows) ? item.rows.map((row) => row.itemName) : []),
  ].filter(Boolean).join(" ").toLowerCase();
}

function estimateWorkerPayout(b, expectedRevenue = 0) {
  const closedPayout = closedEventWorkerPayout(b);
  if (closedPayout !== null) return closedPayout;
  // Canonical: workerPayoutAmount / workerPayoutEstimatedAmount already represents the full payout
  // when created by BookingCreateModal (service commission + tip + product commission).
  // Do not add product payout again, otherwise product commission is double-counted.
  const totalDirect = numberFrom(
    b?.totalWorkerPayoutAmount ??
    b?.workerPayoutAmount ??
    b?.workerPayoutEstimatedAmount ??
    b?.workerPayout ??
    b?.payment?.totalWorkerPayoutAmount ??
    b?.payment?.workerPayoutAmount ??
    b?.payment?.workerPayoutEstimatedAmount ??
    0
  );
  if (totalDirect > 0) return totalDirect;
  return getBookingServiceCommissionAmount(b, expectedRevenue) + getBookingWorkerTipAmount(b) + getBookingProductSalesPayout(b);
}

function paymentMethodLabel(value) {
  return PAYMENT_METHOD_LABELS[normalizePaymentMethod(value)] || "Sin método";
}

function getWorkerId(b) {
  return String(b?.employeeId || b?.workerId || b?.resourceId || b?.assignedEmployeeId || "").trim();
}

function getWorkerName(b, employeeMap) {
  const wid = getWorkerId(b);
  const emp = wid ? employeeMap.get(wid) : null;
  return b?.employeeName || b?.workerName || b?.assignedEmployeeName || emp?.name || emp?.displayName || wid || "Sin trabajador";
}

function getCustomerKey(b) {
  return String(
    b?.customerId ||
    b?.clientId ||
    b?.customerEmail ||
    b?.clientEmail ||
    b?.email ||
    b?.customerPhone ||
    b?.clientPhone ||
    b?.phone ||
    b?.customerSnapshot?.email ||
    b?.customerSnapshot?.phone ||
    b?.customerName ||
    b?.clientName ||
    ""
  ).trim().toLowerCase();
}

function getCustomerAge(b) {
  const direct = numberFrom(b?.customerAge ?? b?.age ?? b?.customerSnapshot?.age);
  if (direct > 0 && direct < 120) return direct;

  const birth = b?.customerBirthDate || b?.birthDate || b?.customerSnapshot?.birthDate || b?.customerSnapshot?.dob || b?.dob;
  const bd = safeDate(birth);
  if (!bd) return null;

  const now = new Date();
  let age = now.getFullYear() - bd.getFullYear();
  const m = now.getMonth() - bd.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < bd.getDate())) age -= 1;
  return age > 0 && age < 120 ? age : null;
}

function ageBucket(age) {
  if (!age) return "Sin edad";
  if (age < 18) return "Menor de 18";
  if (age <= 24) return "18-24";
  if (age <= 34) return "25-34";
  if (age <= 44) return "35-44";
  if (age <= 54) return "45-54";
  return "55+";
}

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function startOfMonth(date) {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endExclusiveFromStart(start, period) {
  const d = new Date(start);
  if (period === "day") d.setDate(d.getDate() + 1);
  else if (period === "week") d.setDate(d.getDate() + 7);
  else if (period === "month") d.setMonth(d.getMonth() + 1);
  else d.setFullYear(d.getFullYear() + 50);
  return d;
}

function formatPeriodLabel(period, anchor) {
  try {
    if (period === "day") return anchor.toLocaleDateString("es-PA", { weekday: "long", day: "numeric", month: "short", year: "numeric" });
    if (period === "week") {
      const start = startOfWeek(anchor);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return `${start.toLocaleDateString("es-PA", { day: "numeric", month: "short" })} - ${end.toLocaleDateString("es-PA", { day: "numeric", month: "short" })}`;
    }
    if (period === "month") return anchor.toLocaleDateString("es-PA", { month: "long", year: "numeric" });
    return "Todo el historial";
  } catch (_) {
    return "";
  }
}

function formatCompactDate(dateKey) {
  try {
    const d = new Date(`${dateKey}T00:00:00`);
    return d.toLocaleDateString("es-PA", { day: "numeric", month: "short" });
  } catch (_) {
    return String(dateKey || "").slice(5);
  }
}

function normalizeStatus(status) {
  const s = String(status || "").trim().toLowerCase();
  if (["confirmed", "confirmada", "approved", "accepted", "in_progress", "completed", "complete", "done", "completada", "attended", "atendida", "atendido"].includes(s)) return "confirmed";
  if (["pending", "pendiente", "to_confirm", "por_confirmar"].includes(s)) return "pending";
  if (["cancelled", "canceled", "cancelada", "rejected", "declined", "rechazada"].includes(s)) return "cancelled";
  if (["no_show", "no-show", "noshow", "no asistió", "no_asistio"].includes(s)) return "no_show";
  return s || "confirmed";
}


// LOOP REPORTS V3 A4.2B — reusable finance snapshot + matched-period comparison semantics.
function buildFinanceV3Snapshot({ bookings = [], start = null, end = null, workerFilter = "all", serviceMap, employeeMap }) {
  const allInPeriod = (bookings || [])
    .map((b) => ({ ...b, __date: getBookingDate(b), __createdDate: getCreatedDate(b), __status: normalizeStatus(b.status || b.confirmationStatus) }))
    .filter((b) => {
      const d = b.__date || b.__createdDate;
      if (!d) return false;
      if (start && end && !(d >= start && d < end)) return false;
      if (workerFilter !== "all") return String(getWorkerId(b)) === String(workerFilter);
      return true;
    });

  const operationalBookings = allInPeriod.filter((b) => b.__status !== "deleted");
  const revenueBookings = operationalBookings.filter((b) => ["confirmed", "no_show"].includes(b.__status) || eventFinanceSummary(b)?.hasActivity);
  const unpaidBookings = [];
  const paymentMismatchBookings = [];
  const financeRows = [];
  const cashByMethod = new Map();
  const coveredByMethod = new Map();
  const revenueByService = new Map();
  const countByService = new Map();
  const revenueByWorker = new Map();
  const countByWorker = new Map();
  const materialCostByService = new Map();

  let revenue = 0;
  let collectedRevenue = 0;
  let pendingRevenue = 0;
  let paymentMismatchRevenue = 0;
  let trackedRevenueBase = 0;
  let untrackedExpectedRevenue = 0;
  let coveredRevenueTotal = 0;
  let totalTips = 0;
  let workerPayoutTotal = 0;
  let materialCostTotal = 0;
  let productSalesRevenueTotal = 0;
  let productSalesCostTotal = 0;

  operationalBookings.forEach((b) => {
    const eventFinance = eventFinanceSummary(b);
    const amount = eventFinance ? eventFinance.amount : getBookingRevenue(b, serviceMap);
    const serviceName = getServiceName(b, serviceMap);
    const workerName = getWorkerName(b, employeeMap);
    const isRevenueBooking = ["confirmed", "no_show"].includes(b.__status) || eventFinance?.hasActivity;
    if (!isRevenueBooking) return;

    const dayKey = (b.__date || b.__createdDate)?.toISOString().slice(0, 10);
    const rawPaymentStatus = String(b?.paymentStatus ?? b?.payment?.status ?? b?.finance?.paymentStatus ?? "").trim();
    const rawPaymentMethod = String(b?.paymentMethod ?? b?.payment?.method ?? b?.finance?.paymentMethod ?? "").trim();
    const paymentStatus = normalizePaymentStatus(rawPaymentStatus, b);
    const paymentMethod = normalizePaymentMethod(rawPaymentMethod);
    const paymentHasTrace = bookingHasPaymentTrace(b);
    const paymentIsCovered = paymentStatus === "covered" || isCoveredPaymentMethod(paymentMethod);
    const paymentIsRefunded = !eventFinance && (paymentStatus === "refunded" || getBookingRefundAmount(b) > 0);
    const paymentIsLoyaltyReward = paymentIsCovered && paymentMethod === "loyalty_reward";
    const paymentIsMembership = paymentIsCovered && paymentMethod === "membership";
    const lifecycle = bookingLifecycleFromRecord(b || {});
    const serviceCollectedAmount = paymentIsCovered ? 0 : Math.max(0, numberFrom(lifecycle?.serviceCollectedAmount));
    const coveredServiceAmount = paymentIsCovered ? Math.max(0, numberFrom(b?.coveredAmount ?? b?.payment?.coveredAmount ?? b?.benefitCoveredAmount ?? b?.membershipCoveredAmount ?? b?.payment?.membershipCoveredAmount ?? amount)) : 0;
    const loyaltyCoveredAmount = paymentIsLoyaltyReward ? Math.max(0, numberFrom(b?.loyaltyBusinessSubsidyAmount ?? b?.payment?.loyaltyBusinessSubsidyAmount ?? b?.loyaltyBusinessAbsorbedAmount ?? coveredServiceAmount)) : 0;
    const tipAmount = getBookingTipAmount(b);
    const tipCashMethod = getBookingTipPaymentMethod(b, paymentMethod);
    const cashMethod = normalizeCashMethod(paymentMethod, "unknown");
    const serviceCommissionAmount = getBookingServiceCommissionAmount(b, amount);
    const workerTipAmount = getBookingWorkerTipAmount(b);
    const productSalesPayout = getBookingProductSalesPayout(b);
    const workerPayout = estimateWorkerPayout(b, amount);
    const payoutStatus = String(b.workerPayoutStatus || b?.payment?.workerPayoutStatus || "pending").trim().toLowerCase() === "paid" ? "paid" : "pending";
    const inventoryCost = getBookingInventoryCost(b);
    const productSalesTotal = getBookingProductSalesTotal(b);
    const productSalesCost = getBookingProductSalesCost(b);
    const serviceRevenueAmount = Math.max(0, amount - productSalesTotal);
    const explicitGrossCollectedAmount = getBookingExplicitGrossCollectedAmount(b);
    const canonicalGrossCollectedAmount = paymentIsCovered ? 0 : Math.max(0, getBookingCollectedAmount(b, amount));
    const grossSalesCollectedCandidate = Math.max(
      0,
      (explicitGrossCollectedAmount > 0
        ? explicitGrossCollectedAmount
        : ["paid", "partial"].includes(paymentStatus)
          ? canonicalGrossCollectedAmount
          : serviceCollectedAmount) - tipAmount
    );
    const collectedAmount = eventFinance ? eventFinance.net : paymentHasTrace && !paymentIsCovered && !paymentIsRefunded
      ? Math.min(amount, grossSalesCollectedCandidate)
      : 0;
    const pendingAmount = eventFinance ? eventFinance.pending : paymentHasTrace && !paymentIsCovered && !paymentIsRefunded && ["pending", "partial"].includes(paymentStatus)
      ? Math.max(0, amount - collectedAmount)
      : 0;
    const paymentMismatchAmount = eventFinance ? 0 : paymentHasTrace && !paymentIsCovered && !paymentIsRefunded && paymentStatus === "paid"
      ? Math.max(0, amount - collectedAmount)
      : 0;
    const untrackedExpectedAmount = !paymentHasTrace && !paymentIsCovered && !paymentIsRefunded ? amount : 0;
    const coveredTrackedAmount = paymentIsCovered ? Math.min(amount, coveredServiceAmount || amount) : 0;
    const trackedBaseAmount = eventFinance ? eventFinance.net + eventFinance.pending : paymentHasTrace && !paymentIsCovered && !paymentIsRefunded ? amount : 0;
    const cashBaseAmount = collectedAmount;
    const serviceCashAmount = Math.min(serviceRevenueAmount, cashBaseAmount);
    const productCashAmount = Math.max(0, cashBaseAmount - serviceCashAmount);
    const membershipBusinessAmount = paymentIsMembership
      ? Math.max(0, numberFrom(
        b?.membershipBusinessIncomeAmount ??
        b?.payment?.membershipBusinessIncomeAmount ??
        b?.membershipCoveredAmount ??
        b?.payment?.membershipCoveredAmount ??
        coveredServiceAmount
      ))
      : Math.max(0, numberFrom(b?.membershipBusinessIncomeAmount ?? b?.payment?.membershipBusinessIncomeAmount ?? 0));
    const serviceRevenueDisplayAmount = Math.max(0, serviceRevenueAmount - membershipBusinessAmount);
    const totalBusinessAmount = serviceRevenueDisplayAmount + productSalesTotal + membershipBusinessAmount;
    const membershipWorkerIncomeAmount = paymentIsMembership
      ? Math.max(0, numberFrom(
        b?.membershipWorkerIncomeAmount ??
        b?.payment?.membershipWorkerIncomeAmount ??
        b?.membershipWorkerPayoutAmount ??
        b?.payment?.membershipWorkerPayoutAmount ??
        Math.max(0, workerPayout - productSalesPayout - workerTipAmount)
      ))
      : Math.max(0, numberFrom(b?.membershipWorkerIncomeAmount ?? b?.payment?.membershipWorkerIncomeAmount ?? 0));
    const serviceCommissionDisplayAmount = Math.max(0, serviceCommissionAmount - membershipWorkerIncomeAmount);
    const loyaltyWorkerPayoutImpact = paymentIsLoyaltyReward ? workerPayout : 0;
    const loyaltyMaterialCostImpact = paymentIsLoyaltyReward ? inventoryCost : 0;
    const loyaltyProductCostImpact = paymentIsLoyaltyReward ? productSalesCost : 0;
    const loyaltyBusinessCashImpact = paymentIsLoyaltyReward
      ? numberFrom(b?.loyaltyBusinessCashImpactAmount ?? b?.payment?.loyaltyBusinessCashImpactAmount ?? -(loyaltyWorkerPayoutImpact + loyaltyMaterialCostImpact + loyaltyProductCostImpact))
      : 0;
    const estimatedBusinessNet = cashBaseAmount + tipAmount - workerPayout - inventoryCost - productSalesCost;

    collectedRevenue += collectedAmount;
    pendingRevenue += pendingAmount;
    paymentMismatchRevenue += paymentMismatchAmount;
    trackedRevenueBase += trackedBaseAmount;
    untrackedExpectedRevenue += untrackedExpectedAmount;
    coveredRevenueTotal += coveredTrackedAmount;
    totalTips += tipAmount;
    workerPayoutTotal += workerPayout;
    materialCostTotal += inventoryCost;
    productSalesRevenueTotal += productSalesTotal;
    productSalesCostTotal += productSalesCost;
    revenue += amount;

    groupAdd(revenueByService, serviceName, amount);
    groupAdd(countByService, serviceName, 1);
    groupAdd(revenueByWorker, workerName, amount);
    groupAdd(countByWorker, workerName, 1);
    groupAdd(materialCostByService, serviceName, inventoryCost);

    if (cashBaseAmount > 0) {
      const currentCash = cashByMethod.get(cashMethod) || { method: cashMethod, label: paymentMethodLabel(cashMethod), service: 0, product: 0, tip: 0, total: 0 };
      currentCash.service += serviceCashAmount;
      currentCash.product += productCashAmount;
      currentCash.total += cashBaseAmount;
      cashByMethod.set(cashMethod, currentCash);
    }
    if (tipAmount > 0) {
      const currentTip = cashByMethod.get(tipCashMethod) || { method: tipCashMethod, label: paymentMethodLabel(tipCashMethod), service: 0, product: 0, tip: 0, total: 0 };
      currentTip.tip += tipAmount;
      currentTip.total += tipAmount;
      cashByMethod.set(tipCashMethod, currentTip);
    }
    if (paymentIsCovered && coveredServiceAmount > 0) {
      groupAdd(coveredByMethod, coveredMethodLabel(paymentMethod), coveredServiceAmount);
    }

    financeRows.push({
      id: b.id || b.bookingId || `${dayKey}-${financeRows.length}`,
      date: b.__date || b.__createdDate,
      client: b.customerName || b.clientName || b.name || "Cliente",
      service: serviceName,
      worker: workerName,
      amount: totalBusinessAmount,
      serviceRevenueAmount,
      serviceRevenueDisplayAmount,
      productSalesAmount: productSalesTotal,
      membershipBusinessAmount,
      totalBusinessAmount,
      collected: collectedAmount,
      cashBaseAmount,
      cashReceivedAmount: collectedAmount + tipAmount,
      grossCollectedAmount: canonicalGrossCollectedAmount,
      paymentTracked: paymentHasTrace,
      untrackedExpectedAmount,
      trackedBaseAmount,
      coveredServiceAmount,
      coveredTrackedAmount,
      loyaltyCoveredAmount,
      paymentMethod,
      paymentIsLoyaltyReward,
      loyaltyWorkerPayoutImpact,
      loyaltyMaterialCostImpact,
      loyaltyProductCostImpact,
      loyaltyBusinessCashImpact,
      pending: pendingAmount,
      paymentMismatchAmount,
      tip: tipAmount,
      tipPaymentMethod: tipCashMethod,
      tipPaymentMethodLabel: paymentMethodLabel(tipCashMethod),
      workerTipAmount,
      serviceCommissionAmount,
      serviceCommissionDisplayAmount,
      membershipWorkerIncomeAmount,
      method: paymentMethodLabel(paymentMethod),
      status: paymentStatus,
      payout: workerPayout,
      payoutStatus,
      inventoryCost,
      productSalesTotal,
      productSalesCost,
      productSalesPayout,
      workerPaymentNotes: b.workerPaymentNotes || b?.payment?.workerPaymentNotes || "",
      net: estimatedBusinessNet,
    });

    if (pendingAmount > 0) unpaidBookings.push(b);
    if (paymentMismatchAmount > 0) paymentMismatchBookings.push(b);
  });

  return {
    revenue,
    revenueBookings,
    collectedRevenue,
    pendingRevenue,
    paymentMismatchRevenue,
    paymentMismatchBookings,
    trackedRevenueBase,
    untrackedExpectedRevenue,
    coveredRevenueTotal,
    totalTips,
    workerPayoutTotal,
    materialCostTotal,
    productSalesRevenueTotal,
    productSalesCostTotal,
    cashByMethod,
    coveredByMethod,
    revenueByService,
    countByService,
    revenueByWorker,
    countByWorker,
    materialCostByService,
    ticketAvg: revenueBookings.length ? revenue / revenueBookings.length : 0,
    unpaidBookings,
    financeRows: financeRows.sort((a, b) => (b.date?.getTime?.() || 0) - (a.date?.getTime?.() || 0)),
    businessNetRevenue: collectedRevenue + totalTips - workerPayoutTotal - materialCostTotal - productSalesCostTotal,
    collectionRate: trackedRevenueBase ? Math.round((collectedRevenue / trackedRevenueBase) * 100) : 0,
  };
}

function buildFinanceV3ComparisonScope(period, periodRange, anchorDate, business = {}) {
  const scope=buildFinanceV3WallComparisonScope(period, {...periodRange,start:periodRange?.start ? wallDate(periodRange.start,business) : null,end:periodRange?.end ? wallDate(periodRange.end,business) : null},anchorDate,business);
  for(const field of ['currentStart','currentEnd','previousStart','previousEnd']) if(scope[field])scope[field]=wallToInstant(scope[field],business);
  return scope;
}
function buildFinanceV3WallComparisonScope(period, periodRange, anchorDate, business = {}) {
  const start = periodRange?.start instanceof Date ? new Date(periodRange.start) : null;
  const end = periodRange?.end instanceof Date ? new Date(periodRange.end) : null;
  if (period === "all" || !start || !end || !Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start) {
    return { enabled: false, currentStart: start, currentEnd: end, previousStart: null, previousEnd: null, label: "", matchedToDate: false };
  }

  const now = wallDate(new Date(),business);
  if (start > now) {
    return { enabled: false, currentStart: start, currentEnd: end, previousStart: null, previousEnd: null, label: "", matchedToDate: false };
  }

  let currentEnd = new Date(end);
  let matchedToDate = false;

  if (period === "day") {
    const activeStart = startOfDaySafe(now);
    if (start.getTime() === activeStart.getTime() && now < currentEnd) {
      currentEnd = now;
      matchedToDate = true;
    }
  } else if (period === "week") {
    const activeStart = startOfWeek(now);
    if (start.getTime() === activeStart.getTime() && now < currentEnd) {
      currentEnd = now;
      matchedToDate = true;
    }
  } else if (period === "month") {
    const activeStart = startOfMonth(now);
    if (start.getTime() === activeStart.getTime() && now < currentEnd) {
      currentEnd = now;
      matchedToDate = true;
    }
  }

  let previousStart = null;
  let previousEnd = null;
  let label = "vs período anterior";

  if (period === "day") {
    previousStart = addDays(start, -1);
    previousEnd = matchedToDate ? new Date(previousStart.getTime() + (currentEnd.getTime() - start.getTime())) : new Date(start);
    label = matchedToDate ? "vs ayer · misma hora" : "vs día anterior";
  } else if (period === "week") {
    previousStart = addDays(start, -7);
    previousEnd = matchedToDate ? new Date(previousStart.getTime() + (currentEnd.getTime() - start.getTime())) : new Date(start);
    label = matchedToDate ? "vs semana anterior · mismo avance" : "vs semana anterior";
  } else if (period === "month") {
    previousStart = new Date(start);
    previousStart.setMonth(previousStart.getMonth() - 1);
    previousStart.setDate(1);
    previousStart.setHours(0, 0, 0, 0);
    previousEnd = matchedToDate
      ? new Date(Math.min(start.getTime(), previousStart.getTime() + (currentEnd.getTime() - start.getTime())))
      : new Date(start);
    label = matchedToDate ? "vs mes anterior · mismo avance" : "vs mes anterior";
  } else if (period === "custom") {
    const duration = end.getTime() - start.getTime();
    previousEnd = new Date(start);
    previousStart = new Date(start.getTime() - duration);
    label = "vs rango anterior";
  } else {
    return { enabled: false, currentStart: start, currentEnd, previousStart: null, previousEnd: null, label: "", matchedToDate: false };
  }

  return {
    enabled: Boolean(previousStart && previousEnd && previousEnd > previousStart),
    currentStart: start,
    currentEnd,
    previousStart,
    previousEnd,
    label,
    matchedToDate,
  };
}

function groupAdd(map, key, value = 1) {
  const safeKey = String(key || "Sin datos").trim() || "Sin datos";
  map.set(safeKey, (map.get(safeKey) || 0) + value);
}

function topEntry(map) {
  let best = null;
  for (const [key, value] of map.entries()) {
    if (!best || value > best.value) best = { key, value };
  }
  return best;
}

function percent(value, total) {
  if (!total) return 0;
  return Math.round((value / total) * 100);
}

function eventType(e) {
  return String(e?.type || e?.eventType || e?.name || e?.action || e?.event || "").trim().toLowerCase();
}

function eventDate(e) {
  return safeDate(e?.createdAt) || safeDate(e?.timestamp) || safeDate(e?.date) || safeDate(e?.occurredAt) || null;
}

function eventVisitorKey(e) {
  return String(e?.visitorId || e?.sessionId || e?.anonymousId || e?.userId || e?.uid || e?.ipHash || "").trim();
}

function eventSource(e) {
  const raw = String(e?.source || e?.utmSource || e?.utm_source || e?.referrerHost || e?.referrer || "").trim();
  if (!raw) return "Directo / desconocido";
  const lower = raw.toLowerCase();
  if (lower.includes("instagram")) return "Instagram";
  if (lower.includes("facebook")) return "Facebook";
  if (lower.includes("google")) return "Google";
  if (lower.includes("whatsapp") || lower.includes("wa.me")) return "WhatsApp";
  if (lower.includes("loop")) return "Loop";
  return raw.replace(/^https?:\/\//, "").split("/")[0].slice(0, 32);
}

function isEventType(e, names = []) {
  const t = eventType(e);
  return names.includes(t);
}

function ownerFriendlyTrafficLabel(key) {
  const normalized = String(key || "").trim().toLowerCase();
  const labels = {
    business_page_view: "Vio tu página",
    page_view: "Vio tu página",
    view: "Vio tu página",
    business_view: "Vio tu página",
    profile_view: "Vio tu página",
    booking_opened: "Abrió el formulario de reserva",
    booking_open: "Abrió el formulario de reserva",
    booking_started: "Empezó una reserva",
    reserve_click: "Tocó reservar",
    book_now_click: "Tocó reservar",
    service_clicked: "Tocó un servicio",
    service_click: "Tocó un servicio",
    booking_completed: "Completó una reserva",
    booking_created: "Completó una reserva",
    reservation_created: "Completó una reserva",
  };
  return labels[normalized] || "Interacción";
}

function trafficReadinessText(hasTracking, completedEvents) {
  if (!hasTracking) return "Empezará a mostrarse cuando haya visitas nuevas en la página pública.";
  if (!completedEvents) return "Ya estamos midiendo visitas e intención. Las reservas completadas aparecerán cuando un cliente finalice una reserva.";
  return "Tráfico, intención y reservas completadas medidos desde la página pública.";
}

function MiniTrend({ data, maxRevenue }) {
 const MONEY=useRegionMoney();

  if (!data.length) return <div className="ba-empty slim">{t("Sin tendencia para este filtro.")}</div>;
  return (
    <div className="ba-trend" role="img" aria-label={t("Tendencia de reservas e ingresos")}>
      {data.map((item) => {
        const h = maxRevenue > 0 ? Math.max(8, Math.round((item.revenue / maxRevenue) * 100)) : item.count ? 18 : 8;
        return (
          <div className="ba-trend-col" key={item.key} title={`${item.key}: ${MONEY.format(item.revenue)} · ${item.count} reservas`}>
            <i style={{ height: `${h}%` }} />
            <span>{formatCompactDate(item.key)}</span>
          </div>
        );
      })}
    </div>
  );
}

function OverviewRevenueLine({ data = [], maxRevenue = 0, label = "Ingresos por día", bestLabel = "Mejor día" }) {
 const MONEY=useRegionMoney();

  const rows = Array.isArray(data) ? data : [];
  if (!rows.length) {
    return (
      <div className="ba-owner-empty-chart">
        <div aria-hidden="true">↗</div>
        <strong>{t("Sin tendencia todavía")}</strong>
        <span>{t("Cuando entren reservas en este periodo, verás aquí la evolución de ingresos.")}</span>
      </div>
    );
  }

  const width = 720;
  const height = 250;
  const padX = 26;
  const padTop = 24;
  const padBottom = 34;
  const safeMax = Math.max(1, maxRevenue || rows.reduce((max, item) => Math.max(max, numberFrom(item.revenue)), 0));
  const usableW = width - padX * 2;
  const usableH = height - padTop - padBottom;
  const points = rows.map((item, index) => {
    const x = rows.length === 1 ? width / 2 : padX + (index / Math.max(1, rows.length - 1)) * usableW;
    const y = padTop + usableH - (Math.max(0, numberFrom(item.revenue)) / safeMax) * usableH;
    return { ...item, x, y };
  });
  const line = points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const area = `${padX},${height - padBottom} ${line} ${width - padX},${height - padBottom}`;
  const best = points.reduce((acc, item) => (numberFrom(item.revenue) > numberFrom(acc?.revenue) ? item : acc), points[0]);

  return (
    <div className="ba-owner-line-chart" role="img" aria-label={label}>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id="baOwnerRevenueFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#14b8a6" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((step) => (
          <line key={step} x1={padX} x2={width - padX} y1={padTop + usableH * step} y2={padTop + usableH * step} className="grid" />
        ))}
        <polygon points={area} className="area" />
        <polyline points={line} className="line" />
        {points.map((p, idx) => <circle key={`${p.key}-${idx}`} cx={p.x} cy={p.y} r={idx % 3 === 0 || p.key === best?.key ? 4.3 : 3.2} className={p.key === best?.key ? "best dot" : "dot"} />)}
      </svg>
      <div className="ba-owner-line-axis">
        {points.filter((_, idx) => idx === 0 || idx === Math.floor(points.length / 2) || idx === points.length - 1).map((item) => (
          <span key={`axis-${item.key}`}>{formatCompactDate(item.key)}</span>
        ))}
      </div>
      {best ? <div className="ba-owner-chart-pill">{bestLabel} · {formatCompactDate(best.key)} · {MONEY.format(best.revenue)}</div> : null}
    </div>
  );
}



function FinanceV3TrendChart({ rows = [], comparisonRows = [], grain = "day", comparisonLabel = "" }) {
 const MONEY=useRegionMoney();

  const data = Array.isArray(rows) ? rows : [];
  const comparison = Array.isArray(comparisonRows) ? comparisonRows : [];
  if (!data.length && !comparison.length) {
    return (
      <div className="ba-finance-v3-empty-chart">
        <strong>{t("Sin evolución financiera todavía")}</strong>
        <span>{t("Cuando existan ventas con fecha, Loop mostrará aquí ventas, cobros y contribución.")}</span>
      </div>
    );
  }

  const axisData = data.length ? data : comparison;
  const width = 820;
  const height = 220;
  const padX = 34;
  const padTop = 22;
  const padBottom = 34;
  const usableW = width - padX * 2;
  const usableH = height - padTop - padBottom;

  const allRows = data.concat(comparison);
  const maxValue = Math.max(
    0,
    ...allRows.flatMap((row) => [numberFrom(row.sales), numberFrom(row.collected), numberFrom(row.result)])
  );
  const minValue = Math.min(0, ...allRows.map((row) => numberFrom(row.result)));
  const span = Math.max(1, maxValue - minValue);
  const yFor = (value) => padTop + ((maxValue - numberFrom(value)) / span) * usableH;
  const baselineY = yFor(0);

  const pointsFor = (source, field) => source.map((row, index) => ({
    key: row.key,
    x: source.length === 1 ? width / 2 : padX + (index / Math.max(1, source.length - 1)) * usableW,
    y: yFor(row[field]),
  }));

  const salesPoints = pointsFor(data, "sales");
  const collectedPoints = pointsFor(data, "collected");
  const resultPoints = pointsFor(data, "result");
  const previousSalesPoints = pointsFor(comparison, "sales");
  const previousCollectedPoints = pointsFor(comparison, "collected");
  const previousResultPoints = pointsFor(comparison, "result");
  const poly = (points) => points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  const formatAxis = (key) => {
    try {
      const d = new Date(`${key}T12:00:00`);
      if (!Number.isFinite(d.getTime())) return key;
      if (grain === "month") return d.toLocaleDateString("es-PA", { month: "short", year: "2-digit" }).replace(".", "");
      return d.toLocaleDateString("es-PA", { day: "numeric", month: "short" }).replace(".", "");
    } catch (_) {
      return key;
    }
  };

  const axisRows = axisData.filter((_, index) => (
    index === 0 ||
    index === Math.floor((axisData.length - 1) / 2) ||
    index === axisData.length - 1
  ));

  return (
    <div className="ba-finance-v3-chart" role="img" aria-label={t("Evolución de ventas, cobros y contribución con comparación del período anterior")}>
      <div className="ba-finance-v3-chart-legend">
        <span className="sales">{t("Ventas") + " "}</span>
        <span className="collected">{t("Cobrado")}</span>
        <span className="result">{t("Contribución")}</span>
        {comparison.length ? <span className="previous">{t("Período anterior ·") + " "}{comparisonLabel || "comparación"}</span> : null}
        <span className="scale">{t("Escala") + " "}{MONEY.format(minValue)} – {MONEY.format(maxValue)}</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
        {[0, 0.25, 0.5, 0.75, 1].map((step) => (
          <line key={step} x1={padX} x2={width - padX} y1={padTop + usableH * step} y2={padTop + usableH * step} className="grid" />
        ))}
        {minValue < 0 ? <line x1={padX} x2={width - padX} y1={baselineY} y2={baselineY} className="zero" /> : null}
        {comparison.length ? (
          <>
            <polyline points={poly(previousSalesPoints)} className="sales-line comparison" />
            <polyline points={poly(previousCollectedPoints)} className="collected-line comparison" />
            <polyline points={poly(previousResultPoints)} className="result-line comparison" />
            {previousSalesPoints.map((point, index) => <circle key={`prev-sales-${point.key}-${index}`} cx={point.x} cy={point.y} r="3.2" className="sales-dot comparison-dot" />)}
            {previousCollectedPoints.map((point, index) => <circle key={`prev-collected-${point.key}-${index}`} cx={point.x} cy={point.y} r="3.2" className="collected-dot comparison-dot" />)}
            {previousResultPoints.map((point, index) => <circle key={`prev-result-${point.key}-${index}`} cx={point.x} cy={point.y} r="3.2" className="result-dot comparison-dot" />)}
          </>
        ) : null}
        {data.length ? (
          <>
            <polyline points={poly(salesPoints)} className="sales-line" />
            <polyline points={poly(collectedPoints)} className="collected-line" />
            <polyline points={poly(resultPoints)} className="result-line" />
          </>
        ) : null}
      </svg>
      <div className="ba-finance-v3-chart-axis">
        {axisRows.map((row) => <span key={row.key}>{formatAxis(row.key)}</span>)}
      </div>
    </div>
  );
}

function financeV3SignedMoney(value, MONEY = regionMoney()) {
  const amount = safeMoneyValue(value);
  if (Math.abs(amount) <= 0.005) return MONEY.format(0);
  return `${amount > 0 ? "+" : "−"} ${MONEY.format(Math.abs(amount))}`;
}

function financeV3SignedCount(value) {
  const count = Number(value || 0);
  if (!Number.isFinite(count) || count === 0) return "Sin cambio";
  return `${count > 0 ? "+" : "−"}${Math.abs(Math.round(count))}`;
}

function FinanceV3ComparisonDelta({ comparison = null, label = "" }) {
 const MONEY=useRegionMoney();

  if (!comparison) return null;
  return (
    <small
      className={`ba-finance-v3-delta ${comparison.tone || "neutral"}`}
      title={`${MONEY.format(comparison.current)} actual · ${MONEY.format(comparison.previous)} anterior`}
    >
      <strong>{comparison.text}</strong>
      <span>{label}{" " + t("· ant.") + " "}{MONEY.format(comparison.previous)}</span>
    </small>
  );
}

function MetricCard({ label, value, hint, tone = "neutral" }) {
  return (
    <article className={`ba-metric ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {hint ? <em>{hint}</em> : null}
    </article>
  );
}

// LOOP MEMBERSHIP DASHBOARD V2 R2 — frontend presentation helpers
function membershipAnalyticsDate(value, fallbackMs = 0) {
  try {
    if (value && typeof value.toDate === "function") return value.toDate();

    const rawSeconds =
      value && typeof value === "object"
        ? (value.seconds ?? value._seconds)
        : null;

    if (rawSeconds !== null && rawSeconds !== undefined && Number.isFinite(Number(rawSeconds))) {
      const rawNanos = Number(value.nanoseconds ?? value._nanoseconds ?? 0) || 0;
      return new Date(Number(rawSeconds) * 1000 + Math.floor(rawNanos / 1000000));
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

    if (typeof value === "number" && Number.isFinite(value) && value > 0) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) return d;
    }

    if (typeof value === "string" && value.trim()) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) return d;
    }

    if (Number.isFinite(Number(fallbackMs)) && Number(fallbackMs) > 0) {
      const d = new Date(Number(fallbackMs));
      if (!Number.isNaN(d.getTime())) return d;
    }
  } catch (_) {}

  return null;
}

function membershipFormatDate(value, fallbackMs = 0, { withTime = false } = {}) {
  const date = membershipAnalyticsDate(value, fallbackMs);
  if (!date) return "Fecha por confirmar";

  try {
    return date.toLocaleString("es-PA", withTime
      ? { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }
      : { day: "numeric", month: "short" });
  } catch (_) {
    return date.toISOString().slice(0, 10);
  }
}

function membershipMoney(value, currency = "USD") {
  const amount = numberFrom(value);
  const safeCurrency = String(currency || "USD").trim().toUpperCase() || "USD";

  try {
    return new Intl.NumberFormat("es-PA", {
      style: "currency",
      currency: safeCurrency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (_) {
    return `$${amount.toFixed(amount % 1 === 0 ? 0 : 2)}`;
  }
}

function membershipRenewalModeLabel(value) {
  const mode = String(value || "").trim().toLowerCase();
  if (mode === "automatic") return "Automática";
  if (mode === "ending") return "No renueva";
  return "No automática";
}

function membershipRenewalDateLabel(value) {
  const mode = String(value || "").trim().toLowerCase();
  if (mode === "automatic") return "Renueva";
  if (mode === "ending") return "Vigente hasta";
  return "Vence";
}

function membershipAutomaticRenewalCount(snapshot = {}) {
  return (Array.isArray(snapshot?.upcomingRenewals) ? snapshot.upcomingRenewals : [])
    .filter((row) => String(row?.renewalMode || "").trim().toLowerCase() === "automatic")
    .length;
}

function membershipRenewalAmountLabel(row = {}) {
  const amount = numberFrom(row?.price);
  if (amount <= 0) return "Sin cobro";
  return membershipMoney(amount, row?.currency || "USD");
}

function membershipAttentionLabel(row = {}) {
  const kind = String(row.kind || row.reason || "").trim().toLowerCase();
  if (kind === "payment_issue") return "Problema de pago";
  if (kind === "source_issue") return "Método de pago";
  if (kind === "reconciliation") return "Conciliación";
  if (kind === "grace") return "En período de gracia";
  if (kind === "cancel_at_period_end") return "Finaliza al cierre";
  if (kind === "unused") {
    return row.daysActive != null
      ? `Sin uso · ${row.daysActive} día${Number(row.daysActive) === 1 ? "" : "s"}`
      : "Sin uso";
  }
  if (row.label) return row.label;
  return "Revisar";
}

function membershipAttentionDetail(row = {}) {
  const planName = row.planName || "Membresía";
  const kind = String(row.kind || row.reason || "").trim().toLowerCase();
  const dateValue = row.renewalDate || row.endDate || row.currentPeriodEnd || row.nextRenewalAt || null;

  if (kind === "grace") {
    if (dateValue) return `${planName} · renovación ${membershipFormatDate(dateValue)}`;
    if (row.daysLeft != null) return `${planName} · renovación en ${row.daysLeft} día${Number(row.daysLeft) === 1 ? "" : "s"}`;
  }

  if (kind === "cancel_at_period_end") {
    if (dateValue) return `${planName} · finaliza ${membershipFormatDate(dateValue)}`;
    if (row.daysLeft != null) return `${planName} · finaliza en ${row.daysLeft} día${Number(row.daysLeft) === 1 ? "" : "s"}`;
  }

  if (kind === "unused" && row.daysActive != null) {
    return `${planName} · membresía activa sin uso`;
  }

  if (row.daysLeft != null) {
    return `${planName} · ${row.daysLeft === 0 ? "hoy" : `en ${row.daysLeft} día${Number(row.daysLeft) === 1 ? "" : "s"}`}`;
  }

  return planName;
}

function membershipActivityTypeLabel(value) {
  const type = String(value || "").trim().toLowerCase();
  if (type === "membership_renewed") return "Renovación";
  if (type === "membership_granted") return "Activación";
  if (type === "membership_expired") return "Vencimiento";
  if (type === "membership_cancelled") return "Cancelación";
  if (type === "credit_used") return "Crédito usado";
  if (type === "credit_adjusted") return "Ajuste de créditos";
  if (type === "discount_applied") return "Descuento";
  if (type === "unlimited_used") return "Uso ilimitado";
  return "Actividad";
}

function membershipDetailSearchKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function membershipDetailMatchesSearch(row = {}, query = "") {
  const q = membershipDetailSearchKey(query);
  if (!q) return true;
  return membershipDetailSearchKey([
    row.customerName,
    row.planName,
    row.title,
    row.serviceName,
    row.healthLabel,
    row.label,
    row.kind,
    row.type,
  ].filter(Boolean).join(" ")).includes(q);
}

function membershipRecordStatusLabel(row = {}) {
  const status = String(row.statusKey || row.status || "").trim().toLowerCase();
  if (status === "active") return "Activa";
  if (status === "grace") return "En gracia";
  if (status === "paused") return "Pausada";
  if (status === "expired") return "Finalizada";
  if (status === "cancelled") return "Cancelada";
  return row.statusLabel || "Histórica";
}

function membershipRecordStatusTone(row = {}) {
  const status = String(row.statusKey || row.status || "").trim().toLowerCase();
  if (status === "active") return "active";
  if (status === "grace") return "warning";
  if (status === "paused") return "paused";
  if (status === "cancelled") return "cancelled";
  return "historical";
}

function membershipRecordUsageLabel(row = {}) {
  const included = numberFrom(row.creditsIncluded);
  if (included <= 0) return "—";
  const used = Math.max(0, numberFrom(row.creditsUsed));
  return `${used}/${included}`;
}

function membershipRecordNextLabel(row = {}) {
  const status = String(row.statusKey || row.status || "").trim().toLowerCase();
  if (status !== "active" && status !== "grace") {
    return row.endedAt ? `Terminó ${membershipFormatDate(row.endedAt)}` : "Sin próxima fecha";
  }
  if (!row.nextDate && !row.renewalDate) return "Sin próxima fecha";
  const verb = membershipRenewalDateLabel(row.renewalMode);
  return `${verb} ${membershipFormatDate(row.nextDate || row.renewalDate)}`;
}

function membershipRecordValueLabel(row = {}) {
  const amount = numberFrom(row.price);
  if (amount <= 0) return "Sin cobro";
  return membershipMoney(amount, row.currency || "USD");
}

function membershipDetailFallbackRows(snapshot = {}, view = "", { search = "", filter = "all", rangeDays = 30 } = {}) {
  let rows = [];

  if (view === "memberships") rows = Array.isArray(snapshot?.membershipOverview) && snapshot.membershipOverview.length
    ? snapshot.membershipOverview
    : (Array.isArray(snapshot?.upcomingRenewals) ? snapshot.upcomingRenewals.map((row) => ({ ...row, status: "active", statusKey: row.healthState === "grace" ? "grace" : "active", startedAt: null, nextDate: row.renewalDate })) : []);
  else if (view === "calendar") rows = Array.isArray(snapshot?.upcomingRenewals) ? snapshot.upcomingRenewals : [];
  else if (view === "attention") rows = Array.isArray(snapshot?.needsAttention) ? snapshot.needsAttention : [];
  else if (view === "activity") rows = Array.isArray(snapshot?.activityRecent) ? snapshot.activityRecent : [];

  const safeFilter = String(filter || "all").trim().toLowerCase();
  const safeRangeDays = Number(rangeDays);
  const fromMs = safeRangeDays > 0 ? Date.now() - safeRangeDays * 86400000 : 0;

  return rows.filter((row) => {
    if (!membershipDetailMatchesSearch(row, search)) return false;

    if (view === "memberships") {
      if (safeFilter === "all") return true;
      if (safeFilter === "active") return String(row?.status || "").toLowerCase() === "active";
      return String(row?.statusKey || row?.status || "").toLowerCase() === safeFilter;
    }

    if (view === "calendar") {
      return safeFilter === "all" || String(row?.renewalMode || "").toLowerCase() === safeFilter;
    }

    if (view === "attention") {
      if (safeFilter === "all") return true;
      if (safeFilter === "critical") return String(row?.severity || "").toLowerCase() === "critical";
      return String(row?.kind || row?.reason || "").toLowerCase() === safeFilter;
    }

    if (view === "activity") {
      const ms = membershipAnalyticsDate(row?.createdAt, row?.sortMs)?.getTime() || 0;
      if (fromMs > 0 && ms > 0 && ms < fromMs) return false;
      return safeFilter === "all" || String(row?.type || row?.activityType || "").toLowerCase() === safeFilter;
    }

    return true;
  });
}

function membershipHealthIssueCount(snapshot = {}) {
  const health = snapshot?.healthSummary || {};
  return (
    numberFrom(health.paymentIssues) +
    numberFrom(health.sourceIssues) +
    numberFrom(health.reconciliation)
  );
}

function BarRow({ label, value, max, money = false, subtitle }) {
 const MONEY=useRegionMoney();

  const width = max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 0;
  return (
    <div className="ba-row">
      <div className="ba-row-top">
        <span>{label}</span>
        <strong>{money ? MONEY.format(value) : value}</strong>
      </div>
      {subtitle ? <small>{subtitle}</small> : null}
      <div className="ba-track"><i style={{ width: `${width}%` }} /></div>
    </div>
  );
}

function SimpleList({ items, max, money = false, empty = "Sin datos todavía." }) {
  if (!items.length) return <div className="ba-empty">{empty}</div>;
  return (
    <div className="ba-list-bars">
      {items.map(([label, value]) => (
        <BarRow key={label} label={label} value={value} max={max} money={money} />
      ))}
    </div>
  );
}

function StatusLine({ label, value, total, tone }) {
  return (
    <div className="ba-status-line">
      <span><i className={tone} />{label}</span>
      <strong>{value}</strong>
      <em>{percent(value, total)}%</em>
    </div>
  );
}



// LOOP CRM ADVANCED — helpers for Customer 360 + segmentation.
function crmNormalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function crmDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

function crmCustomerIdentityFromAny(raw = {}) {
  const phone = crmDigits(raw.phoneNormalized || raw.phone || raw.customerPhone || raw.clientPhone || raw.whatsapp || raw.whatsApp || "");
  const email = String(raw.emailNormalized || raw.email || raw.customerEmail || raw.clientEmail || "").trim().toLowerCase();
  const explicit = String(raw.customerId || raw.clientId || raw.id || raw.uid || "").trim();
  const name = crmNormalizeText(raw.name || raw.customerName || raw.clientName || raw.displayName || raw.fullName || "");
  if (phone) return `phone:${phone}`;
  if (email) return `email:${email}`;
  if (explicit) return `id:${explicit}`;
  return name ? `name:${name}` : "unknown";
}

function crmCustomerName(raw = {}) {
  return String(raw.name || raw.customerName || raw.clientName || raw.displayName || raw.fullName || "Cliente").trim() || "Cliente";
}

function crmCustomerPhone(raw = {}) {
  return String(raw.phoneNormalized || raw.phone || raw.customerPhone || raw.clientPhone || raw.whatsapp || raw.whatsApp || "").trim();
}

function crmCustomerEmail(raw = {}) {
  return String(raw.emailNormalized || raw.email || raw.customerEmail || raw.clientEmail || "").trim().toLowerCase();
}

function crmStatus(raw = {}) {
  const s = String(raw?.status || raw?.confirmationStatus || raw?.state || "").trim().toLowerCase();
  if (["cancelled", "canceled", "cancelada", "rejected", "declined", "rechazada"].includes(s)) return "cancelled";
  if (["no_show", "no-show", "noshow", "no asistió", "no_asistio"].includes(s)) return "no_show";
  if (["pending", "pendiente", "to_confirm", "por_confirmar"].includes(s)) return "pending";
  if (["completed", "complete", "done", "completada"].includes(s)) return "completed";
  return "confirmed";
}

function crmBookingDate(raw = {}) {
  return safeDate(raw?.scheduledAt) || safeDate(raw?.date) || safeDate(raw?.start) || safeDate(raw?.createdAt) || null;
}

function crmDaysSince(date) {
  if (!date) return null;
  const diff = Date.now() - date.getTime();
  return Math.max(0, Math.floor(diff / 86400000));
}

function crmNextBirthdayInfo(raw = {}) {
  const value = raw.birthDate || raw.customerBirthDate || raw.dob || raw.birthday || raw.birthdate;
  const date = safeDate(value);
  if (!date) return { hasBirthday: false, daysUntil: null, label: "Sin cumpleaños" };
  const now = new Date();
  const next = new Date(now.getFullYear(), date.getMonth(), date.getDate());
  if (next < new Date(now.getFullYear(), now.getMonth(), now.getDate())) next.setFullYear(now.getFullYear() + 1);
  const daysUntil = Math.ceil((next.getTime() - now.getTime()) / 86400000);
  return { hasBirthday: true, daysUntil, label: next.toLocaleDateString("es-PA", { day: "numeric", month: "short" }) };
}

function crmSegmentMeta(key) {
  const labels = {
    all: "Todos",
    new: "Cliente nuevo",
    frequent: "Frecuente",
    vip: "VIP",
    dormant: "Dormido",
    at_risk: "En riesgo",
    no_show: "No-show recurrente",
    high_ticket: "Alto ticket",
    product_buyer: "Compra productos",
    birthday_soon: "Cumpleaños pronto",
  };
  return labels[key] || key;
}

function crmSegmentTone(key) {
  if (["vip", "frequent", "product_buyer", "birthday_soon"].includes(key)) return "success";
  if (["dormant", "at_risk", "no_show"].includes(key)) return "warning";
  if (key === "high_ticket") return "primary";
  return "neutral";
}

function crmBuildWhatsAppLink(phone, message = "") {
  const digits = crmDigits(phone);
  if (!digits) return "";
  return `https://wa.me/${digits}${message ? `?text=${encodeURIComponent(message)}` : ""}`;
}

function crmWhatsAppPhoneStatus(customer = {}) {
  const phone = crmDigits(customer.phoneNormalized || customer.phone || customer.customerPhone || customer.whatsapp || customer.whatsApp || "");
  if (!phone) return "missing";
  if (phone.length < 8) return "invalid";
  return "valid";
}

function crmWhatsAppPreference(customer = {}) {
  const optedOut = customer.whatsappOptOut === true || customer.customerWhatsappOptOut === true || customer.whatsAppOptOut === true;
  const optedInFalse = customer.whatsappOptIn === false || customer.customerWhatsappConsent === false || customer.allowWhatsApp === false;
  const phoneStatus = crmWhatsAppPhoneStatus(customer);
  if (optedOut || optedInFalse) return { key: "blocked", label: "No acepta WhatsApp", tone: "blocked", canSend: false, optedOut: true, phoneStatus };
  if (phoneStatus === "missing") return { key: "missing", label: "Sin teléfono", tone: "muted", canSend: false, optedOut: false, phoneStatus };
  if (phoneStatus === "invalid") return { key: "invalid", label: "Teléfono inválido", tone: "warning", canSend: false, optedOut: false, phoneStatus };
  return { key: "ready", label: "WhatsApp listo", tone: "ready", canSend: true, optedOut: false, phoneStatus };
}

function crmFirstName(name = "") {
  return String(name || "").trim().split(/\s+/).filter(Boolean)[0] || "";
}

function crmSafeMessageText(value = "") {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function crmBuildSegmentMessage({ customer = {}, businessName = "Tu negocio", segment = "general", template = "" } = {}) {
  const firstName = crmFirstName(customer.name) || "";
  const favoriteService = customer.favoriteService && customer.favoriteService !== "—" ? customer.favoriteService : "tu servicio favorito";
  const business = businessName || "Tu negocio";

  const templates = {
    at_risk: "Hola {nombre}, te escribimos de {negocio}. Hace un tiempo no te vemos por acá y queríamos saber si te gustaría reservar nuevamente {servicio} esta semana.",
    dormant: "Hola {nombre}, te escribimos de {negocio}. Hace un tiempo no te vemos y queríamos ayudarte a encontrar un horario cómodo para tu próxima visita.",
    vip: "Hola {nombre}, gracias por ser parte de {negocio}. Queríamos darte prioridad para tu próxima reserva de {servicio} y ayudarte a encontrar un horario cómodo.",
    frequent: "Hola {nombre}, te escribimos de {negocio}. Como vienes seguido, queríamos ayudarte a reservar tu próxima visita de {servicio} antes de que se llenen los horarios.",
    birthday_soon: "Hola {nombre}, vimos que tu cumpleaños está cerca. De parte de {negocio}, nos encantaría ayudarte a preparar algo especial para esa fecha.",
    product_buyer: "Hola {nombre}, te escribimos de {negocio}. Queríamos revisar si necesitas reponer algún producto o complementar tu próximo servicio.",
    no_show: "Hola {nombre}, te escribimos de {negocio}. Queríamos ayudarte a reagendar tu próxima reserva en un horario que te quede cómodo.",
    high_ticket: "Hola {nombre}, te escribimos de {negocio}. Tenemos disponibilidad para ayudarte con una próxima reserva de {servicio} cuando gustes.",
    new: "Hola {nombre}, te escribimos de {negocio}. Gracias por visitarnos. Nos encantaría ayudarte con tu próxima reserva cuando la necesites.",
    general: "Hola {nombre}, te escribimos de {negocio}. Queríamos ayudarte con tu próxima reserva cuando gustes.",
  };

  const source = template || templates[segment] || templates.general;
  return crmSafeMessageText(
    source
      .replace(/\{nombre\}/g, firstName)
      .replace(/\{negocio\}/g, business)
      .replace(/\{servicio\}/g, favoriteService)
  );
}

function crmActionTypeLabel(segment = "general") {
  const map = {
    at_risk: "Reactivación",
    dormant: "Reactivación",
    vip: "Cuidado VIP",
    frequent: "Recurrencia",
    birthday_soon: "Cumpleaños",
    product_buyer: "Recompra producto",
    no_show: "Reagendar",
    high_ticket: "Alto ticket",
    new: "Seguimiento nuevo",
    general: "Seguimiento",
  };
  return map[segment] || map.general;
}

// LOOP PHASE 2.2D — follow-up pipeline helpers.
function crmFollowUpMeta(key) {
  const map = {
    all: "Todos",
    not_contacted: "Sin contacto",
    recently_contacted: "Contactado reciente",
    pending_response: "Esperando respuesta",
    follow_up_due: "Dar seguimiento",
    reactivated: "Reactivado",
  };
  return map[key] || map.not_contacted;
}

function crmFollowUpTone(key) {
  if (key === "reactivated") return "success";
  if (key === "follow_up_due") return "warning";
  if (key === "pending_response") return "primary";
  if (key === "recently_contacted") return "neutral";
  return "muted";
}

function crmFollowUpInfo(customer = {}) {
  const lastContactedAt = safeDate(customer.lastContactedAt) || safeDate(customer.lastCrmActionAt) || null;
  const lastVisit = safeDate(customer.lastVisit) || safeDate(customer.lastBookingAt) || null;
  const nextBooking = safeDate(customer.nextBooking) || null;
  if (!lastContactedAt) {
    return { key: "not_contacted", label: "Sin contacto", tone: "muted", daysSinceContact: null, lastContactedAt: null, reactivated: false, hint: "Todavía no hay acción CRM guardada." };
  }

  const daysSinceContact = crmDaysSince(lastContactedAt);
  const reactivated = Boolean(lastVisit && lastVisit > lastContactedAt);
  if (reactivated) {
    return { key: "reactivated", label: "Reactivado", tone: "success", daysSinceContact, lastContactedAt, reactivated: true, hint: "Reservó o volvió después del contacto." };
  }
  if (nextBooking && nextBooking > new Date()) {
    return { key: "reactivated", label: "Reserva futura", tone: "success", daysSinceContact, lastContactedAt, reactivated: true, hint: "Tiene una próxima reserva después del contacto." };
  }
  if (daysSinceContact !== null && daysSinceContact >= 7) {
    return { key: "follow_up_due", label: "Dar seguimiento", tone: "warning", daysSinceContact, lastContactedAt, reactivated: false, hint: "Han pasado 7+ días sin una nueva reserva visible." };
  }
  if (daysSinceContact !== null && daysSinceContact >= 3) {
    return { key: "pending_response", label: "Esperando respuesta", tone: "primary", daysSinceContact, lastContactedAt, reactivated: false, hint: "Buen momento para revisar si respondió." };
  }
  return { key: "recently_contacted", label: "Contactado reciente", tone: "neutral", daysSinceContact, lastContactedAt, reactivated: false, hint: "Dale unos días antes de insistir." };
}

function crmFormatDate(value) {
  if (!value) return "—";
  try {
    return value.toLocaleDateString("es-PA", { day: "numeric", month: "short", year: "numeric" });
  } catch (_) {
    return "—";
  }
}


// LOOP PHASE 2.2E — CRM Revenue Attribution helpers.
const CRM_ATTRIBUTION_WINDOW_DAYS = 30;

function crmAttributionWindowEnd(contactDate, days = CRM_ATTRIBUTION_WINDOW_DAYS) {
  const start = safeDate(contactDate);
  if (!start) return null;
  return new Date(start.getTime() + Math.max(1, Number(days || CRM_ATTRIBUTION_WINDOW_DAYS)) * 86400000);
}

function crmAttributionForCustomer(customer = {}, MONEY = regionMoney()) {
  const lastContactedAt = safeDate(customer.lastContactedAt) || safeDate(customer.lastCrmActionAt) || null;
  if (!lastContactedAt) {
    return {
      recoveredRevenue: 0,
      recoveredCollected: 0,
      recoveredBookings: 0,
      recoveredCustomers: 0,
      firstReactivatedAt: null,
      attributionWindowEnd: null,
      attributionLabel: "Sin contacto",
      attributionTone: "muted",
    };
  }

  const windowEnd = crmAttributionWindowEnd(lastContactedAt);
  const rows = Array.isArray(customer.bookingRows) ? customer.bookingRows : [];
  const attributed = rows.filter((row) => {
    const d = safeDate(row.bookingDate);
    if (!d || d <= lastContactedAt) return false;
    if (windowEnd && d > windowEnd) return false;
    const status = String(row.status || "").toLowerCase();
    if (["cancelled", "canceled", "cancelada", "no_show", "noshow", "no-show", "rejected", "declined"].includes(status)) return false;
    return true;
  });

  const recoveredRevenue = attributed.reduce((sum, row) => sum + numberFrom(row.revenue), 0);
  const recoveredCollected = attributed.reduce((sum, row) => sum + numberFrom(row.collected), 0);
  const firstReactivatedAt = attributed
    .map((row) => safeDate(row.bookingDate))
    .filter(Boolean)
    .sort((a, b) => a.getTime() - b.getTime())[0] || null;

  const hasRevenue = recoveredRevenue > 0 || attributed.length > 0;
  return {
    recoveredRevenue,
    recoveredCollected,
    recoveredBookings: attributed.length,
    recoveredCustomers: hasRevenue ? 1 : 0,
    firstReactivatedAt,
    attributionWindowEnd: windowEnd,
    attributionLabel: hasRevenue ? `CRM +${MONEY.format(recoveredRevenue)}` : "Sin revenue CRM",
    attributionTone: hasRevenue ? "success" : "muted",
  };
}

const EMPTY_INVENTORY_FORM = {
  id: "",
  name: "",
  category: "",
  currentStock: "",
  minStock: "",
  unit: "unidad",
  unitCost: "",
  salePrice: "",
  supplier: "",
  notes: "",
};

function normalizeInventoryItem(raw = {}) {
  const currentStock = numberFrom(raw.currentStock ?? raw.stock ?? raw.quantity ?? 0);
  const minStock = numberFrom(raw.minStock ?? raw.minimumStock ?? raw.reorderPoint ?? 0);
  const unitCost = numberFrom(raw.unitCost ?? raw.cost ?? raw.costPerUnit ?? 0);
  const salePrice = numberFrom(raw.salePrice ?? raw.price ?? 0);
  return {
    id: raw.id || raw.itemId || "",
    name: String(raw.name || raw.title || raw.productName || "").trim(),
    category: String(raw.category || raw.type || "General").trim() || "General",
    currentStock,
    minStock,
    unit: String(raw.unit || raw.measureUnit || "unidad").trim() || "unidad",
    unitCost,
    salePrice,
    supplier: String(raw.supplier || raw.vendor || "").trim(),
    notes: String(raw.notes || raw.description || "").trim(),
    active: raw.active !== false,
    updatedAt: raw.updatedAt || null,
    createdAt: raw.createdAt || null,
  };
}

function inventoryFormFromItem(item = {}) {
  const normalized = normalizeInventoryItem(item);
  return {
    id: normalized.id || "",
    name: normalized.name || "",
    category: normalized.category === "General" ? "" : normalized.category,
    currentStock: normalized.currentStock ? String(normalized.currentStock) : "",
    minStock: normalized.minStock ? String(normalized.minStock) : "",
    unit: normalized.unit || "unidad",
    unitCost: normalized.unitCost ? String(normalized.unitCost) : "",
    salePrice: normalized.salePrice ? String(normalized.salePrice) : "",
    supplier: normalized.supplier || "",
    notes: normalized.notes || "",
  };
}

function inventoryPayloadFromForm(form = {}) {
  const name = String(form.name || "").trim();
  return {
    name,
    category: String(form.category || "General").trim() || "General",
    currentStock: numberFrom(form.currentStock),
    minStock: numberFrom(form.minStock),
    unit: String(form.unit || "unidad").trim() || "unidad",
    unitCost: numberFrom(form.unitCost),
    salePrice: numberFrom(form.salePrice),
    supplier: String(form.supplier || "").trim() || null,
    notes: String(form.notes || "").trim() || null,
    active: true,
    updatedAt: serverTimestamp(),
  };
}

function inventoryStockTone(item) {
  const current = numberFrom(item.currentStock);
  const min = numberFrom(item.minStock);
  if (min > 0 && current <= 0) return "critical";
  if (min > 0 && current <= min) return "low";
  return "ok";
}

function inventoryStockLabel(item) {
  const tone = inventoryStockTone(item);
  if (tone === "critical") return "Sin stock";
  if (tone === "low") return "Bajo stock";
  return "OK";
}

function inventoryMovementTypeLabel(value) {
  const raw = String(value || "").trim().toLowerCase();
  const labels = {
    restock: "Reposición",
    manual_adjustment: "Ajuste manual",
    stocktake_adjustment: "Conteo físico",
    internal_use: "Uso interno",
    damage_loss: "Daño / pérdida",
    return_in: "Devolución",
    booking_consumption: "Uso en servicio",
    retail_sale: "Venta producto",
    retail_sale_adjustment: "Ajuste venta producto",
  };
  return labels[raw] || "Movimiento";
}

function normalizeInventoryMovement(raw = {}) {
  const created = safeDate(raw.createdAt) || safeDate(raw.occurredAt) || null;
  return {
    id: raw.id || raw.movementId || "",
    type: String(raw.type || raw.movementType || "manual_adjustment").trim() || "manual_adjustment",
    itemId: String(raw.itemId || raw.inventoryItemId || "").trim(),
    itemName: String(raw.itemName || raw.productName || raw.name || "Producto").trim() || "Producto",
    unit: String(raw.unit || "un.").trim() || "un.",
    quantity: numberFrom(raw.quantity ?? raw.quantityDelta ?? raw.delta ?? 0),
    stockBefore: numberFrom(raw.stockBefore ?? 0),
    stockAfter: numberFrom(raw.stockAfter ?? 0),
    unitCost: numberFrom(raw.unitCost ?? raw.cost ?? 0),
    totalCost: numberFrom(raw.totalCost ?? 0),
    reason: String(raw.reason || raw.notes || raw.description || "").trim(),
    source: String(raw.source || "manual").trim(),
    createdAt: created,
    createdByEmail: raw.createdByEmail || raw.userEmail || "",
    bookingId: raw.bookingId || null,
  };
}

function auditEventTypeLabel(value) {
  const raw = String(value || "").trim().toLowerCase();
  const labels = {
    booking_status_changed: "Estado actualizado",
    payment_status_changed: "Pago actualizado",
    inventory_deducted: "Inventario descontado",
    inventory_deduction_failed: "Error de inventario",
    product_sales_updated: "Productos actualizados",
    product_sales_deducted: "Productos descontados",
    inventory_adjusted: "Inventario ajustado",
  };
  return labels[raw] || "Evento";
}

function normalizeAuditLog(raw = {}) {
  const created = safeDate(raw.createdAt) || safeDate(raw.occurredAt) || safeDate(raw.createdAtClient) || null;
  const details = raw.details && typeof raw.details === "object" ? raw.details : {};
  return {
    id: raw.id || raw.logId || "",
    type: String(raw.eventType || raw.type || "audit_event").trim(),
    label: raw.label || auditEventTypeLabel(raw.eventType || raw.type),
    severity: String(raw.severity || "info").trim(),
    bookingId: raw.bookingId || details.bookingId || null,
    bookingClientName: raw.bookingClientName || details.clientName || "",
    bookingServiceName: raw.bookingServiceName || details.serviceName || "",
    actorEmail: raw.createdByEmail || raw.actorEmail || raw.userEmail || "",
    actorUid: raw.createdByUid || raw.actorUid || "",
    details,
    createdAt: created,
  };
}

function auditLogSummary(log = {}) {
  const details = log.details || {};
  if (log.type === "booking_status_changed") return `${details.from || "—"} → ${details.to || "—"}`;
  if (log.type === "payment_status_changed") return `${details.paymentStatus || "pago"} · ${details.paymentMethod || "sin método"}`;
  if (log.type === "inventory_deducted") return `${details.rowCount || 0} movimiento(s) · ${details.trigger || "trigger"}`;
  if (log.type === "inventory_deduction_failed") return `${details.reason || "error"} · ${details.source || "inventario"}`;
  if (log.type === "product_sales_deducted" || log.type === "product_sales_updated") return `${details.productCount || 0} producto(s)`;
  return details.reason || details.source || "Sin detalle";
}

function auditLogIsInventoryRelated(log = {}) {
  const type = String(log.type || "").toLowerCase();
  return type.includes("inventory") || type.includes("product_sales") || type.includes("stock");
}

function auditLogIsPaymentRelated(log = {}) {
  const type = String(log.type || "").toLowerCase();
  return type.includes("payment") || type.includes("paid") || type.includes("payout");
}

function auditLogSearchText(log = {}) {
  const details = log.details || {};
  let detailsText = "";
  try { detailsText = JSON.stringify(details); } catch (_) { detailsText = ""; }
  return [
    log.type,
    log.label,
    auditLogSummary(log),
    log.bookingId,
    log.bookingClientName,
    log.bookingServiceName,
    log.actorEmail,
    log.actorUid,
    detailsText,
  ].filter(Boolean).join(" ").toLowerCase();
}

function auditLogDateKey(log = {}) {
  return log.createdAt ? formatDateInputValue(log.createdAt) : "";
}

function auditLogActorLabel(log = {}) {
  return log.actorEmail || log.actorUid || "Sistema automático";
}

// LOOP PHASE 2.8 — Inventory Automation Bridge helpers.
function inventoryDeductionStatus(raw = {}) {
  return normalizeInventoryDeductionStatus(raw.inventoryDeductionStatus || raw.inventory?.deductionStatus || raw.inventoryStatus || "pending");
}

function inventoryDeductionLabel(value) {
  const status = normalizeInventoryDeductionStatus(value);
  const labels = {
    deducted: "Descontado",
    failed: "Error de stock",
    not_configured: "Sin materiales",
    pending: "Pendiente",
  };
  return labels[status] || "Pendiente";
}

function inventoryDeductionTone(value) {
  const status = normalizeInventoryDeductionStatus(value);
  if (status === "deducted") return "ok";
  if (status === "failed") return "critical";
  if (status === "not_configured") return "neutral";
  return "low";
}

function bookingClientLabel(raw = {}) {
  return raw.customerName || raw.clientName || raw.name || raw.customerSnapshot?.name || "Cliente";
}

function buildInventoryConsumptionPlan({ booking = {}, service = {}, inventoryItems = [] } = {}) {
  const usageRows = getInventoryUsageRows(service);
  if (!usageRows.length) {
    return { rows: [], totalCost: 0, hasMaterials: false, hasMissingItems: false, hasInsufficientStock: false, canDeduct: false, reason: "Este servicio no tiene materiales configurados." };
  }

  const itemMap = new Map((Array.isArray(inventoryItems) ? inventoryItems : []).map((item) => [String(item.id), normalizeInventoryItem(item)]));
  const rows = usageRows.map((usage) => {
    const item = itemMap.get(String(usage.itemId));
    const quantityUsed = numberFrom(usage.quantityUsed);
    const stockBefore = numberFrom(item?.currentStock);
    const unitCost = numberFrom(item?.unitCost);
    const missing = !item;
    const insufficient = Boolean(item) && stockBefore < quantityUsed;
    const stockAfter = missing ? 0 : Math.max(0, stockBefore - quantityUsed);
    return {
      itemId: usage.itemId,
      itemName: item?.name || usage.itemId || "Producto",
      unit: item?.unit || "un.",
      quantityUsed,
      stockBefore,
      stockAfter,
      unitCost,
      totalCost: quantityUsed * unitCost,
      missing,
      insufficient,
    };
  });

  const hasMissingItems = rows.some((row) => row.missing);
  const hasInsufficientStock = rows.some((row) => row.insufficient);
  return {
    rows,
    totalCost: rows.reduce((sum, row) => sum + numberFrom(row.totalCost), 0),
    hasMaterials: rows.length > 0,
    hasMissingItems,
    hasInsufficientStock,
    canDeduct: rows.length > 0 && !hasMissingItems && !hasInsufficientStock,
    reason: hasMissingItems ? "Hay materiales que ya no existen en inventario." : hasInsufficientStock ? "Hay materiales con stock insuficiente." : "Listo para descontar.",
  };
}

function inventoryAdjustmentPreview({ item, form }) {
  if (!item) return null;
  const stockBefore = numberFrom(item.currentStock);
  let quantityDelta = 0;
  let stockAfter = stockBefore;
  if (form?.mode === "final") {
    if (String(form?.finalStock ?? "").trim() === "") return null;
    stockAfter = Math.max(0, numberFrom(form.finalStock));
    quantityDelta = stockAfter - stockBefore;
  } else {
    const qty = Math.abs(numberFrom(form?.quantity));
    if (qty <= 0) return null;
    const type = String(form?.type || "restock");
    const isOut = ["internal_use", "damage_loss"].includes(type);
    quantityDelta = isOut ? -qty : qty;
    stockAfter = Math.max(0, stockBefore + quantityDelta);
    quantityDelta = stockAfter - stockBefore;
  }
  return {
    stockBefore,
    stockAfter,
    quantityDelta,
    unit: item.unit || "un.",
    tone: quantityDelta < 0 ? "warning" : quantityDelta > 0 ? "success" : "neutral",
  };
}

function reorderSuggestionForItem(item = {}) {
  const current = numberFrom(item.currentStock);
  const min = numberFrom(item.minStock);
  const target = Math.max(min * 2, min + 1);
  const suggestedQty = min > 0 ? Math.max(0, target - current) : 0;
  return { suggestedQty, targetStock: target };
}

const EMPTY_INVENTORY_ADJUST_FORM = {
  itemId: "",
  type: "restock",
  quantity: "",
  finalStock: "",
  mode: "delta",
  reason: "",
};

const EMPTY_STOCKTAKE_FORM = {
  name: "",
  rows: {},
  notes: "",
};

function movementPayloadBase({ item, type, quantityDelta, stockBefore, stockAfter, reason, source = "manual" }) {
  const safeDelta = numberFrom(quantityDelta);
  const unitCost = numberFrom(item?.unitCost ?? item?.cost ?? 0);
  return {
    type,
    source,
    itemId: item?.id || "",
    itemName: item?.name || item?.title || "Producto",
    unit: item?.unit || "un.",
    quantity: safeDelta,
    stockBefore: numberFrom(stockBefore),
    stockAfter: numberFrom(stockAfter),
    unitCost,
    totalCost: Math.abs(safeDelta) * unitCost,
    reason: String(reason || "").trim() || null,
    createdAt: serverTimestamp(),
  };
}

function formatDateInputValue(date) {
  try {
    const d = date ? new Date(date) : new Date();
    if (Number.isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  } catch (_) {
    return "";
  }
}

function parseDateInputValue(value, endOfDay = false) {
  const m = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return null;
  if (endOfDay) d.setHours(23, 59, 59, 999);
  else d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfDaySafe(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function csvEscape(value) {
  const raw = value === null || value === undefined ? "" : String(value);
  if (/[",\n\r]/.test(raw)) return `"${raw.replace(/"/g, '""')}"`;
  return raw;
}

function buildCsv(headers = [], rows = []) {
  return [headers.map(csvEscape).join(","), ...rows.map((row) => headers.map((h) => csvEscape(row[h])).join(","))].join("\n");
}

function downloadTextFile(filename, text, mime = "text/csv;charset=utf-8") {
  try {
    if (typeof document === "undefined") return;
    const blob = new Blob([text], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 800);
  } catch (err) {
    console.warn("[BusinessAnalytics] export skipped", err);
  }
}

function compactFilename(value = "reporte") {
  return String(value || "reporte")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase()
    .slice(0, 72) || "reporte";
}

function dateLabelShort(value) {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-PA", { day: "2-digit", month: "short", year: "numeric" }).replace(".", "");
}


function clampPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(999, Math.round(n)));
}

function safeMoneyValue(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function moduleHealthTone(value) {
  if (value === "good") return "good";
  if (value === "warning") return "warning";
  if (value === "critical") return "critical";
  return "neutral";
}

function EmptyStateCard({ title, body, actionLabel, onAction }) {
  return (
    <div className="ba-empty-card">
      <div className="ba-empty-icon" aria-hidden="true">◇</div>
      <div>
        <strong>{title}</strong>
        <p>{body}</p>
        {actionLabel && onAction ? <button type="button" onClick={onAction}>{actionLabel}</button> : null}
      </div>
    </div>
  );
}

function normalizeBusinessAnalyticsView(value) {
  const safe = String(value || "overview").trim().toLowerCase();
  if (["cash", "caja", "closeout"].includes(safe)) return "finance";
  if (["reports", "reportes", "exports", "export", "exportar", "descargas"].includes(safe)) return "exports";
  if (["finance", "finanzas"].includes(safe)) return "finance";
  if (["inventory", "inventario"].includes(safe)) return "inventory";
  if (["crm", "clientes", "clients"].includes(safe)) return "crm";
  if (["memberships", "membresias", "membresías"].includes(safe)) return "memberships";
  if (["traffic", "trafico", "tráfico"].includes(safe)) return "traffic";
  if (["insights", "advisor"].includes(safe)) return "insights";
  return "overview";
}

function LoadingSkeleton() {
  return (
    <div className="ba-skeleton-wrap" aria-label={t("Cargando analíticas")}>
      <span /><span /><span /><span />
    </div>
  );
}


const FINANCE_VIEW_CONFIG_STORAGE_KEY = "loop_finance_worker_columns_v111";
const REQUIRED_FINANCE_VIEW_KEYS = new Set(["net"]);
const DEFAULT_FINANCE_VIEW_CONFIG = {
  attended: true,
  serviceSales: true,
  productSales: true,
  businessMembershipIncome: true,
  totalBusiness: true,
  tips: true,
  serviceCommission: true,
  productCommission: true,
  workerMembershipIncome: true,
  totalPayout: true,
  net: true,
  collectedMethods: true,
  workerCommissions: true,
};

function readStoredFinanceViewConfig() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return { ...DEFAULT_FINANCE_VIEW_CONFIG };
    const raw = window.localStorage.getItem(FINANCE_VIEW_CONFIG_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_FINANCE_VIEW_CONFIG };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return { ...DEFAULT_FINANCE_VIEW_CONFIG };
    return { ...DEFAULT_FINANCE_VIEW_CONFIG, ...parsed, net: true };
  } catch (_) {
    return { ...DEFAULT_FINANCE_VIEW_CONFIG };
  }
}

export default function BusinessAnalytics({ businessId, db = defaultDb, auth = defaultAuth, className = "", permissions = null, isOwnerOrAdmin = false, businessSettings = null, initialView = "overview", embeddedNavigation = false }) {
  const language = useLanguage();
  const [membershipEnrollmentOpen, setMembershipEnrollmentOpen] = useState(false);
  const [period, setPeriod] = useState("day");
  const [anchorDate, setAnchorDate] = useState(() => new Date());
  const [workerFilter, setWorkerFilter] = useState("all");
  const [activeView, setActiveView] = useState(() => normalizeBusinessAnalyticsView(initialView));
  const [moduleMenuOpen, setModuleMenuOpen] = useState(false);
  const [reportFiltersOpen, setReportFiltersOpen] = useState(false);

  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [business, setBusiness] = useState(null);
  const MONEY=useMemo(()=>regionMoney(business || {}),[business,language]);
  const [trackingEvents, setTrackingEvents] = useState([]);
  const [trackingReady, setTrackingReady] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventorySaving, setInventorySaving] = useState(false);
  const [inventoryStatus, setInventoryStatus] = useState("");
  const [inventoryForm, setInventoryForm] = useState(() => ({ ...EMPTY_INVENTORY_FORM }));
  const [inventoryPanel, setInventoryPanel] = useState("");
  const [inventorySearch, setInventorySearch] = useState("");
  const [inventoryServiceSearch, setInventoryServiceSearch] = useState("");
  const [inventoryReplenishmentSearch, setInventoryReplenishmentSearch] = useState("");
  const [inventoryReplenishmentFilter, setInventoryReplenishmentFilter] = useState("all");
  const [inventoryMovements, setInventoryMovements] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditFilterType, setAuditFilterType] = useState("all");
  const [auditFilterActor, setAuditFilterActor] = useState("all");
  const [auditFilterFrom, setAuditFilterFrom] = useState("");
  const [auditFilterTo, setAuditFilterTo] = useState("");
  const [auditFilterSearch, setAuditFilterSearch] = useState("");
  const [auditOnlyErrors, setAuditOnlyErrors] = useState(false);
  const [auditOnlyInventory, setAuditOnlyInventory] = useState(false);
  const [auditOnlyPayments, setAuditOnlyPayments] = useState(false);
  const [crmSearch, setCrmSearch] = useState("");
  const [crmSegmentFilter, setCrmSegmentFilter] = useState("all");
  const [crmFollowUpFilter, setCrmFollowUpFilter] = useState("all");
  const [crmActionModal, setCrmActionModal] = useState(null);
  const [crmActionMessage, setCrmActionMessage] = useState("");
  const [crmActionSaving, setCrmActionSaving] = useState(false);
  const [crmActionStatus, setCrmActionStatus] = useState("");
  const [crmWhatsAppSavingKey, setCrmWhatsAppSavingKey] = useState("");
  const [crmWhatsAppStatus, setCrmWhatsAppStatus] = useState("");
  const [inventoryAdjustForm, setInventoryAdjustForm] = useState(() => ({ ...EMPTY_INVENTORY_ADJUST_FORM }));
  const [businessSettingsSaving, setBusinessSettingsSaving] = useState(false);
  const [businessSettingsStatus, setBusinessSettingsStatus] = useState("");
  const [stocktakeForm, setStocktakeForm] = useState(() => ({ ...EMPTY_STOCKTAKE_FORM }));
  const [stocktakeSaving, setStocktakeSaving] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(() => formatDateInputValue(startOfMonth(new Date())));
  const [customEndDate, setCustomEndDate] = useState(() => formatDateInputValue(new Date()));
  const [reportStatus, setReportStatus] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [financeRemoteFallbackLoad, setFinanceRemoteFallbackLoad] = useState(false);
  const financeFastPath = activeView === "finance" && isOwnerOrAdmin && !financeRemoteFallbackLoad;
  const [membershipAnalytics, setMembershipAnalytics] = useState(null);
  const [membershipAnalyticsLoading, setMembershipAnalyticsLoading] = useState(false);
  const [membershipAnalyticsError, setMembershipAnalyticsError] = useState("");
  const [membershipDetailView, setMembershipDetailView] = useState("");
  const [membershipDetailRows, setMembershipDetailRows] = useState([]);
  const [membershipDetailLoading, setMembershipDetailLoading] = useState(false);
  const [membershipDetailError, setMembershipDetailError] = useState("");
  const [membershipDetailSearch, setMembershipDetailSearch] = useState("");
  const [membershipDetailAppliedSearch, setMembershipDetailAppliedSearch] = useState("");
  const [membershipDetailFilter, setMembershipDetailFilter] = useState("all");
  const [membershipDetailRange, setMembershipDetailRange] = useState(30);
  const [membershipDetailCursor, setMembershipDetailCursor] = useState(null);
  const [membershipDetailNextCursor, setMembershipDetailNextCursor] = useState(null);
  const [membershipDetailCursorStack, setMembershipDetailCursorStack] = useState([]);
  const [membershipDetailPage, setMembershipDetailPage] = useState(1);
  const [membershipDetailHasMore, setMembershipDetailHasMore] = useState(false);
  const [membershipDetailTotal, setMembershipDetailTotal] = useState(null);
  const [membershipDetailSummary, setMembershipDetailSummary] = useState(null);
  const [membershipDetailLocalPreview, setMembershipDetailLocalPreview] = useState(false);
  const [membershipSelectedRecord, setMembershipSelectedRecord] = useState(null);
  const [membershipRecordOrigin, setMembershipRecordOrigin] = useState("");
  const [showFinanceCustomize, setShowFinanceCustomize] = useState(false);
  const [financeViewConfig, setFinanceViewConfig] = useState(() => readStoredFinanceViewConfig());
  const [financeDetailView, setFinanceDetailView] = useState("");
  const [financeDetailSearch, setFinanceDetailSearch] = useState("");
  const [financeDetailPage, setFinanceDetailPage] = useState(1);
  // LOOP REPORTS V3 A4.4.4 — scalable Finance workspace navigation.
  const [financeWorkspaceView, setFinanceWorkspaceView] = useState(() => {
    try {
      if (typeof window === "undefined" || !window.localStorage) return "profitability";
      const stored = String(window.localStorage.getItem("loop.finance.workspace.v1") || "");
      return ["profitability", "cash", "pnl", "trends", "team"].includes(stored) ? stored : "profitability";
    } catch (_) {
      return "profitability";
    }
  });

  useEffect(() => {
    try {
      if (typeof window === "undefined" || !window.localStorage) return;
      window.localStorage.setItem("loop.finance.workspace.v1", financeWorkspaceView);
    } catch (_) {}
  }, [financeWorkspaceView]);
  // LOOP REPORTS V3 A4.3 — Gastos operativos: owner/admin operating expenses.
  const [expenseLedgerOpen, setExpenseLedgerOpen] = useState(false);
  const [expenseLedgerSummary, setExpenseLedgerSummary] = useState(() => ({
    incurredTotal: 0,
    paidTotal: 0,
    pendingTotal: 0,
    previousIncurredTotal: 0,
    previousPaidTotal: 0,
    periodCount: 0,
    paidCount: 0,
    allCount: 0,
    voidedCount: 0,
    hasPeriodExpenses: false,
    byCategory: [],
  }));

  useEffect(() => {
    try {
      if (typeof window === "undefined" || !window.localStorage) return;
      window.localStorage.setItem(FINANCE_VIEW_CONFIG_STORAGE_KEY, JSON.stringify({ ...financeViewConfig, net: true }));
    } catch (_) {}
  }, [financeViewConfig]);

  const [showOverviewCustomize, setShowOverviewCustomize] = useState(false);
  const [overviewViewConfig, setOverviewViewConfig] = useState(() => ({
    hero: true,
    kpis: true,
    actions: true,
    topMonth: true,
    revenueChart: true,
    reservationStatus: true,
    serviceRanking: true,
  }));

  const toggleOverviewViewOption = (key) => {
    setOverviewViewConfig((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const nextView = normalizeBusinessAnalyticsView(initialView);
    setActiveView((current) => (current === nextView ? current : nextView));
  }, [initialView]);

  useEffect(() => {
    const normalized = normalizeBusinessAnalyticsView(activeView);
    if (normalized !== activeView) setActiveView(normalized);
  }, [activeView]);

  // LOOP PHASE 2.0.4 — role permissions + Business OS module settings.
  const analyticsPermissions = useMemo(() => normalizeWorkerPermissions(permissions), [permissions]);
  const loopSettings = useMemo(() => normalizeLoopBusinessSettings(businessSettings || business || {}), [businessSettings, business]);
  const analyticsModuleEnabled = loopSettings.enabledModules.analytics !== false;
  const inventoryModuleEnabled = loopSettings.enabledModules.inventory !== false && loopSettings.inventorySettings.enabled !== false && loopSettings.inventorySettings.plan !== "disabled";
  const canViewReports = analyticsModuleEnabled && (isOwnerOrAdmin || analyticsPermissions.canViewReports || analyticsPermissions.canViewAnalytics);
  const canViewFinancials = analyticsModuleEnabled && (isOwnerOrAdmin || analyticsPermissions.canViewFinancials);
  const canViewFinanceUnified = analyticsModuleEnabled && (isOwnerOrAdmin || analyticsPermissions.canViewFinancials || analyticsPermissions.canViewReports);
  const canViewInventory = inventoryModuleEnabled && (isOwnerOrAdmin || analyticsPermissions.canViewInventory);
  const canEditInventory = inventoryModuleEnabled && (isOwnerOrAdmin || analyticsPermissions.canEditInventory);
  const canAdjustInventory = inventoryModuleEnabled && (isOwnerOrAdmin || analyticsPermissions.canAdjustInventory);
  const canViewAuditLogs = loopSettings.enabledModules.auditLogs !== false && loopSettings.auditSettings.enabled !== false && (isOwnerOrAdmin || analyticsPermissions.canViewAuditLogs);
  const crmModuleEnabled = loopSettings.enabledModules.crm !== false;
  const canViewCrm = crmModuleEnabled && (isOwnerOrAdmin || analyticsPermissions.canViewCustomers || analyticsPermissions.canEditCustomers || canViewReports);
  const canEditCrm = crmModuleEnabled && (isOwnerOrAdmin || analyticsPermissions.canEditCustomers || analyticsPermissions.canSendWhatsApp);
  const membershipModuleEnabled = loopSettings.enabledModules.memberships !== false;
  // LOOP D2.8.2 HOTFIX — analytics tab should not disappear just because the operational
  // Membership module flag is off/undefined in older business settings. Reports users can
  // still view analytics; the backend returns zeros when there is no membership data.
  const canViewMembershipAnalytics = analyticsModuleEnabled && (isOwnerOrAdmin || canViewReports || canViewCrm || membershipModuleEnabled);

  useEffect(() => {
    let mounted = true;

    async function loadOptionalTracking() {
      const paths = ["analyticsEvents", "trackingEvents", "events"];
      const rows = [];
      for (const col of paths) {
        try {
          if (isIosCapacitor()) {
            const got = await fsClient.listCollectionByPath(`businesses/${businessId}/${col}`, {}, { db });
            if (Array.isArray(got)) rows.push(...got.map((x) => ({ id: x.id, collection: col, ...(x || {}) })));
          } else {
            const snap = await getDocs(collection(db, `businesses/${businessId}/${col}`));
            rows.push(...snap.docs.map((d) => ({ id: d.id, collection: col, ...(d.data() || {}) })));
          }
        } catch (_) {
          // Tracking is optional. Do not break core analytics when rules/collection do not exist yet.
        }
      }
      return rows;
    }

    async function load() {
      if (!businessId) return;
      setLoading(true);
      setError("");
      setTrackingReady(false);

      try {
        if (isIosCapacitor()) {
          const [businessDoc, bRows, sRows, eRows, cRows, invRows, movementRows, auditRows, tRows] = await Promise.all([
            fsClient.getDocByPath(`businesses/${businessId}`, { db }),
            financeFastPath ? Promise.resolve([]) : fsClient.listCollectionByPath(`businesses/${businessId}/bookings`, {}, { db }),
            fsClient.listCollectionByPath(`businesses/${businessId}/services`, {}, { db }),
            fsClient.listCollectionByPath(`businesses/${businessId}/employees`, {}, { db }),
            fsClient.listCollectionByPath(`businesses/${businessId}/customers`, {}, { db }).catch(() => []),
            fsClient.listCollectionByPath(`businesses/${businessId}/inventoryItems`, {}, { db }).catch(() => []),
            fsClient.listCollectionByPath(`businesses/${businessId}/inventoryMovements`, {}, { db }).catch(() => []),
            fsClient.listCollectionByPath(`businesses/${businessId}/auditLogs`, {}, { db }).catch(() => []),
            financeFastPath ? Promise.resolve([]) : loadOptionalTracking(),
          ]);

          if (!mounted) return;
          setBusiness((prev) => businessDoc || (isIosCapacitor() && prev ? prev : null));
          setBookings((prev) => {
            if (financeFastPath) return [];
            const fresh = Array.isArray(bRows) ? assertSameCurrency(bRows.map((x) => ({ id: x.id || x.bookingId, ...(x || {}) })),businessDoc || {}) : [];
            return fresh.length > 0 || !isIosCapacitor() ? fresh : (Array.isArray(prev) && prev.length > 0 ? prev : fresh);
          });
          setServices((prev) => {
            const fresh = Array.isArray(sRows) ? sRows.map((x) => ({ id: x.id || x.serviceId, ...(x || {}) })) : [];
            return fresh.length > 0 || !isIosCapacitor() ? fresh : (Array.isArray(prev) && prev.length > 0 ? prev : fresh);
          });
          setEmployees((prev) => {
            const fresh = Array.isArray(eRows) ? eRows.map((x) => ({ id: x.id || x.employeeId, ...(x || {}) })) : [];
            return fresh.length > 0 || !isIosCapacitor() ? fresh : (Array.isArray(prev) && prev.length > 0 ? prev : fresh);
          });
          setCustomers((prev) => {
            const fresh = Array.isArray(cRows) ? cRows.map((x) => ({ id: x.id || x.customerId, ...(x || {}) })) : [];
            return fresh.length > 0 || !isIosCapacitor() ? fresh : (Array.isArray(prev) && prev.length > 0 ? prev : fresh);
          });
          setInventoryItems((prev) => {
            const fresh = Array.isArray(invRows) ? invRows.map((x) => normalizeInventoryItem({ id: x.id || x.itemId, ...(x || {}) })) : [];
            return fresh.length > 0 || !isIosCapacitor() ? fresh : (Array.isArray(prev) && prev.length > 0 ? prev : fresh);
          });
          setInventoryMovements((prev) => {
            const fresh = Array.isArray(movementRows) ? movementRows.map((x) => normalizeInventoryMovement({ id: x.id || x.movementId, ...(x || {}) })) : [];
            return fresh.length > 0 || !isIosCapacitor() ? fresh : (Array.isArray(prev) && prev.length > 0 ? prev : fresh);
          });
          setAuditLogs((prev) => {
            const fresh = Array.isArray(auditRows) ? auditRows.map((x) => normalizeAuditLog({ id: x.id || x.logId, ...(x || {}) })) : [];
            return fresh.length > 0 || !isIosCapacitor() ? fresh : (Array.isArray(prev) && prev.length > 0 ? prev : fresh);
          });
          setTrackingEvents((prev) => {
            if (financeFastPath) return [];
            const fresh = Array.isArray(tRows) ? tRows : [];
            return fresh.length > 0 || !isIosCapacitor() ? fresh : (Array.isArray(prev) && prev.length > 0 ? prev : fresh);
          });
          setTrackingReady(true);
        } else {
          const [businessSnap, bSnap, sSnap, eSnap, cSnap, invSnap, movementSnap, auditSnap, tRows] = await Promise.all([
            getDoc(doc(db, `businesses/${businessId}`)),
            financeFastPath ? Promise.resolve({ docs: [] }) : getDocs(collection(db, `businesses/${businessId}/bookings`)),
            getDocs(collection(db, `businesses/${businessId}/services`)),
            getDocs(collection(db, `businesses/${businessId}/employees`)),
            getDocs(collection(db, `businesses/${businessId}/customers`)).catch(() => ({ docs: [] })),
            getDocs(collection(db, `businesses/${businessId}/inventoryItems`)).catch(() => ({ docs: [] })),
            getDocs(collection(db, `businesses/${businessId}/inventoryMovements`)).catch(() => ({ docs: [] })),
            getDocs(collection(db, `businesses/${businessId}/auditLogs`)).catch(() => ({ docs: [] })),
            financeFastPath ? Promise.resolve([]) : loadOptionalTracking(),
          ]);

          if (!mounted) return;
          setBusiness(businessSnap.exists() ? { id: businessSnap.id, ...(businessSnap.data() || {}) } : null);
          setBookings(assertSameCurrency(bSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })),businessSnap.data() || {}));
          setServices(sSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
          setEmployees(eSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
          setCustomers((cSnap.docs || []).map((d) => ({ id: d.id, ...(d.data() || {}) })));
          setInventoryItems((invSnap.docs || []).map((d) => normalizeInventoryItem({ id: d.id, ...(d.data() || {}) })));
          setInventoryMovements((movementSnap.docs || []).map((d) => normalizeInventoryMovement({ id: d.id, ...(d.data() || {}) })));
          setAuditLogs((auditSnap.docs || []).map((d) => normalizeAuditLog({ id: d.id, ...(d.data() || {}) })));
          setTrackingEvents(Array.isArray(tRows) ? tRows : []);
          setTrackingReady(true);
        }
      } catch (e) {
        console.error("[BusinessAnalytics] load failed", e);
        if (mounted) setError("No pude cargar las analíticas. Revisa permisos de Firestore o consola.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [businessId, db, reloadKey, financeFastPath]);



  useEffect(() => {
    let mounted = true;
    async function loadMembershipAnalytics() {
      if (!businessId || !canViewMembershipAnalytics) {
        setMembershipAnalytics(null);
        setMembershipAnalyticsError("");
        return;
      }
      setMembershipAnalyticsLoading(true);
      setMembershipAnalyticsError("");
      try {
        const firebaseApp = db?.app || db?._app;
        const functions = firebaseApp ? getFunctions(firebaseApp) : getFunctions();
        const callable = httpsCallable(functions, "getMembershipAnalytics");
        const res = await callable({ businessId });
        if (!mounted) return;
        setMembershipAnalytics(res?.data?.snapshot || null);
      } catch (e) {
        console.warn("[BusinessAnalytics] membership analytics skipped", e);
        if (mounted) setMembershipAnalyticsError("No pude cargar métricas de membresías todavía.");
      } finally {
        if (mounted) setMembershipAnalyticsLoading(false);
      }
    }
    loadMembershipAnalytics();
    return () => { mounted = false; };
  }, [businessId, db, reloadKey, canViewMembershipAnalytics]);

  const resetMembershipDetailPaging = () => {
    setMembershipDetailCursor(null);
    setMembershipDetailNextCursor(null);
    setMembershipDetailCursorStack([]);
    setMembershipDetailPage(1);
  };

  const openMembershipDetail = (view) => {
    const safeView = ["overview", "memberships", "calendar", "attention", "activity"].includes(view) ? view : "memberships";
    setMembershipSelectedRecord(null);
    setMembershipRecordOrigin("");
    setMembershipDetailView(safeView);
    setMembershipDetailRows([]);
    setMembershipDetailError("");
    setMembershipDetailSearch("");
    setMembershipDetailAppliedSearch("");
    setMembershipDetailFilter("all");
    setMembershipDetailRange(30);
    setMembershipDetailSummary(null);
    setMembershipDetailLocalPreview(false);
    resetMembershipDetailPaging();
  };

  const closeMembershipDetail = () => {
    setMembershipDetailView("");
    setMembershipDetailRows([]);
    setMembershipDetailError("");
    setMembershipDetailLocalPreview(false);
    setMembershipSelectedRecord(null);
    setMembershipRecordOrigin("");
  };

  const openMembershipRecord = (row, origin = "") => {
    if (!row) return;
    const contact = customers.find((customer) => customer.id === row.customerId) || {};
    setMembershipSelectedRecord({ ...row, customerEmail: contact.email || contact.emailNormalized || "", customerPhone: contact.phone || contact.phoneNormalized || "" });
    setMembershipRecordOrigin(String(origin || ""));
    setMembershipDetailView("record");
    setMembershipDetailError("");
    setMembershipDetailLocalPreview(false);
  };

  const returnFromMembershipRecord = () => {
    if (membershipRecordOrigin === "memberships") {
      setMembershipSelectedRecord(null);
      setMembershipRecordOrigin("");
      setMembershipDetailView("memberships");
      return;
    }
    closeMembershipDetail();
  };

  const applyMembershipDetailSearch = (event) => {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    setMembershipDetailAppliedSearch(String(membershipDetailSearch || "").trim());
    resetMembershipDetailPaging();
  };

  const changeMembershipDetailFilter = (value) => {
    setMembershipDetailFilter(String(value || "all"));
    resetMembershipDetailPaging();
  };

  const changeMembershipDetailRange = (value) => {
    setMembershipDetailRange(Number(value));
    resetMembershipDetailPaging();
  };

  const goMembershipDetailNext = () => {
    if (!membershipDetailHasMore || !membershipDetailNextCursor) return;
    setMembershipDetailCursorStack((prev) => [...prev, membershipDetailCursor]);
    setMembershipDetailCursor(membershipDetailNextCursor);
    setMembershipDetailPage((page) => page + 1);
  };

  const goMembershipDetailPrevious = () => {
    if (!membershipDetailCursorStack.length) return;
    const previousCursor = membershipDetailCursorStack[membershipDetailCursorStack.length - 1] || null;
    setMembershipDetailCursorStack((prev) => prev.slice(0, -1));
    setMembershipDetailCursor(previousCursor);
    setMembershipDetailPage((page) => Math.max(1, page - 1));
  };

  useEffect(() => {
    if (!membershipDetailView || !businessId) return undefined;

    if (["overview", "record"].includes(membershipDetailView)) {
      setMembershipDetailRows([]);
      setMembershipDetailLoading(false);
      setMembershipDetailError("");
      setMembershipDetailLocalPreview(false);
      setMembershipDetailTotal(null);
      setMembershipDetailSummary(null);
      return undefined;
    }

    let mounted = true;

    async function loadMembershipDetail() {
      setMembershipDetailLoading(true);
      setMembershipDetailError("");
      setMembershipDetailLocalPreview(false);

      try {
        const firebaseApp = db?.app || db?._app;
        const functions = firebaseApp ? getFunctions(firebaseApp) : getFunctions();
        const callable = httpsCallable(functions, "getMembershipAnalyticsDetailPage");
        const res = await callable({
          businessId,
          view: membershipDetailView,
          pageSize: 20,
          cursor: membershipDetailCursor || null,
          search: membershipDetailAppliedSearch || "",
          filter: membershipDetailFilter || "all",
          rangeDays: Number(membershipDetailRange) || 0,
        });

        if (!mounted) return;

        const payload = res?.data || {};
        const pageInfo = payload?.pageInfo || {};

        setMembershipDetailRows(Array.isArray(payload?.rows) ? payload.rows : []);
        setMembershipDetailNextCursor(pageInfo?.nextCursor || null);
        setMembershipDetailHasMore(pageInfo?.hasMore === true);
        setMembershipDetailTotal(pageInfo?.total !== null && pageInfo?.total !== undefined && Number.isFinite(Number(pageInfo.total)) ? Number(pageInfo.total) : null);
        setMembershipDetailSummary(payload?.summary || null);
      } catch (error) {
        console.warn("[BusinessAnalytics] membership detail page unavailable; using local preview", error);

        if (!mounted) return;

        const fallback = membershipDetailFallbackRows(
          membershipAnalytics,
          membershipDetailView,
          {
            search: membershipDetailAppliedSearch,
            filter: membershipDetailFilter,
            rangeDays: membershipDetailRange,
          }
        );

        setMembershipDetailRows(fallback.slice(0, 20));
        setMembershipDetailNextCursor(null);
        setMembershipDetailHasMore(false);
        setMembershipDetailTotal(fallback.length);
        setMembershipDetailSummary(null);
        setMembershipDetailLocalPreview(true);
        setMembershipDetailError("Vista previa local: la paginación completa se activará al publicar el endpoint de detalle.");
      } finally {
        if (mounted) setMembershipDetailLoading(false);
      }
    }

    loadMembershipDetail();

    return () => {
      mounted = false;
    };
  }, [
    businessId,
    db,
    membershipAnalytics,
    membershipDetailView,
    membershipDetailCursor,
    membershipDetailAppliedSearch,
    membershipDetailFilter,
    membershipDetailRange,
  ]);

  useEffect(() => {
    if (!membershipDetailView || typeof document === "undefined") return undefined;

    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMembershipDetailView("");
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [membershipDetailView]);

  const serviceMap = useMemo(() => {
    const map = new Map();
    (services || []).forEach((s) => {
      if (s?.id) map.set(String(s.id), s);
    });
    return map;
  }, [services]);

  const employeeMap = useMemo(() => {
    const map = new Map();
    (employees || []).forEach((e) => {
      if (e?.id) map.set(String(e.id), e);
    });
    return map;
  }, [employees]);

  const workerOptions = useMemo(() => {
    const map = new Map();
    (employees || []).forEach((e) => {
      if (e?.id) map.set(String(e.id), e.name || e.displayName || "Trabajador");
    });
    (bookings || []).forEach((b) => {
      const id = getWorkerId(b);
      if (id && !map.has(id)) map.set(id, getWorkerName(b, employeeMap));
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [employees, bookings, employeeMap]);

  // LOOP PHASE 3C.9.1 — Worker filter mobile carousel helper.
  // Important: this effect lives AFTER workerOptions is initialized.
  // V23 inserted it too early and caused:
  // "Cannot access 'workerOptions' before initialization".
  useEffect(() => {
    if (typeof document === "undefined") return;
    const row = document.querySelector(".business-analytics .ba-worker-pills-premium");
    if (!row) return;
    const active = row.querySelector("button.active");
    if (!active || typeof active.scrollIntoView !== "function") return;

    window.requestAnimationFrame(() => {
      try {
        active.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      } catch (_) {
        active.scrollIntoView(false);
      }
    });
  }, [workerFilter, workerOptions.length]);

  const periodRange = useMemo(() => {
    const now = new Date(anchorDate);
    let start = null;
    let end = null;
    if (period === "day") {
      start = startOfDaySafe(now);
      end = endExclusiveFromStart(start, "day");
    } else if (period === "week") {
      start = startOfWeek(now);
      end = endExclusiveFromStart(start, "week");
    } else if (period === "month") {
      start = startOfMonth(now);
      end = endExclusiveFromStart(start, "month");
    } else if (period === "custom") {
      let customStart = parseDateInputValue(customStartDate, false);
      let customEnd = parseDateInputValue(customEndDate, true);
      if (customStart && customEnd && customEnd < customStart) {
        const swapStart = startOfDaySafe(customEnd);
        const swapEnd = startOfDaySafe(customStart);
        customStart = swapStart;
        customEnd = swapEnd;
      }
      start = customStart;
      end = customEnd ? addDays(startOfDaySafe(customEnd), 1) : null;
      const labelStart = customStart ? dateLabelShort(customStart) : "Inicio";
      const labelEnd = customEnd ? dateLabelShort(customEnd) : "Fin";
      return { start:start ? wallToInstant(start,business || {}) : null, end:end ? wallToInstant(end,business || {}) : null, label: `${labelStart} - ${labelEnd}` };
    }
    return { start:start ? wallToInstant(start,business || {}) : null, end:end ? wallToInstant(end,business || {}) : null, label: formatPeriodLabel(period, now) };
  }, [period, anchorDate, customStartDate, customEndDate,business]);

  const data = useMemo(() => {
    const { start, end } = periodRange;
    const allInPeriod = (bookings || [])
      .map((b) => ({ ...b, __date: getBookingDate(b), __createdDate: getCreatedDate(b), __status: normalizeStatus(b.status || b.confirmationStatus) }))
      .filter((b) => {
        const d = b.__date || b.__createdDate;
        if (!d) return false;
        if (start && end && !(d >= start && d < end)) return false;
        if (workerFilter !== "all") return String(getWorkerId(b)) === String(workerFilter);
        return true;
      });

    const deletedBookings = allInPeriod.filter((b) => b.__status === "deleted");
    const operationalBookings = allInPeriod.filter((b) => b.__status !== "deleted");
    const revenueBookings = operationalBookings.filter((b) => ["confirmed", "no_show"].includes(b.__status) || eventFinanceSummary(b)?.hasActivity);
    const activeBookings = operationalBookings.filter((b) => b.__status !== "cancelled");
    const completedBookings = operationalBookings.filter((b) => b.__status === "confirmed");
    const pendingBookings = operationalBookings.filter((b) => b.__status === "pending");
    const cancelledBookings = operationalBookings.filter((b) => b.__status === "cancelled");
    const noShowBookings = operationalBookings.filter((b) => b.__status === "no_show");
    const otherStatusBookings = operationalBookings.filter((b) => !["confirmed", "pending", "cancelled", "no_show"].includes(b.__status));

    const revenueByService = new Map();
    const countByService = new Map();
    const revenueByWorker = new Map();
    const countByWorker = new Map();
    const ageBuckets = new Map();
    const byDay = new Map();
    const byHour = new Map();
    const byWeekday = new Map();
    const customerCounts = new Map();
    const paymentMethodMap = new Map();
    const paymentStatusMap = new Map();
    const collectedByMethod = new Map();
    const cashByMethod = new Map();
    const coveredByMethod = new Map();
    const payoutByWorker = new Map();
    const payoutPendingByWorker = new Map();
    const payoutPaidByWorker = new Map();
    const materialCostByService = new Map();
    const materialCostByWorker = new Map();
    const netByWorker = new Map();
    const unpaidBookings = [];
    const untrackedRevenueBookings = [];
    const coveredRevenueBookings = [];
    const paymentMismatchBookings = [];
    const financeRows = [];

    let revenue = 0;
    let collectedRevenue = 0;
    let pendingRevenue = 0;
    let paymentMismatchRevenue = 0;
    let trackedRevenueBase = 0;
    let trackedRevenueBookingsCount = 0;
    let untrackedExpectedRevenue = 0;
    let coveredRevenueTotal = 0;
    let cashReceivedTotal = 0;
    let refundedRevenue = 0;
    let totalDiscount = 0;
    let totalTips = 0;
    let workerPayoutTotal = 0;
    let workerPayoutPendingTotal = 0;
    let workerPayoutPaidTotal = 0;
    let materialCostTotal = 0;
    let productSalesRevenueTotal = 0;
    let productSalesCostTotal = 0;
    let productSalesPayoutTotal = 0;
    let lostRevenue = 0;
    let totalDuration = 0;
    let totalLeadDays = 0;
    let leadCount = 0;

    operationalBookings.forEach((b) => {
      const eventFinance = eventFinanceSummary(b);
    const amount = eventFinance ? eventFinance.amount : getBookingRevenue(b, serviceMap);
      const serviceName = getServiceName(b, serviceMap);
      const workerName = getWorkerName(b, employeeMap);
      const isRevenueBooking = ["confirmed", "no_show"].includes(b.__status) || eventFinance?.hasActivity;
      const dayKey = (b.__date || b.__createdDate)?.toISOString().slice(0, 10);
      const rawPaymentStatus = String(b?.paymentStatus ?? b?.payment?.status ?? b?.finance?.paymentStatus ?? "").trim();
      const rawPaymentMethod = String(b?.paymentMethod ?? b?.payment?.method ?? b?.finance?.paymentMethod ?? "").trim();
      const paymentStatus = normalizePaymentStatus(rawPaymentStatus, b);
      const paymentMethod = normalizePaymentMethod(rawPaymentMethod);
      const paymentHasTrace = bookingHasPaymentTrace(b);
      const paymentIsCovered = paymentStatus === "covered" || isCoveredPaymentMethod(paymentMethod);
      const paymentIsRefunded = !eventFinance && (paymentStatus === "refunded" || getBookingRefundAmount(b) > 0);
      const paymentIsLoyaltyReward = paymentIsCovered && paymentMethod === "loyalty_reward";
      const paymentIsMembership = paymentIsCovered && paymentMethod === "membership";
      const lifecycle = bookingLifecycleFromRecord(b || {});
      const serviceCollectedAmount = paymentIsCovered ? 0 : Math.max(0, numberFrom(lifecycle?.serviceCollectedAmount));
      const coveredServiceAmount = paymentIsCovered ? Math.max(0, numberFrom(b?.coveredAmount ?? b?.payment?.coveredAmount ?? b?.benefitCoveredAmount ?? b?.membershipCoveredAmount ?? b?.payment?.membershipCoveredAmount ?? amount)) : 0;
      const loyaltyCoveredAmount = paymentIsLoyaltyReward ? Math.max(0, numberFrom(b?.loyaltyBusinessSubsidyAmount ?? b?.payment?.loyaltyBusinessSubsidyAmount ?? b?.loyaltyBusinessAbsorbedAmount ?? coveredServiceAmount)) : 0;
      const discountAmount = getBookingDiscountAmount(b);
      const tipAmount = getBookingTipAmount(b);
      const tipCashMethod = getBookingTipPaymentMethod(b, paymentMethod);
      const cashMethod = normalizeCashMethod(paymentMethod, "unknown");
      const serviceCommissionAmount = getBookingServiceCommissionAmount(b, amount);
      const workerTipAmount = getBookingWorkerTipAmount(b);
      const productSalesPayout = getBookingProductSalesPayout(b);
      const workerPayout = estimateWorkerPayout(b, amount);
      const payoutStatus = String(b.workerPayoutStatus || b?.payment?.workerPayoutStatus || "pending").trim().toLowerCase() === "paid" ? "paid" : "pending";
      const inventoryCost = getBookingInventoryCost(b);
      const productSalesTotal = getBookingProductSalesTotal(b);
      const productSalesCost = getBookingProductSalesCost(b);
      const serviceRevenueAmount = Math.max(0, amount - productSalesTotal);
      const explicitGrossCollectedAmount = getBookingExplicitGrossCollectedAmount(b);
      const canonicalGrossCollectedAmount = paymentIsCovered ? 0 : Math.max(0, getBookingCollectedAmount(b, amount));
      const grossSalesCollectedCandidate = Math.max(
        0,
        (explicitGrossCollectedAmount > 0
          ? explicitGrossCollectedAmount
          : ["paid", "partial"].includes(paymentStatus)
            ? canonicalGrossCollectedAmount
            : serviceCollectedAmount) - tipAmount
      );
      const collectedAmount = eventFinance ? eventFinance.net : paymentHasTrace && !paymentIsCovered && !paymentIsRefunded
        ? Math.min(amount, grossSalesCollectedCandidate)
        : 0;
      const pendingAmount = eventFinance ? eventFinance.pending : paymentHasTrace && !paymentIsCovered && !paymentIsRefunded && ["pending", "partial"].includes(paymentStatus)
        ? Math.max(0, amount - collectedAmount)
        : 0;
      const paymentMismatchAmount = eventFinance ? 0 : paymentHasTrace && !paymentIsCovered && !paymentIsRefunded && paymentStatus === "paid"
        ? Math.max(0, amount - collectedAmount)
        : 0;
      const untrackedExpectedAmount = !paymentHasTrace && !paymentIsCovered && !paymentIsRefunded ? amount : 0;
      const coveredTrackedAmount = paymentIsCovered ? Math.min(amount, coveredServiceAmount || amount) : 0;
      const trackedBaseAmount = eventFinance ? eventFinance.net + eventFinance.pending : paymentHasTrace && !paymentIsCovered && !paymentIsRefunded ? amount : 0;
      const cashBaseAmount = collectedAmount;
      const serviceCashAmount = Math.min(serviceRevenueAmount, cashBaseAmount);
      const productCashAmount = Math.max(0, cashBaseAmount - serviceCashAmount);
      const membershipBusinessAmount = paymentIsMembership
        ? Math.max(0, numberFrom(
          b?.membershipBusinessIncomeAmount ??
          b?.payment?.membershipBusinessIncomeAmount ??
          b?.membershipCoveredAmount ??
          b?.payment?.membershipCoveredAmount ??
          coveredServiceAmount
        ))
        : Math.max(0, numberFrom(b?.membershipBusinessIncomeAmount ?? b?.payment?.membershipBusinessIncomeAmount ?? 0));
      const serviceRevenueDisplayAmount = Math.max(0, serviceRevenueAmount - membershipBusinessAmount);
      const totalBusinessAmount = serviceRevenueDisplayAmount + productSalesTotal + membershipBusinessAmount;
      const membershipWorkerIncomeAmount = paymentIsMembership
        ? Math.max(0, numberFrom(
          b?.membershipWorkerIncomeAmount ??
          b?.payment?.membershipWorkerIncomeAmount ??
          b?.membershipWorkerPayoutAmount ??
          b?.payment?.membershipWorkerPayoutAmount ??
          Math.max(0, workerPayout - productSalesPayout - workerTipAmount)
        ))
        : Math.max(0, numberFrom(b?.membershipWorkerIncomeAmount ?? b?.payment?.membershipWorkerIncomeAmount ?? 0));
      const serviceCommissionDisplayAmount = Math.max(0, serviceCommissionAmount - membershipWorkerIncomeAmount);
      const loyaltyWorkerPayoutImpact = paymentIsLoyaltyReward ? workerPayout : 0;
      const loyaltyMaterialCostImpact = paymentIsLoyaltyReward ? inventoryCost : 0;
      const loyaltyProductCostImpact = paymentIsLoyaltyReward ? productSalesCost : 0;
      const loyaltyBusinessCashImpact = paymentIsLoyaltyReward
        ? numberFrom(b?.loyaltyBusinessCashImpactAmount ?? b?.payment?.loyaltyBusinessCashImpactAmount ?? -(loyaltyWorkerPayoutImpact + loyaltyMaterialCostImpact + loyaltyProductCostImpact))
        : 0;

      // Cash-aware net:
      // Paid booking: collected cash + tips - payouts - costs.
      // Loyalty/courtesy/membership covered booking: $0 client cash, but payouts/costs still hit the business.
      const estimatedBusinessNet = cashBaseAmount + tipAmount - workerPayout - inventoryCost - productSalesCost;

      if (isRevenueBooking) {
        collectedRevenue += collectedAmount;
        pendingRevenue += pendingAmount;
        paymentMismatchRevenue += paymentMismatchAmount;
        trackedRevenueBase += trackedBaseAmount;
        if (trackedBaseAmount > 0) trackedRevenueBookingsCount += 1;
        untrackedExpectedRevenue += untrackedExpectedAmount;
        coveredRevenueTotal += coveredTrackedAmount;
        cashReceivedTotal += collectedAmount + tipAmount;
        refundedRevenue += eventFinance ? eventFinance.refunded : paymentIsRefunded ? amount : 0;
        if (untrackedExpectedAmount > 0) untrackedRevenueBookings.push(b);
        if (coveredTrackedAmount > 0) coveredRevenueBookings.push(b);
        if (paymentMismatchAmount > 0) paymentMismatchBookings.push(b);
        totalDiscount += discountAmount;
        totalTips += tipAmount;
        workerPayoutTotal += workerPayout;
        materialCostTotal += inventoryCost;
        productSalesRevenueTotal += productSalesTotal;
        productSalesCostTotal += productSalesCost;
        productSalesPayoutTotal += productSalesPayout;
        groupAdd(materialCostByService, serviceName, inventoryCost);
        groupAdd(materialCostByWorker, workerName, inventoryCost);
        if (payoutStatus === "paid") {
          workerPayoutPaidTotal += workerPayout;
          groupAdd(payoutPaidByWorker, workerName, workerPayout);
        } else {
          workerPayoutPendingTotal += workerPayout;
          groupAdd(payoutPendingByWorker, workerName, workerPayout);
        }
        groupAdd(paymentMethodMap, paymentMethodLabel(paymentMethod), amount);
        groupAdd(paymentStatusMap, paymentStatus, 1);
        if (cashBaseAmount > 0) {
          groupAdd(collectedByMethod, paymentMethodLabel(cashMethod), cashBaseAmount);
          const currentCash = cashByMethod.get(cashMethod) || { method: cashMethod, label: paymentMethodLabel(cashMethod), service: 0, product: 0, tip: 0, total: 0 };
          currentCash.service += serviceCashAmount;
          currentCash.product += productCashAmount;
          currentCash.total += cashBaseAmount;
          cashByMethod.set(cashMethod, currentCash);
        }
        if (tipAmount > 0) {
          groupAdd(collectedByMethod, paymentMethodLabel(tipCashMethod), tipAmount);
          const currentTip = cashByMethod.get(tipCashMethod) || { method: tipCashMethod, label: paymentMethodLabel(tipCashMethod), service: 0, product: 0, tip: 0, total: 0 };
          currentTip.tip += tipAmount;
          currentTip.total += tipAmount;
          cashByMethod.set(tipCashMethod, currentTip);
        }
        if (paymentIsCovered && coveredServiceAmount > 0) {
          groupAdd(coveredByMethod, coveredMethodLabel(paymentMethod), coveredServiceAmount);
        }
        groupAdd(payoutByWorker, workerName, workerPayout);
        groupAdd(netByWorker, workerName, estimatedBusinessNet);
        financeRows.push({
          id: b.id || b.bookingId || `${dayKey}-${financeRows.length}`,
          date: b.__date || b.__createdDate,
          client: b.customerName || b.clientName || b.name || "Cliente",
          service: serviceName,
          worker: workerName,
          amount: totalBusinessAmount,
          serviceRevenueAmount,
          serviceRevenueDisplayAmount,
          productSalesAmount: productSalesTotal,
          membershipBusinessAmount,
          totalBusinessAmount,
          collected: collectedAmount,
          cashBaseAmount,
          cashReceivedAmount: collectedAmount + tipAmount,
          grossCollectedAmount: canonicalGrossCollectedAmount,
          paymentTracked: paymentHasTrace,
          untrackedExpectedAmount,
          trackedBaseAmount,
          coveredServiceAmount,
          coveredTrackedAmount,
          loyaltyCoveredAmount,
          paymentMethod,
          paymentIsLoyaltyReward,
          loyaltyWorkerPayoutImpact,
          loyaltyMaterialCostImpact,
          loyaltyProductCostImpact,
          loyaltyBusinessCashImpact,
          pending: pendingAmount,
          paymentMismatchAmount,
          tip: tipAmount,
          tipPaymentMethod: tipCashMethod,
          tipPaymentMethodLabel: paymentMethodLabel(tipCashMethod),
          workerTipAmount,
          serviceCommissionAmount,
          serviceCommissionDisplayAmount,
          membershipWorkerIncomeAmount,
          method: paymentMethodLabel(paymentMethod),
          status: paymentStatus,
          payout: workerPayout,
          payoutStatus,
          inventoryCost,
          productSalesTotal,
          productSalesCost,
          productSalesPayout,
          workerPaymentNotes: b.workerPaymentNotes || b?.payment?.workerPaymentNotes || "",
          net: estimatedBusinessNet,
        });
        if (pendingAmount > 0) unpaidBookings.push(b);
        revenue += amount;
        groupAdd(revenueByService, serviceName, amount);
        groupAdd(countByService, serviceName, 1);
        groupAdd(revenueByWorker, workerName, amount);
        groupAdd(countByWorker, workerName, 1);
        totalDuration += numberFrom(b.duration || b.durationMins || b.minutes || b.visitMinutes);
      }

      if (["cancelled", "no_show"].includes(b.__status)) lostRevenue += amount;

      if (dayKey) {
        const current = byDay.get(dayKey) || { key: dayKey, revenue: 0, count: 0 };
        if (isRevenueBooking) current.revenue += amount;
        if (b.__status !== "cancelled") current.count += 1;
        byDay.set(dayKey, current);
      }

      if (b.__date && b.__status !== "cancelled") {
        const hour = b.__date.getHours();
        groupAdd(byHour, `${String(hour).padStart(2, "0")}:00`, 1);
        const weekday = b.__date.toLocaleDateString("es-PA", { weekday: "short" }).replace(".", "");
        groupAdd(byWeekday, weekday, 1);
      }

      const customer = getCustomerKey(b);
      if (customer) groupAdd(customerCounts, customer, 1);

      const age = getCustomerAge(b);
      if (age) groupAdd(ageBuckets, ageBucket(age), 1);

      if (b.__date && b.__createdDate && b.__date >= b.__createdDate) {
        const days = (b.__date.getTime() - b.__createdDate.getTime()) / (24 * 60 * 60 * 1000);
        if (Number.isFinite(days) && days >= 0 && days <= 365) {
          totalLeadDays += days;
          leadCount += 1;
        }
      }
    });

    const uniqueCustomers = customerCounts.size;
    const recurrentCustomers = Array.from(customerCounts.values()).filter((count) => count > 1).length;
    const newCustomers = Math.max(0, uniqueCustomers - recurrentCustomers);

    const trackingInPeriod = (trackingEvents || [])
      .map((e) => ({ ...e, __date: eventDate(e), __type: eventType(e) }))
      .filter((e) => {
        if (!e.__date) return false;
        if (start && end && !(e.__date >= start && e.__date < end)) return false;
        return true;
      });

    const pageViewEvents = trackingInPeriod.filter((e) => isEventType(e, ["business_page_view", "page_view", "view", "business_view", "profile_view"]));
    const reserveClickEvents = trackingInPeriod.filter((e) => isEventType(e, ["reserve_click", "book_now_click", "booking_opened", "booking_open"]));
    const bookingStartedEvents = trackingInPeriod.filter((e) => isEventType(e, ["booking_started"]));
    const serviceClickEvents = trackingInPeriod.filter((e) => isEventType(e, ["service_clicked", "service_click", "service_view", "service_selected"]));
    const workerSelectedEvents = trackingInPeriod.filter((e) => isEventType(e, ["worker_selected"]));
    const bookingCompletedEvents = trackingInPeriod.filter((e) => isEventType(e, ["booking_completed", "booking_created", "reservation_created"]));
    const allFunnelEvents = pageViewEvents.concat(reserveClickEvents, bookingStartedEvents, serviceClickEvents, workerSelectedEvents, bookingCompletedEvents);
    const uniqueVisitors = new Set(allFunnelEvents.map(eventVisitorKey).filter(Boolean)).size;
    const sourceMap = new Map();
    allFunnelEvents.forEach((e) => groupAdd(sourceMap, eventSource(e), 1));

    const serviceInterestMap = new Map();
    serviceClickEvents.forEach((e) => groupAdd(serviceInterestMap, e?.serviceName || e?.serviceTitle || e?.serviceId || "Servicio", 1));

    const pageViews = pageViewEvents.length;
    const reserveClicks = reserveClickEvents.length;
    const bookingStarts = bookingStartedEvents.length || reserveClicks;
    const serviceInterest = serviceClickEvents.length;
    const workerSelections = workerSelectedEvents.length;
    const completedFromTracking = bookingCompletedEvents.length;
    const completedForConversion = completedFromTracking || completedBookings.length;

    return {
      allInPeriod,
      operationalBookings,
      deletedBookings,
      otherStatusBookings,
      revenueBookings,
      activeBookings,
      completedBookings,
      pendingBookings,
      cancelledBookings,
      noShowBookings,
      revenue,
      lostRevenue,
      ticketAvg: revenueBookings.length ? revenue / revenueBookings.length : 0,
      totalDuration,
      uniqueCustomers,
      recurrentCustomers,
      newCustomers,
      totalLeadDays,
      leadCount,
      avgLeadDays: leadCount ? totalLeadDays / leadCount : 0,
      revenueByService,
      countByService,
      revenueByWorker,
      countByWorker,
      ageBuckets,
      byDay,
      byHour,
      byWeekday,
      trackingInPeriod,
      pageViews,
      reserveClicks,
      bookingIntent: reserveClicks,
      bookingStarts,
      serviceInterest,
      workerSelections,
      serviceInterestMap,
      completedFromTracking,
      completedForConversion,
      uniqueVisitors,
      sourceMap,
      conversionRate: pageViews ? clampPercent((completedForConversion / pageViews) * 100) : 0,
      intentRate: pageViews ? clampPercent((reserveClicks / pageViews) * 100) : 0,
      startRate: reserveClicks ? clampPercent((bookingStarts / reserveClicks) * 100) : 0,
      completionRate: bookingStarts ? clampPercent((completedForConversion / bookingStarts) * 100) : 0,
      topService: topEntry(countByService),
      topMoneyService: topEntry(revenueByService),
      topWorker: topEntry(revenueByWorker),
      peakHour: topEntry(byHour),
      peakDay: topEntry(byWeekday),
      paymentMethodMap,
      paymentStatusMap,
      collectedByMethod,
      cashByMethod,
      coveredByMethod,
      payoutByWorker,
      payoutPendingByWorker,
      payoutPaidByWorker,
      netByWorker,
      unpaidBookings,
      untrackedRevenueBookings,
      coveredRevenueBookings,
      financeRows: financeRows.sort((a, b) => (b.date?.getTime?.() || 0) - (a.date?.getTime?.() || 0)),
      collectedRevenue,
      pendingRevenue,
      paymentMismatchRevenue,
      paymentMismatchBookings,
      trackedRevenueBase,
      trackedRevenueBookingsCount,
      untrackedExpectedRevenue,
      coveredRevenueTotal,
      cashReceivedTotal,
      refundedRevenue,
      totalDiscount,
      totalTips,
      workerPayoutTotal,
      workerPayoutPendingTotal,
      workerPayoutPaidTotal,
      materialCostTotal,
      productSalesRevenueTotal,
      productSalesCostTotal,
      productSalesPayoutTotal,
      materialCostByService,
      materialCostByWorker,
      businessNetRevenue: collectedRevenue + totalTips - workerPayoutTotal - materialCostTotal - productSalesCostTotal,
      trackedReconciliationGap: Math.max(0, trackedRevenueBase - collectedRevenue - pendingRevenue - paymentMismatchRevenue),
      financeReconciliationGap: Math.max(0, revenue - collectedRevenue - pendingRevenue - paymentMismatchRevenue - untrackedExpectedRevenue - coveredRevenueTotal - refundedRevenue),
      collectionRate: trackedRevenueBase ? Math.round((collectedRevenue / trackedRevenueBase) * 100) : 0,
      periodLabel: periodRange.label,
    };
  }, [bookings, trackingEvents, serviceMap, employeeMap, periodRange, workerFilter]);

  const financeV3ComparisonScope = useMemo(
    () => buildFinanceV3ComparisonScope(period, periodRange, anchorDate, business),
    [period, periodRange, anchorDate, business]
  );

  const financeV3LocalCurrentData = useMemo(
    () => buildFinanceV3Snapshot({
      bookings,
      start: financeV3ComparisonScope.currentStart,
      end: financeV3ComparisonScope.currentEnd,
      workerFilter,
      serviceMap,
      employeeMap,
    }),
    [bookings, financeV3ComparisonScope.currentStart, financeV3ComparisonScope.currentEnd, workerFilter, serviceMap, employeeMap]
  );

  const financeV3LocalPreviousData = useMemo(
    () => financeV3ComparisonScope.enabled
      ? buildFinanceV3Snapshot({
        bookings,
        start: financeV3ComparisonScope.previousStart,
        end: financeV3ComparisonScope.previousEnd,
        workerFilter,
        serviceMap,
        employeeMap,
      })
      : null,
    [bookings, financeV3ComparisonScope.enabled, financeV3ComparisonScope.previousStart, financeV3ComparisonScope.previousEnd, workerFilter, serviceMap, employeeMap]
  );

  const financeRemote = useFinanceRemoteCutover({
    db,
    businessId,
    enabled: isOwnerOrAdmin,
    profitabilityEnabled: activeView === "finance" && isOwnerOrAdmin && financeWorkspaceView === "profitability",
    agingEnabled: activeView === "finance" && isOwnerOrAdmin && financeWorkspaceView === "cash",
    currentStart: financeV3ComparisonScope.currentStart,
    currentEnd: financeV3ComparisonScope.currentEnd,
    forecastEnd: periodRange.end,
    comparisonEnabled: financeV3ComparisonScope.enabled,
    previousStart: financeV3ComparisonScope.previousStart,
    previousEnd: financeV3ComparisonScope.previousEnd,
    workerId: workerFilter,
    detailView: financeDetailView,
    detailSearch: financeDetailSearch,
    detailPage: financeDetailPage,
    detailPageSize: 20,
  });

  useEffect(() => {
    if (!isOwnerOrAdmin || activeView !== "finance") {
      if (financeRemoteFallbackLoad) setFinanceRemoteFallbackLoad(false);
      return;
    }
    if (financeRemote.rangeStatus === "error" && !financeRemoteFallbackLoad) {
      setFinanceRemoteFallbackLoad(true);
      return;
    }
    if (financeRemote.rangeStatus === "ready" && financeRemoteFallbackLoad) {
      setFinanceRemoteFallbackLoad(false);
    }
  }, [activeView, isOwnerOrAdmin, financeRemote.rangeStatus, financeRemoteFallbackLoad]);

  const financeV3CurrentData = useMemo(
    () => financeRemote.rangeReady
      ? overlayFinanceSummary(financeV3LocalCurrentData, financeRemote.current, financeRemote.currentUiRows)
      : financeV3LocalCurrentData,
    [financeRemote.rangeReady, financeRemote.current, financeV3LocalCurrentData]
  );

  const financeV3PreviousData = useMemo(
    () => financeV3ComparisonScope.enabled
      ? (financeRemote.rangeReady && financeRemote.previous
        ? overlayFinanceSummary(financeV3LocalPreviousData, financeRemote.previous)
        : financeV3LocalPreviousData)
      : null,
    [financeRemote.rangeReady, financeRemote.previous, financeV3ComparisonScope.enabled, financeV3LocalPreviousData]
  );

  const financeData = useMemo(
    () => ({ ...financeV3CurrentData, periodLabel: data.periodLabel }),
    [financeV3CurrentData, data.periodLabel]
  );

  const financeV3SortedCashMethods = Array.from((financeData.cashByMethod || new Map()).values())
    .sort((a, b) => safeMoneyValue(b.total) - safeMoneyValue(a.total))
    .slice(0, 8);

  const financeV3SortedCoveredMethods = Array.from((financeData.coveredByMethod || new Map()).entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);

  const financeV3Comparison = useMemo(() => {
    if (!financeV3ComparisonScope.enabled || !financeV3PreviousData) {
      return { enabled: false, label: "", metrics: {} };
    }

    const buildMetric = (currentValue, previousValue, preference = "higher") => {
      const current = safeMoneyValue(currentValue);
      const previous = safeMoneyValue(previousValue);
      const delta = current - previous;
      const epsilon = 0.005;
      const direction = Math.abs(delta) <= epsilon ? "flat" : delta > 0 ? "up" : "down";
      const percent = Math.abs(previous) > epsilon ? (delta / Math.abs(previous)) * 100 : null;
      const favorable = direction === "flat"
        ? null
        : preference === "lower"
          ? direction === "down"
          : direction === "up";
      const tone = favorable === null ? "neutral" : favorable ? "success" : "warning";
      const arrow = direction === "up" ? "↑" : direction === "down" ? "↓" : "→";
      const text = percent === null
        ? (Math.abs(current) <= epsilon ? "Sin cambio" : "Sin base anterior")
        : `${arrow} ${Math.abs(percent).toFixed(1)}%`;

      return { current, previous, delta, direction, percent, tone, text };
    };

    return {
      enabled: true,
      label: financeV3ComparisonScope.label,
      metrics: {
        revenue: buildMetric(financeData.revenue, financeV3PreviousData.revenue, "higher"),
        collected: buildMetric(financeData.collectedRevenue, financeV3PreviousData.collectedRevenue, "higher"),
        pending: buildMetric(financeData.pendingRevenue, financeV3PreviousData.pendingRevenue, "lower"),
        result: buildMetric(
          safeMoneyValue(financeData.businessNetRevenue) - (isOwnerOrAdmin ? safeMoneyValue(expenseLedgerSummary.paidTotal) : 0),
          safeMoneyValue(financeV3PreviousData.businessNetRevenue) - (isOwnerOrAdmin ? safeMoneyValue(expenseLedgerSummary.previousPaidTotal) : 0),
          "higher"
        ),
      },
    };
  }, [financeData, financeV3ComparisonScope.enabled, financeV3ComparisonScope.label, financeV3PreviousData, isOwnerOrAdmin, expenseLedgerSummary.paidTotal, expenseLedgerSummary.previousPaidTotal]);

  const financeV3Drivers = useMemo(() => {
    if (!financeV3Comparison.enabled || !financeV3PreviousData) {
      return { enabled: false };
    }

    const epsilon = 0.005;
    const buildMoves = (currentMap, previousMap) => {
      const current = currentMap instanceof Map ? currentMap : new Map();
      const previous = previousMap instanceof Map ? previousMap : new Map();
      const keys = new Set([...current.keys(), ...previous.keys()]);
      return Array.from(keys)
        .map((key) => {
          const currentValue = safeMoneyValue(current.get(key));
          const previousValue = safeMoneyValue(previous.get(key));
          return { key, current: currentValue, previous: previousValue, delta: currentValue - previousValue };
        })
        .filter((row) => Math.abs(row.delta) > epsilon)
        .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta) || b.delta - a.delta || String(a.key).localeCompare(String(b.key)));
    };

    const salesDelta = safeMoneyValue(financeData.revenue) - safeMoneyValue(financeV3PreviousData.revenue);
    const currentCount = Array.isArray(financeData.revenueBookings) ? financeData.revenueBookings.length : 0;
    const previousCount = Array.isArray(financeV3PreviousData.revenueBookings) ? financeV3PreviousData.revenueBookings.length : 0;
    const bookingDelta = currentCount - previousCount;
    const currentTicket = currentCount ? safeMoneyValue(financeData.revenue) / currentCount : 0;
    const previousTicket = previousCount ? safeMoneyValue(financeV3PreviousData.revenue) / previousCount : 0;
    const ticketDelta = currentTicket - previousTicket;
    const bridgeAvailable = currentCount > 0 && previousCount > 0;
    const volumeImpact = bridgeAvailable ? bookingDelta * ((currentTicket + previousTicket) / 2) : 0;
    const ticketImpact = bridgeAvailable ? ticketDelta * ((currentCount + previousCount) / 2) : 0;
    const serviceMoves = buildMoves(financeData.revenueByService, financeV3PreviousData.revenueByService);
    const workerMoves = buildMoves(financeData.revenueByWorker, financeV3PreviousData.revenueByWorker);
    const direction = Math.abs(salesDelta) <= epsilon ? "flat" : salesDelta > 0 ? "up" : "down";
    const headline = direction === "flat"
      ? `Ventas estables ${financeV3Comparison.label}.`
      : `Ventas ${direction === "up" ? "crecieron" : "bajaron"} ${MONEY.format(Math.abs(salesDelta))} ${financeV3Comparison.label}.`;

    const detailParts = [];
    if (serviceMoves[0]) detailParts.push(`Mayor movimiento por servicio: ${serviceMoves[0].key} ${financeV3SignedMoney(serviceMoves[0].delta, MONEY)}`);
    if (workerMoves[0] && workerFilter === "all") detailParts.push(`por equipo: ${workerMoves[0].key} ${financeV3SignedMoney(workerMoves[0].delta, MONEY)}`);

    return {
      enabled: true,
      salesDelta,
      direction,
      headline,
      detail: detailParts.length ? `${detailParts.join(" · ")}.` : "Sin movimientos relevantes por servicio o equipo.",
      currentCount,
      previousCount,
      bookingDelta,
      currentTicket,
      previousTicket,
      ticketDelta,
      bridgeAvailable,
      volumeImpact,
      ticketImpact,
      serviceMoves: serviceMoves.slice(0, 3),
      workerMoves: workerMoves.slice(0, 3),
    };
  }, [financeData, financeV3Comparison.enabled, financeV3Comparison.label, financeV3PreviousData, workerFilter, MONEY]);

  const sortedServiceRevenue = Array.from(data.revenueByService.entries()).sort((a, b) => b[1] - a[1]).slice(0, 7);
  const sortedServiceCount = Array.from(data.countByService.entries()).sort((a, b) => b[1] - a[1]).slice(0, 7);
  const sortedWorkerRevenue = Array.from(data.revenueByWorker.entries()).sort((a, b) => b[1] - a[1]).slice(0, 7);
  const sortedSources = Array.from(data.sourceMap.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const sortedServiceInterest = Array.from((data.serviceInterestMap || new Map()).entries()).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const sortedPaymentMethods = Array.from(data.paymentMethodMap.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const sortedCollectedMethods = Array.from(data.collectedByMethod.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const sortedCashMethods = Array.from((data.cashByMethod || new Map()).values()).sort((a, b) => safeMoneyValue(b.total) - safeMoneyValue(a.total)).slice(0, 8);
  const sortedCoveredMethods = Array.from((data.coveredByMethod || new Map()).entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const sortedPayouts = Array.from(data.payoutByWorker.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const sortedPendingPayouts = Array.from(data.payoutPendingByWorker.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const sortedPaidPayouts = Array.from(data.payoutPaidByWorker.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const sortedNetByWorker = Array.from(data.netByWorker.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const sortedMaterialCosts = Array.from((data.materialCostByService || new Map()).entries()).sort((a, b) => b[1] - a[1]).slice(0, 8);

  const workerFinanceSummary = useMemo(() => {
    const map = new Map();
    (financeData.financeRows || []).forEach((row) => {
      const worker = String(row?.worker || "Sin trabajador").trim() || "Sin trabajador";
      const current = map.get(worker) || {
        worker,
        attended: 0,
        tips: 0,
        serviceSales: 0,
        productSales: 0,
        businessMembershipIncome: 0,
        totalBusiness: 0,
        sales: 0,
        collected: 0,
        pending: 0,
        serviceCommission: 0,
        productCommission: 0,
        workerMembershipIncome: 0,
        commission: 0,
        totalPayout: 0,
        net: 0,
        loyaltyCovered: 0,
        loyaltyPayoutImpact: 0,
        loyaltyCostImpact: 0,
        loyaltyCashImpact: 0,
      };
      current.attended += 1;
      current.tips += safeMoneyValue(row?.workerTipAmount ?? row?.tip);
      current.serviceSales += safeMoneyValue(row?.serviceRevenueDisplayAmount ?? row?.serviceRevenueAmount ?? row?.amount);
      current.productSales += safeMoneyValue(row?.productSalesAmount ?? row?.productSalesTotal);
      current.businessMembershipIncome += safeMoneyValue(row?.membershipBusinessAmount);
      current.totalBusiness += safeMoneyValue(row?.totalBusinessAmount ?? row?.amount);
      current.sales = current.totalBusiness;
      current.collected += safeMoneyValue(row?.collected);
      current.pending += safeMoneyValue(row?.pending);
      current.serviceCommission += safeMoneyValue(row?.serviceCommissionDisplayAmount ?? row?.serviceCommissionAmount);
      current.productCommission += safeMoneyValue(row?.productSalesPayout);
      current.workerMembershipIncome += safeMoneyValue(row?.membershipWorkerIncomeAmount);
      current.commission += safeMoneyValue(row?.serviceCommissionDisplayAmount ?? row?.serviceCommissionAmount) + safeMoneyValue(row?.productSalesPayout) + safeMoneyValue(row?.membershipWorkerIncomeAmount);
      current.totalPayout += safeMoneyValue(row?.payout);
      current.net += safeMoneyValue(row?.net);
      current.loyaltyCovered += safeMoneyValue(row?.loyaltyCoveredAmount);
      current.loyaltyPayoutImpact += safeMoneyValue(row?.loyaltyWorkerPayoutImpact);
      current.loyaltyCostImpact += safeMoneyValue(row?.loyaltyMaterialCostImpact) + safeMoneyValue(row?.loyaltyProductCostImpact);
      current.loyaltyCashImpact += safeMoneyValue(row?.loyaltyBusinessCashImpact);
      map.set(worker, current);
    });
    const rows = Array.from(map.values()).sort((a, b) => b.totalBusiness - a.totalBusiness || b.attended - a.attended || a.worker.localeCompare(b.worker));
    const total = rows.reduce((acc, row) => ({
      worker: "Total",
      attended: acc.attended + row.attended,
      tips: acc.tips + row.tips,
      serviceSales: acc.serviceSales + row.serviceSales,
      productSales: acc.productSales + row.productSales,
      businessMembershipIncome: acc.businessMembershipIncome + row.businessMembershipIncome,
      totalBusiness: acc.totalBusiness + row.totalBusiness,
      sales: acc.sales + row.totalBusiness,
      serviceCommission: acc.serviceCommission + row.serviceCommission,
      productCommission: acc.productCommission + row.productCommission,
      workerMembershipIncome: acc.workerMembershipIncome + row.workerMembershipIncome,
      commission: acc.commission + row.commission,
      totalPayout: acc.totalPayout + row.totalPayout,
      collected: acc.collected + row.collected,
      pending: acc.pending + row.pending,
      net: acc.net + row.net,
    }), { worker: "Total", attended: 0, tips: 0, serviceSales: 0, productSales: 0, businessMembershipIncome: 0, totalBusiness: 0, sales: 0, collected: 0, pending: 0, serviceCommission: 0, productCommission: 0, workerMembershipIncome: 0, commission: 0, totalPayout: 0, net: 0 });
    return { rows, total };
  }, [financeData.financeRows]);


  const financeExecutiveDashboard = useMemo(() => {
    const rows = Array.isArray(financeData.financeRows) ? financeData.financeRows : [];
    const attended = Number(financeData.revenueBookings?.length || 0);
    const totalRevenue = safeMoneyValue(financeData.revenue);
    const collected = safeMoneyValue(financeData.collectedRevenue);
    const pending = safeMoneyValue(financeData.pendingRevenue);
    const payout = safeMoneyValue(financeData.workerPayoutTotal);
    const net = safeMoneyValue(financeData.businessNetRevenue);
    const tips = safeMoneyValue(financeData.totalTips);
    const products = safeMoneyValue(financeData.productSalesRevenueTotal);
    const materials = safeMoneyValue(financeData.materialCostTotal);
    const collectionRate = Number(financeData.collectionRate || 0);
    const trackedRevenueBase = safeMoneyValue(financeData.trackedRevenueBase);
    const untrackedExpected = safeMoneyValue(financeData.untrackedExpectedRevenue);
    const coveredRevenue = safeMoneyValue(financeData.coveredRevenueTotal);
    const averageTicket = attended > 0 ? totalRevenue / attended : 0;
    const pendingRows = rows.filter((r) => safeMoneyValue(r.pending) > 0).sort((a, b) => safeMoneyValue(b.pending) - safeMoneyValue(a.pending));
    const topPending = pendingRows.slice(0, 3);
    const netMargin = collected > 0 ? Math.round((net / collected) * 100) : 0;
    const payoutRatio = collected > 0 ? Math.round((payout / collected) * 100) : 0;
    const costRatio = collected > 0 ? Math.round(((materials + safeMoneyValue(financeData.productSalesCostTotal)) / collected) * 100) : 0;
    const cashHealth = trackedRevenueBase <= 0
      ? { label: "Cobranza sin base rastreada", tone: "neutral", hint: untrackedExpected > 0 ? "Hay histórico sin trazabilidad de pago." : "Todavía no hay ventas con trazabilidad de cobro." }
      : collectionRate >= 85 && pending <= collected * 0.15
        ? { label: "Caja saludable", tone: "success", hint: "La mayoría de ventas rastreadas ya está cobrada." }
        : pending > Math.max(collected * 0.35, trackedRevenueBase * 0.2)
          ? { label: "Cobros por priorizar", tone: "warning", hint: "Hay saldos pendientes con trazabilidad." }
          : { label: "Caja en control", tone: "primary", hint: "Mantén seguimiento de los saldos rastreados." };

    const workerRows = Array.isArray(workerFinanceSummary.rows) ? workerFinanceSummary.rows : [];
    const topWorker = workerRows.slice().sort((a, b) => safeMoneyValue(b.net) - safeMoneyValue(a.net))[0] || null;
    const topSeller = workerRows.slice().sort((a, b) => safeMoneyValue(b.totalBusiness) - safeMoneyValue(a.totalBusiness))[0] || null;
    const topPayout = workerRows.slice().sort((a, b) => safeMoneyValue(b.totalPayout) - safeMoneyValue(a.totalPayout))[0] || null;

    const actions = [
      pending > 0 ? {
        key: "collect",
        tone: pending > collected * 0.25 ? "warning" : "primary",
        title: "Cobrar pendientes",
        value: MONEY.format(pending),
        hint: `${pendingRows.length} reserva${pendingRows.length === 1 ? "" : "s"} por cobrar`,
      } : null,
      payout > 0 ? {
        key: "payout",
        tone: "neutral",
        title: "Preparar pago al equipo",
        value: MONEY.format(payout),
        hint: `Ratio payout ${payoutRatio}% de lo cobrado`,
      } : null,
      products > 0 ? {
        key: "retail",
        tone: "success",
        title: "Revisar retail vendido",
        value: MONEY.format(products),
        hint: "Productos vendidos dentro de reservas",
      } : null,
      costRatio > 0 ? {
        key: "costs",
        tone: costRatio > 25 ? "warning" : "neutral",
        title: "Controlar costos",
        value: `${costRatio}%`,
        hint: "Materiales + costo de productos sobre cobrado",
      } : null,
    ].filter(Boolean).slice(0, 4);

    return {
      attended,
      totalRevenue,
      collected,
      pending,
      payout,
      net,
      tips,
      products,
      materials,
      collectionRate,
      trackedRevenueBase,
      untrackedExpected,
      coveredRevenue,
      averageTicket,
      pendingRows,
      topPending,
      netMargin,
      payoutRatio,
      costRatio,
      cashHealth,
      topWorker,
      topSeller,
      topPayout,
      actions,
    };
  }, [financeData, workerFinanceSummary, MONEY]);


  const dailyCashClosing = useMemo(() => {
    const dayStart = startOfDaySafe(anchorDate || new Date());
    const dayEnd = addDays(dayStart, 1);
    const rows = (bookings || [])
      .map((booking, index) => {
        const scheduledDate = getBookingDate(booking) || getCreatedDate(booking);
        if (!scheduledDate || scheduledDate < dayStart || scheduledDate >= dayEnd) return null;
        if (workerFilter !== "all" && String(getWorkerId(booking)) !== String(workerFilter)) return null;

        const lifecycle = bookingLifecycleFromRecord(booking || {});
        const expected = getBookingRevenue(booking, serviceMap);
        const collected = lifecycle.totalCollectedAmount || getBookingCollectedAmount(booking, expected);
        const serviceCollected = lifecycle.serviceCollectedAmount || Math.max(0, collected - lifecycle.tipAmount - lifecycle.productSalesTotalAmount);
        const pending = ["cancelled", "rejected", "no_show"].includes(lifecycle.bookingStatus)
          ? 0
          : Math.max(0, expected - serviceCollected);
        const payout = estimateWorkerPayout({ ...(booking || {}), ...lifecycle }, expected);
        const inventoryCost = getBookingInventoryCost(booking);
        const productCost = getBookingProductSalesCost(booking);
        const method = paymentMethodLabel(lifecycle.paymentMethod || booking.paymentMethod || booking?.payment?.method);
        const net = Math.max(0, collected - payout - inventoryCost - productCost);

        return {
          id: booking.id || booking.bookingId || `cash-${index}`,
          date: scheduledDate,
          client: booking.customerName || booking.clientName || booking.name || "Cliente",
          service: getServiceName(booking, serviceMap),
          worker: getWorkerName(booking, employeeMap),
          bookingStatus: lifecycle.bookingStatus,
          attendanceStatus: lifecycle.attendanceStatus,
          paymentStatus: lifecycle.paymentStatus,
          paymentMethod: method,
          lifecycleStage: lifecycle.lifecycleStage,
          expected,
          collected,
          serviceCollected,
          pending,
          tip: lifecycle.tipAmount || getBookingTipAmount(booking),
          productSales: lifecycle.productSalesTotalAmount || getBookingProductSalesTotal(booking),
          payout,
          inventoryCost,
          productCost,
          net,
          commissionLifecycleStatus: lifecycle.commissionLifecycleStatus,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (b.date?.getTime?.() || 0) - (a.date?.getTime?.() || 0));

    const byMethod = new Map();
    let collected = 0;
    let pending = 0;
    let expected = 0;
    let tips = 0;
    let productSales = 0;
    let payoutReady = 0;
    let payoutTotal = 0;
    let inventoryCost = 0;
    let productCost = 0;
    let net = 0;

    const completedPaid = [];
    const completedUnpaid = [];
    const completedPartial = [];
    const paidNotCompleted = [];
    const noShows = [];
    const cancelled = [];
    const inProgress = [];
    const pendingApproval = [];

    rows.forEach((row) => {
      collected += safeMoneyValue(row.collected);
      pending += safeMoneyValue(row.pending);
      expected += safeMoneyValue(row.expected);
      tips += safeMoneyValue(row.tip);
      productSales += safeMoneyValue(row.productSales);
      payoutTotal += safeMoneyValue(row.payout);
      inventoryCost += safeMoneyValue(row.inventoryCost);
      productCost += safeMoneyValue(row.productCost);
      net += safeMoneyValue(row.net);
      if (row.collected > 0) groupAdd(byMethod, row.paymentMethod || "Sin método", row.collected);
      if (row.commissionLifecycleStatus === "payable") payoutReady += safeMoneyValue(row.payout);

      if (row.bookingStatus === "pending") pendingApproval.push(row);
      if (row.bookingStatus === "in_progress") inProgress.push(row);
      if (row.bookingStatus === "completed" && row.paymentStatus === "paid") completedPaid.push(row);
      if (row.bookingStatus === "completed" && row.paymentStatus === "partial") completedPartial.push(row);
      if (row.bookingStatus === "completed" && row.paymentStatus === "pending") completedUnpaid.push(row);
      if (!["completed", "cancelled", "rejected", "no_show"].includes(row.bookingStatus) && row.paymentStatus === "paid") paidNotCompleted.push(row);
      if (row.bookingStatus === "no_show" || row.attendanceStatus === "no_show") noShows.push(row);
      if (["cancelled", "rejected"].includes(row.bookingStatus)) cancelled.push(row);
    });

    const health = completedUnpaid.length || completedPartial.length || pending > 0
      ? { tone: "warning", label: "Cierre con pendientes", hint: "Hay reservas completadas o atendidas que todavía requieren cobro o seguimiento." }
      : paidNotCompleted.length
        ? { tone: "primary", label: "Pagos por completar", hint: "Hay pagos registrados en reservas que todavía no están completadas." }
        : collected > 0
          ? { tone: "success", label: "Cierre saludable", hint: "No hay alertas fuertes de cobro para este día." }
          : { tone: "neutral", label: "Sin caja registrada", hint: "Todavía no hay cobros registrados para este día." };

    const actions = [
      completedUnpaid.length ? {
        key: "completed-unpaid",
        tone: "warning",
        title: "Cobrar completadas",
        value: MONEY.format(completedUnpaid.reduce((sum, row) => sum + safeMoneyValue(row.pending), 0)),
        hint: `${completedUnpaid.length} completada${completedUnpaid.length === 1 ? "" : "s"} sin pago`,
      } : null,
      completedPartial.length ? {
        key: "completed-partial",
        tone: "warning",
        title: "Resolver saldos parciales",
        value: MONEY.format(completedPartial.reduce((sum, row) => sum + safeMoneyValue(row.pending), 0)),
        hint: `${completedPartial.length} reserva${completedPartial.length === 1 ? "" : "s"} con saldo pendiente`,
      } : null,
      paidNotCompleted.length ? {
        key: "paid-not-completed",
        tone: "primary",
        title: "Completar pagadas",
        value: String(paidNotCompleted.length),
        hint: "Pagadas pero aún no completadas",
      } : null,
      payoutReady > 0 ? {
        key: "payout-ready",
        tone: "success",
        title: "Comisiones listas",
        value: MONEY.format(payoutReady),
        hint: "Completadas y cobradas",
      } : null,
      noShows.length ? {
        key: "no-show",
        tone: "neutral",
        title: "Revisar no-shows",
        value: String(noShows.length),
        hint: "No cuentan como ingreso salvo cobro manual",
      } : null,
    ].filter(Boolean).slice(0, 5);

    return {
      dateLabel: dayStart.toLocaleDateString("es-PA", { weekday: "long", day: "numeric", month: "long" }),
      rows,
      byMethod: Array.from(byMethod.entries()).sort((a, b) => b[1] - a[1]),
      collected,
      pending,
      expected,
      tips,
      productSales,
      payoutReady,
      payoutTotal,
      inventoryCost,
      productCost,
      net,
      completedPaid,
      completedUnpaid,
      completedPartial,
      paidNotCompleted,
      noShows,
      cancelled,
      inProgress,
      pendingApproval,
      health,
      actions,
      needsAttention: completedUnpaid.concat(completedPartial, paidNotCompleted).slice(0, 8),
    };
  }, [bookings, anchorDate, workerFilter, serviceMap, employeeMap, MONEY]);

  const sortedAges = ["Menor de 18", "18-24", "25-34", "35-44", "45-54", "55+", "Sin edad"]
    .map((k) => [k, data.ageBuckets.get(k) || 0])
    .filter(([, v]) => v > 0);

  const dailyTrend = Array.from(data.byDay.values()).sort((a, b) => a.key.localeCompare(b.key));

  const overviewTrend = useMemo(() => {
    const rows = Array.isArray(dailyTrend) ? dailyTrend : [];
    if (rows.length <= 1) {
      return { rows, grain: "day", label: "Evolución diaria", bestLabel: "Mejor día" };
    }

    const first = new Date(`${rows[0].key}T12:00:00`);
    const last = new Date(`${rows[rows.length - 1].key}T12:00:00`);
    const spanDays = Number.isFinite(first.getTime()) && Number.isFinite(last.getTime())
      ? Math.max(0, Math.round((last.getTime() - first.getTime()) / 86400000))
      : 0;

    const grain = spanDays > 540 ? "month" : spanDays > 120 ? "week" : "day";
    if (grain === "day") {
      return { rows, grain, label: "Evolución diaria", bestLabel: "Mejor día" };
    }

    const grouped = new Map();
    rows.forEach((row) => {
      const d = new Date(`${row.key}T12:00:00`);
      if (!Number.isFinite(d.getTime())) return;

      let key = row.key;
      if (grain === "month") {
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
      } else {
        const monday = new Date(d);
        const delta = (monday.getDay() + 6) % 7;
        monday.setDate(monday.getDate() - delta);
        key = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, "0")}-${String(monday.getDate()).padStart(2, "0")}`;
      }

      const current = grouped.get(key) || { key, revenue: 0, count: 0 };
      current.revenue += numberFrom(row.revenue);
      current.count += numberFrom(row.count);
      grouped.set(key, current);
    });

    return {
      rows: Array.from(grouped.values()).sort((a, b) => a.key.localeCompare(b.key)),
      grain,
      label: grain === "month" ? "Evolución mensual" : "Evolución semanal",
      bestLabel: grain === "month" ? "Mejor mes" : "Mejor semana",
    };
  }, [dailyTrend]);

  const maxOverviewTrendRevenue = Math.max(0, ...overviewTrend.rows.map((v) => numberFrom(v.revenue)));

  const maxServiceRevenue = Math.max(0, ...sortedServiceRevenue.map(([, v]) => v));
  const maxServiceCount = Math.max(0, ...sortedServiceCount.map(([, v]) => v));
  const maxWorkerRevenue = Math.max(0, ...sortedWorkerRevenue.map(([, v]) => v));
  const maxSources = Math.max(0, ...sortedSources.map(([, v]) => v));
  const maxServiceInterest = Math.max(0, ...sortedServiceInterest.map(([, v]) => v));
  const maxPaymentMethods = Math.max(0, ...sortedPaymentMethods.map(([, v]) => v));
  const maxCollectedMethods = Math.max(0, ...sortedCollectedMethods.map(([, v]) => v));
  const maxCashMethods = Math.max(0, ...sortedCashMethods.map((row) => safeMoneyValue(row.total)));
  const maxCoveredMethods = Math.max(0, ...sortedCoveredMethods.map(([, v]) => v));
  const maxPayouts = Math.max(0, ...sortedPayouts.map(([, v]) => v));
  const maxPendingPayouts = Math.max(0, ...sortedPendingPayouts.map(([, v]) => v));
  const maxPaidPayouts = Math.max(0, ...sortedPaidPayouts.map(([, v]) => v));
  const maxNetByWorker = Math.max(0, ...sortedNetByWorker.map(([, v]) => v));
  const maxMaterialCosts = Math.max(0, ...sortedMaterialCosts.map(([, v]) => v));
  const maxAge = Math.max(0, ...sortedAges.map(([, v]) => v));

  const movePeriod = (delta) => {
    setAnchorDate((prev) => {
      const next = new Date(prev);
      if (period === "day") next.setDate(next.getDate() + delta);
      else if (period === "week") next.setDate(next.getDate() + delta * 7);
      else if (period === "month") next.setMonth(next.getMonth() + delta);
      else next.setFullYear(next.getFullYear() + delta);
      return next;
    });
  };

  const quickInsights = [
    data.topMoneyService ? `Mayor ingreso: ${data.topMoneyService.key}.` : null,
    data.topService ? `Servicio más reservado: ${data.topService.key}.` : null,
    data.topWorker ? `Mejor desempeño del equipo: ${data.topWorker.key}.` : null,
    data.peakHour ? `Hora pico: ${data.peakHour.key}.` : null,
    data.recurrentCustomers ? `${data.recurrentCustomers} cliente${data.recurrentCustomers === 1 ? "" : "s"} recurrente${data.recurrentCustomers === 1 ? "" : "s"}.` : null,
    data.pageViews ? `De cada 100 visitas, ${data.conversionRate} terminan en reserva.` : null,
    data.materialCostTotal ? `Costo de materiales: ${MONEY.format(data.materialCostTotal)}.` : null,
  ].filter(Boolean);

  const businessName = business?.name || business?.businessName || "Tu empresa";

  // LOOP PHASE 2.0.4 — normalized settings from Business OS panel.
  const inventoryEnabled = inventoryModuleEnabled;
  const financeSettings = loopSettings.financeSettings || {};
  const inventoryPlan = loopSettings.inventorySettings?.plan || (inventoryEnabled ? "early_access" : "disabled");
  const inventoryAutoDeduct = loopSettings.inventorySettings?.autoDeduct !== false;
  const inventoryDeductTrigger = loopSettings.inventorySettings?.deductTrigger || "completed";
  const workerCompensationVisibility = loopSettings.workerSettings?.compensationVisibilityDefault || "hidden";

  useEffect(() => {
    if (activeView === "inventory" && !canViewInventory) setActiveView("overview");
    if (activeView === "crm" && !canViewCrm) setActiveView("overview");
    if (activeView === "memberships" && !canViewMembershipAnalytics) setActiveView("overview");
    if (activeView === "finance" && !canViewFinanceUnified) setActiveView("overview");
    if (activeView === "exports" && !(canViewReports || canViewFinancials || canViewInventory || canViewCrm)) setActiveView("overview");
  }, [activeView, canViewInventory, canViewCrm, canViewFinanceUnified, canViewMembershipAnalytics, canViewReports, canViewFinancials]);

  const hasTracking = (data.trackingInPeriod || []).length > 0;

  const inventoryData = useMemo(() => {
    const rows = (inventoryItems || []).map(normalizeInventoryItem).filter((item) => item.active !== false);
    const lowStockItems = rows.filter((item) => inventoryStockTone(item) !== "ok");
    const totalInventoryValue = rows.reduce((sum, item) => sum + numberFrom(item.currentStock) * numberFrom(item.unitCost), 0);
    const potentialSalesValue = rows.reduce((sum, item) => sum + numberFrom(item.currentStock) * numberFrom(item.salePrice), 0);
    const categories = new Map();
    rows.forEach((item) => groupAdd(categories, item.category || "General", numberFrom(item.currentStock) * numberFrom(item.unitCost)));
    const topValueItems = rows.slice().sort((a, b) => (numberFrom(b.currentStock) * numberFrom(b.unitCost)) - (numberFrom(a.currentStock) * numberFrom(a.unitCost))).slice(0, 8);
    const movements = (inventoryMovements || []).map(normalizeInventoryMovement).filter((m) => m.itemId || m.itemName).sort((a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0));
    const movementsByType = new Map();
    movements.forEach((m) => groupAdd(movementsByType, inventoryMovementTypeLabel(m.type), Math.abs(numberFrom(m.quantity))));
    const stocktakeDiffCount = movements.filter((m) => m.type === "stocktake_adjustment" && numberFrom(m.quantity) !== 0).length;
    return { rows, lowStockItems, totalInventoryValue, potentialSalesValue, categories, topValueItems, movements, movementsByType, stocktakeDiffCount };
  }, [inventoryItems, inventoryMovements]);

  const auditData = useMemo(() => {
    const allRows = (auditLogs || []).map(normalizeAuditLog).sort((a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0));
    const inventoryRows = allRows.filter(auditLogIsInventoryRelated);
    const paymentRows = allRows.filter(auditLogIsPaymentRelated);
    const errorRows = allRows.filter((log) => log.severity === "warning" || String(log.type || "").includes("failed"));

    const typeOptions = Array.from(new Set(allRows.map((log) => String(log.type || "").trim()).filter(Boolean)))
      .sort((a, b) => auditEventTypeLabel(a).localeCompare(auditEventTypeLabel(b), "es"));
    const actorOptions = Array.from(new Set(allRows.map(auditLogActorLabel).filter(Boolean)))
      .sort((a, b) => String(a).localeCompare(String(b), "es"));

    const q = String(auditFilterSearch || "").trim().toLowerCase();
    const from = parseDateInputValue(auditFilterFrom, false);
    const to = parseDateInputValue(auditFilterTo, true);

    const rows = allRows.filter((log) => {
      if (auditFilterType !== "all" && String(log.type || "") !== auditFilterType) return false;
      if (auditFilterActor !== "all" && auditLogActorLabel(log) !== auditFilterActor) return false;
      if (auditOnlyErrors && !(log.severity === "warning" || String(log.type || "").includes("failed"))) return false;
      if (auditOnlyInventory && !auditLogIsInventoryRelated(log)) return false;
      if (auditOnlyPayments && !auditLogIsPaymentRelated(log)) return false;
      if (from && (!log.createdAt || log.createdAt < from)) return false;
      if (to && (!log.createdAt || log.createdAt > to)) return false;
      if (q && !auditLogSearchText(log).includes(q)) return false;
      return true;
    });

    return { rows, allRows, inventoryRows, paymentRows, errorRows, typeOptions, actorOptions };
  }, [auditLogs, auditFilterType, auditFilterActor, auditFilterFrom, auditFilterTo, auditFilterSearch, auditOnlyErrors, auditOnlyInventory, auditOnlyPayments]);


  const crmData = useMemo(() => {
    const customerMap = new Map();
    const ensure = (seed = {}) => {
      const key = crmCustomerIdentityFromAny(seed);
      if (!key || key === "unknown") return null;
      const current = customerMap.get(key) || {
        key,
        id: seed.id || seed.customerId || null,
        name: crmCustomerName(seed),
        phone: crmCustomerPhone(seed),
        email: crmCustomerEmail(seed),
        notes: String(seed.notes || seed.customerNotes || "").trim(),
        tags: Array.isArray(seed.tags) ? seed.tags : [],
        preferences: seed.preferences || seed.customerPreferences || {},
        source: seed.source || "manual",
        totalBookings: 0,
        completedBookings: 0,
        cancelledBookings: 0,
        noShowBookings: 0,
        pendingBookings: 0,
        totalSpent: 0,
        collected: 0,
        tips: 0,
        productSales: 0,
        services: new Map(),
        workers: new Map(),
        bookingRows: [],
        lastVisit: safeDate(seed.lastBookingAt) || safeDate(seed.importedLastBookingAt) || null,
        nextBooking: null,
        birthday: crmNextBirthdayInfo(seed),
        lastContactedAt: safeDate(seed.lastContactedAt) || safeDate(seed.lastCrmActionAt) || null,
        lastCrmActionAt: safeDate(seed.lastCrmActionAt) || safeDate(seed.lastContactedAt) || null,
        lastCrmAction: seed.lastCrmAction || "",
        lastCrmActionSegment: seed.lastCrmActionSegment || "",
        lastCrmActionMessage: seed.lastCrmActionMessage || "",
        whatsappOptOut: seed.whatsappOptOut === true || seed.customerWhatsappOptOut === true || seed.whatsAppOptOut === true,
        whatsappOptIn: seed.whatsappOptIn === false || seed.customerWhatsappConsent === false || seed.allowWhatsApp === false ? false : true,
        customerWhatsappConsent: seed.customerWhatsappConsent === false || seed.whatsappOptIn === false || seed.allowWhatsApp === false ? false : true,
        whatsappPhoneStatus: seed.whatsappPhoneStatus || seed.customerPhoneStatus || "",
      };
      current.id = current.id || seed.id || seed.customerId || null;
      current.name = current.name && current.name !== "Cliente" ? current.name : crmCustomerName(seed);
      current.phone = current.phone || crmCustomerPhone(seed);
      current.email = current.email || crmCustomerEmail(seed);
      current.notes = current.notes || String(seed.notes || seed.customerNotes || "").trim();
      current.tags = Array.from(new Set([...(current.tags || []), ...(Array.isArray(seed.tags) ? seed.tags : [])].filter(Boolean)));
      if (seed.whatsappOptOut === true || seed.customerWhatsappOptOut === true || seed.whatsAppOptOut === true) current.whatsappOptOut = true;
      if (seed.whatsappOptIn === false || seed.customerWhatsappConsent === false || seed.allowWhatsApp === false) {
        current.whatsappOptIn = false;
        current.customerWhatsappConsent = false;
      }
      if (seed.whatsappPhoneStatus || seed.customerPhoneStatus) current.whatsappPhoneStatus = seed.whatsappPhoneStatus || seed.customerPhoneStatus;
      if (!current.birthday?.hasBirthday) current.birthday = crmNextBirthdayInfo(seed);
      const seedLastContacted = safeDate(seed.lastContactedAt) || safeDate(seed.lastCrmActionAt) || null;
      if (seedLastContacted && (!current.lastContactedAt || seedLastContacted > current.lastContactedAt)) {
        current.lastContactedAt = seedLastContacted;
        current.lastCrmActionAt = seedLastContacted;
        current.lastCrmAction = seed.lastCrmAction || current.lastCrmAction || "";
        current.lastCrmActionSegment = seed.lastCrmActionSegment || current.lastCrmActionSegment || "";
        current.lastCrmActionMessage = seed.lastCrmActionMessage || current.lastCrmActionMessage || "";
      }
      customerMap.set(key, current);
      return current;
    };

    (customers || []).forEach((customer) => ensure(customer));

    const now = new Date();
    (bookings || []).forEach((booking) => {
      const customer = ensure(booking);
      if (!customer) return;
      const bookingDate = crmBookingDate(booking);
      const status = crmStatus(booking);
      const revenue = getBookingRevenue(booking, serviceMap);
      const collected = getBookingCollectedAmount(booking, revenue);
      const serviceName = getServiceName(booking, serviceMap);
      const workerName = getWorkerName(booking, employeeMap);
      const productSales = getBookingProductSalesTotal(booking);
      const tip = getBookingTipAmount(booking);

      customer.totalBookings += 1;
      if (status === "completed" || status === "confirmed") customer.completedBookings += 1;
      else if (status === "cancelled") customer.cancelledBookings += 1;
      else if (status === "no_show") customer.noShowBookings += 1;
      else customer.pendingBookings += 1;

      if (status !== "cancelled" && status !== "no_show") {
        customer.totalSpent += revenue;
        customer.collected += collected;
        customer.tips += tip;
        customer.productSales += productSales;
      }
      if (serviceName) groupAdd(customer.services, serviceName, 1);
      if (workerName) groupAdd(customer.workers, workerName, 1);
      if (bookingDate) {
        if (bookingDate <= now && (!customer.lastVisit || bookingDate > customer.lastVisit)) customer.lastVisit = bookingDate;
        if (bookingDate > now && (!customer.nextBooking || bookingDate < customer.nextBooking)) customer.nextBooking = bookingDate;
      }
      customer.bookingRows.push({ booking, bookingDate, status, revenue, collected, serviceName, workerName });
    });

    const rows = Array.from(customerMap.values()).map((customer) => {
      const favoriteService = topEntry(customer.services)?.key || "—";
      const favoriteWorker = topEntry(customer.workers)?.key || "—";
      const avgTicket = customer.completedBookings > 0 ? customer.totalSpent / customer.completedBookings : customer.totalSpent;
      const daysSinceLastVisit = crmDaysSince(customer.lastVisit);
      const segments = [];
      if (customer.totalBookings <= 1) segments.push("new");
      if (customer.completedBookings >= 3) segments.push("frequent");
      if (customer.totalSpent >= 250 || customer.completedBookings >= 5) segments.push("vip");
      if (daysSinceLastVisit !== null && daysSinceLastVisit >= 45) segments.push("dormant");
      else if (daysSinceLastVisit !== null && daysSinceLastVisit >= 30) segments.push("at_risk");
      if (customer.noShowBookings >= 2) segments.push("no_show");
      if (avgTicket >= 55) segments.push("high_ticket");
      if (customer.productSales > 0) segments.push("product_buyer");
      if (customer.birthday?.hasBirthday && customer.birthday.daysUntil <= 30) segments.push("birthday_soon");
      const followUp = crmFollowUpInfo({ ...customer, lastVisit: customer.lastVisit, nextBooking: customer.nextBooking });
      const attribution = crmAttributionForCustomer({ ...customer, lastContactedAt: followUp.lastContactedAt || customer.lastContactedAt }, MONEY);
      return {
        ...customer,
        crmRecoveredRevenue: attribution.recoveredRevenue,
        crmRecoveredCollected: attribution.recoveredCollected,
        crmRecoveredBookings: attribution.recoveredBookings,
        crmFirstReactivatedAt: attribution.firstReactivatedAt,
        crmAttributionWindowEnd: attribution.attributionWindowEnd,
        crmAttributionLabel: attribution.attributionLabel,
        crmAttributionTone: attribution.attributionTone,
        followUpKey: attribution.recoveredBookings > 0 ? "reactivated" : followUp.key,
        followUpLabel: attribution.recoveredBookings > 0 ? "Reactivado" : followUp.label,
        followUpTone: attribution.recoveredBookings > 0 ? "success" : followUp.tone,
        followUpHint: attribution.recoveredBookings > 0 ? "Reserva atribuida a acción CRM." : followUp.hint,
        daysSinceContact: followUp.daysSinceContact,
        lastContactedAt: followUp.lastContactedAt || customer.lastContactedAt || null,
        reactivatedAfterContact: followUp.reactivated,
        avgTicket,
        favoriteService,
        favoriteWorker,
        daysSinceLastVisit,
        segments: segments.length ? segments : ["new"],
      };
    }).sort((a, b) => b.totalSpent - a.totalSpent || (b.lastVisit?.getTime?.() || 0) - (a.lastVisit?.getTime?.() || 0));

    const segmentCounts = new Map();
    rows.forEach((row) => row.segments.forEach((seg) => groupAdd(segmentCounts, seg, 1)));
    const q = crmNormalizeText(crmSearch);
    const filteredRows = rows.filter((row) => {
      if (crmSegmentFilter !== "all" && !row.segments.includes(crmSegmentFilter)) return false;
      // LOOP PHASE 2.2F HOTFIX — “Ver prioridades” must show the executive priority base,
      // not only follow-up-due customers. Many priority customers are dormant/at-risk but
      // have not been contacted yet, so the old needs_follow_up filter could return 0.
      if (crmFollowUpFilter === "priority") {
        const days = Number(row.daysSinceLastVisit);
        const isFollowUpPriority = ["follow_up_due", "pending_response"].includes(row.followUpKey);
        const isRiskPriority = row.segments.includes("dormant") || row.segments.includes("at_risk");
        const isVipPriority = row.segments.includes("vip") && Number.isFinite(days) && days >= 21 && row.followUpKey !== "recently_contacted" && row.followUpKey !== "reactivated";
        if (!isFollowUpPriority && !isRiskPriority && !isVipPriority) return false;
      }
      if (crmFollowUpFilter === "needs_follow_up" && !["follow_up_due", "pending_response"].includes(row.followUpKey)) return false;
      if (crmFollowUpFilter === "contacted" && row.followUpKey === "not_contacted") return false;
      if (crmFollowUpFilter === "reactivated" && row.followUpKey !== "reactivated") return false;
      if (crmFollowUpFilter === "revenue_attributed" && !(Number(row.crmRecoveredRevenue || 0) > 0)) return false;
      if (crmFollowUpFilter === "not_contacted" && row.followUpKey !== "not_contacted") return false;
      if (!q) return true;
      const searchText = crmNormalizeText([row.name, row.phone, row.email, row.favoriteService, row.favoriteWorker, ...(row.tags || [])].join(" "));
      return searchText.includes(q) || crmDigits(row.phone).includes(crmDigits(q));
    });
    const vipRows = rows.filter((row) => row.segments.includes("vip"));
    const dormantRows = rows.filter((row) => row.segments.includes("dormant") || row.segments.includes("at_risk"));
    const birthdayRows = rows.filter((row) => row.segments.includes("birthday_soon"));
    const followUpDueRows = rows.filter((row) => ["follow_up_due", "pending_response"].includes(row.followUpKey));
    const contactedRows = rows.filter((row) => row.followUpKey && row.followUpKey !== "not_contacted");
    const reactivatedRows = rows.filter((row) => row.followUpKey === "reactivated");
    const recentContactRows = rows.filter((row) => row.followUpKey === "recently_contacted");
    const crmRevenueRows = rows.filter((row) => Number(row.crmRecoveredRevenue || 0) > 0);
    const crmRecoveredRevenue = crmRevenueRows.reduce((sum, row) => sum + Number(row.crmRecoveredRevenue || 0), 0);
    const crmRecoveredCollected = crmRevenueRows.reduce((sum, row) => sum + Number(row.crmRecoveredCollected || 0), 0);
    const crmRecoveredBookings = crmRevenueRows.reduce((sum, row) => sum + Number(row.crmRecoveredBookings || 0), 0);
    const totalSpent = rows.reduce((sum, row) => sum + row.totalSpent, 0);
    const avgTicket = rows.length ? rows.reduce((sum, row) => sum + row.avgTicket, 0) / rows.length : 0;
    return { rows, filteredRows, segmentCounts, vipRows, dormantRows, birthdayRows, followUpDueRows, contactedRows, reactivatedRows, recentContactRows, crmRevenueRows, crmRecoveredRevenue, crmRecoveredCollected, crmRecoveredBookings, totalSpent, avgTicket };
  }, [customers, bookings, serviceMap, employeeMap, crmSearch, crmSegmentFilter, crmFollowUpFilter, MONEY]);

  const crmExecutiveDashboard = useMemo(() => {
    // LOOP PHASE 2.2F — owner-facing executive CRM summary.
    const rows = Array.isArray(crmData.rows) ? crmData.rows : [];
    const today = new Date();
    const activeRows = rows.filter((row) => {
      if (row.nextBooking) return true;
      const days = Number(row.daysSinceLastVisit);
      return Number.isFinite(days) && days <= 30;
    });
    const riskRows = rows.filter((row) => row.segments?.includes("dormant") || row.segments?.includes("at_risk"));
    const vipRows = Array.isArray(crmData.vipRows) ? crmData.vipRows : [];
    const contactedRows = Array.isArray(crmData.contactedRows) ? crmData.contactedRows : [];
    const reactivatedRows = Array.isArray(crmData.reactivatedRows) ? crmData.reactivatedRows : [];
    const followUpDueRows = Array.isArray(crmData.followUpDueRows) ? crmData.followUpDueRows : [];
    const crmRevenueRows = Array.isArray(crmData.crmRevenueRows) ? crmData.crmRevenueRows : [];

    const dedupe = (list) => {
      const map = new Map();
      (Array.isArray(list) ? list : []).forEach((row) => {
        const key = row?.key || row?.id || row?.phone || row?.email || row?.name;
        if (key && !map.has(key)) map.set(key, row);
      });
      return Array.from(map.values());
    };

    const priorityRows = dedupe([
      ...followUpDueRows,
      ...riskRows.filter((row) => row.followUpKey !== "recently_contacted" && row.followUpKey !== "reactivated"),
      ...vipRows.filter((row) => {
        const days = Number(row.daysSinceLastVisit);
        return Number.isFinite(days) && days >= 21 && row.followUpKey !== "recently_contacted";
      }),
    ]).sort((a, b) => {
      const score = (row) => {
        let out = Number(row.avgTicket || 0) + Number(row.totalSpent || 0) / 10;
        if (row.followUpKey === "follow_up_due") out += 120;
        if (row.segments?.includes("vip")) out += 90;
        if (row.segments?.includes("dormant")) out += 70;
        if (row.segments?.includes("at_risk")) out += 55;
        if (row.segments?.includes("high_ticket")) out += 45;
        return out;
      };
      return score(b) - score(a);
    });

    const priorityCount = Math.min(priorityRows.length, 8);
    const prioritySample = priorityRows.slice(0, priorityCount || 4);
    const defaultTicket = Number(crmData.avgTicket || 0) > 0 ? Number(crmData.avgTicket || 0) : 35;
    const potentialRevenue = prioritySample.reduce((sum, row) => sum + Math.max(Number(row.avgTicket || 0), defaultTicket), 0);
    const conversionRate = contactedRows.length ? Math.round((reactivatedRows.length / contactedRows.length) * 100) : 0;
    const roiRate = contactedRows.length ? Math.round((crmRevenueRows.length / contactedRows.length) * 100) : 0;

    const daysToMonthEnd = (() => {
      try {
        const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return Math.max(0, Math.ceil((end.getTime() - today.getTime()) / 86400000));
      } catch (_) {
        return null;
      }
    })();

    const headline = priorityCount > 0
      ? `Contacta ${priorityCount} cliente${priorityCount === 1 ? "" : "s"} esta semana`
      : "Tu CRM está al día";
    const subheadline = priorityCount > 0
      ? `Potencial estimado: ${MONEY.format(potentialRevenue)} usando ticket promedio e historial.`
      : "No hay clientes urgentes para seguimiento en este momento.";

    return {
      activeRows,
      riskRows,
      vipRows,
      contactedRows,
      reactivatedRows,
      priorityRows,
      priorityCount,
      prioritySample,
      potentialRevenue,
      conversionRate,
      roiRate,
      daysToMonthEnd,
      headline,
      subheadline,
    };
  }, [crmData, MONEY]);


  const crmSmartTasks = useMemo(() => {
    // LOOP PHASE 2.3 — Smart Tasks / light automations.
    // These are generated from the same CRM signals without sending anything automatically.
    const rows = Array.isArray(crmData.rows) ? crmData.rows : [];
    const priorityRows = Array.isArray(crmExecutiveDashboard.priorityRows) ? crmExecutiveDashboard.priorityRows : [];
    const followUpDueRows = Array.isArray(crmData.followUpDueRows) ? crmData.followUpDueRows : [];
    const recentContactRows = Array.isArray(crmData.recentContactRows) ? crmData.recentContactRows : [];
    const birthdayRows = Array.isArray(crmData.birthdayRows) ? crmData.birthdayRows : [];
    const vipInactiveRows = rows.filter((row) => {
      const days = Number(row.daysSinceLastVisit);
      return row.segments?.includes("vip") && Number.isFinite(days) && days >= 21 && row.followUpKey !== "recently_contacted" && row.followUpKey !== "reactivated";
    });
    const productBuyerRows = rows.filter((row) => row.segments?.includes("product_buyer") && row.followUpKey !== "recently_contacted");
    const noShowRows = rows.filter((row) => row.segments?.includes("no_show"));
    const highTicketRows = rows.filter((row) => row.segments?.includes("high_ticket") && row.followUpKey !== "recently_contacted");

    const makeTask = ({ key, title, description, rows: sourceRows, tone = "neutral", segment = "general", followUpFilter = "all", segmentFilter = "all", actionTitle = "Contactar cliente", cta = "Ver clientes", urgency = 1 }) => {
      const cleanRows = Array.isArray(sourceRows) ? sourceRows.filter(Boolean) : [];
      const customer = cleanRows[0] || null;
      const value = cleanRows.reduce((sum, row) => sum + Math.max(Number(row.avgTicket || 0), Number(crmData.avgTicket || 0) || 0), 0);
      return {
        key,
        title,
        description,
        count: cleanRows.length,
        customer,
        value,
        tone,
        segment,
        followUpFilter,
        segmentFilter,
        actionTitle,
        cta,
        urgency,
      };
    };

    const tasks = [
      makeTask({
        key: "priority_week",
        title: "Atacar prioridades de la semana",
        description: "Clientes dormidos, en riesgo o VIP que Loop recomienda contactar primero.",
        rows: priorityRows,
        tone: priorityRows.length ? "primary" : "success",
        followUpFilter: "priority",
        segmentFilter: "all",
        segment: "dormant",
        actionTitle: "Contactar prioridad",
        cta: "Ver prioridades",
        urgency: 5,
      }),
      makeTask({
        key: "follow_up_due",
        title: "Dar seguimiento a contactados",
        description: "Clientes contactados que todavía no tienen una nueva reserva visible.",
        rows: followUpDueRows,
        tone: followUpDueRows.length ? "warning" : "neutral",
        followUpFilter: "needs_follow_up",
        segmentFilter: "all",
        segment: "general",
        actionTitle: "Dar seguimiento",
        cta: "Ver seguimiento",
        urgency: 4,
      }),
      makeTask({
        key: "birthday_soon",
        title: "Preparar cumpleaños próximos",
        description: "Oportunidad de mensaje personal para fechas cercanas.",
        rows: birthdayRows,
        tone: birthdayRows.length ? "blue" : "neutral",
        followUpFilter: "all",
        segmentFilter: "birthday_soon",
        segment: "birthday_soon",
        actionTitle: "Mensaje de cumpleaños",
        cta: "Ver cumpleaños",
        urgency: 3,
      }),
      makeTask({
        key: "vip_care",
        title: "Cuidar VIP sin visita reciente",
        description: "Clientes valiosos que conviene mantener cerca antes de que se enfríen.",
        rows: vipInactiveRows,
        tone: vipInactiveRows.length ? "success" : "neutral",
        followUpFilter: "all",
        segmentFilter: "vip",
        segment: "vip",
        actionTitle: "Cuidado VIP",
        cta: "Ver VIPs",
        urgency: 3,
      }),
      makeTask({
        key: "retail_repurchase",
        title: "Impulsar recompra de productos",
        description: "Clientes que compraron retail y pueden necesitar reposición o complemento.",
        rows: productBuyerRows,
        tone: productBuyerRows.length ? "green" : "neutral",
        followUpFilter: "all",
        segmentFilter: "product_buyer",
        segment: "product_buyer",
        actionTitle: "Recompra de producto",
        cta: "Ver retail",
        urgency: 2,
      }),
      makeTask({
        key: "no_show_recovery",
        title: "Recuperar no-shows recurrentes",
        description: "Clientes con historial de no-show que necesitan re-agenda cuidadosa.",
        rows: noShowRows,
        tone: noShowRows.length ? "warning" : "neutral",
        followUpFilter: "all",
        segmentFilter: "no_show",
        segment: "no_show",
        actionTitle: "Reagendar cliente",
        cta: "Ver no-shows",
        urgency: 2,
      }),
      makeTask({
        key: "high_ticket_nurture",
        title: "Proteger alto ticket",
        description: "Clientes con ticket alto que pueden mover el revenue del mes.",
        rows: highTicketRows,
        tone: highTicketRows.length ? "primary" : "neutral",
        followUpFilter: "all",
        segmentFilter: "high_ticket",
        segment: "high_ticket",
        actionTitle: "Contactar alto ticket",
        cta: "Ver alto ticket",
        urgency: 2,
      }),
    ].sort((a, b) => (b.count > 0 ? b.urgency : 0) - (a.count > 0 ? a.urgency : 0));

    const activeTasks = tasks.filter((task) => task.count > 0);
    const totalPotential = activeTasks.reduce((sum, task) => sum + Number(task.value || 0), 0);
    const topTask = activeTasks[0] || tasks[0];
    const recentContactGuard = recentContactRows.length;

    return {
      tasks,
      activeTasks,
      topTask,
      totalPotential,
      todayCount: activeTasks.reduce((sum, task) => sum + Math.min(task.count, task.key === "priority_week" ? 8 : 3), 0),
      recentContactGuard,
      headline: activeTasks.length
        ? `${activeTasks.length} frente${activeTasks.length === 1 ? "" : "s"} comerciales activos`
        : "No hay tareas críticas hoy",
      subheadline: activeTasks.length
        ? `Prioriza ${topTask?.title?.toLowerCase?.() || "seguimientos"}. Potencial visible: ${MONEY.format(totalPotential || 0)}.`
        : "La base está ordenada; revisa nuevos clientes o crea campañas suaves más adelante.",
    };
  }, [crmData, crmExecutiveDashboard, MONEY]);

  const crmRetentionActionCenter = useMemo(() => {
    const firstName = (name = "") => String(name || "").trim().split(/\s+/)[0] || "";
    const makeMessage = (customer, template) => template
      .replace("{nombre}", firstName(customer?.name) || "")
      .replace("{negocio}", businessName || "Loop")
      .replace(/\s+/g, " ")
      .trim();

    const buildAction = ({ key, segment, title, subtitle, goal, rows, template, tone = "neutral" }) => {
      const sortedRows = (Array.isArray(rows) ? rows : [])
        .slice()
        .sort((a, b) => {
          const scoreA = Number(a.totalSpent || 0) + Number(a.completedBookings || 0) * 20;
          const scoreB = Number(b.totalSpent || 0) + Number(b.completedBookings || 0) * 20;
          return scoreB - scoreA;
        });
      const lead = sortedRows.find((row) => crmDigits(row.phone)) || sortedRows[0] || null;
      const message = lead ? makeMessage(lead, template) : template.replace("{negocio}", businessName || "Loop").replace("{nombre}", "").replace(/\s+/g, " ").trim();
      return {
        key,
        segment,
        title,
        subtitle,
        goal,
        tone,
        count: sortedRows.length,
        lead,
        message,
        template,
        whatsapp: lead ? crmBuildWhatsAppLink(lead.phone, message) : "",
      };
    };

    const rows = Array.isArray(crmData.rows) ? crmData.rows : [];
    const dormantRows = rows.filter((row) => row.segments.includes("dormant") || row.segments.includes("at_risk"));
    const vipRows = rows.filter((row) => row.segments.includes("vip"));
    const birthdayRows = rows.filter((row) => row.segments.includes("birthday_soon"));
    const productRows = rows.filter((row) => row.segments.includes("product_buyer"));

    return [
      buildAction({
        key: "reactivation",
        segment: "at_risk",
        title: "Reactivar clientes",
        subtitle: "Clientes dormidos o en riesgo",
        goal: "Recuperar recurrencia sin sonar agresivo.",
        rows: dormantRows,
        tone: "warning",
        template: "Hola {nombre}, te escribimos de {negocio}. Hace un tiempo no te vemos por acá y queríamos saber si te gustaría reservar nuevamente esta semana.",
      }),
      buildAction({
        key: "vip-care",
        segment: "vip",
        title: "Cuidar VIP",
        subtitle: "Clientes de mayor valor",
        goal: "Mantener relación y aumentar recompra.",
        rows: vipRows,
        tone: "success",
        template: "Hola {nombre}, gracias por ser parte de {negocio}. Queríamos darte prioridad para tu próxima reserva y ayudarte a encontrar un horario cómodo.",
      }),
      buildAction({
        key: "birthday",
        segment: "birthday_soon",
        title: "Cumpleaños próximos",
        subtitle: "Oportunidad de mensaje personal",
        goal: "Crear una experiencia más cercana.",
        rows: birthdayRows,
        tone: "primary",
        template: "Hola {nombre}, vimos que tu cumpleaños está cerca. De parte de {negocio}, nos encantaría ayudarte a preparar algo especial para esa fecha.",
      }),
      buildAction({
        key: "retail-upsell",
        segment: "product_buyer",
        title: "Recompra de productos",
        subtitle: "Clientes que compraron retail",
        goal: "Impulsar productos y add-ons relevantes.",
        rows: productRows,
        tone: "neutral",
        template: "Hola {nombre}, te escribimos de {negocio}. Queríamos revisar si necesitas reponer algún producto o complementar tu próximo servicio.",
      }),
    ];
  }, [crmData.rows, businessName]);

  const openCrmActionModal = (actionLike = {}, customerOverride = null) => {
    const customer = customerOverride || actionLike.lead || null;
    if (!customer) {
      setCrmActionStatus("No hay cliente con teléfono o historial suficiente para esta acción.");
      return;
    }
    const segment = actionLike.segment || (Array.isArray(customer.segments) ? customer.segments[0] : "general") || "general";
    const message = crmBuildSegmentMessage({
      customer,
      businessName,
      segment,
      template: actionLike.template || "",
    });
    setCrmActionModal({
      title: actionLike.title || crmActionTypeLabel(segment),
      subtitle: actionLike.subtitle || crmSegmentMeta(segment),
      segment,
      customer,
      sourceActionKey: actionLike.key || segment || "manual",
      followUp: crmFollowUpInfo(customer),
    });
    setCrmActionMessage(message);
    setCrmActionStatus("");
  };

  const closeCrmActionModal = () => {
    if (crmActionSaving) return;
    setCrmActionModal(null);
    setCrmActionMessage("");
    setCrmActionStatus("");
  };

  const saveCrmAction = async ({ openWhatsApp = false } = {}) => {
    if (!businessId || !crmActionModal?.customer) return;
    const customer = crmActionModal.customer || {};
    const message = crmSafeMessageText(crmActionMessage);
    if (!message) {
      setCrmActionStatus("Escribe un mensaje antes de guardar la acción.");
      return;
    }
    setCrmActionSaving(true);
    setCrmActionStatus("");

    const actor = (() => {
      try { return defaultAuth?.currentUser || null; } catch (_) { return null; }
    })();
    const now = isIosCapacitor() ? new Date().toISOString() : serverTimestamp();
    const followUpDueAtDate = new Date(Date.now() + 3 * 86400000);
    const followUpDueAt = isIosCapacitor() ? followUpDueAtDate.toISOString() : followUpDueAtDate;
    const customerId = String(customer.id || customer.customerId || "").trim();
    const payload = {
      type: "retention_whatsapp",
      channel: "whatsapp",
      status: openWhatsApp ? "opened_whatsapp" : "logged",
      segment: crmActionModal.segment || "general",
      actionKey: crmActionModal.sourceActionKey || "manual",
      actionLabel: crmActionTypeLabel(crmActionModal.segment || "general"),
      businessId,
      customerId: customerId || null,
      customerName: customer.name || "Cliente",
      customerPhone: customer.phone || "",
      customerEmail: customer.email || "",
      favoriteService: customer.favoriteService || "",
      message,
      followUpStatus: "pending",
      followUpDueAt,
      expectedOutcome: "new_booking",
      createdAt: now,
      updatedAt: now,
      createdByUid: actor?.uid || null,
      createdByEmail: actor?.email || "",
    };

    try {
      if (isIosCapacitor() && typeof fsClient.addDocToCollectionPath === "function") {
        await fsClient.addDocToCollectionPath(`businesses/${businessId}/crmActions`, payload, { db });
        if (customerId) {
          await fsClient.addDocToCollectionPath(`businesses/${businessId}/customers/${customerId}/crmActions`, payload, { db });
          if (typeof fsClient.updateDocByPath === "function") {
            await fsClient.updateDocByPath(`businesses/${businessId}/customers/${customerId}`, {
              lastContactedAt: now,
              lastCrmActionAt: now,
              lastCrmAction: payload.actionLabel,
              lastCrmActionSegment: payload.segment,
              lastCrmActionMessage: message,
              nextCrmFollowUpAt: followUpDueAt,
              crmFollowUpStatus: "pending",
              updatedAt: now,
            }, { db });
          }
        }
      } else {
        await addDoc(collection(db, `businesses/${businessId}/crmActions`), payload);
        if (customerId) {
          await addDoc(collection(db, `businesses/${businessId}/customers/${customerId}/crmActions`), payload);
          await updateDoc(doc(db, `businesses/${businessId}/customers/${customerId}`), {
            lastContactedAt: serverTimestamp(),
            lastCrmActionAt: serverTimestamp(),
            lastCrmAction: payload.actionLabel,
            lastCrmActionSegment: payload.segment,
            lastCrmActionMessage: message,
            nextCrmFollowUpAt: followUpDueAt,
            crmFollowUpStatus: "pending",
            updatedAt: serverTimestamp(),
          });
        }
      }

      setCustomers((prev) => (Array.isArray(prev) ? prev : []).map((row) => (
        customerId && String(row.id) === customerId
          ? { ...row, lastContactedAt: new Date().toISOString(), lastCrmAction: payload.actionLabel, lastCrmActionSegment: payload.segment, lastCrmActionMessage: message, nextCrmFollowUpAt: followUpDueAtDate.toISOString(), crmFollowUpStatus: "pending" }
          : row
      )));

      if (openWhatsApp) {
        const href = crmBuildWhatsAppLink(customer.phone, message);
        if (href && typeof window !== "undefined") window.open(href, "_blank", "noopener,noreferrer");
      }
      setCrmActionStatus(openWhatsApp ? "Acción guardada y WhatsApp abierto." : "Acción guardada en el historial del cliente.");
      window.setTimeout(() => {
        setCrmActionModal(null);
        setCrmActionMessage("");
        setCrmActionStatus("");
      }, 650);
    } catch (err) {
      console.error("[BusinessAnalytics] CRM action save failed", err);
      setCrmActionStatus("No pude guardar la acción. Revisa permisos de Firestore.");
    } finally {
      setCrmActionSaving(false);
    }
  };

  const updateCrmCustomerWhatsAppPreference = async (customer = {}, nextOptOut = false) => {
    const customerId = String(customer.id || customer.customerId || "").trim();
    if (!businessId || !customerId) {
      setCrmWhatsAppStatus("Este cliente no tiene ID de CRM todavía. Ábrelo desde una reserva o guárdalo como cliente antes de bloquear WhatsApp.");
      return;
    }
    if (!canEditCrm) {
      setCrmWhatsAppStatus("No tienes permiso para cambiar preferencias de WhatsApp.");
      return;
    }

    setCrmWhatsAppSavingKey(customer.key || customerId);
    setCrmWhatsAppStatus("");
    try {
      const functions = getFunctions();
      const call = httpsCallable(functions, "updateBusinessCustomerWhatsAppPreference");
      await call({ businessId, customerId, whatsappOptOut: nextOptOut === true });
      setCustomers((prev) => (Array.isArray(prev) ? prev : []).map((row) => (
        String(row.id || row.customerId || "") === customerId
          ? {
              ...row,
              whatsappOptOut: nextOptOut === true,
              whatsAppOptOut: nextOptOut === true,
              customerWhatsappOptOut: nextOptOut === true,
              whatsappOptIn: nextOptOut === true ? false : true,
              customerWhatsappConsent: nextOptOut === true ? false : true,
              whatsappPreferenceUpdatedAt: new Date().toISOString(),
            }
          : row
      )));
      setCrmWhatsAppStatus(nextOptOut ? "Cliente marcado como: no enviar WhatsApp." : "Cliente marcado como: WhatsApp permitido.");
    } catch (err) {
      console.error("[BusinessAnalytics] WhatsApp preference update failed", err);
      setCrmWhatsAppStatus(err?.message || "No pude actualizar la preferencia de WhatsApp.");
    } finally {
      setCrmWhatsAppSavingKey("");
    }
  };

  const sortedInventoryCategories = Array.from(inventoryData.categories.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const maxInventoryCategoryValue = Math.max(0, ...sortedInventoryCategories.map(([, v]) => v));
  const servicesWithInventoryUsage = useMemo(() => (services || []).filter((svc) => getInventoryUsageRows(svc).length > 0), [services]);
  const inventoryServiceInsights = useMemo(() => {
    const all = (Array.isArray(services) ? services : []).map((service) => serviceInventoryConnectionSummary(service, inventoryData.rows, MONEY));
    const connected = all.filter((item) => item.connected);
    const unconnected = all.filter((item) => !item.connected);
    const risk = all.filter((item) => ["warning", "critical"].includes(item.tone));
    const critical = all.filter((item) => item.tone === "critical");
    const totalMaterialCost = connected.reduce((sum, item) => sum + numberFrom(item.materialCost), 0);
    const avgMarginPct = connected.filter((item) => item.marginPct !== null).length
      ? Math.round(connected.filter((item) => item.marginPct !== null).reduce((sum, item) => sum + numberFrom(item.marginPct), 0) / connected.filter((item) => item.marginPct !== null).length)
      : null;
    return { all, connected, unconnected, risk, critical, totalMaterialCost, avgMarginPct };
  }, [services, inventoryData.rows, MONEY]);

  const filteredInventoryServiceInsights = useMemo(() => {
    const q = String(inventoryServiceSearch || "").trim().toLowerCase();
    const rows = inventoryServiceInsights.all || [];
    if (!q) return rows;
    return rows.filter((item) => serviceInventorySearchText(item).includes(q));
  }, [inventoryServiceInsights, inventoryServiceSearch]);


  // LOOP PHASE 3.0 — Smart Replenishment Planner / Lista de compra accionable.
  const inventoryPurchasePlan = useMemo(() => {
    const activeItems = Array.isArray(inventoryData.rows) ? inventoryData.rows : [];
    const serviceUsageByItem = new Map();

    (servicesWithInventoryUsage || []).forEach((svc) => {
      const serviceName = String(svc?.name || svc?.title || svc?.label || "Servicio").trim() || "Servicio";
      const servicePrice = numberFrom(svc?.price ?? svc?.basePrice ?? svc?.servicePrice ?? 0);
      getInventoryUsageRows(svc).forEach((usage) => {
        const itemId = String(usage.itemId || "").trim();
        if (!itemId) return;
        const arr = serviceUsageByItem.get(itemId) || [];
        arr.push({ serviceName, servicePrice, quantityUsed: numberFrom(usage.quantityUsed) });
        serviceUsageByItem.set(itemId, arr);
      });
    });

    const candidates = activeItems
      .map((item) => {
        const tone = inventoryStockTone(item);
        const suggestion = reorderSuggestionForItem(item);
        const current = numberFrom(item.currentStock);
        const min = numberFrom(item.minStock);
        const unitCost = numberFrom(item.unitCost);
        const supplier = String(item.supplier || "Sin proveedor").trim() || "Sin proveedor";
        const servicesUsing = serviceUsageByItem.get(String(item.id)) || [];
        const usagePerFullServiceRound = servicesUsing.reduce((sum, row) => sum + Math.max(0, numberFrom(row.quantityUsed)), 0);
        const revenueAtRisk = servicesUsing.reduce((sum, row) => sum + numberFrom(row.servicePrice), 0);
        const suggestedQty = Math.max(0, numberFrom(suggestion.suggestedQty));
        const suggestedCost = suggestedQty * unitCost;
        const targetStock = numberFrom(suggestion.targetStock);
        const stockGap = Math.max(0, targetStock - current);
        const serviceCoverage = usagePerFullServiceRound > 0 ? Math.floor(current / usagePerFullServiceRound) : null;
        const targetCoverage = usagePerFullServiceRound > 0 ? Math.floor(targetStock / usagePerFullServiceRound) : null;
        const urgencyScore =
          (tone === "critical" ? 100 : tone === "low" ? 70 : 0) +
          Math.min(40, servicesUsing.length * 8) +
          (revenueAtRisk > 0 ? Math.min(25, Math.round(revenueAtRisk / 20)) : 0) +
          (min > 0 && current <= min / 2 ? 15 : 0) +
          (serviceCoverage !== null && serviceCoverage <= 1 ? 18 : serviceCoverage !== null && serviceCoverage <= 3 ? 8 : 0);

        let priority = "Baja";
        let buyBucket = "watch";
        if (tone === "critical" || urgencyScore >= 110) {
          priority = "Alta";
          buyBucket = "now";
        } else if (tone === "low" || urgencyScore >= 80) {
          priority = "Media";
          buyBucket = "soon";
        }

        const reason = tone === "critical"
          ? "Sin stock o stock crítico"
          : tone === "low"
            ? "Stock por debajo del mínimo"
            : servicesUsing.length
              ? "Producto conectado a servicios"
              : "Inventario estable";

        const coverageHint = serviceCoverage === null
          ? "Sin consumo conectado"
          : serviceCoverage <= 0
            ? "No alcanza para el próximo servicio"
            : `Alcanza aprox. ${serviceCoverage} servicio${serviceCoverage === 1 ? "" : "s"}`;

        const recommendedAction = buyBucket === "now"
          ? "Comprar hoy"
          : buyBucket === "soon"
            ? "Comprar pronto"
            : "Monitorear";

        return {
          ...item,
          tone,
          priority,
          buyBucket,
          reason,
          coverageHint,
          recommendedAction,
          current,
          min,
          unitCost,
          supplier,
          suggestedQty,
          suggestedCost,
          targetStock,
          targetCoverage,
          stockGap,
          servicesUsing,
          usagePerFullServiceRound,
          serviceCoverage,
          serviceCount: servicesUsing.length,
          revenueAtRisk,
          urgencyScore,
        };
      })
      .filter((item) => item.tone !== "ok" || (item.serviceCount > 0 && item.min > 0 && item.current <= item.min * 1.25))
      .sort((a, b) => b.urgencyScore - a.urgencyScore || b.suggestedCost - a.suggestedCost || String(a.name).localeCompare(String(b.name), "es"));

    const totalSuggestedCost = candidates.reduce((sum, item) => sum + numberFrom(item.suggestedCost), 0);
    const criticalCount = candidates.filter((item) => item.tone === "critical").length;
    const highPriorityCount = candidates.filter((item) => item.priority === "Alta").length;
    const nowCount = candidates.filter((item) => item.buyBucket === "now").length;
    const soonCount = candidates.filter((item) => item.buyBucket === "soon").length;
    const watchCount = candidates.filter((item) => item.buyBucket === "watch").length;
    const affectedServiceNames = new Set();
    candidates.forEach((item) => item.servicesUsing.forEach((svc) => affectedServiceNames.add(svc.serviceName)));
    const estimatedRevenueAtRisk = candidates.reduce((sum, item) => sum + numberFrom(item.revenueAtRisk), 0);

    const supplierMap = new Map();
    candidates.forEach((item) => {
      const key = item.supplier || "Sin proveedor";
      const current = supplierMap.get(key) || { supplier: key, items: [], totalCost: 0, nowCount: 0, soonCount: 0 };
      current.items.push(item);
      current.totalCost += numberFrom(item.suggestedCost);
      if (item.buyBucket === "now") current.nowCount += 1;
      if (item.buyBucket === "soon") current.soonCount += 1;
      supplierMap.set(key, current);
    });

    const supplierGroups = Array.from(supplierMap.values())
      .sort((a, b) => b.nowCount - a.nowCount || b.totalCost - a.totalCost || String(a.supplier).localeCompare(String(b.supplier), "es"));

    const health = criticalCount > 0
      ? { tone: "critical", label: "Compra urgente", hint: `${criticalCount} producto${criticalCount === 1 ? "" : "s"} sin stock o crítico. Prioriza compras antes de aceptar más reservas conectadas.` }
      : candidates.length > 0
        ? { tone: "warning", label: "Compra sugerida", hint: `${candidates.length} producto${candidates.length === 1 ? "" : "s"} por revisar antes de quedarte corto.` }
        : { tone: "success", label: "Inventario saludable", hint: "No hay productos por debajo del mínimo configurado." };

    return {
      items: candidates,
      supplierGroups,
      totalSuggestedCost,
      criticalCount,
      highPriorityCount,
      nowCount,
      soonCount,
      watchCount,
      affectedServiceCount: affectedServiceNames.size,
      estimatedRevenueAtRisk,
      health,
    };
  }, [inventoryData.rows, servicesWithInventoryUsage]);

  const filteredInventoryPurchaseItems = useMemo(() => {
    const q = String(inventoryReplenishmentSearch || "").trim().toLowerCase();
    return (inventoryPurchasePlan.items || []).filter((item) => {
      if (inventoryReplenishmentFilter === "now" && item.buyBucket !== "now") return false;
      if (inventoryReplenishmentFilter === "soon" && item.buyBucket !== "soon") return false;
      if (inventoryReplenishmentFilter === "watch" && item.buyBucket !== "watch") return false;
      if (!q) return true;
      return [
        item.name,
        item.category,
        item.supplier,
        item.priority,
        item.reason,
        item.coverageHint,
        ...(Array.isArray(item.servicesUsing) ? item.servicesUsing.map((svc) => svc.serviceName) : []),
      ].filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [inventoryPurchasePlan.items, inventoryReplenishmentFilter, inventoryReplenishmentSearch]);

  const inventoryAutomationDashboard = useMemo(() => {
    const settings = { autoDeduct: inventoryAutoDeduct, deductTrigger: inventoryDeductTrigger };
    const active = (bookings || []).filter((booking) => {
      const lifecycle = bookingLifecycleFromRecord(booking || {});
      return !["cancelled", "rejected", "no_show"].includes(lifecycle.bookingStatus);
    });
    const readyBookings = active.filter((booking) => shouldAutoDeductInventoryForBooking(booking, settings));
    const deductedBookings = active.filter((booking) => inventoryDeductionStatus(booking) === "deducted" || booking.inventoryDeductedAt || booking.inventory?.deductedAt);
    const failedBookings = active.filter((booking) => inventoryDeductionStatus(booking) === "failed");
    const notConfiguredBookings = active.filter((booking) => inventoryDeductionStatus(booking) === "not_configured");
    const readyWithPlans = readyBookings.map((booking) => {
      const serviceId = getServiceId(booking);
      const service = serviceId ? serviceMap.get(serviceId) : null;
      const plan = service ? buildInventoryConsumptionPlan({ booking, service, inventoryItems: inventoryData.rows }) : null;
      return {
        booking,
        service,
        plan,
        client: bookingClientLabel(booking),
        serviceName: getServiceName(booking, serviceMap),
        date: getBookingDate(booking),
        status: inventoryDeductionStatus(booking),
      };
    });
    const readyCost = readyWithPlans.reduce((sum, row) => sum + numberFrom(row.plan?.totalCost), 0);
    return { active, readyBookings, readyWithPlans, readyCost, deductedBookings, failedBookings, notConfiguredBookings };
  }, [bookings, serviceMap, inventoryData.rows, inventoryAutoDeduct, inventoryDeductTrigger]);

  const dataQuality = useMemo(() => {
    const warnings = [];
    if (!businessId) warnings.push({ tone: "critical", title: "Sin empresa seleccionada", body: "No pude identificar el negocio para cargar analíticas." });
    if (!loading && !data.allInPeriod.length && !data.trackingInPeriod.length) warnings.push({ tone: "neutral", title: "Panel listo para datos", body: "Cuando entren reservas, visitas o inventario, este panel se llenará automáticamente." });
    if (period === "custom" && (!customStartDate || !customEndDate)) warnings.push({ tone: "warning", title: "Rango incompleto", body: "Selecciona fecha desde y hasta para que el reporte sea más preciso." });
    if (data.pendingRevenue > 0) warnings.push({ tone: "warning", title: "Por cobrar confirmado", body: `${MONEY.format(data.pendingRevenue)} tiene estado pendiente o parcial con trazabilidad de pago.` });
    if (data.paymentMismatchRevenue > 0) warnings.push({ tone: "warning", title: "Conciliación de pagos", body: `${data.paymentMismatchBookings.length} reserva${data.paymentMismatchBookings.length === 1 ? "" : "s"} marcada${data.paymentMismatchBookings.length === 1 ? "" : "s"} como pagada${data.paymentMismatchBookings.length === 1 ? "" : "s"} tiene${data.paymentMismatchBookings.length === 1 ? "" : "n"} ${MONEY.format(data.paymentMismatchRevenue)} de diferencia entre venta y cobro registrado.` });
    if (data.untrackedExpectedRevenue > 0) warnings.push({ tone: "neutral", title: "Histórico sin trazabilidad de cobro", body: `${data.untrackedRevenueBookings.length} reserva${data.untrackedRevenueBookings.length === 1 ? "" : "s"} · ${MONEY.format(data.untrackedExpectedRevenue)} de valor esperado no se trata como deuda.` });
    if (inventoryData.lowStockItems.length > 0) warnings.push({ tone: "warning", title: "Inventario por revisar", body: `${inventoryData.lowStockItems.length} producto${inventoryData.lowStockItems.length === 1 ? "" : "s"} en bajo stock o sin stock.` });
    if (!hasTracking && !loading) warnings.push({ tone: "neutral", title: "Tráfico en calentamiento", body: "El embudo se activará con nuevas visitas a la página pública." });
    return warnings.slice(0, 3);
  }, [businessId, loading, data.allInPeriod.length, data.trackingInPeriod.length, data.pendingRevenue, data.paymentMismatchRevenue, data.paymentMismatchBookings.length, data.untrackedExpectedRevenue, data.untrackedRevenueBookings.length, inventoryData.lowStockItems.length, hasTracking, period, customStartDate, customEndDate, MONEY]);


  const smartInsights = useMemo(() => {
    const items = [];
    const add = ({ priority = 3, tone = "info", title, metric = "", body, action, area = "Operación" }) => {
      if (!title || !body) return;
      items.push({ priority, tone, title, metric, body, action, area });
    };

    const lowStockCount = inventoryData.lowStockItems.length;
    const revenue = Number(data.revenue || 0);
    const collected = Number(data.collectedRevenue || 0);
    const net = Number(data.businessNetRevenue || 0);
    const marginPct = collected > 0 ? Math.round((net / collected) * 100) : 0;
    const materialPct = revenue > 0 ? Math.round((Number(data.materialCostTotal || 0) / revenue) * 100) : 0;
    const recurrentRate = data.uniqueCustomers ? Math.round((data.recurrentCustomers / data.uniqueCustomers) * 100) : 0;
    const topSource = sortedSources[0];
    const topInterest = sortedServiceInterest[0];
    const topBooked = sortedServiceCount[0];
    const topRevenue = sortedServiceRevenue[0];
    const highPendingPayout = Number(data.workerPayoutPendingTotal || 0);

    if (!hasTracking) {
      add({
        priority: 1,
        tone: "info",
        area: "Tráfico",
        title: "Activa una lectura comercial real",
        metric: "Sin tráfico medido",
        body: "El tracking ya está listo, pero este periodo todavía no tiene suficientes eventos. Comparte el link público con UTM en Instagram/WhatsApp para empezar a medir qué canal convierte mejor.",
        action: "Usa links como ?utm_source=instagram y revisa esta vista después de las primeras visitas.",
      });
    } else {
      if (data.pageViews >= 10 && data.conversionRate < 3) {
        add({
          priority: 1,
          tone: "danger",
          area: "Conversión",
          title: "Hay tráfico, pero poca reserva final",
          metric: `${data.conversionRate}% conversión`,
          body: `En este periodo hubo ${data.pageViews} visitas al perfil y solo ${data.completedForConversion} reservas completadas. La demanda existe, pero algo puede estar frenando al cliente antes de confirmar.`,
          action: "Revisa fotos, precios visibles, disponibilidad y el primer servicio mostrado. Haz una prueba completa del flujo desde móvil.",
        });
      }

      if (data.pageViews >= 10 && data.intentRate < 8) {
        add({
          priority: 2,
          tone: "warning",
          area: "Perfil público",
          title: "Pocas personas tocan reservar",
          metric: `${data.intentRate}% interés`,
          body: `De ${data.pageViews} visitas, ${data.reserveClicks} tocaron reservar. Puede faltar una oferta clara, mejores fotos o una llamada a la acción más fuerte arriba del perfil.`,
          action: "Mueve el servicio estrella arriba, usa una foto más clara y prueba un texto tipo “Reserva en menos de 1 minuto”.",
        });
      }

      if (data.reserveClicks >= 5 && data.completionRate < 55) {
        add({
          priority: 1,
          tone: "danger",
          area: "Booking flow",
          title: "Clientes entran al flujo pero se caen antes del final",
          metric: `${data.completionRate}% completa`,
          body: `Hubo ${data.bookingStarts} inicios de reserva y ${data.completedForConversion} reservas completadas. Esto apunta a fricción dentro del flujo, no necesariamente a falta de demanda.`,
          action: "Revisa disponibilidad, selección de trabajador, campos obligatorios y tiempos de carga en celular.",
        });
      }

      if (topSource && topSource[1] >= 3) {
        add({
          priority: 4,
          tone: "success",
          area: "Canales",
          title: `${topSource[0]} ya está trayendo intención`,
          metric: `${topSource[1]} eventos`,
          body: `La fuente con más actividad en este periodo fue ${topSource[0]}. Esto puede ayudarte a decidir dónde empujar contenido o pauta primero.`,
          action: "Duplica el canal que más intención trae y separa links por canal con UTM para comparar conversión real.",
        });
      }
    }

    if (data.pendingRevenue > 0) {
      add({
        priority: 1,
        tone: "warning",
        area: "Cobros",
        title: "Hay dinero pendiente por cobrar",
        metric: MONEY.format(data.pendingRevenue),
        body: `Tienes ${data.unpaidBookings.length} reserva${data.unpaidBookings.length === 1 ? "" : "s"} con cobro pendiente. Esto afecta caja aunque el calendario se vea lleno.`,
        action: "Marca el pago apenas se cobre y revisa reservas completadas sin método de pago.",
      });
    }

    if (Number(data.trackedRevenueBase || 0) > 0 && data.collectionRate < 85) {
      add({
        priority: 2,
        tone: "warning",
        area: "Finanzas",
        title: "La tasa de cobro puede mejorar",
        metric: `${data.collectionRate}% cobrado`,
        body: `El negocio generó ${MONEY.format(revenue)}, pero se ha registrado como cobrado ${MONEY.format(collected)}.`,
        action: "Usa el estado Pagado/Pendiente en cada reserva para que el dashboard refleje la caja real.",
      });
    }

    if (highPendingPayout > 0) {
      add({
        priority: 2,
        tone: "info",
        area: "Equipo",
        title: "Tienes payout pendiente al equipo",
        metric: MONEY.format(highPendingPayout),
        body: "Las comisiones pendientes ya están separadas del neto del negocio. Esto ayuda a evitar confundir venta con utilidad disponible.",
        action: "Cierra pagos semanalmente y marca cada reserva como payout pagado cuando corresponda.",
      });
    }

    if (lowStockCount > 0) {
      add({
        priority: 1,
        tone: "danger",
        area: "Inventario",
        title: "Riesgo operativo por bajo stock",
        metric: `${lowStockCount} producto${lowStockCount === 1 ? "" : "s"}`,
        body: `Hay ${lowStockCount} producto${lowStockCount === 1 ? "" : "s"} por debajo del mínimo. Si esos productos están conectados a servicios activos, podrías aceptar reservas sin tener insumos suficientes.`,
        action: "Reabastece primero los productos conectados a tus servicios más reservados.",
      });
    }

    if (revenue > 0 && materialPct >= 25) {
      add({
        priority: 2,
        tone: "warning",
        area: "Margen",
        title: "Los materiales están pesando en el margen",
        metric: `${materialPct}% de ventas`,
        body: `El costo registrado de materiales representa aproximadamente ${materialPct}% del ingreso estimado.`,
        action: "Revisa costos unitarios, cantidades usadas por servicio y precios de los servicios con mayor consumo.",
      });
    }

    if (collected > 0 && marginPct < 35) {
      add({
        priority: 2,
        tone: "warning",
        area: "Rentabilidad",
        title: "El margen neto parece bajo",
        metric: `${marginPct}% neto`,
        body: `Después de comisiones y materiales, el neto estimado es ${MONEY.format(net)} sobre ${MONEY.format(collected)} cobrado.`,
        action: "Compara servicios de alto ingreso vs alto costo. Ajusta comisión, precio o consumo de inventario donde el margen sea más débil.",
      });
    }

    if (topInterest && topBooked && String(topInterest[0]) !== String(topBooked[0]) && topInterest[1] >= 3) {
      add({
        priority: 3,
        tone: "info",
        area: "Servicios",
        title: "Interés y reservas no están alineados",
        metric: topInterest[0],
        body: `${topInterest[0]} genera mucho interés, pero el servicio más reservado es ${topBooked[0]}. Puede haber curiosidad sin conversión.`,
        action: "Revisa precio, duración, descripción y disponibilidad del servicio con más clicks.",
      });
    }

    if (topRevenue && topBooked && String(topRevenue[0]) !== String(topBooked[0])) {
      add({
        priority: 4,
        tone: "success",
        area: "Mix de servicios",
        title: "Tu servicio de más ingreso no es necesariamente el más reservado",
        metric: topRevenue[0],
        body: `${topRevenue[0]} lidera ingresos, mientras ${topBooked[0]} lidera volumen. Esto puede ser una oportunidad para empujar servicios de mayor ticket.`,
        action: "Destaca el servicio de mayor ingreso en el perfil y combínalo con el servicio más popular como paquete o recomendación.",
      });
    }

    if (data.uniqueCustomers >= 5 && recurrentRate < 20) {
      add({
        priority: 3,
        tone: "info",
        area: "Clientes",
        title: "Hay oportunidad de retención",
        metric: `${recurrentRate}% recurrentes`,
        body: `Tienes ${data.uniqueCustomers} clientes únicos y ${data.recurrentCustomers} recurrentes en este periodo.`,
        action: "Prueba recordatorios, promociones de segunda visita o mensajes post-servicio para subir recurrencia.",
      });
    }

    if (data.peakHour) {
      add({
        priority: 5,
        tone: "success",
        area: "Agenda",
        title: "Hay una hora pico clara",
        metric: data.peakHour.key,
        body: `${data.peakHour.key} concentra más actividad en el calendario.`,
        action: "Asegura disponibilidad del equipo y evita bloquear esa franja con tareas internas.",
      });
    }

    if (!items.length) {
      add({
        priority: 6,
        tone: "info",
        area: "Loop",
        title: "Todo listo para generar mejores recomendaciones",
        metric: "Sin alertas fuertes",
        body: "A medida que entren más reservas, pagos, inventario y tráfico, Loop podrá detectar oportunidades más precisas por servicio, canal y trabajador.",
        action: "Sigue registrando pagos, payouts e inventario para mejorar la calidad de los insights.",
      });
    }

    return items.sort((a, b) => a.priority - b.priority).slice(0, 12);
  }, [data, inventoryData, hasTracking, sortedSources, sortedServiceInterest, sortedServiceCount, sortedServiceRevenue, MONEY]);

  const topSmartInsights = smartInsights.slice(0, 4);


  // LOOP PHASE 3B — Executive Summary Premium.
  const insightTargetView = (area = "") => {
    const key = String(area || "").toLowerCase();
    if (key.includes("inventario") || key.includes("margen") || key.includes("rentabilidad")) return "inventory";
    if (key.includes("cliente") || key.includes("crm") || key.includes("retención")) return "crm";
    if (key.includes("tráfico") || key.includes("conversion") || key.includes("conversión") || key.includes("perfil")) return "traffic";
    if (key.includes("caja") || key.includes("cobro") || key.includes("finanzas") || key.includes("equipo")) return "finance";
    return "insights";
  };

  const executiveHealth = data.pendingRevenue > 0
    ? {
        tone: "warning",
        eyebrow: "Atención de caja",
        title: "Hay dinero pendiente por cerrar",
        body: `${MONEY.format(data.pendingRevenue)} sigue pendiente. Prioriza cobros antes de mirar crecimiento.`,
      }
    : inventoryData.lowStockItems.length > 0
      ? {
          tone: "warning",
          eyebrow: "Riesgo operativo",
          title: "Inventario necesita revisión",
          body: `${inventoryData.lowStockItems.length} producto${inventoryData.lowStockItems.length === 1 ? "" : "s"} en bajo stock o sin stock.`,
        }
      : Number(data.revenue || 0) > 0
        ? {
            tone: "success",
            eyebrow: "Negocio saludable",
            title: "El periodo está bajo control",
            body: "No hay alertas fuertes de caja o inventario para este filtro.",
          }
        : {
            tone: "neutral",
            eyebrow: "Listo para operar",
            title: "Esperando más actividad",
            body: "Cuando entren reservas, cobros e inventario, aquí aparecerán prioridades accionables.",
          };

  const overviewPriorityRows = [
    data.pendingRevenue > 0 ? {
      tone: "warning",
      title: "Cerrar cobros confirmados",
      metric: MONEY.format(data.pendingRevenue),
      body: `${data.unpaidBookings.length} reserva${data.unpaidBookings.length === 1 ? "" : "s"} con pago pendiente o parcial rastreado.`,
      targetView: "finance",
    } : null,
    data.paymentMismatchRevenue > 0 ? {
      tone: "warning",
      title: "Revisar pagos inconsistentes",
      metric: MONEY.format(data.paymentMismatchRevenue),
      body: `${data.paymentMismatchBookings.length} reserva${data.paymentMismatchBookings.length === 1 ? "" : "s"} marcada${data.paymentMismatchBookings.length === 1 ? "" : "s"} como pagada${data.paymentMismatchBookings.length === 1 ? "" : "s"} tiene${data.paymentMismatchBookings.length === 1 ? "" : "n"} cobro menor al valor esperado.`,
      targetView: "finance",
    } : null,
    Number(data.workerPayoutPendingTotal || 0) > 0 ? {
      tone: "info",
      title: "Revisar payout del equipo",
      metric: MONEY.format(data.workerPayoutPendingTotal),
      body: "Comisiones pendientes separadas del neto del negocio.",
      targetView: "finance",
    } : null,
    inventoryData.lowStockItems.length > 0 ? {
      tone: "danger",
      title: "Reponer inventario sensible",
      metric: String(inventoryData.lowStockItems.length),
      body: "Productos bajo mínimo pueden afectar servicios activos.",
      targetView: "inventory",
    } : null,
    (crmExecutiveDashboard.priorityRows || []).length > 0 ? {
      tone: "success",
      title: "Oportunidad CRM",
      metric: `${(crmExecutiveDashboard.priorityRows || []).length} clientes`,
      body: "Base total · reactivación, VIP o seguimiento pendiente; no depende del período.",
      targetView: "crm",
    } : null,
    !hasTracking ? {
      tone: "neutral",
      title: "Activar lectura de tráfico",
      metric: "Pendiente",
      body: "Comparte el link público para medir visitas, intención y conversión.",
      targetView: "traffic",
    } : null,
    ...topSmartInsights.slice(0, 2).map((item) => ({
      tone: item.tone || "info",
      title: item.title,
      metric: item.metric || item.area || "Insight",
      body: item.action || item.body,
      targetView: insightTargetView(item.area),
    })),
  ].filter(Boolean).slice(0, 5);

  const overviewOpportunityRows = [
    data.topMoneyService ? {
      label: "Servicio estrella",
      title: data.topMoneyService.key,
      value: MONEY.format(data.topMoneyService.value),
      hint: "Mayor ingreso del periodo",
      targetView: "finance",
    } : null,
    data.topService ? {
      label: "Mayor demanda",
      title: data.topService.key,
      value: String(data.topService.value),
      hint: "Servicio más reservado",
      targetView: "finance",
    } : null,
    data.topWorker ? {
      label: "Equipo",
      title: data.topWorker.key,
      value: MONEY.format(data.topWorker.value),
      hint: "Mayor ingreso por trabajador",
      targetView: "finance",
    } : null,
    hasTracking ? {
      label: "Conversión",
      title: `${data.conversionRate}%`,
      value: `${data.pageViews || 0} visitas`,
      hint: "Perfil público a reserva",
      targetView: "traffic",
    } : null,
    data.recurrentCustomers ? {
      label: "Retención",
      title: `${data.recurrentCustomers} recurrente${data.recurrentCustomers === 1 ? "" : "s"}`,
      value: `${data.uniqueCustomers || 0} clientes`,
      hint: "Oportunidad CRM",
      targetView: "crm",
    } : null,
  ].filter(Boolean).slice(0, 4);


  const reportData = useMemo(() => {
    const reservationsRows = data.financeRows.map((row) => ({
      Fecha: dateLabelShort(row.date),
      Cliente: row.client,
      Servicio: row.service,
      Trabajador: row.worker,
      Venta: row.amount,
      Cobrado: row.collected,
      Pendiente: row.pending,
      Metodo: row.method,
      EstadoPago: row.status,
      Payout: row.payout,
      EstadoPayout: row.payoutStatus,
      Materiales: row.inventoryCost,
      Neto: row.net,
    }));

    const payoutRows = Array.from(data.payoutByWorker.entries()).map(([worker, total]) => ({
      Trabajador: worker,
      PayoutTotal: total,
      PayoutPendiente: data.payoutPendingByWorker.get(worker) || 0,
      PayoutPagado: data.payoutPaidByWorker.get(worker) || 0,
      NetoNegocio: data.netByWorker.get(worker) || 0,
      IngresoGenerado: data.revenueByWorker.get(worker) || 0,
    }));

    const inventoryRows = inventoryData.rows.map((item) => ({
      Producto: item.name,
      Categoria: item.category,
      Stock: item.currentStock,
      Minimo: item.minStock,
      Unidad: item.unit,
      CostoUnitario: item.unitCost,
      ValorInventario: item.currentStock * item.unitCost,
      PrecioVenta: item.salePrice,
      ValorVentaPotencial: item.currentStock * item.salePrice,
      Proveedor: item.supplier,
      Estado: inventoryStockLabel(item),
    }));

    const trafficRows = data.trackingInPeriod.map((event) => ({
      Fecha: dateLabelShort(event.__date),
      Evento: ownerFriendlyTrafficLabel(event.__type || eventType(event)),
      Tipo: event.__type || eventType(event),
      Fuente: eventSource(event),
      Servicio: event.serviceName || event.serviceTitle || event.serviceId || "",
      Dispositivo: event.device || "",
      VisitorId: eventVisitorKey(event),
      Path: event.path || event.url || "",
    }));

    const summaryRows = [
      { Metrica: "Periodo", Valor: data.periodLabel },
      { Metrica: "Ingresos estimados", Valor: data.revenue },
      { Metrica: "Cobrado de ventas", Valor: data.collectedRevenue },
      { Metrica: "Por cobrar confirmado", Valor: data.pendingRevenue },
      { Metrica: "Conciliación pendiente", Valor: data.paymentMismatchRevenue },
      { Metrica: "Payout trabajadores", Valor: data.workerPayoutTotal },
      { Metrica: "Payout pendiente", Valor: data.workerPayoutPendingTotal },
      { Metrica: "Costo materiales", Valor: data.materialCostTotal },
      { Metrica: "Resultado operativo estimado", Valor: data.businessNetRevenue },
      { Metrica: "Reservas operativas", Valor: data.operationalBookings.length },
      { Metrica: "Ticket promedio", Valor: Math.round(data.ticketAvg || 0) },
      { Metrica: "Tasa cobranza rastreada", Valor: `${data.collectionRate || 0}%` },
      { Metrica: "Conversión", Valor: hasTracking ? `${data.conversionRate || 0}%` : "Sin tracking" },
      { Metrica: "Productos bajo stock", Valor: inventoryData.lowStockItems.length },
    ];

    const cashByMethod = Array.from(data.collectedByMethod.entries()).map(([method, amount]) => ({ method, amount }));
    const lowStockRows = inventoryData.lowStockItems.map((item) => {
      const suggestion = reorderSuggestionForItem(item);
      return {
        Producto: item.name,
        Categoria: item.category,
        StockActual: item.currentStock,
        StockMinimo: item.minStock,
        Unidad: item.unit,
        CantidadSugeridaCompra: suggestion.suggestedQty,
        StockObjetivo: suggestion.targetStock,
        CostoEstimadoReposicion: suggestion.suggestedQty * numberFrom(item.unitCost),
        Proveedor: item.supplier || "",
        Estado: inventoryStockLabel(item),
      };
    });
    const closeoutRows = dailyTrend.map((row) => ({
      Fecha: row.key,
      Reservas: row.count,
      IngresoEstimado: row.revenue,
    }));

    const crmRows = crmData.rows.map((customer) => ({
      Cliente: customer.name,
      Telefono: customer.phone,
      Email: customer.email,
      Segmentos: customer.segments.map(crmSegmentMeta).join(" | "),
      Reservas: customer.totalBookings,
      Completadas: customer.completedBookings,
      Canceladas: customer.cancelledBookings,
      NoShow: customer.noShowBookings,
      TotalGastado: customer.totalSpent,
      TicketPromedio: customer.avgTicket,
      UltimaVisita: crmFormatDate(customer.lastVisit),
      ProximaReserva: crmFormatDate(customer.nextBooking),
      ServicioFavorito: customer.favoriteService,
      TrabajadorFavorito: customer.favoriteWorker,
      ProductosComprados: customer.productSales,
      Tags: (customer.tags || []).join(" | "),
      Notas: customer.notes || "",
    }));

    return { reservationsRows, payoutRows, inventoryRows, trafficRows, summaryRows, cashByMethod, lowStockRows, closeoutRows, crmRows };
  }, [data, inventoryData, crmData, hasTracking, dailyTrend]);

  const reportBaseName = compactFilename(`${businessName || "loop"}-${data.periodLabel || "reporte"}`);

  const exportCsv = (kind) => {
    setReportStatus("");
    const configs = {
      resumen: { name: "resumen", headers: ["Metrica", "Valor"], rows: reportData.summaryRows },
      reservas: { name: "reservas-finanzas", headers: ["Fecha", "Cliente", "Servicio", "Trabajador", "Venta", "Cobrado", "Pendiente", "Metodo", "EstadoPago", "Payout", "EstadoPayout", "Materiales", "Neto"], rows: reportData.reservationsRows },
      payouts: { name: "payout-trabajadores", headers: ["Trabajador", "PayoutTotal", "PayoutPendiente", "PayoutPagado", "NetoNegocio", "IngresoGenerado"], rows: reportData.payoutRows },
      inventario: { name: "inventario", headers: ["Producto", "Categoria", "Stock", "Minimo", "Unidad", "CostoUnitario", "ValorInventario", "PrecioVenta", "ValorVentaPotencial", "Proveedor", "Estado"], rows: reportData.inventoryRows },
      bajo_stock: { name: "reorden-bajo-stock", headers: ["Producto", "Categoria", "StockActual", "StockMinimo", "Unidad", "CantidadSugeridaCompra", "StockObjetivo", "CostoEstimadoReposicion", "Proveedor", "Estado"], rows: reportData.lowStockRows },
      cierre_diario: { name: "cierre-diario", headers: ["Fecha", "Reservas", "IngresoEstimado"], rows: reportData.closeoutRows },
      crm: { name: "crm-clientes", headers: ["Cliente", "Telefono", "Email", "Segmentos", "Reservas", "Completadas", "Canceladas", "NoShow", "TotalGastado", "TicketPromedio", "UltimaVisita", "ProximaReserva", "ServicioFavorito", "TrabajadorFavorito", "ProductosComprados", "Tags", "Notas"], rows: reportData.crmRows },
      trafico: { name: "trafico", headers: ["Fecha", "Evento", "Tipo", "Fuente", "Servicio", "Dispositivo", "VisitorId", "Path"], rows: reportData.trafficRows },
    };
    const cfg = configs[kind];
    if (!cfg) return;
    if (!cfg.rows.length) {
      setReportStatus("No hay filas para exportar en este reporte.");
      return;
    }
    downloadTextFile(`${reportBaseName}-${cfg.name}.csv`, buildCsv(cfg.headers, cfg.rows));
    setReportStatus(`Exportado: ${cfg.name}.csv`);
  };


  const exportInventoryPurchasePlan = () => {
    const rows = (inventoryPurchasePlan.items || []).map((item) => ({
      Prioridad: item.priority,
      Accion: item.recommendedAction,
      Producto: item.name,
      Categoria: item.category,
      Proveedor: item.supplier || "",
      StockActual: item.current,
      StockMinimo: item.min,
      StockObjetivo: item.targetStock,
      CantidadSugeridaCompra: item.suggestedQty,
      Unidad: item.unit,
      CostoUnitario: item.unitCost,
      CostoEstimadoCompra: item.suggestedCost,
      CoberturaActual: item.coverageHint,
      ServiciosConectados: (item.servicesUsing || []).map((svc) => svc.serviceName).join(" | "),
      RevenueEnRiesgo: item.revenueAtRisk,
      Motivo: item.reason,
    }));

    if (!rows.length) {
      setReportStatus("No hay productos en la lista de compra sugerida.");
      return;
    }

    const headers = [
      "Prioridad",
      "Accion",
      "Producto",
      "Categoria",
      "Proveedor",
      "StockActual",
      "StockMinimo",
      "StockObjetivo",
      "CantidadSugeridaCompra",
      "Unidad",
      "CostoUnitario",
      "CostoEstimadoCompra",
      "CoberturaActual",
      "ServiciosConectados",
      "RevenueEnRiesgo",
      "Motivo",
    ];

    downloadTextFile(`${reportBaseName}-compra-sugerida.csv`, buildCsv(headers, rows));
    setReportStatus("Exportado: compra-sugerida.csv");
  };

  const exportExecutiveReport = () => {
    const lines = [
      `Reporte Loop - ${businessName}`,
      `Periodo: ${data.periodLabel}`,
      "",
      "Resumen financiero",
      `Ingresos estimados: ${MONEY.format(data.revenue)}`,
      `Cobrado de ventas: ${MONEY.format(data.collectedRevenue)}`,
      `Por cobrar confirmado: ${MONEY.format(data.pendingRevenue)}`,
      `Payout trabajadores: ${MONEY.format(data.workerPayoutTotal)}`,
      `Costo materiales: ${MONEY.format(data.materialCostTotal)}`,
      `Neto estimado negocio: ${MONEY.format(data.businessNetRevenue)}`,
      "",
      "Operación",
      `Reservas activas: ${data.activeBookings.length}`,
      `Reservas pendientes: ${data.pendingBookings.length}`,
      `Canceladas: ${data.cancelledBookings.length}`,
      `No-show: ${data.noShowBookings.length}`,
      "",
      "Caja por método",
      ...(reportData.cashByMethod.length ? reportData.cashByMethod.map((row) => `- ${row.method}: ${MONEY.format(row.amount)}`) : ["- Sin cobros registrados"]),
      "",
      "Top insights",
      ...(topSmartInsights.length ? topSmartInsights.map((item, idx) => `${idx + 1}. ${item.title} — ${item.action || item.body}`) : ["Sin insights críticos en este periodo."]),
    ];
    downloadTextFile(`${reportBaseName}-reporte-ejecutivo.txt`, lines.join("\n"), "text/plain;charset=utf-8");
    setReportStatus("Reporte ejecutivo descargado.");
  };

  const updateBusinessPhase2Settings = async (patch = {}) => {
    if (!businessId) return;
    setBusinessSettingsSaving(true);
    setBusinessSettingsStatus("");
    try {
      // LOOP PHASE 2.8 — keep legacy flat settings and normalized nested settings in sync.
      const normalizedPatch = { ...(patch || {}) };
      if (Object.prototype.hasOwnProperty.call(patch, "inventoryEnabled")) {
        normalizedPatch.enabledModules = { ...(loopSettings.enabledModules || {}), inventory: patch.inventoryEnabled !== false };
        normalizedPatch.inventorySettings = { ...(loopSettings.inventorySettings || {}), enabled: patch.inventoryEnabled !== false, plan: patch.inventoryEnabled === false ? "disabled" : (patch.inventoryPlan || loopSettings.inventorySettings?.plan || "early_access") };
      }
      if (Object.prototype.hasOwnProperty.call(patch, "inventoryAutoDeduct")) {
        normalizedPatch.inventorySettings = { ...(normalizedPatch.inventorySettings || loopSettings.inventorySettings || {}), autoDeduct: patch.inventoryAutoDeduct !== false };
      }
      if (Object.prototype.hasOwnProperty.call(patch, "inventoryDeductTrigger")) {
        normalizedPatch.inventorySettings = { ...(normalizedPatch.inventorySettings || loopSettings.inventorySettings || {}), deductTrigger: patch.inventoryDeductTrigger || "completed" };
      }
      const payload = {
        ...normalizedPatch,
        updatedAt: serverTimestamp(),
        phase2ASettingsUpdatedAt: serverTimestamp(),
      };
      if (isIosCapacitor() && typeof fsClient.setDocByPath === "function") {
        const nowIso = new Date().toISOString();
        await fsClient.setDocByPath(`businesses/${businessId}`, { ...normalizedPatch, updatedAt: nowIso, phase2ASettingsUpdatedAt: nowIso }, { db, merge: true });
      } else {
        await updateDoc(doc(db, `businesses/${businessId}`), payload);
      }
      setBusiness((prev) => ({ ...(prev || {}), ...normalizedPatch }));
      setBusinessSettingsStatus("Configuración actualizada.");
    } catch (err) {
      console.error("[BusinessAnalytics] phase2 settings update failed", err);
      setBusinessSettingsStatus("No pude guardar la configuración. Revisa permisos de Firestore.");
    } finally {
      setBusinessSettingsSaving(false);
    }
  };

  const resetInventoryForm = () => {
    setInventoryForm({ ...EMPTY_INVENTORY_FORM });
    setInventoryStatus("");
  };

  const editInventoryItem = (item) => {
    setInventoryForm(inventoryFormFromItem(item));
    setInventoryStatus("");
    setActiveView("inventory");
    setInventoryPanel("product");
  };

  const saveInventoryItem = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    const name = String(inventoryForm.name || "").trim();
    if (!businessId) return setInventoryStatus("No hay negocio seleccionado.");
    if (!name) return setInventoryStatus("Agrega el nombre del producto.");
    setInventorySaving(true);
    setInventoryStatus("");
    try {
      const payload = inventoryPayloadFromForm(inventoryForm);
      if (inventoryForm.id) {
        if (isIosCapacitor() && typeof fsClient.setDocByPath === "function") {
          await fsClient.setDocByPath(`businesses/${businessId}/inventoryItems/${inventoryForm.id}`, payload, { db, merge: true });
        } else {
          await updateDoc(doc(db, `businesses/${businessId}/inventoryItems/${inventoryForm.id}`), payload);
        }
        setInventoryItems((prev) => prev.map((item) => String(item.id) === String(inventoryForm.id) ? normalizeInventoryItem({ ...item, ...payload, updatedAt: new Date().toISOString() }) : item));
        setInventoryStatus("Producto actualizado.");
      } else {
        const localId = `item-${Date.now()}`;
        let newId = localId;
        const createPayload = { ...payload, createdAt: serverTimestamp() };
        if (isIosCapacitor() && typeof fsClient.setDocByPath === "function") {
          await fsClient.setDocByPath(`businesses/${businessId}/inventoryItems/${localId}`, createPayload, { db, merge: true });
        } else {
          const created = await addDoc(collection(db, `businesses/${businessId}/inventoryItems`), createPayload);
          newId = created.id;
        }
        setInventoryItems((prev) => [normalizeInventoryItem({ id: newId, ...payload, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }), ...prev]);
        setInventoryStatus("Producto agregado al inventario.");
      }
      setInventoryForm({ ...EMPTY_INVENTORY_FORM });
      setInventoryPanel("");
    } catch (err) {
      console.error("[BusinessAnalytics] save inventory item failed", err);
      setInventoryStatus("No pude guardar el producto. Revisa permisos de Firestore.");
    } finally {
      setInventorySaving(false);
    }
  };

  const addInventoryMovement = async (payload) => {
    if (!businessId) return null;
    const localId = `mov-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    if (isIosCapacitor() && typeof fsClient.setDocByPath === "function") {
      const restPayload = { ...payload, createdAt: new Date().toISOString() };
      await fsClient.setDocByPath(`businesses/${businessId}/inventoryMovements/${localId}`, restPayload, { db, merge: true });
      return { id: localId, ...restPayload };
    }
    const created = await addDoc(collection(db, `businesses/${businessId}/inventoryMovements`), payload);
    return { id: created.id, ...payload, createdAt: new Date() };
  };

  const addAuditLog = async (payload = {}) => {
    if (!businessId || loopSettings.enabledModules.auditLogs === false || loopSettings.auditSettings.enabled === false) return null;
    const localId = `audit-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const basePayload = {
      eventType: payload.eventType || payload.type || "audit_event",
      severity: payload.severity || "info",
      source: payload.source || "business_analytics",
      details: payload.details || {},
      bookingId: payload.bookingId || null,
      bookingClientName: payload.bookingClientName || "",
      bookingServiceName: payload.bookingServiceName || "",
      createdByUid: payload.createdByUid || null,
      createdByEmail: payload.createdByEmail || "dashboard",
      createdAt: serverTimestamp(),
    };
    try {
      if (isIosCapacitor() && typeof fsClient.setDocByPath === "function") {
        const nowIso = new Date().toISOString();
        const restPayload = { ...basePayload, createdAt: nowIso };
        await fsClient.setDocByPath(`businesses/${businessId}/auditLogs/${localId}`, restPayload, { db, merge: true });
        return { id: localId, ...restPayload };
      }
      const created = await addDoc(collection(db, `businesses/${businessId}/auditLogs`), basePayload);
      return { id: created.id, ...basePayload, createdAt: new Date() };
    } catch (err) {
      console.warn("[BusinessAnalytics] audit log skipped", err);
      return null;
    }
  };

  const updateInventoryStockValue = async (itemId, nextStock, patch = {}) => {
    const updatePayload = { currentStock: nextStock, updatedAt: serverTimestamp(), ...patch };
    if (isIosCapacitor() && typeof fsClient.setDocByPath === "function") {
      const nowIso = new Date().toISOString();
      const restPayload = Object.fromEntries(Object.entries({ ...updatePayload, updatedAt: nowIso }).map(([key, value]) => {
        if (/At$/.test(key) && value && typeof value === "object") return [key, nowIso];
        return [key, value];
      }));
      await fsClient.setDocByPath(`businesses/${businessId}/inventoryItems/${itemId}`, restPayload, { db, merge: true });
    } else {
      await updateDoc(doc(db, `businesses/${businessId}/inventoryItems/${itemId}`), updatePayload);
    }
  };


  const updateBookingInventoryFields = async (bookingId, patch = {}) => {
    if (!businessId || !bookingId) return;
    if (isIosCapacitor() && typeof fsClient.setDocByPath === "function") {
      const nowIso = new Date().toISOString();
      const restPayload = Object.fromEntries(Object.entries({ ...patch }).map(([key, value]) => {
        if (/At$/.test(key) && value && typeof value === "object") return [key, nowIso];
        return [key, value];
      }));
      await fsClient.setDocByPath(`businesses/${businessId}/bookings/${bookingId}`, restPayload, { db, merge: true });
    } else {
      await updateDoc(doc(db, `businesses/${businessId}/bookings/${bookingId}`), patch);
    }
  };

  const markBookingInventoryDeductionIssue = async ({ booking, status = "failed", reason = "No se pudo descontar inventario.", plan = null }) => {
    const bookingId = booking?.id || booking?.bookingId;
    const serviceName = getServiceName(booking, serviceMap);
    const patch = {
      inventoryDeductionStatus: status,
      inventoryDeductionReason: reason,
      inventoryDeductionCheckedAt: serverTimestamp(),
      inventory: {
        ...(booking?.inventory || {}),
        deductionStatus: status,
        deductionReason: reason,
        deductionCheckedAtClient: new Date().toISOString(),
      },
    };
    if (bookingId) await updateBookingInventoryFields(bookingId, patch);
    const audit = await addAuditLog({
      eventType: status === "not_configured" ? "inventory_deduction_skipped" : "inventory_deduction_failed",
      severity: status === "failed" ? "warning" : "info",
      bookingId: bookingId || null,
      bookingClientName: bookingClientLabel(booking),
      bookingServiceName: serviceName,
      details: {
        reason,
        source: "inventory_automation_bridge",
        status,
        rowCount: plan?.rows?.length || 0,
        missingItems: plan?.rows?.filter((row) => row.missing).map((row) => row.itemName) || [],
        insufficientItems: plan?.rows?.filter((row) => row.insufficient).map((row) => row.itemName) || [],
      },
    });
    setBookings((prev) => prev.map((row) => String(row.id || row.bookingId) === String(bookingId) ? { ...row, inventoryDeductionStatus: status, inventoryDeductionReason: reason, inventoryDeductionCheckedAt: new Date().toISOString(), inventory: { ...(row.inventory || {}), deductionStatus: status, deductionReason: reason } } : row));
    if (audit) setAuditLogs((prev) => [normalizeAuditLog(audit), ...prev]);
  };

  const deductInventoryForBooking = async (booking, options = {}) => {
    const bookingId = booking?.id || booking?.bookingId;
    if (!businessId || !bookingId) return { ok: false, reason: "No hay reserva válida." };
    if (!canAdjustInventory) return { ok: false, reason: "No tienes permiso para descontar inventario." };
    const serviceId = getServiceId(booking);
    const service = serviceId ? serviceMap.get(serviceId) : null;
    const serviceName = getServiceName(booking, serviceMap);
    const triggerSettings = { autoDeduct: true, deductTrigger: inventoryDeductTrigger };

    if (inventoryDeductionStatus(booking) === "deducted" || booking.inventoryDeductedAt || booking.inventory?.deductedAt) {
      return { ok: true, skipped: true, reason: "La reserva ya tenía inventario descontado." };
    }
    if (!options.force && !shouldAutoDeductInventoryForBooking(booking, triggerSettings)) {
      return { ok: false, skipped: true, reason: "La reserva todavía no cumple el trigger de descuento." };
    }
    if (!service) {
      await markBookingInventoryDeductionIssue({ booking, status: "not_configured", reason: "No encontré el servicio conectado a la reserva." });
      return { ok: false, reason: "No encontré el servicio conectado a la reserva." };
    }

    const plan = buildInventoryConsumptionPlan({ booking, service, inventoryItems: inventoryData.rows });
    if (!plan.hasMaterials) {
      await markBookingInventoryDeductionIssue({ booking, status: "not_configured", reason: plan.reason, plan });
      return { ok: true, skipped: true, reason: plan.reason };
    }
    if (!plan.canDeduct) {
      await markBookingInventoryDeductionIssue({ booking, status: "failed", reason: plan.reason, plan });
      return { ok: false, reason: plan.reason };
    }

    const createdMovements = [];
    for (const row of plan.rows) {
      const item = inventoryData.rows.find((candidate) => String(candidate.id) === String(row.itemId));
      if (!item) continue;
      await updateInventoryStockValue(item.id, row.stockAfter, { lastBookingConsumptionAt: serverTimestamp() });
      const movement = await addInventoryMovement(movementPayloadBase({
        item,
        type: "booking_consumption",
        quantityDelta: -Math.abs(row.quantityUsed),
        stockBefore: row.stockBefore,
        stockAfter: row.stockAfter,
        reason: `${options.manual ? "Descuento manual" : "Auto-descuento"}: ${bookingClientLabel(booking)} · ${serviceName}`,
        source: options.manual ? "booking_inventory_manual" : "booking_inventory_auto",
      }));
      createdMovements.push(normalizeInventoryMovement({
        ...movement,
        bookingId,
        itemName: row.itemName,
        unit: row.unit,
        totalCost: row.totalCost,
      }));
    }

    const consumedRows = plan.rows.map((row) => ({
      itemId: row.itemId,
      itemName: row.itemName,
      unit: row.unit,
      quantityUsed: row.quantityUsed,
      stockBefore: row.stockBefore,
      stockAfter: row.stockAfter,
      unitCost: row.unitCost,
      totalCost: row.totalCost,
    }));
    const patch = {
      inventoryDeductionStatus: "deducted",
      inventoryDeductedAt: serverTimestamp(),
      inventoryDeductionTrigger: inventoryDeductTrigger,
      inventoryDeductionSource: options.manual ? "manual_dashboard" : "analytics_bridge",
      inventoryCostAmount: plan.totalCost,
      materialCostAmount: plan.totalCost,
      inventoryConsumed: consumedRows,
      inventory: {
        ...(booking.inventory || {}),
        deductionStatus: "deducted",
        deductedAtClient: new Date().toISOString(),
        deductionTrigger: inventoryDeductTrigger,
        deductionSource: options.manual ? "manual_dashboard" : "analytics_bridge",
        materialCostAmount: plan.totalCost,
        consumed: consumedRows,
      },
    };
    await updateBookingInventoryFields(bookingId, patch);
    const audit = await addAuditLog({
      eventType: "inventory_deducted",
      severity: "info",
      bookingId,
      bookingClientName: bookingClientLabel(booking),
      bookingServiceName: serviceName,
      details: {
        rowCount: consumedRows.length,
        totalCost: plan.totalCost,
        trigger: inventoryDeductTrigger,
        source: options.manual ? "manual_dashboard" : "analytics_bridge",
        items: consumedRows.map((row) => ({ itemId: row.itemId, itemName: row.itemName, quantityUsed: row.quantityUsed, stockBefore: row.stockBefore, stockAfter: row.stockAfter })),
      },
    });

    setInventoryItems((prev) => prev.map((item) => {
      const consumed = consumedRows.find((row) => String(row.itemId) === String(item.id));
      return consumed ? normalizeInventoryItem({ ...item, currentStock: consumed.stockAfter, updatedAt: new Date().toISOString() }) : item;
    }));
    if (createdMovements.length) setInventoryMovements((prev) => [...createdMovements, ...prev]);
    setBookings((prev) => prev.map((row) => String(row.id || row.bookingId) === String(bookingId) ? { ...row, ...patch, inventoryDeductedAt: new Date().toISOString(), inventoryDeductionStatus: "deducted", inventoryCostAmount: plan.totalCost, materialCostAmount: plan.totalCost, inventoryConsumed: consumedRows } : row));
    if (audit) setAuditLogs((prev) => [normalizeAuditLog(audit), ...prev]);
    return { ok: true, deducted: true, totalCost: plan.totalCost, rows: consumedRows.length };
  };

  const runInventoryDeductionBridge = async () => {
    if (!inventoryEnabled) return setInventoryStatus("Activa inventario antes de descontar materiales.");
    if (!inventoryAutoDeduct) return setInventoryStatus("Auto-descuento está apagado. Enciéndelo para procesar reservas listas.");
    const ready = inventoryAutomationDashboard.readyBookings;
    if (!ready.length) return setInventoryStatus("No hay reservas listas para descontar inventario.");
    setInventorySaving(true);
    setInventoryStatus("Procesando descuentos de inventario...");
    try {
      let deducted = 0;
      let skipped = 0;
      let failed = 0;
      for (const booking of ready) {
        const result = await deductInventoryForBooking(booking, { manual: false });
        if (result.deducted) deducted += 1;
        else if (result.skipped) skipped += 1;
        else failed += 1;
      }
      setInventoryStatus(`Puente de inventario listo: ${deducted} reserva${deducted === 1 ? "" : "s"} descontada${deducted === 1 ? "" : "s"}, ${skipped} omitida${skipped === 1 ? "" : "s"}, ${failed} con error.`);
    } catch (err) {
      console.error("[BusinessAnalytics] inventory deduction bridge failed", err);
      setInventoryStatus("No pude completar el puente de inventario. Revisa permisos y stock disponible.");
    } finally {
      setInventorySaving(false);
    }
  };

  const selectedAdjustItem = inventoryData.rows.find((item) => String(item.id) === String(inventoryAdjustForm.itemId));
  const inventoryAdjustPreview = inventoryAdjustmentPreview({ item: selectedAdjustItem, form: inventoryAdjustForm });

  const submitInventoryAdjustment = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (!businessId) return setInventoryStatus("No hay negocio seleccionado.");
    if (!canAdjustInventory) return setInventoryStatus("No tienes permiso para ajustar inventario.");
    const item = inventoryData.rows.find((row) => String(row.id) === String(inventoryAdjustForm.itemId));
    if (!item) return setInventoryStatus("Selecciona un producto para ajustar.");
    const reasonText = String(inventoryAdjustForm.reason || "").trim();
    if (reasonText.length < 3) return setInventoryStatus("Agrega un motivo claro antes de registrar el movimiento.");

    const preview = inventoryAdjustmentPreview({ item, form: inventoryAdjustForm });
    if (!preview) return setInventoryStatus(inventoryAdjustForm.mode === "final" ? "Agrega el stock final." : "Agrega una cantidad mayor a 0.");

    const stockBefore = preview.stockBefore;
    const stockAfter = preview.stockAfter;
    const quantityDelta = preview.quantityDelta;
    if (quantityDelta === 0) return setInventoryStatus("El ajuste no cambia el stock actual.");

    setInventorySaving(true);
    setInventoryStatus("");
    try {
      await updateInventoryStockValue(item.id, stockAfter, { lastManualAdjustmentAt: serverTimestamp() });
      const movement = await addInventoryMovement(movementPayloadBase({
        item,
        type: inventoryAdjustForm.mode === "final" ? "manual_adjustment" : inventoryAdjustForm.type,
        quantityDelta,
        stockBefore,
        stockAfter,
        reason: reasonText,
        source: "inventory_adjustment",
      }));
      const audit = await addAuditLog({
        eventType: "inventory_adjusted",
        severity: quantityDelta < 0 ? "warning" : "info",
        details: {
          itemId: item.id,
          itemName: item.name,
          movementType: inventoryAdjustForm.mode === "final" ? "manual_adjustment" : inventoryAdjustForm.type,
          quantity: quantityDelta,
          stockBefore,
          stockAfter,
          reason: reasonText,
          source: "manual_inventory_adjustment",
        },
      });
      setInventoryItems((prev) => prev.map((row) => String(row.id) === String(item.id) ? normalizeInventoryItem({ ...row, currentStock: stockAfter, updatedAt: new Date().toISOString() }) : row));
      setInventoryMovements((prev) => [normalizeInventoryMovement(movement), ...prev]);
      if (audit) setAuditLogs((prev) => [normalizeAuditLog(audit), ...prev]);
      setInventoryAdjustForm({ ...EMPTY_INVENTORY_ADJUST_FORM });
      setInventoryStatus("Movimiento registrado, stock actualizado y auditoría guardada.");
    } catch (err) {
      console.error("[BusinessAnalytics] inventory adjustment failed", err);
      setInventoryStatus("No pude registrar el movimiento. Revisa permisos de Firestore.");
    } finally {
      setInventorySaving(false);
    }
  };

  const updateStocktakeCount = (itemId, value) => {
    setStocktakeForm((prev) => ({ ...prev, rows: { ...(prev.rows || {}), [itemId]: value } }));
  };

  const submitStocktake = async () => {
    if (!businessId) return setInventoryStatus("No hay negocio seleccionado.");
    const entries = inventoryData.rows
      .map((item) => ({ item, counted: stocktakeForm.rows?.[item.id] }))
      .filter((row) => row.counted !== undefined && row.counted !== "");
    if (!entries.length) return setInventoryStatus("Agrega al menos un conteo físico.");

    setStocktakeSaving(true);
    setInventoryStatus("");
    try {
      const createdMovements = [];
      for (const entry of entries) {
        const item = entry.item;
        const stockBefore = numberFrom(item.currentStock);
        const stockAfter = Math.max(0, numberFrom(entry.counted));
        const quantityDelta = stockAfter - stockBefore;
        if (quantityDelta === 0) continue;
        await updateInventoryStockValue(item.id, stockAfter, { lastStocktakeAt: serverTimestamp() });
        const movement = await addInventoryMovement(movementPayloadBase({
          item,
          type: "stocktake_adjustment",
          quantityDelta,
          stockBefore,
          stockAfter,
          reason: stocktakeForm.notes || stocktakeForm.name || "Conteo físico",
          source: "stocktake",
        }));
        createdMovements.push(normalizeInventoryMovement(movement));
      }
      setInventoryItems((prev) => prev.map((row) => {
        const counted = stocktakeForm.rows?.[row.id];
        if (counted === undefined || counted === "") return row;
        return normalizeInventoryItem({ ...row, currentStock: Math.max(0, numberFrom(counted)), updatedAt: new Date().toISOString() });
      }));
      if (createdMovements.length) setInventoryMovements((prev) => [...createdMovements, ...prev]);
      setStocktakeForm({ ...EMPTY_STOCKTAKE_FORM });
      setInventoryStatus(createdMovements.length ? "Conteo físico aplicado." : "Conteo revisado. No había diferencias que ajustar.");
    } catch (err) {
      console.error("[BusinessAnalytics] stocktake failed", err);
      setInventoryStatus("No pude aplicar el conteo físico. Revisa permisos de Firestore.");
    } finally {
      setStocktakeSaving(false);
    }
  };

  const removeInventoryItem = async (item) => {
    const itemId = String(item?.id || "").trim();
    if (!businessId || !itemId) return;
    setInventorySaving(true);
    setInventoryStatus("");
    try {
      if (isIosCapacitor() && typeof fsClient.setDocByPath === "function") {
        await fsClient.setDocByPath(`businesses/${businessId}/inventoryItems/${itemId}`, { active: false, updatedAt: new Date().toISOString() }, { db, merge: true });
      } else {
        await deleteDoc(doc(db, `businesses/${businessId}/inventoryItems/${itemId}`));
      }
      setInventoryItems((prev) => prev.filter((row) => String(row.id) !== itemId));
      setInventoryStatus("Producto eliminado del inventario.");
      if (String(inventoryForm.id || "") === itemId) setInventoryForm({ ...EMPTY_INVENTORY_FORM });
    } catch (err) {
      console.error("[BusinessAnalytics] delete inventory item failed", err);
      setInventoryStatus("No pude eliminar el producto. Revisa permisos de Firestore.");
    } finally {
      setInventorySaving(false);
    }
  };




  function CashMethodBreakdown({ rows = [], coveredRows = [], max = 0, maxCovered = 0 }) {
    const hasRows = Array.isArray(rows) && rows.length > 0;
    const hasCovered = Array.isArray(coveredRows) && coveredRows.length > 0;
    if (!hasRows && !hasCovered) return <div className="ba-empty">{t("Todavía no hay caja ni servicios cubiertos para este filtro.")}</div>;
    return (
      <div className="ba-cash-method-wrap">
        {hasRows ? (
          <div className="ba-cash-method-table" role="table" aria-label={t("Caja por método")}>
            <div className="ba-cash-method-row head" role="row">
              <span>{t("Método")}</span><span>{t("Cobro")}</span><span>{t("Propina")}</span><span>Total</span>
            </div>
            {rows.map((row) => {
              const width = max > 0 ? Math.max(4, Math.round((safeMoneyValue(row.total) / max) * 100)) : 0;
              return (
                <div className="ba-cash-method-row" role="row" key={row.method || row.label}>
                  <strong>{row.label || "Sin método"}<i style={{ width: `${width}%` }} /></strong>
                  <span>{MONEY.format(row.service || 0)}</span>
                  <span>{MONEY.format(row.tip || 0)}</span>
                  <em>{MONEY.format(row.total || 0)}</em>
                </div>
              );
            })}
          </div>
        ) : <div className="ba-empty slim">{t("Sin caja registrada por método.")}</div>}
        {hasCovered ? (
          <div className="ba-covered-methods">
            <div><span>{t("Servicios cubiertos")}</span><small>{t("No son caja cobrada hoy; sirven para valorar membresías, fidelidad y cortesías.")}</small></div>
            {coveredRows.map(([label, value]) => {
              const width = maxCovered > 0 ? Math.max(4, Math.round((safeMoneyValue(value) / maxCovered) * 100)) : 0;
              return <BarRow key={label} label={label} value={value} max={maxCovered} money subtitle={`${width}% del valor cubierto`} />;
            })}
          </div>
        ) : null}
      </div>
    );
  }
  const financeColumnOptions = [
    {
      key: "attended",
      group: "activity",
      tone: "count",
      emphasis: "soft",
      label: "Atendidas",
      shortLabel: "Atend.",
      render: (row) => row.attended,
      renderTotal: () => workerFinanceSummary.total.attended,
    },
    {
      key: "serviceSales",
      group: "business",
      tone: "income",
      emphasis: "soft",
      label: "Servicios",
      shortLabel: "Servicios",
      render: (row) => MONEY.format(row.serviceSales),
      renderTotal: () => MONEY.format(workerFinanceSummary.total.serviceSales),
    },
    {
      key: "productSales",
      group: "business",
      tone: "income",
      emphasis: "soft",
      label: "Producto",
      shortLabel: "Producto",
      render: (row) => MONEY.format(row.productSales),
      renderTotal: () => MONEY.format(workerFinanceSummary.total.productSales),
    },
    {
      key: "businessMembershipIncome",
      group: "business",
      tone: "membership-income",
      emphasis: "soft",
      label: "Ingreso membresía",
      shortLabel: "Membresía",
      render: (row) => MONEY.format(row.businessMembershipIncome),
      renderTotal: () => MONEY.format(workerFinanceSummary.total.businessMembershipIncome),
    },
    {
      key: "totalBusiness",
      group: "business",
      tone: "business-total",
      emphasis: "strong",
      label: "Total negocio",
      shortLabel: "Total negocio",
      render: (row) => MONEY.format(row.totalBusiness),
      renderTotal: () => MONEY.format(workerFinanceSummary.total.totalBusiness),
    },
    {
      key: "tips",
      group: "worker",
      tone: "worker-soft",
      emphasis: "soft",
      label: "Propinas",
      shortLabel: "Propinas",
      render: (row) => MONEY.format(row.tips),
      renderTotal: () => MONEY.format(workerFinanceSummary.total.tips),
    },
    {
      key: "serviceCommission",
      group: "worker",
      tone: "worker-soft",
      emphasis: "soft",
      label: "Comisión servicio",
      shortLabel: "Com. servicio",
      render: (row) => MONEY.format(row.serviceCommission),
      renderTotal: () => MONEY.format(workerFinanceSummary.total.serviceCommission),
    },
    {
      key: "productCommission",
      group: "worker",
      tone: "worker-soft",
      emphasis: "soft",
      label: "Comisión producto",
      shortLabel: "Com. producto",
      render: (row) => MONEY.format(row.productCommission),
      renderTotal: () => MONEY.format(workerFinanceSummary.total.productCommission),
    },
    {
      key: "workerMembershipIncome",
      group: "worker",
      tone: "worker-membership-income",
      emphasis: "soft",
      label: "Ingreso membresía",
      shortLabel: "Membresía",
      render: (row) => MONEY.format(row.workerMembershipIncome),
      renderTotal: () => MONEY.format(workerFinanceSummary.total.workerMembershipIncome),
    },
    {
      key: "totalPayout",
      group: "worker",
      tone: "worker-total",
      emphasis: "strong",
      label: "Total trabajador",
      shortLabel: "Trabajador",
      render: (row) => MONEY.format(row.totalPayout),
      renderTotal: () => MONEY.format(workerFinanceSummary.total.totalPayout),
    },
    {
      key: "net",
      group: "result",
      tone: "net",
      emphasis: "hero",
      label: "Neto negocio",
      shortLabel: "Neto",
      render: (row) => MONEY.format(row.net),
      renderTotal: () => MONEY.format(workerFinanceSummary.total.net),
    },
  ];

  const financeColumnGroups = [
    { key: "activity", label: "Actividad", hint: "Qué se atendió" },
    { key: "business", label: "Negocio", hint: "Lo que genera el negocio" },
    { key: "worker", label: "Trabajador", hint: "Lo que recibe el trabajador" },
    { key: "result", label: "Resultado", hint: "Resumen final" },
  ];

  const visibleFinanceColumns = financeColumnOptions.filter((col) => financeViewConfig[col.key] !== false || REQUIRED_FINANCE_VIEW_KEYS.has(col.key));
  const visibleFinanceColumnGroups = financeColumnGroups
    .map((group) => ({
      ...group,
      count: visibleFinanceColumns.filter((col) => col.group === group.key).length,
    }))
    .filter((group) => group.count > 0);

  const financeColumnCustomizeGroups = [
    {
      key: "general",
      label: "General",
      hint: "Identidad y volumen",
      options: [
        { key: "worker", label: "Trabajador", required: true, note: "Obligatorio" },
        { key: "attended", label: "Atendidas" },
      ],
    },
    {
      key: "business",
      label: "Negocio",
      hint: "Ingresos generados por la operación",
      options: [
        { key: "serviceSales", label: "Servicios" },
        { key: "productSales", label: "Productos" },
        { key: "businessMembershipIncome", label: "Membresías" },
        { key: "totalBusiness", label: "Total negocio" },
      ],
    },
    {
      key: "worker",
      label: "Trabajador",
      hint: "Propinas, comisiones y payout",
      options: [
        { key: "tips", label: "Propinas" },
        { key: "serviceCommission", label: "Comisión servicio" },
        { key: "productCommission", label: "Comisión producto" },
        { key: "workerMembershipIncome", label: "Membresías trabajador" },
        { key: "totalPayout", label: "Total trabajador" },
      ],
    },
    {
      key: "result",
      label: "Resultado",
      hint: "Lectura final del negocio",
      options: [
        { key: "net", label: "Neto negocio", required: true, note: "Obligatorio" },
      ],
    },
  ];

  const toggleFinanceViewOption = (key) => {
    if (REQUIRED_FINANCE_VIEW_KEYS.has(key) || key === "worker") return;
    setFinanceViewConfig((prev) => ({ ...prev, [key]: !(prev[key] !== false), net: true }));
  };

  const restoreFinanceViewConfig = () => {
    setFinanceViewConfig({ ...DEFAULT_FINANCE_VIEW_CONFIG });
  };

  const financeConfigVisible = (key) => key === "worker" || REQUIRED_FINANCE_VIEW_KEYS.has(key) || financeViewConfig[key] !== false;

  // LOOP PHASE 3C — Finanzas operativas: lectura rápida primero, detalles después.
  const financeTotalCashVisible = Math.max(1, safeMoneyValue(data.collectedRevenue) + safeMoneyValue(data.totalTips));
  const financePendingImportantTotal = safeMoneyValue(data.pendingRevenue) + safeMoneyValue(data.workerPayoutPendingTotal);
  const financeWorkerRows = Array.isArray(workerFinanceSummary.rows) ? workerFinanceSummary.rows : [];


  // LOOP PHASE 3C.10 — Finance worker table v111: configurable executive compact view.
  const financeExecutiveMoney = (value) => MONEY.format(safeMoneyValue(value));

  const financeExecutiveInitial = (value = "") => {
    const clean = String(value || "T").trim();
    return (clean ? clean.slice(0, 1) : "T").toUpperCase();
  };

  const financeExecutiveBreakdownRows = (row = {}, type = "business") => {
    const source = row || {};
    if (type === "business") {
      return [
        { key: "serviceSales", label: "Serv.", value: safeMoneyValue(source.serviceSales) },
        { key: "productSales", label: "Prod.", value: safeMoneyValue(source.productSales) },
        { key: "businessMembershipIncome", label: "Memb.", value: safeMoneyValue(source.businessMembershipIncome) },
      ].filter((item) => financeConfigVisible(item.key));
    }
    return [
      { key: "tips", label: "Prop.", value: safeMoneyValue(source.tips) },
      { key: "serviceCommission", label: "Com. serv.", value: safeMoneyValue(source.serviceCommission) },
      { key: "productCommission", label: "Com. prod.", value: safeMoneyValue(source.productCommission) },
      { key: "workerMembershipIncome", label: "Memb.", value: safeMoneyValue(source.workerMembershipIncome) },
    ].filter((item) => financeConfigVisible(item.key));
  };

  const showFinanceAttendedColumn = financeConfigVisible("attended");
  const showFinanceBusinessBreakdownColumn = ["serviceSales", "productSales", "businessMembershipIncome"].some(financeConfigVisible);
  const showFinanceTotalBusinessColumn = financeConfigVisible("totalBusiness");
  const showFinanceWorkerBreakdownColumn = ["tips", "serviceCommission", "productCommission", "workerMembershipIncome"].some(financeConfigVisible);
  const showFinanceTotalWorkerColumn = financeConfigVisible("totalPayout");

  const financeWorkerExecutiveBaseColumns = [
    { key: "worker", label: "Trabajador", group: "general", width: "minmax(270px,1.65fr)", minPx: 270, visible: true },
    { key: "attended", label: "Atendidas", group: "general", width: "92px", minPx: 92, visible: showFinanceAttendedColumn },
    { key: "businessBreakdown", label: "Negocio", group: "business", width: "minmax(230px,1.12fr)", minPx: 230, visible: showFinanceBusinessBreakdownColumn },
    { key: "totalBusiness", label: "Total negocio", group: "business", width: "minmax(150px,.78fr)", minPx: 150, visible: showFinanceTotalBusinessColumn },
    { key: "workerBreakdown", label: "Trabajador", group: "worker", width: "minmax(255px,1.18fr)", minPx: 255, visible: showFinanceWorkerBreakdownColumn },
    { key: "totalPayout", label: "Total trabajador", group: "worker", width: "minmax(170px,.88fr)", minPx: 170, visible: showFinanceTotalWorkerColumn },
    { key: "net", label: "Neto", group: "result", width: "minmax(145px,.74fr)", minPx: 145, visible: true },
  ];

  const financeWorkerExecutiveColumns = financeWorkerExecutiveBaseColumns
    .filter((col) => col.visible !== false)
    .map((col, index, arr) => ({
      ...col,
      isGroupStart: index > 0 && arr[index - 1]?.group !== col.group,
    }));

  const financeWorkerGridTemplate = financeWorkerExecutiveColumns.map((col) => col.width).join(" ");
  const financeWorkerTableMinWidth = Math.max(760, financeWorkerExecutiveColumns.reduce((sum, col) => sum + numberFrom(col.minPx), 0));
  const financeWorkerTableStyle = {
    "--finance-worker-v110-cols": financeWorkerGridTemplate,
    "--finance-worker-v110-min-width": `${financeWorkerTableMinWidth}px`,
  };

  const financeGroupLabels = {
    business: "NEGOCIO",
    worker: "TRABAJADOR",
    result: "RESULTADO",
  };

  const financeWorkerGroupRanges = ["business", "worker", "result"].map((group) => {
    const first = financeWorkerExecutiveColumns.findIndex((col) => col.group === group);
    if (first < 0) return null;
    let last = first;
    financeWorkerExecutiveColumns.forEach((col, index) => {
      if (col.group === group) last = index;
    });
    return { key: group, label: financeGroupLabels[group], start: first + 1, end: last + 2 };
  }).filter(Boolean);

  const financeWorkerColumnClassName = (col, base = "") => [
    base,
    "ba-finance-worker-cell-v110",
    `is-${col.key}`,
    `is-group-${col.group}`,
    col.isGroupStart ? "is-group-start" : "",
    col.isGroupStart ? `is-${col.group}-start` : "",
  ].filter(Boolean).join(" ");

  function FinanceExecutiveBreakdown({ rows = [] }) {
    if (!rows.length) return <span className="ba-finance-breakdown-empty-v110">—</span>;
    return (
      <div className="ba-finance-breakdown-v110">
        {rows.map((item) => (
          <span key={item.key}>
            <em>{item.label}</em>
            <strong>{financeExecutiveMoney(item.value)}</strong>
          </span>
        ))}
      </div>
    );
  }

  function FinanceExecutivePill({ value, tone = "business" }) {
    return <span className={`ba-finance-pill-v110 is-${tone}`}>{financeExecutiveMoney(value)}</span>;
  }

  function FinanceExecutiveNet({ value }) {
    const amount = safeMoneyValue(value);
    const tone = amount < 0 ? "negative" : amount > 0 ? "positive" : "neutral";
    return <span className={`ba-finance-net-v110 is-${tone}`}>{financeExecutiveMoney(amount)}</span>;
  }

  const renderFinanceWorkerExecutiveCell = (row = {}, col = {}, isTotal = false) => {
    const key = col.key;
    if (key === "worker") {
      return (
        <strong key={key} className={financeWorkerColumnClassName(col, `ba-finance-worker-name-v110 ${isTotal ? "ba-finance-worker-total-name-v110" : ""}`)}>
          <i aria-hidden="true">{isTotal ? "Σ" : financeExecutiveInitial(row.worker)}</i>
          <span>{isTotal ? "Total" : row.worker}</span>
        </strong>
      );
    }
    if (key === "attended") {
      return <span key={key} className={financeWorkerColumnClassName(col, "ba-finance-worker-count-v110")}>{numberFrom(row.attended)}</span>;
    }
    if (key === "businessBreakdown") {
      return <span key={key} className={financeWorkerColumnClassName(col, "ba-finance-worker-breakdown-cell-v110")}><FinanceExecutiveBreakdown rows={financeExecutiveBreakdownRows(row, "business")} /></span>;
    }
    if (key === "totalBusiness") {
      return <span key={key} className={financeWorkerColumnClassName(col, "ba-finance-worker-pill-cell-v110")}><FinanceExecutivePill value={row.totalBusiness} tone="business" /></span>;
    }
    if (key === "workerBreakdown") {
      return <span key={key} className={financeWorkerColumnClassName(col, "ba-finance-worker-breakdown-cell-v110")}><FinanceExecutiveBreakdown rows={financeExecutiveBreakdownRows(row, "worker")} /></span>;
    }
    if (key === "totalPayout") {
      return <span key={key} className={financeWorkerColumnClassName(col, "ba-finance-worker-pill-cell-v110")}><FinanceExecutivePill value={row.totalPayout} tone="worker" /></span>;
    }
    return <span key={key} className={financeWorkerColumnClassName(col, "ba-finance-worker-net-cell-v110")}><FinanceExecutiveNet value={row.net} /></span>;
  };

  const financeCashMethodTotals = financeV3SortedCashMethods.reduce((acc, row) => {
    acc.service += safeMoneyValue(row.service);
    acc.product += safeMoneyValue(row.product);
    acc.tip += safeMoneyValue(row.tip);
    acc.total += safeMoneyValue(row.total);
    return acc;
  }, { service: 0, product: 0, tip: 0, total: 0 });

  const financeCoveredTotal = financeV3SortedCoveredMethods.reduce((sum, row) => sum + safeMoneyValue(row[1]), 0);
  const financeCoveredBreakdown = financeV3SortedCoveredMethods
    .filter((row) => safeMoneyValue(row[1]) > 0)
    .map(([label, value]) => ({ label, value: safeMoneyValue(value) }));

  const financeBusinessNetValue = Number.isFinite(Number(workerFinanceSummary?.total?.net))
    ? Number(workerFinanceSummary.total.net)
    : safeMoneyValue(financeData.businessNetRevenue);

  const financeLoyaltyImpact = (Array.isArray(financeData.financeRows) ? financeData.financeRows : [])
    .filter((row) => row?.paymentIsLoyaltyReward || String(row?.paymentMethod || "").toLowerCase() === "loyalty_reward" || safeMoneyValue(row?.loyaltyCoveredAmount) > 0)
    .reduce((acc, row) => {
      const covered = safeMoneyValue(row?.loyaltyCoveredAmount || row?.coveredServiceAmount);
      const payout = safeMoneyValue(row?.loyaltyWorkerPayoutImpact || row?.payout);
      const costs = safeMoneyValue(row?.loyaltyMaterialCostImpact) + safeMoneyValue(row?.loyaltyProductCostImpact);
      const cashImpact = row?.loyaltyBusinessCashImpact !== undefined && row?.loyaltyBusinessCashImpact !== null
        ? safeMoneyValue(row.loyaltyBusinessCashImpact)
        : -(payout + costs);
      acc.count += 1;
      acc.covered += covered;
      acc.payout += payout;
      acc.costs += costs;
      acc.cashImpact += cashImpact;
      return acc;
    }, { count: 0, covered: 0, payout: 0, costs: 0, cashImpact: 0 });

  // LOOP REPORTS V3 A4.2 — Finance V3 owner dashboard / scalable drill-down.
  const financeV3ServiceSales = Math.max(0, safeMoneyValue(financeData.revenue) - safeMoneyValue(financeData.productSalesRevenueTotal));
  const financeV3DirectCosts =
    safeMoneyValue(financeData.workerPayoutTotal) +
    safeMoneyValue(financeData.materialCostTotal) +
    safeMoneyValue(financeData.productSalesCostTotal);
  const financeV3GeneratedMargin = safeMoneyValue(financeData.revenue) - financeV3DirectCosts;
  const financeV3CashResult = safeMoneyValue(financeData.businessNetRevenue);
  const financeV3OperatingExpenses = isOwnerOrAdmin ? safeMoneyValue(expenseLedgerSummary.incurredTotal) : 0;
  const financeV3PaidOperatingExpenses = isOwnerOrAdmin ? safeMoneyValue(expenseLedgerSummary.paidTotal) : 0;
  const financeV3OperatingResult = financeV3GeneratedMargin - financeV3OperatingExpenses;
  const financeV3CashAfterExpenses = financeV3CashResult - financeV3PaidOperatingExpenses;
  const financeV3CashReceived = safeMoneyValue(financeData.collectedRevenue) + safeMoneyValue(financeData.totalTips);
  const financeV3MembershipIncome = safeMoneyValue(workerFinanceSummary?.total?.businessMembershipIncome);

  // LOOP REPORTS V3 A4.8.9C — operating contribution alignment
  const financeV3Trend = useMemo(() => {
    const normalizeRows = (rows) => (Array.isArray(rows) ? rows : [])
      .filter((row) => row?.date instanceof Date && Number.isFinite(row.date.getTime()))
      .slice()
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    const source = normalizeRows(financeData.financeRows);
    const comparisonSource = normalizeRows(financeV3PreviousData?.financeRows);

    const rangeStart = financeV3ComparisonScope.currentStart;
    const rangeEnd = financeV3ComparisonScope.currentEnd;
    const rangeSpanDays = rangeStart instanceof Date && rangeEnd instanceof Date
      ? Math.max(0, Math.round((rangeEnd.getTime() - rangeStart.getTime()) / 86400000))
      : null;

    const sourceSpanDays = source.length
      ? Math.max(0, Math.round((source[source.length - 1].date.getTime() - source[0].date.getTime()) / 86400000))
      : 0;
    const spanDays = rangeSpanDays ?? sourceSpanDays;
    const grain = spanDays > 540 ? "month" : spanDays > 120 ? "week" : "day";

    const keyFor = (date) => {
      const d = new Date(date);
      if (grain === "month") {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
      }
      if (grain === "week") {
        const monday = new Date(d);
        const delta = (monday.getDay() + 6) % 7;
        monday.setDate(monday.getDate() - delta);
        return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, "0")}-${String(monday.getDate()).padStart(2, "0")}`;
      }
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    };

    const groupRows = (rows) => {
      const grouped = new Map();
      rows.forEach((row) => {
        const key = keyFor(row.date);
        const current = grouped.get(key) || { key, sales: 0, collected: 0, result: 0, count: 0 };
        current.sales += safeMoneyValue(row.totalBusinessAmount ?? row.amount);
        current.collected += safeMoneyValue(row.collected);
        current.result +=
          safeMoneyValue(row.totalBusinessAmount ?? row.amount) -
          safeMoneyValue(row.payout) -
          safeMoneyValue(row.inventoryCost) -
          safeMoneyValue(row.productSalesCost);
        current.count += 1;
        grouped.set(key, current);
      });
      return Array.from(grouped.values()).sort((a, b) => a.key.localeCompare(b.key));
    };

    return {
      rows: groupRows(source),
      comparisonRows: financeV3Comparison.enabled ? groupRows(comparisonSource) : [],
      grain,
      label: grain === "month" ? "Mensual" : grain === "week" ? "Semanal" : "Diario",
    };
  }, [
    financeData.financeRows,
    financeV3PreviousData,
    financeV3Comparison.enabled,
    financeV3ComparisonScope.currentStart,
    financeV3ComparisonScope.currentEnd,
  ]);

  const financeV3WorkerLeaders = useMemo(() => {
    const grouped = new Map();
    (Array.isArray(financeData.financeRows) ? financeData.financeRows : []).forEach((row) => {
      const worker = String(row?.worker || "Sin trabajador").trim() || "Sin trabajador";
      const current = grouped.get(worker) || {
        worker,
        totalBusiness: 0,
        totalPayout: 0,
        directCosts: 0,
        operatingContribution: 0,
      };
      const revenue = safeMoneyValue(row?.totalBusinessAmount ?? row?.amount);
      const payout = safeMoneyValue(row?.payout);
      const materialCost = safeMoneyValue(row?.inventoryCost);
      const productCost = safeMoneyValue(row?.productSalesCost);
      const directCosts = payout + materialCost + productCost;
      current.totalBusiness += revenue;
      current.totalPayout += payout;
      current.directCosts += directCosts;
      current.operatingContribution += revenue - directCosts;
      grouped.set(worker, current);
    });
    return Array.from(grouped.values())
      .sort((a, b) =>
        safeMoneyValue(b.operatingContribution) - safeMoneyValue(a.operatingContribution) ||
        safeMoneyValue(b.totalBusiness) - safeMoneyValue(a.totalBusiness) ||
        a.worker.localeCompare(b.worker)
      )
      .slice(0, 5);
  }, [financeData.financeRows]);

  const financeV3ServiceLeaders = Array.from((financeData.revenueByService || new Map()).entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([service, revenue]) => ({
      service,
      revenue: safeMoneyValue(revenue),
      materialCost: safeMoneyValue(financeData.materialCostByService?.get?.(service)),
    }));

  const openFinanceDetail = (view) => {
    setFinanceDetailView(view);
    setFinanceDetailSearch("");
    setFinanceDetailPage(1);
  };

  const closeFinanceDetail = () => {
    setFinanceDetailView("");
    setFinanceDetailSearch("");
    setFinanceDetailPage(1);
  };

  const financeV3DetailMeta = {
    receivables: {
      eyebrow: "Cobranza",
      title: "Por cobrar confirmado",
      description: "Solo reservas con pago pendiente o parcial y trazabilidad financiera.",
      amountKey: "pending",
      amountLabel: "Saldo",
    },
    reconciliation: {
      eyebrow: "Conciliación",
      title: "Pagos inconsistentes",
      description: "Reservas marcadas como pagadas cuyo monto registrado es menor al esperado.",
      amountKey: "paymentMismatchAmount",
      amountLabel: "Diferencia",
    },
    untracked: {
      eyebrow: "Calidad de datos",
      title: "Histórico sin trazabilidad",
      description: "Reservas históricas con valor esperado, pero sin evidencia suficiente de pago. No son cuentas por cobrar.",
      amountKey: "untrackedExpectedAmount",
      amountLabel: "Valor esperado",
    },
    covered: {
      eyebrow: "Beneficios",
      title: "Servicios cubiertos",
      description: "Valor cubierto por membresía, fidelidad o cortesía. No representa caja cobrada.",
      amountKey: "coveredTrackedAmount",
      amountLabel: "Cubierto",
    },
  };

  const financeV3LocalDetailSourceRows = (Array.isArray(financeData.financeRows) ? financeData.financeRows : []).filter((row) => {
    if (financeDetailView === "receivables") return safeMoneyValue(row.pending) > 0;
    if (financeDetailView === "reconciliation") return safeMoneyValue(row.paymentMismatchAmount) > 0;
    if (financeDetailView === "untracked") return safeMoneyValue(row.untrackedExpectedAmount) > 0;
    if (financeDetailView === "covered") return safeMoneyValue(row.coveredTrackedAmount) > 0;
    return false;
  });

  const financeV3DetailQuery = String(financeDetailSearch || "").trim().toLowerCase();
  const financeV3LocalDetailFilteredRows = financeV3LocalDetailSourceRows.filter((row) => {
    if (!financeV3DetailQuery) return true;
    return [
      row.client,
      row.service,
      row.worker,
      row.method,
      row.status,
    ].map((value) => String(value || "").toLowerCase()).join(" ").includes(financeV3DetailQuery);
  });

  const financeV3LocalDetailSummaryAmount = financeV3LocalDetailFilteredRows.reduce((sum, row) => {
    if (financeDetailView === "receivables") return sum + safeMoneyValue(row.pending);
    if (financeDetailView === "reconciliation") return sum + safeMoneyValue(row.paymentMismatchAmount);
    if (financeDetailView === "untracked") return sum + safeMoneyValue(row.untrackedExpectedAmount);
    if (financeDetailView === "covered") return sum + safeMoneyValue(row.coveredTrackedAmount);
    return sum;
  }, 0);

  const financeV3DetailPageSize = 20;
  const financeV3RemoteDetailReady = isOwnerOrAdmin && financeRemote.detailReady;
  const financeV3DetailFilteredRows = financeV3RemoteDetailReady
    ? Array.from({ length: Math.max(0, Number(financeRemote.detailTotal || 0)) })
    : financeV3LocalDetailFilteredRows;
  const financeV3DetailPages = financeV3RemoteDetailReady
    ? Math.max(1, Number(financeRemote.detailTotalPages || 1))
    : Math.max(1, Math.ceil(financeV3LocalDetailFilteredRows.length / financeV3DetailPageSize));
  const financeV3SafeDetailPage = financeV3RemoteDetailReady
    ? Math.min(Math.max(1, Number(financeRemote.detailPage || 1)), financeV3DetailPages)
    : Math.min(financeDetailPage, financeV3DetailPages);
  const financeV3DetailPageRows = financeV3RemoteDetailReady
    ? (Array.isArray(financeRemote.detailRows) ? financeRemote.detailRows : [])
    : financeV3LocalDetailFilteredRows.slice(
      (financeV3SafeDetailPage - 1) * financeV3DetailPageSize,
      financeV3SafeDetailPage * financeV3DetailPageSize
    );
  const financeV3ActiveDetailMeta = financeV3DetailMeta[financeDetailView] || financeV3DetailMeta.receivables;

  // LOOP PHASE 3C.8 — Mobile finance UX: resumen primero, detalle expandible.
  const financeMoneyParts = (items = []) => (Array.isArray(items) ? items : [])
    .filter((item) => safeMoneyValue(item?.value) > 0)
    .map((item) => `${item.label} ${MONEY.format(safeMoneyValue(item.value))}`);

  const financeCashMethodMobileRows = [
    ...financeV3SortedCashMethods.map((row) => ({
      key: row.method || row.label || "unknown",
      label: row.label || "Sin método",
      total: safeMoneyValue(row.total),
      tone: "",
      parts: financeMoneyParts([
        { label: "Servicio", value: row.service },
        { label: "Producto", value: row.product },
        { label: "Propina", value: row.tip },
      ]),
    })),
    ...(financeV3SortedCashMethods.length ? [{
      key: "total",
      label: "Total",
      total: financeCashMethodTotals.total,
      tone: "total",
      parts: financeMoneyParts([
        { label: "Servicio", value: financeCashMethodTotals.service },
        { label: "Producto", value: financeCashMethodTotals.product },
        { label: "Propina", value: financeCashMethodTotals.tip },
      ]),
    }] : []),
  ];

  const canWorkerSeeTipsMobile = isOwnerOrAdmin || ["tips_only", "payout_total", "full_breakdown"].includes(workerCompensationVisibility);
  const canWorkerSeePayoutMobile = isOwnerOrAdmin || ["payout_total", "full_breakdown"].includes(workerCompensationVisibility);
  const canWorkerSeePayoutBreakdownMobile = isOwnerOrAdmin || workerCompensationVisibility === "full_breakdown";
  const canWorkerSeeBusinessNetMobile = isOwnerOrAdmin;

  const financeWorkerMobileMainValue = (row) => {
    if (isOwnerOrAdmin) return MONEY.format(safeMoneyValue(row.totalBusiness));
    if (canWorkerSeePayoutMobile) return MONEY.format(safeMoneyValue(row.totalPayout));
    if (canWorkerSeeTipsMobile) return MONEY.format(safeMoneyValue(row.tips));
    return `${numberFrom(row.attended)} atendido${numberFrom(row.attended) === 1 ? "" : "s"}`;
  };

  const financeWorkerMobileSummary = (row) => {
    const parts = [`${numberFrom(row.attended)} atendido${numberFrom(row.attended) === 1 ? "" : "s"}`];
    if (isOwnerOrAdmin && safeMoneyValue(row.totalPayout) > 0) parts.push(`Comisión ${MONEY.format(row.totalPayout)}`);
    if (isOwnerOrAdmin && safeMoneyValue(row.net) !== 0) parts.push(`Neto ${MONEY.format(row.net)}`);
    if (!isOwnerOrAdmin && canWorkerSeeTipsMobile && safeMoneyValue(row.tips) > 0) parts.push(`Propinas ${MONEY.format(row.tips)}`);
    if (!isOwnerOrAdmin && canWorkerSeePayoutMobile && safeMoneyValue(row.totalPayout) > 0) parts.push(`Mi comisión ${MONEY.format(row.totalPayout)}`);
    return parts.join(" · ");
  };

  const financeWorkerMobileDetailRows = (row, isTotal = false) => ([
    { key: "attended", label: "Atendidos", value: `${numberFrom(row.attended)}`, show: financeConfigVisible("attended") },
    { key: "serviceSales", label: "Servicios", value: MONEY.format(row.serviceSales), show: financeConfigVisible("serviceSales") && (safeMoneyValue(row.serviceSales) > 0 || isTotal) },
    { key: "productSales", label: "Producto", value: MONEY.format(row.productSales), show: financeConfigVisible("productSales") && safeMoneyValue(row.productSales) > 0 },
    { key: "businessMembershipIncome", label: "Ingreso membresía negocio", value: MONEY.format(row.businessMembershipIncome), show: financeConfigVisible("businessMembershipIncome") && isOwnerOrAdmin && (safeMoneyValue(row.businessMembershipIncome) > 0 || isTotal) },
    { key: "totalBusiness", label: "Total negocio", value: MONEY.format(row.totalBusiness), show: financeConfigVisible("totalBusiness") && isOwnerOrAdmin && (safeMoneyValue(row.totalBusiness) > 0 || isTotal) },
    { key: "tips", label: "Propinas", value: MONEY.format(row.tips), show: financeConfigVisible("tips") && canWorkerSeeTipsMobile && safeMoneyValue(row.tips) > 0 },
    { key: "serviceCommission", label: "Comisión servicio", value: MONEY.format(row.serviceCommission), show: financeConfigVisible("serviceCommission") && canWorkerSeePayoutBreakdownMobile && safeMoneyValue(row.serviceCommission) > 0 },
    { key: "productCommission", label: "Comisión producto", value: MONEY.format(row.productCommission), show: financeConfigVisible("productCommission") && canWorkerSeePayoutBreakdownMobile && safeMoneyValue(row.productCommission) > 0 },
    { key: "workerMembershipIncome", label: "Ingreso membresía trabajador", value: MONEY.format(row.workerMembershipIncome), show: financeConfigVisible("workerMembershipIncome") && canWorkerSeePayoutBreakdownMobile && (safeMoneyValue(row.workerMembershipIncome) > 0 || isTotal) },
    { key: "totalPayout", label: isOwnerOrAdmin ? "Total comisión" : "Mi comisión", value: MONEY.format(row.totalPayout), show: financeConfigVisible("totalPayout") && canWorkerSeePayoutMobile && safeMoneyValue(row.totalPayout) > 0 },
    { key: "net", label: "Neto negocio", value: MONEY.format(row.net), show: canWorkerSeeBusinessNetMobile && (safeMoneyValue(row.net) !== 0 || isTotal) },
  ]).filter((item) => item.show);

  // LOOP PHASE 3A.2 — Reportes limpio: la identidad del negocio vive en el sidebar global.
  // Aquí solo mostramos el módulo activo, contexto del periodo y filtros.
  const selectedWorkerLabel = workerFilter === "all"
    ? "Todos los trabajadores"
    : workerOptions.find((worker) => String(worker.id) === String(workerFilter))?.name || "Trabajador";

  const activeReportMeta = {
    overview: {
      label: "Resumen",
      title: "Resumen ejecutivo",
      description: "Ventas, caja, clientes y operación en una lectura compacta del período.",
      statLabel: "Reservas en vista",
      statValue: String((data.allInPeriod || []).length),
    },
    finance: {
      label: "Finanzas",
      title: "Finanzas",
      description: "Estado de resultados, caja, cobranza, rentabilidad y conciliación del período.",
      statLabel: "Cobranza rastreada",
      statValue: data.trackedRevenueBase > 0 ? `${data.collectionRate}%` : "—",
    },
    inventory: {
      label: "Inventario",
      title: "Inventario",
      description: "Stock bajo, reposición sugerida, materiales por servicio y señales de margen.",
      statLabel: "Por revisar",
      statValue: String(inventoryData.lowStockItems.length),
    },
    crm: {
      label: "Clientes",
      title: "Clientes y CRM",
      description: "Clientes recurrentes, segmentos, oportunidades de reactivación y acciones comerciales.",
      statLabel: "Prioridades",
      statValue: String(crmExecutiveDashboard.priorityCount || 0),
    },
    memberships: {
      label: "Membresías",
      title: "Membresías",
      description: "Uso, renovaciones, beneficios consumidos y salud de los planes activos.",
      statLabel: "Activas",
      statValue: String(membershipAnalytics?.activeMemberships || 0),
    },
    traffic: {
      label: "Tráfico",
      title: "Tráfico y conversión",
      description: "Visitas del perfil, intención de reserva, fuentes y conversión de la página pública.",
      statLabel: "Conversión",
      statValue: hasTracking ? `${data.conversionRate}%` : "—",
    },
    insights: {
      label: "Advisor",
      title: "Loop Advisor",
      description: "Acciones recomendadas para mejorar caja, conversión, inventario, margen y operación.",
      statLabel: "Acciones",
      statValue: String(smartInsights.length),
    },
    exports: {
      label: "Exportar",
      title: "Exportar reportes",
      description: "Descarga información financiera, CRM, inventario y tráfico del período actual.",
      statLabel: "Periodo",
      statValue: data.periodLabel,
    },
  }[activeView] || {
    label: "Reportes",
    title: "Reportes",
    description: "Vista consolidada del negocio.",
    statLabel: "Periodo",
    statValue: data.periodLabel,
  };

  const reportModuleOptions = [
    { id: "overview", label: "Resumen", helper: "Lectura ejecutiva", number: "01", visible: true },
    { id: "finance", label: "Finanzas", helper: "Caja, cobros y payout", number: "02", visible: canViewFinanceUnified },
    { id: "inventory", label: "Inventario", helper: "Stock, margen y compras", number: "04", visible: canViewInventory },
    { id: "crm", label: "Clientes", helper: "CRM y retención", number: "05", visible: canViewCrm },
    { id: "memberships", label: "Membresías", helper: "Uso y renovaciones", number: "06", visible: canViewMembershipAnalytics },
    { id: "exports", label: "Exportar", helper: "CSV y reporte ejecutivo", number: "07", visible: canViewReports || canViewFinancials || canViewInventory || canViewCrm },
    { id: "traffic", label: "Tráfico", helper: "Perfil y conversión", number: "08", visible: canViewReports },
    { id: "insights", label: "Advisor", helper: "Acciones recomendadas", number: "09", visible: canViewReports },
  ].filter((item) => item.visible);

  // LOOP REPORTS V3 A4.8.1 — contextual report filters.
  const financePeriodPolicies = {
    profitability: { periods: ["week", "month", "custom"], defaultPeriod: "month", showWorker: true, context: "Rentabilidad · semana, mes o rango" },
    cash: { periods: ["day", "week", "month"], defaultPeriod: "day", showWorker: false, context: "Caja y cierre · negocio completo, día por defecto" },
    pnl: { periods: ["month", "custom", "all"], defaultPeriod: "month", showWorker: false, context: "Estado de resultados · negocio completo" },
    trends: { periods: ["week", "month", "custom", "all"], defaultPeriod: "month", showWorker: true, context: "Tendencias · evolución en el tiempo" },
    team: { periods: ["week", "month", "custom"], defaultPeriod: "month", showWorker: true, context: "Servicios y equipo · período + trabajador" },
    forecast: { periods: ["week", "month"], defaultPeriod: "month", showWorker: false, context: "Metas y proyección · semana o mes" },
  };
  const activeFinancePeriodPolicy = financePeriodPolicies[financeWorkspaceView] || financePeriodPolicies.profitability;
  const periodOptionMeta = {
    day: { label: "Hoy" },
    week: { label: "Semana" },
    month: { label: "Mes" },
    custom: { label: "Rango" },
    all: { label: "Historial" },
  };
  const reportFilterPolicy = activeView === "overview"
    ? { showPeriod: true, showWorker: true, periods: ["day", "week", "month", "custom", "all"], context: "Resumen del negocio · período + trabajador" }
    : activeView === "finance"
      ? { showPeriod: true, showWorker: activeFinancePeriodPolicy.showWorker, periods: activeFinancePeriodPolicy.periods, context: activeFinancePeriodPolicy.context }
      : activeView === "inventory"
        ? { showPeriod: false, showWorker: false, periods: [], context: "Stock actual · sin filtro global de fechas" }
        : activeView === "crm"
          ? { showPeriod: false, showWorker: false, periods: [], context: "Clientes · usa búsqueda, segmentos y seguimiento" }
          : activeView === "memberships"
            ? { showPeriod: false, showWorker: false, periods: [], context: "Actividad y calendario · últimos 30 días" }
            : { showPeriod: true, showWorker: false, periods: ["day", "week", "month", "custom", "all"], context: `${activeReportMeta.label} · filtro de período` };

  const handleFinanceWorkspaceChange = (nextView) => {
    const next = String(nextView || "profitability");
    const policy = financePeriodPolicies[next] || financePeriodPolicies.profitability;
    setFinanceWorkspaceView(next);
    setPeriod(policy.defaultPeriod);
    if (!policy.showWorker) setWorkerFilter("all");
  };

  const activateReportModule = (nextView) => {
    const next = String(nextView || "overview");
    setActiveView(next);
    if (next === "finance") {
      setPeriod(activeFinancePeriodPolicy.defaultPeriod);
      if (!activeFinancePeriodPolicy.showWorker) setWorkerFilter("all");
      return;
    }
    if (next !== "overview") setWorkerFilter("all");
  };

  useEffect(() => {
    if (activeView === "finance") {
      setPeriod(activeFinancePeriodPolicy.defaultPeriod);
      if (!activeFinancePeriodPolicy.showWorker) setWorkerFilter("all");
      return;
    }
    if (activeView !== "overview") setWorkerFilter("all");
  }, [activeView, financeWorkspaceView]);

  if (!canViewReports && !canViewFinancials && !canViewInventory && !canViewCrm) {
    return (
      <section className={`business-analytics analytics-minimal ${isOwnerOrAdmin ? "ba-owner-view" : "ba-worker-view"} ${activeView === "finance" ? "ba-finance-active-view" : ""} ba-worker-comp-${workerCompensationVisibility || "hidden"} ${className || ""}`}>
        <style>{styles}</style>
        <EmptyStateCard
          title={t("Sin acceso a Reportes")}
          body="Tu rol no tiene permisos para ver reportes, finanzas, CRM o inventario de este negocio. Pide al dueño o admin que ajuste tus permisos."
        />
      </section>
    );
  }

  return (
    <BusinessRegionContext.Provider value={business || {}}><section className={`business-analytics analytics-minimal ${isOwnerOrAdmin ? "ba-owner-view" : "ba-worker-view"} ${activeView === "finance" ? "ba-finance-active-view" : ""} ba-worker-comp-${workerCompensationVisibility || "hidden"} ${className || ""}`}>
      <style>{styles}</style>
      {membershipEnrollmentOpen && <ReportMembershipEnrollment customers={customers} businessId={businessId} onClose={() => { setMembershipEnrollmentOpen(false); setReloadKey((value) => value + 1); }} />}

      <style>{`
        /* LOOP REPORTS V3 A4.8.1 — clean module nav + contextual filters */
        .ba-report-filterbar-smart{padding:9px!important;border:1px solid #e5ecef!important;border-radius:16px!important;background:#fff!important;box-shadow:0 7px 22px rgba(15,23,42,.035)!important;margin-bottom:10px!important;}
        .ba-report-smart-top{display:grid;grid-template-columns:minmax(185px,230px) minmax(0,1fr);gap:9px;align-items:stretch;}
        .ba-report-filterbar-smart .ba-report-module-select{min-height:44px!important;padding:0!important;border:1px solid #dfe8eb!important;border-radius:12px!important;background:#fbfdfd!important;box-shadow:none!important;overflow:visible!important;}
        .ba-report-filterbar-smart .ba-report-module-select:before{display:none!important;}
        .ba-report-filterbar-smart .ba-module-trigger{width:100%;min-height:42px!important;display:flex!important;align-items:center!important;justify-content:space-between!important;gap:10px!important;padding:6px 10px!important;border:0!important;background:transparent!important;text-align:left!important;}
        .ba-report-filterbar-smart .ba-module-trigger>span{display:grid;gap:2px;min-width:0;}
        .ba-report-filterbar-smart .ba-module-trigger small{font-size:7.5px;font-weight:900;letter-spacing:.12em;text-transform:uppercase;color:#94a3b8;}
        .ba-report-filterbar-smart .ba-module-trigger strong{font-size:12px!important;color:#0f172a!important;line-height:1.15!important;}
        .ba-report-filterbar-smart .ba-module-trigger i{font-size:13px!important;color:#64748b!important;}
        .ba-report-filterbar-smart .ba-module-menu{min-width:250px!important;padding:6px!important;border:1px solid #dfe8eb!important;border-radius:13px!important;box-shadow:0 18px 42px rgba(15,23,42,.13)!important;}
        .ba-report-filterbar-smart .ba-module-menu button{display:grid!important;grid-template-columns:8px minmax(0,1fr)!important;gap:9px!important;align-items:center!important;min-height:44px!important;padding:6px 8px!important;border-radius:9px!important;}
        .ba-module-menu-copy{display:grid;gap:1px;text-align:left;min-width:0;}
        .ba-module-menu-copy strong{font-size:10.5px!important;color:#0f172a!important;}
        .ba-module-menu-copy small{font-size:8.5px;color:#94a3b8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .ba-report-smart-context{min-height:44px;display:flex;flex-direction:column;justify-content:center;gap:2px;padding:6px 11px;border:1px solid #edf1f3;border-radius:12px;background:#fcfdfd;}
        .ba-report-smart-context span{font-size:7.5px;font-weight:900;letter-spacing:.11em;text-transform:uppercase;color:#94a3b8;}
        .ba-report-smart-context strong{font-size:10.5px;color:#475569;font-weight:750;}
        .ba-report-smart-controls{display:grid;grid-template-columns:max-content minmax(210px,1fr) minmax(150px,210px);gap:8px;align-items:center;margin-top:8px;padding-top:8px;border-top:1px solid #eef2f4;}
        .ba-smart-periods{width:max-content!important;display:inline-flex!important;gap:3px!important;padding:3px!important;border:1px solid #e5eaed!important;border-radius:10px!important;background:#f7f9fa!important;}
        .ba-smart-periods button{min-width:62px!important;min-height:29px!important;padding:0 9px!important;border:0!important;border-radius:7px!important;background:transparent!important;color:#64748b!important;font-size:9.5px!important;font-weight:750!important;}
        .ba-smart-periods button.active{background:#fff!important;color:#00675f!important;box-shadow:0 1px 4px rgba(15,23,42,.08)!important;}
        .ba-smart-date-control{min-height:35px!important;border-radius:10px!important;border-color:#e5eaed!important;background:#fff!important;}
        .ba-smart-date-control strong{font-size:10.5px!important;color:#334155!important;}
        .ba-smart-worker-select{min-height:35px;display:grid;grid-template-columns:auto minmax(0,1fr) auto;gap:7px;align-items:center;padding:0 9px;border:1px solid #e5eaed;border-radius:10px;background:#fff;position:relative;}
        .ba-smart-worker-select>span{font-size:8px;font-weight:850;color:#94a3b8;text-transform:uppercase;letter-spacing:.08em;}
        .ba-smart-worker-select select{appearance:none;width:100%;border:0;outline:0;background:transparent;font-size:10px;font-weight:750;color:#334155;padding:0 15px 0 0;}
        .ba-smart-worker-select i{font-style:normal;font-size:11px;color:#94a3b8;}
        .ba-smart-custom-range{margin-top:8px!important;padding-top:8px!important;border-top:1px solid #eef2f4!important;}
        @media(max-width:900px){.ba-report-smart-top{grid-template-columns:1fr;}.ba-report-smart-controls{grid-template-columns:1fr;}.ba-smart-periods{width:100%!important;overflow-x:auto!important;}.ba-smart-periods button{flex:1 0 auto!important;}.ba-smart-worker-select{width:100%;}}
      `}</style>
      <style>{`
        /* LOOP REPORTS V3 A4.8.2 — simple Reports menu + compact Finance insights */
        .ba-report-filterbar-smart .ba-module-trigger>strong{display:block!important;min-width:0!important;color:#0f172a!important;font-size:12.5px!important;font-weight:900!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;}
        .ba-report-filterbar-smart .ba-module-menu{width:210px!important;min-width:210px!important;}
        .ba-report-filterbar-smart .ba-module-menu button{display:flex!important;grid-template-columns:none!important;min-height:35px!important;gap:9px!important;padding:0 9px!important;}
        .ba-report-filterbar-smart .ba-module-menu button>strong{display:block!important;min-width:0!important;color:#0f172a!important;font-size:11px!important;font-weight:850!important;line-height:1!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;text-transform:none!important;letter-spacing:0!important;}
        .ba-report-filterbar-smart .ba-module-menu-copy{display:none!important;}
        .ba-finance-v3-drivers-compact{padding:0!important;overflow:hidden!important;}
        .ba-finance-v3-insight-summary{list-style:none;min-height:58px;display:grid;grid-template-columns:minmax(0,1fr) auto;gap:14px;align-items:center;padding:10px 14px;cursor:pointer;}
        .ba-finance-v3-insight-summary::-webkit-details-marker{display:none;}
        .ba-finance-v3-insight-summary>div:first-child{min-width:0;display:grid;grid-template-columns:auto minmax(0,1fr);gap:9px;align-items:center;}
        .ba-finance-v3-insight-summary>div:first-child>span{color:#0f766e;font-size:8px;font-weight:900;text-transform:uppercase;letter-spacing:.09em;white-space:nowrap;}
        .ba-finance-v3-insight-summary strong{min-width:0;color:#334155;font-size:11.5px;font-weight:900;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .ba-finance-v3-insight-summary strong.is-up{color:#047857}.ba-finance-v3-insight-summary strong.is-down{color:#b45309}
        .ba-finance-v3-insight-summary>div:last-child{display:flex;align-items:center;gap:8px;white-space:nowrap;}
        .ba-finance-v3-insight-summary em{font-size:8px;font-style:normal;font-weight:800;color:#94a3b8;}
        .ba-finance-v3-insight-summary b{font-size:9px;color:#0f766e;font-weight:900;}
        .ba-finance-v3-drivers-compact[open] .ba-finance-v3-insight-summary{border-bottom:1px solid #eef2f4;background:#fbfdfd;}
        .ba-finance-v3-drivers-compact[open] .ba-finance-v3-insight-summary b{font-size:0;}
        .ba-finance-v3-drivers-compact[open] .ba-finance-v3-insight-summary b:after{content:"Ocultar";font-size:9px;}
        .ba-finance-v3-insight-expanded{padding:12px 14px 13px;}
        .ba-finance-v3-insight-detail{margin:0 0 10px;color:#64748b;font-size:9.5px;line-height:1.35;font-weight:720;}
        @media(max-width:760px){.ba-finance-v3-insight-summary{grid-template-columns:1fr;gap:5px}.ba-finance-v3-insight-summary>div:first-child{grid-template-columns:1fr;gap:3px}.ba-finance-v3-insight-summary strong{white-space:normal}.ba-finance-v3-insight-summary>div:last-child{justify-content:space-between}}
      `}</style>
      <style>{`
        /* LOOP REPORTS V3 A4.8.3R — Finance shell density + integrated selector */
        .ba-report-filterbar-smart{padding:7px!important;margin-bottom:8px!important;border-radius:14px!important;}
        .ba-report-smart-top{grid-template-columns:minmax(230px,300px) minmax(0,1fr)!important;gap:6px!important;}
        .ba-report-filterbar-smart .ba-report-module-select{min-height:40px!important;}
        .ba-report-filterbar-smart .ba-module-trigger{min-height:38px!important;padding:5px 10px!important;}
        .ba-report-smart-context{min-height:40px!important;padding:0 5px!important;border:0!important;background:transparent!important;}
        .ba-report-smart-context>span{display:none!important;}
        .ba-report-smart-context strong{font-size:9.5px!important;font-weight:720!important;color:#64748b!important;}
        .ba-report-smart-controls{margin-top:6px!important;padding-top:6px!important;gap:7px!important;}
        .ba-smart-periods button{min-height:27px!important;min-width:58px!important;}
        .ba-smart-date-control,.ba-smart-worker-select{min-height:32px!important;}
        .ba-finance-v3-head{display:grid!important;grid-template-columns:minmax(270px,.8fr) minmax(480px,1.35fr) minmax(210px,.55fr)!important;gap:12px!important;align-items:center!important;margin-bottom:10px!important;}
        .ba-finance-v3-head-workspace{min-width:0;width:100%;}
.ba-finance-v3-head.is-non-cash{grid-template-columns:minmax(270px,.8fr) minmax(520px,1.7fr)!important;}
.ba-finance-v3-collections-legacy{display:none!important;}
        .ba-finance-v3-head>div:first-child p{margin-top:3px!important;max-width:430px!important;}
        .ba-finance-v3-health{min-width:0!important;padding:8px 10px!important;border-radius:11px!important;}
        .ba-finance-v3-health>strong{font-size:14px!important;}
        .ba-finance-v3-strip>div{padding:11px 15px!important;}
        .ba-finance-v3-insight-summary{min-height:50px!important;padding:7px 12px!important;}
        .ba-finance-dev-diagnostics>summary::-webkit-details-marker{display:none;}
        .ba-finance-dev-diagnostics>summary::marker{content:"";}
        @media(max-width:1250px){.ba-finance-v3-head{grid-template-columns:1fr!important;align-items:stretch!important}.ba-finance-v3-head-workspace{order:2}.ba-finance-v3-health{order:3;text-align:left!important}}
        @media(max-width:900px){.ba-report-smart-top{grid-template-columns:1fr!important}.ba-report-smart-context{min-height:26px!important;padding:0 3px!important}}
      `}</style>
      <style>{`
        /* LOOP REPORTS V3 A4.8.5 — compact inline toolbar + post-KPI finance selector */
        .ba-report-filterbar-smart{
          display:grid!important;
          grid-template-columns:minmax(210px,250px) max-content minmax(280px,1fr) minmax(165px,205px)!important;
          gap:7px!important;
          align-items:center!important;
          padding:7px 9px!important;
          margin-bottom:8px!important;
        }
        .ba-report-smart-top{display:contents!important;}
        .ba-report-smart-context{display:none!important;}
        .ba-report-smart-controls{display:contents!important;margin:0!important;padding:0!important;border:0!important;}
        .ba-report-filterbar-smart .ba-report-module-select{width:100%!important;min-width:0!important;max-width:none!important;min-height:34px!important;}
        .ba-report-filterbar-smart .ba-module-trigger{width:100%!important;min-height:34px!important;padding:4px 9px!important;gap:7px!important;}
        .ba-report-filterbar-smart .ba-module-trigger>strong{display:block!important;width:auto!important;max-width:none!important;min-width:0!important;flex:1 1 auto!important;overflow:visible!important;text-overflow:clip!important;white-space:nowrap!important;text-align:left!important;font-size:11.5px!important;}
        .ba-report-filterbar-smart .ba-module-trigger i{flex:0 0 auto!important;}
        .ba-smart-periods{width:auto!important;min-width:0!important;padding:2px!important;gap:2px!important;border-radius:9px!important;}
        .ba-smart-periods button{min-width:48px!important;min-height:29px!important;padding:0 8px!important;font-size:9px!important;}
        .ba-smart-date-control{min-height:34px!important;border-radius:9px!important;}
        .ba-smart-worker-select{display:none!important;}
        .ba-smart-worker-menu{position:relative;min-width:0;}
        .ba-smart-worker-menu>summary{list-style:none;min-height:34px;display:grid;grid-template-columns:auto minmax(0,1fr) auto;gap:7px;align-items:center;padding:0 9px;border:1px solid #e5eaed;border-radius:9px;background:#fff;cursor:pointer;}
        .ba-smart-worker-menu>summary::-webkit-details-marker{display:none;}
        .ba-smart-worker-menu>summary::marker{content:"";}
        .ba-smart-worker-menu>summary span{font-size:7.5px;font-weight:900;color:#94a3b8;text-transform:uppercase;letter-spacing:.08em;}
        .ba-smart-worker-menu>summary strong{min-width:0;font-size:10px;font-weight:850;color:#334155;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .ba-smart-worker-menu>summary i{font-style:normal;font-size:11px;color:#94a3b8;}
        .ba-smart-worker-menu-list{position:absolute;right:0;top:calc(100% + 5px);z-index:220;width:max(190px,100%);max-height:280px;overflow:auto;padding:5px;border:1px solid #dfe8eb;border-radius:11px;background:#fff;box-shadow:0 16px 38px rgba(15,23,42,.14);}
        .ba-smart-worker-menu-list button{width:100%;min-height:32px;border:0;border-radius:7px;background:transparent;padding:0 9px;text-align:left;font-size:10px;font-weight:780;color:#334155;cursor:pointer;}
        .ba-smart-worker-menu-list button:hover{background:#f8fafc;}
        .ba-smart-worker-menu-list button.active{background:#ecfdf8;color:#0f766e;}
        .ba-smart-custom-range{grid-column:1/-1!important;margin-top:0!important;}
        .ba-finance-v3-head{grid-template-columns:minmax(0,1fr) minmax(210px,280px)!important;gap:12px!important;}
        .ba-finance-v3-head.is-non-cash{grid-template-columns:1fr!important;}
        .ba-finance-v3-head-workspace{display:none!important;}
        .ba-finance-v3-post-kpi-workspace{margin:10px 0 8px;min-width:0;}
        .ba-finance-v3-post-kpi-workspace .ba-finance-view-picker-main{grid-template-columns:minmax(280px,360px) minmax(0,1fr)!important;gap:12px!important;}
        @media(max-width:1180px){
          .ba-report-filterbar-smart{grid-template-columns:minmax(190px,230px) max-content minmax(240px,1fr)!important;}
          .ba-smart-worker-menu{grid-column:1/-1;width:min(220px,100%);}
        }
        @media(max-width:820px){
          .ba-report-filterbar-smart{grid-template-columns:1fr!important;}
          .ba-report-smart-top,.ba-report-smart-controls{display:contents!important;}
          .ba-smart-periods{width:100%!important;overflow-x:auto!important;}
          .ba-smart-date-control,.ba-smart-worker-menu{width:100%!important;grid-column:auto!important;}
          .ba-finance-v3-post-kpi-workspace .ba-finance-view-picker-main{grid-template-columns:1fr!important;}
        }
      `}</style>
      <style>{`
        /* LOOP REPORTS V3 A4.8.6 — executive Finance header */
        .ba-finance-v3-head,
        .ba-finance-v3-head.is-cash,
        .ba-finance-v3-head.is-non-cash{
          display:grid!important;
          grid-template-columns:minmax(265px,.72fr) minmax(620px,1.8fr)!important;
          gap:14px!important;
          align-items:stretch!important;
          margin-bottom:6px!important;
        }
        .ba-finance-v3-head>div:first-child{
          min-width:0;
          display:flex!important;
          flex-direction:column;
          justify-content:center;
          padding:4px 0!important;
        }
        .ba-finance-v3-head>div:first-child p{
          max-width:390px!important;
          margin:4px 0 0!important;
          font-size:9.5px!important;
          line-height:1.35!important;
        }
        .ba-finance-exec-status{
          width:max-content;
          max-width:100%;
          margin-top:6px;
          padding:3px 7px;
          border:1px solid #dbe9e6;
          border-radius:999px;
          background:#f8fcfb;
          color:#64748b;
          font-size:7.5px;
          font-weight:850;
          white-space:nowrap;
        }
        .ba-finance-exec-kpis{
          min-width:0;
          display:grid;
          grid-template-columns:repeat(4,minmax(0,1fr));
          align-self:stretch;
          overflow:hidden;
          border:1px solid #dfe8ec;
          border-radius:13px;
          background:#fff;
          box-shadow:0 5px 18px rgba(15,23,42,.025);
        }
        .ba-finance-exec-kpis>div{
          min-width:0;
          display:grid;
          align-content:center;
          gap:2px;
          padding:9px 12px;
          border-left:1px solid #edf1f3;
        }
        .ba-finance-exec-kpis>div:first-child{border-left:0;}
        .ba-finance-exec-kpis span{
          color:#64748b;
          font-size:7.5px;
          font-weight:900;
          letter-spacing:.025em;
        }
        .ba-finance-exec-kpis strong{
          color:#0f172a;
          font-size:15px;
          line-height:1.05;
          font-weight:950;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
        }
        .ba-finance-exec-kpis em{
          color:#94a3b8;
          font-size:7.5px;
          line-height:1.25;
          font-style:normal;
          font-weight:700;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
        }
        .ba-finance-exec-kpis .warning strong{color:#b45309;}
        .ba-finance-exec-kpis .result strong{color:#047857;}
        .ba-finance-exec-kpis .result.negative strong{color:#b91c1c!important;}
        .ba-finance-v3-strip{display:none!important;}
        .ba-finance-v3-health{display:none!important;}
        .ba-finance-v3-post-kpi-workspace{margin:6px 0 7px!important;}
        .ba-finance-v3-post-kpi-workspace .ba-finance-view-picker-main{
          grid-template-columns:minmax(280px,350px) minmax(0,1fr)!important;
          gap:10px!important;
        }
        .ba-finance-v3-drivers-compact{margin-top:0!important;}
        @media(max-width:1180px){
          .ba-finance-v3-head,.ba-finance-v3-head.is-cash,.ba-finance-v3-head.is-non-cash{grid-template-columns:1fr!important;}
          .ba-finance-v3-head>div:first-child p{max-width:none!important;}
        }
        @media(max-width:720px){
          .ba-finance-exec-kpis{grid-template-columns:repeat(2,minmax(0,1fr));}
          .ba-finance-exec-kpis>div:nth-child(3){border-left:0;border-top:1px solid #edf1f3;}
          .ba-finance-exec-kpis>div:nth-child(4){border-top:1px solid #edf1f3;}
          .ba-finance-v3-post-kpi-workspace .ba-finance-view-picker-main{grid-template-columns:1fr!important;}
        }
      `}</style>
      <style>{`
        /* LOOP REPORTS V3 A4.8.7 — dropdown affordance */
        .ba-report-filterbar-smart .ba-module-trigger.ba-module-trigger-minimal{
          position:relative!important;
          overflow:hidden!important;
          min-height:36px!important;
          padding:0 43px 0 12px!important;
          border:1px solid #dbe4e8!important;
          border-radius:10px!important;
          background:#ffffff!important;
          box-shadow:none!important;
          transition:border-color .16s ease,box-shadow .16s ease,background .16s ease!important;
        }
        .ba-report-filterbar-smart .ba-module-trigger.ba-module-trigger-minimal:hover{
          border-color:rgba(15,118,110,.40)!important;
          background:#fcfffe!important;
          box-shadow:0 4px 14px rgba(15,118,110,.06)!important;
        }
        .ba-report-filterbar-smart .ba-module-trigger.ba-module-trigger-minimal i{
          position:absolute!important;
          inset:0 0 0 auto!important;
          transform:none!important;
          width:38px!important;
          height:100%!important;
          display:grid!important;
          place-items:center!important;
          border:0!important;
          border-left:1px solid #e5eaed!important;
          border-radius:0 9px 9px 0!important;
          background:#f7faf9!important;
          color:#64748b!important;
          font-size:14px!important;
          line-height:1!important;
          transition:transform .16s ease,background .16s ease,color .16s ease!important;
        }
        .ba-report-filterbar-smart .ba-module-trigger.ba-module-trigger-minimal:hover i{
          background:#eef9f6!important;
          color:#0f766e!important;
        }
        .ba-report-filterbar-smart .ba-module-trigger.ba-module-trigger-minimal[aria-expanded="true"]{
          border-color:rgba(15,118,110,.52)!important;
          background:#ffffff!important;
          box-shadow:0 6px 18px rgba(15,118,110,.08)!important;
        }
        .ba-report-filterbar-smart .ba-module-trigger.ba-module-trigger-minimal[aria-expanded="true"] i{
          background:#e9f7f3!important;
          color:#0f766e!important;
          transform:rotate(180deg)!important;
        }
        .ba-report-filterbar-smart .ba-module-trigger.ba-module-trigger-minimal:focus-visible{
          outline:2px solid rgba(15,118,110,.20)!important;
          outline-offset:2px!important;
        }
      `}</style>
      <style>{`
        /* LOOP REPORTS V3 A4.8.8 — finance correctness + contextual shell */
      `}</style>
      <style>{`
        /* LOOP REPORTS V3 A4.8.9 — final workspace polish */
        .ba-finance-v3-trend .ba-finance-v3-chart svg{display:block;width:100%;height:260px!important;max-height:260px!important;}
        .ba-finance-v3-trend .ba-finance-v3-chart-legend{align-items:center!important;gap:12px!important;}
        .ba-finance-v3-trend .ba-finance-v3-chart-legend .scale{margin-left:auto;color:#94a3b8;font-size:9px;font-weight:750;}
        .ba-finance-v3-trend .ba-finance-v3-card-head{margin-bottom:8px!important;}
        @media(max-width:720px){
          .ba-finance-v3-trend .ba-finance-v3-chart svg{height:220px!important;max-height:220px!important;}
          .ba-finance-v3-trend .ba-finance-v3-chart-legend .scale{width:100%;margin-left:0;}
        }
      `}</style>
      <header
        className={`ba-report-filterbar ba-report-filterbar-smart ba-report-compact ${reportFiltersOpen ? "filters-expanded" : ""}`}
        aria-label={t("Navegación y filtros contextuales de reportes")}
        title={`Módulo: ${activeReportMeta.label} · ${reportFilterPolicy.context}`}
      >
        {!embeddedNavigation ? <div className="ba-report-smart-top">
          <div
            className={`ba-report-module-select ba-report-module-switcher ${moduleMenuOpen ? "open" : ""}`}
            aria-label={t("Módulo actual")}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) setModuleMenuOpen(false);
            }}
          >
            <button
              type="button"
              className="ba-module-trigger ba-module-trigger-minimal"
              aria-haspopup="listbox"
              aria-expanded={moduleMenuOpen}
              onClick={() => setModuleMenuOpen((open) => !open)}
            >
              <strong>{activeReportMeta.label}</strong>
              <i aria-hidden="true">⌄</i>
            </button>
            {moduleMenuOpen ? (
              <div className="ba-module-menu ba-module-menu-minimal" role="listbox" aria-label={t("Cambiar módulo de reportes")}>
                {reportModuleOptions.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    role="option"
                    aria-selected={String(activeView) === String(item.id)}
                    className={String(activeView) === String(item.id) ? "active" : ""}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      activateReportModule(item.id);
                      setModuleMenuOpen(false);
                    }}
                  >
                    <span className="ba-module-menu-dot" aria-hidden="true" />
                    <strong>{item.label}</strong>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="ba-report-smart-context" aria-label={t("Contexto de la vista")}>
            <span>{t("Vista actual")}</span>
            <strong>{reportFilterPolicy.context}</strong>
          </div>
        </div> : null}
        {reportFilterPolicy.showPeriod || reportFilterPolicy.showWorker ? <div className="ba-filter-summary">
          <div className="ba-report-heading"><h2>{activeReportMeta.label}</h2><span>{data.periodLabel}{reportFilterPolicy.showWorker && workerFilter !== "all" ? ` · ${selectedWorkerLabel}` : ""}</span></div>
          <button type="button" aria-expanded={reportFiltersOpen} aria-controls="report-filter-options" onClick={() => setReportFiltersOpen(open => !open)}>{reportFiltersOpen ? t("Cerrar") : "Filtros"}{workerFilter !== "all" && reportFilterPolicy.showWorker ? " · 1" : ""}</button>
        </div> : null}
        <div id="report-filter-options" className="ba-filter-options">
        {reportFilterPolicy.showPeriod || reportFilterPolicy.showWorker ? (
          <div className="ba-report-smart-controls">
            {reportFilterPolicy.showPeriod ? (
              <div className="ba-segmented ba-smart-periods" aria-label={t("Periodo disponible para esta vista")}>
                {reportFilterPolicy.periods.map((periodKey) => (
                  <button
                    type="button"
                    key={periodKey}
                    className={period === periodKey ? "active" : ""}
                    onClick={() => {
                      if (periodKey === "day") setAnchorDate(new Date());
                      setPeriod(periodKey);
                    }}
                  >
                    {activeView === "finance" && periodKey === "day" ? t("Día") : (periodOptionMeta[periodKey]?.label || periodKey)}
                  </button>
                ))}
              </div>
            ) : null}

            {reportFilterPolicy.showPeriod ? (
              <div className="ba-date-control ba-date-control-compact ba-date-control-premium ba-smart-date-control">
                {period !== "all" && period !== "custom" ? <button type="button" onClick={() => movePeriod(-1)} aria-label={t("Periodo anterior")}>‹</button> : <span />}
                <strong>{data.periodLabel}</strong>
                {period !== "all" && period !== "custom" ? <button type="button" onClick={() => movePeriod(1)} aria-label={t("Periodo siguiente")}>›</button> : <span />}
              </div>
            ) : null}

            {reportFilterPolicy.showWorker ? (
              <details className="ba-smart-worker-menu">
                <summary aria-label={`Trabajador: ${selectedWorkerLabel}`}>
                  <span>{t("Trabajador")}</span>
                  <strong>{workerFilter === "all" ? t("Todos") : selectedWorkerLabel}</strong>
                  <i aria-hidden="true">⌄</i>
                </summary>
                <div className="ba-smart-worker-menu-list" role="listbox" aria-label={t("Filtrar por trabajador")}>
                  <button
                    type="button"
                    className={workerFilter === "all" ? "active" : ""}
                    onClick={(event) => {
                      setWorkerFilter("all");
                      event.currentTarget.closest("details")?.removeAttribute("open");
                    }}
                  >{t("Todos")}</button>
                  {workerOptions.map((w) => (
                    <button
                      type="button"
                      key={w.id}
                      className={String(workerFilter) === String(w.id) ? "active" : ""}
                      onClick={(event) => {
                        setWorkerFilter(w.id);
                        event.currentTarget.closest("details")?.removeAttribute("open");
                      }}
                    >
                      {w.name}
                    </button>
                  ))}
                </div>
              </details>
            ) : null}
          </div>
        ) : null}

        {reportFilterPolicy.showPeriod && period === "custom" ? (
          <div className="ba-custom-range ba-custom-range-inline ba-custom-range-premium ba-smart-custom-range" aria-label={t("Rango personalizado")}>
            <label>{t("Desde")}<input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} /></label>
            <label>{t("Hasta")}<input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} /></label>
          </div>
        ) : null}
        </div>
      </header>

      <div className="ba-command-workspace">
        <main className="ba-command-main">
          {error ? (
        <div className="ba-alert error ba-alert-action">
          <span>{error}</span>
          <button type="button" onClick={() => setReloadKey((x) => x + 1)}>{t("Reintentar")}</button>
        </div>
      ) : null}
      {/* Loading skeleton removed to avoid the four-card flash when opening Reportes. */}
      {!loading && !error && dataQuality.length ? (
        <section className="ba-quality-strip" aria-label={t("Estado del panel")}>
          {dataQuality.map((item) => (
            <article key={`${item.title}-${item.body}`} className={`ba-quality-card ${moduleHealthTone(item.tone)}`}>
              <strong>{item.title}</strong>
              <span>{item.body}</span>
            </article>
          ))}
        </section>
      ) : null}

      {!loading && !error ? (
        <section className="ba-phase2-strip" aria-label={t("Configuración fase 2A")}>
          <article>
            <span>{t("Inventario")}</span>
            <strong>{inventoryEnabled ? t("Activo") : "Inactivo"}</strong>
            <em>{inventoryPlan === "early_access" ? "Early Access gratis" : inventoryPlan}</em>
          </article>
          <article>
            <span>{t("Auto-descuento")}</span>
            <strong>{inventoryAutoDeduct ? "Encendido" : "Apagado"}</strong>
            <em>{inventoryDeductTrigger === "paid" ? "al cobrar" : "al completar"}</em>
          </article>
          <article>
            <span>{t("Comisiones trabajador")}</span>
            <strong>{workerCompensationVisibility === "hidden" ? "Privadas" : "Visibles"}</strong>
            <em>{workerCompensationVisibility === "full_breakdown" ? "desglose completo" : workerCompensationVisibility === "payout_total" ? "total payout" : workerCompensationVisibility === "tips_only" ? "solo propinas" : "solo dueño/admin"}</em>
          </article>
          <article>
            <span>{t("Caja por método")}</span>
            <strong>{financeSettings.showPaymentMethods === false ? t("Oculto") : t("Visible")}</strong>
            <em>{t("Efectivo · Yappy · Tarjeta · Transferencia")}</em>
          </article>
        </section>
      ) : null}

      {activeView === "overview" ? (
        <section className="ba-overview-v3" aria-label={t("Resumen ejecutivo del negocio")}>
          <header className="ba-overview-v3-head">
            <div>
              <span>{t("Resumen ejecutivo")}</span>
              <h3>{t("Tu negocio, de un vistazo")}</h3>
              <p>{data.periodLabel}{" " + t("· ventas, caja, clientes y operación en una sola lectura.")}</p>
            </div>
            <div className={`ba-overview-v3-health ${
              data.pendingRevenue > 0
                ? "warning"
                : Number(data.trackedRevenueBase || 0) > 0
                  ? "success"
                  : (data.operationalBookings || []).length > 0
                    ? "info"
                    : "neutral"
            }`}>
              <span>{
                data.pendingRevenue > 0
                  ? "Requiere seguimiento"
                  : Number(data.trackedRevenueBase || 0) > 0
                    ? "Cobro al día"
                    : (data.operationalBookings || []).length > 0
                      ? "Agenda activa"
                      : "Sin movimiento"
              }</span>
              <strong>{
                data.pendingRevenue > 0
                  ? MONEY.format(data.pendingRevenue)
                  : Number(data.trackedRevenueBase || 0) > 0
                    ? `${data.collectionRate}% cobrado`
                    : (data.operationalBookings || []).length > 0
                      ? `${data.operationalBookings.length} reserva${data.operationalBookings.length === 1 ? "" : "s"}`
                      : "Sin reservas"
              }</strong>
              <em>{
                data.pendingRevenue > 0
                  ? "pendiente por cobrar"
                  : Number(data.trackedRevenueBase || 0) > 0
                    ? "de ventas con trazabilidad"
                    : (data.allInPeriod || []).length > 0
                      ? `${data.pendingBookings.length} pendiente${data.pendingBookings.length === 1 ? "" : "s"} · sin ventas cobrables aún`
                      : "No hay actividad en este período"
              }</em>
            </div>
          </header>

          <section className="ba-overview-v3-summary" aria-label={t("Indicadores ejecutivos")}>
            <article>
              <header><span>{t("Ventas y caja")}</span><strong>{MONEY.format(data.revenue)}</strong></header>
              <div><span>{t("Ventas generadas")}</span><strong>{MONEY.format(data.revenue)}</strong><em>{data.revenueBookings.length}{" " + t("reserva")}{data.revenueBookings.length === 1 ? "" : "s"}{" " + t("con ingreso")}</em></div>
              <div><span>{t("Cobrado de ventas")}</span><strong>{MONEY.format(data.collectedRevenue)}</strong><em>{data.trackedRevenueBase > 0 ? `${data.collectionRate}% de ventas con trazabilidad · propinas ${MONEY.format(data.totalTips)}` : `Sin base rastreada · propinas ${MONEY.format(data.totalTips)}`}</em></div>
              <div className={data.pendingRevenue > 0 ? "warning" : ""}><span>{t("Por cobrar confirmado")}</span><strong>{MONEY.format(data.pendingRevenue)}</strong><em>{data.unpaidBookings.length}{" " + t("reserva")}{data.unpaidBookings.length === 1 ? "" : "s"}{" " + t("con pago pendiente o parcial")}</em></div>
            </article>

            <article>
              <header><span>{t("Operación")}</span><strong>{data.operationalBookings.length}{" " + t("reserva")}{data.operationalBookings.length === 1 ? "" : "s"}</strong></header>
              <div><span>{t("Ticket promedio")}</span><strong>{MONEY.format(data.ticketAvg || 0)}</strong><em>{t("Sobre") + " "}{data.revenueBookings.length}{" " + t("reserva")}{data.revenueBookings.length === 1 ? "" : "s"}{" " + t("con ingreso")}</em></div>
              <div><span>{t("Clientes")}</span><strong>{data.uniqueCustomers || 0}</strong><em>{data.newCustomers || 0}{" " + t("nuevos ·") + " "}{data.recurrentCustomers || 0}{" " + t("recurrentes")}</em></div>
              <div><span>{t("Estado")}</span><strong>{data.pendingBookings.length}{" " + t("pendiente")}{data.pendingBookings.length === 1 ? "" : "s"}</strong><em>{data.completedBookings.length}{" " + t("atendida")}{data.completedBookings.length === 1 ? "" : "s"} · {data.cancelledBookings.length}{" " + t("cancelada")}{data.cancelledBookings.length === 1 ? "" : "s"} · {data.noShowBookings.length}{" " + t("no-show")}</em></div>
            </article>

            <article>
              <header><span>{t("Resultado operativo")}</span><strong>{MONEY.format(data.businessNetRevenue)}</strong></header>
              <div><span>{t("Payout calculado")}</span><strong>{MONEY.format(data.workerPayoutTotal)}</strong><em>{MONEY.format(data.workerPayoutPendingTotal)}{" " + t("pendiente de liquidar")}</em></div>
              <div><span>{t("Costos directos")}</span><strong>{MONEY.format((data.materialCostTotal || 0) + (data.productSalesCostTotal || 0))}</strong><em>{t("Materiales + costo de productos")}</em></div>
              <div><span>{t("Resultado operativo estimado")}</span><strong>{MONEY.format(data.businessNetRevenue)}</strong><em>{t("Cobro de ventas + propinas − payout − costos directos; sin gastos fijos")}</em></div>
            </article>
          </section>

          {(data.untrackedExpectedRevenue > 0 || data.coveredRevenueTotal > 0 || data.paymentMismatchRevenue > 0) ? (
            <div className="ba-overview-v3-finance-trace">
              {data.untrackedExpectedRevenue > 0 ? (
                <span><strong>{t("Histórico sin trazabilidad:")}</strong> {data.untrackedRevenueBookings.length}{" " + t("reserva")}{data.untrackedRevenueBookings.length === 1 ? "" : "s"} · {MONEY.format(data.untrackedExpectedRevenue)}{" " + t("de valor esperado. No se incluye en “por cobrar”.")}</span>
              ) : null}
              {data.coveredRevenueTotal > 0 ? (
                <span><strong>{t("Cubierto por beneficios:")}</strong> {MONEY.format(data.coveredRevenueTotal)}{" " + t("· membresías, fidelidad o cortesías; no se trata como caja ni deuda.")}</span>
              ) : null}
              {data.paymentMismatchRevenue > 0 ? (
                <span><strong>{t("Conciliación pendiente:")}</strong> {data.paymentMismatchBookings.length}{" " + t("reserva")}{data.paymentMismatchBookings.length === 1 ? "" : "s"} · {MONEY.format(data.paymentMismatchRevenue)}{" " + t("de diferencia en pagos marcados como pagados. No se suma a “por cobrar”.")}</span>
              ) : null}
            </div>
          ) : null}

          <section className="ba-overview-v3-main-grid">
            <article className={`ba-panel ba-overview-v3-trend ${maxOverviewTrendRevenue <= 0 ? "is-empty" : ""}`}>
              <div className="ba-overview-v3-panel-head">
                <div>
                  <span>{t("Tendencia")}</span>
                  <h4>{t("Ingresos del período")}</h4>
                  <p>{MONEY.format(data.revenue)}{" " + t("generados ·") + " "}{MONEY.format(data.collectedRevenue)}{" " + t("cobrados de ventas ·") + " "}{MONEY.format(data.totalTips)}{" " + t("en propinas")}</p>
                </div>
                <button type="button" onClick={() => setActiveView("finance")}>{t("Abrir finanzas")}</button>
              </div>
              {maxOverviewTrendRevenue > 0 ? (
                <OverviewRevenueLine data={overviewTrend.rows} maxRevenue={maxOverviewTrendRevenue} label={overviewTrend.label} bestLabel={overviewTrend.bestLabel} />
              ) : (
                <div className="ba-overview-v3-empty-trend">
                  <div aria-hidden="true">↗</div>
                  <span>
                    <strong>{t("Todavía no hay ingresos registrados en este período.")}</strong>
                    <em>{
                      data.pendingBookings.length > 0
                        ? `Hay ${data.pendingBookings.length} reserva${data.pendingBookings.length === 1 ? "" : "s"} pendiente${data.pendingBookings.length === 1 ? "" : "s"} en agenda.`
                        : (data.operationalBookings || []).length > 0
                          ? `Hay ${data.operationalBookings.length} reserva${data.operationalBookings.length === 1 ? "" : "s"} registrada${data.operationalBookings.length === 1 ? "" : "s"}, todavía sin ingreso.`
                          : "Cuando registres reservas e ingresos, aquí aparecerá la tendencia."
                    }</em>
                  </span>
                </div>
              )}
            </article>

            <article className="ba-panel ba-overview-v3-priorities">
              <div className="ba-overview-v3-panel-head">
                <div>
                  <span>{t("Prioridades")}</span>
                  <h4>{t("Qué requiere tu atención")}</h4>
                  <p>{t("Las señales más importantes detectadas por Loop.")}</p>
                </div>
              </div>
              <div className="ba-overview-v3-priority-list">
                {overviewPriorityRows.length ? overviewPriorityRows.slice(0, 4).map((item, idx) => (
                  <button
                    type="button"
                    key={`${item.title}-${idx}`}
                    className={item.tone || "neutral"}
                    onClick={() => setActiveView(normalizeBusinessAnalyticsView(item.targetView || "overview"))}
                  >
                    <b>{idx + 1}</b>
                    <span><strong>{item.title}</strong><em>{item.body}</em></span>
                    <i>{item.metric}</i>
                  </button>
                )) : (
                  <div className="ba-empty">{t("No hay alertas importantes para este período.")}</div>
                )}
              </div>
            </article>
          </section>

          <section className="ba-overview-v3-performance">
            <article className="ba-panel">
              <div className="ba-overview-v3-panel-head compact">
                <div><span>{t("Rendimiento")}</span><h4>{t("Lo que está moviendo el negocio")}</h4></div>
              </div>
              <div className="ba-overview-v3-performance-grid">
                <div>
                  <span>{t("Servicio con mayor ingreso")}</span>
                  <strong>{data.topMoneyService?.key || "Sin datos"}</strong>
                  <em>{data.topMoneyService ? MONEY.format(data.topMoneyService.value) : "—"}</em>
                </div>
                <div>
                  <span>{t("Servicio más reservado")}</span>
                  <strong>{data.topService?.key || "Sin datos"}</strong>
                  <em>{data.topService ? `${data.topService.value} reserva${Number(data.topService.value) === 1 ? "" : "s"}` : "—"}</em>
                </div>
                <div>
                  <span>{t("Equipo")}</span>
                  <strong>{data.topWorker?.key || "Sin datos"}</strong>
                  <em>{data.topWorker ? MONEY.format(data.topWorker.value) : "Mayor ingreso por trabajador"}</em>
                </div>
                <div>
                  <span>{t("Clientes recurrentes")}</span>
                  <strong>{data.recurrentCustomers || 0}</strong>
                  <em>{" " + t("de") + " "}{data.uniqueCustomers || 0}{" " + t("clientes en el período")}</em>
                </div>
              </div>
            </article>
          </section>

          <section className="ba-overview-v3-bottom-grid">
            <article className="ba-panel">
              <div className="ba-overview-v3-panel-head compact"><div><span>{t("Reservas")}</span><h4>{t("Estado operativo")}</h4></div></div>
              <div className="ba-overview-v3-status-list">
                <StatusLine label={t("Atendidas / confirmadas")} value={data.completedBookings.length} total={data.operationalBookings.length} tone="ok" />
                <StatusLine label={t("Pendientes")} value={data.pendingBookings.length} total={data.operationalBookings.length} tone="warn" />
                <StatusLine label={t("Canceladas")} value={data.cancelledBookings.length} total={data.operationalBookings.length} tone="bad" />
                <StatusLine label={t("No-show")} value={data.noShowBookings.length} total={data.operationalBookings.length} tone="muted" />
                {data.otherStatusBookings.length > 0 ? <StatusLine label={t("Otros estados")} value={data.otherStatusBookings.length} total={data.operationalBookings.length} tone="muted" /> : null}
              </div>
              {data.deletedBookings.length > 0 ? (
                <div className="ba-overview-v3-reconciliation-note">
                  {data.deletedBookings.length}{" " + t("reserva")}{data.deletedBookings.length === 1 ? "" : "s"}{" " + t("eliminada")}{data.deletedBookings.length === 1 ? "" : "s"}{" " + t("se excluye")}{data.deletedBookings.length === 1 ? "" : "n"}{t("del total operativo.")}</div>
              ) : null}
            </article>

            <article className="ba-panel">
              <div className="ba-overview-v3-panel-head compact"><div><span>{t("Servicios")}</span><h4>{t("Mayor demanda")}</h4></div></div>
              <SimpleList items={sortedServiceCount.slice(0, 5)} max={maxServiceCount} empty="Sin reservas en este filtro." />
            </article>
          </section>

          <footer className="ba-overview-v3-footnote">
            <span>{t("Resultado operativo estimado = cobro de ventas + propinas − payout − materiales − costo de productos.")}</span>
            <strong>{t("“Por cobrar” incluye solo pagos pendientes/parciales con trazabilidad. Los gastos fijos todavía no están registrados.")}</strong>
          </footer>
        </section>
      ) : null}


      {activeView === "crm" && canViewCrm ? (
        <section className="ba-crm-module">
          <div className="ba-finance-hero crm">
            <div>
              <span>{t("CRM avanzado")}</span>
              <h3>{t("Clientes, segmentos y señales de retención")}</h3>
              <p>{t("Identifica clientes VIP, dormidos, en riesgo, cumpleaños próximos y oportunidades de recompra.")}</p>
            </div>
            <strong>{crmData.rows.length}</strong>
          </div>

          <section className="ba-executive-grid crm" aria-label={t("Resumen CRM")}>
            <MetricCard label={t("Clientes")} value={crmData.rows.length} hint="Base consolidada" tone="primary" />
            <MetricCard label="VIP" value={crmData.vipRows.length} hint="Alto valor / frecuencia" tone="success" />
            <MetricCard label={t("Dormidos o riesgo")} value={crmData.dormantRows.length} hint="Reactivar por WhatsApp" tone={crmData.dormantRows.length ? "warning" : "success"} />
            <MetricCard label={t("Cumpleaños pronto")} value={crmData.birthdayRows.length} hint="Próximos 30 días" />
            <MetricCard label={t("Gasto total")} value={MONEY.format(crmData.totalSpent)} hint="Historial cliente" />
            <MetricCard label={t("Ticket promedio")} value={MONEY.format(crmData.avgTicket)} hint="Promedio por cliente" />
            <MetricCard label={t("Revenue CRM")} value={MONEY.format(crmData.crmRecoveredRevenue || 0)} hint={`${crmData.crmRecoveredBookings || 0} reservas atribuidas`} tone={(crmData.crmRecoveredRevenue || 0) > 0 ? "success" : "neutral"} />
          </section>

          <section className="ba-crm-exec-dashboard" aria-label={t("Dashboard ejecutivo CRM")}>
            <article className="ba-crm-exec-priority">
              <div>
                <span>{t("Prioridad comercial")}</span>
                <h3>{crmExecutiveDashboard.headline}</h3>
                <p>{crmExecutiveDashboard.subheadline}</p>
              </div>
              <strong>{MONEY.format(crmExecutiveDashboard.potentialRevenue || 0)}</strong>
              <em>{t("Potencial semanal")}</em>
              <button type="button" onClick={() => { setCrmFollowUpFilter("priority"); setCrmSegmentFilter("all"); setCrmSearch(""); }}>{t("Ver prioridades")}</button>
            </article>

            <div className="ba-crm-exec-kpis">
              <article><span>{t("Clientes activos")}</span><strong>{crmExecutiveDashboard.activeRows.length}</strong><em>{t("Con visita reciente o próxima")}</em></article>
              <article className={crmExecutiveDashboard.riskRows.length ? "warning" : "success"}><span>{t("Clientes en riesgo")}</span><strong>{crmExecutiveDashboard.riskRows.length}</strong><em>{t("Dormidos o 30+ días")}</em></article>
              <article className="success"><span>VIPs</span><strong>{crmExecutiveDashboard.vipRows.length}</strong><em>{t("Alto valor/frecuencia")}</em></article>
              <article className="primary"><span>{t("Conversión CRM")}</span><strong>{crmExecutiveDashboard.conversionRate}%</strong><em>{crmExecutiveDashboard.reactivatedRows.length}{" " + t("reactivados /") + " "}{crmExecutiveDashboard.contactedRows.length}{" " + t("contactados")}</em></article>
              <article><span>{t("Clientes con ROI")}</span><strong>{crmData.crmRevenueRows?.length || 0}</strong><em>{crmExecutiveDashboard.roiRate}{t("% de contactados")}</em></article>
              <article><span>{t("Días cierre mes")}</span><strong>{crmExecutiveDashboard.daysToMonthEnd ?? "—"}</strong><em>{t("Ventana para recuperar revenue")}</em></article>
            </div>

            <article className="ba-crm-exec-list">
              <div><span>{t("Top oportunidades")}</span><strong>{crmExecutiveDashboard.priorityRows.length}</strong></div>
              {crmExecutiveDashboard.prioritySample.length ? crmExecutiveDashboard.prioritySample.slice(0, 4).map((customer) => (
                <button type="button" key={customer.key || customer.id || customer.name} onClick={() => openCrmActionModal({ segment: customer.segments?.[0] || "general", title: "Contactar prioridad", subtitle: "Prioridad sugerida por Loop" }, customer)}>
                  <span>{customer.name}</span>
                  <strong>{MONEY.format(Math.max(Number(customer.avgTicket || 0), Number(crmData.avgTicket || 0) || 0))}</strong>
                  <em>{customer.followUpLabel || crmSegmentMeta(customer.segments?.[0])}</em>
                </button>
              )) : <p>{t("No hay oportunidades urgentes en este momento.")}</p>}
            </article>
          </section>

          <section className="ba-crm-smart-tasks" aria-label={t("Tareas inteligentes CRM")}>
            <div className="ba-crm-smart-head">
              <div>
                <span>{t("Smart tasks")}</span>
                <h3>{crmSmartTasks.headline}</h3>
                <p>{crmSmartTasks.subheadline}</p>
              </div>
              <div>
                <strong>{crmSmartTasks.todayCount}</strong>
                <em>{t("Tareas sugeridas")}</em>
              </div>
            </div>

            <div className="ba-crm-smart-grid">
              {crmSmartTasks.tasks.slice(0, 6).map((task) => (
                <article key={task.key} className={`ba-crm-smart-card ${task.tone} ${task.count ? "has-work" : "is-empty"}`}>
                  <div className="ba-crm-smart-card-top">
                    <span>{task.count ? "Acción sugerida" : "Sin urgencia"}</span>
                    <strong>{task.count}</strong>
                  </div>
                  <h4>{task.title}</h4>
                  <p>{task.description}</p>
                  <em>{task.customer ? `Próximo: ${task.customer.name}` : "Sin clientes para esta tarea"}</em>
                  <div className="ba-crm-smart-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setCrmFollowUpFilter(task.followUpFilter || "all");
                        setCrmSegmentFilter(task.segmentFilter || "all");
                        setCrmSearch("");
                      }}
                    >
                      {task.cta || t("Ver clientes")}
                    </button>
                    <button
                      type="button"
                      className="primary"
                      disabled={!task.customer || !canEditCrm}
                      onClick={() => openCrmActionModal({ segment: task.segment || task.customer?.segments?.[0] || "general", title: task.actionTitle || "Contactar cliente", subtitle: "Smart task sugerida por Loop" }, task.customer)}
                    >{t("Contactar")}</button>
                  </div>
                </article>
              ))}
            </div>

            {crmSmartTasks.recentContactGuard ? (
              <p className="ba-crm-smart-note">{crmSmartTasks.recentContactGuard}{" " + t("cliente")}{crmSmartTasks.recentContactGuard === 1 ? "" : "s"}{" " + t("fueron contactados recientemente; Loop evita insistir demasiado pronto.")}</p>
            ) : null}
          </section>

          <section className="ba-crm-followup-strip" aria-label={t("Pipeline de seguimiento CRM")}>
            <article className="warning"><span>{t("Seguimiento pendiente")}</span><strong>{crmData.followUpDueRows.length}</strong><em>{t("Contactados sin nueva reserva")}</em></article>
            <article className="neutral"><span>{t("Contactados")}</span><strong>{crmData.contactedRows.length}</strong><em>{t("Acciones CRM guardadas")}</em></article>
            <article className="success"><span>{t("Reactivados")}</span><strong>{crmData.reactivatedRows.length}</strong><em>{t("Reservaron después del contacto")}</em></article>
            <article className="primary"><span>{t("Contactados recientes")}</span><strong>{crmData.recentContactRows.length}</strong><em>{t("Espera antes de insistir")}</em></article>
          </section>

          <section className="ba-crm-revenue-strip" aria-label={t("Revenue recuperado por CRM")}>
            <article className="success"><span>{t("Revenue atribuido")}</span><strong>{MONEY.format(crmData.crmRecoveredRevenue || 0)}</strong><em>{t("Reservas dentro de") + " "}{CRM_ATTRIBUTION_WINDOW_DAYS}{" " + t("días post-contacto")}</em></article>
            <article className="primary"><span>{t("Reservas recuperadas")}</span><strong>{crmData.crmRecoveredBookings || 0}</strong><em>{t("Bookings después de acción CRM")}</em></article>
            <article className="neutral"><span>{t("Clientes con ROI")}</span><strong>{crmData.crmRevenueRows?.length || 0}</strong><em>{t("Contactados que volvieron")}</em></article>
            <article className="warning"><span>{t("Cobrado atribuido")}</span><strong>{MONEY.format(crmData.crmRecoveredCollected || 0)}</strong><em>{t("Cash registrado post-contacto")}</em></article>
          </section>

          <section className="ba-retention-action-center" aria-label={t("Centro de acciones de retención")}>
            {crmRetentionActionCenter.map((action) => (
              <article key={action.key} className={`ba-retention-card ${action.tone}`}>
                <div>
                  <span>{action.subtitle}</span>
                  <h4>{action.title}</h4>
                  <p>{action.goal}</p>
                </div>
                <strong>{action.count}</strong>
                {action.lead ? <em>{t("Próximo:") + " "}{action.lead.name}</em> : <em>{t("Sin clientes en este segmento")}</em>}
                <div className="ba-retention-actions">
                  <button type="button" onClick={() => { setActiveView("crm"); setCrmSegmentFilter(action.segment); setCrmSearch(""); }}>{t("Ver segmento")}</button>
                  <button type="button" className="primary" disabled={!action.lead || !canEditCrm} onClick={() => openCrmActionModal(action)}>{t("Contactar")}</button>
                  {action.whatsapp ? <a href={action.whatsapp} target="_blank" rel="noreferrer">{t("WhatsApp sugerido")}</a> : null}
                </div>
              </article>
            ))}
          </section>

          <section className="ba-main-grid crm-grid">
            <article className="ba-panel ba-panel-large">
              <div className="ba-panel-head compact">
                <div><span>{t("Customer 360")}</span><h3>{t("Base de clientes")}</h3></div>
                <strong>{crmData.filteredRows.length}</strong>
              </div>
              <div className="ba-crm-filters">
                <input value={crmSearch} onChange={(e) => setCrmSearch(e.target.value)} placeholder={t("Buscar cliente, teléfono, servicio, tag...")} />
                <select value={crmSegmentFilter} onChange={(e) => setCrmSegmentFilter(e.target.value)}>
                  <option value="all">{t("Todos los segmentos")}</option>
                  {["new", "frequent", "vip", "dormant", "at_risk", "no_show", "high_ticket", "product_buyer", "birthday_soon"].map((seg) => (
                    <option key={seg} value={seg}>{crmSegmentMeta(seg)} ({crmData.segmentCounts.get(seg) || 0})</option>
                  ))}
                </select>
                <select value={crmFollowUpFilter} onChange={(e) => setCrmFollowUpFilter(e.target.value)} aria-label={t("Filtrar por seguimiento")}>
                  <option value="all">{t("Todos los seguimientos")}</option>
                  <option value="priority">{t("Prioridades (")}{crmExecutiveDashboard.priorityRows.length})</option>
                  <option value="needs_follow_up">{t("Necesitan seguimiento (")}{crmData.followUpDueRows.length})</option>
                  <option value="contacted">{t("Contactados (")}{crmData.contactedRows.length})</option>
                  <option value="reactivated">{t("Reactivados (")}{crmData.reactivatedRows.length})</option>
                  <option value="revenue_attributed">{t("Con revenue CRM (")}{crmData.crmRevenueRows?.length || 0})</option>
                  <option value="not_contacted">{t("Sin contactar")}</option>
                </select>
                <button type="button" onClick={() => { setCrmSearch(""); setCrmSegmentFilter("all"); setCrmFollowUpFilter("all"); }}>{t("Limpiar")}</button>
              </div>
              {crmWhatsAppStatus ? <div className="ba-crm-whatsapp-status">{crmWhatsAppStatus}</div> : null}

              <div className="ba-crm-table">
                {crmData.filteredRows.length ? crmData.filteredRows.slice(0, 28).map((customer) => {
                  const whatsappPref = crmWhatsAppPreference(customer);
                  const whatsapp = whatsappPref.canSend ? crmBuildWhatsAppLink(customer.phone, `Hola ${customer.name.split(" ")[0] || ""}, te escribimos de ${businessName}.`) : "";
                  const isWhatsappSaving = crmWhatsAppSavingKey === (customer.key || customer.id);
                  return (
                    <div key={customer.key} className="ba-crm-row">
                      <div className="ba-crm-person">
                        <strong>{customer.name}</strong>
                        <span>{customer.phone || "Sin teléfono"}{customer.email ? ` · ${customer.email}` : ""}</span>
                        <div className="ba-crm-tags">{customer.segments.slice(0, 4).map((seg) => <em key={seg} className={crmSegmentTone(seg)}>{crmSegmentMeta(seg)}</em>)}</div>
                      </div>
                      <div><span>{t("Gasto")}</span><strong>{MONEY.format(customer.totalSpent)}</strong><em>{customer.totalBookings}{" " + t("reserva")}{customer.totalBookings === 1 ? "" : "s"}</em></div>
                      <div><span>{t("Ticket")}</span><strong>{MONEY.format(customer.avgTicket)}</strong><em>{customer.completedBookings}{" " + t("completadas")}</em></div>
                      <div><span>{t("Última visita")}</span><strong>{crmFormatDate(customer.lastVisit)}</strong><em>{customer.daysSinceLastVisit !== null ? `${customer.daysSinceLastVisit} días` : "Sin historial"}</em></div>
                      <div><span>{t("Seguimiento")}</span><strong className={`ba-followup-label ${customer.followUpTone || "muted"}`}>{customer.followUpLabel || "Sin contacto"}</strong><em>{customer.daysSinceContact !== null ? `hace ${customer.daysSinceContact}d` : customer.followUpHint}</em></div>
                      <div><span>{t("Revenue CRM")}</span><strong className={`ba-attribution-label ${customer.crmAttributionTone || "muted"}`}>{Number(customer.crmRecoveredRevenue || 0) > 0 ? MONEY.format(customer.crmRecoveredRevenue) : "—"}</strong><em>{Number(customer.crmRecoveredBookings || 0) > 0 ? `${customer.crmRecoveredBookings} reserva${Number(customer.crmRecoveredBookings) === 1 ? "" : "s"}` : customer.crmAttributionLabel}</em></div>
                      <div><span>{t("Favoritos")}</span><strong>{customer.favoriteService}</strong><em>{customer.favoriteWorker}</em></div>
                      <div className="ba-crm-whatsapp-cell">
                        <span>WhatsApp</span>
                        <strong className={`ba-whatsapp-pref ${whatsappPref.tone}`}>{whatsappPref.label}</strong>
                        <button
                          type="button"
                          disabled={!canEditCrm || isWhatsappSaving || !String(customer.id || customer.customerId || "").trim()}
                          onClick={() => updateCrmCustomerWhatsAppPreference(customer, !whatsappPref.optedOut)}
                          title={!String(customer.id || customer.customerId || "").trim() ? "Cliente sin ID CRM editable" : "Cambiar preferencia WhatsApp"}
                        >
                          {isWhatsappSaving ? t("Guardando...") : whatsappPref.optedOut ? "Permitir" : "Bloquear"}
                        </button>
                      </div>
                      <div className="ba-crm-actions">
                        <button type="button" disabled={!canEditCrm} onClick={() => openCrmActionModal({ segment: customer.segments?.[0] || "general", title: "Contactar cliente", subtitle: "Acción comercial" }, customer)}>{t("Contactar")}</button>
                        {whatsapp ? <a href={whatsapp} target="_blank" rel="noreferrer">WhatsApp</a> : <em>{whatsappPref.label}</em>}
                      </div>
                    </div>
                  );
                }) : <div className="ba-empty">{t("No hay clientes para este filtro.")}</div>}
              </div>
            </article>

            <article className="ba-panel">
              <div className="ba-panel-head compact"><div><span>{t("Segmentos")}</span><h3>{t("Distribución automática")}</h3></div></div>
              <div className="ba-crm-segments">
                {["vip", "frequent", "dormant", "at_risk", "no_show", "high_ticket", "product_buyer", "birthday_soon", "new"].map((seg) => (
                  <button type="button" key={seg} className={crmSegmentFilter === seg ? "active" : ""} onClick={() => setCrmSegmentFilter(seg)}>
                    <span>{crmSegmentMeta(seg)}</span><strong>{crmData.segmentCounts.get(seg) || 0}</strong>
                  </button>
                ))}
              </div>
            </article>

            <article className="ba-panel">
              <div className="ba-panel-head compact"><div><span>{t("Retención")}</span><h3>{t("Acciones rápidas")}</h3></div></div>
              <div className="ba-insights-list">
                <div className="ba-insight"><b>1</b><span>{t("Contacta clientes dormidos con una promo suave o recordatorio de mantenimiento.")}</span></div>
                <div className="ba-insight"><b>2</b><span>{t("Ofrece add-ons o productos a quienes ya compraron retail.")}</span></div>
                <div className="ba-insight"><b>3</b><span>{t("Usa cumpleaños próximos para enviar un mensaje personalizado.")}</span></div>
                <div className="ba-insight"><b>4</b><span>{t("Cuida a los VIP: son los que más ayudan a crecer recurrencia y ticket.")}</span></div>
              </div>
            </article>
          </section>
        </section>
      ) : null}



      {activeView === "memberships" && canViewMembershipAnalytics ? (
        <section className="ba-membership-module ba-membership-v2">
          <div className="ba-membership-hero-v2">
            <div className="ba-membership-hero-copy">
              <span>{t("Membresías")}</span>
              <h3>{t("Salud, renovaciones y uso")}</h3>
              <p>{t("Entiende tu base activa, próximas renovaciones y qué clientes necesitan seguimiento.")}</p>
            </div>
          </div>

          {membershipAnalyticsError ? <div className="ba-alert warning"><span>{membershipAnalyticsError}</span></div> : null}

          <article className="ba-panel ba-membership-summary-panel">
            <div className="ba-membership-summary-head">
              <div>
                <span>{t("Resumen operativo")}</span>
                <h3>{t("Lo importante de tu base de membresías")}</h3>
                <p>{t("Una lectura compacta de tamaño, renovación, uso y señales que requieren seguimiento.")}</p>
              </div>
              <button type="button" onClick={() => openMembershipDetail("overview")}>{t("Ver detalle operativo")}</button>
            </div>

            <div className="ba-membership-summary-grid">
              <section>
                <header>Base</header>
                <div><span>{t("Activas")}</span><strong>{membershipAnalytics?.activeMemberships || 0}</strong></div>
                <div><span>{t("Miembros activos")}</span><strong>{membershipAnalytics?.activeCustomers || 0}</strong></div>
                <div><span>{t("Valor activo")}</span><strong>{membershipMoney(membershipAnalytics?.activeValue || 0)}</strong></div>
              </section>

              <section>
                <header>{t("Renovación")}</header>
                <div><span>{t("Auto-renuevan")}</span><strong>{membershipAnalytics?.healthSummary?.autoRenewing || 0}</strong></div>
                <div><span>{t("Próx. 30 días")}</span><strong>{membershipAutomaticRenewalCount(membershipAnalytics)}</strong></div>
                <div className={(membershipAnalytics?.healthSummary?.grace || 0) > 0 ? "warning" : ""}><span>{t("En gracia")}</span><strong>{membershipAnalytics?.healthSummary?.grace || 0}</strong></div>
              </section>

              <section>
                <header>{t("Uso y seguimiento")}</header>
                <div><span>{t("Uso de beneficios")}</span><strong>{membershipAnalytics?.activeUsageRate || 0}%</strong></div>
                <div className={(membershipAnalytics?.needsAttention?.length || 0) > 0 ? "warning" : ""}><span>{t("Requieren atención")}</span><strong>{membershipAnalytics?.needsAttention?.length || 0}</strong></div>
                <div className={membershipHealthIssueCount(membershipAnalytics) > 0 ? "critical" : ""}><span>{t("Pago / método")}</span><strong>{membershipHealthIssueCount(membershipAnalytics)}</strong></div>
              </section>
            </div>

            <div className="ba-membership-summary-foot">
              <span><strong>{membershipAnalytics?.totalMemberships || 0}</strong>{" " + t("históricas")}</span>
              <i />
              <span><strong>{membershipAnalytics?.newMemberships30d || 0}</strong>{" " + t("nuevas · 30 días")}</span>
              <i />
              <span><strong>{membershipAnalytics?.renewals30d || 0}</strong>{" " + t("renovación")}{Number(membershipAnalytics?.renewals30d || 0) === 1 ? "" : "es"}{" " + t("· 30 días")}</span>
              {(membershipAnalytics?.healthSummary?.cancelAtPeriodEnd || 0) > 0 ? (
                <>
                  <i />
                  <span><strong>{membershipAnalytics?.healthSummary?.cancelAtPeriodEnd || 0}</strong>{" " + t("finalizan al cierre")}</span>
                </>
              ) : null}
            </div>
          </article>

          <article className="ba-panel ba-membership-master-panel">
            <div className="ba-panel-head compact">
              <div><span>{t("Membresías")}</span><h3>{t("Clientes y estado de sus membresías")}</h3></div>
              <div className="ba-membership-master-actions">
                {isOwnerOrAdmin && <button type="button" style={{ background: "#007d73", color: "white", minHeight: 40, padding: "10px 16px", borderRadius: 10 }} onClick={() => setMembershipEnrollmentOpen(true)}>{t("+ Agregar membresía")}</button>}
                <span>{membershipAnalytics?.totalMemberships || 0}{" " + t("históricas")}</span>
                {(membershipAnalytics?.totalMemberships || 0) > 0 ? (
                  <button type="button" onClick={() => openMembershipDetail("memberships")}>{t("Ver todas")}</button>
                ) : null}
              </div>
            </div>

            <div className="ba-membership-master-list">
              {(membershipAnalytics?.membershipOverview || membershipAnalytics?.upcomingRenewals || []).length ?
                (membershipAnalytics?.membershipOverview?.length ? membershipAnalytics.membershipOverview : (membershipAnalytics?.upcomingRenewals || []).map((row) => ({ ...row, status: "active", statusKey: row.healthState === "grace" ? "grace" : "active", nextDate: row.renewalDate })))
                  .slice(0, 5)
                  .map((row) => (
                    <button
                      type="button"
                      key={row.membershipId || row.id}
                      className="ba-membership-master-row"
                      onClick={() => openMembershipRecord(row)}
                      aria-label={`Ver detalle de ${row.customerName || "cliente"} · ${row.planName || "membresía"}`}
                    >
                      <div className="ba-membership-master-person">
                        <strong>{row.customerName || "Cliente sin identificar"}</strong>
                        <span>{row.planName || t("Membresía")}</span>
                      </div>
                      <em className={`ba-membership-status ${membershipRecordStatusTone(row)}`}>{membershipRecordStatusLabel(row)}</em>
                      <div>
                        <span>{t("Desde")}</span>
                        <strong>{row.startedAt ? membershipFormatDate(row.startedAt) : "Por confirmar"}</strong>
                      </div>
                      <div>
                        <span>{t("Próxima fecha")}</span>
                        <strong>{membershipRecordNextLabel(row)}</strong>
                      </div>
                      <div>
                        <span>Uso</span>
                        <strong>{membershipRecordUsageLabel(row)}</strong>
                      </div>
                      <div>
                        <span>{t("Valor")}</span>
                        <strong>{membershipRecordValueLabel(row)}</strong>
                      </div>
                    </button>
                  )) : (
                    <div className="ba-empty">{t("Todavía no hay membresías registradas.")}</div>
                  )}
            </div>

          </article>

          <article className="ba-panel ba-membership-attention-panel">
            <div className="ba-panel-head compact">
              <div><span>{t("Seguimiento")}</span><h3>{t("Requieren atención")}</h3></div>
              <strong>{membershipAnalytics?.needsAttention?.length || 0}</strong>
            </div>

            {(membershipAnalytics?.needsAttention || []).length ? (
              <>
                <div className="ba-membership-attention-list-v2">
                  {(membershipAnalytics.needsAttention || []).slice(0, 5).map((row) => (
                    <div key={`${row.kind || row.reason}-${row.membershipId}`} className={`ba-membership-attention-v2 ${row.severity || "info"} ${row.kind || ""}`}>
                      <b>{membershipAttentionLabel(row)}</b>
                      <div>
                        <strong>{row.customerName || "Cliente sin identificar"}</strong>
                        <span>{membershipAttentionDetail(row)}</span>
                      </div>
                      <em>
                        {row.creditsRemaining != null
                          ? `${row.creditsRemaining} crédito${Number(row.creditsRemaining) === 1 ? "" : "s"} disp.`
                          : "Revisar"}
                      </em>
                    </div>
                  ))}
                </div>

                {(membershipAnalytics?.needsAttention?.length || 0) > 5 ? (
                  <button type="button" className="ba-membership-expand" onClick={() => openMembershipDetail("attention")}>{t("Ver todos los seguimientos")}</button>
                ) : null}
              </>
            ) : (
              <div className="ba-membership-clear-state">
                <div aria-hidden="true">✓</div>
                <strong>{t("Todo en orden")}</strong>
                <span>{t("No detectamos pagos fallidos, conciliaciones, cancelaciones próximas ni membresías sin uso que requieran seguimiento.")}</span>
              </div>
            )}
          </article>

          <article className="ba-panel ba-membership-plans-panel">
            <div className="ba-panel-head compact">
              <div><span>{t("Planes")}</span><h3>{t("Desempeño de membresías activas")}</h3></div>
            </div>

            {(membershipAnalytics?.topPlans || []).length ? (
              <div className="ba-membership-plan-table">
                <div className="ba-membership-plan-table-head">
                  <span>{t("Plan")}</span>
                  <span>{t("Activas")}</span>
                  <span>{t("Miembros")}</span>
                  <span>Uso</span>
                  <span>{t("Valor activo")}</span>
                </div>
                {(membershipAnalytics.topPlans || []).map((plan) => (
                  <div key={plan.planId || plan.planName} className="ba-membership-plan-table-row">
                    <div><strong>{plan.planName || t("Membresía")}</strong></div>
                    <strong>{plan.active || 0}</strong>
                    <strong>{plan.activeCustomers || 0}</strong>
                    <div className="ba-membership-plan-usage" aria-label={`Uso ${plan.activeUsageRate || 0}%`}>
                      <span><i style={{ width: `${Math.max(0, Math.min(100, numberFrom(plan.activeUsageRate || 0)))}%` }} /></span>
                      <strong>{plan.activeUsageRate || 0}%</strong>
                    </div>
                    <strong>{membershipMoney(plan.activeValue || 0)}</strong>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ba-empty">{t("Todavía no hay membresías por plan.")}</div>
            )}
          </article>

          <section className="ba-membership-bottom-grid">
            <article className="ba-panel ba-membership-services-panel">
              <div className="ba-panel-head compact">
                <div><span>{t("Servicios")}</span><h3>{t("Más usados · últimos 30 días")}</h3></div>
              </div>
              <div className="ba-membership-service-list">
                {(membershipAnalytics?.topServicesUsed30d || []).length ? (membershipAnalytics.topServicesUsed30d || []).map((svc, index) => (
                  <div key={svc.serviceName} className="ba-membership-service-row">
                    <b>{index + 1}</b>
                    <div>
                      <strong>{svc.serviceName || "Servicio"}</strong>
                      <span>{svc.count || 0}{" " + t("reserva")}{Number(svc.count || 0) === 1 ? "" : "s"}</span>
                    </div>
                    <em>{svc.credits || 0} crédito{Number(svc.credits || 0) === 1 ? "" : "s"}</em>
                  </div>
                )) : (
                  <div className="ba-empty">{t("No se han usado créditos de membresía en los últimos 30 días.")}</div>
                )}
              </div>
            </article>

            <article className="ba-panel ba-membership-activity-panel">
              <div className="ba-panel-head compact">
                <div><span>{t("Actividad")}</span><h3>{t("Últimos movimientos")}</h3></div>
              </div>
              <div className="ba-membership-timeline">
                {(membershipAnalytics?.activityRecent || []).length ? (membershipAnalytics.activityRecent || []).slice(0, 5).map((row) => (
                  <div key={row.id} className="ba-membership-activity-item">
                    <i aria-hidden="true" />
                    <div>
                      <div className="ba-membership-activity-head">
                        <strong>{row.customerName || "Cliente sin identificar"}</strong>
                        <time>{membershipFormatDate(row.createdAt, row.sortMs, { withTime: true })}</time>
                      </div>
                      <p>{row.title || "Actividad de membresía"}</p>
                      <span>
                        {row.planName || row.membershipPlanName || t("Membresía")}
                        {row.creditsAfter != null ? ` · ${row.creditsAfter} crédito${Number(row.creditsAfter) === 1 ? "" : "s"} disp.` : ""}
                      </span>
                    </div>
                  </div>
                )) : (
                  <div className="ba-empty">{t("Sin actividad reciente.")}</div>
                )}
              </div>
              {(membershipAnalytics?.activityRecent?.length || 0) > 0 ? (
                <button type="button" className="ba-membership-expand" onClick={() => openMembershipDetail("activity")}>{t("Ver historial completo")}</button>
              ) : null}
            </article>
          </section>
        </section>
      ) : null}

      {activeView === "exports" ? (
        <section className="ba-reports-module" aria-label={t("Exportar reportes de Loop")}>
          <div className="ba-insights-hero reports">
            <div>
              <span>{t("Exportar")}</span>
              <h3>{t("Descargas del período")}</h3>
              <p>{t("Genera archivos para cierre, conciliación, seguimiento comercial, inventario y revisión operativa. Usa los filtros superiores para cambiar período y trabajador antes de exportar.")}</p>
            </div>
            <strong>{data.periodLabel}</strong>
          </div>

          <section className="ba-main-grid reports-grid">
            <article className="ba-panel">
              <div className="ba-panel-head compact"><div><span>{t("Ejecutivo")}</span><h3>{t("Resumen listo para compartir")}</h3></div></div>
              <div className="ba-export-grid">
                <button type="button" className="primary" onClick={exportExecutiveReport}>{t("Descargar reporte ejecutivo TXT")}</button>
                <button type="button" onClick={() => exportCsv("resumen")}>{t("Resumen CSV")}</button>
                <button type="button" onClick={() => window.print()}>{t("Imprimir vista actual / PDF")}</button>
              </div>
            </article>

            <article className="ba-panel">
              <div className="ba-panel-head compact"><div><span>{t("Finanzas")}</span><h3>{t("Cobros, pendientes y payout")}</h3></div></div>
              <div className="ba-export-grid">
                <button type="button" className="primary" disabled={!canViewFinancials && !canViewFinanceUnified} onClick={() => exportCsv("reservas")}>{t("Reservas financieras CSV")}</button>
                <button type="button" disabled={!canViewFinancials && !canViewFinanceUnified} onClick={() => exportCsv("payouts")}>{t("Payout trabajadores CSV")}</button>
                <button type="button" disabled={!canViewFinancials && !canViewFinanceUnified} onClick={() => exportCsv("cierre_diario")}>{t("Cierre diario CSV")}</button>
              </div>
            </article>

            <article className="ba-panel">
              <div className="ba-panel-head compact"><div><span>{t("Inventario")}</span><h3>{t("Stock y compras")}</h3></div></div>
              <div className="ba-export-grid">
                <button type="button" className="primary" disabled={!canViewInventory} onClick={() => exportCsv("inventario")}>{t("Inventario CSV")}</button>
                <button type="button" disabled={!canViewInventory} onClick={() => exportCsv("bajo_stock")}>{t("Bajo stock CSV")}</button>
                <button type="button" disabled={!canViewInventory || !inventoryPurchasePlan.items.length} onClick={exportInventoryPurchasePlan}>{t("Compra sugerida CSV")}</button>
              </div>
            </article>

            <article className="ba-panel">
              <div className="ba-panel-head compact"><div><span>{t("Clientes")}</span><h3>{t("CRM y retención")}</h3></div></div>
              <div className="ba-export-grid">
                <button type="button" className="primary" disabled={!canViewCrm} onClick={() => exportCsv("crm")}>{t("Clientes CRM CSV")}</button>
                <button type="button" disabled={!canViewReports} onClick={() => exportCsv("trafico")}>{t("Tráfico CSV")}</button>
              </div>
            </article>

            <article className="ba-panel ba-panel-large">
              <div className="ba-panel-head compact"><div><span>{t("Qué incluye")}</span><h3>{t("Datos exportables")}</h3></div></div>
              <div className="ba-report-summary">
                <p><strong>{t("Período:")}</strong> {data.periodLabel}</p>
                <p><strong>{t("Reservas con ingreso:")}</strong> {data.revenueBookings.length}</p>
                <p><strong>{t("Cobrado de ventas:")}</strong> {MONEY.format(data.collectedRevenue)} · <strong>{t("Por cobrar confirmado:")}</strong> {MONEY.format(data.pendingRevenue)}</p>
                <p><strong>{t("Propinas:")}</strong> {MONEY.format(data.totalTips)} · <strong>{t("Sin trazabilidad:")}</strong> {MONEY.format(data.untrackedExpectedRevenue)}</p>
                <p><strong>{t("Conciliación pendiente:")}</strong> {MONEY.format(data.paymentMismatchRevenue)} · {data.paymentMismatchBookings.length}{" " + t("pago")}{data.paymentMismatchBookings.length === 1 ? "" : "s"}{" " + t("inconsistente")}{data.paymentMismatchBookings.length === 1 ? "" : "s"}</p>
                <p><strong>{t("Clientes CRM:")}</strong> {crmData.rows.length} · <strong>{t("Productos:")}</strong> {inventoryData.rows.length}</p>
              </div>
              {reportStatus ? <div className="ba-report-status">{reportStatus}</div> : null}
            </article>
          </section>
        </section>
      ) : null}

      {activeView === "finance" && canViewFinanceUnified ? (
        <section className={`ba-finance-v3 ${isOwnerOrAdmin ? "is-owner" : "is-worker"}`} aria-label={t("Finanzas")}>
          <header className={`ba-finance-v3-head ${financeWorkspaceView === "cash" ? "is-cash" : "is-non-cash"}`}>
            <div>
              <span>{t("Finanzas")}</span>
              <div className="ba-finance-title-row"><h3>{t("Control financiero")}</h3>{isOwnerOrAdmin ? <div className="ba-finance-title-nav"><FinanceWorkspaceNav value={financeWorkspaceView} onChange={handleFinanceWorkspaceChange} /></div> : null}</div>
              <p>{t("Ventas, caja, cobranza, rentabilidad y conciliación del período en una sola lectura.")}</p>
                            {financeWorkspaceView === "cash" && financeData.trackedRevenueBase > 0 ? (
                <span className="ba-finance-exec-status">{t("Cobranza ·")}{financeData.collectionRate}{t("% cobrado")}</span>
              ) : null}
            </div>

                        <section className="ba-finance-exec-kpis" aria-label={t("Resumen financiero ejecutivo")}>
              <div>
                <span>{t("Ventas") + " "}</span>
                <strong>{MONEY.format(financeData.revenue)}</strong>
                <em>{t("Ingreso reconocido del período")}</em>
              </div>
              <div>
                <span>{t("Cobrado de citas")}</span>
                <strong>{MONEY.format(financeData.collectedRevenue)}</strong>
                <em>{t("Según fecha de la cita. Los anticipos aparecen en Caja.")}</em>
              </div>
              <div className={financeData.pendingRevenue > 0 ? "warning" : ""}>
                <span>{t("Por cobrar")}</span>
                <strong>{MONEY.format(financeData.pendingRevenue)}</strong>
                <em>{t("Saldo rastreado pendiente")}</em>
              </div>
              <div className={financeV3OperatingResult < 0 ? "result negative" : "result"}>
                <span>{t("Resultado")}</span>
                <strong>{MONEY.format(financeV3OperatingResult)}</strong>
                <em>{t("Después de costos directos y gastos operativos")}</em>
              </div>
            </section>

            <div className={`ba-finance-v3-health ${financeExecutiveDashboard.cashHealth?.tone || "neutral"} ${financeWorkspaceView !== "cash" ? "ba-finance-workspace-hidden" : ""}`}>
              <span>{t("Cobranza rastreada")}</span>
              <strong>{financeData.trackedRevenueBase > 0 ? `${financeData.collectionRate}% cobrado` : "Sin base rastreada"}</strong>
              <em>
                {financeData.trackedRevenueBase > 0
                  ? (financeData.pendingRevenue > 0
                    ? `${MONEY.format(financeData.pendingRevenue)} por cobrar confirmado`
                    : "Sin saldos confirmados por cobrar")
                  : "Todavía no hay ventas con trazabilidad de cobro"}
              </em>
              {financeData.paymentMismatchRevenue > 0 ? (
                <button type="button" onClick={() => openFinanceDetail("reconciliation")}>
                  {financeData.paymentMismatchBookings.length}{" " + t("pago")}{financeData.paymentMismatchBookings.length === 1 ? "" : "s"}{" " + t("por conciliar") + " "}<span aria-hidden="true">›</span>
                </button>
              ) : null}
            </div>
          </header>

          {isOwnerOrAdmin ? (
            <>
              <section className="ba-finance-v3-strip" aria-label={t("Indicadores financieros principales")}>
                <div>
                  <span>{t("Ventas generadas")}</span>
                  <strong>{MONEY.format(financeData.revenue)}</strong>
                  <em>{financeData.revenueBookings.length}{" " + t("reserva")}{financeData.revenueBookings.length === 1 ? "" : "s"}{" " + t("con ingreso")}</em>
                  {financeV3Comparison.enabled ? <FinanceV3ComparisonDelta comparison={financeV3Comparison.metrics.revenue} label={financeV3Comparison.label} /> : null}
                </div>
                <div>
                  <span>{t("Cobrado de ventas")}</span>
                  <strong>{MONEY.format(financeData.collectedRevenue)}</strong>
                  <em>{MONEY.format(financeData.totalTips)}{" " + t("en propinas aparte")}</em>
                  {financeV3Comparison.enabled ? <FinanceV3ComparisonDelta comparison={financeV3Comparison.metrics.collected} label={financeV3Comparison.label} /> : null}
                </div>
                <button
                  type="button"
                  className={financeData.pendingRevenue > 0 ? "warning is-clickable" : "is-clickable"}
                  onClick={() => openFinanceDetail("receivables")}
                  aria-label={t("Ver detalle de por cobrar confirmado")}
                >
                  <span>{t("Por cobrar confirmado")}</span>
                  <strong>{MONEY.format(financeData.pendingRevenue)}</strong>
                  <em>{financeData.unpaidBookings.length}{" " + t("saldo")}{financeData.unpaidBookings.length === 1 ? "" : "s"}{" " + t("rastreado")}{financeData.unpaidBookings.length === 1 ? "" : "s"}{" " + t("· Ver detalle")}</em>
                  {financeV3Comparison.enabled ? <FinanceV3ComparisonDelta comparison={financeV3Comparison.metrics.pending} label={financeV3Comparison.label} /> : null}
                </button>
                <div className="result">
                  <span>{t("Resultado operativo")}</span>
                  <strong>{MONEY.format(financeV3CashAfterExpenses)}</strong>
                  <em>{t("Caja rastreada después de payout, costos directos")}{financeV3PaidOperatingExpenses > 0 ? " y gastos pagados" : ""}</em>
                  {financeV3Comparison.enabled ? <FinanceV3ComparisonDelta comparison={financeV3Comparison.metrics.result} label={financeV3Comparison.label} /> : null}
                </div>
              </section>

              {financeV3Drivers.enabled && financeWorkspaceView !== "cash" && financeWorkspaceView !== "forecast" ? (
                <details key={financeWorkspaceView} className="ba-panel ba-finance-v3-drivers ba-finance-v3-drivers-compact">
                  <summary className="ba-finance-v3-insight-summary">
                    <div>
                      <span>{t("Insight del período")}</span>
                      <strong className={`is-${financeV3Drivers.direction}`}>{financeV3Drivers.headline}</strong>
                    </div>
                    <div>
                      <em>{financeV3Comparison.label}</em>
                      <b>{t("Ver análisis")}</b>
                    </div>
                  </summary>

                  <div className="ba-finance-v3-insight-expanded">
                    <p className="ba-finance-v3-insight-detail">{financeV3Drivers.detail}</p>

                    <div className="ba-finance-v3-drivers-grid">
                      <div className="ba-finance-v3-driver-summary">
                        <div className="ba-finance-v3-driver-bridge">
                          <div>
                            <span>{t("Reservas con ingreso")}</span>
                            <strong>{financeV3Drivers.currentCount}</strong>
                            <em>{financeV3SignedCount(financeV3Drivers.bookingDelta)}{" " + t("vs ant.") + " "}{financeV3Drivers.previousCount}</em>
                          </div>
                          <div>
                            <span>{t("Ticket promedio")}</span>
                            <strong>{MONEY.format(financeV3Drivers.currentTicket)}</strong>
                            <em>{financeV3SignedMoney(financeV3Drivers.ticketDelta, MONEY)}{" " + t("vs ant.") + " "}{MONEY.format(financeV3Drivers.previousTicket)}</em>
                          </div>
                        </div>

                        {financeV3Drivers.bridgeAvailable ? (
                          <div className="ba-finance-v3-driver-reconcile">
                            <span>{t("Puente de ventas")}</span>
                            <em>{t("Volumen") + " "}{financeV3SignedMoney(financeV3Drivers.volumeImpact, MONEY)}{" " + t("· ticket") + " "}{financeV3SignedMoney(financeV3Drivers.ticketImpact, MONEY)}</em>
                          </div>
                        ) : null}
                      </div>

                      <div className="ba-finance-v3-driver-column">
                        <span className="ba-finance-v3-driver-column-title">{t("Por servicio")}</span>
                        {financeV3Drivers.serviceMoves.length ? financeV3Drivers.serviceMoves.map((row) => (
                          <div className="ba-finance-v3-driver-row" key={`service-driver-${row.key}`}>
                            <div>
                              <strong>{row.key}</strong>
                              <span>{MONEY.format(row.current)}{" " + t("actual ·") + " "}{MONEY.format(row.previous)}{" " + t("anterior")}</span>
                            </div>
                            <em className={row.delta < 0 ? "negative" : row.delta > 0 ? "positive" : ""}>{financeV3SignedMoney(row.delta, MONEY)}</em>
                          </div>
                        )) : <div className="ba-finance-v3-driver-empty">{t("Sin cambios relevantes.")}</div>}
                      </div>

                      <div className="ba-finance-v3-driver-column">
                        <span className="ba-finance-v3-driver-column-title">{t("Por equipo")}</span>
                        {financeV3Drivers.workerMoves.length ? financeV3Drivers.workerMoves.map((row) => (
                          <div className="ba-finance-v3-driver-row" key={`worker-driver-${row.key}`}>
                            <div>
                              <strong>{row.key}</strong>
                              <span>{MONEY.format(row.current)}{" " + t("actual ·") + " "}{MONEY.format(row.previous)}{" " + t("anterior")}</span>
                            </div>
                            <em className={row.delta < 0 ? "negative" : row.delta > 0 ? "positive" : ""}>{financeV3SignedMoney(row.delta, MONEY)}</em>
                          </div>
                        )) : <div className="ba-finance-v3-driver-empty">{t("Sin cambios relevantes.")}</div>}
                      </div>
                    </div>

                    <p className="ba-finance-v3-drivers-note">{t("Servicio y equipo son dos lecturas alternativas del mismo movimiento y no se suman entre sí. El puente volumen / ticket es una descomposición matemática de la variación total de ventas.")}</p>
                  </div>
                </details>
              ) : null}
              <section className="ba-finance-v3-core-grid">

                {isOwnerOrAdmin && financeWorkspaceView === "profitability" ? (
                  <FinanceProfitabilityPanel
                    remote={financeRemote}
                    periodLabel={financeData.periodLabel}
                    workerId={workerFilter}
                  />
                ) : null}

                <article
                  className={`ba-panel ba-finance-v3-pnl ${isOwnerOrAdmin && financeWorkspaceView !== "pnl" ? "ba-finance-workspace-hidden" : ""}`}
                  style={isOwnerOrAdmin ? { gridColumn: "1 / -1" } : undefined}
                >
                  <div className="ba-finance-v3-card-head">
                    <div>
                      <span>{t("Estado de resultados")}</span>
                      <h4>{t("Estado de resultados ·") + " "}{isOwnerOrAdmin && expenseLedgerSummary.hasPeriodExpenses ? "según registros" : "parcial"}</h4>
                      <p>{isOwnerOrAdmin && expenseLedgerSummary.hasPeriodExpenses ? "Ventas, costos directos y gastos operativos registrados para el período." : "Lectura gerencial antes de gastos operativos completos."}</p>
                    </div>
                    <span className={`ba-finance-v3-badge ${isOwnerOrAdmin && expenseLedgerSummary.hasPeriodExpenses ? "success" : "neutral"}`}>
                      {isOwnerOrAdmin
                        ? (expenseLedgerSummary.hasPeriodExpenses
                          ? `${expenseLedgerSummary.periodCount} gasto${expenseLedgerSummary.periodCount === 1 ? "" : "s"} registrado${expenseLedgerSummary.periodCount === 1 ? "" : "s"}`
                          : "Faltan gastos")
                        : "Vista operativa"}
                    </span>
                  </div>

                  <div className="ba-finance-v3-pnl-body">
                    <div className="ba-finance-v3-pnl-line">
                      <span>{t("Ventas de servicios")}</span>
                      <strong>{MONEY.format(financeV3ServiceSales)}</strong>
                    </div>
                    <div className="ba-finance-v3-pnl-line">
                      <span>{t("Ventas de productos")}</span>
                      <strong>{MONEY.format(financeData.productSalesRevenueTotal)}</strong>
                    </div>
                    {financeV3MembershipIncome > 0 ? (
                      <div className="ba-finance-v3-pnl-note">
                        <span>{t("Incluye") + " "}{MONEY.format(financeV3MembershipIncome)}{" " + t("de ingreso asociado a membresías.")}</span>
                      </div>
                    ) : null}
                    <div className="ba-finance-v3-pnl-line subtotal">
                      <span>{t("Ventas generadas")}</span>
                      <strong>{MONEY.format(financeData.revenue)}</strong>
                    </div>

                    <div className="ba-finance-v3-pnl-separator" />

                    <div className="ba-finance-v3-pnl-line negative">
                      <span>{t("Pagos al equipo / comisiones")}</span>
                      <strong>− {MONEY.format(financeData.workerPayoutTotal)}</strong>
                    </div>
                    <div className="ba-finance-v3-pnl-line negative">
                      <span>{t("Materiales consumidos")}</span>
                      <strong>− {MONEY.format(financeData.materialCostTotal)}</strong>
                    </div>
                    <div className="ba-finance-v3-pnl-line negative">
                      <span>{t("Costo de productos")}</span>
                      <strong>− {MONEY.format(financeData.productSalesCostTotal)}</strong>
                    </div>
                    <div className="ba-finance-v3-pnl-line result">
                      <span>{t("Margen antes de gastos operativos")}</span>
                      <strong>{MONEY.format(financeV3GeneratedMargin)}</strong>
                    </div>

                    {isOwnerOrAdmin ? (
                      <button
                        type="button"
                        className={`ba-finance-v3-pnl-expenses ${expenseLedgerSummary.hasPeriodExpenses ? "has-data" : ""}`}
                        onClick={() => setExpenseLedgerOpen(true)}
                      >
                        <div>
                          <span>{t("Gastos operativos registrados")}</span>
                          <em>
                            {expenseLedgerSummary.hasPeriodExpenses
                              ? `${expenseLedgerSummary.periodCount} registro${expenseLedgerSummary.periodCount === 1 ? "" : "s"} · ${MONEY.format(expenseLedgerSummary.pendingTotal)} pendiente`
                              : "Alquiler, nómina fija, servicios, software, marketing y otros · Gestionar"}
                          </em>
                        </div>
                        <strong>{expenseLedgerSummary.hasPeriodExpenses ? `− ${MONEY.format(financeV3OperatingExpenses)}` : "Gestionar ›"}</strong>
                      </button>
                    ) : (
                      <div className="ba-finance-v3-pnl-missing">
                        <div>
                          <span>{t("Gastos operativos")}</span>
                          <em>{t("El detalle de gastos está disponible solo para dueño y administradores.")}</em>
                        </div>
                        <strong>—</strong>
                      </div>
                    )}

                    {isOwnerOrAdmin ? (
                      <div className="ba-finance-v3-pnl-line operating-result">
                        <span>{t("Resultado operativo")}</span>
                        <strong>{MONEY.format(financeV3OperatingResult)}</strong>
                      </div>
                    ) : null}
                  </div>
                </article>

                {isOwnerOrAdmin && financeWorkspaceView === "cash" ? (
                  <FinanceCloseoutPanel
                    remote={financeRemote}
                    businessId={businessId}
                    db={db}
                    auth={auth}
                    period={period}
                    periodLabel={financeData.periodLabel}
                    workerId={workerFilter}
                  />
                ) : null}
                {isOwnerOrAdmin && financeWorkspaceView === "cash" ? (
                  <FinanceAgingPanel
                    remote={financeRemote}
                    periodLabel={financeData.periodLabel}
                    workerId={workerFilter}
                  />
                ) : null}
                {isOwnerOrAdmin && financeWorkspaceView === "cash" ? (
                  <><LoopCollectionsPanel businessId={businessId} startDate={financeV3ComparisonScope.currentStart} endDate={financeV3ComparisonScope.currentEnd} />
                  <MembershipCollectionsPanel
                    db={db}
                    businessId={businessId}
                    startDate={financeV3ComparisonScope.currentStart}
                    endDate={financeV3ComparisonScope.currentEnd}
                    periodLabel={financeData.periodLabel}
                    enabled={activeView === "finance" && financeWorkspaceView === "cash"}
                  /></>
                ) : null}

                {isOwnerOrAdmin && financeWorkspaceView === "forecast" ? (
                  <FinanceForecastPanel
                    remote={financeRemote}
                    businessId={businessId}
                    db={db}
                    auth={auth}
                    periodLabel={financeData.periodLabel}
                    workerId={workerFilter}
                  />
                ) : null}


                <article className={`ba-panel ba-finance-v3-collections ba-finance-v3-collections-legacy ${isOwnerOrAdmin && financeWorkspaceView !== "cash" ? "ba-finance-workspace-hidden" : ""}`} style={isOwnerOrAdmin ? { display: "none" } : undefined} aria-hidden={isOwnerOrAdmin ? "true" : undefined}>
                  <div className="ba-finance-v3-card-head">
                    <div>
                      <span>{t("Caja y cobranza")}</span>
                      <h4>{t("Qué entró y qué falta cobrar")}</h4>
                      <p>{t("Solo saldos con trazabilidad se consideran cuentas por cobrar.")}</p>
                    </div>
                  </div>

                  <div className="ba-finance-v3-collection-stack">
                    <div>
                      <span>{t("Base rastreada")}</span>
                      <strong>{MONEY.format(financeData.trackedRevenueBase)}</strong>
                    </div>
                    <div>
                      <span>{t("Cobrado de ventas")}</span>
                      <strong>{MONEY.format(financeData.collectedRevenue)}</strong>
                    </div>
                    <div className="warning">
                      <span>{t("Por cobrar")}</span>
                      <strong>{MONEY.format(financeData.pendingRevenue)}</strong>
                    </div>
                    <div className={financeData.paymentMismatchRevenue > 0 ? "danger" : ""}>
                      <span>{t("Conciliación")}</span>
                      <strong>{MONEY.format(financeData.paymentMismatchRevenue)}</strong>
                    </div>
                  </div>

                  {financeData.trackedRevenueBase > 0 ? (
                    <div className="ba-finance-v3-collection-bar" aria-label={t("Distribución de ventas rastreadas")}>
                      <span
                        className="collected"
                        style={{ width: `${Math.max(0, Math.min(100, (safeMoneyValue(financeData.collectedRevenue) / financeData.trackedRevenueBase) * 100))}%` }}
                      />
                      <span
                        className="pending"
                        style={{ width: `${Math.max(0, Math.min(100, (safeMoneyValue(financeData.pendingRevenue) / financeData.trackedRevenueBase) * 100))}%` }}
                      />
                      <span
                        className="mismatch"
                        style={{ width: `${Math.max(0, Math.min(100, (safeMoneyValue(financeData.paymentMismatchRevenue) / financeData.trackedRevenueBase) * 100))}%` }}
                      />
                    </div>
                  ) : null}

                  <div className="ba-finance-v3-collection-actions">
                    <button type="button" className="ba-finance-v3-detail-trigger" aria-label={t("Ver detalle de por cobrar confirmado")} onClick={() => openFinanceDetail("receivables")}>
                      <span>{t("Por cobrar confirmado")}</span>
                      <strong>{MONEY.format(financeData.pendingRevenue)}</strong>
                    </button>
                    <button type="button" className="ba-finance-v3-detail-trigger" aria-label={t("Ver detalle de conciliaciones")} onClick={() => openFinanceDetail("reconciliation")}>
                      <span>{t("Conciliaciones")}</span>
                      <strong>{MONEY.format(financeData.paymentMismatchRevenue)}</strong>
                    </button>
                    <button type="button" className="ba-finance-v3-detail-trigger" aria-label={t("Ver detalle del histórico sin trazabilidad")} onClick={() => openFinanceDetail("untracked")}>
                      <span>{t("Histórico sin trazabilidad")}</span>
                      <strong>{MONEY.format(financeData.untrackedExpectedRevenue)}</strong>
                    </button>
                    <button type="button" className="ba-finance-v3-detail-trigger" aria-label={t("Ver detalle de servicios cubiertos por beneficios")} onClick={() => openFinanceDetail("covered")}>
                      <span>{t("Cubierto por beneficios")}</span>
                      <strong>{MONEY.format(financeData.coveredRevenueTotal)}</strong>
                    </button>
                  </div>

                  <div className="ba-finance-v3-methods">
                    <div className="ba-finance-v3-methods-head">
                      <span>{t("Caja por método")}</span>
                      <strong>{MONEY.format(financeCashMethodTotals.total)}</strong>
                    </div>
                    {sortedCashMethods.length ? sortedCashMethods.slice(0, 4).map((row) => (
                      <div className="ba-finance-v3-method-row" key={row.method || row.label}>
                        <span>{row.label || "Sin método"}</span>
                        <em>{MONEY.format(row.total)}</em>
                      </div>
                    )) : <div className="ba-empty">{t("Sin caja registrada por método.")}</div>}
                    {sortedCashMethods.length > 4 ? (
                      <details className="ba-finance-v3-method-more">
                        <summary>{t("Ver") + " "}{sortedCashMethods.length - 4}{" " + t("método")}{sortedCashMethods.length - 4 === 1 ? "" : "s"}{" " + t("adicional")}{sortedCashMethods.length - 4 === 1 ? "" : "es"}</summary>
                        {sortedCashMethods.slice(4).map((row) => (
                          <div className="ba-finance-v3-method-row" key={`extra-${row.method || row.label}`}>
                            <span>{row.label || "Sin método"}</span>
                            <em>{MONEY.format(row.total)}</em>
                          </div>
                        ))}
                      </details>
                    ) : null}
                  </div>
                </article>
              </section>


              <article className={`ba-panel ba-finance-v3-trend ${isOwnerOrAdmin && financeWorkspaceView !== "trends" ? "ba-finance-workspace-hidden" : ""}`}>
                <div className="ba-finance-v3-card-head">
                  <div>
                    <span>{t("Evolución financiera")}</span>
                    <h4>{t("Ventas, cobros y contribución")}</h4>
                    <p>{financeV3Trend.label}{" " + t("· el gráfico se adapta automáticamente al rango seleccionado.")}</p>
                  </div>
                  <span className="ba-finance-v3-badge">{financeData.periodLabel}</span>
                </div>
                <FinanceV3TrendChart
                  rows={financeV3Trend.rows}
                  comparisonRows={financeV3Trend.comparisonRows}
                  grain={financeV3Trend.grain}
                  comparisonLabel={financeV3Comparison.label}
                />
              </article>

              <section className={`ba-finance-v3-performance-grid ${isOwnerOrAdmin && financeWorkspaceView !== "team" ? "ba-finance-workspace-hidden" : ""}`}>
                <article className="ba-panel ba-finance-v3-ranking">
                  <div className="ba-finance-v3-card-head">
                    <div>
                      <span>{t("Servicios")}</span>
                      <h4>{t("Qué está generando ventas")}</h4>
                      <p>{t("Ventas generadas y costo material registrado por servicio.")}</p>
                    </div>
                  </div>
                  <div className="ba-finance-v3-ranking-list">
                    {financeV3ServiceLeaders.length ? financeV3ServiceLeaders.map((row, index) => (
                      <div className="ba-finance-v3-ranking-row" key={row.service}>
                        <i>{index + 1}</i>
                        <div>
                          <strong>{row.service}</strong>
                          <span>{row.materialCost > 0 ? `Materiales ${MONEY.format(row.materialCost)}` : "Sin costo material registrado"}</span>
                        </div>
                        <em>{MONEY.format(row.revenue)}</em>
                      </div>
                    )) : <div className="ba-empty">{t("Sin ventas por servicio para este período.")}</div>}
                  </div>
                </article>

                <article className="ba-panel ba-finance-v3-ranking">
                  <div className="ba-finance-v3-card-head">
                    <div>
                      <span>{t("Equipo")}</span>
                      <h4>{t("Aporte operativo del equipo")}</h4>
                      <p>{t("Ventas reconocidas menos pagos al equipo y costos directos atribuibles.")}</p>
                    </div>
                  </div>
                  <div className="ba-finance-v3-ranking-list">
                    {financeV3WorkerLeaders.length ? financeV3WorkerLeaders.map((row, index) => (
                      <div className="ba-finance-v3-ranking-row worker" key={row.worker}>
                        <i>{financeExecutiveInitial(row.worker)}</i>
                        <div>
                          <strong>{row.worker}</strong>
                          <span>{t("Ventas") + " "}{MONEY.format(row.totalBusiness)}{" " + t("· pago al equipo") + " "}{MONEY.format(row.totalPayout)}</span>
                        </div>
                        <em className={safeMoneyValue(row.operatingContribution) < 0 ? "negative" : ""}>{MONEY.format(row.operatingContribution)}</em>
                      </div>
                    )) : <div className="ba-empty">{t("Sin actividad financiera del equipo para este período.")}</div>}
                  </div>
                </article>
              </section>
            </>
          ) : (
            <article className="ba-panel ba-finance-v3-worker-self">
              <div className="ba-finance-v3-card-head">
                <div>
                  <span>{t("Mi actividad")}</span>
                  <h4>{t("Resumen financiero")}</h4>
                  <p>{t("La información visible respeta la configuración de compensación definida por el negocio.")}</p>
                </div>
              </div>
              <div className="ba-finance-worker-mobile-list is-worker-v3">
                {financeWorkerRows.length ? financeWorkerRows.map((row) => (
                  <details className="ba-finance-worker-mobile-row" key={row.worker}>
                    <summary>
                      <div>
                        <strong>{row.worker}</strong>
                        <span>{financeWorkerMobileSummary(row)}</span>
                      </div>
                      <em>{financeWorkerMobileMainValue(row)}</em>
                    </summary>
                    <dl>
                      {financeWorkerMobileDetailRows(row).map((item) => (
                        <div key={item.label}><dt>{item.label}</dt><dd>{item.value}</dd></div>
                      ))}
                    </dl>
                  </details>
                )) : <div className="ba-empty">{t("Sin actividad financiera para este período.")}</div>}
              </div>
            </article>
          )}

          {isOwnerOrAdmin ? (
            <details className={`ba-panel ba-finance-v3-worker-detail ${isOwnerOrAdmin && financeWorkspaceView !== "team" ? "ba-finance-workspace-hidden" : ""}`}>
              <summary>
                <div>
                  <span>{t("Detalle del equipo")}</span>
                  <strong>{t("Ventas, pagos al equipo y caja neta por trabajador")}</strong>
                </div>
                <div className="ba-finance-v3-worker-detail-action">
                  <em>{financeWorkerRows.length}{" " + t("trabajador")}{financeWorkerRows.length === 1 ? "" : "es"}</em>
                  <strong>{t("Ver detalle")}</strong>
                  <i aria-hidden="true">⌄</i>
                </div>
              </summary>
              <div className="ba-finance-v3-worker-detail-body">
                <div className="ba-finance-v3-worker-detail-head">
                  <p>{t("Abre este detalle solo cuando necesites revisar el desglose completo.")}</p>
                  <button type="button" className="ba-soft-action" onClick={() => setShowFinanceCustomize(true)}>{t("Personalizar columnas")}</button>
                </div>
                {financeWorkerRows.length ? (
                  <>
                    <div className="ba-finance-worker-table-v110-wrap">
                      <div
                        className="ba-finance-worker-table-v110"
                        style={financeWorkerTableStyle}
                        role="table"
                        aria-label={t("Ventas, pagos al equipo y caja neta por trabajador")}
                      >
                        <div className="ba-finance-worker-groups-v110" role="row" aria-hidden="true">
                          {financeWorkerGroupRanges.map((group) => (
                            <span
                              key={group.key}
                              className={`ba-finance-group-v110 is-${group.key}`}
                              style={{ gridColumn: `${group.start} / ${group.end}` }}
                            >
                              <b>{group.label}</b>
                            </span>
                          ))}
                        </div>

                        <div className="ba-finance-worker-head-v110" role="row">
                          {financeWorkerExecutiveColumns.map((col) => (
                            <span key={col.key} className={financeWorkerColumnClassName(col)}>{col.label}</span>
                          ))}
                        </div>

                        {financeWorkerRows.map((row) => (
                          <div className="ba-finance-worker-row-v110" role="row" key={row.worker}>
                            {financeWorkerExecutiveColumns.map((col) => renderFinanceWorkerExecutiveCell(row, col, false))}
                          </div>
                        ))}

                        <div className="ba-finance-worker-row-v110 is-total" role="row">
                          {financeWorkerExecutiveColumns.map((col) => renderFinanceWorkerExecutiveCell({ ...workerFinanceSummary.total, worker: "Total" }, col, true))}
                        </div>
                      </div>
                    </div>

                    <div className="ba-worker-close-cards ba-finance-worker-mobile-list" aria-label={t("Resumen por trabajador móvil")}>
                      {financeWorkerRows.map((row) => {
                        const detailRows = financeWorkerMobileDetailRows(row);
                        return (
                          <details className="ba-finance-worker-mobile-row" key={row.worker}>
                            <summary>
                              <div>
                                <strong>{row.worker}</strong>
                                <span>{financeWorkerMobileSummary(row)}</span>
                              </div>
                              <em>{financeWorkerMobileMainValue(row)}</em>
                            </summary>
                            <dl>
                              {detailRows.map((item) => (
                                <div key={item.label}><dt>{item.label}</dt><dd>{item.value}</dd></div>
                              ))}
                            </dl>
                          </details>
                        );
                      })}
                    </div>
                  </>
                ) : <div className="ba-empty">{t("Aún no hay reservas atendidas para este filtro.")}</div>}
              </div>
            </details>
          ) : null}

          {isOwnerOrAdmin ? (
            <FinanceCutoverStatus
              remote={financeRemote}
              localCurrent={financeV3LocalCurrentData}
              resolvedCurrent={financeV3CurrentData}
              localPrevious={financeV3LocalPreviousData}
              resolvedPrevious={financeV3PreviousData}
              periodLabel={financeData.periodLabel}
              workerId={workerFilter}
              detailView={financeDetailView}
              localDetailTotal={financeV3LocalDetailFilteredRows.length}
              localDetailSummaryAmount={financeV3LocalDetailSummaryAmount}
              fastPath={financeFastPath}
              loadedBookings={bookings.length}
            />
          ) : null}


          {isOwnerOrAdmin ? (
            <ExpenseLedger
              businessId={businessId}
              db={db}
              auth={defaultAuth}
              open={expenseLedgerOpen}
              onClose={() => setExpenseLedgerOpen(false)}
              currentStart={financeV3ComparisonScope.currentStart}
              currentEnd={financeV3ComparisonScope.currentEnd}
              previousStart={financeV3ComparisonScope.previousStart}
              previousEnd={financeV3ComparisonScope.previousEnd}
              periodLabel={financeData.periodLabel}
              onSummaryChange={setExpenseLedgerSummary}
            />
          ) : null}

          {financeDetailView ? (
            <div className="ba-finance-v3-drawer-layer" role="presentation" onClick={closeFinanceDetail}>
              <aside className="ba-finance-v3-drawer" role="dialog" aria-modal="true" aria-label={financeV3ActiveDetailMeta.title} onClick={(event) => event.stopPropagation()}>
                <div className="ba-finance-v3-drawer-head">
                  <div>
                    <span>{financeV3ActiveDetailMeta.eyebrow}</span>
                    <h3>{financeV3ActiveDetailMeta.title}</h3>
                    <p>{financeV3ActiveDetailMeta.description}</p>
                  </div>
                  <button type="button" onClick={closeFinanceDetail} aria-label={t("Cerrar")}>×</button>
                </div>

                <div className="ba-finance-v3-drawer-tools">
                  <input
                    type="search"
                    value={financeDetailSearch}
                    onChange={(event) => {
                      setFinanceDetailSearch(event.target.value);
                      setFinanceDetailPage(1);
                    }}
                    placeholder={t("Buscar cliente, servicio o trabajador")}
                  />
                  <span>{financeV3DetailFilteredRows.length}{" " + t("registro")}{financeV3DetailFilteredRows.length === 1 ? "" : "s"}</span>
                </div>

                <div className="ba-finance-v3-drawer-list">
                  {financeV3DetailPageRows.length ? financeV3DetailPageRows.map((row) => (
                    <div className="ba-finance-v3-drawer-row" key={`${financeDetailView}-${row.id}`}>
                      <div>
                        <strong>{row.client || t("Cliente")}</strong>
                        <span>{row.service || "Servicio"} · {row.worker || t("Sin trabajador")}</span>
                        <em>
                          {row.date instanceof Date && Number.isFinite(row.date.getTime())
                            ? row.date.toLocaleDateString("es-PA", { day: "numeric", month: "short", year: "numeric" }).replace(".", "")
                            : t("Sin fecha")}
                          {row.method ? ` · ${row.method}` : ""}
                        </em>
                      </div>
                      <div>
                        <span>{financeV3ActiveDetailMeta.amountLabel}</span>
                        <strong>{MONEY.format(row[financeV3ActiveDetailMeta.amountKey])}</strong>
                        {row.status ? <em>{row.status}</em> : null}
                      </div>
                    </div>
                  )) : <div className="ba-empty">{t("No hay registros que coincidan con esta búsqueda.")}</div>}
                </div>

                <div className="ba-finance-v3-drawer-foot">
                  <button
                    type="button"
                    disabled={financeV3SafeDetailPage <= 1}
                    onClick={() => setFinanceDetailPage((page) => Math.max(1, page - 1))}
                  >{t("Anterior")}</button>
                  <span>{t("Página") + " "}{financeV3SafeDetailPage}{" " + t("de") + " "}{financeV3DetailPages}</span>
                  <button
                    type="button"
                    disabled={financeV3SafeDetailPage >= financeV3DetailPages}
                    onClick={() => setFinanceDetailPage((page) => Math.min(financeV3DetailPages, page + 1))}
                  >{t("Siguiente")}</button>
                </div>
              </aside>
            </div>
          ) : null}
        </section>
      ) : null}

      {showFinanceCustomize ? (
        <div className="ba-view-customizer-backdrop" role="presentation" onClick={() => setShowFinanceCustomize(false)}>
          <section className="ba-view-customizer" role="dialog" aria-modal="true" aria-label={t("Columnas de finanzas")} onClick={(event) => event.stopPropagation()}>
            <div className="ba-view-customizer-head">
              <div>
                <span>{t("Detalle del equipo")}</span>
                <h3>{t("Columnas")}</h3>
                <p>{t("Escoge qué columnas quieres ver en el desglose completo por trabajador.")}</p>
              </div>
              <button type="button" onClick={() => setShowFinanceCustomize(false)} aria-label={t("Cerrar")}>×</button>
            </div>
            <div className="ba-view-customizer-body ba-finance-column-customizer-body">
              {financeColumnCustomizeGroups.map((group) => (
                <div key={group.key} className={`ba-view-customizer-group ba-finance-column-group is-${group.key}`}>
                  <div className="ba-finance-column-group-head">
                    <strong>{group.label}</strong>
                    <span>{group.hint}</span>
                  </div>
                  {group.options.map((option) => {
                    const required = Boolean(option.required);
                    const checked = required ? true : financeConfigVisible(option.key);
                    return (
                      <label key={option.key} className={`ba-view-toggle ba-finance-column-toggle ${required ? "is-required" : ""}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={required}
                          onChange={() => toggleFinanceViewOption(option.key)}
                        />
                        <span>{option.label}</span>
                        {required ? <em>{option.note || "Obligatorio"}</em> : null}
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="ba-view-customizer-foot">
              <button type="button" className="ba-soft-action" onClick={restoreFinanceViewConfig}>{t("Restaurar vista predeterminada")}</button>
              <button type="button" className="ba-primary-action" onClick={() => setShowFinanceCustomize(false)}>{t("Listo")}</button>
            </div>
          </section>
        </div>
      ) : null}

      {activeView === "inventory" && canViewInventory ? (
        <section className="ba-inventory-module ba-inventory-command ba-inventory-simple">
          <section className={`ba-inventory-simple-hero ${inventoryPurchasePlan.health.tone}`}>
            <div className="ba-inventory-simple-copy">
              <span>{t("Inventario")}</span>
              <h3>{inventoryPurchasePlan.health.label}</h3>
              <p>{inventoryPurchasePlan.health.hint}</p>
              <div className="ba-inventory-simple-pills">
                <em className={inventoryEnabled ? "ok" : "muted"}>{inventoryEnabled ? t("Inventario activo") : "Inventario apagado"}</em>
                <em className={inventoryAutoDeduct ? "ok" : "muted"}>{inventoryAutoDeduct ? "Auto-descuento ON" : "Auto-descuento OFF"}</em>
                <em>{inventoryDeductTrigger === "paid" ? "Descuenta al cobrar" : "Descuenta al completar"}</em>
              </div>
            </div>

            <div className="ba-inventory-simple-actions">
              <button type="button" className="primary" disabled={!canEditInventory} onClick={() => { resetInventoryForm(); setInventoryPanel("product"); }}>{t("+ Producto")}</button>
              <button type="button" disabled={!canAdjustInventory} onClick={() => setInventoryPanel("movement")}>{t("Movimiento")}</button>
              <button type="button" disabled={!canAdjustInventory} onClick={() => setInventoryPanel("stocktake")}>{t("Conteo físico")}</button>
            </div>
          </section>

          {inventoryStatus ? <div className="ba-inventory-command-status">{inventoryStatus}</div> : null}
          {businessSettingsStatus ? <div className="ba-inventory-command-status muted">{businessSettingsStatus}</div> : null}

          {!inventoryEnabled ? (
            <div className="ba-inventory-command-alert">
              <div>
                <strong>{t("Inventario está inactivo")}</strong>
                <span>{t("Actívalo para controlar stock, costos y descuento automático de materiales.")}</span>
              </div>
              <button type="button" disabled={businessSettingsSaving || !canEditInventory} onClick={() => updateBusinessPhase2Settings({ inventoryEnabled: true, inventoryPlan: "early_access" })}>{t("Activar ahora")}</button>
            </div>
          ) : null}

          <section className="ba-inventory-simple-kpis">
            <article>
              <span>{t("Valor inventario")}</span>
              <strong>{MONEY.format(inventoryData.totalInventoryValue)}</strong>
              <em>{t("Stock × costo unitario")}</em>
            </article>
            <article>
              <span>{t("Productos activos")}</span>
              <strong>{inventoryData.rows.length}</strong>
              <em>{inventoryData.lowStockItems.length ? `${inventoryData.lowStockItems.length} en alerta` : "Sin alertas"}</em>
            </article>
            <article className={inventoryAutomationDashboard.readyWithPlans.length ? "warning" : ""}>
              <span>{t("Acciones pendientes")}</span>
              <strong>{inventoryAutomationDashboard.readyWithPlans.length}</strong>
              <em>{t("Reservas por revisar")}</em>
            </article>
            <article className={inventoryData.lowStockItems.length ? "warning" : ""}>
              <span>{t("Bajo stock")}</span>
              <strong>{inventoryData.lowStockItems.length}</strong>
              <em>{inventoryData.lowStockItems.length ? "Requiere compra" : t("Todo saludable")}</em>
            </article>
          </section>

          <section className="ba-inventory-simple-grid">
            <article className="ba-inventory-simple-card attention">
              <div className="ba-inventory-simple-card-head">
                <div>
                  <span>{t("Acciones pendientes")}</span>
                  <h3>{inventoryAutomationDashboard.readyWithPlans.length}{" " + t("reservas por revisar")}</h3>
                  <p>{t("Revisa descuentos, servicios sin materiales y posibles errores sin llenar esta pantalla de tablas.")}</p>
                </div>
                <strong>{inventoryAutomationDashboard.failedBookings.length ? `${inventoryAutomationDashboard.failedBookings.length} errores` : t("Operativo")}</strong>
              </div>
              <div className="ba-inventory-simple-stats">
                <button type="button" onClick={() => setInventoryPanel("actions")}><b>{inventoryAutomationDashboard.readyWithPlans.length}</b><span>{t("Por revisar") + " "}</span></button>
                <button type="button" onClick={() => setInventoryPanel("replenishment")}><b>{inventoryData.lowStockItems.length}</b><span>{t("Bajo stock")}</span></button>
                <button type="button" onClick={() => setInventoryPanel("history")}><b>{inventoryAutomationDashboard.failedBookings.length}</b><span>{t("Errores") + " "}</span></button>
              </div>
              <div className="ba-inventory-simple-card-actions">
                <button type="button" className="primary" onClick={() => setInventoryPanel("actions")}>{t("Ver acciones")}</button>
                <button type="button" disabled={inventorySaving || !inventoryEnabled || !inventoryAutoDeduct || !canAdjustInventory || !inventoryAutomationDashboard.readyBookings.length} onClick={runInventoryDeductionBridge}>{inventorySaving ? "Procesando..." : "Procesar listos"}</button>
              </div>
            </article>

            <article className="ba-inventory-simple-card">
              <div className="ba-inventory-simple-card-head">
                <div>
                  <span>{t("Productos clave")}</span>
                  <h3>{t("Inventario operativo")}</h3>
                  <p>{inventoryData.rows.length ? `${inventoryData.rows.length} producto${inventoryData.rows.length === 1 ? "" : "s"} registrado${inventoryData.rows.length === 1 ? "" : "s"}.` : "Agrega tu primer producto para controlar stock y costos."}</p>
                </div>
                <strong>{MONEY.format(inventoryData.totalInventoryValue)}</strong>
              </div>
              <div className="ba-inventory-simple-product-list">
                {inventoryData.rows.length ? inventoryData.rows.slice(0, 3).map((item) => (
                  <button key={item.id || item.name} type="button" onClick={() => editInventoryItem(item)}>
                    <i>{String(item.name || "P").slice(0, 2).toUpperCase()}</i>
                    <span><b>{item.name}</b><em>{item.currentStock} {item.unit}{" " + t("· mínimo") + " "}{item.minStock || "—"}</em></span>
                    <strong>{MONEY.format(item.currentStock * item.unitCost)}</strong>
                  </button>
                )) : <div className="ba-inventory-simple-empty">{t("No hay productos todavía.")}</div>}
              </div>
              <div className="ba-inventory-simple-card-actions">
                <button type="button" className="primary" disabled={!canEditInventory} onClick={() => { resetInventoryForm(); setInventoryPanel("product"); }}>{t("Agregar producto")}</button>
                <button type="button" onClick={() => setInventoryPanel("products")}>{t("Ver inventario completo")}</button>
              </div>
            </article>

            <article className={`ba-inventory-simple-card ${inventoryPurchasePlan.items.length ? "warning" : "success"}`}>
              <div className="ba-inventory-simple-card-head">
                <div>
                  <span>{t("Reposición inteligente")}</span>
                  <h3>{MONEY.format(inventoryPurchasePlan.totalSuggestedCost)}</h3>
                  <p>{inventoryPurchasePlan.items.length ? `${inventoryPurchasePlan.nowCount} comprar hoy · ${inventoryPurchasePlan.soonCount} pronto` : "No hay compras sugeridas."}</p>
                </div>
                <strong>{inventoryPurchasePlan.items.length ? "Comprar" : "OK"}</strong>
              </div>
              <div className="ba-inventory-simple-preview">
                {inventoryPurchasePlan.items.length ? inventoryPurchasePlan.items.slice(0, 3).map((item) => (
                  <div key={item.id || item.name}><span>{item.name}</span><strong>{item.suggestedQty || item.quantity || "—"} {item.unit || "un."}</strong></div>
                )) : <div><span>{t("Todo saludable")}</span><strong>{t("Sin compras")}</strong></div>}
              </div>
              <button type="button" onClick={() => setInventoryPanel("replenishment")}>{t("Ver reposición")}</button>
            </article>

            <article className={`ba-inventory-simple-card ${inventoryServiceInsights.critical.length ? "warning" : inventoryServiceInsights.connected.length ? "success" : ""}`}>
              <div className="ba-inventory-simple-card-head">
                <div>
                  <span>{t("Servicios conectados")}</span>
                  <h3>{inventoryServiceInsights.connected.length}/{inventoryServiceInsights.all.length}</h3>
                  <p>{inventoryServiceInsights.connected.length ? `${MONEY.format(inventoryServiceInsights.totalMaterialCost)} en costo estimado por reserva configurada.` : "Conecta materiales desde Servicios para calcular margen real."}</p>
                </div>
                <strong>{inventoryServiceInsights.critical.length ? "Revisar" : t("Materiales")}</strong>
              </div>
              <div className="ba-inventory-simple-progress"><i style={{ width: `${inventoryServiceInsights.all.length ? Math.round((inventoryServiceInsights.connected.length / inventoryServiceInsights.all.length) * 100) : 0}%` }} /></div>
              <button type="button" onClick={() => setInventoryPanel("analysis")}>{t("Gestionar servicios")}</button>
            </article>

            <article className="ba-inventory-simple-card">
              <div className="ba-inventory-simple-card-head">
                <div>
                  <span>{t("Últimos movimientos")}</span>
                  <h3>{inventoryData.movements.length ? inventoryData.movements[0].itemName : "Sin movimientos"}</h3>
                  <p>{inventoryData.movements.length ? `${inventoryMovementTypeLabel(inventoryData.movements[0].type)} · ${inventoryData.movements[0].quantity > 0 ? "+" : ""}${inventoryData.movements[0].quantity} ${inventoryData.movements[0].unit}` : "Los ajustes, compras y descuentos aparecerán aquí."}</p>
                </div>
                <strong>{inventoryData.movements.length}</strong>
              </div>
              <div className="ba-inventory-simple-preview">
                {inventoryData.movements.length ? inventoryData.movements.slice(0, 3).map((mov) => (
                  <div key={mov.id || `${mov.itemName}-${mov.createdAt}`}><span>{mov.itemName}</span><strong>{mov.quantity > 0 ? "+" : ""}{mov.quantity} {mov.unit}</strong></div>
                )) : <div><span>{t("Sin historial todavía")}</span><strong>—</strong></div>}
              </div>
              <button type="button" onClick={() => setInventoryPanel("history")}>{t("Ver historial")}</button>
            </article>

            <article className={`ba-inventory-simple-card ${auditData.errorRows.length ? "warning" : ""}`}>
              <div className="ba-inventory-simple-card-head">
                <div>
                  <span>{t("Auditoría")}</span>
                  <h3>{auditData.errorRows.length}{" " + t("errores recientes")}</h3>
                  <p>{auditData.errorRows.length ? "Hay eventos que requieren revisión." : "Sin errores recientes."}</p>
                </div>
                <strong>{auditData.errorRows.length ? t("Atención") : "OK"}</strong>
              </div>
              <div className="ba-inventory-simple-card-actions">
                <button type="button" onClick={() => setInventoryPanel("history")}>{t("Abrir auditoría")}</button>
                <button type="button" onClick={() => setInventoryPanel("settings")}>{t("Configuración")}</button>
              </div>
            </article>
          </section>

          {inventoryPanel ? (
            <div className="ba-inventory-modal-layer" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setInventoryPanel(""); }}>
              <section className={`ba-inventory-modal ${["product", "movement", "stocktake", "history", "analysis", "replenishment", "products", "actions"].includes(inventoryPanel) ? "wide" : ""}`} role="dialog" aria-modal="true" aria-label={t("Panel de inventario")}>
                <header className="ba-inventory-modal-head">
                  <div>
                    <span>{t("Inventario")}</span>
                    <h3>{inventoryPanel === "product" ? (inventoryForm.id ? "Editar producto" : t("Agregar producto")) : inventoryPanel === "products" ? "Inventario completo" : inventoryPanel === "actions" ? t("Acciones pendientes") : inventoryPanel === "movement" ? "Registrar movimiento" : inventoryPanel === "stocktake" ? t("Conteo físico") : inventoryPanel === "settings" ? t("Configuración") : inventoryPanel === "replenishment" ? "Lista de compra sugerida" : inventoryPanel === "analysis" ? t("Servicios conectados") : "Historial y auditoría"}</h3>
                    <p>{inventoryPanel === "product" ? "Mantén productos, costos y mínimos actualizados." : inventoryPanel === "products" ? "Busca, edita y registra movimientos sin saturar la vista principal." : inventoryPanel === "actions" ? "Reservas que necesitan revisión, configuración o descuento de materiales." : inventoryPanel === "movement" ? "Suma, resta o fija stock con motivo obligatorio." : inventoryPanel === "stocktake" ? "Cuenta físicamente y ajusta diferencias." : inventoryPanel === "settings" ? "Define cuándo y cómo se descuenta inventario." : inventoryPanel === "replenishment" ? "Productos que conviene comprar primero." : inventoryPanel === "analysis" ? "Materiales por servicio y margen estimado." : "Revisa qué cambió y quién lo cambió."}</p>
                  </div>
                  <button type="button" onClick={() => setInventoryPanel("")} aria-label={t("Cerrar")}>×</button>
                </header>

                <div className="ba-inventory-modal-body">
                  {inventoryPanel === "actions" ? (
                    <section className="ba-inventory-modal-section">
                      <div className="ba-inventory-actions-drawer-head">
                        <div>
                          <span>{t("Operación diaria")}</span>
                          <h4>{inventoryAutomationDashboard.readyWithPlans.length}{" " + t("reservas por revisar")}</h4>
                          <p>{t("Revisa materiales, costos y descuentos pendientes desde un panel dedicado.")}</p>
                        </div>
                        <button type="button" disabled={inventorySaving || !inventoryEnabled || !inventoryAutoDeduct || !canAdjustInventory || !inventoryAutomationDashboard.readyBookings.length} onClick={runInventoryDeductionBridge}>{inventorySaving ? "Procesando..." : "Procesar listos"}</button>
                      </div>

                      <div className="ba-inventory-action-tabs">
                        <button type="button" className="active">{t("Por revisar") + " "}<strong>{inventoryAutomationDashboard.readyWithPlans.length}</strong></button>
                        <button type="button" onClick={() => setInventoryPanel("replenishment")}>{t("Bajo stock")}<strong>{inventoryData.lowStockItems.length}</strong></button>
                        <button type="button" onClick={() => setInventoryPanel("history")}>{t("Errores") + " "}<strong>{inventoryAutomationDashboard.failedBookings.length}</strong></button>
                      </div>

                      <div className="ba-inventory-table ba-inventory-command-table ba-inventory-deduction-table">
                        <div className="ba-inventory-row ba-inventory-row-head">
                          <span>{t("Cliente")}</span>
                          <span>{t("Materiales")}</span>
                          <span>{t("Costo")}</span>
                          <span>{t("Estado")}</span>
                          <span>{t("Acción")}</span>
                        </div>
                        {inventoryAutomationDashboard.readyWithPlans.length ? inventoryAutomationDashboard.readyWithPlans.map(({ booking, plan, client, serviceName, date }) => (
                          <div key={booking.id || booking.bookingId || `${client}-${serviceName}`} className={`ba-inventory-row ba-deduction-row ${plan?.canDeduct ? "ready" : "blocked"}`}>
                            <div className="ba-inventory-name"><strong>{client}</strong><span>{serviceName} · {date ? date.toLocaleString("es-PA", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : t("Sin fecha")}</span></div>
                            <div><span>{t("Materiales")}</span><strong>{plan?.rows?.length || 0}</strong></div>
                            <div><span>{t("Costo")}</span><strong>{MONEY.format(plan?.totalCost || 0)}</strong></div>
                            <div><span>{t("Estado")}</span><strong>{plan?.reason || "Servicio sin materiales configurados"}</strong></div>
                            <div className="ba-inventory-row-actions"><em className={plan?.canDeduct ? "ok" : "low"}>{plan?.canDeduct ? t("Listo") : "Revisar"}</em><button type="button" disabled={inventorySaving || !canAdjustInventory} onClick={() => deductInventoryForBooking(booking, { manual: true, force: true }).then((result) => setInventoryStatus(result.deducted ? "Inventario descontado para la reserva." : result.reason || "Reserva revisada."))}>{plan?.canDeduct ? "Descontar stock" : "Marcar revisado"}</button></div>
                          </div>
                        )) : <div className="ba-empty">{t("Todo listo. No hay reservas completadas/cobradas pendientes de descontar.")}</div>}
                      </div>
                    </section>
                  ) : null}

                  {inventoryPanel === "products" ? (
                    <section className="ba-inventory-modal-section">
                      <div className="ba-inventory-products-toolbar">
                        <input value={inventorySearch} onChange={(e) => setInventorySearch(e.target.value)} placeholder={t("Buscar producto, categoría o proveedor...")} />
                        <button type="button" disabled={!canEditInventory} onClick={() => { resetInventoryForm(); setInventoryPanel("product"); }}>{t("+ Producto")}</button>
                        <button type="button" onClick={() => exportCsv("inventario")}>Exportar CSV</button>
                      </div>

                      <div className="ba-inventory-product-cards">
                        {inventoryData.rows.filter((item) => {
                          const q = String(inventorySearch || "").trim().toLowerCase();
                          if (!q) return true;
                          return `${item.name} ${item.category} ${item.supplier} ${item.unit}`.toLowerCase().includes(q);
                        }).length ? inventoryData.rows.filter((item) => {
                          const q = String(inventorySearch || "").trim().toLowerCase();
                          if (!q) return true;
                          return `${item.name} ${item.category} ${item.supplier} ${item.unit}`.toLowerCase().includes(q);
                        }).map((item) => (
                          <article key={item.id || item.name} className="ba-inventory-product-card">
                            <div className="ba-inventory-product-card-main">
                              <i>{String(item.name || "P").slice(0, 2).toUpperCase()}</i>
                              <div><strong>{item.name}</strong><span>{item.category || t("General")} · {item.supplier || "Sin proveedor"}</span></div>
                              <em className={inventoryStockTone(item)}>{inventoryStockLabel(item)}</em>
                            </div>
                            <dl>
                              <div><dt>{t("Stock")}</dt><dd>{item.currentStock} {item.unit}</dd></div>
                              <div><dt>{t("Mínimo")}</dt><dd>{item.minStock || "—"}</dd></div>
                              <div><dt>{t("Costo")}</dt><dd>{MONEY.format(item.unitCost)}</dd></div>
                              <div><dt>{t("Valor")}</dt><dd>{MONEY.format(item.currentStock * item.unitCost)}</dd></div>
                            </dl>
                            <div className="ba-inventory-product-card-actions">
                              <button type="button" onClick={() => editInventoryItem(item)}>{t("Editar")}</button>
                              <button type="button" disabled={!canAdjustInventory} onClick={() => { setInventoryAdjustForm((prev) => ({ ...prev, itemId: item.id, type: inventoryStockTone(item) === "low" || inventoryStockTone(item) === "critical" ? "restock" : "manual_adjustment", mode: "delta", quantity: "", finalStock: "", reason: "" })); setInventoryPanel("movement"); }}>{t("Movimiento")}</button>
                              <button type="button" className="danger" onClick={() => removeInventoryItem(item)}>{t("Eliminar")}</button>
                            </div>
                          </article>
                        )) : <div className="ba-empty">{t("No hay productos que coincidan con esta búsqueda.")}</div>}
                      </div>
                    </section>
                  ) : null}

                  {inventoryPanel === "product" ? (
                    <form className="ba-inventory-product-designer" onSubmit={saveInventoryItem}>
                      <div className="ba-inventory-product-main">
                        <section className="ba-inventory-form-card">
                          <div className="ba-inventory-form-card-head"><span>{t("Identidad del producto")}</span><strong>{t("Datos principales")}</strong><em>{t("Lo mínimo para que el equipo entienda qué se está controlando.")}</em></div>
                          <label><span>{t("Nombre")}</span><input value={inventoryForm.name} onChange={(e) => setInventoryForm((s) => ({ ...s, name: e.target.value }))} placeholder={t("Ej. Pintura de uñas, guantes, acetona")} /></label>
                          <div className="ba-inventory-form-two">
                            <label><span>{t("Categoría")}</span><input value={inventoryForm.category} onChange={(e) => setInventoryForm((s) => ({ ...s, category: e.target.value }))} placeholder={t("Ej. Uñas, limpieza, café")} /></label>
                            <label><span>{t("Proveedor")}</span><input value={inventoryForm.supplier} onChange={(e) => setInventoryForm((s) => ({ ...s, supplier: e.target.value }))} placeholder={t("Proveedor o tienda")} /></label>
                          </div>
                        </section>

                        <section className="ba-inventory-form-card">
                          <div className="ba-inventory-form-card-head"><span>{t("Stock y costos")}</span><strong>{t("Control operativo")}</strong><em>{t("Estos datos alimentan alertas, valor de inventario y margen por servicio.")}</em></div>
                          <div className="ba-inventory-form-three">
                            <label><span>{t("Stock actual")}</span><input type="number" min="0" step="0.01" value={inventoryForm.currentStock} onChange={(e) => setInventoryForm((s) => ({ ...s, currentStock: e.target.value }))} placeholder="0" /></label>
                            <label><span>{t("Stock mínimo")}</span><input type="number" min="0" step="0.01" value={inventoryForm.minStock} onChange={(e) => setInventoryForm((s) => ({ ...s, minStock: e.target.value }))} placeholder="0" /></label>
                            <label><span>{t("Unidad")}</span><input value={inventoryForm.unit} onChange={(e) => setInventoryForm((s) => ({ ...s, unit: e.target.value }))} placeholder={t("unidad, ml, g, caja")} /></label>
                          </div>
                          <div className="ba-inventory-form-two">
                            <label><span>{t("Costo unitario")}</span><input type="number" min="0" step="0.01" value={inventoryForm.unitCost} onChange={(e) => setInventoryForm((s) => ({ ...s, unitCost: e.target.value }))} placeholder="0.00" /></label>
                            <label><span>{t("Precio venta opcional")}</span><input type="number" min="0" step="0.01" value={inventoryForm.salePrice} onChange={(e) => setInventoryForm((s) => ({ ...s, salePrice: e.target.value }))} placeholder="0.00" /></label>
                          </div>
                          <label><span>{t("Notas")}</span><textarea value={inventoryForm.notes} onChange={(e) => setInventoryForm((s) => ({ ...s, notes: e.target.value }))} placeholder={t("Referencia, color, marca o detalle de compra")} rows={3} /></label>
                        </section>

                        <div className="ba-inventory-form-actions sticky"><button type="submit" disabled={inventorySaving}>{inventorySaving ? t("Guardando...") : inventoryForm.id ? t("Guardar cambios") : t("Agregar producto")}</button>{inventoryForm.id ? <button type="button" className="secondary" onClick={resetInventoryForm} disabled={inventorySaving}>{t("Limpiar formulario")}</button> : null}</div>
                      </div>

                      <aside className="ba-inventory-product-preview">
                        <span>{t("Vista previa")}</span>
                        <div className="ba-inventory-preview-icon">{String(inventoryForm.name || "P").slice(0, 2).toUpperCase()}</div>
                        <h4>{inventoryForm.name || "Nuevo producto"}</h4>
                        <p>{inventoryForm.category || t("Categoría")} · {inventoryForm.supplier || "Sin proveedor"}</p>
                        <dl>
                          <div><dt>{t("Stock")}</dt><dd>{numberFrom(inventoryForm.currentStock)} {inventoryForm.unit || "unidad"}</dd></div>
                          <div><dt>{t("Mínimo")}</dt><dd>{numberFrom(inventoryForm.minStock) || "—"}</dd></div>
                          <div><dt>{t("Costo unit.")}</dt><dd>{MONEY.format(numberFrom(inventoryForm.unitCost))}</dd></div>
                          <div><dt>{t("Valor total")}</dt><dd>{MONEY.format(numberFrom(inventoryForm.currentStock) * numberFrom(inventoryForm.unitCost))}</dd></div>
                        </dl>
                        <em className={numberFrom(inventoryForm.minStock) > 0 && numberFrom(inventoryForm.currentStock) <= numberFrom(inventoryForm.minStock) ? "low" : "ok"}>{numberFrom(inventoryForm.minStock) > 0 && numberFrom(inventoryForm.currentStock) <= numberFrom(inventoryForm.minStock) ? "Requiere reposición" : "Stock saludable"}</em>
                      </aside>
                    </form>
                  ) : null}

                  {inventoryPanel === "movement" ? (
                    (() => {
                      const selectedItem = inventoryData.rows.find((item) => String(item.id) === String(inventoryAdjustForm.itemId));
                      return (
                        <form className="ba-inventory-movement-designer" onSubmit={submitInventoryAdjustment}>
                          <section className="ba-inventory-form-card">
                            <div className="ba-inventory-form-card-head"><span>{t("Movimiento")}</span><strong>{t("Qué cambió")}</strong><em>{t("Registra entradas, salidas o ajustes con trazabilidad.")}</em></div>
                            <label><span>{t("Producto")}</span><select value={inventoryAdjustForm.itemId} onChange={(e) => setInventoryAdjustForm((s) => ({ ...s, itemId: e.target.value }))}><option value="">{t("Selecciona producto")}</option>{inventoryData.rows.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.currentStock} {item.unit}</option>)}</select></label>
                            <div className="ba-inventory-form-two">
                              <label><span>{t("Tipo")}</span><select value={inventoryAdjustForm.type} onChange={(e) => setInventoryAdjustForm((s) => ({ ...s, type: e.target.value }))}><option value="restock">{t("Reposición / entrada")}</option><option value="internal_use">{t("Uso interno")}</option><option value="damage_loss">{t("Daño o pérdida")}</option><option value="return_in">{t("Devolución al inventario")}</option><option value="manual_adjustment">{t("Ajuste manual")}</option></select></label>
                              <label><span>{t("Modo")}</span><select value={inventoryAdjustForm.mode} onChange={(e) => setInventoryAdjustForm((s) => ({ ...s, mode: e.target.value }))}><option value="delta">{t("Sumar/restar cantidad")}</option><option value="final">{t("Fijar stock final")}</option></select></label>
                            </div>
                            {inventoryAdjustForm.mode === "final" ? <label><span>{t("Stock final")}</span><input type="number" min="0" step="0.01" value={inventoryAdjustForm.finalStock} onChange={(e) => setInventoryAdjustForm((s) => ({ ...s, finalStock: e.target.value }))} placeholder={t("Ej. 25")} /></label> : <label><span>{t("Cantidad")}</span><input type="number" min="0" step="0.01" value={inventoryAdjustForm.quantity} onChange={(e) => setInventoryAdjustForm((s) => ({ ...s, quantity: e.target.value }))} placeholder={t("Ej. 5")} /></label>}
                            <label><span>{t("Motivo obligatorio")}</span><textarea rows={3} value={inventoryAdjustForm.reason} onChange={(e) => setInventoryAdjustForm((s) => ({ ...s, reason: e.target.value }))} placeholder={t("Compra nueva, producto dañado, ajuste por conteo, uso interno...")} /></label>
                            <div className="ba-inventory-form-actions"><button type="submit" disabled={inventorySaving || !canAdjustInventory}>{inventorySaving ? t("Guardando...") : "Registrar movimiento"}</button></div>
                          </section>

                          <aside className="ba-inventory-movement-preview">
                            <span>{t("Preview del stock")}</span>
                            {selectedItem ? <><h4>{selectedItem.name}</h4><p>{selectedItem.category || t("General")} · {selectedItem.currentStock} {selectedItem.unit}{" " + t("actuales")}</p></> : <><h4>{t("Selecciona un producto")}</h4><p>{t("Verás el antes y después antes de guardar.")}</p></>}
                            {inventoryAdjustPreview ? <div className={`ba-adjust-preview premium ${inventoryAdjustPreview.tone}`}><span>{t("Resultado esperado")}</span><strong>{inventoryAdjustPreview.stockBefore} {inventoryAdjustPreview.unit} → {inventoryAdjustPreview.stockAfter} {inventoryAdjustPreview.unit}</strong><em>{inventoryAdjustPreview.quantityDelta > 0 ? "+" : ""}{inventoryAdjustPreview.quantityDelta} {inventoryAdjustPreview.unit}</em></div> : <div className="ba-inventory-preview-empty">{t("Completa cantidad o stock final para ver el impacto.")}</div>}
                            {selectedItem ? <dl><div><dt>{t("Stock mínimo")}</dt><dd>{selectedItem.minStock || "—"}</dd></div><div><dt>{t("Costo unit.")}</dt><dd>{MONEY.format(selectedItem.unitCost)}</dd></div><div><dt>{t("Valor actual")}</dt><dd>{MONEY.format(selectedItem.currentStock * selectedItem.unitCost)}</dd></div><div><dt>{t("Estado")}</dt><dd>{inventoryStockLabel(selectedItem)}</dd></div></dl> : null}
                          </aside>
                        </form>
                      );
                    })()
                  ) : null}

                  {inventoryPanel === "stocktake" ? (
                    (() => {
                      const changedCount = Object.entries(stocktakeForm.rows || {}).filter(([itemId, value]) => {
                        if (String(value || "").trim() === "") return false;
                        const item = inventoryData.rows.find((row) => String(row.id) === String(itemId));
                        return item && numberFrom(value) !== numberFrom(item.currentStock);
                      }).length;
                      return (
                        <div className="ba-stocktake-premium">
                          <div className="ba-stocktake-summary">
                            <div><span>{t("Conteo físico")}</span><strong>{changedCount}</strong><em>{" " + t("producto")}{changedCount === 1 ? "" : "s"}{" " + t("con diferencia")}</em></div>
                            <p>{t("Escribe solo los productos que contaste. Loop ajustará únicamente los que tengan diferencia contra el sistema.")}</p>
                          </div>
                          <div className="ba-stocktake-list modal-list">
                            {inventoryData.rows.length ? inventoryData.rows.slice(0, 36).map((item) => {
                              const counted = stocktakeForm.rows?.[item.id] ?? "";
                              const diff = counted === "" ? 0 : numberFrom(counted) - numberFrom(item.currentStock);
                              return <div key={item.id} className="ba-stocktake-row premium"><div><strong>{item.name}</strong><span>{item.category || t("General")}{" " + t("· Sistema:") + " "}{item.currentStock} {item.unit}</span></div><input type="number" min="0" step="0.01" value={counted} onChange={(e) => updateStocktakeCount(item.id, e.target.value)} placeholder={t("Contado")} /><em className={diff < 0 ? "critical" : diff > 0 ? "ok" : "neutral"}>{counted === "" ? "—" : `${diff > 0 ? "+" : ""}${diff}`}</em></div>;
                            }) : <div className="ba-empty">{t("Agrega productos para poder hacer conteo físico.")}</div>}
                          </div>
                          <div className="ba-stocktake-actions premium"><input value={stocktakeForm.notes} onChange={(e) => setStocktakeForm((s) => ({ ...s, notes: e.target.value }))} placeholder={t("Nota del conteo, ej. Conteo viernes")} /><button type="button" onClick={submitStocktake} disabled={stocktakeSaving || inventorySaving || changedCount === 0}>{stocktakeSaving ? "Aplicando..." : "Aplicar conteo"}</button></div>
                        </div>
                      );
                    })()
                  ) : null}

                  {inventoryPanel === "settings" ? (
                    <div className="ba-inventory-settings-grid">
                      <button type="button" className={inventoryEnabled ? "is-active" : ""} disabled={businessSettingsSaving || !canEditInventory} onClick={() => updateBusinessPhase2Settings({ inventoryEnabled: !inventoryEnabled, inventoryPlan: !inventoryEnabled ? "early_access" : "disabled" })}>{inventoryEnabled ? t("Inventario activo") : "Activar inventario"}</button>
                      <button type="button" className={inventoryAutoDeduct ? "is-active" : ""} disabled={businessSettingsSaving || !inventoryEnabled || !canEditInventory} onClick={() => updateBusinessPhase2Settings({ inventoryAutoDeduct: !inventoryAutoDeduct })}>{inventoryAutoDeduct ? "Auto-descuento ON" : "Auto-descuento OFF"}</button>
                      <label><span>{t("Cuándo descontar")}</span><select value={inventoryDeductTrigger} disabled={businessSettingsSaving || !inventoryEnabled || !canEditInventory} onChange={(e) => updateBusinessPhase2Settings({ inventoryDeductTrigger: e.target.value })}><option value="completed">{t("Al completar reserva")}</option><option value="paid">{t("Al cobrar reserva")}</option></select></label>
                      <label><span>{t("Visibilidad trabajador")}</span><select value={workerCompensationVisibility} disabled={businessSettingsSaving} onChange={(e) => updateBusinessPhase2Settings({ workerCompensationVisibility: e.target.value })}><option value="hidden">{t("Trabajador no ve payout")}</option><option value="tips_only">{t("Trabajador ve propinas")}</option><option value="payout_total">{t("Trabajador ve total")}</option><option value="full_breakdown">{t("Trabajador ve desglose")}</option></select></label>
                    </div>
                  ) : null}

                  {inventoryPanel === "replenishment" ? (
                    <section className="ba-replenishment-planner">
                      <div className={`ba-replenishment-hero ba-replenishment-smart ${inventoryPurchasePlan.health.tone}`}>
                        <div>
                          <span>{t("Reposición inteligente")}</span>
                          <h3>{inventoryPurchasePlan.health.label}</h3>
                          <p>{inventoryPurchasePlan.health.hint}</p>
                          <em>{inventoryPurchasePlan.items.length ? `${inventoryPurchasePlan.items.length} producto${inventoryPurchasePlan.items.length === 1 ? "" : "s"} en lista de compra` : "Todo está por encima del mínimo"}</em>
                        </div>
                        <div className="ba-replenishment-hero-total">
                          <strong>{MONEY.format(inventoryPurchasePlan.totalSuggestedCost)}</strong>
                          <small>{t("Compra estimada")}</small>
                          <button type="button" disabled={!inventoryPurchasePlan.items.length} onClick={exportInventoryPurchasePlan}>{t("Exportar lista")}</button>
                        </div>
                      </div>

                      <div className="ba-replenishment-kpis">
                        <article className={inventoryPurchasePlan.nowCount ? "critical" : "success"}><span>{t("Comprar hoy")}</span><strong>{inventoryPurchasePlan.nowCount}</strong><em>{t("Productos críticos o de prioridad alta.")}</em></article>
                        <article className={inventoryPurchasePlan.soonCount ? "warning" : "success"}><span>{t("Comprar pronto")}</span><strong>{inventoryPurchasePlan.soonCount}</strong><em>{t("Por debajo del mínimo o sensibles.")}</em></article>
                        <article><span>{t("Servicios afectados")}</span><strong>{inventoryPurchasePlan.affectedServiceCount}</strong><em>{t("Servicios que dependen de estos materiales.")}</em></article>
                        <article><span>{t("Revenue en riesgo")}</span><strong>{MONEY.format(inventoryPurchasePlan.estimatedRevenueAtRisk)}</strong><em>{t("Precio de servicios conectados a productos en alerta.")}</em></article>
                      </div>

                      <div className="ba-replenishment-toolbar">
                        <div className="ba-replenishment-tabs">
                          <button type="button" className={inventoryReplenishmentFilter === "all" ? "active" : ""} onClick={() => setInventoryReplenishmentFilter("all")}>{t("Todo") + " "}<strong>{inventoryPurchasePlan.items.length}</strong></button>
                          <button type="button" className={inventoryReplenishmentFilter === "now" ? "active" : ""} onClick={() => setInventoryReplenishmentFilter("now")}>{t("Hoy") + " "}<strong>{inventoryPurchasePlan.nowCount}</strong></button>
                          <button type="button" className={inventoryReplenishmentFilter === "soon" ? "active" : ""} onClick={() => setInventoryReplenishmentFilter("soon")}>{t("Pronto") + " "}<strong>{inventoryPurchasePlan.soonCount}</strong></button>
                          <button type="button" className={inventoryReplenishmentFilter === "watch" ? "active" : ""} onClick={() => setInventoryReplenishmentFilter("watch")}>{t("Monitorear") + " "}<strong>{inventoryPurchasePlan.watchCount}</strong></button>
                        </div>
                        <input value={inventoryReplenishmentSearch} onChange={(e) => setInventoryReplenishmentSearch(e.target.value)} placeholder={t("Buscar producto, proveedor o servicio...")} />
                      </div>

                      {inventoryPurchasePlan.supplierGroups.length ? (
                        <div className="ba-replenishment-suppliers">
                          <div className="ba-replenishment-section-head">
                            <div><span>{t("Compra por proveedor")}</span><strong>{t("Agrupa la lista para comprar más rápido")}</strong></div>
                          </div>
                          <div className="ba-replenishment-supplier-grid">
                            {inventoryPurchasePlan.supplierGroups.slice(0, 6).map((group) => (
                              <article key={group.supplier}>
                                <span>{group.supplier}</span>
                                <strong>{MONEY.format(group.totalCost)}</strong>
                                <em>{group.items.length}{" " + t("producto")}{group.items.length === 1 ? "" : "s"} · {group.nowCount}{" " + t("urgente")}{group.nowCount === 1 ? "" : "s"}</em>
                              </article>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      <div className="ba-replenishment-section-head">
                        <div><span>{t("Lista accionable")}</span><strong>{filteredInventoryPurchaseItems.length}{" " + t("producto")}{filteredInventoryPurchaseItems.length === 1 ? "" : "s"}</strong></div>
                        <button type="button" onClick={() => { setInventoryReplenishmentSearch(""); setInventoryReplenishmentFilter("all"); }}>{t("Limpiar filtros")}</button>
                      </div>

                      <div className="ba-replenishment-list">
                        {filteredInventoryPurchaseItems.length ? filteredInventoryPurchaseItems.slice(0, 30).map((item) => (
                          <div key={item.id || item.name} className={`ba-replenishment-row ba-replenishment-row-smart ${item.tone} bucket-${item.buyBucket}`}>
                            <div className="ba-replenishment-main">
                              <div className="ba-replenishment-title-line">
                                <strong>{item.name}</strong>
                                <em className={`bucket ${item.buyBucket}`}>{item.recommendedAction}</em>
                              </div>
                              <span>{item.reason}{" " + t("· Stock") + " "}{item.current} {item.unit}{" " + t("/ mínimo") + " "}{item.min || "—"}{" " + t("· objetivo") + " "}{item.targetStock || "—"}</span>
                              <em>{item.coverageHint}{item.servicesUsing.length ? ` · Usado en ${item.servicesUsing.slice(0, 3).map((svc) => svc.serviceName).join(", ")}${item.servicesUsing.length > 3 ? ` +${item.servicesUsing.length - 3}` : ""}` : " · Sin servicios conectados todavía."}</em>
                            </div>
                            <div className="ba-replenishment-buy">
                              <span>{item.priority}</span>
                              <strong>{item.suggestedQty ? `Comprar ${item.suggestedQty} ${item.unit}` : "Revisar mínimo"}</strong>
                              <em>{item.suggestedCost > 0 ? `${MONEY.format(item.suggestedCost)} · ${item.supplier || "Sin proveedor"}` : `Sin costo unitario · ${item.supplier || "Sin proveedor"}`}</em>
                            </div>
                            <div className="ba-replenishment-impact">
                              <div><span>{t("Cobertura objetivo")}</span><strong>{item.targetCoverage == null ? "—" : `${item.targetCoverage} servicios`}</strong></div>
                              <div><span>{t("Revenue en riesgo")}</span><strong>{MONEY.format(item.revenueAtRisk || 0)}</strong></div>
                            </div>
                            <div className="ba-replenishment-actions">
                              <button type="button" onClick={() => editInventoryItem(item)}>{t("Editar")}</button>
                              <button
                                type="button"
                                className="primary"
                                disabled={!canAdjustInventory || !item.suggestedQty}
                                onClick={() => {
                                  setInventoryAdjustForm((prev) => ({
                                    ...prev,
                                    itemId: item.id,
                                    type: "restock",
                                    mode: "delta",
                                    quantity: item.suggestedQty ? String(item.suggestedQty) : "",
                                    finalStock: "",
                                    reason: `Reposición sugerida por Loop: ${item.recommendedAction}. Stock ${item.current}/${item.min || "sin mínimo"}; objetivo ${item.targetStock || "—"}.`,
                                  }));
                                  setInventoryPanel("movement");
                                }}
                              >{t("Registrar compra")}</button>
                            </div>
                          </div>
                        )) : <div className="ba-empty">{t("No hay compras sugeridas con estos filtros. Configura stock mínimo y materiales por servicio para recomendaciones más inteligentes.")}</div>}
                      </div>
                    </section>
                  ) : null}

                  {inventoryPanel === "analysis" ? (
                    <section className="ba-service-analysis-view">
                      <div className="ba-service-analysis-hero">
                        <div>
                          <span>{t("Servicios conectados")}</span>
                          <h3>{t("Mapa de materiales por servicio")}</h3>
                          <p>{t("Revisa qué servicios descuentan inventario, cuánto cuesta cada reserva y dónde hay riesgo de stock o margen.")}</p>
                        </div>
                        <strong>{inventoryServiceInsights.connected.length}/{inventoryServiceInsights.all.length}</strong>
                      </div>

                      <div className="ba-service-kpi-grid">
                        <article><span>{t("Servicios conectados")}</span><strong>{inventoryServiceInsights.connected.length}</strong><em>{inventoryServiceInsights.unconnected.length}{" " + t("sin materiales")}</em></article>
                        <article><span>{t("Costo configurado")}</span><strong>{MONEY.format(inventoryServiceInsights.totalMaterialCost)}</strong><em>{t("Por una reserva de cada servicio conectado")}</em></article>
                        <article><span>{t("Margen promedio")}</span><strong>{inventoryServiceInsights.avgMarginPct == null ? "—" : `${inventoryServiceInsights.avgMarginPct}%`}</strong><em>{t("Servicios con precio y materiales")}</em></article>
                        <article className={inventoryServiceInsights.risk.length ? "warning" : "success"}><span>{t("Riesgos")}</span><strong>{inventoryServiceInsights.risk.length}</strong><em>{inventoryServiceInsights.critical.length ? `${inventoryServiceInsights.critical.length} crítico(s)` : "Sin críticos"}</em></article>
                      </div>

                      <div className="ba-service-analysis-layout">
                        <aside className="ba-service-analysis-side">
                          <article className="ba-panel">
                            <div className="ba-panel-head compact"><div><span>{t("Categorías")}</span><h3>{t("Valor por categoría")}</h3></div></div>
                            <SimpleList items={sortedInventoryCategories} max={maxInventoryCategoryValue} money empty="Agrega productos para ver valor por categoría." />
                          </article>

                          <article className="ba-panel">
                            <div className="ba-panel-head compact"><div><span>{t("Prioridad")}</span><h3>{t("Qué conectar primero")}</h3></div></div>
                            <div className="ba-service-priority-list">
                              {inventoryServiceInsights.unconnected.slice(0, 5).map((item) => (
                                <div key={item.id || item.name}><strong>{item.name}</strong><span>{t("Sin materiales configurados")}</span></div>
                              ))}
                              {!inventoryServiceInsights.unconnected.length ? <div className="ba-empty slim">{t("Todos los servicios tienen materiales o no hay servicios pendientes.")}</div> : null}
                            </div>
                          </article>
                        </aside>

                        <article className="ba-panel ba-panel-large ba-service-materials-panel">
                          <div className="ba-panel-head compact">
                            <div><span>{t("Servicios")}</span><h3>{t("Costo, margen y stock")}</h3></div>
                            <strong>{filteredInventoryServiceInsights.length}</strong>
                          </div>
                          <div className="ba-service-analysis-toolbar">
                            <input value={inventoryServiceSearch} onChange={(e) => setInventoryServiceSearch(e.target.value)} placeholder={t("Buscar servicio o material...")} />
                            <button type="button" onClick={() => setInventoryServiceSearch("")}>{t("Limpiar")}</button>
                          </div>

                          <div className="ba-service-materials-table premium">
                            {filteredInventoryServiceInsights.length ? filteredInventoryServiceInsights.map((item) => (
                              <div key={item.id || item.name} className={`ba-service-material-card ${item.tone}`}>
                                <div className="ba-service-material-top">
                                  <div>
                                    <strong>{item.name}</strong>
                                    <span>{item.connected ? item.rows.map((row) => `${row.quantityUsed} ${row.unit} ${row.itemName}`).join(" · ") : t("Sin materiales configurados")}</span>
                                  </div>
                                  <em>{item.label}</em>
                                </div>

                                <div className="ba-service-material-stats">
                                  <div><span>{t("Precio")}</span><strong>{item.price > 0 ? MONEY.format(item.price) : "—"}</strong></div>
                                  <div><span>{t("Costo mat.")}</span><strong>{MONEY.format(item.materialCost)}</strong></div>
                                  <div><span>{t("Margen est.")}</span><strong>{item.price > 0 ? MONEY.format(item.margin) : "—"}</strong></div>
                                  <div><span>{t("Margen %")}</span><strong>{item.marginPct == null ? "—" : `${item.marginPct}%`}</strong></div>
                                </div>

                                {item.connected ? (
                                  <div className="ba-service-material-lines">
                                    {item.rows.map((row) => (
                                      <span key={`${item.id}-${row.itemId}`} className={row.missing || row.insufficient ? "critical" : row.nearMinimum ? "warning" : "ok"}>
                                        {row.itemName}: {row.quantityUsed} {row.unit}{" " + t("· stock después") + " "}{row.missing ? "—" : `${row.stockAfter} ${row.unit}`}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="ba-service-material-empty">{t("Conecta este servicio desde Servicios → Editar → Materiales por servicio.")}</p>
                                )}

                                <div className="ba-service-material-actions">
                                  <p>{item.hint}</p>
                                  <button type="button" onClick={() => setInventoryPanel("")}>{t("Configurar en Servicios")}</button>
                                </div>
                              </div>
                            )) : <div className="ba-empty">{t("No hay servicios que coincidan con esta búsqueda.")}</div>}
                          </div>
                        </article>
                      </div>
                    </section>
                  ) : null}

                  {inventoryPanel === "history" ? (
                    <section className="ba-main-grid history-modal-grid">
                      <article className="ba-panel ba-panel-large"><div className="ba-panel-head compact"><div><span>{t("Historial")}</span><h3>{t("Últimos movimientos")}</h3></div><strong>{inventoryData.movements.length}</strong></div><div className="ba-inventory-table">{inventoryData.movements.length ? inventoryData.movements.slice(0, 20).map((m) => <div key={m.id || `${m.itemId}-${m.createdAt?.getTime?.() || Math.random()}`} className="ba-inventory-row ba-movement-row"><div className="ba-inventory-name"><strong>{m.itemName}</strong><span>{inventoryMovementTypeLabel(m.type)} · {m.reason || "Sin nota"}</span></div><div><span>{t("Cambio")}</span><strong>{m.quantity > 0 ? "+" : ""}{m.quantity} {m.unit}</strong></div><div><span>{t("Antes")}</span><strong>{m.stockBefore}</strong></div><div><span>{t("Después")}</span><strong>{m.stockAfter}</strong></div><div><span>{t("Costo ref.")}</span><strong>{MONEY.format(m.totalCost || Math.abs(m.quantity) * m.unitCost)}</strong></div><div className="ba-inventory-row-actions"><em className={m.quantity < 0 ? "low" : "ok"}>{inventoryMovementTypeLabel(m.type)}</em></div></div>) : <div className="ba-empty">{t("Todavía no hay movimientos. Las ventas, ajustes y conteos aparecerán aquí.")}</div>}</div></article>
                      {canViewAuditLogs ? <article className="ba-panel ba-panel-large"><div className="ba-panel-head compact"><div><span>{t("Auditoría")}</span><h3>{t("Eventos recientes")}</h3></div><strong>{auditData.rows.length}/{auditData.allRows.length}</strong></div><div className="ba-filter-card ba-audit-filters"><label><span>{t("Buscar")}</span><input value={auditFilterSearch} onChange={(e) => setAuditFilterSearch(e.target.value)} placeholder={t("Reserva, cliente, producto, actor...")} /></label><label><span>{t("Tipo")}</span><select value={auditFilterType} onChange={(e) => setAuditFilterType(e.target.value)}><option value="all">{t("Todos los eventos")}</option>{auditData.typeOptions.map((type) => <option key={type} value={type}>{auditEventTypeLabel(type)}</option>)}</select></label><label><span>{t("Actor")}</span><select value={auditFilterActor} onChange={(e) => setAuditFilterActor(e.target.value)}><option value="all">{t("Todos")}</option>{auditData.actorOptions.map((actor) => <option key={actor} value={actor}>{actor}</option>)}</select></label><label><span>{t("Desde")}</span><input type="date" value={auditFilterFrom} onChange={(e) => setAuditFilterFrom(e.target.value)} /></label><label><span>{t("Hasta")}</span><input type="date" value={auditFilterTo} onChange={(e) => setAuditFilterTo(e.target.value)} /></label><div className="ba-audit-toggle-row"><button type="button" className={auditOnlyErrors ? "active" : ""} onClick={() => setAuditOnlyErrors((v) => !v)}>{t("Solo errores")}</button><button type="button" className={auditOnlyInventory ? "active" : ""} onClick={() => setAuditOnlyInventory((v) => !v)}>{t("Inventario")}</button><button type="button" className={auditOnlyPayments ? "active" : ""} onClick={() => setAuditOnlyPayments((v) => !v)}>{t("Pagos")}</button></div></div><div className="ba-inventory-table">{auditData.rows.length ? auditData.rows.slice(0, 24).map((log) => <div key={log.id || `${log.type}-${log.createdAt?.getTime?.() || Math.random()}`} className={`ba-inventory-row ba-movement-row ba-audit-row ${log.severity === "warning" ? "warning" : ""}`}><div className="ba-inventory-name"><strong>{log.label}</strong><span>{auditLogSummary(log)}{log.bookingClientName ? ` · ${log.bookingClientName}` : ""}{log.bookingServiceName ? ` · ${log.bookingServiceName}` : ""}</span></div><div><span>{t("Reserva")}</span><strong>{log.bookingId ? String(log.bookingId).slice(0, 8) : "—"}</strong></div><div><span>{t("Actor")}</span><strong>{auditLogActorLabel(log)}</strong></div><div><span>{t("Fecha")}</span><strong>{log.createdAt ? log.createdAt.toLocaleString("es-PA", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</strong></div><div className="ba-inventory-row-actions"><em className={log.severity === "warning" ? "low" : "ok"}>{auditEventTypeLabel(log.type)}</em></div></div>) : <div className="ba-empty">{t("No hay eventos que coincidan con estos filtros.")}</div>}</div></article> : null}
                    </section>
                  ) : null}
                </div>
              </section>
            </div>
          ) : null}
        </section>
      ) : null}

      {activeView === "traffic" && canViewReports ? (
        <section className="ba-traffic-module">
          <section className="ba-executive-grid" aria-label={t("Tráfico y conversión")}>
            <MetricCard label={t("Visitas perfil")} value={hasTracking ? data.pageViews : "—"} hint={hasTracking ? `${data.uniqueVisitors || data.pageViews} visitantes únicos` : "Esperando visitas nuevas"} tone="primary" />
            <MetricCard label={t("Clicks reservar")} value={hasTracking ? data.reserveClicks : "—"} hint="CTA, cards o final de página" />
            <MetricCard label={t("Reservas iniciadas")} value={hasTracking ? data.bookingStarts : "—"} hint="Entraron al flujo" />
            <MetricCard label={t("Reservas completadas")} value={hasTracking ? data.completedForConversion : data.completedBookings.length} hint="Solicitud enviada" />
            <MetricCard label={t("Interés")} value={hasTracking ? `${data.intentRate}%` : "—"} hint="Visitas → reservar" />
            <MetricCard label={t("Conversión")} value={hasTracking ? `${data.conversionRate}%` : "—"} hint="Visitas → reserva completa" />
          </section>
          <section className="ba-main-grid">
            <article className="ba-panel ba-panel-large">
              <div className="ba-panel-head compact"><div><span>{t("Funnel comercial")}</span><h3>{t("De visita a reserva")}</h3></div><em className={`ba-soon ${hasTracking ? "is-live" : ""}`}>{hasTracking ? t("Activo") : "Sin datos aún"}</em></div>
              {hasTracking ? (
                <div className="ba-list-bars">
                  <BarRow label={t("1. Vieron el perfil")} value={data.pageViews} max={Math.max(data.pageViews, 1)} />
                  <BarRow label={t("2. Tocaron reservar")} value={data.reserveClicks} max={Math.max(data.pageViews, data.reserveClicks, 1)} subtitle={`${data.intentRate}% de las visitas`} />
                  <BarRow label={t("3. Iniciaron el flujo")} value={data.bookingStarts} max={Math.max(data.pageViews, data.bookingStarts, 1)} subtitle={`${data.startRate}% de los clicks`} />
                  <BarRow label={t("4. Completaron reserva")} value={data.completedForConversion} max={Math.max(data.pageViews, data.completedForConversion, 1)} subtitle={`${data.completionRate}% desde inicio`} />
                </div>
              ) : (
                <div className="ba-tracking-empty"><strong>{t("Aún no hay tráfico registrado en este periodo")}</strong><p>{trafficReadinessText(hasTracking, data.completedFromTracking)}</p><div><span>{ownerFriendlyTrafficLabel("business_page_view")}</span><span>{ownerFriendlyTrafficLabel("booking_opened")}</span><span>{ownerFriendlyTrafficLabel("service_clicked")}</span><span>{ownerFriendlyTrafficLabel("booking_completed")}</span></div></div>
              )}
            </article>
            <article className="ba-panel"><div className="ba-panel-head compact"><div><span>{t("Fuentes")}</span><h3>{t("De dónde vienen")}</h3></div></div><SimpleList items={sortedSources} max={maxSources} empty="Aún no hay fuente registrada." /></article>
            <article className="ba-panel"><div className="ba-panel-head compact"><div><span>{t("Servicios")}</span><h3>{t("Interés por servicio")}</h3></div></div><SimpleList items={sortedServiceInterest} max={maxServiceInterest} empty="Todavía no hay clicks o vistas de servicios." /></article>
            <article className="ba-panel"><div className="ba-panel-head compact"><div><span>{t("Clientes")}</span><h3>{t("Segmentos")}</h3></div></div><SimpleList items={sortedAges} max={maxAge} empty="No hay edad registrada todavía." /><div className="ba-customer-split"><div><span>{t("Nuevos")}</span><strong>{data.newCustomers || "—"}</strong></div><div><span>{t("Recurrentes")}</span><strong>{data.recurrentCustomers || "—"}</strong></div></div></article>
          </section>
        </section>
      ) : null}

      {activeView === "insights" && canViewReports ? (
        <section className="ba-insights-module">
          <div className="ba-insights-hero">
            <div>
              <span>Loop Advisor</span>
              <h3>{t("Qué haría esta semana")}</h3>
              <p>{t("Priorizamos oportunidades con impacto en caja, conversión, margen, inventario y operación.")}</p>
            </div>
            <strong>{smartInsights.length}</strong>
          </div>

          <section className="ba-executive-grid insights" aria-label={t("Resumen de oportunidades")}>
            <MetricCard label={t("Prioridad alta")} value={smartInsights.filter((x) => x.priority <= 2).length} hint="Acciones de mayor impacto" tone="warning" />
            <MetricCard label={t("Conversión")} value={hasTracking ? `${data.conversionRate}%` : "—"} hint="Visitas a reserva" />
            <MetricCard label={t("Margen neto")} value={data.collectedRevenue ? `${Math.round((data.businessNetRevenue / data.collectedRevenue) * 100)}%` : "—"} hint="Neto / cobrado" />
            <MetricCard label={t("Bajo stock")} value={inventoryData.lowStockItems.length} hint="Productos críticos" tone={inventoryData.lowStockItems.length ? "warning" : "success"} />
          </section>

          <section className="ba-main-grid insights-grid">
            <article className="ba-panel ba-panel-large">
              <div className="ba-panel-head compact"><div><span>{t("Plan de acción")}</span><h3>{t("Recomendaciones priorizadas")}</h3></div><em className="ba-soon is-live">{t("Automático")}</em></div>
              <div className="ba-smart-list">
                {smartInsights.map((item, idx) => (
                  <div key={`${item.title}-${idx}`} className={`ba-smart-card ${item.tone || "info"}`}>
                    <div className="ba-smart-rank">{idx + 1}</div>
                    <div className="ba-smart-copy">
                      <div className="ba-smart-top"><span>{item.area}</span>{item.metric ? <strong>{item.metric}</strong> : null}</div>
                      <h4>{item.title}</h4>
                      <p>{item.body}</p>
                      {item.action ? <em>{item.action}</em> : null}
                    </div>
                  </div>
                ))}
              </div>
            </article>

            <article className="ba-panel">
              <div className="ba-panel-head compact"><div><span>{t("Lectura rápida")}</span><h3>{t("Top 4 acciones")}</h3></div></div>
              <div className="ba-insights-list">
                {topSmartInsights.map((item, idx) => <div className="ba-insight" key={`${item.title}-short`}><b>{idx + 1}</b><span>{item.title}</span></div>)}
              </div>
            </article>

            <article className="ba-panel">
              <div className="ba-panel-head compact"><div><span>{t("Salud del negocio")}</span><h3>{t("Señales clave")}</h3></div></div>
              <div className="ba-health-grid">
                <div><span>{t("Cobro")}</span><strong>{data.collectionRate || 0}%</strong><em>{data.pendingRevenue > 0 ? "Mejorable" : "OK"}</em></div>
                <div><span>{t("Conversión")}</span><strong>{hasTracking ? `${data.conversionRate}%` : "—"}</strong><em>{hasTracking ? "Medido" : "Sin data"}</em></div>
                <div><span>{t("Margen")}</span><strong>{data.collectedRevenue ? `${Math.round((data.businessNetRevenue / data.collectedRevenue) * 100)}%` : "—"}</strong><em>{t("Neto estimado")}</em></div>
                <div><span>{t("Inventario")}</span><strong>{inventoryData.lowStockItems.length}</strong><em>{t("Bajo stock")}</em></div>
              </div>
            </article>
          </section>
        </section>
      ) : null}



        </main>
      </div>


      {membershipDetailView ? (
        <div
          className="ba-membership-drawer-layer"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeMembershipDetail();
          }}
        >
          <aside
            className="ba-membership-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ba-membership-drawer-title"
          >
            <header className="ba-membership-drawer-head">
              <div>
                <span>{t("Membresías")}</span>
                <h3 id="ba-membership-drawer-title">
                  {membershipDetailView === "record"
                    ? t("Detalle de membresía")
                    : membershipDetailView === "overview"
                      ? "Detalle operativo"
                      : membershipDetailView === "memberships"
                        ? "Todas las membresías"
                      : membershipDetailView === "calendar"
                        ? "Renovaciones y vencimientos"
                        : membershipDetailView === "attention"
                          ? "Seguimientos"
                          : "Historial de actividad"}
                </h3>
                <p>
                  {membershipDetailView === "record"
                    ? `${membershipSelectedRecord?.customerName || "Cliente"} · ${membershipSelectedRecord?.planName || "Membresía"}`
                    : membershipDetailView === "overview"
                      ? "Desglose de salud, renovación, uso y movimiento reciente de tu base."
                      : membershipDetailView === "memberships"
                        ? "Historial completo por membresía: cliente, estado, antigüedad, próxima fecha, uso y valor."
                      : membershipDetailView === "calendar"
                        ? "Renovaciones automáticas, vencimientos y cierres de período de los próximos 30 días."
                        : membershipDetailView === "attention"
                          ? "Clientes que requieren revisión, ordenados por prioridad."
                          : "Movimientos de membresías con búsqueda, filtros y paginación."}
                </p>
              </div>
              <button type="button" onClick={closeMembershipDetail} aria-label={t("Cerrar panel")}>×</button>
            </header>

            {!["overview", "record"].includes(membershipDetailView) ? (
              <div className="ba-membership-drawer-toolbar">
                <form className="ba-membership-drawer-search" onSubmit={applyMembershipDetailSearch}>
                  <input
                    type="search"
                    value={membershipDetailSearch}
                    onChange={(event) => setMembershipDetailSearch(event.target.value)}
                    placeholder={membershipDetailView === "activity" ? "Buscar cliente, plan o movimiento" : "Buscar cliente o plan"}
                    aria-label={t("Buscar en membresías")}
                  />
                  <button type="submit">{t("Buscar")}</button>
                </form>

                <div className="ba-membership-drawer-filters">
                  {membershipDetailView === "memberships" ? (
                    <label>
                      <span>{t("Estado")}</span>
                      <select value={membershipDetailFilter} onChange={(event) => changeMembershipDetailFilter(event.target.value)}>
                        <option value="all">{t("Todas")}</option>
                        <option value="active">{t("Activas")}</option>
                        <option value="grace">{t("En gracia")}</option>
                        <option value="paused">{t("Pausadas")}</option>
                        <option value="expired">{t("Finalizadas")}</option>
                        <option value="cancelled">{t("Canceladas")}</option>
                      </select>
                    </label>
                  ) : null}
                {membershipDetailView === "calendar" ? (
                  <label>
                    <span>{t("Tipo")}</span>
                    <select value={membershipDetailFilter} onChange={(event) => changeMembershipDetailFilter(event.target.value)}>
                      <option value="all">{t("Todos los eventos")}</option>
                      <option value="automatic">{t("Renovaciones automáticas")}</option>
                      <option value="manual_or_expiry">{t("Vencimientos")}</option>
                      <option value="ending">{t("Finalizan al cierre")}</option>
                    </select>
                  </label>
                ) : null}

                {membershipDetailView === "attention" ? (
                  <label>
                    <span>{t("Prioridad")}</span>
                    <select value={membershipDetailFilter} onChange={(event) => changeMembershipDetailFilter(event.target.value)}>
                      <option value="all">{t("Todos los seguimientos")}</option>
                      <option value="critical">{t("Críticos")}</option>
                      <option value="grace">{t("En gracia")}</option>
                      <option value="unused">{t("Sin uso")}</option>
                      <option value="cancel_at_period_end">{t("Finalizan")}</option>
                    </select>
                  </label>
                ) : null}

                {membershipDetailView === "activity" ? (
                  <>
                    <label>
                      <span>{t("Movimiento")}</span>
                      <select value={membershipDetailFilter} onChange={(event) => changeMembershipDetailFilter(event.target.value)}>
                        <option value="all">{t("Todos")}</option>
                        <option value="membership_renewed">{t("Renovaciones")}</option>
                        <option value="membership_granted">{t("Activaciones")}</option>
                        <option value="credit_used">{t("Créditos usados")}</option>
                        <option value="credit_adjusted">{t("Ajustes de créditos")}</option>
                        <option value="membership_cancelled">{t("Cancelaciones")}</option>
                        <option value="membership_expired">{t("Vencimientos")}</option>
                      </select>
                    </label>
                    <label>
                      <span>{t("Periodo")}</span>
                      <select value={membershipDetailRange} onChange={(event) => changeMembershipDetailRange(event.target.value)}>
                        <option value={30}>{t("Últimos 30 días")}</option>
                        <option value={90}>{t("Últimos 90 días")}</option>
                        <option value={0}>{t("Todo el historial")}</option>
                      </select>
                    </label>
                  </>
                ) : null}
                </div>
              </div>
            ) : null}

            {membershipDetailLocalPreview ? (
              <div className="ba-membership-drawer-preview-note">{t("Vista previa local · la paginación completa se activará cuando publiquemos el endpoint R5.2.")}</div>
            ) : null}

            {membershipDetailError && !membershipDetailLocalPreview ? (
              <div className="ba-alert warning"><span>{membershipDetailError}</span></div>
            ) : null}

            <div className="ba-membership-drawer-body">
              {membershipDetailView === "record" ? (
                membershipSelectedRecord ? (
                  <div className="ba-membership-record-detail">
                    <button type="button" className="ba-membership-record-back" onClick={returnFromMembershipRecord}>
                      {membershipRecordOrigin === "memberships" ? "← Volver a todas las membresías" : "← Volver al resumen"}
                    </button>
                    <section className="ba-membership-record-identity">
                      <div>
                        <span>{t("Cliente")}</span>
                        <strong>{membershipSelectedRecord.customerName || "Cliente sin identificar"}</strong>
                        <em>{membershipSelectedRecord.planName || t("Membresía")}</em>
                        <span>{membershipSelectedRecord.customerEmail || "Sin correo registrado"}</span>
                        <span>{membershipSelectedRecord.customerPhone || "Sin teléfono registrado"}</span>
                      </div>
                      <em className={`ba-membership-status ${membershipRecordStatusTone(membershipSelectedRecord)}`}>{membershipRecordStatusLabel(membershipSelectedRecord)}</em>
                    </section>
                    {["automatic", "ending"].includes(membershipSelectedRecord.renewalMode) && <BusinessMembershipRenewal key={membershipSelectedRecord.membershipId || membershipSelectedRecord.id} businessId={businessId} membershipId={membershipSelectedRecord.membershipId || membershipSelectedRecord.id} onCompleted={() => { setReloadKey((x) => x + 1); setMembershipSelectedRecord((row) => ({ ...row, cancelAtPeriodEnd: true, renewalMode: "ending" })); }} />}
                    <section className="ba-membership-record-grid">
                      <div><span>{t("Desde")}</span><strong>{membershipSelectedRecord.startedAt ? membershipFormatDate(membershipSelectedRecord.startedAt) : "Por confirmar"}</strong><em>{membershipSelectedRecord.daysActive != null ? `${membershipSelectedRecord.daysActive} día${Number(membershipSelectedRecord.daysActive) === 1 ? "" : "s"} activa` : "Antigüedad por confirmar"}</em></div>
                      <div><span>{t("Próxima fecha")}</span><strong>{membershipRecordNextLabel(membershipSelectedRecord)}</strong><em>{membershipSelectedRecord.renewalMode && membershipSelectedRecord.renewalMode !== "none" ? membershipRenewalModeLabel(membershipSelectedRecord.renewalMode) : "Sin renovación programada"}</em></div>
                      <div><span>Uso</span><strong>{membershipRecordUsageLabel(membershipSelectedRecord)}</strong><em>{numberFrom(membershipSelectedRecord.creditsIncluded) > 0 ? `${membershipSelectedRecord.creditsRemaining || 0} crédito${Number(membershipSelectedRecord.creditsRemaining || 0) === 1 ? "" : "s"} disponible${Number(membershipSelectedRecord.creditsRemaining || 0) === 1 ? "" : "s"}` : "Plan sin créditos"}</em></div>
                      <div><span>{t("Valor")}</span><strong>{membershipRecordValueLabel(membershipSelectedRecord)}</strong><em>{membershipSelectedRecord.paymentStatus ? `Pago: ${membershipSelectedRecord.paymentStatus}` : "Sin alerta de pago registrada"}</em></div>
                      <div><span>{t("Última renovación")}</span><strong>{membershipSelectedRecord.lastRenewalAt ? membershipFormatDate(membershipSelectedRecord.lastRenewalAt) : "Sin renovación registrada"}</strong><em>{t("Último ciclo renovado visible")}</em></div>
                      <div><span>{t("Beneficio")}</span><strong>{numberFrom(membershipSelectedRecord.creditsIncluded) > 0 ? `${membershipSelectedRecord.creditsUsed || 0}/${membershipSelectedRecord.creditsIncluded || 0} usados` : "Sin créditos"}</strong><em>{numberFrom(membershipSelectedRecord.creditsIncluded) > 0 ? `${membershipSelectedRecord.creditsRemaining || 0} restantes` : "Puede ser descuento u otro beneficio"}</em></div>
                    </section>
                  </div>
                ) : (
                  <div className="ba-membership-drawer-empty"><strong>{t("Sin detalle")}</strong><span>{t("No encontramos información de esta membresía.")}</span></div>
                )
              ) : membershipDetailView === "overview" ? (
                <div className="ba-membership-operational-detail">
                  <section>
                    <header><span>Base</span><strong>{membershipAnalytics?.activeMemberships || 0}{" " + t("activas")}</strong></header>
                    <div><span>{t("Membresías históricas")}</span><strong>{membershipAnalytics?.totalMemberships || 0}</strong><em>{t("Total registrado en el negocio.")}</em></div>
                    <div><span>{t("Miembros activos")}</span><strong>{membershipAnalytics?.activeCustomers || 0}</strong><em>{t("Clientes únicos con al menos una membresía activa.")}</em></div>
                    <div><span>{t("Valor activo")}</span><strong>{membershipMoney(membershipAnalytics?.activeValue || 0)}</strong><em>{t("Suma del precio vigente; no representa cobro confirmado.")}</em></div>
                  </section>

                  <section>
                    <header><span>{t("Renovación")}</span><strong>{membershipAnalytics?.healthSummary?.autoRenewing || 0}{" " + t("auto-renuevan")}</strong></header>
                    <div><span>{t("Próximas · 30 días")}</span><strong>{membershipAutomaticRenewalCount(membershipAnalytics)}</strong><em>{t("Renovaciones automáticas previstas en la ventana actual.")}</em></div>
                    <div><span>{t("En gracia")}</span><strong>{membershipAnalytics?.healthSummary?.grace || 0}</strong><em>{t("Acceso temporal mientras se resuelve la renovación.")}</em></div>
                    <div><span>{t("Finalizan al cierre")}</span><strong>{membershipAnalytics?.healthSummary?.cancelAtPeriodEnd || 0}</strong><em>{t("Membresías activas que no continuarán después del período.")}</em></div>
                    <div><span>{t("Pago / método")}</span><strong>{membershipHealthIssueCount(membershipAnalytics)}</strong><em>{t("Incluye pago, fuente y conciliación que requieren revisión.")}</em></div>
                  </section>

                  <section>
                    <header><span>Uso</span><strong>{membershipAnalytics?.activeUsageRate || 0}%</strong></header>
                    <div><span>{t("Créditos usados")}</span><strong>{membershipAnalytics?.activeCreditsUsed || 0}</strong><em>{t("Sobre") + " "}{membershipAnalytics?.activeCreditsIncluded || 0}{" " + t("créditos incluidos en la base activa.")}</em></div>
                    <div><span>{t("Créditos disponibles")}</span><strong>{membershipAnalytics?.activeCreditsRemaining || 0}</strong><em>{t("Saldo actual de beneficios por crédito.")}</em></div>
                    <div><span>{t("Requieren atención")}</span><strong>{membershipAnalytics?.needsAttention?.length || 0}</strong><em>{t("Seguimientos accionables detectados por Loop.")}</em></div>
                  </section>

                  <section>
                    <header><span>{t("Últimos 30 días")}</span><strong>{t("Movimiento reciente")}</strong></header>
                    <div><span>{t("Nuevas membresías")}</span><strong>{membershipAnalytics?.newMemberships30d || 0}</strong><em>{t("Activadas dentro de los últimos 30 días.")}</em></div>
                    <div><span>{t("Renovaciones")}</span><strong>{membershipAnalytics?.renewals30d || 0}</strong><em>{t("Renovaciones históricas registradas en la ventana.")}</em></div>
                    <div><span>{t("Créditos usados")}</span><strong>{membershipAnalytics?.creditsUsed30d || 0}</strong><em>{t("Consumo de beneficios registrado en los últimos 30 días.")}</em></div>
                  </section>
                </div>
              ) : membershipDetailLoading ? (
                <div className="ba-membership-drawer-loading">{t("Cargando información...")}</div>
              ) : membershipDetailRows.length ? (
                <>
                  {membershipDetailView === "memberships" ? (
                    <div className="ba-membership-drawer-table memberships">
                      <div className="ba-membership-drawer-table-head">
                        <span>{t("Cliente / plan")}</span>
                        <span>{t("Estado")}</span>
                        <span>{t("Desde")}</span>
                        <span>{t("Próxima fecha")}</span>
                        <span>Uso</span>
                        <span>{t("Valor")}</span>
                      </div>
                      {membershipDetailRows.map((row) => (
                        <button
                          type="button"
                          key={row.membershipId || row.id}
                          className="ba-membership-drawer-table-row memberships"
                          onClick={() => openMembershipRecord(row, "memberships")}
                          aria-label={`Ver detalle de ${row.customerName || "cliente"} · ${row.planName || "membresía"}`}
                        >
                          <div>
                            <strong>{row.customerName || "Cliente sin identificar"}</strong>
                            <span>{row.planName || t("Membresía")}</span>
                          </div>
                          <em className={`ba-membership-status ${membershipRecordStatusTone(row)}`}>{membershipRecordStatusLabel(row)}</em>
                          <div>
                            <strong>{row.startedAt ? membershipFormatDate(row.startedAt) : "Por confirmar"}</strong>
                            <span>{row.daysActive != null ? `${row.daysActive} día${Number(row.daysActive) === 1 ? "" : "s"} activa` : "Histórico"}</span>
                          </div>
                          <div>
                            <strong>{membershipRecordNextLabel(row)}</strong>
                            <span>{row.renewalMode && row.renewalMode !== "none" ? membershipRenewalModeLabel(row.renewalMode) : "—"}</span>
                          </div>
                          <div>
                            <strong>{membershipRecordUsageLabel(row)}</strong>
                            <span>{numberFrom(row.creditsIncluded) > 0 ? `${row.creditsRemaining || 0} disp.` : "Sin créditos"}</span>
                          </div>
                          <div>
                            <strong>{membershipRecordValueLabel(row)}</strong>
                            <span>{row.paymentStatus || ""}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : null}

                  {membershipDetailView === "calendar" ? (
                    <div className="ba-membership-drawer-table calendar">
                      <div className="ba-membership-drawer-table-head">
                        <span>{t("Cliente / plan")}</span><span>{t("Fecha")}</span><span>{t("Valor")}</span><span>{t("Estado")}</span>
                      </div>
                      {membershipDetailRows.map((row) => (
                        <div key={row.membershipId || row.id} className="ba-membership-drawer-table-row">
                          <div><strong>{row.customerName || "Cliente sin identificar"}</strong><span>{row.planName || t("Membresía")}</span></div>
                          <div><strong>{membershipFormatDate(row.renewalDate)}</strong><span>{membershipRenewalDateLabel(row.renewalMode)}</span></div>
                          <div><strong>{membershipRenewalAmountLabel(row)}</strong><span>{row.daysLeft != null ? `${row.daysLeft} día${Number(row.daysLeft) === 1 ? "" : "s"}` : "Próximo"}</span></div>
                          <em className={`ba-membership-renewal-mode ${row.renewalMode || "manual_or_expiry"}`}>{membershipRenewalModeLabel(row.renewalMode)}</em>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {membershipDetailView === "attention" ? (
                    <div className="ba-membership-drawer-attention">
                      {membershipDetailRows.map((row) => (
                        <div key={`${row.kind || row.reason}-${row.membershipId}`} className={`ba-membership-attention-v2 ${row.severity || "info"} ${row.kind || ""}`}>
                          <b>{membershipAttentionLabel(row)}</b>
                          <div><strong>{row.customerName || "Cliente sin identificar"}</strong><span>{membershipAttentionDetail(row)}</span></div>
                          <em>{row.creditsRemaining != null ? `${row.creditsRemaining} crédito${Number(row.creditsRemaining) === 1 ? "" : "s"} disp.` : "Revisar"}</em>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {membershipDetailView === "activity" ? (
                    <div className="ba-membership-drawer-activity">
                      <div className="ba-membership-drawer-activity-head"><span>{t("Fecha")}</span><span>{t("Cliente")}</span><span>{t("Movimiento")}</span><span>{t("Plan / detalle")}</span></div>
                      {membershipDetailRows.map((row) => (
                        <div key={row.id} className="ba-membership-drawer-activity-row">
                          <time>{membershipFormatDate(row.createdAt, row.sortMs, { withTime: true })}</time>
                          <strong>{row.customerName || "Cliente sin identificar"}</strong>
                          <div><b>{membershipActivityTypeLabel(row.type || row.activityType)}</b><span>{row.title || "Actividad de membresía"}</span></div>
                          <div><strong>{row.planName || row.membershipPlanName || t("Membresía")}</strong><span>{row.serviceName || ""}{row.creditsAfter != null ? `${row.serviceName ? " · " : ""}${row.creditsAfter} crédito${Number(row.creditsAfter) === 1 ? "" : "s"} disp.` : ""}</span></div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="ba-membership-drawer-empty"><strong>{t("Sin resultados")}</strong><span>{t("Prueba otra búsqueda o cambia los filtros.")}</span></div>
              )}
            </div>
            {!["overview", "record"].includes(membershipDetailView) ? (
              <footer className="ba-membership-drawer-foot">
                <span>{t("Página") + " "}{membershipDetailPage}
                {membershipDetailTotal !== null ? ` · ${membershipDetailTotal} resultado${Number(membershipDetailTotal) === 1 ? "" : "s"}` : ""}
              </span>
              <div>
                <button type="button" disabled={!membershipDetailCursorStack.length || membershipDetailLoading} onClick={goMembershipDetailPrevious}>{t("Anterior")}</button>
                <button type="button" disabled={!membershipDetailHasMore || membershipDetailLoading} onClick={goMembershipDetailNext}>{t("Siguiente")}</button>
                </div>
              </footer>
            ) : null}
          </aside>
        </div>
      ) : null}

      {crmActionModal ? (
        <div className="ba-crm-action-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeCrmActionModal(); }}>
          <div className="ba-crm-action-modal" role="dialog" aria-modal="true" aria-labelledby="ba-crm-action-title">
            <div className="ba-crm-action-head">
              <div>
                <span>{crmActionModal.subtitle || "CRM"}</span>
                <h3 id="ba-crm-action-title">{crmActionModal.title || "Contactar cliente"}</h3>
                <p>{crmActionModal.customer?.name || t("Cliente")}{crmActionModal.customer?.phone ? ` · ${crmActionModal.customer.phone}` : ""}</p>
              </div>
              <button type="button" onClick={closeCrmActionModal} aria-label={t("Cerrar")}>×</button>
            </div>

            <div className="ba-crm-action-body">
              <div className="ba-crm-action-insight">
                <div><span>{t("Segmento")}</span><strong>{crmSegmentMeta(crmActionModal.segment)}</strong></div>
                <div><span>{t("Historial")}</span><strong>{crmActionModal.customer?.totalBookings || 0}{" " + t("reservas")}</strong></div>
                <div><span>{t("Favorito")}</span><strong>{crmActionModal.customer?.favoriteService || "—"}</strong></div>
                <div><span>{t("Seguimiento")}</span><strong>{crmActionModal.followUp?.label || crmFollowUpInfo(crmActionModal.customer).label}</strong></div>
                <div><span>{t("Revenue CRM")}</span><strong>{MONEY.format(crmActionModal.customer?.crmRecoveredRevenue || 0)}</strong></div>
              </div>

              <label className="ba-crm-action-field">
                <span>{t("Mensaje sugerido")}</span>
                <textarea
                  value={crmActionMessage}
                  onChange={(e) => setCrmActionMessage(e.target.value)}
                  rows={6}
                  placeholder={t("Escribe el mensaje que quieres enviar por WhatsApp...")}
                />
              </label>

              {crmActionStatus ? <div className="ba-crm-action-status">{crmActionStatus}</div> : null}
            </div>

            <div className="ba-crm-action-foot">
              <button type="button" onClick={closeCrmActionModal} disabled={crmActionSaving}>{t("Cancelar")}</button>
              <button type="button" onClick={() => saveCrmAction({ openWhatsApp: false })} disabled={crmActionSaving}>{t("Guardar acción")}</button>
              <button type="button" className="primary" onClick={() => saveCrmAction({ openWhatsApp: true })} disabled={crmActionSaving || !crmActionModal.customer?.phone}>{t("Guardar y abrir WhatsApp")}</button>
            </div>
          </div>
        </div>
      ) : null}
    </section></BusinessRegionContext.Provider>
  );
}

const styles = `
  .business-analytics.analytics-minimal {
    --brand:${BRAND};
    --brand2:${BRAND_2};
    --ink:#0f172a;
    --muted:#64748b;
    --line:#e6edf3;
    --soft:#f8fafc;
    width:100%;
    max-width:1400px;
    margin:0 auto;
    padding:18px clamp(14px, 2.2vw, 28px) 34px;
    color:var(--ink);
    overflow:visible;
    box-sizing:border-box;
  }

  .analytics-minimal * { box-sizing:border-box; }

  .ba-topbar {
    display:grid;
    grid-template-columns:minmax(320px,1fr) minmax(300px,430px);
    gap:22px;
    align-items:center;
    padding:0 0 14px;
    border-bottom:1px solid var(--line);
  }

  .ba-eyebrow {
    color:var(--brand);
    font-size:11px;
    font-weight:900;
    letter-spacing:.16em;
    text-transform:uppercase;
  }

  .ba-title-wrap h2 {
    margin:8px 0 0;
    color:#08111f;
    font-size:clamp(32px, 4.3vw, 52px);
    line-height:.94;
    letter-spacing:-.078em;
    font-weight:950;
  }

  .ba-title-wrap p {
    margin:10px 0 0;
    color:var(--muted);
    font-size:14px;
    line-height:1.45;
    font-weight:650;
  }

  .ba-controls {
    display:grid;
    grid-template-columns:1fr;
    gap:9px;
    align-self:end;
  }

  .ba-segmented {
    display:grid;
    grid-template-columns:repeat(4,1fr);
    gap:3px;
    min-height:42px;
    border:1px solid var(--line);
    border-radius:14px;
    background:#f8fafc;
    padding:3px;
  }

  .ba-segmented button,
  .ba-date-control button {
    border:0;
    border-radius:11px;
    background:transparent;
    color:#516174;
    font-size:12px;
    font-weight:850;
    cursor:pointer;
  }

  .ba-segmented button.active {
    background:#ffffff;
    color:var(--brand);
    box-shadow:0 6px 16px rgba(15,23,42,.08);
  }

  .ba-date-control {
    min-height:42px;
    border:1px solid var(--line);
    border-radius:14px;
    display:grid;
    grid-template-columns:38px minmax(0,1fr) 38px;
    align-items:center;
    background:#ffffff;
    padding:2px 4px;
  }

  .ba-date-control strong {
    min-width:0;
    text-align:center;
    color:#172033;
    font-size:12.5px;
    line-height:1.2;
    font-weight:900;
    text-transform:capitalize;
    overflow:hidden;
    white-space:nowrap;
    text-overflow:ellipsis;
  }

  .ba-controls select {
    width:100%;
    min-height:42px;
    border:1px solid var(--line);
    border-radius:14px;
    background:#ffffff;
    color:#172033;
    padding:0 12px;
    font-size:12.5px;
    font-weight:800;
    outline:none;
  }

  .ba-alert {
    margin-top:14px;
    border:1px solid var(--line);
    border-radius:16px;
    background:#ffffff;
    padding:13px 14px;
    color:var(--muted);
    font-size:13px;
    font-weight:800;
  }

  .ba-alert.error { border-color:#fecaca; background:#fff7f7; color:#b91c1c; }

  .ba-executive-grid {
    display:grid;
    grid-template-columns:repeat(6,minmax(0,1fr));
    gap:10px;
    margin:18px 0;
  }

  .ba-metric {
    min-height:112px;
    border:1px solid var(--line);
    border-radius:18px;
    background:#ffffff;
    padding:15px;
    display:grid;
    align-content:space-between;
    box-shadow:0 10px 28px rgba(15,23,42,.045);
  }

  .ba-metric.primary {
    background:linear-gradient(135deg,#063f3a 0%,#00675f 100%);
    border-color:#063f3a;
    color:#ffffff;
  }

  .ba-metric span {
    display:block;
    color:inherit;
    opacity:.68;
    font-size:10.5px;
    font-weight:950;
    letter-spacing:.09em;
    text-transform:uppercase;
  }

  .ba-metric strong {
    display:block;
    margin-top:10px;
    color:inherit;
    font-size:clamp(22px, 2.4vw, 31px);
    line-height:.98;
    letter-spacing:-.06em;
    font-weight:950;
  }

  .ba-metric em {
    display:block;
    margin-top:9px;
    color:inherit;
    opacity:.58;
    font-style:normal;
    font-size:11.5px;
    line-height:1.25;
    font-weight:700;
  }

  .ba-main-grid {
    display:grid;
    grid-template-columns:repeat(3,minmax(0,1fr));
    gap:12px;
  }

  .ba-panel {
    min-width:0;
    border:1px solid var(--line);
    border-radius:20px;
    background:#ffffff;
    padding:16px;
    box-shadow:0 12px 30px rgba(15,23,42,.045);
  }

  .ba-panel-large { grid-column:span 2; }

  .ba-panel-head {
    display:flex;
    justify-content:space-between;
    align-items:flex-start;
    gap:14px;
    margin-bottom:14px;
  }

  .ba-panel-head.compact { margin-bottom:12px; }

  .ba-panel-head span,
  .ba-market-sources > span {
    display:block;
    color:var(--brand);
    font-size:10.5px;
    font-weight:950;
    letter-spacing:.13em;
    text-transform:uppercase;
  }

  .ba-panel-head h3 {
    margin:5px 0 0;
    color:#101828;
    font-size:19px;
    line-height:1.03;
    font-weight:950;
    letter-spacing:-.055em;
  }

  .ba-panel-head > strong {
    color:var(--brand);
    font-size:18px;
    font-weight:950;
    white-space:nowrap;
  }

  .ba-soon {
    color:#64748b;
    background:#f1f5f9;
    border-radius:999px;
    padding:6px 9px;
    font-style:normal;
    font-size:11px;
    font-weight:900;
    white-space:nowrap;
  }

  .ba-trend {
    height:240px;
    display:flex;
    align-items:end;
    gap:7px;
    overflow-x:auto;
    padding:10px 2px 0;
    scrollbar-width:none;
  }

  .ba-trend::-webkit-scrollbar { display:none; }

  .ba-trend-col {
    min-width:38px;
    height:218px;
    display:grid;
    grid-template-rows:1fr auto;
    align-items:end;
    justify-items:center;
    gap:8px;
  }

  .ba-trend-col i {
    width:100%;
    min-height:8px;
    border-radius:10px 10px 4px 4px;
    background:linear-gradient(180deg,var(--brand2),var(--brand));
    box-shadow:0 8px 18px rgba(0,103,95,.14);
  }

  .ba-trend-col span {
    color:#748196;
    font-size:10px;
    font-weight:800;
    white-space:nowrap;
  }

  .ba-status-box {
    display:grid;
    gap:10px;
  }

  .ba-status-line {
    display:grid;
    grid-template-columns:minmax(0,1fr) auto 38px;
    gap:10px;
    align-items:center;
    min-height:34px;
    border-bottom:1px solid #f1f5f9;
  }

  .ba-status-line:last-child { border-bottom:0; }
  .ba-status-line span { min-width:0; display:flex; align-items:center; gap:8px; color:#334155; font-size:13px; font-weight:850; }
  .ba-status-line span i { width:8px; height:8px; border-radius:999px; background:#94a3b8; }
  .ba-status-line span i.ok { background:#10b981; }
  .ba-status-line span i.warn { background:#f59e0b; }
  .ba-status-line span i.bad { background:#ef4444; }
  .ba-status-line strong { color:#0f172a; font-size:13px; font-weight:950; }
  .ba-status-line em { color:#94a3b8; font-size:12px; font-style:normal; font-weight:800; text-align:right; }

  .ba-small-facts {
    display:grid;
    grid-template-columns:repeat(3,minmax(0,1fr));
    gap:8px;
    margin-top:14px;
  }

  .ba-small-facts div,
  .ba-customer-split div {
    border:1px solid #eef2f7;
    border-radius:14px;
    background:#f8fafc;
    padding:10px;
    min-width:0;
  }

  .ba-small-facts span,
  .ba-customer-split span {
    display:block;
    color:#64748b;
    font-size:10.5px;
    font-weight:850;
  }

  .ba-small-facts strong,
  .ba-customer-split strong {
    display:block;
    margin-top:4px;
    color:#0f172a;
    font-size:17px;
    line-height:1;
    font-weight:950;
    letter-spacing:-.04em;
  }

  .ba-list-bars { display:grid; gap:11px; }
  .ba-row { min-width:0; }
  .ba-row-top { display:flex; justify-content:space-between; gap:12px; align-items:center; }
  .ba-row-top span { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; color:#172033; font-size:13px; font-weight:850; }
  .ba-row-top strong { flex:0 0 auto; color:var(--brand); font-size:13px; font-weight:950; }
  .ba-row small { display:block; margin-top:3px; color:#94a3b8; font-size:11px; font-weight:750; }
  .ba-track { height:7px; border-radius:999px; background:#f1f5f9; margin-top:7px; overflow:hidden; }
  .ba-track i { display:block; height:100%; border-radius:999px; background:linear-gradient(135deg,var(--brand),var(--brand2)); }

  .ba-customer-split { display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-top:14px; }

  .ba-market-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:10px; }
  .ba-market-grid .ba-metric { min-height:96px; box-shadow:none; }
  .ba-market-sources { grid-column:1 / -1; border-top:1px solid #eef2f7; padding-top:14px; margin-top:2px; }
  .ba-market-sources .ba-list-bars { margin-top:12px; }

  .ba-tracking-empty {
    border:1px dashed #cbd5e1;
    border-radius:18px;
    background:#f8fafc;
    padding:16px;
  }

  .ba-tracking-empty strong { display:block; color:#0f172a; font-size:17px; font-weight:950; letter-spacing:-.035em; }
  .ba-tracking-empty p { margin:8px 0 12px; color:#64748b; font-size:13px; line-height:1.45; font-weight:700; }
  .ba-tracking-empty div { display:flex; flex-wrap:wrap; gap:7px; }
  .ba-tracking-empty span { border:1px solid #e2e8f0; background:#fff; border-radius:999px; padding:7px 9px; color:#475569; font-size:11px; font-weight:850; }

  .ba-insights-list { display:grid; gap:9px; }
  .ba-insight { display:grid; grid-template-columns:30px minmax(0,1fr); gap:9px; align-items:start; border:1px solid #eef2f7; background:#f8fafc; border-radius:15px; padding:10px; }
  .ba-insight b { width:30px; height:30px; border-radius:11px; background:rgba(0,103,95,.09); color:var(--brand); display:grid; place-items:center; font-size:12px; font-weight:950; }
  .ba-insight span { color:#334155; font-size:12.5px; line-height:1.38; font-weight:750; }

  .ba-empty { color:#64748b; border-radius:16px; background:#f8fafc; border:1px dashed #cbd5e1; padding:14px; font-size:13px; font-weight:750; }
  .ba-empty.slim { height:100%; min-height:140px; display:grid; place-items:center; text-align:center; }

  @media(max-width:1180px){
    .ba-command-workspace{grid-template-columns:204px minmax(0,1fr);gap:14px;}
    .ba-command-tabs button{grid-template-columns:30px minmax(0,1fr);padding:10px;}
    .ba-command-tabs button>i{width:30px;height:30px;border-radius:11px;}
    .ba-sidebar-card{padding:12px;border-radius:18px;}
    .ba-executive-grid{grid-template-columns:repeat(3,minmax(0,1fr));}
    .ba-main-grid{grid-template-columns:repeat(2,minmax(0,1fr));}
    .ba-panel-large{grid-column:span 2;}
  }

  @media(max-width:820px){
    .business-analytics.analytics-minimal{padding:14px 12px 28px;}
    .ba-topbar{grid-template-columns:1fr; align-items:start; gap:14px;}
    .ba-command-workspace{display:block;margin-top:12px;}
    .ba-command-sidebar{position:relative;top:auto;max-height:none;overflow:visible;display:grid;gap:10px;margin-bottom:12px;}
    .ba-sidebar-headline,.ba-sidebar-health{display:none;}
    .ba-command-tabs{display:flex;gap:8px;overflow-x:auto;padding-bottom:4px;scroll-snap-type:x mandatory;}
    .ba-command-tabs button{flex:0 0 164px;grid-template-columns:26px minmax(0,1fr);scroll-snap-align:start;border-radius:16px;}
    .ba-command-tabs button>i{width:26px;height:26px;border-radius:10px;}
    .ba-command-tabs button strong{font-size:13px;}
    .ba-command-tabs button em{font-size:10.5px;}
    .ba-executive-grid{grid-template-columns:repeat(2,minmax(0,1fr)); gap:8px;}
    .ba-main-grid{grid-template-columns:1fr; gap:10px;}
    .ba-panel-large{grid-column:span 1;}
    .ba-market-grid{grid-template-columns:1fr;}
    .ba-metric{min-height:102px; border-radius:16px; padding:13px;}
    .ba-metric strong{font-size:24px;}
    .ba-panel{border-radius:18px; padding:14px;}
    .ba-trend{height:210px;}
    .ba-trend-col{height:188px; min-width:34px;}
  }

  @media(max-width:520px){
    .ba-executive-grid{grid-template-columns:1fr 1fr;}
    .ba-title-wrap h2{font-size:34px;}
    .ba-controls{gap:8px;}
    .ba-small-facts{grid-template-columns:1fr;}
    .ba-customer-split{grid-template-columns:1fr 1fr;}
  }
  .ba-soon.is-live {
    background: #ecfdf5 !important;
    color: var(--brand) !important;
    border-color: #bbf7d0 !important;
  }

  .ba-tracking-empty p {
    max-width: 640px;
  }


  /* LOOP PHASE 2.5A — Analytics command layout: left module rail + compact filters */
  .ba-command-workspace{
    display:grid;
    grid-template-columns:236px minmax(0,1fr);
    gap:18px;
    align-items:start;
    margin-top:16px;
  }
  .ba-command-sidebar{
    position:sticky;
    top:86px;
    align-self:start;
    display:grid;
    gap:12px;
    max-height:calc(100vh - 104px);
    overflow:auto;
    padding-right:2px;
  }
  .ba-command-sidebar::-webkit-scrollbar{width:4px;}.ba-command-sidebar::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:99px;}
  .ba-command-main{min-width:0;display:grid;gap:14px;}
  .ba-sidebar-card{border:1px solid var(--line);border-radius:20px;background:#fff;padding:14px;box-shadow:0 12px 30px rgba(15,23,42,.055);}
  .ba-sidebar-card>span{display:block;color:var(--brand);font-size:10px;font-weight:950;letter-spacing:.12em;text-transform:uppercase;}
  .ba-sidebar-headline strong{display:block;margin-top:6px;color:#0f172a;font-size:22px;line-height:1;font-weight:950;letter-spacing:-.055em;}
  .ba-sidebar-headline em{display:block;margin-top:7px;color:#64748b;font-size:12px;font-style:normal;font-weight:800;line-height:1.25;}
  .ba-sidebar-health{display:grid;gap:8px;}
  .ba-sidebar-health button{width:100%;border:1px solid #e6edf3;background:#f8fafc;border-radius:14px;min-height:38px;padding:8px 10px;display:flex;align-items:center;justify-content:space-between;gap:8px;color:#334155;font-size:11.5px;font-weight:900;cursor:pointer;text-align:left;}
  .ba-sidebar-health button strong{color:var(--brand);font-size:12px;font-weight:950;white-space:nowrap;}
  .ba-command-tabs{display:grid;grid-template-columns:1fr;gap:8px;margin:0;}
  .ba-command-tabs button{min-width:0;border:1px solid var(--line);border-radius:18px;background:#ffffff;color:#334155;padding:11px;display:grid;grid-template-columns:34px minmax(0,1fr);gap:10px;align-items:center;text-align:left;cursor:pointer;box-shadow:0 10px 24px rgba(15,23,42,.04);}
  .ba-command-tabs button>i{width:34px;height:34px;border-radius:13px;display:grid;place-items:center;background:#f8fafc;color:#64748b;font-style:normal;font-size:11px;font-weight:950;border:1px solid #eef2f7;}
  .ba-command-tabs button>span{min-width:0;display:block;margin:0;}
  .ba-command-tabs button strong{display:block;font-size:13.5px;font-weight:950;color:#0f172a;letter-spacing:-.02em;}
  .ba-command-tabs button em{display:block;margin-top:3px;font-size:11px;font-weight:760;color:#64748b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-style:normal;}
  .ba-command-tabs button.is-active{background:linear-gradient(135deg,rgba(0,103,95,.09),#fff);border-color:rgba(0,103,95,.30);box-shadow:0 16px 34px rgba(0,103,95,.08);}
  .ba-command-tabs button.is-active>i{background:var(--brand);border-color:var(--brand);color:#fff;}
  .ba-command-tabs button.is-active strong{color:var(--brand);}
  .ba-metric.success{background:#ecfdf5;border-color:#99f6e4;}
  .ba-metric.warning{background:#fffbeb;border-color:#fde68a;}

  /* LOOP PHASE 2.4 — Finance Executive Dashboard */
  .ba-finance-exec-dashboard{display:grid;grid-template-columns:minmax(280px,.9fr) minmax(320px,1.1fr);gap:12px;margin-top:-2px;margin-bottom:2px;align-items:stretch;}
  .ba-finance-exec-command{border:1px solid rgba(0,103,95,.18);border-radius:22px;background:radial-gradient(circle at 100% 0%,rgba(20,184,166,.16),transparent 36%),linear-gradient(135deg,#f0fdfa,#ffffff);box-shadow:0 16px 38px rgba(15,23,42,.055);padding:17px;display:grid;align-content:start;min-height:174px;}
  .ba-finance-exec-command.warning{border-color:#fed7aa;background:radial-gradient(circle at 100% 0%,rgba(251,146,60,.18),transparent 38%),linear-gradient(135deg,#fff7ed,#ffffff);}
  .ba-finance-exec-command.success{border-color:#bbf7d0;background:radial-gradient(circle at 100% 0%,rgba(34,197,94,.14),transparent 38%),linear-gradient(135deg,#f0fdf4,#ffffff);}
  .ba-finance-exec-command span,.ba-finance-action span,.ba-finance-exec-list span,.ba-finance-pending-mini>span{display:block;color:#0f766e;font-size:10px;font-weight:950;letter-spacing:.13em;text-transform:uppercase;}
  .ba-finance-exec-command h3{margin:5px 0 0;color:#0f172a;font-size:22px;line-height:1;font-weight:950;letter-spacing:-.055em;}
  .ba-finance-exec-command p{margin:8px 0 14px;color:#64748b;font-size:12.5px;line-height:1.35;font-weight:750;}
  .ba-finance-exec-command strong{color:#00675f;font-size:34px;line-height:1;font-weight:950;letter-spacing:-.065em;}
  .ba-finance-exec-command em{margin-top:5px;color:#64748b;font-size:12px;font-style:normal;font-weight:800;}
  .ba-finance-exec-kpis{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px;}
  .ba-finance-exec-kpis article{border:1px solid #e5edf4;border-radius:16px;background:#fff;padding:12px;box-shadow:0 10px 24px rgba(15,23,42,.04);min-height:86px;}
  .ba-finance-exec-kpis span{display:block;color:#64748b;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.11em;}
  .ba-finance-exec-kpis strong{display:block;margin-top:7px;color:#0f172a;font-size:21px;line-height:1;font-weight:950;letter-spacing:-.055em;}
  .ba-finance-exec-kpis em{display:block;margin-top:5px;color:#64748b;font-size:11px;font-style:normal;font-weight:750;}
  .ba-finance-action-grid{grid-column:1 / -1;display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;}
  .ba-finance-action{border:1px solid #e5edf4;border-radius:18px;background:#fff;padding:13px;min-height:96px;box-shadow:0 10px 24px rgba(15,23,42,.04);}
  .ba-finance-action.warning{border-color:#fed7aa;background:#fffaf3}.ba-finance-action.success{border-color:#bbf7d0;background:#f7fff9}.ba-finance-action.primary{border-color:#bae6fd;background:#f0f9ff}
  .ba-finance-action strong{display:block;margin-top:8px;color:#0f172a;font-size:22px;line-height:1;font-weight:950;letter-spacing:-.055em;}.ba-finance-action em{display:block;margin-top:6px;color:#64748b;font-size:11.5px;font-style:normal;font-weight:750;line-height:1.3;}
  .ba-finance-top-panel{grid-column:1 / -1;border:1px solid #e5edf4;border-radius:20px;background:#fff;box-shadow:0 12px 30px rgba(15,23,42,.045);padding:14px;display:grid;gap:12px;}
  .ba-finance-exec-list{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;}.ba-finance-exec-list div{border:1px solid #eef2f7;border-radius:14px;background:#f8fafc;padding:10px}.ba-finance-exec-list strong{display:block;margin-top:6px;color:#0f172a;font-size:13px;font-weight:950;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.ba-finance-exec-list em{display:block;margin-top:3px;color:#00675f;font-size:12px;font-style:normal;font-weight:950;}
  .ba-finance-pending-mini{border:1px dashed #cbd5e1;border-radius:16px;background:#fbfdff;padding:11px;display:grid;gap:7px;}.ba-finance-pending-mini div{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:8px;align-items:center;border-top:1px solid #eef2f7;padding-top:7px}.ba-finance-pending-mini strong{min-width:0;color:#0f172a;font-size:12.5px;font-weight:950;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.ba-finance-pending-mini em{grid-column:1;color:#64748b;font-size:11px;font-style:normal;font-weight:750;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.ba-finance-pending-mini b{grid-row:1 / span 2;grid-column:2;color:#b45309;font-size:12.5px;font-weight:950;white-space:nowrap;}
  @media(max-width:980px){.ba-finance-exec-dashboard{grid-template-columns:1fr}.ba-finance-exec-kpis,.ba-finance-action-grid{grid-template-columns:repeat(2,minmax(0,1fr));}.ba-finance-exec-list{grid-template-columns:1fr;}}
  @media(max-width:520px){.ba-finance-exec-kpis,.ba-finance-action-grid{grid-template-columns:1fr 1fr}.ba-finance-exec-command strong{font-size:29px}.ba-finance-action strong,.ba-finance-exec-kpis strong{font-size:19px}}

  .ba-finance-module,.ba-inventory-module,.ba-traffic-module{display:grid;gap:16px;margin-top:18px;}
  .ba-finance-hero{border:1px solid rgba(0,103,95,.16);border-radius:24px;background:radial-gradient(circle at 100% 0%,rgba(20,184,166,.16),transparent 34%),#ffffff;box-shadow:0 16px 42px rgba(15,23,42,.055);padding:18px;display:flex;align-items:center;justify-content:space-between;gap:16px;}
  .ba-finance-hero span{display:block;color:var(--brand);font-size:11px;font-weight:950;letter-spacing:.14em;text-transform:uppercase;}
  .ba-finance-hero h3{margin:6px 0 0;color:#0f172a;font-size:25px;line-height:1;font-weight:950;letter-spacing:-.055em;}
  .ba-finance-hero p{margin:8px 0 0;color:#64748b;font-size:13px;line-height:1.45;font-weight:700;max-width:680px;}
  .ba-finance-hero>strong{color:var(--brand);font-size:30px;font-weight:950;letter-spacing:-.06em;white-space:nowrap;}
  .ba-finance-hero.inventory{border-color:#dbeafe;background:linear-gradient(135deg,#eff6ff,#fff);}
  .ba-finance-table{display:grid;gap:8px;}
  .ba-finance-row{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:12px;align-items:center;border:1px solid #eef2f7;border-radius:14px;background:#f8fafc;padding:10px 12px;}
  .ba-finance-row span{min-width:0;color:#0f172a;font-size:13px;font-weight:950;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .ba-finance-row span em{display:block;margin-top:3px;color:#64748b;font-size:11.5px;font-style:normal;font-weight:750;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .ba-finance-row strong{color:var(--brand);font-size:13px;font-weight:950;white-space:nowrap;}
  .ba-close-hero>strong:before{content:"Comisiones";display:block;color:#64748b;font-size:10.5px;font-weight:950;letter-spacing:.12em;text-transform:uppercase;text-align:right;margin-bottom:2px;}
  .ba-worker-close-panel{grid-column:1 / -1;}
  .ba-worker-close-table{display:grid;gap:0;border:1px solid #e2e8f0;border-radius:20px;overflow:hidden;background:#fff;}
  .ba-worker-close-head,.ba-worker-close-row{display:grid;grid-template-columns:minmax(160px,1.35fr) .6fr .85fr .85fr .85fr .85fr .9fr .9fr;align-items:center;gap:10px;padding:13px 16px;}
  .ba-worker-close-head{background:#f8fafc;color:#64748b;font-size:10.5px;font-weight:950;letter-spacing:.1em;text-transform:uppercase;border-bottom:1px solid #e2e8f0;}
  .ba-worker-close-row{border-bottom:1px solid #eef2f7;color:#0f172a;font-size:13px;font-weight:850;}
  .ba-worker-close-row:last-child{border-bottom:0;}
  .ba-worker-close-row strong{font-size:14px;font-weight:950;color:#0f172a;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .ba-worker-close-row span{font-weight:900;color:#334155;white-space:nowrap;}
  .ba-worker-close-row.total{background:linear-gradient(135deg,rgba(0,103,95,.08),#fff);}
  .ba-worker-close-row.total strong,.ba-worker-close-row.total span{color:var(--brand);font-weight:950;}
  .ba-worker-close-cards{display:none;gap:10px;}
  .ba-worker-card{border:1px solid #e2e8f0;border-radius:18px;background:#fff;padding:12px;box-shadow:0 10px 24px rgba(15,23,42,.035);}
  .ba-worker-card>div:first-child{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;margin-bottom:10px;}
  .ba-worker-card strong{color:#0f172a;font-size:14px;font-weight:950;}
  .ba-worker-card span{color:#64748b;font-size:12px;font-weight:800;text-align:right;}
  .ba-worker-card dl{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin:0;}
  .ba-worker-card dl div{border-radius:14px;background:#f8fafc;border:1px solid #eef2f7;padding:9px;}
  .ba-worker-card dt{margin:0;color:#64748b;font-size:10.5px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;}
  .ba-worker-card dd{margin:4px 0 0;color:var(--brand);font-size:14px;font-weight:950;}
  .ba-worker-card.total{background:linear-gradient(135deg,rgba(0,103,95,.08),#fff);border-color:rgba(0,103,95,.18);}
  .ba-finance-secondary{margin-top:0;}
  .ba-roadmap-list{display:grid;gap:10px;}
  .ba-roadmap-list div{border:1px solid #eef2f7;background:#f8fafc;border-radius:16px;padding:12px;}
  .ba-roadmap-list strong{display:block;color:#0f172a;font-size:14px;font-weight:950;}
  .ba-roadmap-list span{display:block;margin-top:4px;color:#64748b;font-size:12.5px;line-height:1.4;font-weight:700;}
  .ba-code{margin:0;white-space:pre-wrap;border:1px solid #e2e8f0;background:#0f172a;color:#e2e8f0;border-radius:16px;padding:14px;font-size:12px;line-height:1.45;overflow:auto;}
  @media(max-width:980px){.ba-worker-close-table{overflow-x:auto}.ba-worker-close-head,.ba-worker-close-row{min-width:920px;grid-template-columns:minmax(150px,1.35fr) .6fr .85fr .85fr .85fr .85fr .9fr .9fr;padding:12px;font-size:12px;}}
  @media(max-width:820px){.ba-command-tabs{grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}.ba-finance-hero{display:grid}.ba-finance-hero>strong{font-size:25px}.ba-executive-grid.finance{grid-template-columns:repeat(2,minmax(0,1fr));}}
  @media(max-width:620px){.ba-worker-close-table{display:none}.ba-worker-close-cards{display:grid}.ba-worker-close-panel .ba-panel-head{align-items:flex-start}.ba-worker-close-panel .ba-panel-head strong{font-size:12px}.ba-close-hero>strong:before{text-align:left}.ba-finance-secondary{gap:12px}}
  @media(max-width:520px){.ba-command-tabs button{padding:11px}.ba-command-tabs button span{white-space:normal}.ba-finance-hero h3{font-size:22px}.ba-executive-grid.finance{grid-template-columns:1fr 1fr}}

  .ba-inventory-form{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-top:12px;}
  .ba-inventory-form label{display:grid;gap:6px;min-width:0;}
  .ba-inventory-form label.wide{grid-column:1 / -1;}
  .ba-inventory-form label span{color:#64748b;font-size:10.5px;font-weight:950;text-transform:uppercase;letter-spacing:.09em;}
  .ba-inventory-form input,.ba-inventory-form textarea{width:100%;border:1px solid #dbe5ee;border-radius:14px;background:#fff;color:#0f172a;padding:0 11px;min-height:42px;font-size:13px;font-weight:850;outline:none;}
  .ba-inventory-form textarea{padding:10px 11px;resize:vertical;}
  .ba-inventory-form input:focus,.ba-inventory-form textarea:focus{border-color:rgba(0,103,95,.34);box-shadow:0 0 0 4px rgba(0,103,95,.07);}
  .ba-inventory-form-actions{grid-column:1 / -1;display:flex;gap:9px;flex-wrap:wrap;align-items:center;}
  .ba-inventory-form-actions button,.ba-inventory-mini{min-height:40px;border:0;border-radius:14px;background:var(--brand);color:#fff;padding:0 14px;font-size:12px;font-weight:950;cursor:pointer;}
  .ba-inventory-form-actions button.secondary,.ba-inventory-mini{background:#fff;color:#334155;border:1px solid #e2e8f0;}
  .ba-inventory-form-actions button:disabled{opacity:.62;cursor:not-allowed;}
  .ba-inventory-status{margin-top:10px;border-radius:14px;background:#f8fafc;border:1px solid #e2e8f0;color:#334155;padding:10px 12px;font-size:12px;font-weight:850;}
  .ba-inventory-alerts{display:grid;gap:8px;margin-top:10px;}
  .ba-inventory-alert{width:100%;border:1px solid #e2e8f0;background:#fff;border-radius:16px;padding:10px;display:flex;align-items:center;justify-content:space-between;gap:10px;text-align:left;cursor:pointer;}
  .ba-inventory-alert span{min-width:0;color:#0f172a;font-size:13px;font-weight:950;}
  .ba-inventory-alert em{display:block;margin-top:3px;color:#64748b;font-size:11px;font-style:normal;font-weight:800;}
  .ba-inventory-alert strong{flex:0 0 auto;border-radius:999px;padding:6px 8px;font-size:10.5px;font-weight:950;}
  .ba-inventory-alert.low strong{background:#fef3c7;color:#92400e;}
  .ba-inventory-alert.critical strong{background:#fee2e2;color:#991b1b;}
  .ba-inventory-table{display:grid;gap:8px;margin-top:10px;}
  .ba-inventory-row{display:grid;grid-template-columns:minmax(180px,1.4fr) repeat(4,minmax(80px,.7fr)) minmax(190px,1fr);gap:8px;align-items:center;border:1px solid #e8eef5;background:#fff;border-radius:18px;padding:10px;}
  .ba-inventory-row>div{min-width:0;}
  .ba-inventory-row span{display:block;color:#64748b;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;}
  .ba-inventory-row strong{display:block;color:#0f172a;font-size:12.5px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .ba-inventory-name strong{font-size:14px;}
  .ba-inventory-name span{text-transform:none;letter-spacing:0;font-size:11.5px;font-weight:800;margin-top:3px;}
  .ba-inventory-row-actions{display:flex;align-items:center;justify-content:flex-end;gap:6px;flex-wrap:wrap;}
  .ba-inventory-row-actions em{border-radius:999px;padding:6px 8px;font-size:10px;font-style:normal;font-weight:950;background:#ecfdf5;color:#047857;}
  .ba-inventory-row-actions em.low{background:#fef3c7;color:#92400e;}
  .ba-inventory-row-actions em.critical{background:#fee2e2;color:#991b1b;}
  .ba-inventory-row-actions button{border:1px solid #e2e8f0;background:#fff;color:#334155;border-radius:999px;min-height:30px;padding:0 9px;font-size:11px;font-weight:950;cursor:pointer;}
  .ba-inventory-row-actions button.danger{color:#b91c1c;background:#fff7f7;border-color:#fecaca;}


  .ba-mini-link{border:0;background:#ecfdf5;color:var(--brand);border-radius:999px;padding:7px 10px;font-size:11px;font-weight:950;cursor:pointer;}
  .ba-insights-module{display:grid;gap:16px;margin-top:18px;}
  .ba-insights-hero{display:flex;align-items:flex-end;justify-content:space-between;gap:18px;border:1px solid rgba(0,103,95,.16);background:linear-gradient(135deg,rgba(0,103,95,.08),rgba(20,184,166,.08));border-radius:24px;padding:20px;box-shadow:0 16px 38px rgba(15,23,42,.05);}
  .ba-insights-hero span{display:block;color:var(--brand);font-size:11px;font-weight:950;letter-spacing:.14em;text-transform:uppercase;}
  .ba-insights-hero h3{margin:6px 0 0;color:#0f172a;font-size:clamp(25px,3vw,38px);font-weight:950;letter-spacing:-.06em;line-height:.95;}
  .ba-insights-hero p{margin:8px 0 0;color:#64748b;font-size:13px;font-weight:750;line-height:1.45;max-width:680px;}
  .ba-insights-hero strong{font-size:44px;font-weight:950;color:var(--brand);letter-spacing:-.08em;line-height:1;}
  .ba-main-grid.insights-grid{align-items:start;}
  .ba-smart-list{display:grid;gap:11px;}
  .ba-smart-card{display:grid;grid-template-columns:38px minmax(0,1fr);gap:12px;border:1px solid #e6edf3;background:#fff;border-radius:18px;padding:13px;box-shadow:0 10px 24px rgba(15,23,42,.035);}
  .ba-smart-card.danger{border-color:#fecaca;background:#fff7f7;}
  .ba-smart-card.warning{border-color:#fde68a;background:#fffbeb;}
  .ba-smart-card.success{border-color:#bbf7d0;background:#f0fdf4;}
  .ba-smart-card.info{border-color:#bfdbfe;background:#eff6ff;}
  .ba-smart-rank{width:38px;height:38px;border-radius:14px;background:#0f172a;color:#fff;display:grid;place-items:center;font-size:13px;font-weight:950;}
  .ba-smart-card.danger .ba-smart-rank{background:#dc2626;}
  .ba-smart-card.warning .ba-smart-rank{background:#d97706;}
  .ba-smart-card.success .ba-smart-rank{background:#059669;}
  .ba-smart-card.info .ba-smart-rank{background:#2563eb;}
  .ba-smart-copy{min-width:0;}
  .ba-smart-top{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:5px;}
  .ba-smart-top span{color:#64748b;font-size:10px;font-weight:950;letter-spacing:.12em;text-transform:uppercase;}
  .ba-smart-top strong{color:#0f172a;font-size:12px;font-weight:950;background:rgba(255,255,255,.7);border:1px solid rgba(226,232,240,.9);border-radius:999px;padding:4px 8px;white-space:nowrap;}
  .ba-smart-copy h4{margin:0;color:#0f172a;font-size:15px;font-weight:950;letter-spacing:-.035em;line-height:1.1;}
  .ba-smart-copy p{margin:6px 0 0;color:#334155;font-size:12.5px;font-weight:720;line-height:1.45;}
  .ba-smart-copy em{display:block;margin-top:8px;color:#00675f;font-style:normal;font-size:12px;font-weight:900;line-height:1.35;}
  .ba-health-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:9px;}
  .ba-health-grid div{border:1px solid #eef2f7;background:#f8fafc;border-radius:16px;padding:12px;}
  .ba-health-grid span{display:block;color:#64748b;font-size:10px;font-weight:950;letter-spacing:.1em;text-transform:uppercase;}
  .ba-health-grid strong{display:block;margin-top:5px;color:#0f172a;font-size:22px;font-weight:950;letter-spacing:-.06em;}
  .ba-health-grid em{display:block;margin-top:2px;color:#64748b;font-size:11px;font-style:normal;font-weight:850;}
  @media(max-width:900px){.ba-inventory-row{grid-template-columns:1fr 1fr;}.ba-inventory-name,.ba-inventory-row-actions{grid-column:1 / -1;}.ba-inventory-row-actions{justify-content:flex-start;}}
  .ba-inventory-form select{width:100%;border:1px solid #dbe5ee;border-radius:14px;background:#fff;color:#0f172a;padding:0 11px;min-height:42px;font-size:13px;font-weight:850;outline:none;}
  .ba-inventory-note{margin:0 0 12px;color:#64748b;font-size:12px;font-weight:760;line-height:1.45;}
  .ba-stocktake-list{display:grid;gap:8px;max-height:360px;overflow:auto;padding-right:3px;}
  .ba-stocktake-row{display:grid;grid-template-columns:minmax(0,1fr) 118px 58px;gap:10px;align-items:center;border:1px solid #eef2f7;border-radius:15px;background:#fbfdff;padding:10px;}
  .ba-stocktake-row strong{display:block;color:#101828;font-size:13px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .ba-stocktake-row span{display:block;color:#64748b;font-size:11px;font-weight:800;margin-top:3px;}
  .ba-stocktake-row input,.ba-stocktake-actions input{width:100%;min-height:38px;border:1px solid #dbe5ee;border-radius:13px;background:#fff;color:#0f172a;padding:0 10px;font-size:12px;font-weight:850;outline:none;}
  .ba-stocktake-row em{font-style:normal;text-align:center;border-radius:999px;padding:6px 8px;font-size:11px;font-weight:950;background:#f8fafc;color:#64748b;}
  .ba-stocktake-row em.ok{background:#ecfdf5;color:#047857;}
  .ba-stocktake-row em.critical{background:#fef2f2;color:#b91c1c;}
  .ba-stocktake-actions{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:10px;margin-top:12px;}
  .ba-stocktake-actions button{min-height:40px;border:0;border-radius:14px;background:var(--brand);color:#fff;padding:0 14px;font-size:12px;font-weight:950;cursor:pointer;}
  .ba-stocktake-actions button:disabled{opacity:.62;cursor:not-allowed;}
  .ba-movement-row{grid-template-columns:minmax(170px,1.4fr) repeat(4,minmax(80px,.75fr)) auto;}

  @media(max-width:640px){.ba-inventory-form{grid-template-columns:1fr;}.ba-inventory-row{grid-template-columns:1fr;}.ba-inventory-row-actions{display:grid;grid-template-columns:1fr 1fr;}.ba-inventory-row-actions em{grid-column:1 / -1;width:fit-content;}}


  .ba-custom-range {
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:8px;
  }
  .ba-custom-range label {
    display:grid;
    gap:4px;
    color:#64748b;
    font-size:10px;
    font-weight:950;
    text-transform:uppercase;
    letter-spacing:.08em;
  }
  .ba-custom-range input {
    min-height:40px;
    border:1px solid var(--line);
    border-radius:13px;
    background:#fff;
    color:#0f172a;
    padding:0 10px;
    font-size:12px;
    font-weight:850;
    outline:none;
  }
  .ba-insights-hero.reports {
    background:linear-gradient(135deg, rgba(0,103,95,.10), rgba(20,184,166,.06)), #fff;
  }
  .ba-export-grid {
    display:grid;
    grid-template-columns:1fr;
    gap:9px;
  }
  .ba-export-grid button {
    min-height:42px;
    border:1px solid #dbe5ee;
    border-radius:14px;
    background:#fff;
    color:#0f172a;
    font-size:13px;
    font-weight:950;
    cursor:pointer;
    text-align:left;
    padding:0 13px;
  }
  .ba-export-grid button.primary {
    border-color:rgba(0,103,95,.18);
    background:#00675f;
    color:#fff;
    text-align:center;
  }
  .ba-report-status {
    margin-top:10px;
    border:1px solid #ccfbf1;
    background:#f0fdfa;
    color:#00675f;
    border-radius:14px;
    padding:10px 12px;
    font-size:12px;
    font-weight:900;
  }
  .ba-closeout-list,
  .ba-checklist,
  .ba-report-summary {
    display:grid;
    gap:10px;
  }
  .ba-closeout-row,
  .ba-closeout-total {
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:12px;
    border:1px solid #eef2f7;
    background:#f8fafc;
    border-radius:15px;
    padding:11px 12px;
  }
  .ba-closeout-row span,
  .ba-closeout-total span { color:#64748b; font-size:12px; font-weight:950; }
  .ba-closeout-row strong,
  .ba-closeout-total strong { color:#0f172a; font-size:15px; font-weight:950; }
  .ba-closeout-total { background:#ecfdf5; border-color:#99f6e4; }
  .ba-closeout-total strong { color:#00675f; }
  .ba-checklist div {
    border:1px solid #eef2f7;
    background:#f8fafc;
    border-radius:15px;
    padding:11px 12px;
    display:grid;
    gap:4px;
  }
  .ba-checklist div.ok { background:#f0fdf4; border-color:#bbf7d0; }
  .ba-checklist div.warn { background:#fffbeb; border-color:#fde68a; }
  .ba-checklist span { color:#64748b; font-size:10px; font-weight:950; text-transform:uppercase; letter-spacing:.08em; }
  .ba-checklist strong { color:#0f172a; font-size:13px; font-weight:950; }
  .ba-checklist em { color:#475569; font-size:12px; font-weight:850; font-style:normal; }
  .ba-report-summary p {
    margin:0;
    color:#475569;
    font-size:13px;
    line-height:1.55;
    font-weight:750;
  }
  .ba-report-summary strong { color:#0f172a; font-weight:950; }


  /* Phase 8 — production polish */
  .ba-mobile-hint {
    display:none;
    margin-top:8px;
    color:#64748b;
    font-size:11px;
    font-weight:850;
  }

  .ba-alert-action {
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:12px;
  }
  .ba-alert-action button {
    flex:0 0 auto;
    min-height:34px;
    border:1px solid #fecaca;
    border-radius:999px;
    background:#ffffff;
    color:#b91c1c;
    padding:0 12px;
    font-size:12px;
    font-weight:950;
    cursor:pointer;
  }

  .ba-skeleton-wrap {
    margin-top:14px;
    display:grid;
    grid-template-columns:repeat(4,minmax(0,1fr));
    gap:10px;
  }
  .ba-skeleton-wrap span {
    min-height:74px;
    border-radius:18px;
    background:linear-gradient(90deg,#f1f5f9 0%,#ffffff 48%,#f1f5f9 100%);
    background-size:220% 100%;
    animation:baSkeleton 1.25s ease-in-out infinite;
    border:1px solid #e2e8f0;
  }
  @keyframes baSkeleton { 0%{background-position:0 0;} 100%{background-position:-220% 0;} }

  .ba-quality-strip {
    display:grid;
    grid-template-columns:repeat(3,minmax(0,1fr));
    gap:10px;
    margin-top:14px;
  }
  .ba-quality-card {
    border:1px solid #e2e8f0;
    border-radius:18px;
    background:#ffffff;
    padding:12px 13px;
    display:grid;
    gap:4px;
  }
  .ba-quality-card strong { color:#0f172a; font-size:13px; font-weight:950; }
  .ba-quality-card span { color:#64748b; font-size:12px; line-height:1.35; font-weight:750; }
  .ba-quality-card.warning { background:#fffbeb; border-color:#fde68a; }
  .ba-quality-card.critical { background:#fff7f7; border-color:#fecaca; }
  .ba-quality-card.good { background:#f0fdf4; border-color:#bbf7d0; }

  .ba-empty-card {
    min-height:110px;
    border:1px dashed #cbd5e1;
    border-radius:20px;
    background:linear-gradient(180deg,#ffffff,#f8fafc);
    display:grid;
    grid-template-columns:42px minmax(0,1fr);
    gap:12px;
    align-items:start;
    padding:16px;
  }
  .ba-empty-icon {
    width:42px;
    height:42px;
    border-radius:16px;
    display:grid;
    place-items:center;
    background:#ecfdf5;
    color:#00675f;
    font-size:20px;
    font-weight:950;
  }
  .ba-empty-card strong { color:#0f172a; font-size:14px; font-weight:950; }
  .ba-empty-card p { margin:5px 0 0; color:#64748b; font-size:13px; line-height:1.45; font-weight:750; }
  .ba-empty-card button {
    margin-top:10px;
    min-height:36px;
    border:0;
    border-radius:999px;
    background:#00675f;
    color:#fff;
    padding:0 13px;
    font-size:12px;
    font-weight:950;
    cursor:pointer;
  }

  @media (max-width: 920px) {
    .ba-command-tabs {
      display:flex;
      overflow-x:auto;
      padding-bottom:4px;
      scroll-snap-type:x proximity;
      -webkit-overflow-scrolling:touch;
    }
    .ba-command-tabs button {
      flex:0 0 min(72vw, 220px);
      scroll-snap-align:start;
    }
    .ba-mobile-hint { display:block; }
    .ba-quality-strip,
    .ba-skeleton-wrap { grid-template-columns:1fr 1fr; }
  }

  @media (max-width: 640px) {
    .business-analytics { padding-bottom:28px; }
    .ba-header {
      border-radius:22px;
      padding:16px;
    }
    .ba-title h2 { font-size:28px; letter-spacing:-.055em; }
    .ba-controls { gap:8px; }
    .ba-segmented {
      overflow-x:auto;
      justify-content:flex-start;
      -webkit-overflow-scrolling:touch;
    }
    .ba-segmented button { flex:0 0 auto; min-width:74px; }
    .ba-custom-range { grid-template-columns:1fr; }
    .ba-executive-grid,
    .ba-executive-grid.reports,
    .ba-main-grid,
    .ba-main-grid.reports-grid,
    .ba-inventory-grid,
    .ba-insights-grid {
      grid-template-columns:1fr !important;
    }
    .ba-metric { min-height:94px; }
    .ba-panel { border-radius:20px; }
    .ba-quality-strip,
    .ba-skeleton-wrap { grid-template-columns:1fr; }
    .ba-alert-action { align-items:flex-start; flex-direction:column; }
    .ba-alert-action button { width:100%; }
    .ba-closeout-row,
    .ba-closeout-total { align-items:flex-start; flex-direction:column; }
    .ba-export-grid button { min-height:46px; }
  }


  /* LOOP PHASE 2A — settings + finance foundations */
  .ba-phase2-strip{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;margin:12px 0 0;}
  .ba-phase2-strip article{border:1px solid #e2e8f0;background:#fff;border-radius:18px;padding:12px;box-shadow:0 10px 24px rgba(15,23,42,.045);min-width:0;}
  .ba-phase2-strip span,.ba-phase2-settings-card span{display:block;color:#64748b;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.1em;}
  .ba-phase2-strip strong{display:block;margin-top:5px;color:#0f172a;font-size:14px;font-weight:950;}
  .ba-phase2-strip em{display:block;margin-top:3px;color:#64748b;font-size:11px;font-style:normal;font-weight:800;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .ba-phase2-settings-card{border:1px solid rgba(0,103,95,.14);border-radius:22px;background:linear-gradient(135deg,#ffffff,#f0fdfa);padding:14px;display:grid;grid-template-columns:minmax(0,1fr) minmax(340px,.95fr);gap:14px;align-items:center;box-shadow:0 14px 34px rgba(15,23,42,.055);}
  .ba-phase2-settings-card h3{margin:5px 0 0;color:#0f172a;font-size:18px;font-weight:950;letter-spacing:-.035em;}
  .ba-phase2-settings-card p{margin:6px 0 0;color:#64748b;font-size:12.5px;line-height:1.45;font-weight:760;}
  .ba-phase2-settings-actions{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;}
  .ba-phase2-settings-actions button,.ba-phase2-settings-actions select{height:38px;border-radius:13px;border:1px solid #dbe5ee;background:#fff;color:#334155;padding:0 10px;font-size:11.5px;font-weight:950;outline:none;cursor:pointer;}
  .ba-phase2-settings-actions button.is-active{background:#00675f;border-color:#00675f;color:#fff;}
  .ba-phase2-settings-actions button:disabled,.ba-phase2-settings-actions select:disabled{opacity:.55;cursor:not-allowed;}
  .ba-phase2-settings-status{grid-column:1 / -1;color:#00675f;font-style:normal;font-size:12px;font-weight:900;}
  @media(max-width:880px){.ba-phase2-strip{grid-template-columns:repeat(2,minmax(0,1fr));}.ba-phase2-settings-card{grid-template-columns:1fr;}.ba-phase2-settings-actions{grid-template-columns:1fr 1fr;}}
  @media(max-width:560px){.ba-phase2-strip{grid-template-columns:1fr 1fr}.ba-phase2-settings-actions{grid-template-columns:1fr}.ba-phase2-strip article{padding:10px}.ba-phase2-strip em{white-space:normal;}}

  /* LOOP PHASE 2.1.4 — audit log UX filters */
  .ba-audit-filters{
    margin:10px 0 12px;
    border:1px solid #e2e8f0;
    border-radius:18px;
    background:#f8fafc;
    padding:12px;
    display:grid;
    grid-template-columns:minmax(180px,1.35fr) minmax(150px,.8fr) minmax(150px,.8fr) minmax(120px,.55fr) minmax(120px,.55fr);
    gap:10px;
    align-items:end;
  }
  .ba-audit-filters label{display:grid;gap:5px;min-width:0;}
  .ba-audit-filters label span{color:#64748b;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;}
  .ba-audit-filters input,.ba-audit-filters select{height:38px;border:1px solid #dbe5ee;border-radius:13px;background:#fff;color:#0f172a;padding:0 10px;font-size:12px;font-weight:850;outline:none;min-width:0;}
  .ba-audit-toggle-row{grid-column:1 / -1;display:flex;gap:8px;flex-wrap:wrap;}
  .ba-audit-toggle-row button{min-height:34px;border:1px solid #dbe5ee;border-radius:999px;background:#fff;color:#475569;padding:0 12px;font-size:11px;font-weight:950;cursor:pointer;}
  .ba-audit-toggle-row button.active{background:#00675f;border-color:#00675f;color:#fff;}
  .ba-audit-summary-row{display:flex;gap:8px;flex-wrap:wrap;margin:0 0 12px;}
  .ba-audit-summary-row span{border:1px solid #e2e8f0;background:#fff;border-radius:999px;padding:6px 10px;color:#64748b;font-size:11px;font-weight:900;}
  .ba-audit-summary-row strong{color:#0f172a;}
  .ba-audit-row.warning{background:#fffbeb;border-color:#fde68a;}
  @media(max-width:880px){.ba-audit-filters{grid-template-columns:1fr 1fr;}.ba-audit-filters label:first-child{grid-column:1 / -1;}}
  @media(max-width:560px){.ba-audit-filters{grid-template-columns:1fr;}.ba-audit-filters label:first-child{grid-column:auto;}}


  /* LOOP CRM ADVANCED — Customer 360 + segmentation */
  .ba-retention-action-center{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;margin:4px 0 2px;}
  .ba-retention-card{border:1px solid #e2e8f0;background:#fff;border-radius:20px;padding:14px;display:grid;gap:10px;box-shadow:0 14px 34px rgba(15,23,42,.055);}
  .ba-retention-card.success{background:linear-gradient(180deg,#ffffff,#f0fdf4);border-color:#bbf7d0;}.ba-retention-card.warning{background:linear-gradient(180deg,#ffffff,#fffbeb);border-color:#fde68a;}.ba-retention-card.primary{background:linear-gradient(180deg,#ffffff,#eff6ff);border-color:#bfdbfe;}
  .ba-retention-card span{display:block;color:#64748b;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;}
  .ba-retention-card h4{margin:3px 0 0;color:#0f172a;font-size:15px;font-weight:950;letter-spacing:-.02em;}
  .ba-retention-card p{margin:5px 0 0;color:#475569;font-size:12px;line-height:1.35;font-weight:750;}
  .ba-retention-card>strong{color:var(--brand);font-size:28px;line-height:1;font-weight:950;letter-spacing:-.06em;}
  .ba-retention-card>em{color:#64748b;font-size:11.5px;font-style:normal;font-weight:850;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .ba-retention-actions{display:flex;gap:6px;flex-wrap:wrap;}
  .ba-retention-actions button,.ba-retention-actions a{min-height:34px;border-radius:999px;border:1px solid #e2e8f0;background:#fff;color:#0f172a;padding:0 10px;font-size:11px;font-weight:950;text-decoration:none;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;}
  .ba-retention-actions a{background:var(--brand);border-color:var(--brand);color:#fff;}
  @media(max-width:1180px){.ba-retention-action-center{grid-template-columns:repeat(2,minmax(0,1fr));}}
  @media(max-width:620px){.ba-retention-action-center{grid-template-columns:1fr;}.ba-retention-card{border-radius:18px;}}
  .ba-crm-module{display:grid;gap:16px;margin-top:18px;}
  .ba-finance-hero.crm{border-color:rgba(20,184,166,.24);background:radial-gradient(circle at 100% 0%,rgba(0,103,95,.12),transparent 34%),linear-gradient(135deg,#f0fdfa,#ffffff);}
  .ba-crm-filters{display:grid;grid-template-columns:minmax(0,1fr) minmax(190px,260px) auto;gap:8px;margin:12px 0;align-items:center;}
  .ba-crm-filters input,.ba-crm-filters select{width:100%;min-height:42px;border:1px solid #dbe5ee;border-radius:14px;background:#fff;color:#0f172a;padding:0 12px;font-size:13px;font-weight:850;outline:none;}
  .ba-crm-filters button{min-height:42px;border:1px solid #e2e8f0;border-radius:14px;background:#fff;color:#334155;padding:0 13px;font-size:12px;font-weight:950;cursor:pointer;}
  .ba-crm-whatsapp-status{margin:10px 0 0;padding:10px 12px;border:1px solid #cdebe7;background:#f0fdfa;color:#0f766e;border-radius:14px;font-weight:900;font-size:12px;}
.ba-crm-table{display:grid;gap:8px;margin-top:10px;}
.ba-crm-whatsapp-cell{display:flex;flex-direction:column;gap:5px;min-width:128px;}
.ba-crm-whatsapp-cell span{font-size:10px;text-transform:uppercase;letter-spacing:.08em;color:#64748b;font-weight:900;}
.ba-whatsapp-pref{display:inline-flex;align-items:center;width:max-content;max-width:150px;padding:5px 8px;border-radius:999px;font-size:11px;font-weight:950;border:1px solid #dbeafe;background:#eff6ff;color:#1d4ed8;}
.ba-whatsapp-pref.ready{border-color:#99f6e4;background:#ecfdf5;color:#047857;}
.ba-whatsapp-pref.blocked{border-color:#fecaca;background:#fef2f2;color:#b91c1c;}
.ba-whatsapp-pref.warning{border-color:#fde68a;background:#fffbeb;color:#92400e;}
.ba-whatsapp-pref.muted{border-color:#e2e8f0;background:#f8fafc;color:#64748b;}
.ba-crm-whatsapp-cell button{border:1px solid #d8e7e4;background:#fff;color:#00675f;border-radius:999px;padding:6px 9px;font-weight:900;font-size:11px;cursor:pointer;}
.ba-crm-whatsapp-cell button:disabled{opacity:.55;cursor:not-allowed;}
  .ba-crm-row{display:grid;grid-template-columns:minmax(220px,1.35fr) repeat(4,minmax(90px,.7fr)) minmax(95px,.45fr);gap:10px;align-items:center;border:1px solid #e8eef5;background:#fff;border-radius:18px;padding:11px;}
  .ba-crm-row>div{min-width:0;}
  .ba-crm-row span{display:block;color:#64748b;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;}
  .ba-crm-row strong{display:block;color:#0f172a;font-size:12.5px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .ba-crm-row em{display:block;margin-top:3px;color:#64748b;font-size:11px;font-style:normal;font-weight:800;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .ba-crm-person strong{font-size:14px;}
  .ba-crm-person>span{text-transform:none;letter-spacing:0;font-size:11.5px;font-weight:800;margin-top:3px;}
  .ba-crm-tags{display:flex;gap:5px;flex-wrap:wrap;margin-top:7px;}
  .ba-crm-tags em{display:inline-flex;margin:0;border-radius:999px;padding:5px 7px;font-size:10px;font-weight:950;background:#f1f5f9;color:#475569;}
  .ba-crm-tags em.success{background:#ecfdf5;color:#047857;}.ba-crm-tags em.warning{background:#fffbeb;color:#b45309;}.ba-crm-tags em.primary{background:#eff6ff;color:#1d4ed8;}
  .ba-crm-actions{display:flex;justify-content:flex-end;align-items:center;gap:6px;}
  .ba-crm-actions a{display:inline-flex;align-items:center;justify-content:center;min-height:32px;border-radius:999px;background:var(--brand);color:#fff;text-decoration:none;padding:0 10px;font-size:11px;font-weight:950;}
  .ba-crm-actions button{display:inline-flex;align-items:center;justify-content:center;min-height:32px;border-radius:999px;border:1px solid #dbe5ee;background:#fff;color:#0f172a;padding:0 10px;font-size:11px;font-weight:950;cursor:pointer;}
  .ba-crm-actions button:disabled,.ba-retention-actions button:disabled{opacity:.46;cursor:not-allowed;}
  .ba-retention-actions button.primary{background:#0f172a;border-color:#0f172a;color:#fff;}
  .ba-crm-action-overlay{position:fixed;inset:0;z-index:2147483000;background:rgba(15,23,42,.40);display:flex;align-items:center;justify-content:center;padding:18px;backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);}

  /* LOOP PHASE 2.2D — CRM Follow-up Pipeline */
  .ba-crm-followup-strip{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;margin:14px 0 18px;}
  .ba-crm-followup-strip article{border:1px solid rgba(226,232,240,.88);border-radius:18px;background:#fff;padding:14px 15px;box-shadow:0 12px 30px rgba(15,23,42,.06);}
  .ba-crm-followup-strip span{display:block;color:#64748b;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;}
  .ba-crm-followup-strip strong{display:block;margin-top:6px;color:#0f172a;font-size:28px;font-weight:950;letter-spacing:-.05em;}
  .ba-crm-followup-strip em{display:block;margin-top:4px;color:#64748b;font-size:12px;font-weight:750;font-style:normal;}
  .ba-crm-followup-strip article.warning{background:linear-gradient(135deg,#fff7ed,#fff);border-color:#fed7aa;}
  .ba-crm-followup-strip article.success{background:linear-gradient(135deg,#ecfdf5,#fff);border-color:#bbf7d0;}
  .ba-crm-followup-strip article.primary{background:linear-gradient(135deg,#eff6ff,#fff);border-color:#bfdbfe;}
  .ba-followup-label{display:inline-flex!important;align-items:center;width:max-content;border-radius:999px;padding:4px 9px;font-size:11px!important;font-weight:950!important;}
  .ba-followup-label.success{background:#dcfce7;color:#047857;}
  .ba-followup-label.warning{background:#ffedd5;color:#c2410c;}
  .ba-followup-label.primary{background:#dbeafe;color:#1d4ed8;}
  .ba-followup-label.neutral{background:#f1f5f9;color:#475569;}
  .ba-followup-label.muted{background:#f8fafc;color:#94a3b8;}
  @media(max-width:900px){.ba-crm-followup-strip{grid-template-columns:repeat(2,minmax(0,1fr));}}
  @media(max-width:560px){.ba-crm-followup-strip{grid-template-columns:1fr;}}

  .ba-crm-action-modal{width:min(560px,100%);max-height:min(760px,92vh);overflow:auto;border:1px solid rgba(226,232,240,.88);border-radius:24px;background:#fff;box-shadow:0 30px 90px rgba(15,23,42,.30);}
  .ba-crm-action-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;padding:18px 18px 12px;border-bottom:1px solid #eef2f7;background:linear-gradient(135deg,#f0fdfa,#fff);}
  .ba-crm-action-head span{display:block;color:#00675f;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.1em;}.ba-crm-action-head h3{margin:4px 0 0;color:#0f172a;font-size:20px;font-weight:950;letter-spacing:-.04em;}.ba-crm-action-head p{margin:5px 0 0;color:#64748b;font-size:12px;font-weight:850;}.ba-crm-action-head button{width:36px;height:36px;border-radius:999px;border:1px solid #e2e8f0;background:#fff;color:#0f172a;font-size:24px;line-height:1;cursor:pointer;}
  .ba-crm-action-body{padding:16px 18px;display:grid;gap:14px;}.ba-crm-action-insight{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;}.ba-crm-action-insight div{border:1px solid #e2e8f0;border-radius:16px;background:#f8fafc;padding:10px;min-width:0;}.ba-crm-action-insight span,.ba-crm-action-field span{display:block;color:#64748b;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;}.ba-crm-action-insight strong{display:block;margin-top:4px;color:#0f172a;font-size:12px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .ba-crm-action-field{display:grid;gap:7px;}.ba-crm-action-field textarea{width:100%;min-height:132px;border:1px solid #dbe5ee;border-radius:18px;background:#fff;color:#0f172a;padding:12px;font-size:14px;font-weight:760;line-height:1.45;outline:none;resize:vertical;}.ba-crm-action-field textarea:focus{border-color:rgba(0,103,95,.35);box-shadow:0 0 0 4px rgba(20,184,166,.12);}
  .ba-crm-action-status{border-radius:14px;background:#f0fdfa;color:#00675f;padding:10px 12px;font-size:12px;font-weight:900;}.ba-crm-action-foot{display:flex;justify-content:flex-end;gap:8px;flex-wrap:wrap;padding:14px 18px 18px;border-top:1px solid #eef2f7;background:#fff;}.ba-crm-action-foot button{min-height:40px;border-radius:999px;border:1px solid #e2e8f0;background:#fff;color:#0f172a;padding:0 14px;font-size:12px;font-weight:950;cursor:pointer;}.ba-crm-action-foot button.primary{background:#00675f;border-color:#00675f;color:#fff;}.ba-crm-action-foot button:disabled{opacity:.5;cursor:not-allowed;}
  @media(max-width:620px){.ba-crm-action-overlay{align-items:flex-end;padding:10px;}.ba-crm-action-modal{border-radius:22px;max-height:88vh;}.ba-crm-action-insight{grid-template-columns:1fr;}.ba-crm-action-foot{display:grid;grid-template-columns:1fr;}.ba-crm-action-foot button{width:100%;}}
  .ba-crm-segments{display:grid;gap:8px;}
  .ba-crm-segments button{border:1px solid #e2e8f0;background:#fff;border-radius:14px;padding:10px 12px;display:flex;align-items:center;justify-content:space-between;gap:10px;cursor:pointer;}
  .ba-crm-segments button.active{border-color:rgba(0,103,95,.35);background:#f0fdfa;}
  .ba-crm-segments span{color:#0f172a;font-size:12px;font-weight:950;}
  .ba-crm-segments strong{color:var(--brand);font-size:13px;font-weight:950;}
  @media(max-width:980px){.ba-crm-row{grid-template-columns:1fr 1fr;}.ba-crm-person,.ba-crm-actions{grid-column:1 / -1;}.ba-crm-actions{justify-content:flex-start;}.ba-crm-filters{grid-template-columns:1fr;}}
  @media(max-width:560px){.ba-crm-row{grid-template-columns:1fr;}}



  /* LOOP PHASE 2.7 — Smart Replenishment / Lista de compra sugerida */
  .ba-replenishment-command{display:grid;gap:14px;margin:18px 0 20px;}
  .ba-replenishment-hero{border:1px solid rgba(226,232,240,.92);background:linear-gradient(135deg,#ffffff,#f8fafc);border-radius:26px;padding:18px;display:grid;grid-template-columns:1fr auto;gap:16px;align-items:center;box-shadow:0 18px 42px rgba(15,23,42,.065);position:relative;overflow:hidden;}
  .ba-replenishment-hero:before{content:"";position:absolute;right:-48px;top:-48px;width:150px;height:150px;border-radius:999px;background:rgba(20,184,166,.10);}
  .ba-replenishment-hero.success{border-color:rgba(16,185,129,.24);background:linear-gradient(135deg,#ecfdf5,#fff);}
  .ba-replenishment-hero.warning{border-color:rgba(245,158,11,.30);background:linear-gradient(135deg,#fffbeb,#fff);}
  .ba-replenishment-hero.critical{border-color:rgba(239,68,68,.24);background:linear-gradient(135deg,#fef2f2,#fff);}
  .ba-replenishment-hero span,.ba-replenishment-kpis article span{font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.09em;color:var(--muted);}
  .ba-replenishment-hero h3{margin:4px 0 0;color:var(--ink);font-size:24px;line-height:1.05;letter-spacing:-.055em;font-weight:950;}
  .ba-replenishment-hero p{margin:6px 0 0;color:var(--muted);font-size:13px;font-weight:760;line-height:1.45;max-width:720px;}
  .ba-replenishment-hero em{display:block;margin-top:8px;color:#00675f;font-size:12px;font-weight:900;font-style:normal;}
  .ba-replenishment-hero>strong{position:relative;z-index:1;color:#00675f;font-size:34px;font-weight:950;letter-spacing:-.065em;line-height:1;text-align:right;white-space:nowrap;}
  .ba-replenishment-hero>small{position:relative;z-index:1;color:var(--muted);font-size:12px;font-weight:850;text-align:right;margin-top:-12px;}
  .ba-replenishment-kpis{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;}
  .ba-replenishment-kpis article{border:1px solid var(--line);background:#fff;border-radius:18px;padding:14px;display:grid;gap:4px;box-shadow:0 10px 24px rgba(15,23,42,.045);}
  .ba-replenishment-kpis article strong{font-size:24px;font-weight:950;color:var(--ink);letter-spacing:-.05em;}
  .ba-replenishment-kpis article em{font-style:normal;color:var(--muted);font-size:11.5px;font-weight:780;line-height:1.25;}
  .ba-replenishment-panel{box-shadow:0 14px 34px rgba(15,23,42,.055);}
  .ba-replenishment-list{display:grid;gap:10px;}
  .ba-replenishment-row{border:1px solid #e2e8f0;background:#fff;border-radius:18px;padding:13px;display:grid;grid-template-columns:minmax(0,1.5fr) minmax(170px,.55fr) auto;gap:14px;align-items:center;}
  .ba-replenishment-row.low{border-color:rgba(245,158,11,.25);background:linear-gradient(135deg,#fffbeb,#fff);}
  .ba-replenishment-row.critical{border-color:rgba(239,68,68,.22);background:linear-gradient(135deg,#fef2f2,#fff);}
  .ba-replenishment-main{min-width:0;display:grid;gap:3px;}
  .ba-replenishment-main strong{color:var(--ink);font-size:14px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .ba-replenishment-main span{color:#475569;font-size:12.5px;font-weight:850;}
  .ba-replenishment-main em{color:var(--muted);font-size:11.5px;font-style:normal;font-weight:760;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .ba-replenishment-buy{border:1px solid #e2e8f0;background:#f8fafc;border-radius:16px;padding:10px 12px;display:grid;gap:3px;}
  .ba-replenishment-buy span{color:#00675f;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;}
  .ba-replenishment-buy strong{color:var(--ink);font-size:13px;font-weight:950;white-space:nowrap;}
  .ba-replenishment-buy em{color:var(--muted);font-size:11.5px;font-style:normal;font-weight:800;}
  .ba-replenishment-actions{display:flex;align-items:center;gap:8px;flex-wrap:wrap;justify-content:flex-end;}
  .ba-replenishment-actions button{min-height:36px;border-radius:999px;border:1px solid #dbe5ee;background:#fff;color:#0f172a;padding:0 12px;font-size:11.5px;font-weight:950;cursor:pointer;}
  .ba-replenishment-actions button.primary{background:#00675f;border-color:#00675f;color:#fff;box-shadow:0 10px 20px rgba(0,103,95,.16);}
  .ba-replenishment-actions button:disabled{opacity:.45;cursor:not-allowed;box-shadow:none;}
  @media(max-width:1100px){.ba-replenishment-kpis{grid-template-columns:repeat(2,minmax(0,1fr));}.ba-replenishment-row{grid-template-columns:1fr;}.ba-replenishment-actions{justify-content:flex-start;}}
  @media(max-width:680px){.ba-replenishment-hero{grid-template-columns:1fr;}.ba-replenishment-hero>strong,.ba-replenishment-hero>small{text-align:left;}.ba-replenishment-kpis{grid-template-columns:1fr 1fr;}.ba-replenishment-actions button{flex:1 1 auto;}}



  /* LOOP PHASE 2.6 — Daily Cash Closing / Caja del día */
  .ba-daily-close-dashboard{display:grid;gap:14px;margin:18px 0 20px;}
  .ba-daily-close-hero{border:1px solid rgba(226,232,240,.9);background:linear-gradient(135deg,#ffffff,#f8fafc);border-radius:26px;padding:18px;display:grid;grid-template-columns:1fr auto;gap:16px;align-items:center;box-shadow:0 18px 42px rgba(15,23,42,.065);position:relative;overflow:hidden;}
  .ba-daily-close-hero:before{content:"";position:absolute;right:-44px;top:-46px;width:150px;height:150px;border-radius:999px;background:rgba(20,184,166,.10);}
  .ba-daily-close-hero.success{border-color:rgba(16,185,129,.25);background:linear-gradient(135deg,#ecfdf5,#fff);}
  .ba-daily-close-hero.warning{border-color:rgba(245,158,11,.30);background:linear-gradient(135deg,#fffbeb,#fff);}
  .ba-daily-close-hero.primary{border-color:rgba(20,184,166,.28);background:linear-gradient(135deg,#f0fdfa,#fff);}
  .ba-daily-close-hero span,.ba-daily-close-kpis article span,.ba-daily-close-status-grid article span{font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.09em;color:var(--muted);}
  .ba-daily-close-hero h3{margin:4px 0 0;color:var(--ink);font-size:24px;line-height:1.05;letter-spacing:-.055em;font-weight:950;}
  .ba-daily-close-hero p{margin:6px 0 0;color:var(--muted);font-size:13px;font-weight:760;line-height:1.45;max-width:720px;}
  .ba-daily-close-hero em{display:block;margin-top:8px;color:#00675f;font-size:12px;font-weight:900;font-style:normal;text-transform:capitalize;}
  .ba-daily-close-hero>strong{position:relative;z-index:1;color:#00675f;font-size:34px;font-weight:950;letter-spacing:-.065em;line-height:1;text-align:right;}
  .ba-daily-close-hero>small{position:relative;z-index:1;color:var(--muted);font-size:12px;font-weight:850;text-align:right;margin-top:-12px;}
  .ba-daily-close-kpis{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;}
  .ba-daily-close-kpis article,.ba-daily-close-status-grid article{border:1px solid var(--line);background:#fff;border-radius:18px;padding:14px;display:grid;gap:4px;box-shadow:0 10px 24px rgba(15,23,42,.045);}
  .ba-daily-close-kpis article strong{font-size:24px;font-weight:950;color:var(--ink);letter-spacing:-.05em;}
  .ba-daily-close-kpis article em{font-style:normal;color:var(--muted);font-size:11.5px;font-weight:780;line-height:1.25;}
  .ba-daily-close-status-grid{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:10px;}
  .ba-daily-close-status-grid article strong{font-size:24px;font-weight:950;color:var(--ink);line-height:1;}
  .ba-daily-close-status-grid article.success{border-color:rgba(16,185,129,.22);background:#ecfdf5;}
  .ba-daily-close-status-grid article.warning{border-color:rgba(245,158,11,.24);background:#fffbeb;}
  .ba-daily-close-status-grid article.primary{border-color:rgba(20,184,166,.24);background:#f0fdfa;}
  .ba-daily-close-grid{display:grid;grid-template-columns:1.1fr .9fr;gap:14px;}
  .ba-daily-close-panel{box-shadow:0 14px 34px rgba(15,23,42,.055);}
  .ba-daily-action-list{display:grid;gap:10px;}
  .ba-daily-action{border:1px solid #e2e8f0;background:#fff;border-radius:16px;padding:12px;display:grid;grid-template-columns:1fr auto;gap:2px 12px;align-items:center;}
  .ba-daily-action.warning{border-color:rgba(245,158,11,.28);background:#fffbeb;}
  .ba-daily-action.success{border-color:rgba(16,185,129,.24);background:#ecfdf5;}
  .ba-daily-action.primary{border-color:rgba(20,184,166,.24);background:#f0fdfa;}
  .ba-daily-action span{grid-column:1;color:var(--ink);font-size:13px;font-weight:950;}
  .ba-daily-action strong{grid-column:2;grid-row:1 / span 2;color:#00675f;font-size:18px;font-weight:950;}
  .ba-daily-action em{grid-column:1;color:var(--muted);font-size:12px;font-style:normal;font-weight:760;}
  .ba-daily-review-list{display:grid;gap:8px;}
  .ba-daily-review-row{border:1px solid #e2e8f0;background:#fff;border-radius:16px;padding:11px 12px;display:grid;grid-template-columns:minmax(0,1fr) auto auto;gap:12px;align-items:center;}
  .ba-daily-review-row span{min-width:0;display:grid;gap:2px;}
  .ba-daily-review-row span strong{color:var(--ink);font-size:13px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .ba-daily-review-row span em{color:var(--muted);font-size:11.5px;font-weight:760;font-style:normal;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .ba-daily-review-row small{border-radius:999px;background:#f8fafc;border:1px solid #e2e8f0;color:#475569;padding:7px 10px;font-size:11px;font-weight:900;white-space:nowrap;}
  .ba-daily-review-row b{color:#00675f;font-size:14px;font-weight:950;white-space:nowrap;}
  @media(max-width:1100px){.ba-daily-close-kpis{grid-template-columns:repeat(2,minmax(0,1fr));}.ba-daily-close-status-grid{grid-template-columns:repeat(3,minmax(0,1fr));}.ba-daily-close-grid{grid-template-columns:1fr;}}
  @media(max-width:680px){.ba-daily-close-hero{grid-template-columns:1fr;}.ba-daily-close-hero>strong,.ba-daily-close-hero>small{text-align:left;}.ba-daily-close-kpis,.ba-daily-close-status-grid{grid-template-columns:1fr 1fr;}.ba-daily-review-row{grid-template-columns:1fr;}.ba-daily-review-row small,.ba-daily-review-row b{width:max-content;}}


  /* LOOP PHASE 2.3 — CRM Smart Tasks / light automations */
  .ba-crm-smart-tasks{margin:16px 0 18px;border:1px solid rgba(226,232,240,.88);background:linear-gradient(135deg,#ffffff,#f8fafc);border-radius:24px;padding:16px;box-shadow:0 16px 38px rgba(15,23,42,.06);display:grid;gap:14px;}
  .ba-crm-smart-head{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;}
  .ba-crm-smart-head span{display:block;color:#00675f;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.09em;}
  .ba-crm-smart-head h3{margin:4px 0 0;color:var(--ink);font-size:22px;line-height:1.06;letter-spacing:-.05em;font-weight:950;}
  .ba-crm-smart-head p{margin:6px 0 0;color:var(--muted);font-size:13px;font-weight:780;line-height:1.42;max-width:720px;}
  .ba-crm-smart-head>div:last-child{min-width:112px;border:1px solid rgba(20,184,166,.18);background:#f0fdfa;border-radius:18px;padding:11px 13px;text-align:right;}
  .ba-crm-smart-head>div:last-child strong{display:block;color:#00675f;font-size:28px;font-weight:950;line-height:1;letter-spacing:-.06em;}
  .ba-crm-smart-head>div:last-child em{display:block;margin-top:4px;color:#64748b;font-style:normal;font-size:11px;font-weight:850;}
  .ba-crm-smart-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;}
  .ba-crm-smart-card{border:1px solid #e2e8f0;background:#fff;border-radius:20px;padding:14px;display:grid;gap:8px;box-shadow:0 10px 24px rgba(15,23,42,.045);}
  .ba-crm-smart-card.has-work.primary{border-color:rgba(20,184,166,.26);background:linear-gradient(135deg,#f0fdfa,#fff);}
  .ba-crm-smart-card.has-work.warning{border-color:rgba(245,158,11,.28);background:linear-gradient(135deg,#fffbeb,#fff);}
  .ba-crm-smart-card.has-work.success,.ba-crm-smart-card.has-work.green{border-color:rgba(16,185,129,.26);background:linear-gradient(135deg,#ecfdf5,#fff);}
  .ba-crm-smart-card.has-work.blue{border-color:rgba(59,130,246,.24);background:linear-gradient(135deg,#eff6ff,#fff);}
  .ba-crm-smart-card.is-empty{opacity:.76;background:#fbfdff;}
  .ba-crm-smart-card-top{display:flex;align-items:center;justify-content:space-between;gap:8px;}
  .ba-crm-smart-card-top span{color:var(--muted);font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;}
  .ba-crm-smart-card-top strong{color:#00675f;font-size:20px;font-weight:950;letter-spacing:-.04em;}
  .ba-crm-smart-card h4{margin:0;color:var(--ink);font-size:15px;line-height:1.12;font-weight:950;letter-spacing:-.025em;}
  .ba-crm-smart-card p{margin:0;color:var(--muted);font-size:12.5px;font-weight:760;line-height:1.38;}
  .ba-crm-smart-card>em{color:#475569;font-size:12px;font-style:normal;font-weight:900;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .ba-crm-smart-actions{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:2px;}
  .ba-crm-smart-actions button{min-height:34px;border-radius:999px;border:1px solid #dbe5ee;background:#fff;color:#0f172a;padding:0 11px;font-size:11px;font-weight:950;cursor:pointer;}
  .ba-crm-smart-actions button.primary{background:#0f172a;border-color:#0f172a;color:#fff;}
  .ba-crm-smart-actions button:disabled{opacity:.45;cursor:not-allowed;}
  .ba-crm-smart-note{margin:0;border-radius:16px;background:#f8fafc;border:1px dashed #cbd5e1;color:#64748b;padding:10px 12px;font-size:12px;font-weight:850;}
  @media(max-width:1100px){.ba-crm-smart-grid{grid-template-columns:repeat(2,minmax(0,1fr));}}
  @media(max-width:680px){.ba-crm-smart-head{display:grid;}.ba-crm-smart-head>div:last-child{text-align:left;}.ba-crm-smart-grid{grid-template-columns:1fr;}}

  /* LOOP PHASE 2.2F — CRM Executive Dashboard */
  .ba-crm-exec-dashboard{display:grid;grid-template-columns:1.05fr 1.35fr .85fr;gap:14px;margin:16px 0 18px;align-items:stretch;}
  .ba-crm-exec-priority{border:1px solid rgba(20,184,166,.24);background:linear-gradient(135deg,#f0fdfa,#fff 58%,#ecfeff);border-radius:24px;padding:18px;box-shadow:0 18px 44px rgba(15,23,42,.075);display:grid;align-content:start;gap:10px;position:relative;overflow:hidden;}
  .ba-crm-exec-priority:before{content:"";position:absolute;right:-38px;top:-42px;width:132px;height:132px;border-radius:999px;background:rgba(20,184,166,.10);}
  .ba-crm-exec-priority span,.ba-crm-exec-list span,.ba-crm-exec-kpis article span{font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.09em;color:var(--muted);}
  .ba-crm-exec-priority h3{margin:0;color:var(--ink);font-size:22px;line-height:1.04;letter-spacing:-.055em;font-weight:950;max-width:330px;}
  .ba-crm-exec-priority p{margin:0;color:var(--muted);font-size:13px;line-height:1.45;font-weight:750;max-width:360px;}
  .ba-crm-exec-priority strong{font-size:30px;line-height:1;color:#00675f;font-weight:950;letter-spacing:-.06em;}
  .ba-crm-exec-priority em{font-size:12px;font-style:normal;color:var(--muted);font-weight:850;}
  .ba-crm-exec-priority button{width:max-content;border:0;border-radius:999px;background:#00675f;color:#fff;padding:10px 14px;font-size:12px;font-weight:950;cursor:pointer;box-shadow:0 12px 24px rgba(0,103,95,.18);}
  .ba-crm-exec-kpis{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;}
  .ba-crm-exec-kpis article{border:1px solid var(--line);background:#fff;border-radius:18px;padding:14px;box-shadow:0 10px 24px rgba(15,23,42,.05);display:grid;gap:4px;}
  .ba-crm-exec-kpis article strong{font-size:23px;font-weight:950;color:var(--ink);letter-spacing:-.05em;}
  .ba-crm-exec-kpis article em{font-size:11.5px;font-style:normal;color:var(--muted);font-weight:750;line-height:1.25;}
  .ba-crm-exec-kpis article.success{border-color:rgba(16,185,129,.22);background:linear-gradient(135deg,#ecfdf5,#fff);}
  .ba-crm-exec-kpis article.warning{border-color:rgba(245,158,11,.24);background:linear-gradient(135deg,#fffbeb,#fff);}
  .ba-crm-exec-kpis article.primary{border-color:rgba(20,184,166,.24);background:linear-gradient(135deg,#f0fdfa,#fff);}
  .ba-crm-exec-list{border:1px solid var(--line);background:#fff;border-radius:22px;padding:14px;box-shadow:0 12px 30px rgba(15,23,42,.06);display:grid;gap:10px;}
  .ba-crm-exec-list>div{display:flex;align-items:center;justify-content:space-between;gap:10px;}
  .ba-crm-exec-list>div strong{font-size:18px;font-weight:950;color:var(--ink);}
  .ba-crm-exec-list button{border:1px solid #e2e8f0;background:#f8fafc;border-radius:15px;padding:10px;text-align:left;display:grid;grid-template-columns:1fr auto;gap:3px 8px;cursor:pointer;}
  .ba-crm-exec-list button span{grid-column:1;letter-spacing:0;text-transform:none;font-size:13px;color:var(--ink);}
  .ba-crm-exec-list button strong{grid-column:2;grid-row:1 / span 2;color:#00675f;font-weight:950;}
  .ba-crm-exec-list button em{grid-column:1;font-style:normal;color:var(--muted);font-size:11.5px;font-weight:760;}
  .ba-crm-exec-list p{margin:0;color:var(--muted);font-size:13px;font-weight:750;line-height:1.4;}
  @media (max-width: 1180px){.ba-crm-exec-dashboard{grid-template-columns:1fr;}.ba-crm-exec-kpis{grid-template-columns:repeat(3,minmax(0,1fr));}}
  @media (max-width: 720px){.ba-crm-exec-kpis{grid-template-columns:repeat(2,minmax(0,1fr));}.ba-crm-exec-priority h3{font-size:20px;}.ba-crm-exec-priority strong{font-size:26px;}}
  @media (max-width: 480px){.ba-crm-exec-kpis{grid-template-columns:1fr;}}

  /* LOOP PHASE 2.2E — CRM Revenue Attribution */
  .ba-crm-revenue-strip{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;margin:14px 0 18px;}
  .ba-crm-revenue-strip article{border:1px solid var(--line);background:#fff;border-radius:18px;padding:15px;box-shadow:0 12px 28px rgba(15,23,42,.055);display:grid;gap:5px;}
  .ba-crm-revenue-strip article span{font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;color:var(--muted);}
  .ba-crm-revenue-strip article strong{font-size:22px;font-weight:950;color:var(--ink);letter-spacing:-.04em;}
  .ba-crm-revenue-strip article em{font-size:12px;font-style:normal;color:var(--muted);font-weight:750;}
  .ba-crm-revenue-strip article.success{border-color:rgba(16,185,129,.24);background:linear-gradient(135deg,#ecfdf5,#fff);}
  .ba-crm-revenue-strip article.primary{border-color:rgba(20,184,166,.24);background:linear-gradient(135deg,#f0fdfa,#fff);}
  .ba-crm-revenue-strip article.warning{border-color:rgba(245,158,11,.24);background:linear-gradient(135deg,#fffbeb,#fff);}
  .ba-attribution-label.success{color:#047857!important;}
  .ba-attribution-label.muted{color:#94a3b8!important;}
  @media (max-width: 980px){.ba-crm-revenue-strip{grid-template-columns:repeat(2,minmax(0,1fr));}}
  @media (max-width: 640px){.ba-crm-revenue-strip{grid-template-columns:1fr;}}

  /* LOOP PHASE 2.8 — Inventory Automation Bridge */
  .ba-inventory-automation{display:grid;gap:12px;margin:16px 0 18px;}
  .ba-inventory-automation-hero{border:1px solid rgba(20,184,166,.22);background:linear-gradient(135deg,#f0fdfa,#fff 60%,#ecfeff);border-radius:24px;padding:17px;display:grid;grid-template-columns:1fr auto auto auto;gap:14px;align-items:center;box-shadow:0 18px 42px rgba(15,23,42,.06);}
  .ba-inventory-automation-hero.is-off{background:linear-gradient(135deg,#f8fafc,#fff);border-color:#e2e8f0;}
  .ba-inventory-automation-hero span{display:block;color:#00675f;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.09em;}
  .ba-inventory-automation-hero h3{margin:4px 0 0;color:var(--ink);font-size:22px;line-height:1.05;letter-spacing:-.05em;font-weight:950;}
  .ba-inventory-automation-hero p{margin:6px 0 0;color:var(--muted);font-size:13px;font-weight:780;line-height:1.42;max-width:760px;}
  .ba-inventory-automation-hero>strong{font-size:31px;font-weight:950;color:#00675f;letter-spacing:-.06em;text-align:right;}
  .ba-inventory-automation-hero>em{font-style:normal;color:var(--muted);font-size:12px;font-weight:900;white-space:nowrap;}
  .ba-inventory-automation-hero>button{border:0;border-radius:999px;background:#00675f;color:#fff;padding:11px 14px;font-size:12px;font-weight:950;cursor:pointer;box-shadow:0 12px 24px rgba(0,103,95,.18);white-space:nowrap;}
  .ba-inventory-automation-hero>button:disabled{opacity:.45;cursor:not-allowed;box-shadow:none;}
  .ba-inventory-automation-kpis{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;}
  .ba-inventory-automation-kpis article{border:1px solid var(--line);background:#fff;border-radius:18px;padding:14px;box-shadow:0 10px 24px rgba(15,23,42,.045);display:grid;gap:4px;}
  .ba-inventory-automation-kpis article span{font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;color:var(--muted);}
  .ba-inventory-automation-kpis article strong{font-size:22px;font-weight:950;color:var(--ink);letter-spacing:-.04em;}
  .ba-inventory-automation-kpis article em{font-style:normal;color:var(--muted);font-size:11.5px;font-weight:760;}
  .ba-inventory-automation-kpis article.success{border-color:rgba(16,185,129,.22);background:linear-gradient(135deg,#ecfdf5,#fff);}
  .ba-inventory-automation-kpis article.warning{border-color:rgba(245,158,11,.25);background:linear-gradient(135deg,#fffbeb,#fff);}
  .ba-inventory-automation-panel{margin-top:0;}
  .ba-deduction-row.ready{border-color:rgba(20,184,166,.18);background:#faffff;}
  .ba-deduction-row.blocked{border-color:rgba(245,158,11,.22);background:#fffbeb;}
  @media(max-width:980px){.ba-inventory-automation-hero{grid-template-columns:1fr auto;}.ba-inventory-automation-hero>em{grid-column:2}.ba-inventory-automation-hero>button{grid-column:1 / -1;width:max-content}.ba-inventory-automation-kpis{grid-template-columns:repeat(2,minmax(0,1fr));}}
  @media(max-width:640px){.ba-inventory-automation-hero{grid-template-columns:1fr;}.ba-inventory-automation-hero>strong,.ba-inventory-automation-hero>em{text-align:left;}.ba-inventory-automation-kpis{grid-template-columns:1fr;}}


/* LOOP D2.8.2 — Membership Dashboard V2 */
/* LOOP MEMBERSHIP DASHBOARD V2 R5.1 — compact owner-first visual polish */
  .ba-membership-v2{display:grid;gap:14px;}
  .ba-membership-hero-v2{display:block;border:1px solid rgba(20,184,166,.20);background:linear-gradient(135deg,#f0fdfa,#fff 68%,#ecfeff);border-radius:22px;padding:18px 20px;box-shadow:0 14px 34px rgba(15,23,42,.045);}
  .ba-membership-hero-copy>span{display:block;color:#00675f;font-size:10.5px;font-weight:950;text-transform:uppercase;letter-spacing:.11em;}
  .ba-membership-hero-copy h3{margin:5px 0 0;color:var(--ink);font-size:clamp(23px,2.7vw,32px);line-height:1;letter-spacing:-.052em;font-weight:950;}
  .ba-membership-hero-copy p{margin:7px 0 0;max-width:760px;color:var(--muted);font-size:12.5px;line-height:1.45;font-weight:760;}

  .ba-report-filterbar-main.ba-report-filterbar-with-module.is-memberships{grid-template-columns:minmax(120px,172px) minmax(280px,440px)!important;justify-content:start!important;align-items:center!important;}
  .ba-membership-scope-chip{min-width:0;min-height:40px;display:flex;align-items:center;gap:8px;border:1px solid rgba(204,231,226,.92);border-radius:999px;background:rgba(255,255,255,.88);padding:0 13px;box-shadow:0 8px 20px rgba(15,23,42,.035);}
  .ba-membership-scope-chip span{flex:0 0 auto;color:#00675f;font-size:9.5px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;}
  .ba-membership-scope-chip span:after{content:"";display:inline-block;width:3px;height:3px;margin:0 0 2px 8px;border-radius:50%;background:#94a3b8;}
  .ba-membership-scope-chip strong{min-width:0;color:#334155;font-size:11.5px;font-weight:900;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}

  .ba-executive-grid.membership.membership-v2{margin:0;}
  .ba-executive-grid.membership.membership-v2 .ba-metric{min-height:98px;padding-top:14px;padding-bottom:14px;}

  .ba-membership-v2-grid{display:grid;grid-template-columns:minmax(280px,.78fr) minmax(0,1.52fr);gap:14px;align-items:start;}
  .ba-membership-health-panel,.ba-membership-renewals-panel,.ba-membership-attention-panel,.ba-membership-plans-panel,.ba-membership-services-panel,.ba-membership-activity-panel{min-width:0;align-self:start;}

  .ba-membership-health-stack{display:grid;border:1px solid #e7edf3;border-radius:16px;overflow:hidden;background:#fff;}
  .ba-membership-health-stack>div{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:12px;align-items:center;padding:10px 12px;border-bottom:1px solid #eef2f7;background:#fff;}
  .ba-membership-health-stack>div:last-child{border-bottom:0;}
  .ba-membership-health-stack>div.warning{background:#fffbeb;}
  .ba-membership-health-stack>div.critical{background:#fff7f7;}
  .ba-membership-health-stack>div.ok{background:linear-gradient(90deg,rgba(236,253,245,.72),#fff 58%);}
  .ba-membership-health-stack div>div{min-width:0;}
  .ba-membership-health-stack span{display:block;color:#334155;font-size:11px;font-weight:950;}
  .ba-membership-health-stack em{display:block;margin-top:2px;color:#94a3b8;font-size:10px;line-height:1.2;font-style:normal;font-weight:720;}
  .ba-membership-health-stack strong{color:var(--ink);font-size:18px;line-height:1;font-weight:950;letter-spacing:-.04em;}
  .ba-membership-health-stack .warning strong{color:#b45309;}
  .ba-membership-health-stack .critical strong{color:#b91c1c;}
  .ba-membership-health-foot{display:flex;align-items:center;gap:7px;flex-wrap:wrap;margin-top:10px;padding-top:10px;border-top:1px solid #eef2f7;color:#64748b;font-size:10.5px;font-weight:760;}
  .ba-membership-health-foot strong{color:#00675f;font-size:13px;font-weight:950;}
  .ba-membership-health-foot i{width:3px;height:3px;border-radius:50%;background:#cbd5e1;margin:0 2px;}

  .ba-membership-renewal-list{display:grid;gap:7px;}
  .ba-membership-renewal-row{display:grid;grid-template-columns:minmax(0,1.5fr) minmax(82px,.6fr) minmax(78px,.52fr) auto;gap:10px;align-items:center;border:1px solid #e2e8f0;background:#fff;border-radius:14px;padding:9px 11px;}
  .ba-membership-renewal-row.warning{border-color:rgba(245,158,11,.24);background:#fffcf5;}
  .ba-membership-renewal-row.critical{border-color:rgba(239,68,68,.2);background:#fff8f8;}
  .ba-membership-person strong,.ba-membership-renewal-date strong,.ba-membership-renewal-value strong{display:block;color:var(--ink);font-size:12.5px;font-weight:950;}
  .ba-membership-person span,.ba-membership-renewal-date span,.ba-membership-renewal-value span{display:block;margin-top:2px;color:#64748b;font-size:10px;font-weight:760;}
  .ba-membership-renewal-date span{text-transform:uppercase;letter-spacing:.06em;font-size:9px;}
  .ba-membership-renewal-mode{justify-self:end;border-radius:999px;padding:5px 8px;background:#ecfdf5;color:#047857;font-style:normal;font-size:9px;font-weight:950;text-transform:uppercase;letter-spacing:.05em;white-space:nowrap;}
  .ba-membership-renewal-mode.ending{background:#fff7ed;color:#c2410c;}
  .ba-membership-renewal-mode.manual_or_expiry{background:#f1f5f9;color:#64748b;}
  .ba-membership-expand{width:100%;margin-top:8px;min-height:34px;border:1px solid #dbe7ee;border-radius:12px;background:#f8fafc;color:#00675f;font-size:10.5px;font-weight:950;cursor:pointer;transition:.16s ease;}
  .ba-membership-expand:hover{background:#ecfdf5;border-color:rgba(20,184,166,.28);}

  .ba-membership-attention-list-v2{display:grid;gap:8px;}
  .ba-membership-attention-v2{display:grid;grid-template-columns:minmax(120px,auto) minmax(0,1fr) auto;gap:11px;align-items:center;border:1px solid #e2e8f0;background:#fff;border-radius:14px;padding:10px 11px;}
  .ba-membership-attention-v2.critical{border-color:rgba(239,68,68,.2);background:#fff8f8;}
  .ba-membership-attention-v2.warning{border-color:rgba(245,158,11,.22);background:#fffbeb;}
  .ba-membership-attention-v2.info{border-color:#e2e8f0;background:#f8fafc;}
  .ba-membership-attention-v2 b{justify-self:start;border-radius:999px;padding:5px 8px;background:#fee2e2;color:#b91c1c;font-size:9px;font-weight:950;text-transform:uppercase;letter-spacing:.04em;white-space:nowrap;}
  .ba-membership-attention-v2.warning b{background:#fef3c7;color:#92400e;}
  .ba-membership-attention-v2.info b{background:#e2e8f0;color:#475569;}
  .ba-membership-attention-v2>div strong{display:block;color:var(--ink);font-size:13px;font-weight:950;}
  .ba-membership-attention-v2>div span{display:block;margin-top:2px;color:#64748b;font-size:10.5px;font-weight:760;}
  .ba-membership-attention-v2>em{color:#00675f;font-size:10.5px;font-style:normal;font-weight:950;white-space:nowrap;}
  .ba-membership-clear-state{display:grid;grid-template-columns:34px minmax(0,1fr);column-gap:10px;align-items:center;border:1px solid rgba(16,185,129,.2);background:#f0fdf4;border-radius:15px;padding:11px;}
  .ba-membership-clear-state>div{grid-row:span 2;width:34px;height:34px;border-radius:11px;display:grid;place-items:center;background:#d1fae5;color:#047857;font-size:16px;font-weight:950;}
  .ba-membership-clear-state strong{color:#065f46;font-size:13px;font-weight:950;}
  .ba-membership-clear-state span{color:#047857;font-size:10.5px;line-height:1.35;font-weight:720;}

  .ba-membership-plan-table{overflow:hidden;border:1px solid #e2e8f0;border-radius:15px;}
  .ba-membership-plan-table-head,.ba-membership-plan-table-row{display:grid;grid-template-columns:minmax(190px,1.7fr) repeat(2,minmax(72px,.56fr)) minmax(130px,.78fr) minmax(82px,.65fr);gap:10px;align-items:center;padding:9px 11px;}
  .ba-membership-plan-table-head{background:#f8fafc;border-bottom:1px solid #e2e8f0;}
  .ba-membership-plan-table-head span{color:#64748b;font-size:9px;font-weight:950;text-transform:uppercase;letter-spacing:.07em;}
  .ba-membership-plan-table-head span:not(:first-child){text-align:right;}
  .ba-membership-plan-table-row{background:#fff;border-bottom:1px solid #eef2f7;}
  .ba-membership-plan-table-row:last-child{border-bottom:0;}
  .ba-membership-plan-table-row>div:first-child strong{display:block;color:var(--ink);font-size:12.5px;font-weight:950;}
  .ba-membership-plan-table-row>strong{color:#334155;font-size:11.5px;font-weight:900;text-align:right;}
  .ba-membership-plan-usage{display:grid;grid-template-columns:minmax(54px,1fr) 34px;gap:7px;align-items:center;}
  .ba-membership-plan-usage>span{height:6px;border-radius:999px;background:#e8eef2;overflow:hidden;}
  .ba-membership-plan-usage>span i{display:block;height:100%;border-radius:inherit;background:linear-gradient(90deg,#0f766e,#14b8a6);}
  .ba-membership-plan-usage>strong{color:#334155;font-size:10.5px;font-weight:950;text-align:right;}

  .ba-membership-bottom-grid{display:grid;grid-template-columns:minmax(240px,.66fr) minmax(0,1.34fr);gap:14px;align-items:start;}
  .ba-membership-service-list{display:grid;gap:7px;}
  .ba-membership-service-row{display:grid;grid-template-columns:28px minmax(0,1fr) auto;align-items:center;gap:9px;border:1px solid #e2e8f0;background:#fff;border-radius:13px;padding:9px 10px;}
  .ba-membership-service-row>b{width:28px;height:28px;border-radius:9px;display:grid;place-items:center;background:#ecfdf5;color:#047857;font-size:10.5px;font-weight:950;}
  .ba-membership-service-row strong{display:block;color:var(--ink);font-size:12.5px;font-weight:950;}
  .ba-membership-service-row span{display:block;margin-top:2px;color:#64748b;font-size:10px;font-weight:760;}
  .ba-membership-service-row em{color:#00675f;font-size:10.5px;font-style:normal;font-weight:950;white-space:nowrap;}

  .ba-membership-timeline{display:grid;gap:0;}
  .ba-membership-activity-item{display:grid;grid-template-columns:13px minmax(0,1fr);gap:8px;position:relative;padding:0 0 11px;}
  .ba-membership-activity-item:last-child{padding-bottom:0;}
  .ba-membership-activity-item:before{content:"";position:absolute;left:5px;top:11px;bottom:-1px;width:1px;background:#e2e8f0;}
  .ba-membership-activity-item:last-child:before{display:none;}
  .ba-membership-activity-item>i{position:relative;z-index:1;width:10px;height:10px;margin-top:4px;border:3px solid #ccfbf1;border-radius:50%;background:#0f766e;}
  .ba-membership-activity-item>div{border-bottom:1px solid #eef2f7;padding-bottom:10px;min-width:0;}
  .ba-membership-activity-item:last-child>div{border-bottom:0;padding-bottom:0;}
  .ba-membership-activity-head{display:flex;align-items:baseline;justify-content:space-between;gap:10px;}
  .ba-membership-activity-head strong{color:var(--ink);font-size:12px;font-weight:950;}
  .ba-membership-activity-head time{color:#94a3b8;font-size:9px;font-weight:760;white-space:nowrap;}
  .ba-membership-activity-item p{margin:3px 0 0;color:#334155;font-size:11px;line-height:1.3;font-weight:800;}
  .ba-membership-activity-item>div>span{display:block;margin-top:2px;color:#64748b;font-size:10px;font-weight:720;}

  @media(max-width:1100px){
    .ba-membership-v2-grid{grid-template-columns:1fr;}
    .ba-membership-bottom-grid{grid-template-columns:1fr;}
  }

  @media(max-width:860px){
    .ba-report-filterbar-main.ba-report-filterbar-with-module.is-memberships{grid-template-columns:1fr!important;}
    .ba-membership-scope-chip{width:100%;}
  }

  @media(max-width:820px){
    .ba-membership-renewal-row{grid-template-columns:minmax(0,1fr) auto;}
    .ba-membership-renewal-date,.ba-membership-renewal-value{grid-row:2;}
    .ba-membership-renewal-mode{grid-column:2;grid-row:1;align-self:start;}
    .ba-membership-plan-table{overflow-x:auto;}
    .ba-membership-plan-table-head,.ba-membership-plan-table-row{min-width:650px;}
  }

  @media(max-width:560px){
    .ba-membership-scope-chip{align-items:flex-start;flex-direction:column;gap:2px;padding:8px 12px;border-radius:16px;}
    .ba-membership-scope-chip span:after{display:none;}
    .ba-membership-scope-chip strong{white-space:normal;overflow:visible;}
    .ba-membership-attention-v2{grid-template-columns:1fr;}
    .ba-membership-attention-v2>em{white-space:normal;}
    .ba-membership-renewal-row{grid-template-columns:1fr;}
    .ba-membership-renewal-date,.ba-membership-renewal-value,.ba-membership-renewal-mode{grid-column:auto;grid-row:auto;justify-self:start;}
    .ba-membership-activity-head{display:grid;gap:2px;}
  }


/* LOOP MEMBERSHIP DASHBOARD V2 R5.2 — scalable drawers + professional report chrome */
  .ba-report-filterbar.is-memberships{
    border:0!important;
    border-bottom:1px solid #e2e8f0!important;
    border-radius:0!important;
    background:transparent!important;
    box-shadow:none!important;
    backdrop-filter:none!important;
    -webkit-backdrop-filter:none!important;
    padding:2px 0 8px!important;
    margin-bottom:12px!important;
  }
  .ba-report-filterbar.is-memberships:before{display:none!important;}
  .ba-report-filterbar.is-memberships .ba-report-filterbar-main.ba-report-filterbar-with-module.is-memberships{
    grid-template-columns:max-content minmax(240px,1fr)!important;
    gap:14px!important;
  }
  .ba-report-filterbar.is-memberships .ba-report-module-switcher{
    width:auto!important;
    min-width:0!important;
  }
  .ba-report-filterbar.is-memberships .ba-module-trigger-minimal{
    min-height:34px!important;
    height:34px!important;
    padding:0 4px!important;
    border:0!important;
    border-radius:8px!important;
    background:transparent!important;
    box-shadow:none!important;
    color:#0f172a!important;
    gap:7px!important;
  }
  .ba-report-filterbar.is-memberships .ba-module-trigger-minimal:hover{
    background:#f8fafc!important;
  }
  .ba-report-filterbar.is-memberships .ba-module-trigger-minimal strong{
    font-size:13px!important;
    font-weight:950!important;
  }
  .ba-report-filterbar.is-memberships .ba-module-trigger-minimal i{
    width:auto!important;
    height:auto!important;
    border:0!important;
    background:transparent!important;
    box-shadow:none!important;
    color:#64748b!important;
    font-size:12px!important;
  }
  .ba-membership-scope-line{
    min-width:0;
    min-height:34px;
    display:flex;
    align-items:center;
    gap:8px;
    padding-left:14px;
    border-left:1px solid #e2e8f0;
    color:#64748b;
  }
  .ba-membership-scope-line span{
    color:#64748b;
    font-size:10px;
    font-weight:900;
    text-transform:uppercase;
    letter-spacing:.08em;
  }
  .ba-membership-scope-line strong{
    min-width:0;
    color:#334155;
    font-size:11.5px;
    font-weight:850;
    white-space:nowrap;
    overflow:hidden;
    text-overflow:ellipsis;
  }
  .ba-membership-renewals-panel .ba-panel-head.compact>strong{
    font-size:10.5px!important;
    font-weight:900!important;
    color:#64748b!important;
    white-space:nowrap;
  }

  .ba-membership-drawer-layer{
    position:fixed;
    inset:0;
    z-index:1300;
    display:flex;
    justify-content:flex-end;
    background:rgba(15,23,42,.28);
    backdrop-filter:blur(2px);
    -webkit-backdrop-filter:blur(2px);
  }
  .ba-membership-drawer{
    width:min(780px,54vw);
    height:100%;
    display:grid;
    grid-template-rows:auto auto auto minmax(0,1fr) auto;
    background:#fff;
    border-left:1px solid #dbe4ea;
    box-shadow:-24px 0 60px rgba(15,23,42,.16);
    animation:baMembershipDrawerIn .18s ease-out;
    overflow:hidden;
  }
  @keyframes baMembershipDrawerIn{
    from{transform:translateX(22px);opacity:.65;}
    to{transform:translateX(0);opacity:1;}
  }
  .ba-membership-drawer-head{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:18px;
    padding:20px 22px 16px;
    border-bottom:1px solid #e8edf2;
    background:#fff;
  }
  .ba-membership-drawer-head>div{min-width:0;}
  .ba-membership-drawer-head span{
    display:block;
    color:#00675f;
    font-size:9.5px;
    font-weight:950;
    text-transform:uppercase;
    letter-spacing:.12em;
  }
  .ba-membership-drawer-head h3{
    margin:5px 0 4px;
    color:#0f172a;
    font-size:23px;
    line-height:1.08;
    letter-spacing:-.04em;
    font-weight:950;
  }
  .ba-membership-drawer-head p{
    margin:0;
    max-width:600px;
    color:#64748b;
    font-size:11.5px;
    line-height:1.45;
    font-weight:720;
  }
  .ba-membership-drawer-head>button{
    flex:0 0 auto;
    width:34px;
    height:34px;
    display:grid;
    place-items:center;
    border:1px solid #dbe4ea;
    border-radius:8px;
    background:#fff;
    color:#334155;
    font-size:20px;
    line-height:1;
    cursor:pointer;
  }
  .ba-membership-drawer-head>button:hover{background:#f8fafc;}

  .ba-membership-drawer-toolbar{
    display:grid;
    gap:10px;
    padding:14px 22px;
    border-bottom:1px solid #eef2f6;
    background:#fbfcfd;
  }
  .ba-membership-drawer-search{
    display:grid;
    grid-template-columns:minmax(0,1fr) auto;
    gap:8px;
  }
  .ba-membership-drawer-search input,
  .ba-membership-drawer-filters select{
    width:100%;
    min-height:38px;
    border:1px solid #cfd9e2;
    border-radius:9px;
    background:#fff;
    color:#0f172a;
    outline:none;
    font-size:11.5px;
    font-weight:750;
  }
  .ba-membership-drawer-search input{
    padding:0 11px;
  }
  .ba-membership-drawer-search input:focus,
  .ba-membership-drawer-filters select:focus{
    border-color:#14b8a6;
    box-shadow:0 0 0 3px rgba(20,184,166,.10);
  }
  .ba-membership-drawer-search button,
  .ba-membership-drawer-foot button{
    min-height:38px;
    padding:0 13px;
    border:1px solid #cfd9e2;
    border-radius:9px;
    background:#fff;
    color:#0f172a;
    font-size:11px;
    font-weight:900;
    cursor:pointer;
  }
  .ba-membership-drawer-search button{
    border-color:#00675f;
    background:#00675f;
    color:#fff;
  }
  .ba-membership-drawer-filters{
    display:flex;
    gap:10px;
    flex-wrap:wrap;
  }
  .ba-membership-drawer-filters label{
    min-width:170px;
    flex:0 1 220px;
  }
  .ba-membership-drawer-filters label>span{
    display:block;
    margin-bottom:4px;
    color:#64748b;
    font-size:9px;
    font-weight:950;
    text-transform:uppercase;
    letter-spacing:.07em;
  }
  .ba-membership-drawer-filters select{
    padding:0 30px 0 10px;
  }
  .ba-membership-drawer-preview-note{
    margin:10px 22px 0;
    padding:9px 10px;
    border-left:3px solid #14b8a6;
    background:#f0fdfa;
    color:#0f766e;
    font-size:10.5px;
    line-height:1.35;
    font-weight:800;
  }
  .ba-membership-drawer>.ba-alert{
    margin:10px 22px 0!important;
  }

  .ba-membership-drawer-body{
    min-height:0;
    overflow:auto;
    padding:14px 22px 20px;
    background:#fff;
  }
  .ba-membership-drawer-loading,
  .ba-membership-drawer-empty{
    min-height:180px;
    display:grid;
    place-content:center;
    text-align:center;
    color:#64748b;
    font-size:11.5px;
    font-weight:800;
  }
  .ba-membership-drawer-empty strong{
    display:block;
    color:#0f172a;
    font-size:14px;
    font-weight:950;
  }
  .ba-membership-drawer-empty span{
    display:block;
    margin-top:4px;
  }

  .ba-membership-drawer-table{
    border:1px solid #e2e8f0;
    border-radius:10px;
    overflow:hidden;
  }
  .ba-membership-drawer-table-head,
  .ba-membership-drawer-table-row{
    display:grid;
    grid-template-columns:minmax(180px,1.4fr) minmax(86px,.6fr) minmax(86px,.6fr) auto;
    gap:12px;
    align-items:center;
  }
  .ba-membership-drawer-table-head{
    padding:8px 11px;
    background:#f8fafc;
    border-bottom:1px solid #e2e8f0;
  }
  .ba-membership-drawer-table-head span{
    color:#64748b;
    font-size:8.5px;
    font-weight:950;
    text-transform:uppercase;
    letter-spacing:.07em;
  }
  .ba-membership-drawer-table-row{
    padding:10px 11px;
    border-bottom:1px solid #eef2f7;
    background:#fff;
  }
  .ba-membership-drawer-table-row:last-child{border-bottom:0;}
  .ba-membership-drawer-table-row>div strong{
    display:block;
    color:#0f172a;
    font-size:11.5px;
    font-weight:950;
  }
  .ba-membership-drawer-table-row>div span{
    display:block;
    margin-top:2px;
    color:#64748b;
    font-size:9.5px;
    font-weight:730;
  }

  .ba-membership-drawer-attention{
    display:grid;
    gap:8px;
  }

  .ba-membership-drawer-activity{
    border:1px solid #e2e8f0;
    border-radius:10px;
    overflow:hidden;
  }
  .ba-membership-drawer-activity-head,
  .ba-membership-drawer-activity-row{
    display:grid;
    grid-template-columns:112px minmax(120px,.8fr) minmax(145px,1fr) minmax(160px,1.25fr);
    gap:12px;
    align-items:center;
  }
  .ba-membership-drawer-activity-head{
    padding:8px 11px;
    background:#f8fafc;
    border-bottom:1px solid #e2e8f0;
  }
  .ba-membership-drawer-activity-head span{
    color:#64748b;
    font-size:8.5px;
    font-weight:950;
    text-transform:uppercase;
    letter-spacing:.07em;
  }
  .ba-membership-drawer-activity-row{
    padding:10px 11px;
    border-bottom:1px solid #eef2f7;
  }
  .ba-membership-drawer-activity-row:last-child{border-bottom:0;}
  .ba-membership-drawer-activity-row time{
    color:#64748b;
    font-size:9.5px;
    font-weight:760;
  }
  .ba-membership-drawer-activity-row>strong{
    color:#0f172a;
    font-size:11px;
    font-weight:950;
  }
  .ba-membership-drawer-activity-row div b,
  .ba-membership-drawer-activity-row div strong{
    display:block;
    color:#0f172a;
    font-size:10.5px;
    font-weight:900;
  }
  .ba-membership-drawer-activity-row div span{
    display:block;
    margin-top:2px;
    color:#64748b;
    font-size:9.5px;
    line-height:1.3;
    font-weight:720;
  }

  .ba-membership-drawer-foot{
    display:flex;
    justify-content:space-between;
    align-items:center;
    gap:12px;
    min-height:58px;
    padding:10px 22px;
    border-top:1px solid #e8edf2;
    background:#fff;
  }
  .ba-membership-drawer-foot>span{
    color:#64748b;
    font-size:10.5px;
    font-weight:800;
  }
  .ba-membership-drawer-foot>div{
    display:flex;
    gap:8px;
  }
  .ba-membership-drawer-foot button:disabled{
    opacity:.4;
    cursor:not-allowed;
  }

  @media(max-width:1100px){
    .ba-membership-drawer{width:min(840px,72vw);}
  }
  @media(max-width:760px){
    .ba-report-filterbar.is-memberships .ba-report-filterbar-main.ba-report-filterbar-with-module.is-memberships{
      grid-template-columns:1fr!important;
      gap:4px!important;
    }
    .ba-membership-scope-line{
      min-height:auto;
      padding:0 4px 4px;
      border-left:0;
      gap:6px;
    }
    .ba-membership-drawer{width:100%;border-left:0;}
    .ba-membership-drawer-head{padding:16px 16px 13px;}
    .ba-membership-drawer-toolbar{padding:12px 16px;}
    .ba-membership-drawer-body{padding:12px 16px 18px;}
    .ba-membership-drawer-foot{padding:10px 16px;}
    .ba-membership-drawer-preview-note{margin:9px 16px 0;}
    .ba-membership-drawer-table-head{display:none;}
    .ba-membership-drawer-table-row{grid-template-columns:minmax(0,1fr) auto;}
    .ba-membership-drawer-table-row>div:nth-child(2),
    .ba-membership-drawer-table-row>div:nth-child(3){grid-row:2;}
    .ba-membership-drawer-activity-head{display:none;}
    .ba-membership-drawer-activity-row{
      grid-template-columns:1fr;
      gap:5px;
    }
    .ba-membership-drawer-activity-row time{order:-1;}
  }
  @media(max-width:520px){
    .ba-membership-drawer-search{grid-template-columns:1fr;}
    .ba-membership-drawer-search button{width:100%;}
    .ba-membership-drawer-filters{display:grid;}
    .ba-membership-drawer-filters label{min-width:0;max-width:none;}
    .ba-membership-drawer-foot{align-items:flex-start;flex-direction:column;}
    .ba-membership-drawer-foot>div{width:100%;}
    .ba-membership-drawer-foot button{flex:1;}
  }



/* LOOP MEMBERSHIP DASHBOARD V2 R5.3 — compact summary + master membership view */
/* LOOP MEMBERSHIP DASHBOARD V2 R5.4 — micro polish + membership record detail */
  .ba-executive-grid.membership.membership-v2{display:none!important;}
  .ba-membership-v2-grid{display:block!important;}
  .ba-membership-health-panel,.ba-membership-renewals-panel{display:none!important;}

  .ba-membership-summary-panel{padding:0!important;overflow:hidden;}
  .ba-membership-summary-head{display:flex;align-items:flex-end;justify-content:space-between;gap:18px;padding:15px 17px 13px;border-bottom:1px solid #e8edf2;}
  .ba-membership-summary-head>div{min-width:0;}
  .ba-membership-summary-head span{display:block;color:#00675f;font-size:9.5px;font-weight:950;text-transform:uppercase;letter-spacing:.11em;}
  .ba-membership-summary-head h3{margin:4px 0 3px;color:#0f172a;font-size:17px;line-height:1.15;letter-spacing:-.035em;font-weight:950;}
  .ba-membership-summary-head p{margin:0;color:#64748b;font-size:10.5px;line-height:1.4;font-weight:720;}
  .ba-membership-summary-head button{flex:0 0 auto;min-height:34px;border:1px solid #d7e2e8;border-radius:8px;background:#fff;color:#00675f;padding:0 11px;font-size:10.5px;font-weight:950;cursor:pointer;}
  .ba-membership-summary-head button:hover{background:#f8fafc;border-color:#b9d7d2;}
  .ba-membership-summary-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));}
  .ba-membership-summary-grid section{min-width:0;padding:12px 16px 10px;border-right:1px solid #edf1f4;}
  .ba-membership-summary-grid section:last-child{border-right:0;}
  .ba-membership-summary-grid header{margin-bottom:5px;color:#64748b;font-size:9px;font-weight:950;text-transform:uppercase;letter-spacing:.09em;}
  .ba-membership-summary-grid section>div{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:12px;align-items:center;min-height:31px;border-top:1px solid #f1f4f6;}
  .ba-membership-summary-grid section>div:first-of-type{border-top:0;}
  .ba-membership-summary-grid section>div>span{color:#475569;font-size:10.5px;font-weight:800;}
  .ba-membership-summary-grid section>div>strong{color:#0f172a;font-size:14px;font-weight:950;letter-spacing:-.025em;white-space:nowrap;}
  .ba-membership-summary-grid section>div.warning>strong{color:#b45309;}
  .ba-membership-summary-grid section>div.critical>strong{color:#b91c1c;}
  .ba-membership-summary-foot{display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:9px 16px;border-top:1px solid #edf1f4;background:#fbfcfd;color:#64748b;font-size:9.5px;font-weight:760;}
  .ba-membership-summary-foot span{display:flex;align-items:baseline;gap:4px;}
  .ba-membership-summary-foot strong{color:#00675f;font-size:11.5px;font-weight:950;}
  .ba-membership-summary-foot i{width:3px;height:3px;border-radius:50%;background:#cbd5e1;}

  .ba-membership-master-panel{min-width:0;}
  .ba-membership-master-actions{display:flex;align-items:center;gap:10px;}
  .ba-membership-master-actions>span{color:#64748b;font-size:10px;font-weight:900;white-space:nowrap;}
  .ba-membership-master-actions>button{border:0;background:transparent;color:#00675f;padding:4px 0;font-size:10.5px;font-weight:950;cursor:pointer;white-space:nowrap;}
  .ba-membership-master-actions>button:hover{text-decoration:underline;}
  .ba-membership-master-list{display:grid;border:1px solid #e3eaf0;border-radius:14px;overflow:hidden;background:#fff;}
  .ba-membership-master-row{width:100%;display:grid;grid-template-columns:minmax(170px,1.35fr) auto minmax(82px,.62fr) minmax(150px,1fr) minmax(55px,.42fr) minmax(72px,.48fr);gap:12px;align-items:center;padding:9px 11px;border:0;border-bottom:1px solid #edf1f4;background:#fff;text-align:left;font:inherit;cursor:pointer;transition:background .14s ease;}
  .ba-membership-master-row:hover{background:#f8fbfb;}
  .ba-membership-master-row:focus-visible{outline:2px solid rgba(20,184,166,.35);outline-offset:-2px;}
  .ba-membership-master-row:last-child{border-bottom:0;}
  .ba-membership-master-person strong{display:block;color:#0f172a;font-size:12px;font-weight:950;}
  .ba-membership-master-person span,.ba-membership-master-row>div:not(.ba-membership-master-person)>span{display:block;margin-top:2px;color:#94a3b8;font-size:9px;font-weight:760;}
  .ba-membership-master-row>div:not(.ba-membership-master-person)>span{margin:0 0 2px;text-transform:uppercase;letter-spacing:.05em;}
  .ba-membership-master-row>div:not(.ba-membership-master-person)>strong{display:block;color:#334155;font-size:10.5px;line-height:1.25;font-weight:900;}
  .ba-membership-status{justify-self:start;border-radius:999px;padding:5px 8px;background:#f1f5f9;color:#64748b;font-size:8.7px;font-style:normal;font-weight:950;text-transform:uppercase;letter-spacing:.05em;white-space:nowrap;}
  .ba-membership-status.active{background:#ecfdf5;color:#047857;}
  .ba-membership-status.warning{background:#fef3c7;color:#92400e;}
  .ba-membership-status.paused{background:#eff6ff;color:#1d4ed8;}
  .ba-membership-status.cancelled{background:#fff1f2;color:#be123c;}

  .ba-membership-drawer-table.memberships{min-width:920px;}
  .ba-membership-drawer-table.memberships .ba-membership-drawer-table-head,
  .ba-membership-drawer-table-row.memberships{grid-template-columns:minmax(180px,1.4fr) 92px 100px minmax(150px,1fr) 80px 92px;}
  .ba-membership-drawer-table-row.memberships{width:100%;border-left:0;border-right:0;background:#fff;text-align:left;font:inherit;cursor:pointer;transition:background .14s ease;}
  .ba-membership-drawer-table-row.memberships:hover{background:#f8fbfb;}
  .ba-membership-drawer-table-row.memberships:focus-visible{outline:2px solid rgba(20,184,166,.35);outline-offset:-2px;}
  .ba-membership-drawer-table-row.memberships>div>strong{display:block;color:#0f172a;font-size:11px;font-weight:950;}
  .ba-membership-drawer-table-row.memberships>div>span{display:block;margin-top:2px;color:#64748b;font-size:9.5px;font-weight:720;}
  .ba-membership-drawer-table-row.memberships>.ba-membership-status{justify-self:start;align-self:center;}

  .ba-membership-record-detail{display:grid;gap:12px;}
  .ba-membership-record-back{justify-self:start;border:0;background:transparent;color:#00675f;padding:0;font-size:10.5px;font-weight:950;cursor:pointer;}
  .ba-membership-record-back:hover{text-decoration:underline;}
  .ba-membership-record-identity{display:flex;align-items:center;justify-content:space-between;gap:14px;border:1px solid #e3eaf0;border-radius:14px;background:#fff;padding:13px 14px;}
  .ba-membership-record-identity>div{min-width:0;}
  .ba-membership-record-identity span{display:block;color:#64748b;font-size:9px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;}
  .ba-membership-record-identity strong{display:block;margin-top:3px;color:#0f172a;font-size:17px;font-weight:950;letter-spacing:-.035em;}
  .ba-membership-record-identity>div>em{display:block;margin-top:2px;color:#64748b;font-size:10.5px;font-style:normal;font-weight:760;}
  .ba-membership-record-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));border:1px solid #e3eaf0;border-radius:14px;overflow:hidden;background:#fff;}
  .ba-membership-record-grid>div{min-width:0;padding:12px 13px;border-right:1px solid #edf1f4;border-bottom:1px solid #edf1f4;}
  .ba-membership-record-grid>div:nth-child(2n){border-right:0;}
  .ba-membership-record-grid>div:nth-last-child(-n+2){border-bottom:0;}
  .ba-membership-record-grid span{display:block;color:#64748b;font-size:9px;font-weight:950;text-transform:uppercase;letter-spacing:.07em;}
  .ba-membership-record-grid strong{display:block;margin-top:4px;color:#0f172a;font-size:13px;font-weight:950;}
  .ba-membership-record-grid em{display:block;margin-top:3px;color:#94a3b8;font-size:9.5px;line-height:1.35;font-style:normal;font-weight:720;}

  .ba-membership-operational-detail{display:grid;gap:12px;}
  .ba-membership-operational-detail section{border:1px solid #e3eaf0;border-radius:14px;overflow:hidden;background:#fff;}
  .ba-membership-operational-detail section>header{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 12px;background:#f8fafc;border-bottom:1px solid #e8edf2;}
  .ba-membership-operational-detail section>header span{color:#00675f;font-size:9px;font-weight:950;text-transform:uppercase;letter-spacing:.09em;}
  .ba-membership-operational-detail section>header strong{color:#334155;font-size:10.5px;font-weight:900;}
  .ba-membership-operational-detail section>div{display:grid;grid-template-columns:minmax(0,1fr) auto;column-gap:14px;row-gap:2px;padding:10px 12px;border-bottom:1px solid #edf1f4;}
  .ba-membership-operational-detail section>div:last-child{border-bottom:0;}
  .ba-membership-operational-detail section>div>span{color:#334155;font-size:10.5px;font-weight:850;}
  .ba-membership-operational-detail section>div>strong{color:#0f172a;font-size:13px;font-weight:950;}
  .ba-membership-operational-detail section>div>em{grid-column:1/-1;color:#94a3b8;font-size:9.5px;line-height:1.35;font-style:normal;font-weight:700;}

  @media(max-width:980px){
    .ba-membership-summary-grid{grid-template-columns:1fr;}
    .ba-membership-summary-grid section{border-right:0;border-bottom:1px solid #edf1f4;}
    .ba-membership-summary-grid section:last-child{border-bottom:0;}
    .ba-membership-master-row{grid-template-columns:minmax(0,1fr) auto minmax(90px,.65fr);}
    .ba-membership-master-row>div:nth-of-type(3),.ba-membership-master-row>div:nth-of-type(4),.ba-membership-master-row>div:nth-of-type(5){display:none;}
  }
  @media(max-width:640px){
    .ba-membership-master-actions{align-items:flex-end;flex-direction:column;gap:3px;}
    .ba-membership-record-grid{grid-template-columns:1fr;}
    .ba-membership-record-grid>div,.ba-membership-record-grid>div:nth-child(2n),.ba-membership-record-grid>div:nth-last-child(-n+2){border-right:0;border-bottom:1px solid #edf1f4;}
    .ba-membership-record-grid>div:last-child{border-bottom:0;}
    .ba-membership-summary-head{align-items:flex-start;flex-direction:column;}
    .ba-membership-summary-head button{width:100%;}
    .ba-membership-master-row{grid-template-columns:minmax(0,1fr) auto;}
    .ba-membership-master-row>div:not(.ba-membership-master-person){display:none;}
    .ba-membership-drawer-table.memberships{min-width:820px;}
  }

/* LOOP D2.9.2A.1 — Analytics layout + finance simplification */
.business-analytics {
  max-width: min(1540px, calc(100vw - 188px - 32px)) !important;
  margin-left: 0 !important;
  margin-right: auto !important;
}
.ba-shell,
.ba-command-workspace,
.ba-command-main { overflow: visible !important; }
.ba-command-workspace {
  grid-template-columns: minmax(190px, 218px) minmax(0, 1fr) !important;
  gap: 20px !important;
}
.ba-command-main { min-width: 0 !important; }
.ba-topbar {
  grid-template-columns: minmax(0, 1fr) minmax(360px, 440px) !important;
  align-items: end !important;
}
.ba-controls { gap: 10px !important; }
.ba-friendly-periods { padding: 4px !important; border-radius: 18px !important; background: #f8fafc !important; }
.ba-friendly-periods button { border-radius: 13px !important; min-height: 34px !important; padding: 0 13px !important; font-size: 12px !important; }
.ba-controls select { border-radius: 16px !important; min-height: 42px !important; background: #ffffff !important; }
.ba-phase2-strip,
.ba-quality-strip { display: none !important; }
.ba-finance-minimal { display: grid !important; gap: 16px !important; }
.ba-finance-minimal-head {
  display: flex !important;
  align-items: flex-end !important;
  justify-content: space-between !important;
  gap: 14px !important;
  padding: 4px 0 2px !important;
}
.ba-finance-minimal-head span { display: block !important; color: #00675f !important; text-transform: uppercase !important; letter-spacing: .14em !important; font-size: 11px !important; font-weight: 950 !important; }
.ba-finance-minimal-head h3 { margin: 3px 0 4px !important; color: #0f172a !important; font-size: clamp(22px, 2.1vw, 32px) !important; line-height: 1.05 !important; letter-spacing: -.045em !important; }
.ba-finance-minimal-head p { margin: 0 !important; color: #64748b !important; font-size: 13px !important; font-weight: 750 !important; }
.ba-soft-action {
  min-height: 40px !important;
  border: 1px solid #cfe0ea !important;
  background: #ffffff !important;
  color: #00675f !important;
  border-radius: 999px !important;
  padding: 0 14px !important;
  font-size: 12px !important;
  font-weight: 950 !important;
  cursor: pointer !important;
  box-shadow: 0 8px 22px rgba(15,23,42,.045) !important;
}
.ba-finance-primary-table { margin: 0 !important; }
.ba-finance-two-charts {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
  gap: 16px !important;
  align-items: stretch !important;
}
.ba-finance-two-charts .ba-panel { min-height: 260px !important; }
@media (max-width: 1100px) {
  .business-analytics { max-width: 100% !important; }
  .ba-command-workspace { grid-template-columns: 1fr !important; }
  .ba-topbar { grid-template-columns: 1fr !important; }
  .ba-finance-two-charts { grid-template-columns: 1fr !important; }
  .ba-finance-minimal-head { align-items: flex-start !important; flex-direction: column !important; }
}
/* LOOP D2.9.2A.2 — single-line filters + view customizer */
.ba-topbar {
  grid-template-columns: minmax(260px,.72fr) minmax(760px,1.28fr) !important;
  gap: 18px !important;
  align-items: end !important;
}
.ba-controls-inline {
  display: block !important;
  width: 100% !important;
}
.ba-filter-row-main {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  gap: 8px !important;
  width: 100% !important;
  min-width: 0 !important;
}
.ba-friendly-periods {
  flex: 0 0 auto !important;
  width: auto !important;
  min-height: 38px !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 3px !important;
  padding: 3px !important;
  border-radius: 999px !important;
  border: 1px solid #e3ebf2 !important;
  background: #f8fafc !important;
}
.ba-friendly-periods button {
  min-height: 30px !important;
  padding: 0 12px !important;
  border-radius: 999px !important;
  white-space: nowrap !important;
}
.ba-date-control-compact {
  flex: 0 1 240px !important;
  min-width: 165px !important;
  min-height: 38px !important;
  border-radius: 999px !important;
  grid-template-columns: 30px minmax(0,1fr) 30px !important;
  padding: 2px 4px !important;
}
.ba-date-control-compact button { border-radius: 999px !important; }
.ba-worker-pills {
  flex: 1 1 auto !important;
  min-width: 180px !important;
  max-width: 360px !important;
  min-height: 38px !important;
  display: flex !important;
  align-items: center !important;
  gap: 5px !important;
  padding: 3px !important;
  border: 1px solid #e3ebf2 !important;
  border-radius: 999px !important;
  background: #ffffff !important;
  overflow-x: auto !important;
  scrollbar-width: none !important;
}
.ba-worker-pills::-webkit-scrollbar { display: none !important; }
.ba-worker-pills button {
  flex: 0 0 auto !important;
  min-height: 30px !important;
  border: 0 !important;
  border-radius: 999px !important;
  background: transparent !important;
  color: #516174 !important;
  padding: 0 12px !important;
  font-size: 12px !important;
  font-weight: 900 !important;
  cursor: pointer !important;
  white-space: nowrap !important;
}
.ba-worker-pills button.active {
  background: #00675f !important;
  color: #ffffff !important;
  box-shadow: 0 8px 18px rgba(0,103,95,.18) !important;
}
.ba-custom-range-inline {
  margin-top: 8px !important;
  justify-content: flex-end !important;
}
.ba-view-customizer-backdrop {
  position: fixed !important;
  inset: 0 !important;
  z-index: 9999 !important;
  background: rgba(15,23,42,.28) !important;
  display: grid !important;
  place-items: center !important;
  padding: 18px !important;
}
.ba-view-customizer {
  width: min(560px, calc(100vw - 28px)) !important;
  max-height: min(720px, calc(100dvh - 36px)) !important;
  overflow: auto !important;
  border: 1px solid #dbe7ef !important;
  border-radius: 24px !important;
  background: #ffffff !important;
  box-shadow: 0 28px 80px rgba(15,23,42,.22) !important;
}
.ba-view-customizer-head {
  display: flex !important;
  align-items: flex-start !important;
  justify-content: space-between !important;
  gap: 12px !important;
  padding: 20px 20px 14px !important;
  border-bottom: 1px solid #edf2f7 !important;
}
.ba-view-customizer-head span { color:#00675f !important; text-transform:uppercase !important; letter-spacing:.14em !important; font-size:11px !important; font-weight:950 !important; }
.ba-view-customizer-head h3 { margin:4px 0 4px !important; font-size:24px !important; line-height:1.05 !important; letter-spacing:-.045em !important; }
.ba-view-customizer-head p { margin:0 !important; color:#64748b !important; font-size:13px !important; font-weight:750 !important; }
.ba-view-customizer-head button { width:38px !important; height:38px !important; border-radius:999px !important; border:1px solid #dbe7ef !important; background:#fff !important; color:#0f172a !important; font-size:20px !important; font-weight:850 !important; cursor:pointer !important; }
.ba-view-customizer-body { display:grid !important; grid-template-columns:1fr 1fr !important; gap:14px !important; padding:18px 20px !important; }
.ba-view-customizer-group { border:1px solid #edf2f7 !important; border-radius:18px !important; padding:14px !important; background:#fbfdff !important; display:grid !important; gap:8px !important; align-content:start !important; }
.ba-view-customizer-group > strong { margin-bottom:4px !important; font-size:13px !important; font-weight:950 !important; color:#0f172a !important; }
.ba-view-toggle { display:flex !important; align-items:center !important; gap:9px !important; min-height:34px !important; color:#334155 !important; font-size:13px !important; font-weight:850 !important; }
.ba-view-toggle input { accent-color:#00675f !important; width:16px !important; height:16px !important; }
.ba-view-customizer-foot { display:flex !important; justify-content:flex-end !important; gap:10px !important; padding:14px 20px 20px !important; border-top:1px solid #edf2f7 !important; }
.ba-primary-action { min-height:40px !important; border:0 !important; border-radius:999px !important; background:#00675f !important; color:white !important; padding:0 18px !important; font-size:12px !important; font-weight:950 !important; cursor:pointer !important; box-shadow:0 10px 22px rgba(0,103,95,.18) !important; }
.ba-worker-close-head, .ba-worker-close-row { grid-template-columns: minmax(150px,1.2fr) repeat(var(--finance-col-count, 7), minmax(92px,1fr)) !important; }
@media (max-width: 1320px) {
  .ba-topbar { grid-template-columns: 1fr !important; }
  .ba-filter-row-main { justify-content: flex-start !important; flex-wrap: wrap !important; }
  .ba-worker-pills { max-width: 100% !important; }
}
@media (max-width: 720px) {
  .ba-filter-row-main { gap: 7px !important; }
  .ba-friendly-periods, .ba-date-control-compact, .ba-worker-pills { width: 100% !important; max-width: 100% !important; }
  .ba-view-customizer-body { grid-template-columns: 1fr !important; }
}



  /* LOOP D2.9.3E — cash method breakdown */
  .ba-cash-method-wrap { display:grid; gap:14px; }
  .ba-cash-method-table { display:grid; gap:0; border:1px solid #e2e8f0; border-radius:18px; overflow:hidden; background:#fff; }
  .ba-cash-method-row { position:relative; display:grid; grid-template-columns:minmax(130px,1.2fr) repeat(3,minmax(74px,.7fr)); gap:10px; align-items:center; padding:12px 14px; border-top:1px solid #eef2f7; color:#334155; font-size:13px; font-weight:850; }
  .ba-cash-method-row:first-child { border-top:0; }
  .ba-cash-method-row.head { background:#f8fafc; color:#64748b; font-size:11px; font-weight:950; text-transform:uppercase; letter-spacing:.08em; }
  .ba-cash-method-row strong { position:relative; min-width:0; color:#0f172a; font-size:13px; font-weight:950; padding-bottom:8px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .ba-cash-method-row strong i { position:absolute; left:0; bottom:0; height:5px; border-radius:999px; background:linear-gradient(90deg,#00796f,#14b8a6); min-width:18px; }
  .ba-cash-method-row span, .ba-cash-method-row em { text-align:right; font-style:normal; white-space:nowrap; }
  .ba-cash-method-row em { color:var(--brand); font-weight:950; }
  .ba-covered-methods { display:grid; gap:10px; border:1px dashed rgba(0,103,95,.22); background:#f8fffd; border-radius:18px; padding:13px; }
  .ba-covered-methods > div:first-child { display:grid; gap:2px; }
  .ba-covered-methods > div:first-child span { color:#00675f; font-size:11px; font-weight:950; text-transform:uppercase; letter-spacing:.10em; }
  .ba-covered-methods > div:first-child small { color:#64748b; font-size:11px; font-weight:750; line-height:1.35; }
  @media (max-width: 720px) { .ba-cash-method-row { grid-template-columns:1fr; gap:5px; } .ba-cash-method-row.head { display:none; } .ba-cash-method-row span,.ba-cash-method-row em { text-align:left; } .ba-cash-method-row span::before { color:#94a3b8; font-size:10px; font-weight:950; text-transform:uppercase; letter-spacing:.08em; margin-right:8px; } .ba-cash-method-row span:nth-child(2)::before { content:"Cobro"; } .ba-cash-method-row span:nth-child(3)::before { content:"Propina"; } .ba-cash-method-row em::before { content:"Total"; color:#94a3b8; font-size:10px; font-weight:950; text-transform:uppercase; letter-spacing:.08em; margin-right:8px; } }

  /* LOOP Inventory Command Center Revamp */
  .ba-inventory-command { display:grid; gap:18px; }
  .ba-inventory-command-head { display:flex; align-items:flex-start; justify-content:space-between; gap:18px; border:1px solid rgba(0,103,95,.12); background:linear-gradient(135deg,#fffdf8 0%,#ffffff 56%,#effdf9 100%); border-radius:28px; padding:24px; box-shadow:0 18px 44px rgba(15,23,42,.06); }
  .ba-inventory-command-head span, .ba-inventory-health-main > span, .ba-inventory-secondary-card span, .ba-inventory-mini-card span { color:var(--brand); font-size:11px; font-weight:950; text-transform:uppercase; letter-spacing:.14em; }
  .ba-inventory-command-head h3 { margin:7px 0 5px; font-size:30px; line-height:1; letter-spacing:-.04em; color:#0f172a; }
  .ba-inventory-command-head p { margin:0; max-width:680px; color:#5f6f85; font-size:13px; line-height:1.45; font-weight:800; }
  .ba-inventory-command-actions { display:flex; align-items:center; justify-content:flex-end; gap:9px; flex-wrap:wrap; }
  .ba-inventory-command-actions button, .ba-inventory-products-toolbar button, .ba-inventory-secondary-card button, .ba-inventory-mini-card button, .ba-inventory-modal-head button, .ba-inventory-settings-grid button { border:1px solid #dbe6ef; background:#fff; color:#26364d; border-radius:14px; min-height:40px; padding:0 13px; font-size:12px; font-weight:950; cursor:pointer; box-shadow:0 8px 18px rgba(15,23,42,.04); }
  .ba-inventory-command-actions button.primary, .ba-inventory-products-toolbar button:first-of-type, .ba-inventory-form-actions button, .ba-inventory-settings-grid button.is-active { background:linear-gradient(135deg,#00796f,#00675f); border-color:#00675f; color:#fff; box-shadow:0 12px 26px rgba(0,103,95,.18); }
  .ba-inventory-command-actions button:disabled, .ba-inventory-products-toolbar button:disabled, .ba-inventory-form-actions button:disabled { opacity:.55; cursor:not-allowed; }
  .ba-inventory-command-status { border:1px solid rgba(0,103,95,.16); background:#effdf9; color:#00675f; border-radius:16px; padding:11px 14px; font-size:12px; font-weight:900; }
  .ba-inventory-command-status.muted { background:#f8fafc; color:#64748b; border-color:#e2e8f0; }
  .ba-inventory-command-alert { display:flex; align-items:center; justify-content:space-between; gap:14px; border:1px solid #fed7aa; background:#fff7ed; color:#7c2d12; border-radius:20px; padding:14px 16px; }
  .ba-inventory-command-alert strong { display:block; color:#7c2d12; font-size:14px; font-weight:950; }
  .ba-inventory-command-alert span { display:block; font-size:12px; font-weight:800; color:#9a3412; margin-top:2px; }
  .ba-inventory-command-alert button { border:1px solid #fdba74; background:#fff; color:#9a3412; border-radius:14px; min-height:38px; padding:0 13px; font-weight:950; cursor:pointer; }
  .ba-inventory-health { display:grid; grid-template-columns:minmax(0,1.1fr) minmax(420px, .9fr); gap:18px; align-items:stretch; border:1px solid rgba(0,103,95,.14); border-radius:28px; padding:20px; background:radial-gradient(circle at 92% 8%, rgba(20,184,166,.13), transparent 32%), #ffffff; box-shadow:0 18px 44px rgba(15,23,42,.055); }
  .ba-inventory-health.warning { border-color:#fde68a; background:radial-gradient(circle at 92% 8%, rgba(245,158,11,.14), transparent 32%), #ffffff; }
  .ba-inventory-health.critical { border-color:#fecaca; background:radial-gradient(circle at 92% 8%, rgba(239,68,68,.12), transparent 32%), #ffffff; }
  .ba-inventory-health-main { display:grid; align-content:center; gap:8px; padding:8px 4px; }
  .ba-inventory-health-main h3 { margin:0; font-size:28px; line-height:1.05; letter-spacing:-.035em; color:#0f172a; }
  .ba-inventory-health-main p { margin:0; color:#64748b; font-size:13px; font-weight:850; line-height:1.45; max-width:620px; }
  .ba-inventory-health-pills { display:flex; flex-wrap:wrap; gap:8px; margin-top:4px; }
  .ba-inventory-health-pills em { font-style:normal; border-radius:999px; border:1px solid #dbe6ef; background:#f8fafc; color:#52657d; padding:7px 10px; font-size:11px; font-weight:950; }
  .ba-inventory-health-pills em.ok { background:#ecfdf5; border-color:#bbf7d0; color:#047857; }
  .ba-inventory-health-pills em.muted { background:#f1f5f9; color:#64748b; }
  .ba-inventory-health-metrics { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:10px; }
  .ba-inventory-health-metrics article { border:1px solid #e2e8f0; background:#fbfdff; border-radius:20px; padding:15px; display:grid; gap:5px; align-content:center; min-height:118px; }
  .ba-inventory-health-metrics span { color:#64748b; font-size:11px; font-weight:950; text-transform:uppercase; letter-spacing:.08em; }
  .ba-inventory-health-metrics strong { color:#0f172a; font-size:26px; font-weight:950; letter-spacing:-.04em; }
  .ba-inventory-health-metrics em { color:#64748b; font-style:normal; font-size:11px; font-weight:800; }
  .ba-inventory-focus-grid { display:grid; grid-template-columns:minmax(0,1fr) 280px; gap:16px; align-items:start; }
  .ba-inventory-side-stack { display:grid; gap:12px; }
  .ba-inventory-mini-card, .ba-inventory-secondary-card { border:1px solid #e2e8f0; background:#fff; border-radius:22px; padding:16px; box-shadow:0 12px 28px rgba(15,23,42,.045); display:grid; gap:8px; }
  .ba-inventory-mini-card.success { background:#f0fdf8; border-color:#bbf7d0; }
  .ba-inventory-mini-card.warning { background:#fff7ed; border-color:#fed7aa; }
  .ba-inventory-mini-card strong, .ba-inventory-secondary-card strong { color:#0f172a; font-size:28px; font-weight:950; letter-spacing:-.035em; }
  .ba-inventory-mini-card p, .ba-inventory-secondary-card p { margin:0; color:#64748b; font-size:12px; line-height:1.35; font-weight:800; }
  .ba-inventory-action-tabs { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:12px; }
  .ba-inventory-action-tabs button { border:1px solid #dbe6ef; background:#fff; color:#475569; border-radius:999px; padding:9px 12px; font-size:12px; font-weight:950; cursor:pointer; }
  .ba-inventory-action-tabs button.active { background:#00675f; border-color:#00675f; color:#fff; }
  .ba-inventory-action-tabs strong { margin-left:6px; }
  .ba-inventory-products-toolbar { display:grid; grid-template-columns:minmax(220px,1fr) auto auto; gap:10px; margin-bottom:14px; }
  .ba-inventory-products-toolbar input { border:1px solid #dbe6ef; background:#f8fafc; border-radius:16px; min-height:42px; padding:0 14px; font-size:13px; font-weight:800; color:#0f172a; }
  .ba-inventory-secondary-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:14px; }
  .ba-inventory-secondary-card { grid-template-columns:minmax(0,1fr) auto; align-items:center; }
  .ba-inventory-secondary-card button { grid-column:1 / -1; justify-self:start; }
  .ba-inventory-modal-layer { position:fixed; inset:0; z-index:9999; background:rgba(15,23,42,.32); backdrop-filter:blur(6px); display:flex; justify-content:flex-end; padding:22px; }
  .ba-inventory-modal { width:min(560px, calc(100vw - 44px)); max-height:calc(100vh - 44px); overflow:hidden; border-radius:26px; background:#fff; border:1px solid #dbe6ef; box-shadow:0 34px 90px rgba(15,23,42,.24); display:flex; flex-direction:column; }
  .ba-inventory-modal.wide { width:min(1080px, calc(100vw - 44px)); }
  .ba-inventory-modal-head { padding:20px 22px; border-bottom:1px solid #edf2f7; display:flex; align-items:flex-start; justify-content:space-between; gap:14px; background:linear-gradient(135deg,#fffdf8,#ffffff); }
  .ba-inventory-modal-head span { color:#00675f; font-size:11px; font-weight:950; text-transform:uppercase; letter-spacing:.14em; }
  .ba-inventory-modal-head h3 { margin:6px 0 4px; color:#0f172a; font-size:24px; line-height:1.05; letter-spacing:-.035em; }
  .ba-inventory-modal-head p { margin:0; color:#64748b; font-size:12px; font-weight:800; line-height:1.4; }
  .ba-inventory-modal-head button { width:38px; padding:0; border-radius:50%; font-size:24px; line-height:1; }
  .ba-inventory-modal-body { padding:18px 22px 22px; overflow:auto; }
  .ba-inventory-modal .ba-inventory-form { grid-template-columns:repeat(2,minmax(0,1fr)); }
  .ba-inventory-modal .ba-inventory-form .wide { grid-column:1 / -1; }
  .ba-inventory-settings-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:12px; }
  .ba-inventory-settings-grid label { display:grid; gap:6px; color:#475569; font-size:11px; font-weight:950; text-transform:uppercase; letter-spacing:.06em; }
  .ba-inventory-settings-grid select { border:1px solid #dbe6ef; border-radius:14px; min-height:42px; padding:0 12px; background:#fff; color:#0f172a; font-size:13px; font-weight:850; }
  .modal-list { max-height:52vh; overflow:auto; padding-right:4px; }
  .analysis-modal-grid, .history-modal-grid { grid-template-columns:minmax(260px,.75fr) minmax(0,1.25fr); }
  @media (max-width: 980px) {
    .ba-inventory-command-head, .ba-inventory-health { grid-template-columns:1fr; display:grid; }
    .ba-inventory-command-actions { justify-content:flex-start; }
    .ba-inventory-focus-grid, .ba-inventory-secondary-grid, .analysis-modal-grid, .history-modal-grid { grid-template-columns:1fr; }
    .ba-inventory-health-metrics { grid-template-columns:1fr; }
    .ba-inventory-products-toolbar { grid-template-columns:1fr; }
    .ba-inventory-modal-layer { padding:10px; }
    .ba-inventory-modal, .ba-inventory-modal.wide { width:100%; max-height:calc(100vh - 20px); }
    .ba-inventory-modal .ba-inventory-form, .ba-inventory-settings-grid { grid-template-columns:1fr; }
  }



  /* LOOP Inventory Command Center Phase 2 — product and movement premium workflows */
  .ba-inventory-product-designer, .ba-inventory-movement-designer { display:grid; grid-template-columns:minmax(0,1.25fr) 340px; gap:18px; align-items:start; }
  .ba-inventory-product-main { display:grid; gap:14px; }
  .ba-inventory-form-card, .ba-inventory-product-preview, .ba-inventory-movement-preview, .ba-stocktake-summary { border:1px solid #e2e8f0; border-radius:22px; background:linear-gradient(180deg,#ffffff,#fbfdff); padding:16px; box-shadow:0 12px 28px rgba(15,23,42,.045); }
  .ba-inventory-form-card { display:grid; gap:12px; }
  .ba-inventory-form-card-head { display:grid; gap:4px; margin-bottom:2px; }
  .ba-inventory-form-card-head span, .ba-inventory-product-preview > span, .ba-inventory-movement-preview > span, .ba-stocktake-summary span { color:#00675f; font-size:10px; font-weight:950; text-transform:uppercase; letter-spacing:.14em; }
  .ba-inventory-form-card-head strong { color:#0f172a; font-size:17px; font-weight:950; letter-spacing:-.025em; }
  .ba-inventory-form-card-head em, .ba-inventory-product-preview p, .ba-inventory-movement-preview p, .ba-stocktake-summary p { color:#64748b; font-size:12px; font-style:normal; font-weight:800; line-height:1.4; margin:0; }
  .ba-inventory-form-card label, .ba-inventory-product-designer label, .ba-inventory-movement-designer label { display:grid; gap:6px; color:#475569; font-size:11px; font-weight:950; text-transform:uppercase; letter-spacing:.06em; }
  .ba-inventory-form-card input, .ba-inventory-form-card select, .ba-inventory-form-card textarea, .ba-inventory-product-designer input, .ba-inventory-product-designer textarea, .ba-inventory-movement-designer input, .ba-inventory-movement-designer select, .ba-inventory-movement-designer textarea { border:1px solid #dbe6ef; background:#ffffff; border-radius:15px; min-height:42px; padding:0 12px; color:#0f172a; font-size:13px; font-weight:850; outline:none; }
  .ba-inventory-form-card textarea, .ba-inventory-product-designer textarea, .ba-inventory-movement-designer textarea { padding:12px; resize:vertical; line-height:1.45; }
  .ba-inventory-form-card input:focus, .ba-inventory-form-card select:focus, .ba-inventory-form-card textarea:focus, .ba-inventory-product-designer input:focus, .ba-inventory-product-designer textarea:focus, .ba-inventory-movement-designer input:focus, .ba-inventory-movement-designer select:focus, .ba-inventory-movement-designer textarea:focus { border-color:#14b8a6; box-shadow:0 0 0 4px rgba(20,184,166,.10); }
  .ba-inventory-form-two { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:12px; }
  .ba-inventory-form-three { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:12px; }
  .ba-inventory-form-actions.sticky { position:sticky; bottom:-18px; z-index:2; margin-top:2px; padding:12px 0 0; background:linear-gradient(180deg,rgba(255,255,255,0),#fff 28%); }
  .ba-inventory-product-preview, .ba-inventory-movement-preview { position:sticky; top:0; display:grid; gap:12px; align-content:start; }
  .ba-inventory-preview-icon { width:64px; height:64px; border-radius:20px; background:linear-gradient(135deg,#00796f,#00675f); color:#fff; display:grid; place-items:center; font-size:22px; font-weight:950; box-shadow:0 16px 34px rgba(0,103,95,.18); }
  .ba-inventory-product-preview h4, .ba-inventory-movement-preview h4 { margin:0; color:#0f172a; font-size:23px; line-height:1.05; letter-spacing:-.04em; }
  .ba-inventory-product-preview dl, .ba-inventory-movement-preview dl { display:grid; gap:8px; margin:4px 0 0; }
  .ba-inventory-product-preview dl div, .ba-inventory-movement-preview dl div { display:flex; align-items:center; justify-content:space-between; gap:10px; border:1px solid #edf2f7; background:#f8fafc; border-radius:14px; padding:10px 11px; }
  .ba-inventory-product-preview dt, .ba-inventory-movement-preview dt { color:#64748b; font-size:11px; font-weight:950; text-transform:uppercase; letter-spacing:.06em; }
  .ba-inventory-product-preview dd, .ba-inventory-movement-preview dd { margin:0; color:#0f172a; font-size:13px; font-weight:950; text-align:right; }
  .ba-inventory-product-preview > em { justify-self:start; border-radius:999px; padding:8px 10px; font-size:11px; font-weight:950; font-style:normal; }
  .ba-inventory-product-preview > em.ok { color:#047857; background:#ecfdf5; border:1px solid #bbf7d0; }
  .ba-inventory-product-preview > em.low { color:#9a3412; background:#fff7ed; border:1px solid #fed7aa; }
  .ba-adjust-preview.premium { border-radius:18px; padding:14px; display:grid; gap:6px; }
  .ba-inventory-preview-empty { border:1px dashed #cbd5e1; border-radius:18px; background:#f8fafc; color:#64748b; padding:18px; font-size:12px; font-weight:850; text-align:center; }
  .ba-stocktake-premium { display:grid; gap:14px; }
  .ba-stocktake-summary { display:flex; align-items:center; justify-content:space-between; gap:18px; background:linear-gradient(135deg,#effdf9,#ffffff); }
  .ba-stocktake-summary strong { display:block; color:#0f172a; font-size:28px; line-height:1; font-weight:950; letter-spacing:-.035em; margin-top:4px; }
  .ba-stocktake-summary em { display:block; color:#64748b; font-size:11px; font-style:normal; font-weight:850; margin-top:2px; }
  .ba-stocktake-row.premium { border-radius:18px; border:1px solid #e2e8f0; background:#fff; padding:12px; margin-bottom:8px; }
  .ba-stocktake-actions.premium { position:sticky; bottom:-22px; background:linear-gradient(180deg,rgba(255,255,255,0),#fff 28%); padding-top:12px; }
  .ba-stocktake-actions.premium button:disabled { opacity:.55; cursor:not-allowed; }
  @media (max-width: 980px) {
    .ba-inventory-product-designer, .ba-inventory-movement-designer { grid-template-columns:1fr; }
    .ba-inventory-product-preview, .ba-inventory-movement-preview { position:static; }
    .ba-inventory-form-two, .ba-inventory-form-three { grid-template-columns:1fr; }
    .ba-stocktake-summary { align-items:flex-start; flex-direction:column; }
  }

  /* LOOP Inventory Command Center Phase 3 — services/materials connection map */
  .ba-service-connect-strip { display:grid; gap:14px; }
  .ba-service-connect-summary, .ba-service-kpi-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:10px; }
  .ba-service-kpi-grid { grid-template-columns:repeat(4,minmax(0,1fr)); }
  .ba-service-connect-summary article, .ba-service-kpi-grid article { border:1px solid #e2e8f0; background:#fbfdff; border-radius:18px; padding:13px; display:grid; gap:4px; }
  .ba-service-connect-summary span, .ba-service-kpi-grid span { color:#64748b; font-size:10px; font-weight:950; text-transform:uppercase; letter-spacing:.08em; }
  .ba-service-connect-summary strong, .ba-service-kpi-grid strong { color:#0f172a; font-size:24px; line-height:1; font-weight:950; letter-spacing:-.04em; }
  .ba-service-connect-summary em, .ba-service-kpi-grid em { color:#64748b; font-style:normal; font-size:11px; font-weight:800; }
  .ba-service-kpi-grid article.warning { background:#fff7ed; border-color:#fed7aa; }
  .ba-service-kpi-grid article.success { background:#ecfdf5; border-color:#bbf7d0; }
  .ba-service-connect-preview, .ba-service-priority-list { display:grid; gap:8px; }
  .ba-service-chip-row { border:1px solid #e2e8f0; background:#fff; border-radius:16px; padding:11px 12px; display:flex; align-items:center; justify-content:space-between; gap:12px; }
  .ba-service-chip-row strong { display:block; color:#0f172a; font-size:13px; font-weight:950; }
  .ba-service-chip-row span { display:block; color:#64748b; font-size:11.5px; font-weight:800; margin-top:2px; }
  .ba-service-chip-row em { font-style:normal; border-radius:999px; padding:6px 9px; font-size:10.5px; font-weight:950; background:#f1f5f9; color:#475569; white-space:nowrap; }
  .ba-service-chip-row.ok em { background:#ecfdf5; color:#047857; }
  .ba-service-chip-row.warning em { background:#fff7ed; color:#9a3412; }
  .ba-service-chip-row.critical em { background:#fef2f2; color:#b91c1c; }
  .ba-service-connect-open { justify-self:start; border:1px solid rgba(0,103,95,.18); background:#effdf9; color:#00675f; border-radius:14px; min-height:40px; padding:0 13px; font-size:12px; font-weight:950; cursor:pointer; }
  .ba-service-analysis-view { display:grid; gap:16px; }
  .ba-service-analysis-hero { border:1px solid rgba(0,103,95,.14); background:linear-gradient(135deg,#effdf9,#ffffff); border-radius:24px; padding:18px; display:flex; align-items:center; justify-content:space-between; gap:18px; }
  .ba-service-analysis-hero span { color:#00675f; font-size:10px; font-weight:950; text-transform:uppercase; letter-spacing:.14em; }
  .ba-service-analysis-hero h3 { margin:5px 0 4px; color:#0f172a; font-size:24px; line-height:1.05; letter-spacing:-.04em; }
  .ba-service-analysis-hero p { margin:0; max-width:720px; color:#64748b; font-size:12px; font-weight:800; line-height:1.45; }
  .ba-service-analysis-hero > strong { color:#00675f; font-size:38px; font-weight:950; letter-spacing:-.06em; }
  .ba-service-analysis-layout { display:grid; grid-template-columns:300px minmax(0,1fr); gap:14px; align-items:start; }
  .ba-service-analysis-side { display:grid; gap:14px; }
  .ba-service-priority-list > div { border:1px solid #e2e8f0; background:#fbfdff; border-radius:14px; padding:10px 11px; display:grid; gap:2px; }
  .ba-service-priority-list strong { color:#0f172a; font-size:12.5px; font-weight:950; }
  .ba-service-priority-list span { color:#64748b; font-size:11px; font-weight:800; }
  .ba-service-analysis-toolbar { display:grid; grid-template-columns:minmax(220px,1fr) auto; gap:10px; margin-bottom:12px; }
  .ba-service-analysis-toolbar input { border:1px solid #dbe6ef; background:#f8fafc; border-radius:15px; min-height:42px; padding:0 13px; color:#0f172a; font-size:13px; font-weight:850; outline:none; }
  .ba-service-analysis-toolbar button, .ba-service-material-actions button { border:1px solid #dbe6ef; background:#fff; color:#26364d; border-radius:14px; min-height:40px; padding:0 13px; font-size:12px; font-weight:950; cursor:pointer; }
  .ba-service-materials-table.premium { display:grid; gap:10px; }
  .ba-service-material-card { border:1px solid #e2e8f0; border-radius:18px; background:#fff; padding:14px; display:grid; gap:12px; }
  .ba-service-material-card.warning { background:#fffdf5; border-color:#fde68a; }
  .ba-service-material-card.critical { background:#fff7f7; border-color:#fecaca; }
  .ba-service-material-top { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; }
  .ba-service-material-top strong { color:#0f172a; font-size:15px; font-weight:950; }
  .ba-service-material-top span { display:block; color:#64748b; font-size:12px; font-weight:800; margin-top:3px; line-height:1.35; }
  .ba-service-material-top em { font-style:normal; border-radius:999px; padding:7px 9px; font-size:11px; font-weight:950; background:#f1f5f9; color:#475569; white-space:nowrap; }
  .ba-service-material-card.ok .ba-service-material-top em { background:#ecfdf5; color:#047857; }
  .ba-service-material-card.warning .ba-service-material-top em { background:#fff7ed; color:#9a3412; }
  .ba-service-material-card.critical .ba-service-material-top em { background:#fef2f2; color:#b91c1c; }
  .ba-service-material-stats { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:8px; }
  .ba-service-material-stats div { border:1px solid #edf2f7; background:#f8fafc; border-radius:14px; padding:10px; display:grid; gap:3px; }
  .ba-service-material-stats span { color:#64748b; font-size:10px; font-weight:950; text-transform:uppercase; letter-spacing:.08em; }
  .ba-service-material-stats strong { color:#0f172a; font-size:13px; font-weight:950; }
  .ba-service-material-lines { display:flex; flex-wrap:wrap; gap:6px; }
  .ba-service-material-lines span { border:1px solid #dbe6ef; background:#fff; border-radius:999px; padding:7px 9px; color:#475569; font-size:11px; font-weight:900; }
  .ba-service-material-lines span.ok { background:#ecfdf5; border-color:#bbf7d0; color:#047857; }
  .ba-service-material-lines span.warning { background:#fff7ed; border-color:#fed7aa; color:#9a3412; }
  .ba-service-material-lines span.critical { background:#fef2f2; border-color:#fecaca; color:#b91c1c; }
  .ba-service-material-empty { margin:0; border:1px dashed #cbd5e1; background:#f8fafc; border-radius:14px; padding:12px; color:#64748b; font-size:12px; font-weight:850; }
  .ba-service-material-actions { display:flex; align-items:center; justify-content:space-between; gap:10px; border-top:1px solid #edf2f7; padding-top:10px; }
  .ba-service-material-actions p { margin:0; color:#64748b; font-size:11.5px; font-weight:850; line-height:1.35; }
  @media (max-width: 980px) {
    .ba-service-connect-summary, .ba-service-kpi-grid, .ba-service-material-stats, .ba-service-analysis-layout { grid-template-columns:1fr; }
    .ba-service-analysis-toolbar { grid-template-columns:1fr; }
    .ba-service-material-actions, .ba-service-analysis-hero, .ba-service-material-top { flex-direction:column; align-items:flex-start; }
  }


  /* LOOP PHASE 3.0 — Smart Replenishment Planner */
  .ba-replenishment-planner{display:grid;gap:14px;}
  .ba-replenishment-smart{grid-template-columns:minmax(0,1fr) auto;align-items:center;}
  .ba-replenishment-hero-total{position:relative;z-index:1;display:grid;justify-items:end;gap:8px;min-width:210px;}
  .ba-replenishment-hero-total strong{color:#00675f;font-size:36px;font-weight:950;letter-spacing:-.07em;line-height:1;}
  .ba-replenishment-hero-total small{color:var(--muted);font-size:12px;font-weight:850;}
  .ba-replenishment-hero-total button{min-height:38px;border:1px solid rgba(0,103,95,.18);background:#fff;color:#00675f;border-radius:999px;padding:0 14px;font-size:12px;font-weight:950;cursor:pointer;box-shadow:0 10px 18px rgba(15,23,42,.045);}
  .ba-replenishment-hero-total button:disabled{opacity:.45;cursor:not-allowed;}
  .ba-replenishment-kpis article.critical{border-color:rgba(239,68,68,.25);background:linear-gradient(135deg,#fef2f2,#fff);}
  .ba-replenishment-kpis article.warning{border-color:rgba(245,158,11,.28);background:linear-gradient(135deg,#fffbeb,#fff);}
  .ba-replenishment-kpis article.success{border-color:rgba(20,184,166,.22);background:linear-gradient(135deg,#f0fdfa,#fff);}
  .ba-replenishment-toolbar{display:grid;grid-template-columns:minmax(0,1fr) minmax(240px,360px);gap:12px;align-items:center;}
  .ba-replenishment-tabs{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
  .ba-replenishment-tabs button{min-height:38px;border-radius:999px;border:1px solid #dbe5ee;background:#fff;color:#334155;padding:0 13px;font-size:12px;font-weight:950;cursor:pointer;display:inline-flex;align-items:center;gap:7px;}
  .ba-replenishment-tabs button strong{font-size:11px;color:inherit;background:#f1f5f9;border-radius:999px;padding:2px 7px;}
  .ba-replenishment-tabs button.active{background:#00675f;border-color:#00675f;color:#fff;box-shadow:0 12px 22px rgba(0,103,95,.16);}
  .ba-replenishment-tabs button.active strong{background:rgba(255,255,255,.18);color:#fff;}
  .ba-replenishment-toolbar input{min-height:42px;border:1px solid #dbe5ee;border-radius:16px;background:#fff;padding:0 14px;color:var(--ink);font-size:13px;font-weight:850;outline:none;}
  .ba-replenishment-suppliers{border:1px solid #e2e8f0;background:linear-gradient(135deg,#fff,#f8fafc);border-radius:22px;padding:14px;display:grid;gap:12px;}
  .ba-replenishment-section-head{display:flex;align-items:center;justify-content:space-between;gap:12px;}
  .ba-replenishment-section-head span{display:block;color:#00675f;text-transform:uppercase;letter-spacing:.11em;font-size:10px;font-weight:950;}
  .ba-replenishment-section-head strong{display:block;color:var(--ink);font-size:14px;font-weight:950;}
  .ba-replenishment-section-head button{min-height:34px;border-radius:999px;border:1px solid #dbe5ee;background:#fff;color:#334155;padding:0 12px;font-size:11.5px;font-weight:950;cursor:pointer;}
  .ba-replenishment-supplier-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;}
  .ba-replenishment-supplier-grid article{border:1px solid #e2e8f0;background:#fff;border-radius:18px;padding:12px;display:grid;gap:3px;box-shadow:0 10px 22px rgba(15,23,42,.04);}
  .ba-replenishment-supplier-grid article span{color:#64748b;font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:.06em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .ba-replenishment-supplier-grid article strong{color:#0f172a;font-size:20px;font-weight:950;letter-spacing:-.04em;}
  .ba-replenishment-supplier-grid article em{color:#64748b;font-size:11.5px;font-weight:800;font-style:normal;}
  .ba-replenishment-row-smart{grid-template-columns:minmax(0,1.45fr) minmax(185px,.55fr) minmax(165px,.45fr) auto;}
  .ba-replenishment-row-smart.bucket-now{border-color:rgba(239,68,68,.25);}
  .ba-replenishment-row-smart.bucket-soon{border-color:rgba(245,158,11,.28);}
  .ba-replenishment-title-line{display:flex;align-items:center;gap:9px;min-width:0;}
  .ba-replenishment-title-line strong{min-width:0;}
  .ba-replenishment-title-line .bucket{border-radius:999px;padding:4px 8px;font-size:10.5px;font-weight:950;font-style:normal;white-space:nowrap;}
  .ba-replenishment-title-line .bucket.now{background:#fee2e2;color:#b91c1c;}
  .ba-replenishment-title-line .bucket.soon{background:#fef3c7;color:#92400e;}
  .ba-replenishment-title-line .bucket.watch{background:#e0f2fe;color:#075985;}
  .ba-replenishment-impact{display:grid;gap:8px;}
  .ba-replenishment-impact div{border:1px solid #e2e8f0;background:#f8fafc;border-radius:14px;padding:8px 10px;display:grid;gap:2px;}
  .ba-replenishment-impact span{color:#64748b;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.06em;}
  .ba-replenishment-impact strong{color:#0f172a;font-size:12.5px;font-weight:950;white-space:nowrap;}
  @media(max-width:1120px){
    .ba-replenishment-toolbar{grid-template-columns:1fr;}
    .ba-replenishment-supplier-grid{grid-template-columns:repeat(2,minmax(0,1fr));}
    .ba-replenishment-row-smart{grid-template-columns:1fr;}
    .ba-replenishment-impact{grid-template-columns:repeat(2,minmax(0,1fr));}
  }
  @media(max-width:680px){
    .ba-replenishment-smart{grid-template-columns:1fr;}
    .ba-replenishment-hero-total{justify-items:start;min-width:0;}
    .ba-replenishment-supplier-grid,.ba-replenishment-impact{grid-template-columns:1fr;}
  }




  /* LOOP Inventory Command Center Phase 5 — premium final layout */
  .ba-inventory-hero-unified{position:relative;overflow:hidden;border:1px solid rgba(0,103,95,.14);border-radius:30px;background:radial-gradient(circle at 94% 10%,rgba(20,184,166,.14),transparent 32%),linear-gradient(135deg,#fffdf8 0%,#fff 50%,#effdf9 100%);box-shadow:0 22px 58px rgba(15,23,42,.065);padding:24px;display:grid;gap:20px;}
  .ba-inventory-hero-unified.warning{border-color:#fde68a;background:radial-gradient(circle at 94% 10%,rgba(245,158,11,.16),transparent 32%),linear-gradient(135deg,#fffdf8 0%,#fff 55%,#fffbeb 100%);}
  .ba-inventory-hero-unified.critical{border-color:#fecaca;background:radial-gradient(circle at 94% 10%,rgba(239,68,68,.13),transparent 32%),linear-gradient(135deg,#fffafa 0%,#fff 55%,#fef2f2 100%);}
  .ba-inventory-hero-top{display:flex;align-items:flex-start;justify-content:space-between;gap:22px;position:relative;z-index:1;}
  .ba-inventory-hero-copy span,.ba-inventory-intel-card span{color:#00675f;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.16em;}
  .ba-inventory-hero-copy h3{margin:7px 0 6px;color:#0f172a;font-size:34px;line-height:.98;letter-spacing:-.05em;}
  .ba-inventory-hero-copy p{margin:0;max-width:720px;color:#5f6f85;font-size:13px;line-height:1.45;font-weight:850;}
  .ba-inventory-hero-actions{align-items:flex-start;min-width:max-content;}
  .ba-inventory-hero-bottom{display:grid;grid-template-columns:minmax(0,.82fr) minmax(540px,1.18fr);gap:18px;align-items:stretch;position:relative;z-index:1;}
  .ba-inventory-hero-metrics{grid-template-columns:repeat(4,minmax(0,1fr));}
  .ba-inventory-hero-metrics article{min-height:112px;background:rgba(255,255,255,.78);backdrop-filter:blur(8px);}
  .ba-inventory-premium-layout{display:grid;grid-template-columns:minmax(0,1fr) 340px;gap:18px;align-items:start;}
  .ba-inventory-premium-main{display:grid;gap:18px;min-width:0;}
  .ba-inventory-table-panel,.ba-inventory-products-priority{box-shadow:0 18px 44px rgba(15,23,42,.055);}
  .ba-inventory-deduction-table .ba-inventory-row,.ba-inventory-products-table .ba-inventory-row{display:grid;align-items:center;gap:12px;}
  .ba-inventory-deduction-table .ba-inventory-row{grid-template-columns:minmax(220px,1.25fr) minmax(90px,.42fr) minmax(90px,.42fr) minmax(220px,1fr) minmax(150px,.58fr);}
  .ba-inventory-products-table .ba-inventory-row{grid-template-columns:minmax(220px,1.25fr) minmax(110px,.5fr) minmax(90px,.42fr) minmax(100px,.45fr) minmax(100px,.45fr) minmax(230px,.9fr);}
  .ba-inventory-row-head{border:0!important;background:#f8fafc!important;box-shadow:none!important;color:#64748b;text-transform:uppercase;letter-spacing:.08em;font-size:10.5px;font-weight:950;padding:10px 14px!important;min-height:auto!important;}
  .ba-inventory-row-head span{color:#64748b!important;font-size:10.5px!important;font-weight:950!important;text-transform:uppercase;letter-spacing:.08em;}
  .ba-inventory-deduction-table .ba-inventory-row:not(.ba-inventory-row-head),.ba-inventory-products-table .ba-inventory-row:not(.ba-inventory-row-head){border-radius:18px;padding:12px 14px;min-height:74px;}
  .ba-inventory-deduction-table .ba-inventory-row:not(.ba-inventory-row-head) > div span,.ba-inventory-products-table .ba-inventory-row:not(.ba-inventory-row-head) > div span{display:block;color:#64748b;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.07em;margin-bottom:3px;}
  .ba-inventory-deduction-table .ba-inventory-row:not(.ba-inventory-row-head) > div strong,.ba-inventory-products-table .ba-inventory-row:not(.ba-inventory-row-head) > div strong{color:#0f172a;font-size:12px;font-weight:950;}
  .ba-inventory-name strong{font-size:14px!important;}
  .ba-inventory-name span{font-size:11.5px!important;text-transform:none!important;letter-spacing:0!important;margin-top:3px;color:#64748b!important;}
  .ba-inventory-intel-panel{position:sticky;top:14px;display:grid;gap:12px;}
  .ba-inventory-intel-card{border:1px solid #e2e8f0;background:linear-gradient(180deg,#fff,#fbfdff);border-radius:24px;padding:17px;display:grid;gap:8px;box-shadow:0 14px 34px rgba(15,23,42,.052);}
  .ba-inventory-intel-card.success{background:linear-gradient(135deg,#f0fdfa,#fff);border-color:#bbf7d0;}
  .ba-inventory-intel-card.warning{background:linear-gradient(135deg,#fff7ed,#fff);border-color:#fed7aa;}
  .ba-inventory-intel-card strong{color:#0f172a;font-size:27px;font-weight:950;letter-spacing:-.045em;line-height:1.05;}
  .ba-inventory-intel-card p{margin:0;color:#64748b;font-size:12px;font-weight:850;line-height:1.35;}
  .ba-inventory-intel-card button{justify-self:start;min-height:36px;border:1px solid #dbe6ef;background:#fff;color:#00675f;border-radius:999px;padding:0 13px;font-size:11.5px;font-weight:950;cursor:pointer;box-shadow:0 8px 18px rgba(15,23,42,.04);}
  .ba-inventory-products-priority{scroll-margin-top:18px;}
  @media(max-width:1180px){
    .ba-inventory-hero-top,.ba-inventory-hero-bottom{grid-template-columns:1fr;display:grid;}
    .ba-inventory-hero-actions{justify-content:flex-start;min-width:0;}
    .ba-inventory-hero-metrics{grid-template-columns:repeat(2,minmax(0,1fr));}
    .ba-inventory-premium-layout{grid-template-columns:1fr;}
    .ba-inventory-intel-panel{position:static;grid-template-columns:repeat(2,minmax(0,1fr));}
  }
  @media(max-width:760px){
    .ba-inventory-hero-unified{padding:18px;border-radius:24px;}
    .ba-inventory-hero-copy h3{font-size:28px;}
    .ba-inventory-hero-metrics,.ba-inventory-intel-panel{grid-template-columns:1fr;}
    .ba-inventory-deduction-table .ba-inventory-row,.ba-inventory-products-table .ba-inventory-row{grid-template-columns:1fr!important;}
    .ba-inventory-row-head{display:none!important;}
  }


  /* LOOP Inventory 5.1 — no double scroll + premium command panel */
  .business-analytics.analytics-minimal{
    overflow:visible!important;
    overscroll-behavior:contain;
    padding-bottom:48px!important;
  }
  .business-analytics.analytics-minimal,
  .ba-shell,
  .ba-command-workspace,
  .ba-command-main{
    min-height:auto!important;
    max-height:none!important;
    overflow:visible!important;
  }
  .ba-command-workspace{
    align-items:start!important;
  }
  .ba-command-sidebar{
    position:sticky!important;
    top:84px!important;
    align-self:start!important;
    max-height:none!important;
    overflow:visible!important;
    padding-right:0!important;
    gap:12px!important;
    scrollbar-width:none!important;
  }
  .ba-command-sidebar::-webkit-scrollbar{
    width:0!important;
    height:0!important;
    display:none!important;
  }

  .ba-sidebar-card{
    border:1px solid rgba(0,103,95,.12)!important;
    border-radius:24px!important;
    background:linear-gradient(180deg,rgba(255,255,255,.92),rgba(248,252,251,.86))!important;
    box-shadow:0 20px 48px rgba(15,23,42,.065)!important;
    backdrop-filter:blur(14px);
  }
  .ba-sidebar-headline{
    position:relative;
    overflow:hidden!important;
    padding:17px!important;
    background:
      radial-gradient(circle at 96% 0%,rgba(20,184,166,.18),transparent 34%),
      linear-gradient(135deg,#ffffff 0%,#f6fffc 100%)!important;
  }
  .ba-sidebar-headline:after{
    content:"";
    position:absolute;
    right:-42px;
    bottom:-52px;
    width:118px;
    height:118px;
    border-radius:999px;
    background:rgba(0,103,95,.07);
    pointer-events:none;
  }
  .ba-sidebar-headline>span,
  .ba-sidebar-headline strong,
  .ba-sidebar-headline em{
    position:relative;
    z-index:1;
  }
  .ba-sidebar-headline strong{
    letter-spacing:-.065em!important;
  }

  .ba-sidebar-health{
    padding:9px!important;
    border-radius:22px!important;
    border:1px solid rgba(226,232,240,.82)!important;
    background:rgba(255,255,255,.78)!important;
    box-shadow:0 14px 30px rgba(15,23,42,.045)!important;
  }
  .ba-sidebar-health button{
    min-height:34px!important;
    border-radius:14px!important;
    border-color:rgba(226,232,240,.9)!important;
    background:linear-gradient(180deg,#ffffff,#f8fafc)!important;
    box-shadow:0 8px 18px rgba(15,23,42,.035)!important;
  }

  .ba-command-tabs{
    gap:6px!important;
    padding:8px!important;
    border:1px solid rgba(226,232,240,.85)!important;
    border-radius:26px!important;
    background:

  /* LOOP PHASE 3A — Premium report shell */
  .ba-report-hero{
    position:relative;
    overflow:hidden;
    display:grid;
    grid-template-columns:minmax(0,1.05fr) minmax(340px,.95fr);
    gap:20px;
    align-items:stretch;
    border:1px solid rgba(0,103,95,.12);
    border-radius:30px;
    background:
      radial-gradient(circle at 0% 0%,rgba(20,184,166,.18),transparent 34%),
      radial-gradient(circle at 100% 0%,rgba(0,103,95,.10),transparent 35%),
      linear-gradient(180deg,#ffffff 0%,#f8fffd 100%);
    padding:18px;
    box-shadow:0 22px 54px rgba(15,23,42,.075);
  }
  .ba-report-hero:after{
    content:"";
    position:absolute;
    right:-82px;
    bottom:-92px;
    width:210px;
    height:210px;
    border-radius:50%;
    background:rgba(0,103,95,.055);
    pointer-events:none;
  }
  .ba-report-hero-main,
  .ba-report-hero-controls{
    position:relative;
    z-index:1;
  }
  .ba-report-hero-main{
    min-width:0;
    display:grid;
    gap:18px;
    align-content:space-between;
  }
  .ba-business-identity{
    display:inline-flex;
    align-items:center;
    gap:12px;
    width:fit-content;
    max-width:100%;
    border:1px solid rgba(0,103,95,.12);
    border-radius:999px;
    background:rgba(255,255,255,.82);
    padding:7px 14px 7px 7px;
    box-shadow:0 12px 28px rgba(0,103,95,.06);
  }
  .ba-business-avatar{
    width:40px;
    height:40px;
    border-radius:999px;
    display:grid;
    place-items:center;
    background:linear-gradient(135deg,var(--brand),var(--brand2));
    color:#fff;
    font-size:13px;
    font-weight:950;
    letter-spacing:-.03em;
    box-shadow:0 10px 22px rgba(0,103,95,.18);
  }
  .ba-business-identity div{
    min-width:0;
    display:grid;
    gap:2px;
  }
  .ba-business-identity strong{
    display:block;
    max-width:min(420px,52vw);
    color:#0f172a;
    font-size:14px;
    line-height:1.05;
    font-weight:950;
    white-space:nowrap;
    overflow:hidden;
    text-overflow:ellipsis;
  }
  .ba-business-identity em{
    color:#64748b;
    font-size:11px;
    font-style:normal;
    font-weight:850;
  }
  .ba-report-heading h2{
    margin:6px 0 0;
    color:#07131f;
    font-size:clamp(34px,4.7vw,58px);
    line-height:.92;
    letter-spacing:-.082em;
    font-weight:950;
  }
  .ba-report-heading p{
    max-width:760px;
    margin:12px 0 0;
    color:#475569;
    font-size:14.5px;
    line-height:1.48;
    font-weight:720;
  }
  .ba-report-context{
    display:flex;
    flex-wrap:wrap;
    gap:8px;
  }
  .ba-report-context span{
    min-height:28px;
    display:inline-flex;
    align-items:center;
    border:1px solid rgba(15,23,42,.075);
    border-radius:999px;
    background:#ffffff;
    color:#334155;
    padding:0 10px;
    font-size:11.5px;
    font-weight:900;
    box-shadow:0 8px 20px rgba(15,23,42,.035);
  }
  .ba-report-hero-controls{
    display:grid;
    align-content:center;
    border:1px solid rgba(226,232,240,.82);
    border-radius:24px;
    background:rgba(255,255,255,.76);
    padding:14px;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.85);
    backdrop-filter:blur(14px);
    -webkit-backdrop-filter:blur(14px);
  }
  .ba-report-hero .ba-controls{
    align-self:center;
  }
  .ba-report-hero .ba-filter-row-main{
    display:grid;
    gap:9px;
  }
  .ba-report-hero .ba-friendly-periods{
    grid-template-columns:repeat(5,minmax(0,1fr));
  }
  .ba-report-hero .ba-worker-pills{
    max-height:92px;
    overflow:auto;
    padding-right:2px;
  }
  .ba-command-workspace{
    margin-top:18px!important;
  }
  .ba-command-sidebar{
    top:18px!important;
  }
  .ba-sidebar-headline{
    background:
      radial-gradient(circle at 100% 0%,rgba(20,184,166,.12),transparent 34%),
      linear-gradient(180deg,#ffffff,#fbfffe)!important;
    border-color:rgba(0,103,95,.14)!important;
  }
  .ba-command-tabs button{
    border-radius:16px!important;
  }
  .ba-command-tabs button.is-active{
    background:
      radial-gradient(circle at 100% 0%,rgba(20,184,166,.13),transparent 36%),
      linear-gradient(135deg,rgba(0,103,95,.10),#ffffff)!important;
  }
  .ba-command-tabs button.is-active>i{
    box-shadow:0 10px 20px rgba(0,103,95,.18);
  }
  .ba-phase2-strip{
    margin-top:0!important;
  }
  @media(max-width:980px){
    .ba-report-hero{
      grid-template-columns:1fr;
      border-radius:26px;
      padding:15px;
    }
    .ba-report-hero-controls{
      border-radius:22px;
    }
    .ba-business-identity strong{
      max-width:72vw;
    }
  }
  @media(max-width:640px){
    .ba-report-hero{
      border-radius:24px;
      padding:14px;
    }
    .ba-report-heading h2{
      font-size:34px;
      letter-spacing:-.07em;
    }
    .ba-report-heading p{
      font-size:13px;
    }
    .ba-report-hero .ba-friendly-periods{
      display:flex;
      overflow-x:auto;
      -webkit-overflow-scrolling:touch;
    }
    .ba-report-hero .ba-friendly-periods button{
      flex:0 0 auto;
      min-width:76px;
      min-height:36px;
    }
    .ba-report-hero .ba-worker-pills{
      display:flex;
      overflow-x:auto;
      max-height:none;
      -webkit-overflow-scrolling:touch;
    }
    .ba-report-hero .ba-worker-pills button{
      flex:0 0 auto;
    }
  }
 linear-gradient(180deg,rgba(255,255,255,.84),rgba(248,250,252,.80))!important;
    box-shadow:0 22px 52px rgba(15,23,42,.07)!important;
    backdrop-filter:blur(14px);
  }
  .ba-command-tabs button{
    position:relative!important;
    min-height:60px!important;
    border:1px solid transparent!important;
    border-radius:19px!important;
    background:transparent!important;
    box-shadow:none!important;
    padding:10px 11px!important;
    transition:transform .16s ease, box-shadow .16s ease, background .16s ease, border-color .16s ease!important;
  }
  .ba-command-tabs button:hover{
    transform:translateY(-1px);
    background:rgba(255,255,255,.72)!important;
    border-color:rgba(226,232,240,.85)!important;
  }
  .ba-command-tabs button.is-active{
    background:linear-gradient(135deg,#ffffff 0%,#f1fffb 100%)!important;
    border-color:rgba(0,103,95,.24)!important;
    box-shadow:0 16px 34px rgba(0,103,95,.12), inset 0 0 0 1px rgba(255,255,255,.72)!important;
  }
  .ba-command-tabs button.is-active:before{
    content:"";
    position:absolute;
    left:0;
    top:12px;
    bottom:12px;
    width:4px;
    border-radius:0 999px 999px 0;
    background:linear-gradient(180deg,#00675f,#14b8a6);
  }
  .ba-command-tabs button>i{
    width:34px!important;
    height:34px!important;
    border-radius:14px!important;
    background:rgba(241,245,249,.82)!important;
    border-color:rgba(226,232,240,.95)!important;
    color:#64748b!important;
  }
  .ba-command-tabs button.is-active>i{
    background:linear-gradient(135deg,#00675f,#00917f)!important;
    border-color:transparent!important;
    color:#ffffff!important;
    box-shadow:0 12px 24px rgba(0,103,95,.20)!important;
  }
  .ba-command-tabs button strong{
    font-size:13.5px!important;
    letter-spacing:-.025em!important;
  }
  .ba-command-tabs button em{
    max-width:100%;
    color:#6b7b91!important;
  }
  .ba-command-tabs button.is-active em{
    color:#00675f!important;
  }

  .ba-inventory-intel-panel{
    overflow:visible!important;
    max-height:none!important;
  }
  .ba-inventory-intel-card{
    position:relative;
    overflow:hidden;
    border-radius:26px!important;
    background:
      radial-gradient(circle at 100% 0%,rgba(20,184,166,.10),transparent 34%),
      linear-gradient(180deg,#ffffff 0%,#fbfefd 100%)!important;
    box-shadow:0 20px 46px rgba(15,23,42,.07)!important;
  }
  .ba-inventory-intel-card:after{
    content:"";
    position:absolute;
    right:-48px;
    bottom:-58px;
    width:126px;
    height:126px;
    border-radius:50%;
    background:rgba(0,103,95,.055);
    pointer-events:none;
  }
  .ba-inventory-intel-card>*{
    position:relative;
    z-index:1;
  }
  .ba-inventory-intel-card button{
    min-height:38px!important;
    border-color:rgba(0,103,95,.16)!important;
    background:rgba(255,255,255,.88)!important;
    box-shadow:0 10px 22px rgba(0,103,95,.06)!important;
  }

  @media(max-width:1180px){
    .ba-command-sidebar{
      position:relative!important;
      top:auto!important;
    }
  }



  /* LOOP PHASE 3A.2 — Clean reports header once business identity moved to sidebar */
  .ba-report-hero{
    grid-template-columns:minmax(0,1fr) minmax(420px,.92fr)!important;
    align-items:center!important;
    gap:18px!important;
    padding:16px 18px!important;
    border-radius:24px!important;
    background:linear-gradient(180deg,#ffffff 0%,#fbfffe 100%)!important;
    border-color:rgba(226,232,240,.95)!important;
    box-shadow:0 14px 34px rgba(15,23,42,.055)!important;
  }
  .ba-report-hero:after{display:none!important;}
  .ba-report-hero-main{
    gap:12px!important;
    align-content:center!important;
    min-height:auto!important;
  }
  .ba-report-hero .ba-business-identity,
  .ba-report-hero .ba-business-avatar,
  .ba-report-hero .ba-eyebrow{
    display:none!important;
  }
  .ba-report-heading h2{
    margin:0!important;
    color:#07131f!important;
    font-size:clamp(27px,3vw,40px)!important;
    line-height:1.02!important;
    letter-spacing:-.062em!important;
    font-weight:950!important;
  }
  .ba-report-heading p{
    max-width:760px!important;
    margin:8px 0 0!important;
    color:#475569!important;
    font-size:14px!important;
    line-height:1.45!important;
    font-weight:720!important;
  }
  .ba-report-context{
    gap:7px!important;
  }
  .ba-report-context span{
    min-height:26px!important;
    background:#f8fafc!important;
    border-color:rgba(226,232,240,.92)!important;
    box-shadow:none!important;
    color:#475569!important;
    font-size:11.5px!important;
  }
  .ba-report-hero-controls{
    border-radius:20px!important;
    padding:12px!important;
    background:rgba(255,255,255,.86)!important;
    box-shadow:none!important;
  }
  .ba-command-workspace{
    margin-top:16px!important;
  }
  @media(max-width:980px){
    .ba-report-hero{
      grid-template-columns:1fr!important;
      border-radius:22px!important;
      padding:14px!important;
    }
    .ba-report-heading h2{
      font-size:30px!important;
    }
  }

  /* LOOP PHASE 3A.3 — ultra minimal filters header, no report title block */
  .business-analytics.analytics-minimal{
    padding-top:10px!important;
  }
  .ba-report-filterbar{
    width:100%;
    position:relative;
    z-index:8;
    margin:0 0 10px!important;
    border:1px solid rgba(226,232,240,.92)!important;
    border-radius:22px!important;
    background:rgba(255,255,255,.90)!important;
    box-shadow:0 10px 28px rgba(15,23,42,.055)!important;
    backdrop-filter:blur(16px);
    -webkit-backdrop-filter:blur(16px);
    padding:10px 12px!important;
  }
  .ba-report-filterbar:before{
    content:"";
    position:absolute;
    inset:0;
    border-radius:inherit;
    pointer-events:none;
    background:
      radial-gradient(circle at 8% 0%,rgba(20,184,166,.08),transparent 26%),
      radial-gradient(circle at 100% 0%,rgba(0,103,95,.055),transparent 28%);
  }
  .ba-report-filterbar-main,
  .ba-custom-range-premium{
    position:relative;
    z-index:1;
  }
  .ba-report-filterbar-main{
    display:grid!important;
    grid-template-columns:minmax(340px,.9fr) minmax(250px,.55fr) minmax(320px,1fr)!important;
    gap:10px!important;
    align-items:center!important;
  }
  .ba-report-filterbar .ba-friendly-periods{
    min-height:38px!important;
    display:grid!important;
    grid-template-columns:repeat(5,minmax(0,1fr))!important;
    gap:4px!important;
    padding:4px!important;
    border:1px solid rgba(226,232,240,.9)!important;
    border-radius:18px!important;
    background:rgba(248,250,252,.92)!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.95)!important;
  }
  .ba-report-filterbar .ba-friendly-periods button,
  .ba-report-filterbar .ba-worker-pills button,
  .ba-report-filterbar .ba-date-control button{
    min-height:30px!important;
    border-radius:14px!important;
    font-size:11.5px!important;
    font-weight:950!important;
    letter-spacing:-.01em!important;
    box-shadow:none!important;
  }
  .ba-report-filterbar .ba-friendly-periods button{
    border:0!important;
    background:transparent!important;
    color:#64748b!important;
    padding:0 10px!important;
  }
  .ba-report-filterbar .ba-friendly-periods button.active{
    background:#ffffff!important;
    color:#00675f!important;
    box-shadow:0 8px 18px rgba(15,23,42,.055)!important;
  }
  .ba-date-control-premium{
    height:38px!important;
    min-width:0!important;
    border:1px solid rgba(226,232,240,.95)!important;
    border-radius:18px!important;
    background:#ffffff!important;
    box-shadow:0 8px 18px rgba(15,23,42,.035)!important;
    display:grid!important;
    grid-template-columns:32px minmax(0,1fr) 32px!important;
    align-items:center!important;
    padding:3px!important;
  }
  .ba-date-control-premium strong{
    min-width:0!important;
    color:#0f172a!important;
    font-size:12px!important;
    font-weight:950!important;
    text-align:center!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }
  .ba-date-control-premium button{
    border:0!important;
    background:#f8fafc!important;
    color:#334155!important;
    padding:0!important;
  }
  .ba-worker-pills-premium{
    min-height:38px!important;
    display:flex!important;
    align-items:center!important;
    gap:5px!important;
    overflow-x:auto!important;
    overflow-y:hidden!important;
    -webkit-overflow-scrolling:touch!important;
    padding:3px!important;
    border:1px solid rgba(226,232,240,.9)!important;
    border-radius:18px!important;
    background:rgba(255,255,255,.86)!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.95)!important;
    scrollbar-width:none;
  }
  .ba-worker-pills-premium::-webkit-scrollbar{display:none;}
  .ba-worker-pills-premium button{
    flex:0 0 auto!important;
    border:0!important;
    background:transparent!important;
    color:#64748b!important;
    padding:0 12px!important;
    white-space:nowrap!important;
  }
  .ba-worker-pills-premium button.active{
    background:linear-gradient(135deg,#00675f,#008b7d)!important;
    color:#ffffff!important;
    box-shadow:0 10px 20px rgba(0,103,95,.18)!important;
  }
  .ba-custom-range-premium{
    margin-top:8px!important;
    justify-content:flex-end!important;
  }
  .ba-command-workspace{
    margin-top:10px!important;
  }
  .ba-command-sidebar{
    top:74px!important;
  }
  .ba-phase2-strip{
    margin-top:0!important;
  }
  @media(max-width:1180px){
    .ba-report-filterbar-main{
      grid-template-columns:1fr!important;
    }
    .ba-report-filterbar .ba-friendly-periods,
    .ba-date-control-premium,
    .ba-worker-pills-premium{
      width:100%!important;
    }
  }
  @media(max-width:640px){
    .business-analytics.analytics-minimal{
      padding-top:8px!important;
    }
    .ba-report-filterbar{
      border-radius:20px!important;
      padding:9px!important;
    }
    .ba-report-filterbar .ba-friendly-periods{
      display:flex!important;
      overflow-x:auto!important;
      scrollbar-width:none;
    }
    .ba-report-filterbar .ba-friendly-periods::-webkit-scrollbar{display:none;}
    .ba-report-filterbar .ba-friendly-periods button{
      flex:0 0 auto!important;
      min-width:76px!important;
    }
    .ba-date-control-premium strong{
      font-size:11.5px!important;
    }
  }


  /* LOOP PHASE 3A.4 — module dropdown header + full-width report canvas */
  .ba-report-filterbar{
    margin-bottom:12px!important;
    padding:9px 10px!important;
    border-radius:20px!important;
  }
  .ba-report-filterbar-main.ba-report-filterbar-with-module{
    grid-template-columns:minmax(180px,230px) minmax(320px,.82fr) minmax(230px,.52fr) minmax(300px,1fr)!important;
    gap:9px!important;
    align-items:center!important;
  }
  .ba-report-module-select{
    min-height:38px!important;
    display:grid!important;
    grid-template-columns:1fr!important;
    gap:2px!important;
    padding:5px 8px 5px 10px!important;
    border:1px solid rgba(226,232,240,.92)!important;
    border-radius:18px!important;
    background:linear-gradient(180deg,#ffffff 0%,#f8fffd 100%)!important;
    box-shadow:0 8px 18px rgba(15,23,42,.035)!important;
    position:relative!important;
    overflow:hidden!important;
  }
  .ba-report-module-select:before{
    content:"";
    position:absolute;
    inset:0 auto 0 0;
    width:3px;
    background:linear-gradient(180deg,#00675f,#14b8a6);
    border-radius:999px;
  }
  .ba-report-module-select span{
    display:block!important;
    color:#00675f!important;
    font-size:8.5px!important;
    font-weight:950!important;
    line-height:1!important;
    text-transform:uppercase!important;
    letter-spacing:.13em!important;
  }
  .ba-report-module-select label{
    min-width:0!important;
    height:20px!important;
    display:flex!important;
    align-items:center!important;
    gap:6px!important;
    position:relative!important;
    margin:0!important;
  }
  .ba-report-module-select select{
    width:100%!important;
    min-width:0!important;
    height:22px!important;
    border:0!important;
    appearance:none!important;
    -webkit-appearance:none!important;
    background:transparent!important;
    color:#0f172a!important;
    font-size:15px!important;
    font-weight:950!important;
    line-height:1!important;
    letter-spacing:-.035em!important;
    padding:0 22px 0 0!important;
    outline:none!important;
    cursor:pointer!important;
  }
  .ba-report-module-select i{
    position:absolute!important;
    right:0!important;
    top:50%!important;
    transform:translateY(-50%)!important;
    width:18px!important;
    height:18px!important;
    display:grid!important;
    place-items:center!important;
    border-radius:999px!important;
    background:rgba(0,103,95,.07)!important;
    color:#00675f!important;
    font-style:normal!important;
    font-size:12px!important;
    font-weight:950!important;
    pointer-events:none!important;
  }
  .ba-report-module-select em{
    display:block!important;
    min-width:0!important;
    color:#64748b!important;
    font-size:10.5px!important;
    font-weight:850!important;
    font-style:normal!important;
    line-height:1.1!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }
  .ba-command-workspace{
    display:block!important;
    grid-template-columns:1fr!important;
    margin-top:12px!important;
  }
  .ba-command-sidebar{
    display:none!important;
  }
  .ba-command-main{
    width:100%!important;
    min-width:0!important;
    display:grid!important;
    gap:14px!important;
  }
  .ba-phase2-strip{
    margin-top:0!important;
  }
  @media(max-width:1280px){
    .ba-report-filterbar-main.ba-report-filterbar-with-module{
      grid-template-columns:minmax(180px,230px) minmax(300px,1fr) minmax(220px,.7fr)!important;
    }
    .ba-worker-pills-premium{
      grid-column:1 / -1!important;
    }
  }
  @media(max-width:860px){
    .ba-report-filterbar-main.ba-report-filterbar-with-module{
      grid-template-columns:1fr!important;
    }
    .ba-report-module-select,
    .ba-report-filterbar .ba-friendly-periods,
    .ba-date-control-premium,
    .ba-worker-pills-premium{
      width:100%!important;
    }
  }

  /* LOOP PHASE 3B — Executive Summary Premium */
  .ba-overview-hero-premium{
    display:grid!important;
    grid-template-columns:minmax(0,1.65fr) minmax(280px,.72fr)!important;
    gap:14px!important;
    margin:0 0 14px!important;
  }
  .ba-exec-radar,
  .ba-exec-next-action{
    border:1px solid rgba(226,232,240,.96)!important;
    background:#ffffff!important;
    border-radius:24px!important;
    box-shadow:0 18px 45px rgba(15,23,42,.07)!important;
    overflow:hidden!important;
  }
  .ba-exec-radar{
    display:grid!important;
    grid-template-columns:minmax(0,1fr) auto!important;
    align-items:stretch!important;
    gap:18px!important;
    padding:18px!important;
    position:relative!important;
  }
  .ba-exec-radar:before{
    content:""!important;
    position:absolute!important;
    inset:0!important;
    background:radial-gradient(circle at 95% 0%, rgba(20,184,166,.18), transparent 34%), linear-gradient(135deg, rgba(0,103,95,.06), transparent 52%)!important;
    pointer-events:none!important;
  }
  .ba-exec-radar.warning:before{background:radial-gradient(circle at 95% 0%, rgba(245,158,11,.22), transparent 34%), linear-gradient(135deg, rgba(0,103,95,.05), transparent 52%)!important;}
  .ba-exec-radar.success:before{background:radial-gradient(circle at 95% 0%, rgba(16,185,129,.20), transparent 34%), linear-gradient(135deg, rgba(0,103,95,.05), transparent 52%)!important;}
  .ba-exec-radar.danger:before{background:radial-gradient(circle at 95% 0%, rgba(239,68,68,.18), transparent 34%), linear-gradient(135deg, rgba(0,103,95,.05), transparent 52%)!important;}
  .ba-exec-radar-copy,
  .ba-exec-radar-kpis{position:relative!important;z-index:1!important;}
  .ba-exec-radar-copy span,
  .ba-exec-next-action>span{
    display:block!important;
    color:#00675f!important;
    font-size:11px!important;
    font-weight:950!important;
    letter-spacing:.13em!important;
    text-transform:uppercase!important;
  }
  .ba-exec-radar-copy h3,
  .ba-exec-next-action h3{
    margin:5px 0 7px!important;
    color:#0f172a!important;
    font-size:clamp(23px,2.3vw,34px)!important;
    line-height:1.02!important;
    letter-spacing:-.065em!important;
    font-weight:950!important;
  }
  .ba-exec-radar-copy p,
  .ba-exec-next-action p{
    margin:0!important;
    color:#64748b!important;
    font-size:13px!important;
    line-height:1.45!important;
    font-weight:780!important;
    max-width:760px!important;
  }
  .ba-exec-radar-kpis{
    min-width:360px!important;
    display:grid!important;
    grid-template-columns:repeat(3,minmax(0,1fr))!important;
    gap:10px!important;
    align-content:center!important;
  }
  .ba-exec-radar-kpis div{
    border:1px solid rgba(226,232,240,.88)!important;
    background:rgba(255,255,255,.82)!important;
    backdrop-filter:blur(12px)!important;
    border-radius:18px!important;
    padding:13px!important;
    display:grid!important;
    gap:5px!important;
  }
  .ba-exec-radar-kpis span{
    color:#64748b!important;
    font-size:10.5px!important;
    font-weight:950!important;
    letter-spacing:.09em!important;
    text-transform:uppercase!important;
  }
  .ba-exec-radar-kpis strong{
    color:#0f172a!important;
    font-size:clamp(18px,1.45vw,25px)!important;
    line-height:1!important;
    letter-spacing:-.055em!important;
    font-weight:950!important;
  }
  .ba-exec-next-action{
    padding:18px!important;
    display:flex!important;
    flex-direction:column!important;
    justify-content:space-between!important;
    gap:12px!important;
  }
  .ba-exec-next-action h3{font-size:22px!important;letter-spacing:-.05em!important;}
  .ba-exec-next-action button{
    width:max-content!important;
    border:0!important;
    border-radius:999px!important;
    background:#00675f!important;
    color:#fff!important;
    min-height:39px!important;
    padding:0 15px!important;
    font-size:12px!important;
    font-weight:950!important;
    box-shadow:0 14px 28px rgba(0,103,95,.18)!important;
    cursor:pointer!important;
  }
  .ba-overview-kpis{margin-top:0!important;}
  .ba-overview-action-grid{
    display:grid!important;
    grid-template-columns:minmax(0,1.2fr) minmax(360px,.82fr)!important;
    gap:14px!important;
    margin:0 0 14px!important;
  }
  .ba-priority-panel,
  .ba-opportunity-panel{min-height:100%!important;}
  .ba-priority-list{display:grid!important;gap:9px!important;}
  .ba-priority-item{
    width:100%!important;
    border:1px solid #e2e8f0!important;
    background:#fff!important;
    border-radius:17px!important;
    padding:11px!important;
    display:grid!important;
    grid-template-columns:auto minmax(0,1fr) auto!important;
    align-items:center!important;
    gap:11px!important;
    text-align:left!important;
    cursor:pointer!important;
    transition:transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease!important;
  }
  .ba-priority-item:hover{transform:translateY(-1px)!important;box-shadow:0 14px 28px rgba(15,23,42,.08)!important;border-color:rgba(0,103,95,.24)!important;}
  .ba-priority-item b{
    width:34px!important;
    height:34px!important;
    border-radius:999px!important;
    display:grid!important;
    place-items:center!important;
    background:#f1f5f9!important;
    color:#64748b!important;
    font-size:11px!important;
    font-weight:950!important;
  }
  .ba-priority-item span{min-width:0!important;display:grid!important;gap:3px!important;}
  .ba-priority-item strong{color:#0f172a!important;font-size:13.5px!important;font-weight:950!important;line-height:1.08!important;}
  .ba-priority-item em{color:#64748b!important;font-size:12px!important;font-weight:760!important;line-height:1.32!important;font-style:normal!important;}
  .ba-priority-item i{font-style:normal!important;color:#00675f!important;font-size:12px!important;font-weight:950!important;white-space:nowrap!important;}
  .ba-priority-item.warning b{background:#fffbeb!important;color:#b45309!important;}
  .ba-priority-item.danger b{background:#fef2f2!important;color:#dc2626!important;}
  .ba-priority-item.success b{background:#ecfdf5!important;color:#047857!important;}
  .ba-priority-item.info b{background:#eff6ff!important;color:#2563eb!important;}
  .ba-opportunity-grid{display:grid!important;grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:10px!important;}
  .ba-opportunity-grid button{
    border:1px solid #e2e8f0!important;
    background:linear-gradient(180deg,#ffffff,#f8fafc)!important;
    border-radius:18px!important;
    padding:13px!important;
    text-align:left!important;
    display:grid!important;
    gap:4px!important;
    min-height:118px!important;
    cursor:pointer!important;
  }
  .ba-opportunity-grid button span{color:#00675f!important;font-size:10px!important;font-weight:950!important;letter-spacing:.1em!important;text-transform:uppercase!important;}
  .ba-opportunity-grid button strong{color:#0f172a!important;font-size:17px!important;font-weight:950!important;line-height:1.05!important;letter-spacing:-.045em!important;}
  .ba-opportunity-grid button em{color:#00675f!important;font-size:13px!important;font-weight:950!important;font-style:normal!important;}
  .ba-opportunity-grid button small{color:#64748b!important;font-size:11.5px!important;font-weight:760!important;line-height:1.3!important;}
  .ba-overview-secondary{margin-top:0!important;}
  @media(max-width:1180px){
    .ba-overview-hero-premium,
    .ba-overview-action-grid{grid-template-columns:1fr!important;}
    .ba-exec-radar{grid-template-columns:1fr!important;}
    .ba-exec-radar-kpis{min-width:0!important;}
  }
  @media(max-width:720px){
    .ba-exec-radar-kpis,
    .ba-opportunity-grid{grid-template-columns:1fr!important;}
    .ba-priority-item{grid-template-columns:auto minmax(0,1fr)!important;}
    .ba-priority-item i{grid-column:2!important;}
  }



  /* LOOP PHASE 3B.1 — Premium Clean Summary polish */
  .business-analytics.analytics-minimal{
    --loop-cream:#fffdf8;
    --loop-card:#ffffff;
    --loop-card-soft:#fbfdfa;
    --loop-border:rgba(15,23,42,.075);
    --loop-border-strong:rgba(0,103,95,.16);
    --loop-shadow-soft:0 10px 26px rgba(15,23,42,.045);
    --loop-shadow-hover:0 18px 42px rgba(15,23,42,.075);
    background:linear-gradient(180deg,#fbfdfb 0%,#ffffff 46%,#fbfaf6 100%)!important;
    padding-top:14px!important;
  }
  .ba-report-filterbar{
    border:1px solid rgba(226,232,240,.92)!important;
    border-radius:24px!important;
    background:rgba(255,255,255,.82)!important;
    box-shadow:0 12px 34px rgba(15,23,42,.045)!important;
    backdrop-filter:blur(14px)!important;
    -webkit-backdrop-filter:blur(14px)!important;
    padding:11px 14px!important;
  }
  .ba-report-module-select,
  .ba-report-filterbar .ba-friendly-periods,
  .ba-date-control-premium,
  .ba-worker-pills-premium{
    box-shadow:none!important;
    border-color:rgba(226,232,240,.94)!important;
    background:#fff!important;
  }
  .ba-command-main{gap:13px!important;}
  .ba-quality-strip,
  .ba-phase2-strip{
    display:none!important;
  }
  .ba-loop-summary-focus{
    position:relative!important;
    overflow:hidden!important;
    display:grid!important;
    grid-template-columns:minmax(0,1.15fr) minmax(280px,.72fr) minmax(280px,.78fr)!important;
    gap:14px!important;
    align-items:stretch!important;
    min-height:162px!important;
    border:1px solid var(--loop-border-strong)!important;
    border-radius:28px!important;
    background:
      radial-gradient(circle at 96% 8%,rgba(20,184,166,.13),transparent 34%),
      linear-gradient(135deg,#ffffff 0%,#fffdf8 48%,#f0fdfa 100%)!important;
    box-shadow:var(--loop-shadow-soft)!important;
    padding:18px!important;
    margin:0!important;
  }
  .ba-loop-summary-focus.warning{
    border-color:rgba(245,158,11,.22)!important;
    background:radial-gradient(circle at 96% 8%,rgba(245,158,11,.12),transparent 34%),linear-gradient(135deg,#ffffff 0%,#fffdf8 50%,#fffbeb 100%)!important;
  }
  .ba-loop-summary-focus.danger{
    border-color:rgba(239,68,68,.18)!important;
    background:radial-gradient(circle at 96% 8%,rgba(239,68,68,.10),transparent 34%),linear-gradient(135deg,#ffffff 0%,#fffdf8 50%,#fef2f2 100%)!important;
  }
  .ba-loop-summary-focus.success{
    border-color:rgba(16,185,129,.18)!important;
    background:radial-gradient(circle at 96% 8%,rgba(16,185,129,.12),transparent 34%),linear-gradient(135deg,#ffffff 0%,#fffdf8 50%,#ecfdf5 100%)!important;
  }
  .ba-loop-summary-focus-copy{
    display:flex!important;
    flex-direction:column!important;
    justify-content:center!important;
    min-width:0!important;
  }
  .ba-loop-summary-focus-copy span,
  .ba-loop-summary-action-card span,
  .ba-loop-panel-title span{
    color:#00675f!important;
    font-size:10.5px!important;
    line-height:1!important;
    font-weight:950!important;
    letter-spacing:.15em!important;
    text-transform:uppercase!important;
  }
  .ba-loop-summary-focus-copy h3{
    margin:7px 0 7px!important;
    max-width:820px!important;
    color:#0f172a!important;
    font-size:clamp(29px,3.4vw,48px)!important;
    line-height:.95!important;
    letter-spacing:-.075em!important;
    font-weight:950!important;
  }
  .ba-loop-summary-focus-copy p{
    margin:0!important;
    max-width:720px!important;
    color:#667085!important;
    font-size:13px!important;
    line-height:1.45!important;
    font-weight:760!important;
  }
  .ba-loop-summary-action-card{
    min-width:0!important;
    border:1px solid rgba(226,232,240,.86)!important;
    border-radius:22px!important;
    background:rgba(255,255,255,.78)!important;
    padding:15px!important;
    display:grid!important;
    align-content:center!important;
    gap:8px!important;
  }
  .ba-loop-summary-action-card strong{
    color:#0f172a!important;
    font-size:19px!important;
    line-height:1.05!important;
    letter-spacing:-.05em!important;
    font-weight:950!important;
  }
  .ba-loop-summary-action-card em{
    color:#64748b!important;
    font-size:12.2px!important;
    line-height:1.35!important;
    font-style:normal!important;
    font-weight:760!important;
  }
  .ba-loop-summary-action-card button,
  .ba-loop-panel-title button{
    justify-self:start!important;
    border:0!important;
    border-radius:999px!important;
    background:#00675f!important;
    color:#fff!important;
    min-height:34px!important;
    padding:0 13px!important;
    font-size:11.5px!important;
    font-weight:950!important;
    box-shadow:0 12px 24px rgba(0,103,95,.16)!important;
    cursor:pointer!important;
  }
  .ba-loop-summary-numbers{
    min-width:0!important;
    display:grid!important;
    grid-template-columns:1fr!important;
    gap:9px!important;
  }
  .ba-loop-summary-numbers article{
    border:1px solid rgba(226,232,240,.82)!important;
    border-radius:18px!important;
    background:rgba(255,255,255,.72)!important;
    padding:12px 13px!important;
    display:flex!important;
    align-items:center!important;
    justify-content:space-between!important;
    gap:14px!important;
  }
  .ba-loop-summary-numbers span{
    color:#64748b!important;
    font-size:10.2px!important;
    font-weight:950!important;
    letter-spacing:.09em!important;
    text-transform:uppercase!important;
  }
  .ba-loop-summary-numbers strong{
    color:#0f172a!important;
    font-size:21px!important;
    line-height:1!important;
    letter-spacing:-.055em!important;
    font-weight:950!important;
    white-space:nowrap!important;
  }
  .ba-loop-kpi-strip{
    grid-template-columns:repeat(6,minmax(0,1fr))!important;
    gap:11px!important;
    margin:2px 0!important;
  }
  .ba-loop-kpi-strip .ba-metric{
    min-height:98px!important;
    border-color:var(--loop-border)!important;
    border-radius:22px!important;
    box-shadow:var(--loop-shadow-soft)!important;
    padding:14px!important;
    background:#fff!important;
  }
  .ba-loop-kpi-strip .ba-metric.primary{
    background:linear-gradient(135deg,#004f48 0%,#00675f 100%)!important;
    border-color:#00675f!important;
  }
  .ba-loop-kpi-strip .ba-metric.success{
    background:linear-gradient(135deg,#ffffff,#f0fdfa)!important;
    border-color:rgba(20,184,166,.35)!important;
  }
  .ba-loop-kpi-strip .ba-metric.warning{
    background:linear-gradient(135deg,#ffffff,#fffbeb)!important;
    border-color:rgba(245,158,11,.28)!important;
  }
  .ba-loop-kpi-strip .ba-metric strong{
    font-size:clamp(22px,1.9vw,30px)!important;
  }
  .ba-loop-decision-grid{
    display:grid!important;
    grid-template-columns:minmax(0,1.3fr) minmax(330px,.8fr)!important;
    gap:14px!important;
    margin:0!important;
  }
  .ba-loop-priority-panel,
  .ba-loop-opportunity-panel,
  .ba-overview-secondary .ba-panel{
    border-color:var(--loop-border)!important;
    border-radius:24px!important;
    box-shadow:var(--loop-shadow-soft)!important;
    background:#fff!important;
  }
  .ba-loop-panel-title{
    display:flex!important;
    align-items:flex-start!important;
    justify-content:space-between!important;
    gap:14px!important;
    margin-bottom:12px!important;
  }
  .ba-loop-panel-title h3{
    margin:5px 0 0!important;
    color:#0f172a!important;
    font-size:20px!important;
    line-height:1!important;
    letter-spacing:-.055em!important;
    font-weight:950!important;
  }
  .ba-loop-panel-title button{
    min-height:32px!important;
    background:#effaf7!important;
    color:#00675f!important;
    box-shadow:none!important;
  }
  .ba-loop-priority-list{display:grid!important;gap:8px!important;}
  .ba-loop-priority-row{
    width:100%!important;
    min-height:58px!important;
    border:1px solid rgba(226,232,240,.88)!important;
    border-radius:18px!important;
    background:#fff!important;
    padding:10px 11px!important;
    display:grid!important;
    grid-template-columns:auto minmax(0,1fr) auto!important;
    align-items:center!important;
    gap:11px!important;
    text-align:left!important;
    cursor:pointer!important;
    transition:transform 140ms ease, border-color 140ms ease, box-shadow 140ms ease!important;
  }
  .ba-loop-priority-row:hover{
    transform:translateY(-1px)!important;
    border-color:rgba(0,103,95,.22)!important;
    box-shadow:var(--loop-shadow-hover)!important;
  }
  .ba-loop-priority-row b{
    width:31px!important;
    height:31px!important;
    border-radius:999px!important;
    display:grid!important;
    place-items:center!important;
    background:#f8fafc!important;
    color:#64748b!important;
    font-size:10.5px!important;
    font-weight:950!important;
  }
  .ba-loop-priority-row span{display:grid!important;gap:2px!important;min-width:0!important;}
  .ba-loop-priority-row strong{color:#0f172a!important;font-size:13.5px!important;line-height:1.1!important;font-weight:950!important;}
  .ba-loop-priority-row em{color:#64748b!important;font-size:11.5px!important;line-height:1.28!important;font-style:normal!important;font-weight:760!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;}
  .ba-loop-priority-row i{color:#00675f!important;font-size:12px!important;font-style:normal!important;font-weight:950!important;white-space:nowrap!important;}
  .ba-loop-priority-row.warning b{background:#fff7ed!important;color:#b45309!important;}
  .ba-loop-priority-row.danger b{background:#fef2f2!important;color:#dc2626!important;}
  .ba-loop-priority-row.success b{background:#ecfdf5!important;color:#047857!important;}
  .ba-loop-priority-row.info b{background:#eff6ff!important;color:#2563eb!important;}
  .ba-loop-opportunity-list{display:grid!important;grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:9px!important;}
  .ba-loop-opportunity-list button{
    min-height:104px!important;
    border:1px solid rgba(226,232,240,.88)!important;
    border-radius:19px!important;
    background:linear-gradient(180deg,#ffffff 0%,#fbfdfa 100%)!important;
    padding:12px!important;
    text-align:left!important;
    display:grid!important;
    align-content:start!important;
    gap:4px!important;
    cursor:pointer!important;
    transition:transform 140ms ease, border-color 140ms ease, box-shadow 140ms ease!important;
  }
  .ba-loop-opportunity-list button:hover{
    transform:translateY(-1px)!important;
    border-color:rgba(0,103,95,.20)!important;
    box-shadow:var(--loop-shadow-hover)!important;
  }
  .ba-loop-opportunity-list span{color:#00675f!important;font-size:9.8px!important;font-weight:950!important;letter-spacing:.11em!important;text-transform:uppercase!important;}
  .ba-loop-opportunity-list strong{color:#0f172a!important;font-size:15.5px!important;line-height:1.05!important;letter-spacing:-.04em!important;font-weight:950!important;}
  .ba-loop-opportunity-list em{color:#00675f!important;font-size:12.5px!important;font-style:normal!important;font-weight:950!important;}
  .ba-loop-opportunity-list small{color:#64748b!important;font-size:11px!important;line-height:1.25!important;font-weight:760!important;}
  .ba-overview-secondary{gap:14px!important;margin-top:0!important;}
  .ba-overview-secondary .ba-panel-head h3{font-size:18px!important;}
  .ba-trend{min-height:210px!important;}
  @media(max-width:1320px){
    .ba-loop-summary-focus{grid-template-columns:1fr minmax(260px,.8fr)!important;}
    .ba-loop-summary-numbers{grid-column:1 / -1!important;grid-template-columns:repeat(3,minmax(0,1fr))!important;}
    .ba-loop-kpi-strip{grid-template-columns:repeat(3,minmax(0,1fr))!important;}
  }
  @media(max-width:980px){
    .ba-loop-summary-focus,
    .ba-loop-decision-grid{grid-template-columns:1fr!important;}
    .ba-loop-summary-numbers{grid-template-columns:1fr!important;}
    .ba-loop-kpi-strip{grid-template-columns:repeat(2,minmax(0,1fr))!important;}
    .ba-loop-opportunity-list{grid-template-columns:1fr!important;}
    .ba-loop-priority-row em{white-space:normal!important;}
  }
  @media(max-width:560px){
    .business-analytics.analytics-minimal{padding-left:10px!important;padding-right:10px!important;}
    .ba-loop-summary-focus{padding:14px!important;border-radius:22px!important;}
    .ba-loop-summary-focus-copy h3{font-size:28px!important;}
    .ba-loop-kpi-strip{grid-template-columns:1fr 1fr!important;}
  }


  /* LOOP PHASE 3C — Finanzas clean operativo */
  .ba-finance-operational{
    display:grid!important;
    gap:18px!important;
  }
  .ba-finance-operational-head{
    display:flex!important;
    align-items:flex-start!important;
    justify-content:space-between!important;
    gap:18px!important;
    padding:2px 0 0!important;
    margin:0!important;
  }
  .ba-finance-operational-head span{
    display:block!important;
    color:#00675f!important;
    font-size:11px!important;
    font-weight:950!important;
    letter-spacing:.14em!important;
    text-transform:uppercase!important;
    margin-bottom:8px!important;
  }
  .ba-finance-operational-head h3{
    margin:0!important;
    color:#0f172a!important;
    font-size:30px!important;
    line-height:1!important;
    letter-spacing:-.06em!important;
    font-weight:950!important;
  }
  .ba-finance-operational-head p{
    margin:8px 0 0!important;
    color:#64748b!important;
    font-size:14px!important;
    line-height:1.45!important;
    font-weight:720!important;
  }
  .ba-finance-customize-btn{
    min-width:154px!important;
    height:44px!important;
    border-radius:999px!important;
    align-self:center!important;
    background:#fff!important;
  }
  .ba-finance-kpi-strip{
    display:grid!important;
    grid-template-columns:repeat(5,minmax(0,1fr))!important;
    gap:14px!important;
  }
  .ba-finance-kpi{
    min-height:158px!important;
    border:1px solid rgba(226,232,240,.92)!important;
    border-radius:24px!important;
    background:linear-gradient(180deg,#ffffff 0%,#fbfdfa 100%)!important;
    box-shadow:0 18px 45px rgba(15,23,42,.055)!important;
    padding:21px!important;
    display:grid!important;
    grid-template-columns:auto minmax(0,1fr)!important;
    grid-template-rows:1fr auto!important;
    gap:13px 15px!important;
    position:relative!important;
    overflow:hidden!important;
  }
  .ba-finance-kpi:before{
    content:""!important;
    position:absolute!important;
    inset:auto -48px -66px auto!important;
    width:132px!important;
    height:132px!important;
    border-radius:999px!important;
    background:rgba(0,103,95,.055)!important;
    pointer-events:none!important;
  }
  .ba-finance-kpi.primary{
    background:linear-gradient(145deg,#00675f 0%,#034f4a 100%)!important;
    border-color:rgba(0,103,95,.45)!important;
    color:#fff!important;
  }
  .ba-finance-kpi.success{border-color:rgba(20,184,166,.35)!important;background:linear-gradient(180deg,#ffffff 0%,#effdf8 100%)!important;}
  .ba-finance-kpi.warning{border-color:rgba(245,158,11,.36)!important;background:linear-gradient(180deg,#ffffff 0%,#fffaf0 100%)!important;}
  .ba-finance-kpi.net{background:linear-gradient(180deg,#ffffff 0%,#f8fffc 100%)!important;}
  .ba-finance-kpi-icon{
    width:48px!important;
    height:48px!important;
    border-radius:999px!important;
    display:grid!important;
    place-items:center!important;
    background:#e7f8ef!important;
    color:#00675f!important;
    font-size:18px!important;
    font-weight:950!important;
    box-shadow:inset 0 0 0 1px rgba(0,103,95,.08)!important;
    grid-row:1 / 2!important;
  }
  .ba-finance-kpi.primary .ba-finance-kpi-icon{background:rgba(255,255,255,.18)!important;color:#fff!important;}
  .ba-finance-kpi.warning .ba-finance-kpi-icon{background:#fbbf24!important;color:#fff!important;}
  .ba-finance-kpi.net .ba-finance-kpi-icon{background:linear-gradient(135deg,#00675f,#0f766e)!important;color:#fff!important;}
  .ba-finance-kpi div:nth-child(2){min-width:0!important;}
  .ba-finance-kpi span{
    display:block!important;
    color:#64748b!important;
    font-size:11px!important;
    font-weight:950!important;
    text-transform:uppercase!important;
    letter-spacing:.09em!important;
    margin-bottom:8px!important;
  }
  .ba-finance-kpi.primary span{color:rgba(255,255,255,.78)!important;}
  .ba-finance-kpi strong{
    display:block!important;
    color:#0f172a!important;
    font-size:30px!important;
    line-height:1!important;
    letter-spacing:-.06em!important;
    font-weight:950!important;
    white-space:nowrap!important;
  }
  .ba-finance-kpi.primary strong{color:#fff!important;}
  .ba-finance-kpi em{
    display:block!important;
    margin-top:8px!important;
    color:#64748b!important;
    font-size:12px!important;
    line-height:1.25!important;
    font-style:normal!important;
    font-weight:760!important;
  }
  .ba-finance-kpi.primary em{color:rgba(255,255,255,.83)!important;}
  .ba-finance-kpi button{
    grid-column:1 / -1!important;
    height:34px!important;
    justify-self:stretch!important;
    border:0!important;
    border-radius:999px!important;
    background:rgba(0,103,95,.055)!important;
    color:#00675f!important;
    padding:0 14px!important;
    font-size:12.5px!important;
    font-weight:920!important;
    text-align:left!important;
    cursor:pointer!important;
  }
  .ba-finance-kpi.primary button{background:rgba(255,255,255,.12)!important;color:#fff!important;}
  .ba-finance-kpi>i{
    grid-column:1 / -1!important;
    height:8px!important;
    border-radius:999px!important;
    background:rgba(0,103,95,.10)!important;
    overflow:hidden!important;
    align-self:end!important;
  }
  .ba-finance-kpi>i b{display:block!important;height:100%!important;border-radius:999px!important;background:#00675f!important;}
  .ba-finance-worker-panel{
    border-radius:25px!important;
    box-shadow:0 18px 45px rgba(15,23,42,.055)!important;
  }
  .ba-finance-section-head{
    align-items:center!important;
  }
  .ba-finance-section-head>div span,
  .ba-finance-compact-head span{
    color:#00675f!important;
    font-size:11px!important;
    font-weight:950!important;
    letter-spacing:.12em!important;
    text-transform:uppercase!important;
  }
  .ba-finance-section-head h3,
  .ba-finance-compact-head h3{
    margin-top:5px!important;
    color:#0f172a!important;
    letter-spacing:-.04em!important;
    font-weight:950!important;
  }
  .ba-finance-worker-table{
    border-radius:20px!important;
    overflow:hidden!important;
    border:1px solid rgba(226,232,240,.78)!important;
  }
  .ba-finance-worker-name{
    display:flex!important;
    align-items:center!important;
    gap:11px!important;
  }
  .ba-finance-worker-name i{
    width:34px!important;
    height:34px!important;
    border-radius:999px!important;
    display:grid!important;
    place-items:center!important;
    background:#f1faf7!important;
    color:#00675f!important;
    font-size:12px!important;
    font-style:normal!important;
    font-weight:950!important;
    box-shadow:inset 0 0 0 1px rgba(0,103,95,.10)!important;
  }
  .ba-finance-compact-grid{
    display:grid!important;
    grid-template-columns:repeat(3,minmax(0,1fr))!important;
    gap:14px!important;
  }
  .ba-finance-compact-card{
    border-radius:24px!important;
    box-shadow:0 18px 45px rgba(15,23,42,.055)!important;
    min-height:260px!important;
    padding:20px!important;
  }
  .ba-finance-compact-head{
    display:flex!important;
    justify-content:space-between!important;
    align-items:flex-start!important;
    gap:14px!important;
    margin-bottom:16px!important;
  }
  .ba-finance-compact-head button{
    height:34px!important;
    border:1px solid rgba(0,103,95,.14)!important;
    background:#fff!important;
    color:#00675f!important;
    border-radius:999px!important;
    padding:0 13px!important;
    font-size:12px!important;
    font-weight:920!important;
    cursor:pointer!important;
    white-space:nowrap!important;
  }
  .ba-finance-method-list,
  .ba-finance-commission-list,
  .ba-finance-pending-list{display:grid!important;gap:10px!important;}
  .ba-finance-method-row{
    display:grid!important;
    grid-template-columns:34px minmax(0,1fr) auto 42px!important;
    align-items:center!important;
    gap:11px!important;
    min-height:42px!important;
  }
  .ba-finance-method-row i,
  .ba-finance-pending-row i{
    width:31px!important;
    height:31px!important;
    border-radius:999px!important;
    display:grid!important;
    place-items:center!important;
    background:#f1faf7!important;
    color:#00675f!important;
    font-size:12px!important;
    font-style:normal!important;
    font-weight:950!important;
  }
  .ba-finance-method-row span{font-size:13px!important;color:#0f172a!important;font-weight:900!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;}
  .ba-finance-method-row strong{font-size:13px!important;color:#0f172a!important;font-weight:950!important;white-space:nowrap!important;}
  .ba-finance-method-row em{font-size:12px!important;color:#64748b!important;font-style:normal!important;font-weight:850!important;text-align:right!important;}
  .ba-finance-covered-note,
  .ba-finance-compact-total,
  .ba-finance-pending-total{
    margin-top:8px!important;
    padding-top:13px!important;
    border-top:1px solid rgba(226,232,240,.82)!important;
    display:flex!important;
    justify-content:space-between!important;
    align-items:center!important;
    gap:12px!important;
  }
  .ba-finance-covered-note span,
  .ba-finance-compact-total span,
  .ba-finance-pending-total span{font-size:13px!important;color:#64748b!important;font-weight:850!important;}
  .ba-finance-covered-note strong,
  .ba-finance-compact-total strong{font-size:14px!important;color:#0f172a!important;font-weight:950!important;}
  .ba-finance-commission-row{display:grid!important;gap:8px!important;}
  .ba-finance-commission-row div{display:flex!important;justify-content:space-between!important;gap:12px!important;}
  .ba-finance-commission-row strong{color:#0f172a!important;font-size:13.5px!important;font-weight:950!important;}
  .ba-finance-commission-row em{color:#00675f!important;font-size:13.5px!important;font-style:normal!important;font-weight:950!important;}
  .ba-finance-commission-row i{height:8px!important;border-radius:999px!important;background:rgba(0,103,95,.10)!important;overflow:hidden!important;}
  .ba-finance-commission-row i b{display:block!important;height:100%!important;border-radius:999px!important;background:linear-gradient(90deg,#00675f,#14b8a6)!important;}
  .ba-finance-pending-row{
    display:grid!important;
    grid-template-columns:36px minmax(0,1fr) auto!important;
    gap:11px!important;
    align-items:center!important;
    padding:7px 0!important;
  }
  .ba-finance-pending-row div{display:grid!important;gap:3px!important;min-width:0!important;}
  .ba-finance-pending-row strong{color:#0f172a!important;font-size:13.5px!important;font-weight:950!important;}
  .ba-finance-pending-row span{color:#64748b!important;font-size:12px!important;font-weight:760!important;}
  .ba-finance-pending-row em{color:#f97316!important;font-size:14px!important;font-style:normal!important;font-weight:950!important;white-space:nowrap!important;}
  .ba-finance-pending-total strong{color:#f97316!important;font-size:16px!important;font-weight:950!important;}
  .ba-finance-footer-note{
    min-height:58px!important;
    border:1px solid rgba(226,232,240,.82)!important;
    border-radius:20px!important;
    background:linear-gradient(180deg,#ffffff 0%,#fbfdfa 100%)!important;
    display:flex!important;
    align-items:center!important;
    gap:13px!important;
    padding:13px 18px!important;
    box-shadow:0 14px 34px rgba(15,23,42,.045)!important;
  }
  .ba-finance-footer-note span{
    width:34px!important;
    height:34px!important;
    border-radius:999px!important;
    background:#effaf7!important;
    color:#00675f!important;
    display:grid!important;
    place-items:center!important;
    font-weight:950!important;
  }
  .ba-finance-footer-note p{margin:0!important;color:#334155!important;font-size:13px!important;font-weight:760!important;line-height:1.35!important;flex:1!important;}
  .ba-finance-footer-note button{
    height:36px!important;
    border:0!important;
    border-radius:999px!important;
    padding:0 18px!important;
    background:#fff!important;
    color:#00675f!important;
    font-size:12.5px!important;
    font-weight:920!important;
    box-shadow:inset 0 0 0 1px rgba(0,103,95,.12)!important;
    cursor:pointer!important;
  }
  @media(max-width:1280px){
    .ba-finance-kpi-strip{grid-template-columns:repeat(3,minmax(0,1fr))!important;}
    .ba-finance-compact-grid{grid-template-columns:1fr!important;}
  }
  @media(max-width:820px){
    .ba-finance-operational-head{display:grid!important;}
    .ba-finance-customize-btn{justify-self:start!important;}
    .ba-finance-kpi-strip{grid-template-columns:1fr!important;}
    .ba-finance-kpi{min-height:132px!important;}
    .ba-finance-compact-head{display:grid!important;}
    .ba-finance-compact-head button{justify-self:start!important;}
    .ba-finance-footer-note{align-items:flex-start!important;display:grid!important;grid-template-columns:auto 1fr!important;}
    .ba-finance-footer-note button{grid-column:1 / -1!important;justify-self:start!important;}
  }


  /* LOOP PHASE 3C.1 — finance micro-polish premium */
  .ba-finance-operational{
    gap:14px!important;
  }
  .ba-finance-operational-head{
    align-items:center!important;
    gap:14px!important;
    padding:0!important;
    margin:0 0 2px!important;
  }
  .ba-finance-operational-head span{
    font-size:10.5px!important;
    letter-spacing:.16em!important;
    margin-bottom:6px!important;
  }
  .ba-finance-operational-head h3{
    font-size:clamp(23px,1.75vw,28px)!important;
    letter-spacing:-.055em!important;
  }
  .ba-finance-operational-head p{
    margin-top:6px!important;
    font-size:12.75px!important;
    line-height:1.35!important;
    max-width:760px!important;
  }
  .ba-finance-customize-btn{
    min-width:auto!important;
    height:36px!important;
    padding:0 18px!important;
    border-radius:999px!important;
    align-self:center!important;
    background:rgba(255,255,255,.86)!important;
    color:#00675f!important;
    box-shadow:inset 0 0 0 1px rgba(0,103,95,.13),0 10px 24px rgba(15,23,42,.04)!important;
    font-size:12.25px!important;
    font-weight:930!important;
  }
  .ba-finance-kpi-strip{
    gap:10px!important;
    align-items:stretch!important;
  }
  .ba-finance-kpi{
    min-height:122px!important;
    border-radius:20px!important;
    padding:14px 15px!important;
    grid-template-columns:34px minmax(0,1fr)!important;
    grid-template-rows:auto minmax(20px,auto) auto!important;
    gap:10px 12px!important;
    box-shadow:0 14px 32px rgba(15,23,42,.045)!important;
  }
  .ba-finance-kpi:before{
    width:92px!important;
    height:92px!important;
    inset:auto -42px -54px auto!important;
    background:rgba(0,103,95,.04)!important;
  }
  .ba-finance-kpi-icon{
    width:34px!important;
    height:34px!important;
    font-size:14px!important;
    grid-row:1 / span 2!important;
  }
  .ba-finance-kpi span{
    font-size:10px!important;
    letter-spacing:.105em!important;
    margin-bottom:5px!important;
  }
  .ba-finance-kpi strong{
    font-size:clamp(22px,1.55vw,27px)!important;
    letter-spacing:-.055em!important;
  }
  .ba-finance-kpi em{
    margin-top:6px!important;
    font-size:11.25px!important;
    line-height:1.22!important;
  }
  .ba-finance-kpi button{
    grid-column:2 / 3!important;
    justify-self:start!important;
    align-self:end!important;
    height:auto!important;
    min-height:0!important;
    padding:0!important;
    background:transparent!important;
    color:#00675f!important;
    font-size:12px!important;
    font-weight:950!important;
    text-align:left!important;
    box-shadow:none!important;
  }
  .ba-finance-kpi.primary button{
    background:transparent!important;
    color:rgba(255,255,255,.92)!important;
  }
  .ba-finance-kpi>i{
    grid-column:2 / 3!important;
    height:5px!important;
    align-self:end!important;
    margin-top:2px!important;
  }
  .ba-finance-worker-panel{
    margin-top:2px!important;
  }
  @media(max-width:1280px){
    .ba-finance-kpi-strip{grid-template-columns:repeat(3,minmax(0,1fr))!important;}
  }
  @media(max-width:820px){
    .ba-finance-operational-head{align-items:flex-start!important;}
    .ba-finance-customize-btn{justify-self:start!important;height:34px!important;}
    .ba-finance-kpi-strip{grid-template-columns:1fr 1fr!important;}
    .ba-finance-kpi{min-height:116px!important;}
  }
  @media(max-width:560px){
    .ba-finance-kpi-strip{grid-template-columns:1fr!important;}
  }


  /* LOOP PHASE 3C.2 — Premium module switcher + no loading card flash */
  .ba-skeleton-wrap{display:none!important;}
  .ba-report-filterbar-main.ba-report-filterbar-with-module{
    grid-template-columns:minmax(190px,245px) minmax(320px,.82fr) minmax(230px,.52fr) minmax(300px,1fr)!important;
  }
  .ba-report-module-switcher{
    position:relative!important;
    min-height:46px!important;
    padding:0!important;
    border:0!important;
    background:transparent!important;
    box-shadow:none!important;
    overflow:visible!important;
    z-index:30!important;
  }
  .ba-report-module-switcher:before{display:none!important;}
  .ba-module-trigger{
    width:100%!important;
    min-height:46px!important;
    border:1px solid rgba(204,231,226,.96)!important;
    border-radius:18px!important;
    background:linear-gradient(135deg,#ffffff 0%,#f8fffd 58%,#eefbf8 100%)!important;
    box-shadow:0 16px 34px rgba(15,23,42,.045), inset 0 1px 0 rgba(255,255,255,.88)!important;
    padding:8px 42px 8px 13px!important;
    display:grid!important;
    grid-template-columns:1fr!important;
    gap:2px!important;
    text-align:left!important;
    cursor:pointer!important;
    position:relative!important;
  }
  .ba-module-trigger:before{
    content:"";
    position:absolute;
    left:0;
    top:10px;
    bottom:10px;
    width:3px;
    border-radius:999px;
    background:linear-gradient(180deg,#00675f,#14b8a6);
  }
  .ba-module-trigger span{
    display:block!important;
    color:#00675f!important;
    font-size:8.5px!important;
    line-height:1!important;
    font-weight:950!important;
    letter-spacing:.14em!important;
    text-transform:uppercase!important;
  }
  .ba-module-trigger strong{
    color:#0f172a!important;
    font-size:15px!important;
    line-height:1.05!important;
    font-weight:950!important;
    letter-spacing:-.04em!important;
  }
  .ba-module-trigger em{
    min-width:0!important;
    color:#64748b!important;
    font-size:10.5px!important;
    line-height:1.1!important;
    font-weight:850!important;
    font-style:normal!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }
  .ba-module-trigger i{
    position:absolute!important;
    right:12px!important;
    top:50%!important;
    transform:translateY(-50%) rotate(0deg)!important;
    width:25px!important;
    height:25px!important;
    border-radius:999px!important;
    display:grid!important;
    place-items:center!important;
    background:rgba(0,103,95,.07)!important;
    border:1px solid rgba(0,103,95,.09)!important;
    color:#00675f!important;
    font-size:13px!important;
    font-weight:950!important;
    font-style:normal!important;
    transition:transform .18s ease, background .18s ease!important;
  }
  .ba-report-module-switcher.open .ba-module-trigger{
    border-color:rgba(20,184,166,.45)!important;
    box-shadow:0 18px 42px rgba(0,103,95,.10), inset 0 1px 0 rgba(255,255,255,.9)!important;
  }
  .ba-report-module-switcher.open .ba-module-trigger i{
    transform:translateY(-50%) rotate(180deg)!important;
    background:rgba(0,103,95,.11)!important;
  }
  .ba-module-menu{
    position:absolute!important;
    left:0!important;
    top:calc(100% + 8px)!important;
    width:min(310px,calc(100vw - 36px))!important;
    max-height:410px!important;
    overflow:auto!important;
    padding:8px!important;
    border:1px solid rgba(204,231,226,.96)!important;
    border-radius:22px!important;
    background:rgba(255,255,255,.96)!important;
    backdrop-filter:blur(18px)!important;
    box-shadow:0 24px 60px rgba(15,23,42,.16)!important;
    z-index:80!important;
  }
  .ba-module-menu button{
    width:100%!important;
    border:0!important;
    border-radius:16px!important;
    background:transparent!important;
    padding:10px!important;
    display:grid!important;
    grid-template-columns:32px minmax(0,1fr)!important;
    gap:10px!important;
    align-items:center!important;
    text-align:left!important;
    cursor:pointer!important;
  }
  .ba-module-menu button:hover{background:#f3fbf9!important;}
  .ba-module-menu button.active{background:linear-gradient(135deg,#e8fff8,#ffffff)!important;box-shadow:inset 0 0 0 1px rgba(0,103,95,.14)!important;}
  .ba-module-menu b{
    width:32px!important;
    height:32px!important;
    border-radius:12px!important;
    display:grid!important;
    place-items:center!important;
    background:#f1f5f9!important;
    color:#64748b!important;
    font-size:11px!important;
    font-weight:950!important;
  }
  .ba-module-menu button.active b{background:#00675f!important;color:white!important;}
  .ba-module-menu span{min-width:0!important;display:block!important;}
  .ba-module-menu strong{display:block!important;color:#0f172a!important;font-size:13px!important;font-weight:950!important;line-height:1.05!important;}
  .ba-module-menu em{display:block!important;margin-top:3px!important;color:#64748b!important;font-size:11px!important;font-weight:800!important;font-style:normal!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;}
  @media(max-width:860px){
    .ba-module-menu{width:100%!important;}
    .ba-report-filterbar-main.ba-report-filterbar-with-module{grid-template-columns:1fr!important;}
  }


  /* LOOP PHASE 3C.3 — module switcher minimal premium */
  .ba-report-filterbar-main.ba-report-filterbar-with-module{
    grid-template-columns:minmax(120px,172px) minmax(320px,.9fr) minmax(230px,.55fr) minmax(300px,1fr)!important;
    gap:10px!important;
  }
  .ba-report-module-select.ba-report-module-switcher{
    min-height:40px!important;
    padding:0!important;
    border:0!important;
    border-radius:999px!important;
    background:transparent!important;
    box-shadow:none!important;
    overflow:visible!important;
    align-self:center!important;
  }
  .ba-report-module-select.ba-report-module-switcher:before{display:none!important;}
  .ba-module-trigger.ba-module-trigger-minimal{
    width:100%!important;
    min-height:40px!important;
    height:40px!important;
    border:1px solid rgba(204,231,226,.92)!important;
    border-radius:999px!important;
    background:rgba(255,255,255,.88)!important;
    box-shadow:0 10px 24px rgba(15,23,42,.045), inset 0 1px 0 rgba(255,255,255,.92)!important;
    padding:0 36px 0 16px!important;
    display:flex!important;
    align-items:center!important;
    gap:9px!important;
    text-align:left!important;
    cursor:pointer!important;
    position:relative!important;
  }
  .ba-module-trigger.ba-module-trigger-minimal:before{
    content:""!important;
    position:static!important;
    width:7px!important;
    height:7px!important;
    border-radius:999px!important;
    flex:0 0 auto!important;
    background:#00675f!important;
    box-shadow:0 0 0 5px rgba(0,103,95,.08)!important;
  }
  .ba-module-trigger.ba-module-trigger-minimal strong{
    min-width:0!important;
    color:#0f172a!important;
    font-size:13px!important;
    line-height:1!important;
    font-weight:950!important;
    letter-spacing:-.025em!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }
  .ba-module-trigger.ba-module-trigger-minimal span,
  .ba-module-trigger.ba-module-trigger-minimal em{display:none!important;}
  .ba-module-trigger.ba-module-trigger-minimal i{
    position:absolute!important;
    right:9px!important;
    top:50%!important;
    transform:translateY(-50%) rotate(0deg)!important;
    width:24px!important;
    height:24px!important;
    border-radius:999px!important;
    display:grid!important;
    place-items:center!important;
    background:#f3fbf9!important;
    color:#00675f!important;
    font-size:12px!important;
    font-style:normal!important;
    font-weight:950!important;
    line-height:1!important;
    transition:transform .16s ease, background .16s ease!important;
  }
  .ba-report-module-switcher.open .ba-module-trigger.ba-module-trigger-minimal{
    border-color:rgba(20,184,166,.48)!important;
    box-shadow:0 16px 34px rgba(0,103,95,.10), inset 0 1px 0 rgba(255,255,255,.95)!important;
  }
  .ba-report-module-switcher.open .ba-module-trigger.ba-module-trigger-minimal i{
    transform:translateY(-50%) rotate(180deg)!important;
    background:rgba(0,103,95,.10)!important;
  }
  .ba-module-menu.ba-module-menu-minimal{
    position:absolute!important;
    left:0!important;
    top:calc(100% + 8px)!important;
    width:205px!important;
    max-height:none!important;
    overflow:visible!important;
    padding:6px!important;
    border:1px solid rgba(204,231,226,.96)!important;
    border-radius:18px!important;
    background:rgba(255,255,255,.98)!important;
    backdrop-filter:blur(18px)!important;
    -webkit-backdrop-filter:blur(18px)!important;
    box-shadow:0 22px 54px rgba(15,23,42,.14)!important;
    z-index:90!important;
  }
  .ba-module-menu.ba-module-menu-minimal button{
    width:100%!important;
    min-height:34px!important;
    border:0!important;
    border-radius:13px!important;
    background:transparent!important;
    padding:0 10px!important;
    display:flex!important;
    align-items:center!important;
    gap:9px!important;
    text-align:left!important;
    cursor:pointer!important;
    color:#0f172a!important;
  }
  .ba-module-menu.ba-module-menu-minimal button:hover{background:#f7fbfa!important;}
  .ba-module-menu.ba-module-menu-minimal button.active{
    background:#eefbf8!important;
    box-shadow:inset 0 0 0 1px rgba(0,103,95,.12)!important;
  }
  .ba-module-menu.ba-module-menu-minimal .ba-module-menu-dot{
    width:6px!important;
    height:6px!important;
    border-radius:999px!important;
    display:block!important;
    flex:0 0 auto!important;
    background:#cbd5e1!important;
  }
  .ba-module-menu.ba-module-menu-minimal button.active .ba-module-menu-dot{
    background:#00675f!important;
    box-shadow:0 0 0 5px rgba(0,103,95,.08)!important;
  }
  .ba-module-menu.ba-module-menu-minimal b,
  .ba-module-menu.ba-module-menu-minimal em{display:none!important;}
  .ba-module-menu.ba-module-menu-minimal strong{
    display:block!important;
    min-width:0!important;
    color:#0f172a!important;
    font-size:12.5px!important;
    font-weight:900!important;
    line-height:1!important;
    letter-spacing:-.02em!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }
  @media(max-width:860px){
    .ba-report-filterbar-main.ba-report-filterbar-with-module{grid-template-columns:1fr!important;}
    .ba-module-menu.ba-module-menu-minimal{width:100%!important;max-height:320px!important;overflow:auto!important;}
  }


  /* LOOP PHASE 3C.4 — finance split layout: compact summary + cash method breakdown */
  .ba-finance-split-top{
    display:grid!important;
    grid-template-columns:minmax(420px,.95fr) minmax(520px,1.05fr)!important;
    gap:14px!important;
    align-items:stretch!important;
  }
  .ba-finance-summary-card,
  .ba-finance-method-breakdown-card{
    border-radius:24px!important;
    border:1px solid rgba(226,232,240,.86)!important;
    background:linear-gradient(180deg,#ffffff 0%,#fbfdfa 100%)!important;
    box-shadow:0 16px 38px rgba(15,23,42,.05)!important;
    padding:18px!important;
    min-height:196px!important;
    overflow:hidden!important;
    position:relative!important;
  }
  .ba-finance-summary-card:before,
  .ba-finance-method-breakdown-card:before{
    content:""!important;
    position:absolute!important;
    inset:auto -58px -74px auto!important;
    width:150px!important;
    height:150px!important;
    border-radius:999px!important;
    background:rgba(0,103,95,.045)!important;
    pointer-events:none!important;
  }
  .ba-finance-method-breakdown-card{
    background:linear-gradient(135deg,#ffffff 0%,#f8fffc 100%)!important;
  }
  .ba-finance-split-head{
    display:flex!important;
    align-items:flex-start!important;
    justify-content:space-between!important;
    gap:12px!important;
    margin-bottom:14px!important;
    position:relative!important;
    z-index:1!important;
  }
  .ba-finance-split-head span{
    display:block!important;
    color:#00675f!important;
    font-size:10px!important;
    font-weight:950!important;
    letter-spacing:.14em!important;
    text-transform:uppercase!important;
    margin-bottom:5px!important;
  }
  .ba-finance-split-head h3{
    margin:0!important;
    color:#0f172a!important;
    font-size:18px!important;
    line-height:1.05!important;
    letter-spacing:-.04em!important;
    font-weight:950!important;
  }
  .ba-finance-mini-metrics{
    display:grid!important;
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    gap:10px!important;
    position:relative!important;
    z-index:1!important;
  }
  .ba-finance-mini-metrics>div{
    min-height:74px!important;
    border:1px solid rgba(226,232,240,.82)!important;
    border-radius:18px!important;
    padding:12px!important;
    background:rgba(255,255,255,.82)!important;
    display:grid!important;
    align-content:center!important;
    box-shadow:0 8px 20px rgba(15,23,42,.035)!important;
  }
  .ba-finance-mini-metrics>div.primary{
    background:linear-gradient(145deg,#00675f 0%,#03534d 100%)!important;
    border-color:rgba(0,103,95,.36)!important;
  }
  .ba-finance-mini-metrics>div.warning{border-color:rgba(245,158,11,.24)!important;background:#fffdf5!important;}
  .ba-finance-mini-metrics>div.net{border-color:rgba(0,103,95,.18)!important;background:#f8fffc!important;}
  .ba-finance-mini-metrics span{
    color:#64748b!important;
    font-size:9.5px!important;
    font-weight:950!important;
    letter-spacing:.10em!important;
    text-transform:uppercase!important;
    margin-bottom:5px!important;
  }
  .ba-finance-mini-metrics strong{
    color:#0f172a!important;
    font-size:22px!important;
    line-height:1!important;
    letter-spacing:-.055em!important;
    font-weight:950!important;
    white-space:nowrap!important;
  }
  .ba-finance-mini-metrics em{
    color:#64748b!important;
    font-size:10.5px!important;
    line-height:1.2!important;
    font-style:normal!important;
    font-weight:780!important;
    margin-top:5px!important;
  }
  .ba-finance-mini-metrics .primary span,
  .ba-finance-mini-metrics .primary strong,
  .ba-finance-mini-metrics .primary em{color:#fff!important;}
  .ba-finance-mini-metrics .primary span,
  .ba-finance-mini-metrics .primary em{opacity:.82!important;}
  .ba-finance-method-breakdown-table{
    position:relative!important;
    z-index:1!important;
    display:grid!important;
    gap:7px!important;
  }
  .ba-finance-method-breakdown-head,
  .ba-finance-method-breakdown-row{
    display:grid!important;
    grid-template-columns:minmax(110px,1.15fr) repeat(4,minmax(72px,.8fr))!important;
    align-items:center!important;
    gap:10px!important;
  }
  .ba-finance-method-breakdown-head{
    min-height:32px!important;
    padding:0 10px!important;
    color:#64748b!important;
    font-size:9.5px!important;
    font-weight:950!important;
    letter-spacing:.10em!important;
    text-transform:uppercase!important;
  }
  .ba-finance-method-breakdown-head span{
    text-align:center!important;
  }
  .ba-finance-method-breakdown-head span:first-child{
    text-align:left!important;
  }
  .ba-finance-method-breakdown-row{
    min-height:42px!important;
    border:1px solid rgba(226,232,240,.74)!important;
    border-radius:15px!important;
    padding:8px 10px!important;
    background:rgba(255,255,255,.84)!important;
  }
  .ba-finance-method-breakdown-row strong{
    color:#0f172a!important;
    font-size:12.5px!important;
    font-weight:930!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }
  .ba-finance-method-breakdown-row span,
  .ba-finance-method-breakdown-row em{
    color:#334155!important;
    font-size:12px!important;
    font-weight:880!important;
    font-style:normal!important;
    text-align:center!important;
    justify-self:center!important;
    white-space:nowrap!important;
  }
  .ba-finance-method-breakdown-row em{color:#00675f!important;font-weight:950!important;}
  .ba-finance-method-breakdown-row strong{
    justify-self:start!important;
  }
  .ba-finance-method-breakdown-row.total{
    background:#f5fbf8!important;
    border-color:rgba(0,103,95,.16)!important;
  }
  .ba-finance-covered-note-split{
    position:relative!important;
    z-index:1!important;
    margin-top:10px!important;
    border-top:1px solid rgba(226,232,240,.78)!important;
  }
  .ba-finance-table-column-btn{
    height:34px!important;
    padding:0 14px!important;
    border-radius:999px!important;
    font-size:12px!important;
    font-weight:920!important;
  }
  .ba-finance-worker-panel{
    border-radius:24px!important;
    padding:18px!important;
  }
  .ba-finance-section-head{margin-bottom:12px!important;}
  .ba-finance-worker-table .ba-worker-close-head,
  .ba-finance-worker-table .ba-worker-close-row{
    min-height:46px!important;
  }
  .ba-finance-worker-name i{
    width:28px!important;
    height:28px!important;
    font-size:11px!important;
  }
  @media(max-width:1180px){
    .ba-finance-split-top{grid-template-columns:1fr!important;}
  }
  @media(max-width:720px){
    .ba-finance-mini-metrics{grid-template-columns:1fr!important;}
    .ba-finance-method-breakdown-table{overflow:auto!important;padding-bottom:4px!important;}
    .ba-finance-method-breakdown-head,
    .ba-finance-method-breakdown-row{min-width:620px!important;}
  }



/* =========================================================
   LOOP OWNER SUMMARY REVAMP — simple, friendly, customizable
   Keeps the existing top filters untouched.
   ========================================================= */
.ba-owner-overview{display:grid!important;gap:18px!important;max-width:1180px!important;margin:0 auto!important;}
.ba-owner-summary-toolbar{display:flex!important;align-items:center!important;justify-content:space-between!important;gap:12px!important;margin-top:2px!important;}
.ba-owner-summary-toolbar div{display:grid!important;gap:2px!important;}
.ba-owner-summary-toolbar span{color:#00796f!important;font-size:10px!important;font-weight:950!important;letter-spacing:.13em!important;text-transform:uppercase!important;}
.ba-owner-summary-toolbar strong{color:#0f172a!important;font-size:20px!important;font-weight:950!important;letter-spacing:-.04em!important;}
.ba-owner-edit-view{min-height:38px!important;border-radius:999px!important;border:1px solid rgba(203,213,225,.86)!important;background:#fff!important;color:#0f172a!important;font-size:12px!important;font-weight:950!important;display:inline-flex!important;align-items:center!important;gap:8px!important;padding:0 14px!important;box-shadow:0 12px 26px rgba(15,23,42,.05)!important;}
.ba-owner-edit-view i{width:22px!important;height:22px!important;border-radius:999px!important;display:grid!important;place-items:center!important;background:#ecfdf5!important;color:#00675f!important;font-style:normal!important;}
.ba-owner-hero{border:1px solid rgba(226,232,240,.92)!important;border-radius:28px!important;background:linear-gradient(135deg,#fff 0%,#fbfffd 100%)!important;box-shadow:0 18px 46px rgba(15,23,42,.06)!important;padding:30px 34px!important;display:grid!important;grid-template-columns:84px minmax(0,1fr) 260px!important;gap:26px!important;align-items:center!important;}
.ba-owner-hero-icon{width:72px!important;height:72px!important;border-radius:999px!important;display:grid!important;place-items:center!important;background:#fff7e6!important;color:#0f172a!important;font-size:34px!important;font-weight:950!important;}
.ba-owner-hero.success .ba-owner-hero-icon{background:#dcfce7!important;color:#00796f!important;}.ba-owner-hero.neutral .ba-owner-hero-icon{background:#f1f5f9!important;color:#334155!important;}.ba-owner-hero.warning .ba-owner-hero-icon{background:#fff7e6!important;color:#b45309!important;}
.ba-owner-hero-copy{display:grid!important;gap:8px!important;}.ba-owner-hero-copy span{font-size:10px!important;font-weight:950!important;letter-spacing:.14em!important;text-transform:uppercase!important;color:#00796f!important;}.ba-owner-hero-copy h3{margin:0!important;font-size:30px!important;line-height:1.03!important;color:#0f172a!important;font-weight:950!important;letter-spacing:-.055em!important;}.ba-owner-hero-copy p{margin:0!important;max-width:640px!important;color:#64748b!important;font-size:14px!important;line-height:1.45!important;font-weight:780!important;}.ba-owner-hero-copy button{justify-self:start!important;margin-top:8px!important;min-height:40px!important;border:0!important;border-radius:999px!important;background:#00796f!important;color:#fff!important;padding:0 22px!important;font-weight:950!important;box-shadow:0 14px 28px rgba(0,121,111,.20)!important;}
.ba-owner-hero-side{border-left:1px solid rgba(226,232,240,.95)!important;padding-left:28px!important;display:grid!important;gap:18px!important;}.ba-owner-hero-side div{display:grid!important;grid-template-columns:48px minmax(0,1fr)!important;grid-template-rows:auto auto!important;gap:2px 12px!important;align-items:center!important;}.ba-owner-hero-side i{grid-row:1 / span 2!important;width:46px!important;height:46px!important;border-radius:999px!important;display:grid!important;place-items:center!important;background:#dff7f2!important;color:#00796f!important;font-style:normal!important;font-weight:950!important;font-size:20px!important;}.ba-owner-hero-side span{color:#64748b!important;font-size:12px!important;font-weight:820!important;}.ba-owner-hero-side strong{color:#0f172a!important;font-size:22px!important;font-weight:950!important;letter-spacing:-.035em!important;}
.ba-owner-kpis{display:grid!important;grid-template-columns:repeat(4,minmax(0,1fr))!important;gap:14px!important;}.ba-owner-kpi{border:1px solid rgba(226,232,240,.9)!important;border-radius:22px!important;background:#fff!important;padding:20px!important;min-height:118px!important;box-shadow:0 12px 30px rgba(15,23,42,.045)!important;display:grid!important;grid-template-columns:52px minmax(0,1fr)!important;gap:14px!important;align-items:center!important;}.ba-owner-kpi i{width:48px!important;height:48px!important;border-radius:999px!important;display:grid!important;place-items:center!important;background:#dff7f2!important;color:#00796f!important;font-style:normal!important;font-weight:950!important;font-size:22px!important;}.ba-owner-kpi.warn i{background:#fff7e6!important;color:#b45309!important;}.ba-owner-kpi div{display:grid!important;gap:3px!important;}.ba-owner-kpi span{color:#475569!important;font-size:12px!important;font-weight:820!important;}.ba-owner-kpi strong{color:#0f172a!important;font-size:24px!important;line-height:1!important;font-weight:950!important;letter-spacing:-.04em!important;}.ba-owner-kpi em{font-style:normal!important;color:#00796f!important;font-size:11.5px!important;font-weight:820!important;}
.ba-owner-mid-grid{display:grid!important;grid-template-columns:minmax(0,1.35fr) minmax(320px,.9fr)!important;gap:16px!important;}.ba-owner-panel-head{display:flex!important;align-items:center!important;justify-content:space-between!important;gap:12px!important;margin-bottom:14px!important;}.ba-owner-panel-head h3{margin:0!important;color:#0f172a!important;font-size:18px!important;font-weight:950!important;letter-spacing:-.035em!important;}.ba-owner-panel-head span{display:block!important;color:#64748b!important;font-size:12px!important;font-weight:800!important;margin-top:3px!important;}.ba-owner-panel-head button{min-height:32px!important;border:1px solid rgba(203,213,225,.9)!important;border-radius:999px!important;background:#fff!important;color:#00796f!important;font-size:11.5px!important;font-weight:950!important;padding:0 12px!important;}
.ba-owner-actions,.ba-owner-top-list{display:grid!important;gap:9px!important;}.ba-owner-action{width:100%!important;min-height:58px!important;border:1px solid rgba(226,232,240,.9)!important;border-radius:17px!important;background:#fff!important;display:grid!important;grid-template-columns:38px minmax(0,1fr) auto!important;gap:12px!important;align-items:center!important;text-align:left!important;padding:10px 12px!important;}.ba-owner-action b{width:32px!important;height:32px!important;border-radius:999px!important;display:grid!important;place-items:center!important;background:#fff7e6!important;color:#b45309!important;font-size:12px!important;}.ba-owner-action.success b{background:#ecfdf5!important;color:#00796f!important;}.ba-owner-action.info b{background:#eef2ff!important;color:#4f46e5!important;}.ba-owner-action span{display:grid!important;gap:2px!important;min-width:0!important;}.ba-owner-action strong{font-size:13px!important;color:#0f172a!important;font-weight:950!important;overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important;}.ba-owner-action em{font-style:normal!important;color:#64748b!important;font-size:12px!important;font-weight:750!important;overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important;}.ba-owner-action i{font-style:normal!important;color:#00796f!important;font-size:12px!important;font-weight:950!important;white-space:nowrap!important;}
.ba-owner-top-list button{width:100%!important;min-height:64px!important;border:1px solid rgba(226,232,240,.9)!important;border-radius:18px!important;background:#fff!important;display:grid!important;grid-template-columns:42px minmax(0,1fr) auto!important;gap:12px!important;align-items:center!important;text-align:left!important;padding:11px 12px!important;}.ba-owner-top-list i{width:38px!important;height:38px!important;border-radius:999px!important;display:grid!important;place-items:center!important;background:#dff7f2!important;color:#00796f!important;font-style:normal!important;font-weight:950!important;font-size:18px!important;}.ba-owner-top-list span{display:grid!important;gap:1px!important;min-width:0!important;}.ba-owner-top-list em{font-style:normal!important;color:#64748b!important;font-size:11.5px!important;font-weight:800!important;}.ba-owner-top-list strong{font-size:14px!important;font-weight:950!important;color:#0f172a!important;overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important;}.ba-owner-top-list small{color:#94a3b8!important;font-size:11px!important;font-weight:760!important;}.ba-owner-top-list b{color:#00796f!important;font-size:14px!important;font-weight:950!important;white-space:nowrap!important;}
.ba-owner-chart-card{padding:18px 20px 16px!important;}.ba-owner-line-chart{position:relative!important;min-height:248px!important;margin-top:6px!important;}.ba-owner-line-chart svg{display:block!important;width:100%!important;height:230px!important;overflow:visible!important;}.ba-owner-line-chart .grid{stroke:#e8eef4!important;stroke-width:1!important;}.ba-owner-line-chart .area{fill:url(#baOwnerRevenueFill)!important;}.ba-owner-line-chart .line{fill:none!important;stroke:#008578!important;stroke-width:4!important;stroke-linecap:round!important;stroke-linejoin:round!important;}.ba-owner-line-chart .dot{fill:#008578!important;stroke:#fff!important;stroke-width:3!important;}.ba-owner-line-chart .best{fill:#00675f!important;}.ba-owner-line-axis{display:flex!important;justify-content:space-between!important;color:#64748b!important;font-size:11px!important;font-weight:780!important;padding:0 22px!important;margin-top:-6px!important;}.ba-owner-chart-pill{position:absolute!important;right:18px!important;top:8px!important;border:1px solid rgba(20,184,166,.22)!important;background:#ecfdf5!important;color:#00796f!important;border-radius:999px!important;padding:7px 12px!important;font-size:12px!important;font-weight:950!important;}
.ba-owner-bottom-grid{display:grid!important;grid-template-columns:minmax(0,1fr) minmax(0,1fr)!important;gap:16px!important;}.ba-owner-status-list{display:grid!important;gap:12px!important;}.ba-owner-services-panel .ba-list-bars{gap:12px!important;}
.ba-overview-customizer .ba-view-customizer-body{grid-template-columns:1fr!important;}.ba-view-customizer-group.full{grid-column:1/-1!important;}
@media(max-width:1040px){.ba-owner-hero{grid-template-columns:68px minmax(0,1fr)!important;}.ba-owner-hero-side{grid-column:1/-1!important;border-left:0!important;border-top:1px solid rgba(226,232,240,.9)!important;padding:18px 0 0!important;grid-template-columns:repeat(2,minmax(0,1fr))!important;}.ba-owner-kpis{grid-template-columns:repeat(2,minmax(0,1fr))!important;}.ba-owner-mid-grid,.ba-owner-bottom-grid{grid-template-columns:1fr!important;}}
@media(max-width:640px){.ba-owner-overview{gap:14px!important;}.ba-owner-summary-toolbar{align-items:flex-start!important;}.ba-owner-hero{padding:22px!important;grid-template-columns:1fr!important;text-align:left!important;}.ba-owner-hero-icon{width:58px!important;height:58px!important;font-size:28px!important;}.ba-owner-hero-copy h3{font-size:24px!important;}.ba-owner-hero-side{grid-template-columns:1fr!important;}.ba-owner-kpis{grid-template-columns:1fr!important;}.ba-owner-action{grid-template-columns:34px minmax(0,1fr)!important;}.ba-owner-action i{grid-column:2!important;}.ba-owner-chart-pill{position:static!important;display:inline-flex!important;margin:8px 0 0 22px!important;}.ba-owner-line-chart{min-height:220px!important;}.ba-owner-line-chart svg{height:200px!important;}}

/* =========================================================
   LOOP OWNER SUMMARY POLISH V2
   Más ancho, menos aire muerto, zero-states más elegantes
   y cards más cercanas al mockup.
   ========================================================= */

.business-analytics.analytics-minimal .ba-command-main,
.business-analytics.analytics-minimal .ba-shell,
.business-analytics.analytics-minimal .ba-command-workspace{
  min-width:0!important;
}

.ba-owner-overview{
  width:100%!important;
  max-width:min(1320px, calc(100vw - 250px))!important;
  margin:0!important;
  gap:16px!important;
}

.ba-owner-summary-toolbar-polish{
  margin:0!important;
  min-height:38px!important;
  padding:0 2px!important;
  align-items:center!important;
}

.ba-owner-summary-toolbar-polish div{
  gap:1px!important;
}

.ba-owner-summary-toolbar-polish span{
  font-size:9.5px!important;
  letter-spacing:.16em!important;
  color:#00796f!important;
}

.ba-owner-summary-toolbar-polish strong{
  font-size:17px!important;
  letter-spacing:-.035em!important;
}

.ba-owner-edit-view{
  height:34px!important;
  min-height:34px!important;
  padding:0 14px!important;
  border-radius:999px!important;
  background:#fff!important;
  border:1px solid rgba(203,213,225,.9)!important;
  box-shadow:0 8px 20px rgba(15,23,42,.045)!important;
  color:#0f766e!important;
  font-size:11.5px!important;
  font-weight:950!important;
}

.ba-owner-hero{
  grid-template-columns:76px minmax(0,1fr) 250px!important;
  padding:26px 30px!important;
  border-radius:26px!important;
  min-height:154px!important;
  background:
    radial-gradient(circle at 0% 0%, rgba(20,184,166,.075), transparent 34%),
    linear-gradient(135deg,#ffffff 0%,#fbfffd 100%)!important;
}

.ba-owner-hero-icon{
  width:66px!important;
  height:66px!important;
  font-size:30px!important;
  box-shadow:inset 0 0 0 1px rgba(255,255,255,.8)!important;
}

.ba-owner-hero-copy h3{
  font-size:clamp(25px,2.05vw,34px)!important;
  max-width:760px!important;
}

.ba-owner-hero-copy p{
  font-size:13.5px!important;
  max-width:760px!important;
}

.ba-owner-hero-copy button{
  min-height:38px!important;
  margin-top:6px!important;
}

.ba-owner-hero-side{
  padding-left:24px!important;
  gap:14px!important;
}

.ba-owner-hero-side div{
  grid-template-columns:44px minmax(0,1fr)!important;
}

.ba-owner-hero-side i{
  width:42px!important;
  height:42px!important;
  font-size:18px!important;
}

.ba-owner-hero-side strong{
  font-size:20px!important;
}

.ba-owner-kpis{
  grid-template-columns:repeat(4,minmax(0,1fr))!important;
  gap:13px!important;
}

.ba-owner-kpi{
  min-height:104px!important;
  padding:16px 17px!important;
  border-radius:21px!important;
  grid-template-columns:46px minmax(0,1fr)!important;
}

.ba-owner-kpi i{
  width:42px!important;
  height:42px!important;
  font-size:19px!important;
}

.ba-owner-kpi strong{
  font-size:22px!important;
}

.ba-owner-mid-grid{
  grid-template-columns:minmax(0,1.16fr) minmax(330px,.84fr)!important;
  gap:15px!important;
  align-items:stretch!important;
}

.ba-owner-bottom-grid{
  grid-template-columns:minmax(0,.95fr) minmax(0,1.05fr)!important;
  gap:15px!important;
}

.ba-owner-action-panel,
.ba-owner-top-panel,
.ba-owner-chart-card,
.ba-owner-status-panel,
.ba-owner-services-panel{
  border-radius:24px!important;
  box-shadow:0 14px 34px rgba(15,23,42,.045)!important;
}

.ba-owner-panel-head{
  margin-bottom:12px!important;
}

.ba-owner-panel-head h3{
  font-size:17px!important;
}

.ba-owner-action{
  min-height:55px!important;
  border-radius:16px!important;
}

.ba-owner-action strong{
  font-size:12.8px!important;
}

.ba-owner-action em{
  font-size:11.5px!important;
}

.ba-owner-top-list button{
  min-height:58px!important;
  border-radius:16px!important;
}

.ba-owner-chart-card{
  padding:17px 18px 14px!important;
}

.ba-owner-line-chart{
  min-height:228px!important;
}

.ba-owner-line-chart svg{
  height:208px!important;
}

.ba-owner-empty-chart{
  min-height:210px!important;
  border:1px dashed rgba(148,163,184,.42)!important;
  border-radius:22px!important;
  background:
    radial-gradient(circle at 50% 20%, rgba(20,184,166,.07), transparent 35%),
    linear-gradient(135deg,#fbfdff,#f8fafc)!important;
  display:grid!important;
  place-items:center!important;
  align-content:center!important;
  text-align:center!important;
  padding:24px!important;
  color:#64748b!important;
}

.ba-owner-empty-chart div{
  width:48px!important;
  height:48px!important;
  border-radius:999px!important;
  display:grid!important;
  place-items:center!important;
  background:#ecfdf5!important;
  color:#00796f!important;
  font-size:20px!important;
  font-weight:950!important;
  margin-bottom:10px!important;
}

.ba-owner-empty-chart strong{
  color:#0f172a!important;
  font-size:15px!important;
  font-weight:950!important;
}

.ba-owner-empty-chart span{
  max-width:360px!important;
  margin-top:4px!important;
  font-size:12.5px!important;
  line-height:1.35!important;
  font-weight:760!important;
}

.ba-owner-status-list{
  gap:10px!important;
}

.ba-status-line{
  min-height:34px!important;
}

.ba-owner-services-panel .ba-empty,
.ba-owner-top-panel .ba-empty,
.ba-owner-action-panel .ba-empty{
  min-height:82px!important;
  border-radius:18px!important;
  border:1px dashed rgba(148,163,184,.36)!important;
  background:#f8fafc!important;
  display:grid!important;
  place-items:center!important;
  text-align:center!important;
  color:#64748b!important;
  font-size:12.5px!important;
  font-weight:800!important;
}

.ba-owner-services-panel .ba-list-bars{
  gap:10px!important;
}

.ba-owner-services-panel .ba-row{
  padding:7px 0!important;
}

@media(max-width:1180px){
  .ba-owner-overview{
    max-width:100%!important;
  }
  .ba-owner-hero{
    grid-template-columns:68px minmax(0,1fr)!important;
  }
  .ba-owner-hero-side{
    grid-column:1/-1!important;
    border-left:0!important;
    border-top:1px solid rgba(226,232,240,.9)!important;
    padding:16px 0 0!important;
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
  }
}

@media(max-width:980px){
  .ba-owner-mid-grid,
  .ba-owner-bottom-grid{
    grid-template-columns:1fr!important;
  }
  .ba-owner-kpis{
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
  }
}

@media(max-width:640px){
  .ba-owner-summary-toolbar-polish{
    flex-direction:row!important;
    align-items:center!important;
  }
  .ba-owner-summary-toolbar-polish strong{
    font-size:16px!important;
  }
  .ba-owner-edit-view{
    height:32px!important;
    min-height:32px!important;
    padding:0 11px!important;
  }
  .ba-owner-hero{
    padding:20px!important;
    min-height:auto!important;
  }
  .ba-owner-kpis{
    grid-template-columns:1fr!important;
  }
  .ba-owner-empty-chart{
    min-height:180px!important;
  }
}


/* =========================================================
   LOOP INVENTORY REVAMP — SIMPLE OPERATING HOME
   Main page = estado + decisiones. Edición, productos,
   acciones e historial viven en modales/paneles.
   ========================================================= */

.ba-inventory-simple{
  max-width:1320px!important;
  margin:0 auto!important;
  display:grid!important;
  gap:16px!important;
}

.ba-inventory-simple-hero{
  display:grid!important;
  grid-template-columns:minmax(0,1fr) auto!important;
  gap:18px!important;
  align-items:center!important;
  padding:24px 26px!important;
  border:1px solid rgba(203,213,225,.84)!important;
  border-radius:28px!important;
  background:
    radial-gradient(circle at 0% 0%, rgba(20,184,166,.10), transparent 36%),
    linear-gradient(135deg,#ffffff 0%,#fbfffd 100%)!important;
  box-shadow:0 16px 38px rgba(15,23,42,.045)!important;
}

.ba-inventory-simple-copy span,
.ba-inventory-simple-card-head span{
  display:block!important;
  color:#00796f!important;
  font-size:10px!important;
  letter-spacing:.18em!important;
  text-transform:uppercase!important;
  font-weight:950!important;
}

.ba-inventory-simple-copy h3{
  margin:6px 0 6px!important;
  font-size:clamp(27px,2.2vw,38px)!important;
  line-height:.98!important;
  letter-spacing:-.055em!important;
}

.ba-inventory-simple-copy p{
  margin:0!important;
  max-width:760px!important;
  color:#64748b!important;
  font-size:13.5px!important;
  line-height:1.35!important;
  font-weight:760!important;
}

.ba-inventory-simple-pills{
  display:flex!important;
  flex-wrap:wrap!important;
  gap:8px!important;
  margin-top:14px!important;
}

.ba-inventory-simple-pills em{
  min-height:28px!important;
  display:inline-flex!important;
  align-items:center!important;
  padding:0 10px!important;
  border-radius:999px!important;
  background:#fff!important;
  border:1px solid rgba(226,232,240,.88)!important;
  color:#475569!important;
  font-size:11px!important;
  font-weight:900!important;
  font-style:normal!important;
}

.ba-inventory-simple-pills em.ok{
  background:#ecfdf5!important;
  border-color:rgba(20,184,166,.22)!important;
  color:#00675f!important;
}

.ba-inventory-simple-actions{
  display:flex!important;
  align-items:center!important;
  gap:8px!important;
}

.ba-inventory-simple-actions button,
.ba-inventory-simple-card button,
.ba-inventory-simple-card-actions button,
.ba-inventory-actions-drawer-head button{
  min-height:38px!important;
  padding:0 14px!important;
  border-radius:999px!important;
  border:1px solid rgba(203,213,225,.82)!important;
  background:#fff!important;
  color:#0f172a!important;
  font-size:12px!important;
  font-weight:950!important;
  box-shadow:0 8px 20px rgba(15,23,42,.045)!important;
}

.ba-inventory-simple-actions button.primary,
.ba-inventory-simple-card button.primary,
.ba-inventory-simple-card-actions button.primary{
  background:#00796f!important;
  color:#fff!important;
  border-color:#00796f!important;
  box-shadow:0 14px 30px rgba(0,103,95,.16)!important;
}

.ba-inventory-simple-kpis{
  display:grid!important;
  grid-template-columns:repeat(4,minmax(0,1fr))!important;
  gap:12px!important;
}

.ba-inventory-simple-kpis article{
  min-height:102px!important;
  padding:17px 18px!important;
  border-radius:22px!important;
  background:#fff!important;
  border:1px solid rgba(226,232,240,.92)!important;
  box-shadow:0 12px 28px rgba(15,23,42,.04)!important;
}

.ba-inventory-simple-kpis article.warning{
  background:linear-gradient(135deg,#fff,#fffaf1)!important;
  border-color:rgba(245,158,11,.22)!important;
}

.ba-inventory-simple-kpis span{
  display:block!important;
  color:#64748b!important;
  font-size:11px!important;
  font-weight:900!important;
}

.ba-inventory-simple-kpis strong{
  display:block!important;
  margin-top:7px!important;
  font-size:25px!important;
  line-height:1!important;
  letter-spacing:-.04em!important;
}

.ba-inventory-simple-kpis em{
  display:block!important;
  margin-top:7px!important;
  color:#00796f!important;
  font-size:11.5px!important;
  font-style:normal!important;
  font-weight:900!important;
}

.ba-inventory-simple-grid{
  display:grid!important;
  grid-template-columns:repeat(2,minmax(0,1fr))!important;
  gap:14px!important;
  align-items:stretch!important;
}

.ba-inventory-simple-card{
  padding:18px!important;
  border-radius:24px!important;
  background:#fff!important;
  border:1px solid rgba(226,232,240,.92)!important;
  box-shadow:0 14px 34px rgba(15,23,42,.045)!important;
  display:grid!important;
  gap:14px!important;
  align-content:start!important;
}

.ba-inventory-simple-card.attention{
  background:
    radial-gradient(circle at 0% 0%, rgba(245,158,11,.09), transparent 32%),
    linear-gradient(135deg,#fff,#fffdf8)!important;
  border-color:rgba(245,158,11,.20)!important;
}

.ba-inventory-simple-card.success{
  background:
    radial-gradient(circle at 100% 0%, rgba(20,184,166,.08), transparent 34%),
    linear-gradient(135deg,#fff,#fbfffd)!important;
}

.ba-inventory-simple-card.warning{
  background:
    radial-gradient(circle at 100% 0%, rgba(245,158,11,.08), transparent 34%),
    linear-gradient(135deg,#fff,#fffaf2)!important;
  border-color:rgba(245,158,11,.20)!important;
}

.ba-inventory-simple-card-head{
  display:flex!important;
  justify-content:space-between!important;
  gap:16px!important;
  align-items:flex-start!important;
}

.ba-inventory-simple-card-head h3{
  margin:6px 0 5px!important;
  font-size:19px!important;
  line-height:1.08!important;
  letter-spacing:-.035em!important;
}

.ba-inventory-simple-card-head p{
  margin:0!important;
  max-width:520px!important;
  color:#64748b!important;
  font-size:12.5px!important;
  line-height:1.35!important;
  font-weight:760!important;
}

.ba-inventory-simple-card-head > strong{
  min-height:30px!important;
  display:inline-flex!important;
  align-items:center!important;
  padding:0 11px!important;
  border-radius:999px!important;
  background:#ecfdf5!important;
  color:#00675f!important;
  font-size:11px!important;
  font-weight:950!important;
  white-space:nowrap!important;
}

.ba-inventory-simple-stats{
  display:grid!important;
  grid-template-columns:repeat(3,minmax(0,1fr))!important;
  gap:10px!important;
}

.ba-inventory-simple-stats button{
  min-height:78px!important;
  padding:12px!important;
  border-radius:18px!important;
  background:#f8fafc!important;
  border:1px solid rgba(226,232,240,.92)!important;
  box-shadow:none!important;
  text-align:left!important;
  display:grid!important;
  gap:5px!important;
}

.ba-inventory-simple-stats b{
  font-size:23px!important;
  letter-spacing:-.04em!important;
}

.ba-inventory-simple-stats span{
  color:#64748b!important;
  font-size:11px!important;
  font-weight:900!important;
}

.ba-inventory-simple-card-actions{
  display:flex!important;
  flex-wrap:wrap!important;
  gap:9px!important;
}

.ba-inventory-simple-product-list{
  display:grid!important;
  gap:8px!important;
}

.ba-inventory-simple-product-list button{
  min-height:58px!important;
  padding:10px!important;
  border-radius:17px!important;
  display:grid!important;
  grid-template-columns:38px minmax(0,1fr) auto!important;
  gap:10px!important;
  align-items:center!important;
  text-align:left!important;
  background:#f8fafc!important;
  border:1px solid rgba(226,232,240,.92)!important;
  box-shadow:none!important;
}

.ba-inventory-simple-product-list i{
  width:36px!important;
  height:36px!important;
  border-radius:13px!important;
  display:grid!important;
  place-items:center!important;
  background:#ecfdf5!important;
  color:#00796f!important;
  font-style:normal!important;
  font-weight:950!important;
}

.ba-inventory-simple-product-list b,
.ba-inventory-simple-preview span{
  color:#0f172a!important;
  font-size:12.5px!important;
  font-weight:950!important;
}

.ba-inventory-simple-product-list em{
  display:block!important;
  color:#64748b!important;
  font-size:11px!important;
  font-style:normal!important;
  font-weight:800!important;
}

.ba-inventory-simple-product-list strong,
.ba-inventory-simple-preview strong{
  color:#00796f!important;
  font-size:12px!important;
  font-weight:950!important;
  white-space:nowrap!important;
}

.ba-inventory-simple-empty{
  min-height:72px!important;
  display:grid!important;
  place-items:center!important;
  border:1px dashed rgba(148,163,184,.38)!important;
  border-radius:18px!important;
  background:#f8fafc!important;
  color:#64748b!important;
  font-size:12px!important;
  font-weight:850!important;
}

.ba-inventory-simple-preview{
  display:grid!important;
  gap:8px!important;
}

.ba-inventory-simple-preview div{
  min-height:42px!important;
  padding:9px 11px!important;
  border-radius:15px!important;
  background:#f8fafc!important;
  border:1px solid rgba(226,232,240,.92)!important;
  display:flex!important;
  align-items:center!important;
  justify-content:space-between!important;
  gap:12px!important;
}

.ba-inventory-simple-progress{
  height:10px!important;
  border-radius:999px!important;
  overflow:hidden!important;
  background:#e2e8f0!important;
}

.ba-inventory-simple-progress i{
  display:block!important;
  height:100%!important;
  border-radius:inherit!important;
  background:linear-gradient(90deg,#00796f,#14b8a6)!important;
}

.ba-inventory-modal-section{
  display:grid!important;
  gap:14px!important;
}

.ba-inventory-actions-drawer-head{
  display:flex!important;
  align-items:flex-start!important;
  justify-content:space-between!important;
  gap:16px!important;
  padding:16px!important;
  border-radius:20px!important;
  background:#f8fafc!important;
  border:1px solid rgba(226,232,240,.92)!important;
}

.ba-inventory-actions-drawer-head span{
  color:#00796f!important;
  font-size:10px!important;
  letter-spacing:.18em!important;
  text-transform:uppercase!important;
  font-weight:950!important;
}

.ba-inventory-actions-drawer-head h4{
  margin:5px 0 4px!important;
  font-size:22px!important;
  letter-spacing:-.04em!important;
}

.ba-inventory-actions-drawer-head p{
  margin:0!important;
  color:#64748b!important;
  font-size:12.5px!important;
  font-weight:760!important;
}

.ba-inventory-product-cards{
  display:grid!important;
  grid-template-columns:repeat(2,minmax(0,1fr))!important;
  gap:12px!important;
}

.ba-inventory-product-card{
  display:grid!important;
  gap:12px!important;
  padding:14px!important;
  border-radius:20px!important;
  background:#fff!important;
  border:1px solid rgba(226,232,240,.92)!important;
  box-shadow:0 10px 24px rgba(15,23,42,.035)!important;
}

.ba-inventory-product-card-main{
  display:grid!important;
  grid-template-columns:42px minmax(0,1fr) auto!important;
  align-items:center!important;
  gap:10px!important;
}

.ba-inventory-product-card-main i{
  width:40px!important;
  height:40px!important;
  border-radius:15px!important;
  display:grid!important;
  place-items:center!important;
  background:#ecfdf5!important;
  color:#00796f!important;
  font-style:normal!important;
  font-weight:950!important;
}

.ba-inventory-product-card-main strong{
  display:block!important;
  font-size:14px!important;
  line-height:1.1!important;
}

.ba-inventory-product-card-main span{
  display:block!important;
  margin-top:3px!important;
  color:#64748b!important;
  font-size:11.5px!important;
  font-weight:760!important;
}

.ba-inventory-product-card-main em{
  padding:6px 9px!important;
  border-radius:999px!important;
  background:#ecfdf5!important;
  color:#00675f!important;
  font-size:10.5px!important;
  font-weight:950!important;
  font-style:normal!important;
}

.ba-inventory-product-card-main em.low,
.ba-inventory-product-card-main em.critical{
  background:#fff7ed!important;
  color:#b45309!important;
}

.ba-inventory-product-card dl{
  display:grid!important;
  grid-template-columns:repeat(4,minmax(0,1fr))!important;
  gap:8px!important;
  margin:0!important;
}

.ba-inventory-product-card dl div{
  padding:10px!important;
  border-radius:14px!important;
  background:#f8fafc!important;
}

.ba-inventory-product-card dt{
  color:#64748b!important;
  font-size:10px!important;
  font-weight:900!important;
}

.ba-inventory-product-card dd{
  margin:4px 0 0!important;
  color:#0f172a!important;
  font-size:12px!important;
  font-weight:950!important;
}

.ba-inventory-product-card-actions{
  display:flex!important;
  flex-wrap:wrap!important;
  gap:8px!important;
}

.ba-inventory-product-card-actions button{
  min-height:34px!important;
  padding:0 12px!important;
  border-radius:999px!important;
  border:1px solid rgba(203,213,225,.82)!important;
  background:#fff!important;
  color:#334155!important;
  font-size:11px!important;
  font-weight:950!important;
}

.ba-inventory-product-card-actions button.danger{
  color:#dc2626!important;
  border-color:rgba(220,38,38,.22)!important;
}

@media(max-width:1080px){
  .ba-inventory-simple-hero{
    grid-template-columns:1fr!important;
  }
  .ba-inventory-simple-grid,
  .ba-inventory-product-cards{
    grid-template-columns:1fr!important;
  }
}

@media(max-width:780px){
  .ba-inventory-simple-kpis{
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
  }
  .ba-inventory-simple-actions{
    flex-wrap:wrap!important;
  }
  .ba-inventory-product-card dl{
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
  }
}

@media(max-width:540px){
  .ba-inventory-simple-kpis,
  .ba-inventory-simple-stats{
    grid-template-columns:1fr!important;
  }
  .ba-inventory-simple-product-list button{
    grid-template-columns:36px minmax(0,1fr)!important;
  }
  .ba-inventory-simple-product-list strong{
    grid-column:2!important;
  }
}


  /* =========================================================
     LOOP FINANCE MOBILE FRIENDLY V21
     Goal: make Finanzas/Caja friendly on phones and worker view.
     - Less big bubbles.
     - More compact KPI reading.
     - Method table becomes mobile cards instead of horizontal scroll.
     - Worker cards become simpler and hide owner-only net/total noise.
     ========================================================= */

  @media(max-width:720px){
    .business-analytics.analytics-minimal.ba-finance-active-view{
      padding:8px 8px 30px!important;
      max-width:100%!important;
      overflow:visible!important;
    }

    .business-analytics.analytics-minimal.ba-finance-active-view .ba-report-filterbar{
      margin:0 0 12px!important;
      padding:8px!important;
      border-radius:20px!important;
      box-shadow:0 8px 24px rgba(15,23,42,.045)!important;
      background:rgba(255,255,255,.96)!important;
    }

    .business-analytics.analytics-minimal.ba-finance-active-view .ba-report-filterbar-main{
      display:grid!important;
      grid-template-columns:1fr!important;
      gap:8px!important;
      align-items:stretch!important;
    }

    .business-analytics.analytics-minimal.ba-finance-active-view .ba-module-trigger-minimal,
    .business-analytics.analytics-minimal.ba-finance-active-view .ba-date-control-premium{
      min-height:38px!important;
      height:38px!important;
      border-radius:18px!important;
    }

    .business-analytics.analytics-minimal.ba-finance-active-view .ba-friendly-periods{
      min-height:38px!important;
      grid-template-columns:repeat(5,minmax(74px,1fr))!important;
      overflow-x:auto!important;
      scrollbar-width:none!important;
    }
    .business-analytics.analytics-minimal.ba-finance-active-view .ba-friendly-periods::-webkit-scrollbar{display:none!important;}

    .business-analytics.analytics-minimal.ba-finance-active-view .ba-worker-pills-premium{
      display:flex!important;
      gap:6px!important;
      overflow-x:auto!important;
      padding:3px!important;
      border-radius:18px!important;
      scrollbar-width:none!important;
      scroll-snap-type:x proximity!important;
    }
    .business-analytics.analytics-minimal.ba-finance-active-view .ba-worker-pills-premium::-webkit-scrollbar{display:none!important;}
    .business-analytics.analytics-minimal.ba-finance-active-view .ba-worker-pills-premium button{
      flex:0 0 auto!important;
      min-width:auto!important;
      padding:0 12px!important;
      scroll-snap-align:start!important;
    }

    .business-analytics.analytics-minimal.ba-finance-active-view .ba-phase2-strip,
    .business-analytics.analytics-minimal.ba-finance-active-view .ba-quality-strip{
      display:none!important;
    }

    .ba-finance-module.ba-finance-operational{
      gap:10px!important;
      margin-top:6px!important;
    }

    .ba-finance-operational-head{
      display:grid!important;
      grid-template-columns:1fr auto!important;
      gap:8px!important;
      align-items:start!important;
      margin:0!important;
      padding:0 2px!important;
    }
    .ba-finance-operational-head span{
      font-size:10px!important;
      margin-bottom:5px!important;
      letter-spacing:.14em!important;
    }
    .ba-finance-operational-head h3{
      font-size:24px!important;
      line-height:.98!important;
      letter-spacing:-.055em!important;
    }
    .ba-finance-operational-head p{
      display:none!important;
    }
    .ba-finance-customize-btn,
    .ba-finance-table-column-btn{
      min-width:auto!important;
      height:34px!important;
      padding:0 12px!important;
      border-radius:999px!important;
      font-size:11.5px!important;
      box-shadow:none!important;
    }

    .ba-finance-split-top{
      display:grid!important;
      grid-template-columns:1fr!important;
      gap:10px!important;
    }

    .ba-panel.ba-finance-summary-card,
    .ba-panel.ba-finance-method-breakdown-card,
    .ba-panel.ba-worker-close-panel{
      border-radius:18px!important;
      padding:12px!important;
      box-shadow:0 8px 24px rgba(15,23,42,.035)!important;
      overflow:hidden!important;
    }

    .ba-finance-split-head,
    .ba-finance-section-head{
      margin-bottom:8px!important;
      gap:8px!important;
    }
    .ba-finance-split-head span,
    .ba-finance-section-head span{
      font-size:9.5px!important;
      letter-spacing:.13em!important;
    }
    .ba-finance-split-head h3,
    .ba-finance-section-head h3{
      font-size:17px!important;
      line-height:1.05!important;
      letter-spacing:-.035em!important;
    }

    .ba-finance-mini-metrics{
      grid-template-columns:repeat(2,minmax(0,1fr))!important;
      gap:8px!important;
    }
    .ba-finance-mini-metrics>div{
      min-height:62px!important;
      border-radius:15px!important;
      padding:9px 10px!important;
      background:#ffffff!important;
      box-shadow:none!important;
      border:1px solid rgba(226,232,240,.9)!important;
      border-left:4px solid rgba(0,103,95,.22)!important;
      align-content:center!important;
    }
    .ba-finance-mini-metrics>div.primary{
      background:#ffffff!important;
      border-color:rgba(226,232,240,.9)!important;
      border-left-color:#00675f!important;
      color:#0f172a!important;
    }
    .ba-finance-mini-metrics>div.warning{border-left-color:#f59e0b!important;background:#fffdf7!important;}
    .ba-finance-mini-metrics>div.net{border-left-color:#14b8a6!important;background:#f8fffc!important;}
    .ba-finance-mini-metrics span,
    .ba-finance-mini-metrics .primary span{
      color:#64748b!important;
      opacity:1!important;
      font-size:9px!important;
      margin:0 0 4px!important;
      letter-spacing:.10em!important;
    }
    .ba-finance-mini-metrics strong,
    .ba-finance-mini-metrics .primary strong{
      color:#0f172a!important;
      font-size:19px!important;
      line-height:1!important;
      letter-spacing:-.04em!important;
    }
    .ba-finance-mini-metrics em,
    .ba-finance-mini-metrics .primary em{
      color:#64748b!important;
      opacity:1!important;
      font-size:9.5px!important;
      line-height:1.15!important;
      margin-top:4px!important;
    }

    .ba-finance-method-breakdown-table{
      display:grid!important;
      gap:8px!important;
      overflow:visible!important;
      padding-bottom:0!important;
    }
    .ba-finance-method-breakdown-head{display:none!important;}
    .ba-finance-method-breakdown-row{
      min-width:0!important;
      display:grid!important;
      grid-template-columns:1fr auto!important;
      grid-template-areas:
        "method total"
        "service service"!important;
      gap:7px 10px!important;
      min-height:0!important;
      border-radius:15px!important;
      padding:10px!important;
      background:#ffffff!important;
      border:1px solid rgba(226,232,240,.92)!important;
      box-shadow:none!important;
    }
    .ba-finance-method-breakdown-row strong{
      grid-area:method!important;
      align-self:center!important;
      font-size:12.5px!important;
      min-width:0!important;
      overflow:hidden!important;
      text-overflow:ellipsis!important;
      white-space:nowrap!important;
    }
    .ba-finance-method-breakdown-row em{
      grid-area:total!important;
      justify-self:end!important;
      align-self:center!important;
      color:#00675f!important;
      font-size:13px!important;
      font-weight:950!important;
    }
    .ba-finance-method-breakdown-row span{
      min-width:0!important;
      justify-self:stretch!important;
      text-align:left!important;
      border-radius:12px!important;
      background:#f8fafc!important;
      border:1px solid #edf2f7!important;
      padding:7px 8px!important;
      color:#0f172a!important;
      font-size:12px!important;
      font-weight:900!important;
      white-space:nowrap!important;
    }
    .ba-finance-method-breakdown-row span:nth-of-type(1)::before{content:"Servicio";}
    .ba-finance-method-breakdown-row span:nth-of-type(2)::before{content:"Producto";}
    .ba-finance-method-breakdown-row span:nth-of-type(3)::before{content:"Propina";}
    .ba-finance-method-breakdown-row span::before{
      display:block!important;
      margin-bottom:2px!important;
      color:#64748b!important;
      font-size:8.5px!important;
      font-weight:950!important;
      letter-spacing:.10em!important;
      text-transform:uppercase!important;
    }
    .ba-finance-method-breakdown-row.total{
      background:#f0fdfa!important;
      border-color:rgba(0,103,95,.18)!important;
    }
    .ba-finance-covered-note{border-radius:14px!important;padding:10px!important;}

    .ba-worker-close-table{display:none!important;}
    .ba-worker-close-cards{
      display:grid!important;
      gap:8px!important;
    }
    .ba-worker-card{
      border-radius:16px!important;
      padding:10px!important;
      box-shadow:none!important;
      border:1px solid rgba(226,232,240,.92)!important;
      background:#ffffff!important;
    }
    .ba-worker-card>div:first-child{
      display:grid!important;
      grid-template-columns:1fr!important;
      gap:3px!important;
      margin-bottom:8px!important;
    }
    .ba-worker-card strong{
      font-size:13.5px!important;
      letter-spacing:-.02em!important;
    }
    .ba-worker-card span{
      text-align:left!important;
      font-size:11.2px!important;
      line-height:1.25!important;
    }
    .ba-worker-card dl{
      grid-template-columns:repeat(2,minmax(0,1fr))!important;
      gap:6px!important;
    }
    .ba-worker-card dl div{
      border-radius:12px!important;
      padding:8px!important;
      background:#f8fafc!important;
      border:1px solid #edf2f7!important;
    }
    .ba-worker-card dt{
      font-size:8.7px!important;
      line-height:1.05!important;
      letter-spacing:.08em!important;
    }
    .ba-worker-card dd{
      margin-top:4px!important;
      font-size:13.5px!important;
      line-height:1!important;
    }

    .ba-empty,
    .ba-empty.slim{
      min-height:74px!important;
      display:flex!important;
      align-items:center!important;
      justify-content:center!important;
      text-align:center!important;
      border-radius:14px!important;
      padding:14px!important;
      font-size:12px!important;
      line-height:1.25!important;
    }
  }

  @media(max-width:420px){
    .ba-finance-mini-metrics strong,
    .ba-finance-mini-metrics .primary strong{font-size:18px!important;}
    .ba-finance-mini-metrics em,
    .ba-finance-mini-metrics .primary em{display:none!important;}
    .ba-finance-mini-metrics>div{min-height:54px!important;}
    .ba-worker-card dl{grid-template-columns:1fr 1fr!important;}
  }

  /* Worker-facing finance: reduce owner/business-only noise. */
  .business-analytics.analytics-minimal.ba-worker-view.ba-finance-active-view .ba-finance-mini-metrics>div.net,
  .business-analytics.analytics-minimal.ba-worker-view.ba-finance-active-view .ba-worker-card.total{
    display:none!important;
  }

  .business-analytics.analytics-minimal.ba-worker-view.ba-finance-active-view .ba-finance-operational-head h3::after{
    content:"";
  }

  @media(max-width:720px){
    .business-analytics.analytics-minimal.ba-worker-view.ba-finance-active-view .ba-finance-operational-head h3{
      font-size:22px!important;
    }
    .business-analytics.analytics-minimal.ba-worker-view.ba-finance-active-view .ba-finance-summary-card .ba-finance-split-head h3::after{
      content:" · vista trabajador";
      color:#64748b;
      font-size:12px;
      letter-spacing:0;
      font-weight:850;
    }
    .business-analytics.analytics-minimal.ba-worker-view.ba-finance-active-view .ba-worker-card dl div:nth-child(8){
      display:none!important;
    }
    .business-analytics.analytics-minimal.ba-worker-view.ba-worker-comp-hidden.ba-finance-active-view .ba-worker-card dl div:nth-child(4),
    .business-analytics.analytics-minimal.ba-worker-view.ba-worker-comp-hidden.ba-finance-active-view .ba-worker-card dl div:nth-child(5),
    .business-analytics.analytics-minimal.ba-worker-view.ba-worker-comp-hidden.ba-finance-active-view .ba-worker-card dl div:nth-child(6),
    .business-analytics.analytics-minimal.ba-worker-view.ba-worker-comp-hidden.ba-finance-active-view .ba-worker-card dl div:nth-child(7){
      display:none!important;
    }
    .business-analytics.analytics-minimal.ba-worker-view.ba-worker-comp-tips_only.ba-finance-active-view .ba-worker-card dl div:nth-child(5),
    .business-analytics.analytics-minimal.ba-worker-view.ba-worker-comp-tips_only.ba-finance-active-view .ba-worker-card dl div:nth-child(6),
    .business-analytics.analytics-minimal.ba-worker-view.ba-worker-comp-tips_only.ba-finance-active-view .ba-worker-card dl div:nth-child(7){
      display:none!important;
    }
    .business-analytics.analytics-minimal.ba-worker-view.ba-worker-comp-payout_total.ba-finance-active-view .ba-worker-card dl div:nth-child(5),
    .business-analytics.analytics-minimal.ba-worker-view.ba-worker-comp-payout_total.ba-finance-active-view .ba-worker-card dl div:nth-child(6){
      display:none!important;
    }
  }


  /* LOOP PHASE 3C.8 — Mobile finance compact lists */
  .ba-finance-method-mobile-list{display:none!important;}

  @media(max-width:720px){
    .ba-finance-active-view .ba-finance-method-breakdown-table{
      display:none!important;
    }

    .ba-finance-active-view .ba-finance-method-mobile-list{
      display:grid!important;
      gap:7px!important;
    }

    .ba-finance-method-mobile-row{
      display:grid!important;
      grid-template-columns:minmax(0,1fr) auto!important;
      align-items:center!important;
      gap:12px!important;
      min-height:46px!important;
      padding:10px 12px!important;
      border:1px solid rgba(226,232,240,.88)!important;
      border-radius:15px!important;
      background:#ffffff!important;
      box-shadow:none!important;
    }

    .ba-finance-method-mobile-row.total{
      border-color:rgba(0,103,95,.18)!important;
      background:linear-gradient(135deg,#f0fdfa,#ffffff)!important;
    }

    .ba-finance-method-mobile-row div{
      min-width:0!important;
      display:grid!important;
      gap:3px!important;
    }

    .ba-finance-method-mobile-row strong{
      color:#0f172a!important;
      font-size:13.5px!important;
      line-height:1.05!important;
      font-weight:950!important;
      white-space:nowrap!important;
      overflow:hidden!important;
      text-overflow:ellipsis!important;
    }

    .ba-finance-method-mobile-row span{
      color:#64748b!important;
      font-size:11.5px!important;
      line-height:1.2!important;
      font-weight:780!important;
      white-space:nowrap!important;
      overflow:hidden!important;
      text-overflow:ellipsis!important;
    }

    .ba-finance-method-mobile-row em{
      color:#00675f!important;
      font-size:13.5px!important;
      line-height:1!important;
      font-style:normal!important;
      font-weight:950!important;
      white-space:nowrap!important;
    }

    .ba-finance-active-view .ba-worker-close-cards.ba-finance-worker-mobile-list{
      display:grid!important;
      gap:8px!important;
    }

    .ba-finance-worker-mobile-row{
      border:1px solid rgba(226,232,240,.92)!important;
      border-radius:16px!important;
      background:#ffffff!important;
      box-shadow:none!important;
      overflow:hidden!important;
    }

    .ba-finance-worker-mobile-row.total{
      border-color:rgba(0,103,95,.18)!important;
      background:linear-gradient(135deg,#f0fdfa,#ffffff)!important;
    }

    .ba-finance-worker-mobile-row summary{
      list-style:none!important;
      cursor:pointer!important;
      min-height:54px!important;
      padding:12px 13px!important;
      display:grid!important;
      grid-template-columns:minmax(0,1fr) auto!important;
      align-items:center!important;
      gap:12px!important;
    }

    .ba-finance-worker-mobile-row summary::-webkit-details-marker{
      display:none!important;
    }

    .ba-finance-worker-mobile-row summary:after{
      content:"⌄"!important;
      width:24px!important;
      height:24px!important;
      border-radius:999px!important;
      display:grid!important;
      place-items:center!important;
      background:#f3fbf9!important;
      color:#00675f!important;
      font-size:12px!important;
      font-weight:950!important;
      grid-column:3!important;
      transition:transform .16s ease!important;
    }

    .ba-finance-worker-mobile-row[open] summary:after{
      transform:rotate(180deg)!important;
    }

    .ba-finance-worker-mobile-row summary div{
      min-width:0!important;
      display:grid!important;
      gap:4px!important;
    }

    .ba-finance-worker-mobile-row summary strong{
      color:#0f172a!important;
      font-size:13.5px!important;
      line-height:1.05!important;
      font-weight:950!important;
      white-space:nowrap!important;
      overflow:hidden!important;
      text-overflow:ellipsis!important;
    }

    .ba-finance-worker-mobile-row summary span{
      color:#64748b!important;
      font-size:11.5px!important;
      line-height:1.25!important;
      font-weight:780!important;
      white-space:nowrap!important;
      overflow:hidden!important;
      text-overflow:ellipsis!important;
    }

    .ba-finance-worker-mobile-row summary em{
      color:#00675f!important;
      font-size:13.5px!important;
      line-height:1!important;
      font-style:normal!important;
      font-weight:950!important;
      white-space:nowrap!important;
      justify-self:end!important;
    }

    .ba-finance-worker-mobile-row dl{
      margin:0!important;
      padding:0 13px 12px!important;
      display:grid!important;
      grid-template-columns:1fr 1fr!important;
      gap:7px!important;
      border-top:1px solid rgba(226,232,240,.76)!important;
    }

    .ba-finance-worker-mobile-row dl div{
      min-height:42px!important;
      border:1px solid #edf2f7!important;
      border-radius:13px!important;
      background:#f8fafc!important;
      padding:8px!important;
      display:grid!important;
      align-content:center!important;
      gap:4px!important;
    }

    .ba-finance-worker-mobile-row dt{
      color:#64748b!important;
      font-size:8.8px!important;
      line-height:1!important;
      font-weight:950!important;
      letter-spacing:.09em!important;
      text-transform:uppercase!important;
      white-space:nowrap!important;
      overflow:hidden!important;
      text-overflow:ellipsis!important;
    }

    .ba-finance-worker-mobile-row dd{
      margin:0!important;
      color:#00675f!important;
      font-size:13px!important;
      line-height:1!important;
      font-weight:950!important;
      white-space:nowrap!important;
      overflow:hidden!important;
      text-overflow:ellipsis!important;
    }

    .business-analytics.analytics-minimal.ba-worker-view.ba-finance-active-view .ba-finance-worker-mobile-row.total{
      display:none!important;
    }
  }

  @media(max-width:420px){
    .ba-finance-worker-mobile-row summary{
      grid-template-columns:minmax(0,1fr) auto!important;
    }
    .ba-finance-worker-mobile-row summary:after{
      display:none!important;
    }
    .ba-finance-worker-mobile-row dl{
      grid-template-columns:1fr!important;
    }
    .ba-finance-method-mobile-row span,
    .ba-finance-worker-mobile-row summary span{
      white-space:normal!important;
      display:-webkit-box!important;
      -webkit-line-clamp:2!important;
      -webkit-box-orient:vertical!important;
    }
  }


  /* LOOP PHASE 3C.9.1 — Mobile worker filter carousel safe */
  @media(max-width:720px){
    .business-analytics .ba-worker-pills.ba-worker-pills-premium{
      width:100%!important;
      max-width:100%!important;
      display:flex!important;
      align-items:center!important;
      justify-content:flex-start!important;
      flex-wrap:nowrap!important;
      gap:8px!important;
      overflow-x:auto!important;
      overflow-y:hidden!important;
      overscroll-behavior-x:contain!important;
      -webkit-overflow-scrolling:touch!important;
      scroll-snap-type:x proximity!important;
      scroll-padding-left:10px!important;
      padding:8px 10px!important;
      border-radius:18px!important;
      box-sizing:border-box!important;
      white-space:nowrap!important;
      touch-action:pan-x!important;
      scrollbar-width:none!important;
      position:relative!important;
    }

    .business-analytics .ba-worker-pills.ba-worker-pills-premium::-webkit-scrollbar{
      display:none!important;
      width:0!important;
      height:0!important;
    }

    .business-analytics .ba-worker-pills.ba-worker-pills-premium button{
      flex:0 0 auto!important;
      width:auto!important;
      min-width:max-content!important;
      max-width:none!important;
      scroll-snap-align:start!important;
      white-space:nowrap!important;
      overflow:visible!important;
      text-overflow:clip!important;
      padding-left:14px!important;
      padding-right:14px!important;
    }

    .business-analytics .ba-worker-pills.ba-worker-pills-premium button.active{
      scroll-snap-align:center!important;
    }

    .business-analytics .ba-filter-card,
    .business-analytics .ba-report-header,
    .business-analytics .ba-command-header,
    .business-analytics .ba-command-filters{
      max-width:100%!important;
      overflow:visible!important;
    }

    .business-analytics .ba-worker-pills.ba-worker-pills-premium::after{
      content:""!important;
      position:sticky!important;
      right:-10px!important;
      align-self:stretch!important;
      flex:0 0 26px!important;
      width:26px!important;
      min-width:26px!important;
      margin-left:-26px!important;
      pointer-events:none!important;
      background:linear-gradient(90deg,rgba(255,255,255,0),#fff 72%)!important;
      border-radius:0 18px 18px 0!important;
    }

    .business-analytics .ba-finance-worker-mobile-row summary{
      grid-template-columns:minmax(0,1fr) auto 24px!important;
    }

    .business-analytics .ba-finance-worker-mobile-row summary:after{
      grid-column:auto!important;
    }
  }

  @media(max-width:420px){
    .business-analytics .ba-worker-pills.ba-worker-pills-premium{
      padding-left:8px!important;
      padding-right:8px!important;
      gap:7px!important;
    }

    .business-analytics .ba-worker-pills.ba-worker-pills-premium button{
      padding-left:12px!important;
      padding-right:12px!important;
      font-size:12px!important;
    }
  }


    /* LOOP LOYALTY FINANCE IMPACT 3C */
    .ba-finance-mini-metrics .loyalty-impact{
      border-color:rgba(0,103,95,.18)!important;
      background:linear-gradient(135deg,rgba(236,253,245,.98),rgba(255,255,255,.96))!important;
    }
    .ba-finance-mini-metrics .loyalty-impact span{
      color:#00675f!important;
    }
    .ba-finance-mini-metrics .loyalty-impact strong{
      color:#0f172a!important;
    }
    .ba-finance-mini-metrics .loyalty-impact em{
      color:#64748b!important;
    }
    .ba-finance-loyalty-impact-note{
      margin-top:10px!important;
      border:1px solid rgba(0,103,95,.16)!important;
      background:linear-gradient(135deg,#ecfdf5 0%,#ffffff 100%)!important;
      border-radius:18px!important;
      padding:12px!important;
      display:flex!important;
      align-items:flex-start!important;
      justify-content:space-between!important;
      gap:12px!important;
    }
    .ba-finance-loyalty-impact-note div{
      min-width:0!important;
      display:grid!important;
      gap:4px!important;
    }
    .ba-finance-loyalty-impact-note span{
      color:#00675f!important;
      font-size:11px!important;
      font-weight:950!important;
      letter-spacing:.10em!important;
      text-transform:uppercase!important;
    }
    .ba-finance-loyalty-impact-note small{
      color:#64748b!important;
      font-size:12px!important;
      line-height:1.3!important;
      font-weight:850!important;
    }
    .ba-finance-loyalty-impact-note strong{
      color:#0f172a!important;
      font-size:16px!important;
      font-weight:950!important;
      white-space:nowrap!important;
    }

/* =========================================================
   LOOP FINANCE — WORKER SUMMARY TABLE V100
   A cleaner read: negocio → trabajador → resultado.
   Secondary values stay calm; totals carry the visual weight.
   ========================================================= */

.ba-finance-worker-panel{
  background:
    radial-gradient(circle at 0% 0%, rgba(20,184,166,.075), transparent 30%),
    #ffffff !important;
}

.ba-finance-worker-section-head{
  align-items:flex-start!important;
  gap:16px!important;
}

.ba-finance-worker-section-head h3{
  letter-spacing:-.045em!important;
}

.ba-finance-worker-story{
  display:flex!important;
  flex-wrap:wrap!important;
  gap:8px!important;
  margin:-2px 0 14px!important;
}

.ba-finance-worker-story span{
  min-height:28px!important;
  display:inline-flex!important;
  align-items:center!important;
  gap:7px!important;
  border:1px solid rgba(226,232,240,.82)!important;
  background:#fff!important;
  border-radius:999px!important;
  padding:0 10px!important;
  color:#64748b!important;
  font-size:11px!important;
  font-weight:900!important;
  white-space:nowrap!important;
}

.ba-finance-worker-story i{
  width:8px!important;
  height:8px!important;
  border-radius:999px!important;
  display:inline-block!important;
  background:#94a3b8!important;
}

.ba-finance-worker-story i.business{background:#0f766e!important;}
.ba-finance-worker-story i.worker{background:#64748b!important;}
.ba-finance-worker-story i.net{background:#0f172a!important;}

.ba-finance-worker-table-v100{
  border:1px solid rgba(203,213,225,.70)!important;
  border-radius:22px!important;
  overflow:auto!important;
  background:#ffffff!important;
  box-shadow:0 18px 45px rgba(15,23,42,.045)!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-groups,
.ba-finance-worker-table-v100 .ba-worker-close-head,
.ba-finance-worker-table-v100 .ba-worker-close-row{
  display:grid!important;
  grid-template-columns:minmax(164px,1.18fr) repeat(var(--finance-col-count, 9), minmax(96px,1fr))!important;
  min-width:1080px!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-groups{
  align-items:center!important;
  gap:10px!important;
  padding:10px 16px 8px!important;
  background:
    linear-gradient(90deg, rgba(240,253,250,.78), rgba(248,250,252,.85) 54%, rgba(255,255,255,.95))!important;
  border-bottom:1px solid rgba(226,232,240,.88)!important;
}

.ba-finance-worker-group-spacer{
  color:#94a3b8!important;
  font-size:10px!important;
  font-weight:950!important;
  letter-spacing:.13em!important;
  text-transform:uppercase!important;
}

.ba-finance-worker-group{
  min-height:24px!important;
  border-radius:999px!important;
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  font-size:10.5px!important;
  font-weight:950!important;
  letter-spacing:.12em!important;
  text-transform:uppercase!important;
}

.ba-finance-worker-group.is-business{
  background:rgba(15,118,110,.09)!important;
  color:#0f766e!important;
}

.ba-finance-worker-group.is-worker{
  background:rgba(100,116,139,.10)!important;
  color:#475569!important;
}

.ba-finance-worker-group.is-result{
  background:#0f172a!important;
  color:#ffffff!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-head{
  background:#fbfdff!important;
  color:#64748b!important;
  border-bottom:1px solid rgba(226,232,240,.82)!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-head > span{
  color:#64748b!important;
  font-size:10px!important;
  font-weight:950!important;
  letter-spacing:.095em!important;
  text-transform:uppercase!important;
  white-space:normal!important;
  line-height:1.15!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-head .is-business-total,
.ba-finance-worker-table-v100 .ba-worker-close-head .is-worker-total,
.ba-finance-worker-table-v100 .ba-worker-close-head .is-net{
  color:#0f172a!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-row{
  align-items:center!important;
  gap:10px!important;
  padding:14px 16px!important;
  min-height:58px!important;
  border-bottom:1px solid rgba(226,232,240,.72)!important;
  background:#ffffff!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-row:nth-of-type(even){
  background:#fcfdfd!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-row:hover{
  background:#f8fffd!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-row.total{
  background:
    radial-gradient(circle at 0% 50%, rgba(20,184,166,.10), transparent 34%),
    linear-gradient(135deg,#f0fdfa 0%,#ffffff 100%)!important;
  border-top:1px solid rgba(0,103,95,.20)!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-name{
  display:flex!important;
  align-items:center!important;
  gap:11px!important;
  min-width:0!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-name i{
  width:30px!important;
  height:30px!important;
  border-radius:999px!important;
  display:grid!important;
  place-items:center!important;
  background:#ecfdf5!important;
  color:#00796f!important;
  font-style:normal!important;
  font-size:11px!important;
  font-weight:950!important;
  box-shadow:inset 0 0 0 1px rgba(0,103,95,.13)!important;
  flex:0 0 auto!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-name span{
  min-width:0!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
  white-space:nowrap!important;
  color:#0f172a!important;
  font-size:14px!important;
  font-weight:950!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-total-name i{
  background:#00796f!important;
  color:#ffffff!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell{
  min-height:32px!important;
  border-radius:12px!important;
  display:inline-flex!important;
  align-items:center!important;
  justify-content:flex-start!important;
  padding:0 9px!important;
  font-size:13px!important;
  line-height:1!important;
  font-weight:900!important;
  white-space:nowrap!important;
  color:#475569!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-soft,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-count,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-income,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-soft{
  background:transparent!important;
  color:#64748b!important;
  font-weight:850!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total{
  background:#ecfdf5!important;
  color:#00796f!important;
  box-shadow:inset 0 0 0 1px rgba(0,103,95,.12)!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total{
  background:#f1f5f9!important;
  color:#0f172a!important;
  box-shadow:inset 0 0 0 1px rgba(148,163,184,.22)!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-net{
  background:#0f172a!important;
  color:#ffffff!important;
  justify-content:center!important;
  box-shadow:0 10px 22px rgba(15,23,42,.12)!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-row.total .ba-finance-worker-cell{
  font-weight:950!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-row.total .ba-finance-worker-cell.is-soft,
.ba-finance-worker-table-v100 .ba-worker-close-row.total .ba-finance-worker-cell.is-count,
.ba-finance-worker-table-v100 .ba-worker-close-row.total .ba-finance-worker-cell.is-income,
.ba-finance-worker-table-v100 .ba-worker-close-row.total .ba-finance-worker-cell.is-worker-soft{
  color:#0f766e!important;
}

@media(max-width:1180px){
  .ba-finance-worker-table-v100 .ba-worker-close-groups,
  .ba-finance-worker-table-v100 .ba-worker-close-head,
  .ba-finance-worker-table-v100 .ba-worker-close-row{
    min-width:1050px!important;
  }
}

@media(max-width:620px){
  .ba-finance-worker-story{
    display:none!important;
  }

  .ba-finance-worker-section-head h3{
    font-size:18px!important;
  }

  .ba-finance-worker-mobile-row{
    border-radius:18px!important;
    border:1px solid rgba(226,232,240,.92)!important;
    background:#ffffff!important;
    box-shadow:0 12px 26px rgba(15,23,42,.04)!important;
  }

  .ba-finance-worker-mobile-row summary em{
    color:#00796f!important;
    background:#ecfdf5!important;
    border-radius:999px!important;
    padding:7px 9px!important;
    font-weight:950!important;
  }

  .ba-finance-worker-mobile-row.total summary em{
    background:#0f172a!important;
    color:#ffffff!important;
  }
}



/* =========================================================
   LOOP FINANCE — WORKER SUMMARY TABLE V101
   Opción 5: simple con separaciones claras.
   Separa Actividad, Negocio, Trabajador y Resultado como bloques.
   ========================================================= */

.ba-finance-worker-panel{
  background:#ffffff!important;
  border:1px solid rgba(203,213,225,.72)!important;
  border-radius:26px!important;
  box-shadow:0 22px 54px rgba(15,23,42,.055)!important;
  overflow:hidden!important;
}

.ba-finance-worker-section-head{
  padding:24px 28px 16px!important;
  margin:0!important;
  align-items:flex-start!important;
}

.ba-finance-worker-section-head span{
  color:#00796f!important;
  font-size:11px!important;
  font-weight:950!important;
  letter-spacing:.16em!important;
  text-transform:uppercase!important;
}

.ba-finance-worker-section-head h3{
  color:#0f172a!important;
  font-size:27px!important;
  line-height:1.05!important;
  letter-spacing:-.055em!important;
  margin-top:6px!important;
}

.ba-finance-table-column-btn{
  min-height:42px!important;
  border-radius:999px!important;
  border:1px solid rgba(0,103,95,.32)!important;
  background:#ffffff!important;
  color:#00675f!important;
  font-weight:950!important;
  box-shadow:0 12px 24px rgba(15,23,42,.035)!important;
}

.ba-finance-worker-story{
  display:none!important;
}

.ba-finance-worker-table-v100{
  margin:0 28px 28px!important;
  border:1px solid rgba(203,213,225,.84)!important;
  border-radius:0!important;
  overflow:auto!important;
  background:#ffffff!important;
  box-shadow:none!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-groups,
.ba-finance-worker-table-v100 .ba-worker-close-head,
.ba-finance-worker-table-v100 .ba-worker-close-row{
  display:grid!important;
  grid-template-columns:minmax(180px,1.25fr) repeat(var(--finance-col-count, 11), minmax(100px,1fr))!important;
  min-width:1320px!important;
  gap:0!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-groups{
  padding:0!important;
  min-height:78px!important;
  align-items:stretch!important;
  background:#ffffff!important;
  border-bottom:1px solid rgba(203,213,225,.82)!important;
}

.ba-finance-worker-group-spacer,
.ba-finance-worker-group{
  position:relative!important;
  min-height:78px!important;
  border-radius:0!important;
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  font-size:14px!important;
  font-weight:950!important;
  letter-spacing:.14em!important;
  text-transform:uppercase!important;
  border-right:1px solid rgba(203,213,225,.78)!important;
  border-left:0!important;
}

.ba-finance-worker-group-spacer{
  justify-content:flex-start!important;
  padding-left:28px!important;
  color:#0f172a!important;
  letter-spacing:0!important;
  text-transform:none!important;
  font-size:15px!important;
  background:#ffffff!important;
}

.ba-finance-worker-group:after{
  content:""!important;
  position:absolute!important;
  left:18px!important;
  right:18px!important;
  bottom:18px!important;
  border-bottom:2px dashed rgba(148,163,184,.34)!important;
}

.ba-finance-worker-group.is-activity{
  background:#f8fafc!important;
  color:#64748b!important;
}

.ba-finance-worker-group.is-business{
  background:linear-gradient(135deg,#f0fdfa 0%,#ffffff 100%)!important;
  color:#00796f!important;
  border-left:10px solid #ffffff!important;
  box-shadow:inset 1px 0 0 rgba(20,184,166,.22)!important;
}

.ba-finance-worker-group.is-worker{
  background:linear-gradient(135deg,#eff6ff 0%,#ffffff 100%)!important;
  color:#1d4ed8!important;
  border-left:10px solid #ffffff!important;
  box-shadow:inset 1px 0 0 rgba(37,99,235,.18)!important;
}

.ba-finance-worker-group.is-result{
  background:linear-gradient(135deg,#f8fbff 0%,#ffffff 100%)!important;
  color:#1d4ed8!important;
  border-left:10px solid #ffffff!important;
  box-shadow:inset 1px 0 0 rgba(15,23,42,.16)!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-head{
  min-height:64px!important;
  align-items:stretch!important;
  padding:0!important;
  background:#ffffff!important;
  border-bottom:1px solid rgba(203,213,225,.82)!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-head > span{
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  min-height:64px!important;
  padding:0 14px!important;
  color:#0f172a!important;
  font-size:12px!important;
  font-weight:850!important;
  letter-spacing:-.015em!important;
  line-height:1.18!important;
  text-transform:none!important;
  white-space:normal!important;
  border-right:1px solid rgba(226,232,240,.82)!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-head > span:first-child{
  justify-content:flex-start!important;
  padding-left:28px!important;
  font-weight:950!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-head .is-group-activity{
  background:#f8fafc!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-head .is-group-business{
  background:rgba(240,253,250,.50)!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-head .is-group-worker{
  background:rgba(239,246,255,.55)!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-head .is-group-result{
  background:#f8fafc!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-head .is-business-total,
.ba-finance-worker-table-v100 .ba-worker-close-head .is-worker-total,
.ba-finance-worker-table-v100 .ba-worker-close-head .is-net{
  font-weight:950!important;
  color:#0f172a!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-row{
  min-height:70px!important;
  align-items:stretch!important;
  padding:0!important;
  background:#ffffff!important;
  border-bottom:1px solid rgba(203,213,225,.74)!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-row:last-child{
  border-bottom:0!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-row:hover{
  background:#ffffff!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-row.total{
  background:linear-gradient(90deg,rgba(240,253,250,.78),rgba(255,255,255,.96))!important;
  border-top:1px solid rgba(20,184,166,.24)!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-name,
.ba-finance-worker-table-v100 .ba-finance-worker-cell{
  min-height:70px!important;
  border-radius:0!important;
  border-right:1px solid rgba(226,232,240,.82)!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-name{
  display:flex!important;
  align-items:center!important;
  justify-content:flex-start!important;
  gap:12px!important;
  padding:0 24px!important;
  min-width:0!important;
  background:#ffffff!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-name i{
  width:34px!important;
  height:34px!important;
  border-radius:999px!important;
  display:grid!important;
  place-items:center!important;
  background:#d9fbef!important;
  color:#00796f!important;
  font-style:normal!important;
  font-size:13px!important;
  font-weight:950!important;
  box-shadow:inset 0 0 0 1px rgba(20,184,166,.20)!important;
  flex:0 0 auto!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-name span{
  min-width:0!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
  white-space:nowrap!important;
  color:#0f172a!important;
  font-size:15px!important;
  font-weight:950!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-total-name{
  background:rgba(240,253,250,.70)!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-total-name i{
  background:#008577!important;
  color:#ffffff!important;
  box-shadow:none!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell{
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  padding:0 12px!important;
  font-size:14px!important;
  line-height:1!important;
  font-weight:850!important;
  white-space:nowrap!important;
  color:#0f172a!important;
  background:#ffffff!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-group-activity{
  background:#fbfdff!important;
  font-weight:950!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-group-business{
  background:rgba(240,253,250,.30)!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-group-worker{
  background:rgba(239,246,255,.30)!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-group-result{
  background:#fbfdff!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-soft,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-count,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-income,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-membership-income,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-membership-income,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-soft{
  color:#0f172a!important;
  font-weight:850!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-net{
  background:transparent!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total:before,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total:before,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-net:before{
  content:attr(title)!important;
  display:none!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-net{
  font-weight:950!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total{
  color:#00796f!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total{
  color:#1d4ed8!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-net{
  color:#ffffff!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-net{
  position:relative!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total > *,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total > *,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-net > *{
  position:relative!important;
  z-index:1!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total{
  background:rgba(240,253,250,.48)!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total{
  background:rgba(239,246,255,.48)!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total{
  border-radius:0!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total{
  box-shadow:none!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total{
  color:#00796f!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total{
  color:#1d4ed8!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-net{
  background:#f8fafc!important;
  box-shadow:none!important;
  justify-content:center!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total{
  padding:0 16px!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total::after,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total::after,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-net::after{
  content:""!important;
  position:absolute!important;
  inset:auto 18px 50% 18px!important;
  transform:translateY(50%)!important;
  height:36px!important;
  border-radius:999px!important;
  z-index:0!important;
  pointer-events:none!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total::after{
  background:#e1f8ee!important;
  border:1px solid rgba(20,184,166,.28)!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total::after{
  background:#eff6ff!important;
  border:1px solid rgba(37,99,235,.25)!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-net::after{
  background:#0b1740!important;
  border:0!important;
  box-shadow:0 10px 22px rgba(15,23,42,.16)!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total,
.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-net{
  z-index:0!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total,
.ba-finance-worker-table-v100 .ba-worker-close-row.total .ba-finance-worker-cell.is-business-total{
  color:#00796f!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total,
.ba-finance-worker-table-v100 .ba-worker-close-row.total .ba-finance-worker-cell.is-worker-total{
  color:#1d4ed8!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-net,
.ba-finance-worker-table-v100 .ba-worker-close-row.total .ba-finance-worker-cell.is-net{
  color:#ffffff!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-row.total .ba-finance-worker-cell{
  font-weight:950!important;
  color:#00796f!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-row.total .ba-finance-worker-cell.is-group-worker{
  color:#0f766e!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-row.total .ba-finance-worker-cell.is-worker-total{
  color:#1d4ed8!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-row.total .ba-finance-worker-cell.is-net{
  color:#ffffff!important;
}

@media(max-width:1180px){
  .ba-finance-worker-table-v100 .ba-worker-close-groups,
  .ba-finance-worker-table-v100 .ba-worker-close-head,
  .ba-finance-worker-table-v100 .ba-worker-close-row{
    min-width:1280px!important;
  }
}

@media(max-width:760px){
  .ba-finance-worker-section-head{
    padding:20px 18px 12px!important;
  }

  .ba-finance-worker-section-head h3{
    font-size:22px!important;
  }

  .ba-finance-worker-table-v100{
    display:none!important;
  }

  .ba-finance-active-view .ba-worker-close-cards.ba-finance-worker-mobile-list{
    display:grid!important;
    margin:0 16px 20px!important;
  }
}


/* V101 cell value wrapper: creates real centered pills for totals without adding visual noise. */
.ba-finance-worker-table-v100 .ba-finance-worker-cell::after,
.ba-finance-worker-table-v100 .ba-finance-worker-cell::before{
  display:none!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-value{
  position:relative!important;
  z-index:1!important;
  min-height:34px!important;
  display:inline-flex!important;
  align-items:center!important;
  justify-content:center!important;
  border-radius:999px!important;
  padding:0 12px!important;
  color:inherit!important;
  white-space:nowrap!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total .ba-finance-worker-value{
  min-width:86px!important;
  background:#e1f8ee!important;
  border:1px solid rgba(20,184,166,.28)!important;
  color:#00796f!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total .ba-finance-worker-value{
  min-width:86px!important;
  background:#eff6ff!important;
  border:1px solid rgba(37,99,235,.25)!important;
  color:#1d4ed8!important;
}

.ba-finance-worker-table-v100 .ba-finance-worker-cell.is-net .ba-finance-worker-value{
  min-width:96px!important;
  background:#0b1740!important;
  border:1px solid rgba(11,23,64,.60)!important;
  color:#ffffff!important;
  box-shadow:0 10px 22px rgba(15,23,42,.16)!important;
}

.ba-finance-worker-table-v100 .ba-worker-close-row.total .ba-finance-worker-cell.is-net .ba-finance-worker-value{
  background:#00796f!important;
  border-color:#00796f!important;
}


/* =========================================================
   LOOP FINANCE — WORKER SUMMARY TABLE V102
   Más parecido al mockup: panel aireado, grupos como bloques,
   separaciones visibles y pills premium para totales.
   ========================================================= */

.ba-finance-active-view .ba-finance-worker-panel{
  position:relative!important;
  border:1px solid rgba(190,207,220,.82)!important;
  border-radius:30px!important;
  background:
    radial-gradient(circle at 0% 0%, rgba(20,184,166,.045), transparent 34%),
    #ffffff!important;
  box-shadow:0 24px 60px rgba(15,23,42,.07)!important;
  overflow:hidden!important;
}

.ba-finance-active-view .ba-finance-worker-section-head{
  padding:34px 40px 20px!important;
  margin:0!important;
  align-items:flex-start!important;
}

.ba-finance-active-view .ba-finance-worker-section-head span{
  color:#00796f!important;
  font-size:11px!important;
  font-weight:950!important;
  letter-spacing:.18em!important;
  text-transform:uppercase!important;
}

.ba-finance-active-view .ba-finance-worker-section-head h3{
  margin:8px 0 0!important;
  color:#0b1433!important;
  font-size:30px!important;
  line-height:1.02!important;
  letter-spacing:-.06em!important;
  font-weight:950!important;
}

.ba-finance-active-view .ba-finance-table-column-btn{
  min-height:44px!important;
  padding:0 18px!important;
  border-radius:999px!important;
  border:1px solid rgba(0,103,95,.28)!important;
  background:#ffffff!important;
  color:#00675f!important;
  box-shadow:0 14px 30px rgba(15,23,42,.055)!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100{
  margin:0 40px 38px!important;
  border:1px solid rgba(203,213,225,.78)!important;
  border-radius:18px!important;
  overflow:auto!important;
  background:#ffffff!important;
  box-shadow:0 16px 38px rgba(15,23,42,.035)!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-groups,
.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-head,
.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-row{
  display:grid!important;
  grid-template-columns:minmax(190px,1.34fr) repeat(var(--finance-col-count, 11), minmax(104px,1fr))!important;
  min-width:1420px!important;
  gap:0!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-groups{
  min-height:86px!important;
  align-items:stretch!important;
  padding:0!important;
  background:#ffffff!important;
  border-bottom:1px solid rgba(203,213,225,.76)!important;
}

.ba-finance-active-view .ba-finance-worker-group-spacer,
.ba-finance-active-view .ba-finance-worker-group{
  position:relative!important;
  min-height:86px!important;
  border-radius:0!important;
  border-right:1px solid rgba(203,213,225,.70)!important;
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  gap:10px!important;
  font-size:14px!important;
  font-weight:950!important;
  letter-spacing:.15em!important;
  text-transform:uppercase!important;
}

.ba-finance-active-view .ba-finance-worker-group-spacer{
  justify-content:flex-start!important;
  padding-left:30px!important;
  color:#0b1433!important;
  font-size:15px!important;
  letter-spacing:0!important;
  text-transform:none!important;
  background:#ffffff!important;
}

.ba-finance-active-view .ba-finance-worker-group::before,
.ba-finance-active-view .ba-finance-worker-group-spacer::before{
  width:28px!important;
  height:28px!important;
  border-radius:999px!important;
  display:grid!important;
  place-items:center!important;
  flex:0 0 auto!important;
  font-size:14px!important;
  letter-spacing:0!important;
  text-transform:none!important;
}

.ba-finance-active-view .ba-finance-worker-group-spacer::before{content:"◯"!important;color:#64748b!important;border:1px solid rgba(148,163,184,.32)!important;background:#ffffff!important;}
.ba-finance-active-view .ba-finance-worker-group.is-activity::before{content:"⌁"!important;color:#64748b!important;background:#ffffff!important;border:1px solid rgba(148,163,184,.28)!important;}
.ba-finance-active-view .ba-finance-worker-group.is-business::before{content:"▣"!important;color:#00796f!important;background:#ffffff!important;border:1px solid rgba(20,184,166,.28)!important;}
.ba-finance-active-view .ba-finance-worker-group.is-worker::before{content:"♙"!important;color:#1d4ed8!important;background:#ffffff!important;border:1px solid rgba(37,99,235,.24)!important;}
.ba-finance-active-view .ba-finance-worker-group.is-result::before{content:"$"!important;color:#ffffff!important;background:#1d4ed8!important;border:0!important;}

.ba-finance-active-view .ba-finance-worker-group::after{
  content:""!important;
  position:absolute!important;
  left:22px!important;
  right:22px!important;
  bottom:19px!important;
  border-bottom:2px dashed rgba(148,163,184,.32)!important;
}

.ba-finance-active-view .ba-finance-worker-group.is-activity{
  background:linear-gradient(135deg,#f8fafc 0%,#ffffff 100%)!important;
  color:#64748b!important;
}

.ba-finance-active-view .ba-finance-worker-group.is-business{
  background:linear-gradient(135deg,#edfdf8 0%,#ffffff 100%)!important;
  color:#00796f!important;
}

.ba-finance-active-view .ba-finance-worker-group.is-worker{
  background:linear-gradient(135deg,#eff6ff 0%,#ffffff 100%)!important;
  color:#1d4ed8!important;
}

.ba-finance-active-view .ba-finance-worker-group.is-result{
  background:linear-gradient(135deg,#f8fbff 0%,#ffffff 100%)!important;
  color:#1d4ed8!important;
}

.ba-finance-active-view .ba-finance-worker-group.is-business,
.ba-finance-active-view .ba-finance-worker-group.is-worker,
.ba-finance-active-view .ba-finance-worker-group.is-result{
  border-left:14px solid #ffffff!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-head{
  min-height:66px!important;
  align-items:stretch!important;
  padding:0!important;
  background:#ffffff!important;
  border-bottom:1px solid rgba(203,213,225,.72)!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-head > span{
  min-height:66px!important;
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  padding:0 12px!important;
  border-right:1px solid rgba(226,232,240,.74)!important;
  color:#0b1433!important;
  font-size:12px!important;
  line-height:1.16!important;
  font-weight:880!important;
  letter-spacing:-.015em!important;
  text-transform:none!important;
  white-space:normal!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-head > span:first-child{
  justify-content:flex-start!important;
  padding-left:30px!important;
  font-weight:950!important;
  background:#ffffff!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-head .is-group-start,
.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-row .is-group-start{
  border-left:14px solid #ffffff!important;
  box-shadow:inset 1px 0 0 rgba(203,213,225,.62)!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-head .is-group-activity,
.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-row .is-group-activity{
  background:#f8fafc!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-head .is-group-business,
.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-row .is-group-business{
  background:rgba(240,253,250,.48)!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-head .is-group-worker,
.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-row .is-group-worker{
  background:rgba(239,246,255,.48)!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-head .is-group-result,
.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-row .is-group-result{
  background:#fbfdff!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-head .is-business-total,
.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-head .is-worker-total,
.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-head .is-net{
  font-weight:950!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-row{
  min-height:78px!important;
  align-items:stretch!important;
  padding:0!important;
  background:#ffffff!important;
  border-bottom:1px solid rgba(203,213,225,.66)!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-row:last-child{border-bottom:0!important;}
.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-row:hover{background:#ffffff!important;}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-row.total{
  background:linear-gradient(90deg,rgba(229,250,244,.82),rgba(255,255,255,.96))!important;
  border-top:1px solid rgba(20,184,166,.24)!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-finance-worker-name,
.ba-finance-active-view .ba-finance-worker-table-v100 .ba-finance-worker-cell{
  min-height:78px!important;
  border-radius:0!important;
  border-right:1px solid rgba(226,232,240,.74)!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-finance-worker-name{
  display:flex!important;
  align-items:center!important;
  justify-content:flex-start!important;
  gap:13px!important;
  padding:0 28px!important;
  min-width:0!important;
  background:#ffffff!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-row.total .ba-finance-worker-name{
  background:rgba(229,250,244,.78)!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-finance-worker-name i{
  width:36px!important;
  height:36px!important;
  border-radius:999px!important;
  display:grid!important;
  place-items:center!important;
  background:#d9fbef!important;
  color:#00796f!important;
  font-style:normal!important;
  font-size:13px!important;
  font-weight:950!important;
  box-shadow:inset 0 0 0 1px rgba(20,184,166,.22)!important;
  flex:0 0 auto!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-finance-worker-name span{
  min-width:0!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
  white-space:nowrap!important;
  color:#0b1433!important;
  font-size:15px!important;
  font-weight:950!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-finance-worker-total-name i{
  background:#008577!important;
  color:#ffffff!important;
  box-shadow:none!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-finance-worker-cell{
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  padding:0 12px!important;
  background:#ffffff!important;
  color:#0b1433!important;
  font-size:14px!important;
  font-weight:880!important;
  line-height:1!important;
  white-space:nowrap!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-row.total .ba-finance-worker-cell{
  color:#00796f!important;
  font-weight:950!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-row.total .ba-finance-worker-cell.is-group-worker{
  color:#0f766e!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-finance-worker-value{
  min-height:36px!important;
  display:inline-flex!important;
  align-items:center!important;
  justify-content:center!important;
  border-radius:999px!important;
  padding:0 13px!important;
  color:inherit!important;
  white-space:nowrap!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-finance-worker-cell.is-business-total .ba-finance-worker-value{
  min-width:92px!important;
  background:#e1f8ee!important;
  border:1px solid rgba(20,184,166,.30)!important;
  color:#00796f!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-finance-worker-cell.is-worker-total .ba-finance-worker-value{
  min-width:92px!important;
  background:#eff6ff!important;
  border:1px solid rgba(37,99,235,.28)!important;
  color:#1d4ed8!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-finance-worker-cell.is-net .ba-finance-worker-value{
  min-width:102px!important;
  min-height:38px!important;
  background:#0b1740!important;
  border:1px solid rgba(11,23,64,.64)!important;
  color:#ffffff!important;
  box-shadow:0 12px 24px rgba(15,23,42,.18)!important;
}

.ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-row.total .ba-finance-worker-cell.is-net .ba-finance-worker-value{
  background:#00796f!important;
  border-color:#00796f!important;
}

@media(max-width:1180px){
  .ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-groups,
  .ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-head,
  .ba-finance-active-view .ba-finance-worker-table-v100 .ba-worker-close-row{
    min-width:1380px!important;
  }
}

@media(max-width:760px){
  .ba-finance-active-view .ba-finance-worker-section-head{padding:22px 18px 14px!important;}
  .ba-finance-active-view .ba-finance-worker-section-head h3{font-size:22px!important;}
  .ba-finance-active-view .ba-finance-worker-table-v100{display:none!important;}
  .ba-finance-active-view .ba-worker-close-cards.ba-finance-worker-mobile-list{display:grid!important;margin:0 16px 22px!important;}
}



/* LOOP PHASE 3C.9 — Finance worker table v110: executive compact, no icons, no heavy cells. */
.ba-finance-active-view .ba-finance-worker-panel{
  overflow:hidden!important;
}

.ba-finance-active-view .ba-finance-worker-section-head{
  padding:30px clamp(22px, 3.2vw, 44px) 18px!important;
  border-bottom:0!important;
}

.ba-finance-active-view .ba-finance-worker-section-head>div>span{
  color:#00796f!important;
  font-size:11px!important;
  font-weight:950!important;
  letter-spacing:.24em!important;
  text-transform:uppercase!important;
}

.ba-finance-active-view .ba-finance-worker-section-head h3{
  margin-top:8px!important;
  color:#0b1433!important;
  font-size:clamp(25px, 2.5vw, 36px)!important;
  line-height:1.02!important;
  letter-spacing:-.045em!important;
  font-weight:950!important;
}

.ba-finance-active-view .ba-finance-table-column-btn{
  min-height:44px!important;
  padding:0 20px!important;
  border-radius:999px!important;
  border:1px solid rgba(0,103,95,.38)!important;
  background:#ffffff!important;
  color:#00675f!important;
  font-size:13px!important;
  font-weight:900!important;
  box-shadow:0 10px 26px rgba(15,23,42,.05)!important;
}

.ba-finance-worker-table-v110-wrap{
  width:100%!important;
  overflow-x:auto!important;
  padding:22px clamp(18px, 3vw, 40px) 32px!important;
  scrollbar-width:thin!important;
  scrollbar-color:rgba(148,163,184,.55) transparent!important;
}

.ba-finance-worker-table-v110-wrap::-webkit-scrollbar{height:8px!important;}
.ba-finance-worker-table-v110-wrap::-webkit-scrollbar-track{background:transparent!important;}
.ba-finance-worker-table-v110-wrap::-webkit-scrollbar-thumb{background:rgba(148,163,184,.45)!important;border-radius:999px!important;}

.ba-finance-worker-table-v110{
  --finance-worker-v110-cols:minmax(240px,1.5fr) 96px minmax(225px,1.16fr) minmax(152px,.84fr) minmax(235px,1.18fr) minmax(170px,.9fr) minmax(145px,.75fr);
  min-width:1180px!important;
  display:grid!important;
  gap:0!important;
  background:#ffffff!important;
  border:1px solid rgba(226,232,240,.78)!important;
  border-radius:18px!important;
  overflow:hidden!important;
  box-shadow:0 22px 48px rgba(15,23,42,.06)!important;
}

.ba-finance-worker-groups-v110,
.ba-finance-worker-head-v110,
.ba-finance-worker-row-v110{
  display:grid!important;
  grid-template-columns:var(--finance-worker-v110-cols)!important;
  align-items:stretch!important;
}

.ba-finance-worker-groups-v110{
  min-height:72px!important;
  background:linear-gradient(180deg,#ffffff 0%,#fbfefe 100%)!important;
  border-bottom:1px solid rgba(226,232,240,.72)!important;
}

.ba-finance-group-v110{
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  gap:16px!important;
  padding:0 18px!important;
  min-width:0!important;
}

.ba-finance-group-v110::before,
.ba-finance-group-v110::after{
  content:""!important;
  display:block!important;
  height:1px!important;
  min-width:22px!important;
  flex:1 1 auto!important;
  background:rgba(148,163,184,.34)!important;
}

.ba-finance-group-v110 b{
  color:#0b1433!important;
  font-size:12px!important;
  line-height:1!important;
  font-weight:950!important;
  letter-spacing:.18em!important;
  text-transform:uppercase!important;
  white-space:nowrap!important;
}

.ba-finance-group-v110.is-business b{color:#00796f!important;}
.ba-finance-group-v110.is-business::before,
.ba-finance-group-v110.is-business::after{background:rgba(20,184,166,.50)!important;}
.ba-finance-group-v110.is-worker b{color:#2458e6!important;}
.ba-finance-group-v110.is-worker::before,
.ba-finance-group-v110.is-worker::after{background:rgba(37,99,235,.42)!important;}
.ba-finance-group-v110.is-result b{color:#0b1433!important;}

.ba-finance-worker-head-v110{
  min-height:58px!important;
  background:#ffffff!important;
  border-bottom:1px solid rgba(226,232,240,.76)!important;
}

.ba-finance-worker-head-v110>span{
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  padding:0 16px!important;
  color:#0b1433!important;
  font-size:13px!important;
  font-weight:900!important;
  letter-spacing:-.015em!important;
  white-space:nowrap!important;
  border-left:1px solid rgba(226,232,240,.72)!important;
}

.ba-finance-worker-head-v110>span:first-child{
  justify-content:flex-start!important;
  padding-left:28px!important;
  border-left:0!important;
}

.ba-finance-worker-row-v110{
  min-height:86px!important;
  background:#ffffff!important;
  border-bottom:1px solid rgba(226,232,240,.76)!important;
}

.ba-finance-worker-row-v110:last-child{border-bottom:0!important;}

.ba-finance-worker-row-v110>span,
.ba-finance-worker-row-v110>strong{
  min-width:0!important;
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  padding:14px 16px!important;
  border-left:1px solid rgba(226,232,240,.58)!important;
}

.ba-finance-worker-row-v110>strong:first-child{
  border-left:0!important;
}

.ba-finance-worker-name-v110{
  justify-content:flex-start!important;
  gap:14px!important;
  padding-left:28px!important;
  color:#0b1433!important;
  font-size:15px!important;
  font-weight:950!important;
  letter-spacing:-.02em!important;
}

.ba-finance-worker-name-v110 i{
  width:34px!important;
  height:34px!important;
  flex:0 0 34px!important;
  display:grid!important;
  place-items:center!important;
  border-radius:999px!important;
  background:#d8fbef!important;
  color:#00796f!important;
  font-size:13px!important;
  font-style:normal!important;
  font-weight:950!important;
  box-shadow:inset 0 0 0 1px rgba(20,184,166,.24)!important;
}

.ba-finance-worker-name-v110 span{
  min-width:0!important;
  white-space:normal!important;
  overflow:visible!important;
  text-overflow:clip!important;
  word-break:break-word!important;
}

.ba-finance-worker-total-name-v110 i{
  background:#008577!important;
  color:#ffffff!important;
  box-shadow:0 12px 22px rgba(0,133,119,.20)!important;
}

.ba-finance-worker-count-v110{
  color:#0b1433!important;
  font-size:15px!important;
  font-weight:950!important;
}

.ba-finance-breakdown-v110{
  width:100%!important;
  max-width:148px!important;
  display:grid!important;
  gap:8px!important;
  margin:0 auto!important;
}

.ba-finance-breakdown-v110 span{
  display:grid!important;
  grid-template-columns:54px 1fr!important;
  align-items:center!important;
  gap:8px!important;
  min-width:0!important;
}

.ba-finance-breakdown-v110 em{
  color:#667085!important;
  font-size:13px!important;
  line-height:1!important;
  font-style:normal!important;
  font-weight:760!important;
  letter-spacing:-.01em!important;
}

.ba-finance-breakdown-v110 strong{
  color:#26324b!important;
  font-size:13px!important;
  line-height:1!important;
  font-weight:850!important;
  text-align:left!important;
  white-space:nowrap!important;
}

.ba-finance-worker-breakdown-cell-v110{
  justify-content:center!important;
}

.ba-finance-worker-pill-cell-v110,
.ba-finance-worker-net-cell-v110{
  justify-content:center!important;
}

.ba-finance-pill-v110,
.ba-finance-net-v110{
  min-height:34px!important;
  display:inline-flex!important;
  align-items:center!important;
  justify-content:center!important;
  border-radius:999px!important;
  padding:0 18px!important;
  font-size:14px!important;
  line-height:1!important;
  font-weight:950!important;
  white-space:nowrap!important;
}

.ba-finance-pill-v110.is-business{
  min-width:96px!important;
  color:#008577!important;
  background:linear-gradient(180deg,#e4f9f0 0%,#dff7ee 100%)!important;
  border:1px solid rgba(20,184,166,.25)!important;
}

.ba-finance-pill-v110.is-worker{
  min-width:106px!important;
  color:#1d4ed8!important;
  background:linear-gradient(180deg,#f1f7ff 0%,#eaf3ff 100%)!important;
  border:1px solid rgba(96,165,250,.24)!important;
}

.ba-finance-net-v110{
  min-width:96px!important;
  background:transparent!important;
  border:0!important;
}

.ba-finance-net-v110.is-neutral{color:#0b1433!important;}
.ba-finance-net-v110.is-positive{color:#00796f!important;}
.ba-finance-net-v110.is-negative{color:#dc2626!important;}

.ba-finance-worker-row-v110.is-total{
  margin:0!important;
  min-height:88px!important;
  background:linear-gradient(90deg,rgba(223,250,244,.88) 0%,rgba(240,253,250,.78) 52%,rgba(248,253,252,.92) 100%)!important;
  border-bottom:0!important;
}

.ba-finance-worker-row-v110.is-total .ba-finance-worker-count-v110{
  color:#00796f!important;
}

.ba-finance-worker-row-v110.is-total .ba-finance-breakdown-v110 strong,
.ba-finance-worker-row-v110.is-total .ba-finance-breakdown-v110 em{
  color:#00796f!important;
}

@media(max-width:1180px){
  .ba-finance-worker-table-v110{min-width:1120px!important;}
  .ba-finance-worker-table-v110{
    --finance-worker-v110-cols:minmax(220px,1.35fr) 88px minmax(200px,1fr) minmax(140px,.78fr) minmax(215px,1fr) minmax(150px,.82fr) minmax(132px,.7fr);
  }
}

@media(max-width:760px){
  .ba-finance-worker-table-v110-wrap{display:none!important;}
  .ba-finance-active-view .ba-worker-close-cards.ba-finance-worker-mobile-list{display:grid!important;margin:0 16px 22px!important;}
  .ba-finance-active-view .ba-finance-worker-section-head{padding:22px 18px 14px!important;}
  .ba-finance-active-view .ba-finance-worker-section-head h3{font-size:22px!important;}
}

/* LOOP PHASE 3C.10 — Finance worker table v111: grouped, configurable, persistent columns. */
.ba-finance-worker-table-v110{
  min-width:var(--finance-worker-v110-min-width,1180px)!important;
}

.ba-finance-worker-head-v110>span.ba-finance-worker-cell-v110,
.ba-finance-worker-row-v110>.ba-finance-worker-cell-v110{
  border-left:1px solid rgba(226,232,240,.58)!important;
}

.ba-finance-worker-head-v110>span.ba-finance-worker-cell-v110:first-child,
.ba-finance-worker-row-v110>.ba-finance-worker-cell-v110:first-child{
  border-left:0!important;
}

.ba-finance-worker-head-v110>span.is-group-start,
.ba-finance-worker-row-v110>.is-group-start{
  border-left-width:2px!important;
}

.ba-finance-worker-head-v110>span.is-business-start,
.ba-finance-worker-row-v110>.is-business-start{
  border-left-color:rgba(20,184,166,.26)!important;
}

.ba-finance-worker-head-v110>span.is-worker-start,
.ba-finance-worker-row-v110>.is-worker-start{
  border-left-color:rgba(37,99,235,.24)!important;
}

.ba-finance-worker-head-v110>span.is-result-start,
.ba-finance-worker-row-v110>.is-result-start{
  border-left-color:rgba(11,20,51,.20)!important;
}

.ba-finance-worker-head-v110>span.is-group-business,
.ba-finance-worker-row-v110>.is-group-business{
  background:linear-gradient(180deg,rgba(240,253,250,.55),rgba(255,255,255,.86))!important;
}

.ba-finance-worker-head-v110>span.is-group-worker,
.ba-finance-worker-row-v110>.is-group-worker{
  background:linear-gradient(180deg,rgba(239,246,255,.55),rgba(255,255,255,.88))!important;
}

.ba-finance-worker-head-v110>span.is-group-result,
.ba-finance-worker-row-v110>.is-group-result{
  background:linear-gradient(180deg,rgba(248,250,252,.74),rgba(255,255,255,.92))!important;
}

.ba-finance-worker-row-v110.is-total>.is-group-business,
.ba-finance-worker-row-v110.is-total>.is-group-worker,
.ba-finance-worker-row-v110.is-total>.is-group-result{
  background:transparent!important;
}

.ba-finance-worker-row-v110 .ba-finance-worker-name-v110,
.ba-finance-worker-head-v110>span.is-worker{
  justify-content:flex-start!important;
  text-align:left!important;
  padding-left:28px!important;
}

.ba-finance-worker-row-v110 .ba-finance-worker-name-v110{
  width:100%!important;
}

.ba-finance-worker-name-v110 i{
  margin-left:0!important;
}

.ba-finance-breakdown-v110{
  max-width:174px!important;
}

.ba-finance-breakdown-v110 span{
  grid-template-columns:74px 1fr!important;
  gap:10px!important;
}

.ba-finance-breakdown-empty-v110{
  color:#94a3b8!important;
  font-weight:900!important;
}

.ba-finance-column-customizer-body{
  grid-template-columns:1fr 1fr!important;
  gap:14px!important;
}

.ba-finance-column-group{
  position:relative!important;
  overflow:hidden!important;
}

.ba-finance-column-group::before{
  content:""!important;
  position:absolute!important;
  left:0!important;
  top:0!important;
  bottom:0!important;
  width:3px!important;
  background:rgba(148,163,184,.28)!important;
}

.ba-finance-column-group.is-business::before{background:rgba(20,184,166,.64)!important;}
.ba-finance-column-group.is-worker::before{background:rgba(37,99,235,.54)!important;}
.ba-finance-column-group.is-result::before{background:rgba(15,23,42,.42)!important;}
.ba-finance-column-group.is-general::before{background:rgba(0,103,95,.34)!important;}

.ba-finance-column-group-head{
  display:grid!important;
  gap:3px!important;
  margin:0 0 6px!important;
}

.ba-finance-column-group-head strong{
  color:#0b1433!important;
  font-size:13px!important;
  font-weight:950!important;
  letter-spacing:-.015em!important;
}

.ba-finance-column-group-head span{
  color:#64748b!important;
  font-size:11px!important;
  font-weight:760!important;
}

.ba-finance-column-toggle{
  justify-content:flex-start!important;
  padding:5px 2px!important;
}

.ba-finance-column-toggle span{
  flex:1 1 auto!important;
}

.ba-finance-column-toggle em{
  padding:4px 8px!important;
  border-radius:999px!important;
  background:#f1f5f9!important;
  color:#64748b!important;
  font-size:10px!important;
  line-height:1!important;
  font-style:normal!important;
  font-weight:900!important;
}

.ba-finance-column-toggle.is-required{
  color:#64748b!important;
}

.ba-finance-column-toggle.is-required input{
  opacity:.62!important;
  cursor:not-allowed!important;
}

.ba-view-customizer-foot .ba-soft-action{
  min-height:40px!important;
  padding:0 16px!important;
}

@media(max-width:720px){
  .ba-finance-column-customizer-body{grid-template-columns:1fr!important;}
  .ba-view-customizer-foot{flex-direction:column-reverse!important;align-items:stretch!important;}
  .ba-view-customizer-foot button{width:100%!important;}
}

  /* LOOP REPORTS V3 A3.1 — Executive Summary cockpit */
  /* LOOP REPORTS V3 A3.2 — Adaptive semantics + empty states */
  /* LOOP REPORTS V3 A3.3 — status reconciliation + adaptive historical grain */
  /* LOOP REPORTS V3 A4.1 — finance semantics foundation */
  /* LOOP REPORTS V3 A4.1C — paid-vs-collected reconciliation bucket */
  .ba-overview-v3-health.info{border-color:#c8e5ee;background:#f5fbfd}
  .ba-overview-v3-health.neutral{border-color:#e2e8f0;background:#f8fafc}
  .ba-overview-v3-summary>article{padding-top:12px;padding-bottom:10px}
  .ba-overview-v3-summary article>div{padding-top:7px;padding-bottom:7px}
  .ba-overview-v3-trend.is-empty{min-height:0}
  .ba-overview-v3-empty-trend{
    min-height:118px;
    border:1px dashed #dbe5eb;
    border-radius:12px;
    background:#fafcfd;
    display:flex;
    align-items:center;
    justify-content:center;
    gap:12px;
    padding:18px;
  }
  .ba-overview-v3-empty-trend>div{
    width:34px;
    height:34px;
    flex:0 0 34px;
    display:grid;
    place-items:center;
    border-radius:10px;
    background:#eef8f6;
    color:var(--brand);
    font-size:16px;
    font-weight:950;
  }
  .ba-overview-v3-empty-trend>span{display:grid;gap:4px;min-width:0}
  .ba-overview-v3-empty-trend strong{color:#0f172a;font-size:12px;font-weight:950}
  .ba-overview-v3-empty-trend em{color:#64748b;font-size:10px;line-height:1.35;font-style:normal;font-weight:720}
  .ba-overview-v3-priorities{padding-bottom:12px}
  .ba-overview-v3{display:grid;gap:14px;margin-top:14px;}
  .ba-overview-v3-head{display:flex;align-items:flex-start;justify-content:space-between;gap:18px;padding:2px 0 0;}
  .ba-overview-v3-head>div:first-child>span,.ba-overview-v3-panel-head span{display:block;color:var(--brand);font-size:10px;font-weight:950;letter-spacing:.14em;text-transform:uppercase;}
  .ba-overview-v3-head h3{margin:5px 0 0;color:#0f172a;font-size:30px;line-height:1;letter-spacing:-.055em;font-weight:950;}
  .ba-overview-v3-head p{margin:7px 0 0;color:#64748b;font-size:12.5px;font-weight:720;}
  .ba-overview-v3-health{min-width:190px;border:1px solid #dfe8ee;border-radius:14px;background:#fff;padding:11px 13px;display:grid;gap:3px;}
  .ba-overview-v3-health.warning{border-color:#f4d99c;background:#fffaf0}
  .ba-overview-v3-health.success{border-color:#bde9df;background:#f4fffb}
  .ba-overview-v3-health span{color:#64748b;font-size:9px;font-weight:950;letter-spacing:.09em;text-transform:uppercase;}
  .ba-overview-v3-health strong{color:#0f172a;font-size:18px;font-weight:950;letter-spacing:-.025em;}
  .ba-overview-v3-health em{color:#64748b;font-size:10px;font-style:normal;font-weight:750;}
  .ba-overview-v3-summary{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));border:1px solid #e0e8ee;border-radius:16px;overflow:hidden;background:#fff;}
  .ba-overview-v3-summary>article{min-width:0;padding:14px 15px 12px;border-right:1px solid #e7edf2;}
  .ba-overview-v3-summary>article:last-child{border-right:0}
  .ba-overview-v3-summary article>header{display:flex;align-items:center;justify-content:space-between;gap:10px;padding-bottom:9px;border-bottom:1px solid #edf2f5;}
  .ba-overview-v3-summary article>header span{color:#64748b;font-size:9px;font-weight:950;letter-spacing:.11em;text-transform:uppercase;}
  .ba-overview-v3-summary article>header strong{color:#0f172a;font-size:14px;font-weight:950;}
  .ba-overview-v3-summary article>div{display:grid;grid-template-columns:minmax(0,1fr) auto;grid-template-areas:"label value" "hint hint";gap:2px 12px;padding:8px 0;border-bottom:1px solid #f0f4f7;}
  .ba-overview-v3-summary article>div:last-child{border-bottom:0;padding-bottom:0}
  .ba-overview-v3-summary article>div>span{grid-area:label;color:#475569;font-size:11px;font-weight:800;}
  .ba-overview-v3-summary article>div>strong{grid-area:value;color:#0f172a;font-size:12px;font-weight:950;}
  .ba-overview-v3-summary article>div>em{grid-area:hint;color:#94a3b8;font-size:9.5px;font-style:normal;font-weight:700;}
  .ba-overview-v3-summary article>div.warning strong{color:#b45309}
  .ba-overview-v3-main-grid{display:grid;grid-template-columns:minmax(0,1.45fr) minmax(300px,.75fr);gap:12px;align-items:start;}
  .ba-overview-v3 .ba-panel{border-radius:15px;box-shadow:none;}
  .ba-overview-v3-panel-head{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;margin-bottom:12px;}
  .ba-overview-v3-panel-head.compact{margin-bottom:10px}
  .ba-overview-v3-panel-head h4{margin:4px 0 0;color:#0f172a;font-size:17px;line-height:1.1;font-weight:950;letter-spacing:-.03em;}
  .ba-overview-v3-panel-head p{margin:5px 0 0;color:#64748b;font-size:10.5px;font-weight:720;}
  .ba-overview-v3-panel-head button{flex:0 0 auto;border:1px solid #cfe9e4;border-radius:9px;background:#fff;color:var(--brand);min-height:32px;padding:0 10px;font-size:10px;font-weight:950;cursor:pointer;}
  .ba-overview-v3-priority-list{display:grid;gap:6px;}
  .ba-overview-v3-priority-list>button{width:100%;min-width:0;border:1px solid #e5ebf0;border-radius:10px;background:#fff;padding:8px 9px;display:grid;grid-template-columns:23px minmax(0,1fr) auto;gap:9px;align-items:center;text-align:left;cursor:pointer;}
  .ba-overview-v3-priority-list>button.warning{background:#fffaf0;border-color:#f3dfb6}
  .ba-overview-v3-priority-list>button.success{background:#f5fffb;border-color:#cceee5}
  .ba-overview-v3-priority-list>button>b{width:23px;height:23px;display:grid;place-items:center;border-radius:7px;background:#eef8f6;color:var(--brand);font-size:9px;}
  .ba-overview-v3-priority-list>button>span{min-width:0;display:grid;gap:2px;}
  .ba-overview-v3-priority-list>button>span strong{color:#0f172a;font-size:10.5px;font-weight:950;}
  .ba-overview-v3-priority-list>button>span em{color:#64748b;font-size:9px;line-height:1.25;font-style:normal;font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .ba-overview-v3-priority-list>button>i{color:#0f172a;font-size:10px;font-style:normal;font-weight:950;white-space:nowrap;}
  .ba-overview-v3-performance-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));border:1px solid #e6edf2;border-radius:12px;overflow:hidden;}
  .ba-overview-v3-performance-grid>div{min-width:0;padding:11px 12px;border-right:1px solid #e9eef3;display:grid;gap:4px;}
  .ba-overview-v3-performance-grid>div:last-child{border-right:0}
  .ba-overview-v3-performance-grid span{color:#64748b;font-size:8.5px;font-weight:950;letter-spacing:.08em;text-transform:uppercase;}
  .ba-overview-v3-performance-grid strong{color:#0f172a;font-size:12px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .ba-overview-v3-performance-grid em{color:#64748b;font-size:9.5px;font-style:normal;font-weight:750;}
  .ba-overview-v3-bottom-grid{display:grid;grid-template-columns:minmax(0,.85fr) minmax(0,1.15fr);gap:12px;align-items:start;}
  .ba-overview-v3-status-list{display:grid;gap:8px;}
  .ba-overview-v3-footnote{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:8px 2px 0;color:#94a3b8;font-size:9.5px;font-weight:700;}
  .ba-overview-v3-footnote strong{color:#64748b;font-weight:800;text-align:right;}
  @media(max-width:1050px){
    .ba-overview-v3-summary{grid-template-columns:1fr}
    .ba-overview-v3-summary>article{border-right:0;border-bottom:1px solid #e7edf2}
    .ba-overview-v3-summary>article:last-child{border-bottom:0}
    .ba-overview-v3-main-grid{grid-template-columns:1fr}
    .ba-overview-v3-performance-grid{grid-template-columns:1fr 1fr}
    .ba-overview-v3-performance-grid>div:nth-child(2){border-right:0}
    .ba-overview-v3-performance-grid>div:nth-child(-n+2){border-bottom:1px solid #e9eef3}
    .ba-overview-v3-bottom-grid{grid-template-columns:1fr}
  }
  @media(max-width:680px){
    .ba-overview-v3-head{flex-direction:column}
    .ba-overview-v3-health{width:100%;min-width:0}
    .ba-overview-v3-performance-grid{grid-template-columns:1fr}
    .ba-overview-v3-performance-grid>div{border-right:0;border-bottom:1px solid #e9eef3!important}
    .ba-overview-v3-performance-grid>div:last-child{border-bottom:0!important}
    .ba-overview-v3-footnote{flex-direction:column;align-items:flex-start}
    .ba-overview-v3-footnote strong{text-align:left}
  }

  .ba-overview-v3-reconciliation-note{
    margin-top:9px;
    padding-top:8px;
    border-top:1px solid #edf2f5;
    color:#94a3b8;
    font-size:9px;
    font-weight:750;
  }

  .ba-overview-v3-finance-trace{
    margin-top:-4px;
    padding:9px 11px;
    border:1px solid #e2e8f0;
    border-radius:11px;
    background:#f8fafc;
    display:flex;
    flex-wrap:wrap;
    gap:7px 18px;
    color:#64748b;
    font-size:9.5px;
    line-height:1.35;
    font-weight:720;
  }
  .ba-overview-v3-finance-trace strong{color:#334155;font-weight:900}
  .ba-finance-mini-metrics>div.data-quality{
    border-color:#d9e2ea!important;
    background:#f8fafc!important;
  }
  .ba-finance-mini-metrics>div.data-quality strong{color:#475569!important}


  /* LOOP REPORTS V3 A4.2 — Finance V3 professional control center */
  /* LOOP REPORTS V3 A4.2A — Finance V3 UX polish */
  /* LOOP REPORTS V3 A4.2B — matched-period comparison engine */
  /* LOOP REPORTS V3 A4.2B1 — comparison visibility polish */
  /* LOOP REPORTS V3 A4.2C — deterministic sales drivers */
  .ba-finance-v3{
    display:grid;
    margin-top:18px;
    gap:16px;
    min-width:0;
  }

  .ba-finance-v3-head{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:22px;
    padding:4px 2px 2px;
  }

  .ba-finance-v3-head>div:first-child{
    min-width:0;
    display:grid;
    gap:5px;
  }

  .ba-finance-v3-head>div:first-child>span,
  .ba-finance-v3-card-head>div>span{
    color:#00796f;
    font-size:10px;
    line-height:1;
    font-weight:950;
    letter-spacing:.12em;
    text-transform:uppercase;
  }

  .ba-finance-v3-head h3{
    margin:0;
    color:#0f172a;
    font-size:27px;
    line-height:1.05;
    font-weight:950;
    letter-spacing:-.035em;
  }

  .ba-finance-v3-head p,
  .ba-finance-v3-card-head p{
    margin:0;
    color:#64748b;
    font-size:11.5px;
    line-height:1.45;
    font-weight:720;
  }

  .ba-finance-v3-health{
    min-width:210px;
    display:grid;
    gap:3px;
    padding:11px 14px;
    border:1px solid #dfe8ec;
    border-radius:13px;
    background:#fff;
    text-align:right;
  }

  .ba-finance-v3-health>span{
    color:#64748b;
    font-size:9px;
    font-weight:950;
    letter-spacing:.09em;
    text-transform:uppercase;
  }

  .ba-finance-v3-health>strong{
    color:#0f172a;
    font-size:16px;
    line-height:1.05;
    font-weight:950;
  }

  .ba-finance-v3-health>em{
    color:#64748b;
    font-size:9.5px;
    line-height:1.3;
    font-style:normal;
    font-weight:720;
  }

  .ba-finance-v3-health>button{
    justify-self:end;
    margin-top:3px;
    padding:0;
    border:0;
    background:transparent;
    color:#b45309;
    font-size:9px;
    line-height:1.2;
    font-weight:900;
    cursor:pointer;
  }

  .ba-finance-v3-health>button span{
    display:inline-block;
    margin-left:3px;
    transition:transform .15s ease;
  }

  .ba-finance-v3-health>button:hover span{transform:translateX(2px)}

  .ba-finance-v3-health.success{border-color:#cce9df;background:#f8fdfb}
  .ba-finance-v3-health.warning{border-color:#f0d9ad;background:#fffdf7}
  .ba-finance-v3-health.primary{border-color:#cfe4e3;background:#f7fbfb}

  .ba-finance-v3-strip{
    display:grid;
    grid-template-columns:repeat(4,minmax(0,1fr));
    overflow:hidden;
    border:1px solid #e3e9ed;
    border-radius:15px;
    background:#fff;
  }

  .ba-finance-v3-strip>div,
  .ba-finance-v3-strip>button{
    min-width:0;
    display:grid;
    gap:4px;
    padding:14px 17px;
    border:0;
    border-left:1px solid #edf1f4;
    background:#fff;
    text-align:left;
    font:inherit;
  }

  .ba-finance-v3-strip>div:first-child,
  .ba-finance-v3-strip>button:first-child{border-left:0}

  .ba-finance-v3-strip>button.is-clickable{
    cursor:pointer;
    transition:background .15s ease,box-shadow .15s ease;
  }

  .ba-finance-v3-strip>button.is-clickable:hover,
  .ba-finance-v3-strip>button.is-clickable:focus-visible{
    background:#fffaf2;
    box-shadow:inset 0 -2px 0 rgba(180,83,9,.28);
    outline:none;
  }

  .ba-finance-v3-strip span{
    color:#64748b;
    font-size:9.5px;
    line-height:1.15;
    font-weight:900;
  }

  .ba-finance-v3-strip strong{
    color:#0f172a;
    font-size:20px;
    line-height:1;
    font-weight:950;
    letter-spacing:-.025em;
  }

  .ba-finance-v3-strip em{
    min-height:23px;
    color:#94a3b8;
    font-size:9px;
    line-height:1.3;
    font-style:normal;
    font-weight:720;
  }

  .ba-finance-v3-delta{
    min-height:18px;
    display:flex;
    align-items:center;
    gap:5px;
    color:#64748b;
    font-size:8.5px;
    line-height:1.2;
    font-weight:800;
  }

  .ba-finance-v3-delta strong{
    font-size:9px!important;
    line-height:1!important;
    letter-spacing:0!important;
  }

  .ba-finance-v3-delta span{
    overflow:hidden;
    color:#94a3b8!important;
    font-size:8px!important;
    font-weight:760!important;
    white-space:nowrap;
    text-overflow:ellipsis;
  }

  .ba-finance-v3-delta.success strong{color:#047857!important}
  .ba-finance-v3-delta.warning strong{color:#b45309!important}
  .ba-finance-v3-delta.neutral strong{color:#64748b!important}

  .ba-finance-v3-strip .warning strong{color:#b45309}
  .ba-finance-v3-strip .result strong{color:#00796f}

  .ba-finance-v3-drivers{
    padding:15px 17px;
  }

  .ba-finance-v3-drivers .ba-finance-v3-card-head{margin-bottom:11px}

  .ba-finance-v3-drivers-grid{
    display:grid;
    grid-template-columns:minmax(0,1.25fr) minmax(210px,.8fr) minmax(210px,.8fr);
    gap:14px;
    align-items:stretch;
  }

  .ba-finance-v3-driver-summary,
  .ba-finance-v3-driver-column{
    min-width:0;
    padding:12px;
    border:1px solid #e7ecef;
    border-radius:11px;
    background:#fbfcfd;
  }

  .ba-finance-v3-driver-summary{display:grid;gap:8px}

  .ba-finance-v3-driver-summary>strong{
    color:#334155;
    font-size:13px;
    line-height:1.25;
    font-weight:950;
  }

  .ba-finance-v3-driver-summary>strong.is-up{color:#047857}
  .ba-finance-v3-driver-summary>strong.is-down{color:#b45309}

  .ba-finance-v3-driver-summary>p{
    margin:0;
    color:#64748b;
    font-size:9.5px;
    line-height:1.4;
    font-weight:720;
  }

  .ba-finance-v3-driver-bridge{
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:8px;
  }

  .ba-finance-v3-driver-bridge>div{
    display:grid;
    gap:3px;
    padding:9px 10px;
    border:1px solid #edf1f4;
    border-radius:9px;
    background:#fff;
  }

  .ba-finance-v3-driver-bridge span,
  .ba-finance-v3-driver-column-title,
  .ba-finance-v3-driver-reconcile>span{
    color:#64748b;
    font-size:8.5px;
    font-weight:900;
    letter-spacing:.02em;
  }

  .ba-finance-v3-driver-bridge strong{
    color:#0f172a;
    font-size:14px;
    line-height:1;
    font-weight:950;
  }

  .ba-finance-v3-driver-bridge em{
    color:#94a3b8;
    font-size:8.5px;
    line-height:1.25;
    font-style:normal;
    font-weight:760;
  }

  .ba-finance-v3-driver-reconcile{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:10px;
    padding-top:3px;
  }

  .ba-finance-v3-driver-reconcile>em{
    color:#475569;
    font-size:8.8px;
    font-style:normal;
    font-weight:850;
    text-align:right;
  }

  .ba-finance-v3-driver-column{display:grid;align-content:start;gap:0}

  .ba-finance-v3-driver-column-title{
    padding-bottom:6px;
    color:#00796f;
    text-transform:uppercase;
    letter-spacing:.08em;
  }

  .ba-finance-v3-driver-row{
    min-height:43px;
    display:grid;
    grid-template-columns:minmax(0,1fr) auto;
    align-items:center;
    gap:10px;
    border-top:1px solid #edf1f4;
  }

  .ba-finance-v3-driver-row>div{min-width:0;display:grid;gap:2px}

  .ba-finance-v3-driver-row strong{
    overflow:hidden;
    color:#0f172a;
    font-size:9.5px;
    font-weight:900;
    white-space:nowrap;
    text-overflow:ellipsis;
  }

  .ba-finance-v3-driver-row span{
    overflow:hidden;
    color:#94a3b8;
    font-size:8px;
    font-weight:720;
    white-space:nowrap;
    text-overflow:ellipsis;
  }

  .ba-finance-v3-driver-row>em{
    color:#64748b;
    font-size:9.5px;
    font-style:normal;
    font-weight:950;
    white-space:nowrap;
  }

  .ba-finance-v3-driver-row>em.positive{color:#047857}
  .ba-finance-v3-driver-row>em.negative{color:#b45309}

  .ba-finance-v3-driver-empty{
    padding:12px 0;
    border-top:1px solid #edf1f4;
    color:#94a3b8;
    font-size:8.8px;
    font-weight:760;
  }

  .ba-finance-v3-drivers-note{
    margin:9px 1px 0;
    color:#94a3b8;
    font-size:8.2px;
    line-height:1.35;
    font-weight:720;
  }

  .ba-finance-v3-core-grid{
    display:grid;
    grid-template-columns:minmax(0,1.18fr) minmax(330px,.82fr);
    gap:16px;
    align-items:start;
  }

  .ba-finance-v3 .ba-panel{
    border-radius:15px;
    box-shadow:none;
  }

  .ba-finance-v3-pnl,
  .ba-finance-v3-collections,
  .ba-finance-v3-trend,
  .ba-finance-v3-ranking,
  .ba-finance-v3-worker-self{
    padding:17px;
  }

  .ba-finance-v3-card-head{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:14px;
    margin-bottom:14px;
  }

  .ba-finance-v3-card-head>div{
    min-width:0;
    display:grid;
    gap:4px;
  }

  .ba-finance-v3-card-head h4{
    margin:0;
    color:#0f172a;
    font-size:16px;
    line-height:1.12;
    font-weight:950;
    letter-spacing:-.02em;
  }

  .ba-finance-v3-badge{
    flex:0 0 auto;
    min-height:26px;
    display:inline-flex;
    align-items:center;
    justify-content:center;
    padding:0 9px;
    border:1px solid #cfe6e2;
    border-radius:999px;
    background:#f4fbfa;
    color:#00796f;
    font-size:9px;
    font-weight:900;
    white-space:nowrap;
  }

  .ba-finance-v3-badge.neutral{
    border-color:#e2e8f0;
    background:#f8fafc;
    color:#64748b;
  }

  .ba-finance-v3-pnl-body{
    display:grid;
    gap:0;
  }

  .ba-finance-v3-pnl-line{
    min-height:38px;
    display:grid;
    grid-template-columns:minmax(0,1fr) auto;
    align-items:center;
    gap:16px;
    padding:7px 2px;
    border-bottom:1px solid #eef2f5;
  }

  .ba-finance-v3-pnl-line span{
    color:#475569;
    font-size:11px;
    font-weight:760;
  }

  .ba-finance-v3-pnl-line strong{
    color:#0f172a;
    font-size:12.5px;
    font-weight:950;
  }

  .ba-finance-v3-pnl-line.negative strong{color:#64748b}

  .ba-finance-v3-pnl-line.subtotal{
    margin-top:2px;
    border-bottom:0;
    background:#f8fafc;
    border-radius:9px;
    padding:9px 10px;
  }

  .ba-finance-v3-pnl-line.subtotal span,
  .ba-finance-v3-pnl-line.subtotal strong{color:#0f172a;font-weight:950}

  .ba-finance-v3-pnl-line.result{
    margin-top:4px;
    border-bottom:0;
    background:#f1faf7;
    border-radius:9px;
    padding:10px;
  }

  .ba-finance-v3-pnl-line.result span,
  .ba-finance-v3-pnl-line.result strong{color:#00796f;font-weight:950}

  .ba-finance-v3-pnl-note{
    padding:5px 2px 8px;
    color:#64748b;
    font-size:9px;
    font-weight:720;
  }

  .ba-finance-v3-pnl-separator{
    height:11px;
    border-bottom:1px dashed #dbe3e8;
    margin-bottom:5px;
  }

  .ba-finance-v3-pnl-missing,
  .ba-finance-v3-pnl-cash{
    display:grid;
    grid-template-columns:minmax(0,1fr) auto;
    gap:16px;
    align-items:center;
    margin-top:10px;
    padding:11px 12px;
    border:1px dashed #dce4e9;
    border-radius:10px;
    background:#fbfcfd;
  }

  .ba-finance-v3-pnl-missing>div,
  .ba-finance-v3-pnl-cash>div{display:grid;gap:3px}

  .ba-finance-v3-pnl-missing span,
  .ba-finance-v3-pnl-cash span{
    color:#334155;
    font-size:10px;
    font-weight:900;
  }

  .ba-finance-v3-pnl-missing em,
  .ba-finance-v3-pnl-cash em{
    color:#94a3b8;
    font-size:9px;
    line-height:1.35;
    font-style:normal;
    font-weight:720;
  }

  .ba-finance-v3-pnl-missing>strong{
    color:#94a3b8;
    font-size:18px;
    font-weight:950;
  }

  .ba-finance-v3-pnl-cash{
    border-style:solid;
    border-color:#cfe6e2;
    background:#f7fcfb;
  }

  .ba-finance-v3-pnl-cash>strong{
    color:#00796f;
    font-size:18px;
    font-weight:950;
  }

  .ba-finance-v3-collection-stack{
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:8px;
  }

  .ba-finance-v3-collection-stack>div{
    min-width:0;
    display:grid;
    gap:3px;
    padding:9px 10px;
    border:1px solid #e7ecef;
    border-radius:10px;
    background:#fff;
  }

  .ba-finance-v3-collection-stack span{
    color:#64748b;
    font-size:9px;
    font-weight:850;
  }

  .ba-finance-v3-collection-stack strong{
    color:#0f172a;
    font-size:13px;
    font-weight:950;
  }

  .ba-finance-v3-collection-stack .warning strong{color:#b45309}
  .ba-finance-v3-collection-stack .danger strong{color:#b91c1c}

  .ba-finance-v3-collection-bar{
    height:7px;
    display:flex;
    overflow:hidden;
    margin:11px 1px 12px;
    border-radius:999px;
    background:#eef2f5;
  }

  .ba-finance-v3-collection-bar span{height:100%;min-width:0}
  .ba-finance-v3-collection-bar .collected{background:#0f9f8f}
  .ba-finance-v3-collection-bar .pending{background:#e5a43b}
  .ba-finance-v3-collection-bar .mismatch{background:#d65b5b}

  .ba-finance-v3-collection-actions{
    display:grid;
    gap:6px;
  }

  .ba-finance-v3-collection-actions button{
    width:100%;
    min-height:40px;
    display:grid;
    grid-template-columns:minmax(0,1fr) auto 12px;
    align-items:center;
    gap:10px;
    padding:7px 9px;
    border:0;
    border-bottom:1px solid #eef2f5;
    border-radius:7px;
    background:transparent;
    cursor:pointer;
    text-align:left;
    transition:background .15s ease,transform .15s ease;
  }

  .ba-finance-v3-collection-actions button:last-child{border-bottom:0}

  .ba-finance-v3-collection-actions button span{
    color:#475569;
    font-size:10px;
    font-weight:800;
  }

  .ba-finance-v3-collection-actions button span::after{
    content:" · Ver detalle";
    color:#00796f;
    font-size:8.7px;
    font-weight:900;
  }

  .ba-finance-v3-collection-actions button strong{
    color:#0f172a;
    font-size:11px;
    font-weight:950;
  }

  .ba-finance-v3-collection-actions button::after{
    content:"›";
    color:#0f766e;
    font-size:16px;
    line-height:1;
    font-weight:900;
    transition:transform .15s ease;
  }

  .ba-finance-v3-collection-actions button:hover,
  .ba-finance-v3-collection-actions button:focus-visible{
    background:#f5fbfa;
    outline:none;
  }

  .ba-finance-v3-collection-actions button:hover::after,
  .ba-finance-v3-collection-actions button:focus-visible::after{transform:translateX(2px)}

  .ba-finance-v3-methods{
    display:grid;
    gap:0;
    margin-top:12px;
    padding-top:11px;
    border-top:1px solid #edf1f4;
  }

  .ba-finance-v3-methods-head,
  .ba-finance-v3-method-row{
    min-height:32px;
    display:grid;
    grid-template-columns:minmax(0,1fr) auto;
    align-items:center;
    gap:12px;
  }

  .ba-finance-v3-methods-head{
    margin-bottom:3px;
  }

  .ba-finance-v3-methods-head span{
    color:#334155;
    font-size:10px;
    font-weight:950;
  }

  .ba-finance-v3-methods-head strong{
    color:#00796f;
    font-size:11px;
    font-weight:950;
  }

  .ba-finance-v3-method-row{
    padding:5px 0;
    border-top:1px solid #f0f3f5;
  }

  .ba-finance-v3-method-row span{
    color:#64748b;
    font-size:9.5px;
    font-weight:760;
  }

  .ba-finance-v3-method-row em{
    color:#334155;
    font-size:10px;
    font-style:normal;
    font-weight:900;
  }

  .ba-finance-v3-method-more summary{
    cursor:pointer;
    padding:7px 0;
    color:#00796f;
    font-size:9px;
    font-weight:900;
  }

  .ba-finance-v3-trend{min-width:0}

  .ba-finance-v3-chart{
    position:relative;
    min-width:0;
    overflow:hidden;
    padding-top:4px;
  }

  .ba-finance-v3-chart-legend{
    display:flex;
    flex-wrap:wrap;
    gap:13px;
    margin-bottom:7px;
    color:#64748b;
    font-size:9px;
    font-weight:850;
  }

  .ba-finance-v3-chart-legend span::before{
    content:"";
    width:7px;
    height:7px;
    display:inline-block;
    margin-right:5px;
    border-radius:999px;
    vertical-align:middle;
  }

  .ba-finance-v3-chart-legend .sales::before{background:#0f766e}
  .ba-finance-v3-chart-legend .collected::before{background:#2563eb}
  .ba-finance-v3-chart-legend .result::before{background:#7c3aed}
  .ba-finance-v3-chart-legend .previous::before{
    width:13px;
    height:0;
    border-top:2px dashed #94a3b8;
    border-radius:0;
    background:transparent;
  }

  .ba-finance-v3-chart svg{
    width:100%;
    height:180px;
    display:block;
  }

  .ba-finance-v3-chart .grid{
    stroke:#edf1f4;
    stroke-width:1;
  }

  .ba-finance-v3-chart .zero{
    stroke:#cbd5e1;
    stroke-width:1.2;
    stroke-dasharray:4 4;
  }

  .ba-finance-v3-chart .sales-line,
  .ba-finance-v3-chart .collected-line,
  .ba-finance-v3-chart .result-line{
    fill:none;
    stroke-width:3;
    stroke-linejoin:round;
    stroke-linecap:round;
  }

  .ba-finance-v3-chart .sales-line{stroke:#0f766e}
  .ba-finance-v3-chart .collected-line{stroke:#2563eb}
  .ba-finance-v3-chart .result-line{stroke:#7c3aed}

  .ba-finance-v3-chart .comparison{
    stroke-width:2;
    stroke-dasharray:7 7;
    opacity:.48;
  }

  .ba-finance-v3-chart .comparison-dot{
    fill:#fff;
    stroke-width:2;
    opacity:.72;
  }

  .ba-finance-v3-chart .sales-dot{stroke:#0f766e}
  .ba-finance-v3-chart .collected-dot{stroke:#2563eb}
  .ba-finance-v3-chart .result-dot{stroke:#7c3aed}

  .ba-finance-v3-chart-axis{
    display:flex;
    justify-content:space-between;
    gap:10px;
    margin-top:-7px;
    color:#94a3b8;
    font-size:8.5px;
    font-weight:800;
  }

  .ba-finance-v3-empty-chart{
    min-height:150px;
    display:grid;
    place-content:center;
    gap:4px;
    border:1px dashed #dbe3e8;
    border-radius:11px;
    background:#fafcfd;
    text-align:center;
  }

  .ba-finance-v3-empty-chart strong{color:#334155;font-size:11px;font-weight:950}
  .ba-finance-v3-empty-chart span{color:#94a3b8;font-size:9px;font-weight:720}

  .ba-finance-v3-performance-grid{
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:16px;
    align-items:start;
  }

  .ba-finance-v3-ranking-list{display:grid;gap:0}

  .ba-finance-v3-ranking-row{
    min-height:48px;
    display:grid;
    grid-template-columns:26px minmax(0,1fr) auto;
    align-items:center;
    gap:10px;
    padding:7px 0;
    border-top:1px solid #edf1f4;
  }

  .ba-finance-v3-ranking-row:first-child{border-top:0}

  .ba-finance-v3-ranking-row>i{
    width:24px;
    height:24px;
    display:grid;
    place-items:center;
    border-radius:8px;
    background:#f1f5f9;
    color:#64748b;
    font-size:9px;
    font-style:normal;
    font-weight:950;
  }

  .ba-finance-v3-ranking-row.worker>i{
    border-radius:999px;
    background:#e6f7f3;
    color:#00796f;
  }

  .ba-finance-v3-ranking-row>div{
    min-width:0;
    display:grid;
    gap:2px;
  }

  .ba-finance-v3-ranking-row>div strong{
    color:#0f172a;
    font-size:10.5px;
    font-weight:900;
    white-space:nowrap;
    overflow:hidden;
    text-overflow:ellipsis;
  }

  .ba-finance-v3-ranking-row>div span{
    color:#94a3b8;
    font-size:8.8px;
    line-height:1.25;
    font-weight:720;
  }

  .ba-finance-v3-ranking-row>em{
    color:#00796f;
    font-size:10.5px;
    font-style:normal;
    font-weight:950;
    white-space:nowrap;
  }

  .ba-finance-v3-ranking-row>em.negative{color:#b91c1c}

  .ba-finance-v3-worker-detail{
    overflow:hidden;
    padding:0!important;
  }

  .ba-finance-v3-worker-detail>summary{
    min-height:62px;
    display:grid;
    grid-template-columns:minmax(0,1fr) auto;
    align-items:center;
    gap:16px;
    padding:13px 17px;
    cursor:pointer;
    list-style:none;
    transition:background .15s ease;
  }

  .ba-finance-v3-worker-detail>summary:hover,
  .ba-finance-v3-worker-detail>summary:focus-visible{
    background:#f7fbfb;
    outline:none;
  }

  .ba-finance-v3-worker-detail>summary::-webkit-details-marker{display:none}

  .ba-finance-v3-worker-detail>summary>div{
    display:grid;
    gap:3px;
  }

  .ba-finance-v3-worker-detail>summary span{
    color:#00796f;
    font-size:9px;
    font-weight:950;
    letter-spacing:.1em;
    text-transform:uppercase;
  }

  .ba-finance-v3-worker-detail>summary strong{
    color:#0f172a;
    font-size:12px;
    font-weight:950;
  }

  .ba-finance-v3-worker-detail-action{
    display:grid!important;
    grid-template-columns:auto auto auto;
    align-items:center;
    gap:7px!important;
  }

  .ba-finance-v3-worker-detail-action>em{
    color:#64748b;
    font-size:9px;
    font-style:normal;
    font-weight:850;
  }

  .ba-finance-v3-worker-detail-action>strong{
    color:#00796f!important;
    font-size:9.5px!important;
    font-weight:950!important;
  }

  .ba-finance-v3-worker-detail-action>i{
    color:#00796f;
    font-size:15px;
    line-height:1;
    font-style:normal;
    font-weight:900;
    transition:transform .18s ease;
  }

  .ba-finance-v3-worker-detail[open] .ba-finance-v3-worker-detail-action>i{transform:rotate(180deg)}

  .ba-finance-v3-worker-detail-body{
    max-height:650px;
    overflow:auto;
    border-top:1px solid #edf1f4;
  }

  .ba-finance-v3-worker-detail-head{
    position:sticky;
    top:0;
    z-index:3;
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:14px;
    padding:11px 16px;
    border-bottom:1px solid #edf1f4;
    background:rgba(255,255,255,.96);
    backdrop-filter:blur(8px);
  }

  .ba-finance-v3-worker-detail-head p{
    margin:0;
    color:#64748b;
    font-size:9.5px;
    font-weight:720;
  }

  .ba-finance-v3-worker-self .ba-finance-worker-mobile-list.is-worker-v3{
    display:grid!important;
    gap:8px!important;
    margin:0!important;
  }

  .ba-finance-v3-drawer-layer{
    position:fixed;
    inset:0;
    z-index:1300;
    display:flex;
    justify-content:flex-end;
    background:rgba(15,23,42,.28);
    backdrop-filter:blur(2px);
  }

  .ba-finance-v3-drawer{
    width:min(620px,92vw);
    height:100%;
    display:grid;
    grid-template-rows:auto auto minmax(0,1fr) auto;
    background:#fff;
    box-shadow:-18px 0 44px rgba(15,23,42,.16);
  }

  .ba-finance-v3-drawer-head{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:18px;
    padding:22px 22px 16px;
    border-bottom:1px solid #edf1f4;
  }

  .ba-finance-v3-drawer-head>div{display:grid;gap:4px}

  .ba-finance-v3-drawer-head span{
    color:#00796f;
    font-size:9px;
    font-weight:950;
    letter-spacing:.12em;
    text-transform:uppercase;
  }

  .ba-finance-v3-drawer-head h3{
    margin:0;
    color:#0f172a;
    font-size:20px;
    font-weight:950;
    letter-spacing:-.025em;
  }

  .ba-finance-v3-drawer-head p{
    margin:0;
    color:#64748b;
    font-size:10px;
    line-height:1.4;
    font-weight:720;
  }

  .ba-finance-v3-drawer-head>button{
    width:34px;
    height:34px;
    flex:0 0 34px;
    border:1px solid #e2e8f0;
    border-radius:10px;
    background:#fff;
    color:#475569;
    font-size:20px;
    cursor:pointer;
  }

  .ba-finance-v3-drawer-tools{
    display:grid;
    grid-template-columns:minmax(0,1fr) auto;
    align-items:center;
    gap:12px;
    padding:11px 22px;
    border-bottom:1px solid #edf1f4;
  }

  .ba-finance-v3-drawer-tools input{
    width:100%;
    min-height:38px;
    padding:0 11px;
    border:1px solid #dbe3e8;
    border-radius:10px;
    outline:none;
    color:#0f172a;
    font-size:11px;
    font-weight:720;
  }

  .ba-finance-v3-drawer-tools input:focus{border-color:#7ccac1;box-shadow:0 0 0 3px rgba(20,184,166,.08)}

  .ba-finance-v3-drawer-tools span{
    color:#64748b;
    font-size:9px;
    font-weight:850;
    white-space:nowrap;
  }

  .ba-finance-v3-drawer-list{
    min-height:0;
    overflow:auto;
    padding:6px 22px 12px;
  }

  .ba-finance-v3-drawer-row{
    display:grid;
    grid-template-columns:minmax(0,1fr) auto;
    align-items:center;
    gap:18px;
    padding:12px 0;
    border-bottom:1px solid #edf1f4;
  }

  .ba-finance-v3-drawer-row>div{
    min-width:0;
    display:grid;
    gap:3px;
  }

  .ba-finance-v3-drawer-row>div:first-child strong{
    color:#0f172a;
    font-size:11px;
    font-weight:950;
  }

  .ba-finance-v3-drawer-row>div:first-child span,
  .ba-finance-v3-drawer-row>div:first-child em{
    color:#64748b;
    font-size:9px;
    line-height:1.3;
    font-style:normal;
    font-weight:720;
  }

  .ba-finance-v3-drawer-row>div:last-child{text-align:right}

  .ba-finance-v3-drawer-row>div:last-child span,
  .ba-finance-v3-drawer-row>div:last-child em{
    color:#94a3b8;
    font-size:8px;
    font-style:normal;
    font-weight:800;
  }

  .ba-finance-v3-drawer-row>div:last-child strong{
    color:#0f172a;
    font-size:12px;
    font-weight:950;
  }

  .ba-finance-v3-drawer-foot{
    min-height:56px;
    display:grid;
    grid-template-columns:auto minmax(0,1fr) auto;
    align-items:center;
    gap:12px;
    padding:9px 22px;
    border-top:1px solid #edf1f4;
    background:#fff;
  }

  .ba-finance-v3-drawer-foot span{
    text-align:center;
    color:#64748b;
    font-size:9px;
    font-weight:850;
  }

  .ba-finance-v3-drawer-foot button{
    min-height:34px;
    padding:0 12px;
    border:1px solid #dbe3e8;
    border-radius:9px;
    background:#fff;
    color:#334155;
    font-size:9px;
    font-weight:900;
    cursor:pointer;
  }

  .ba-finance-v3-drawer-foot button:disabled{opacity:.42;cursor:not-allowed}

  @media(max-width:1080px){
    .ba-finance-v3-strip{grid-template-columns:1fr 1fr}
    .ba-finance-v3-strip>*:nth-child(3){border-left:0;border-top:1px solid #edf1f4}
    .ba-finance-v3-strip>*:nth-child(4){border-top:1px solid #edf1f4}
    .ba-finance-v3-core-grid{grid-template-columns:1fr}
    .ba-finance-v3-drivers-grid{grid-template-columns:1fr 1fr}
    .ba-finance-v3-driver-summary{grid-column:1 / -1}
  }

  @media(max-width:760px){
    .ba-finance-v3{gap:12px}
    .ba-finance-v3-head{display:grid;gap:10px}
    .ba-finance-v3-head h3{font-size:23px}
    .ba-finance-v3-health{min-width:0;width:100%;text-align:left}
    .ba-finance-v3-strip{grid-template-columns:1fr 1fr}
    .ba-finance-v3-strip>div,
    .ba-finance-v3-strip>button{padding:12px}
    .ba-finance-v3-strip strong{font-size:17px}
    .ba-finance-v3-performance-grid{grid-template-columns:1fr}
    .ba-finance-v3-drivers{padding:14px}
    .ba-finance-v3-drivers-grid{grid-template-columns:1fr}
    .ba-finance-v3-driver-summary{grid-column:auto}
    .ba-finance-v3-driver-bridge{grid-template-columns:1fr 1fr}
    .ba-finance-v3-pnl,
    .ba-finance-v3-collections,
    .ba-finance-v3-trend,
    .ba-finance-v3-ranking,
    .ba-finance-v3-worker-self{padding:14px}
    .ba-finance-v3-card-head{display:grid}
    .ba-finance-v3-badge{justify-self:start}
    .ba-finance-v3-chart svg{height:160px}
    .ba-finance-v3-worker-detail-head{align-items:flex-start;flex-direction:column}
    .ba-finance-v3-worker-detail-head button{width:100%}
    .ba-finance-v3-drawer{width:100vw}
    .ba-finance-v3-drawer-tools{grid-template-columns:1fr}
    .ba-finance-v3-drawer-row{gap:10px}
  }

  @media(max-width:480px){
    .ba-finance-v3-strip{grid-template-columns:1fr}
    .ba-finance-v3-strip>div,
    .ba-finance-v3-strip>button{border-left:0;border-top:1px solid #edf1f4}
    .ba-finance-v3-strip>div:first-child,
    .ba-finance-v3-strip>button:first-child{border-top:0}
    .ba-finance-v3-collection-stack{grid-template-columns:1fr 1fr}
    .ba-finance-v3-pnl-line{gap:10px}
    .ba-finance-v3-pnl-line span{font-size:10px}
    .ba-finance-v3-drawer-head,
    .ba-finance-v3-drawer-tools,
    .ba-finance-v3-drawer-list,
    .ba-finance-v3-drawer-foot{padding-left:15px;padding-right:15px}
  }

`;


